// Scalable JSON-based i18n system for Inkvaluator
// Supports 17 languages and 13 currencies

export type Language =
  | "en"
  | "nl"
  | "de"
  | "fr"
  | "es"
  | "it"
  | "pt"
  | "sv"
  | "pl"
  | "ja"
  | "ko"
  | "ru"
  | "tr"
  | "da"
  | "no"
  | "fi"
  | "cs";
export type Currency =
  | "EUR"
  | "USD"
  | "GBP"
  | "CAD"
  | "AUD"
  | "CHF"
  | "SEK"
  | "NOK"
  | "JPY"
  | "KRW"
  | "PLN"
  | "BRL"
  | "TRY";

export const LANGUAGE_LABELS: Record<Language, string> = {
  en: "English",
  nl: "Nederlands",
  de: "Deutsch",
  fr: "Français",
  es: "Español",
  it: "Italiano",
  pt: "Português",
  sv: "Svenska",
  pl: "Polski",
  ja: "日本語",
  ko: "한국어",
  ru: "Русский",
  tr: "Türkçe",
  da: "Dansk",
  no: "Norsk",
  fi: "Suomi",
  cs: "Čeština",
};

export const CURRENCY_SYMBOLS: Record<Currency, string> = {
  EUR: "€",
  USD: "$",
  GBP: "£",
  CAD: "C$",
  AUD: "A$",
  CHF: "CHF",
  SEK: "kr",
  NOK: "kr",
  JPY: "¥",
  KRW: "₩",
  PLN: "zł",
  BRL: "R$",
  TRY: "₺",
};

export const CURRENCY_LABELS: Record<Currency, string> = {
  EUR: "Euro (€)",
  USD: "US Dollar ($)",
  GBP: "British Pound (£)",
  CAD: "Canadian Dollar (C$)",
  AUD: "Australian Dollar (A$)",
  CHF: "Swiss Franc (CHF)",
  SEK: "Swedish Krona (kr)",
  NOK: "Norwegian Krone (kr)",
  JPY: "Japanese Yen (¥)",
  KRW: "Korean Won (₩)",
  PLN: "Polish Złoty (zł)",
  BRL: "Brazilian Real (R$)",
  TRY: "Turkish Lira (₺)",
};

const CURRENCY_LOCALES: Record<Currency, string> = {
  EUR: "nl-NL",
  USD: "en-US",
  GBP: "en-GB",
  CAD: "en-CA",
  AUD: "en-AU",
  CHF: "de-CH",
  SEK: "sv-SE",
  NOK: "nb-NO",
  JPY: "ja-JP",
  KRW: "ko-KR",
  PLN: "pl-PL",
  BRL: "pt-BR",
  TRY: "tr-TR",
};

export function formatCurrency(amount: number, currency: Currency = "EUR"): string {
  return new Intl.NumberFormat(CURRENCY_LOCALES[currency], {
    style: "currency",
    currency,
    minimumFractionDigits: currency === "JPY" ? 0 : 0,
    maximumFractionDigits: currency === "JPY" ? 0 : 0,
  }).format(amount);
}

export function formatCurrencyRange(min: number, max: number, currency: Currency = "EUR"): string {
  if (min === max) return formatCurrency(min, currency);
  return `${formatCurrency(min, currency)} – ${formatCurrency(max, currency)}`;
}

// ─── BASE ENGLISH ──────────────────────────────────────────────
const en: Record<string, string> = {
  // General
  "app.name": "Inkvaluator",
  "app.tagline": "Calculate your Tattoo Price",
  "app.powered_by": "Powered by",
  // Currency switcher
  "currency.aria_label": "Change currency",
  "currency.title": "Display currency",
  "currency.rates_of": "Rates of {date}",
  "currency.approx_tooltip": "Original price {original} — exchange rate of {date}",
  "currency.approx_prefix": "≈",
  back: "Back",
  save: "Save",
  copy: "Copy",
  copied: "Copied!",
  loading: "Loading...",
  error: "Error",
  optional: "optional",
  // Hero
  "landing.hero_eyebrow_artists": "Artists",
  "landing.hero_eyebrow_prices": "Prices",
  "landing.hero_eyebrow_styles": "Styles",
  "landing.hero_title_new_1": "Find Your",
  "landing.hero_title_new_2": "Perfect",
  "landing.hero_title_new_3": "Tattoo Artist",
  "landing.hero_subtitle_new": "Compare portfolios, styles and prices. Find the best artist near you.",
 
  // Value Props
  "landing.value_eyebrow": "Your tattoo journey",
  "landing.value_title": "Everything you need for your next tattoo",
  "landing.value_subtitle": "Whether it's your first or your twentieth — we make the process effortless.",
  "landing.value_card1_title": "Find Your Artist",
  "landing.value_card1_desc": "Browse verified artists by style, location and price. Compare portfolios and book with confidence.",
  "landing.value_card1_stat": "artists",
  "landing.value_card2_title": "Calculate Your Price",
  "landing.value_card2_desc": "Get a realistic price estimate based on size, style, detail and location. No surprises.",
  "landing.value_card2_stat": "Free estimate",
  "landing.value_card3_title": "Discover Styles",
  "landing.value_card3_desc": "From bold Traditional to delicate Fine Line. Discover which style suits you with real examples.",
  "landing.value_card3_stat": "styles",
  "landing.value_artist_cta": "Are you a tattoo artist? Claim your free profile →",
 
  // Styles
  "landing.styles_title_new": "Find Your Style",
  "landing.styles_subtitle": "From bold Traditional to delicate Fine Line. Discover the aesthetic that speaks to you.",
  "landing.styles_all_cta": "Browse all styles",
 
  // Testimonials
  "landing.reviews_eyebrow": "Experiences",
  "landing.reviews_title": "What clients say",
  "landing.testimonial_1": "The price calculator gave me a clear idea of what to expect. The artist near me was a perfect match for my style.",
  "landing.testimonial_2": "First tattoo and I was nervous. The style comparison and price indication made me feel well prepared.",
  "landing.testimonial_3": "I compared 3 studios via Inkvaluator. The transparency about prices and styles is unmatched anywhere else.",
  "landing.reviews_verified": "Verified artists",
  "landing.reviews_instant": "Compare instantly",
  "landing.reviews_clients": "happy clients",
 
  // Final CTA
  "landing.final_cta_find": "Find an artist",
 
  // Proof bar
  "landing.proof_styles": "Styles",
 
  // Navigation
  "nav.find_artists": "Find Artists",
  "nav.by_location": "By Location",
  "nav.by_location_desc": "Find tattoo studios and artists near you",
  "nav.by_style": "By Style",
  "nav.by_style_desc": "Search artists specialized in your style",
  "nav.browse_studios": "Discover",
  "nav.browse_studios_desc": "Browse all studios on the map and compare",
  "nav.tools": "Tools",
  "nav.price_calculator": "Price Calculator",
  "nav.price_calculator_desc": "Calculate what your tattoo will cost based on style, size and location",
  "nav.style_guide": "Style Guide",
  "nav.style_guide_desc": "50+ tattoo styles explained with examples and price indications",
  "nav.for_artists": "For Artists",
  "nav.free_profile": "Free Profile",
  "nav.free_profile_desc": "Claim your studio profile and start receiving client enquiries",
  "nav.artist_resources": "Blog & Resources",
  "nav.artist_resources_desc": "Tips, trends and guides to grow your studio",
  "nav.artist_register": "Create Account",
  "nav.join_artist": "Claim Profile",
 
  // Footer
  "footer.explore": "Explore",
  "footer.find_artist": "Find an Artist",
  "footer.price_calculator": "Price Calculator",
  "footer.style_guide": "Style Guide",
  "footer.popular_cities": "Popular Cities",
  "footer.all_cities": "All cities →",
  "footer.tattoo_styles": "Tattoo Styles",
  "footer.all_styles": "All styles →",
  "footer.resources": "Resources",
  "footer.claim_profile": "Claim your Profile",
  "footer.tagline_expanded": "Find the best tattoo artists, compare styles and prices, and book with confidence.",
  // Landing
  "landing.hero": "Calculate your",
  "landing.hero_accent": "Tattoo Price",
  "landing.subtitle": "Enter the code from your tattoo artist to get a personalised price estimate.",
  "landing.code_title": "Calculator Code",
  "landing.code_desc": "You received a unique code from your tattoo artist",
  "landing.code_placeholder": "E.g. A3F9K2",
  "landing.start": "Start Calculator",
  "landing.invalid_code": "Enter a valid code",
  "landing.or": "or",
  "landing.artist_cta": "Are you a tattoo artist? Create your own calculator.",
  "landing.artist_register": "Register as artist",
  "landing.artist_login": "Artist login",
  "landing.hero_title": "Calculate your",
  "landing.hero_title_accent": "Tattoo Price",
  "landing.hero_desc": "Discover in seconds what your next tattoo costs. Try it directly.",
  "landing.hero_ai_speed": "From photo to price estimate in seconds — powered by AI",
  "landing.try_it": "Try it yourself!",
  "landing.hero_cta": "TRY THE CALCULATOR",
  "landing.showcase_title": "Premium",
  "landing.showcase_accent": "Styles",
  "landing.showcase_desc": "Explore the most popular tattoo styles from artists worldwide.",
  "landing.estimated_price": "Estimated price",
  "landing.your_rates": "Your rates",
  "landing.rates_desc": "Move the sliders and see the price change",
  "landing.hourly_rate": "Hourly rate",
  "landing.start_cost": "Fixed start costs",
  "landing.size_label": "Size",
  "landing.complexity_label": "Complexity",
  "landing.body_placement": "Body placement",
  "landing.demo_disclaimer": "This is a demo — the actual price depends on the artist and design.",
  "landing.find_artist": "Find your perfect",
  "landing.find_artist_accent": "artist",
  "landing.nearby_desc": "Pro & Studio artists near you",
  "landing.nearby_soon": "Soon artists near you will appear here.",
  "landing.discover_all": "Discover All Artists",
  "landing.artist_or_studio": "Are you an artist or studio?",
  "landing.artist_question": "Are you an artist or studio?",
  "landing.promo_placeholder": "Discount code (optional)",
  "landing.view_options": "View options & pricing",
  "landing.start_free": "Start free",
  "landing.discount_code": "Discount code (optional)",
  "landing.upgrade_to": "Upgrade to",
  "landing.discover": "Discover",
  "landing.login": "Login",
  "landing.founder_desc": "50% discount for the first 50 sign-ups",
  "landing.claim_now": "Claim now →",
  "landing.how_it_works_cta": "See how it works for your studio",

  // Calculator
  "calc.estimate": "Estimated Price",
  "calc.size": "Size",
  "calc.size_desc": "How big will your tattoo be?",
  "calc.complexity": "Complexity",
  "calc.complexity_desc": "How detailed is the design?",
  "calc.body_part": "Body Part",
  "calc.body_part_desc": "Click on the body part where your tattoo will be",
  "calc.style": "Style",
  "calc.style_desc": "Which tattoo style do you want?",
  "calc.budget": "Budget",
  "calc.budget_desc": "What is your maximum budget?",
  "calc.budget_placeholder": "E.g. 300",
  "calc.get_estimate": "Get a realistic price estimate from",
  "calc.no_artist": "No artist found with this code or link.",
  "calc.not_configured": "The price calculator has not been configured by the artist yet.",
  "calc.back_home": "Back to homepage",
  "calc.artist_login": "Artist? Log in",
  "calc.add_color": "Add color?",
  "calc.color_surcharge": "+20% surcharge for color work",
  "calc.color_desc": "Color adds extra time and cost",
  "calc.ai_scan": "AI Image Scan",
  "calc.manual": "Enter manually",
  "calc.details": "Detail level",
  "calc.details_desc": "How detailed is the tattoo?",
  "calc.start": "Start",
  "calc.start_title": "How would you like to start?",
  "calc.start_subtitle": "Upload a reference image or enter details manually",
  "calc.rough_estimate": "Rough estimate",
  "calc.calculated_price": "Calculated price",
  "calc.view_price_cta": "View your price overview",
  "calc.no_photo": "No reference photo added",
  "calc.save_for_later": "Save for later",
  "calc.link_copied": "Link copied!",
  "calc.compare_configs": "Compare configurations",
  "calc.compare_desc": "Compare two price configurations side by side",
  "whatsapp.add_photo": "Add your photo",
  "whatsapp.photos_label": "Reference photos — max. 3",
  "calc.share": "Share",
  "calc.more_options": "More options",
  "landing.find_artist_worldwide": "Find your perfect artist, worldwide",
  "calc.info_style":
    "The chosen style affects the price. Styles like Realism or Japanese require more precision and time, making them more expensive than simpler styles.",
  "calc.info_placement":
    "Some body parts are harder to reach or more sensitive, requiring extra care and time from the artist.",
  "calc.info_size":
    "Size determines the base working hours. A mini tattoo takes under an hour, while XL pieces can span multiple sessions.",
  "calc.info_detail":
    "Higher detail means more precision work, shading, and fine lines — all of which increase session duration.",
  "calc.info_color":
    "Color work requires additional ink types, layering passes, and longer healing. This typically adds ~20% to the total time.",
  "share.native_title": "My Tattoo Estimate – Inkvaluator",
  // Coaching
  "coaching.title": "Tips",
  "coaching.color_tip":
    "Adding color increases time by ~20%. Styles like Watercolor or Neo-Traditional include color by default.",
  "coaching.complexity_tip": "Detailed designs take significantly more time. Consider 'medium' detail to reduce cost.",
  "coaching.session_tip": "This tattoo requires multiple sessions. Plan for 2-4 weeks healing time between sessions.",
  "coaching.placement_tip":
    "This body area is more challenging to tattoo, adding extra time. Arms and legs are generally easier placements.",
  "coaching.daily_rate_tip":
    "A day rate is applied for this larger piece — this is often cheaper than hourly billing for extended sessions.",
  "coaching.xl_tip":
    "Extra-large pieces are major projects. Consider starting with a smaller section and expanding over time.",

  // Layout
  "laf.calculator_mode": "Calculator display",
  "laf.calculator_mode_desc": "Choose how visitors see your calculator",
  "laf.mode_steps": "Step by step",
  "laf.mode_single": "All at once",
  "laf.mode_toggle": "Let visitor choose",
  "layout.visitor_toggle": "Choose display",

  // Stepper
  "step.next": "Continue",
  "step.view_result": "View Price",
  "step.details": "Details",
  "step.details_desc": "How detailed is your design?",
  "layout.stepper": "Step by step",
  "layout.form": "All at once",

  // City page (TattooCity)
  "city.page_title": "Tattoo {city} — {count} studios & prices | Inkvaluator",
  "city.page_desc":
    "Compare {count} tattoo studios in {city}. View prices, styles and reviews. Calculate your tattoo cost instantly.",
  "city.hero_subtitle": "{count} tattoo studios in {city}. Compare artists, view prices and calculate instantly.",
  "city.cta_primary": "Calculate your price in {city}",
  "city.cta_secondary": "Calculate your tattoo price instantly with local artists",
  "city.calc_button": "Calculate price →",
  "city.price_section_title": "What does a tattoo cost in {city}?",
  "city.price_section_subtitle":
    "Price indications based on calculations via Inkvaluator by artists in {city} and surroundings.",
  "city.calc_exact_button": "Calculate your exact price",
  "city.studios_subtitle": "Overview of known tattoo studios in {city}",
  "city.view_button": "View →",
  "city.other_cities_title": "Tattoo artists in other cities",
  "city.faq_price_q": "What does a tattoo cost in {city}?",
  "city.faq_price_a":
    "The price of a tattoo in {city} starts from {from} for a small tattoo (5–10 cm). A medium tattoo (10–20 cm) costs {medium}. A large piece (20 cm+) starts from {large}. Use the Inkvaluator calculator for an accurate estimate.",
  "city.faq_count_q": "How many tattoo shops are there in {city}?",
  "city.faq_count_a": "There are currently {count} tattoo studios known in {city} via Inkvaluator.",
  "city.faq_styles_q": "Which tattoo styles are popular in {city}?",
  "city.faq_styles_a": "The most requested styles in {city} are {styles}.",
  "city.faq_deposit_q": "How much deposit do I pay at a tattoo shop in {city}?",
  "city.faq_deposit_a": "Most tattoo studios in {city} ask for a deposit of 10–25% when scheduling an appointment.",
  "city.faq_choose_q": "How do I choose the right tattoo shop in {city}?",
  "city.faq_choose_a":
    "View portfolios of artists in {city} on Inkvaluator, compare prices and styles, and read Google reviews.",
  "city.faq_calc_q": "Can I calculate the price of a tattoo in {city} online?",
  "city.faq_calc_a":
    "Yes. Via Inkvaluator you can calculate the price of your tattoo with artists in {city}. Fill in style, size, body part and detail level for a fair price indication.",

  // TattooIndex page
  "tattoo_index.page_title": "Tattoo studios by country | Inkvaluator",
  "tattoo_index.page_desc":
    "Discover tattoo studios in your country and city. Compare artists, view portfolios and calculate the price of your tattoo.",
  "tattoo_index.subtitle":
    "Choose your country and discover tattoo studios in your city. Compare artists, view portfolios and calculate your tattoo price.",
  "tattoo_index.stats": "{studios} studios · {cities} cities",
  "tattoo_index.view_cities": "View cities",

  // TattooCountry page
  "tattoo_country.page_title": "Tattoo studios in {country} — {count} studios | Inkvaluator",
  "tattoo_country.page_desc":
    "Discover {studios} tattoo studios in {cities} cities in {country}. Compare artists and calculate your tattoo cost.",
  "tattoo_country.stats": "{studios} studios in {cities} cities",

  "breakdown.title": "Price Breakdown",
  "breakdown.labor": "Labor (time)",
  "breakdown.est_hours": "Estimated hours",
  "breakdown.subtotal": "Subtotal",
  "breakdown.complexity": "Complexity",
  "breakdown.placement": "Placement",
  "breakdown.style": "Style",
  "breakdown.design_cost": "Design cost",
  "breakdown.deposit": "Deposit",
  "breakdown.total_range": "Total range",
  "breakdown.disclaimer":
    "This is an estimate, not a final price. The actual price will be determined after a consultation with the artist.",
  "breakdown.budget_good": "Your budget fits well within this estimate!",
  "breakdown.budget_okay": "Your budget fits within the lower end of this estimate.",
  "breakdown.budget_low": "Consider a smaller or simpler tattoo for this budget.",
  "breakdown.full_disclaimer":
    "Disclaimer: This is an automatic price estimate. The final price will be determined by the artist after a personal consultation.",
  "breakdown.estimate_label": "Estimate:",
  "breakdown.day_sessions": "{count} day session(s) of ±{hours} hours",
  "breakdown.incl_style_detail": "incl. style & detail",
  "breakdown.daily_rate_applied": "Favorable day rate applied",
  "breakdown.daily_rate_tooltip": "Favorable day rate applied due to duration",
  "breakdown.base_price_label": "Starting price (Size & Placement)",
  "breakdown.style_detail_surcharge": "Style & Detail surcharge",
  "breakdown.color_surcharge_label": "Color surcharge (+20% time)",
  "breakdown.total_indication": "Total Indication",
  "breakdown.required_deposit": "Required deposit",
  "breakdown.hours_unit": "hours",

  // Tooltips
  "tooltip.labor": "Based on the estimated time needed for this size tattoo, multiplied by the artist's hourly rate.",
  "tooltip.complexity": "More complex designs (more detail, shadows, textures) require more time and precision.",
  "tooltip.placement": "Some body parts are more sensitive or harder to reach, requiring extra time and care.",
  "tooltip.style": "Different styles require specific techniques and expertise, which affects the price.",
  "tooltip.design": "Costs for designing and preparing your unique tattoo.",
  "tooltip.deposit": "The deposit reserves your appointment and will be deducted from the total price.",

  // FAQ
  "faq.title": "Frequently Asked Questions",
  "faq.deposit_q": "Why a deposit?",
  "faq.deposit_a": "The deposit reserves your appointment and covers design time.",
  "faq.bigger_q": "What if my tattoo gets bigger?",
  "faq.bigger_a": "The price may change after consultation if the design is adjusted.",
  "faq.consult_q": "Is a consultation free?",
  "faq.consult_a": "Contact the studio for information about consultations.",

  // Dashboard
  "dash.welcome": "Welcome to Inkvaluator",
  "dash.setup_desc": "Let's configure your pricing settings",
  "dash.setup_info": "This only takes a few minutes. You can change everything later.",
  "dash.start_config": "Start configuration",
  "dash.logout": "Log out",
  "dash.view_profile": "View profile",
  "home.tab_home": "Home",
  "home.quick_actions": "Quick actions",
  "home.no_city": "City not set yet",
  "home.tile_profile_desc": "Bio, photos & location",
  "home.tile_leads_desc": "Incoming requests",
  "home.tile_analytics_desc": "Reach & conversion",
  "home.tile_basic_desc": "Hourly rate & deposit",
  "home.tile_engine_desc": "Styles & multipliers",
  "home.tile_branding_desc": "Customize look & feel",
  "home.stats_this_month": "This month",
  "home.stat_leads": "Leads",
  "home.stat_calcs": "Calculations",
  "home.stat_conversion": "Conversion",
  "home.stat_avg_lead": "Avg. lead value",
  "home.leads_this_month": "this month",
  "leads.filter_all": "All",
  "leads.filter_photo": "With photo",
  "leads.filter_quality": "High quality",
  "leads.drawer_estimate": "Price estimate",
  "leads.drawer_style": "Style",
  "leads.drawer_size": "Size",
  "leads.drawer_placement": "Placement",
  "leads.drawer_complexity": "Complexity",
  "leads.drawer_message": "Customer message",
  "leads.drawer_reference": "Reference photo",
  "leads.drawer_status": "Status",
  "leads.drawer_save_price": "Save",
  "leads.drawer_whatsapp": "Send WhatsApp message",
  "leads.drawer_no_phone": "No phone number available for WhatsApp",
  "score.profile_score": "Profile score",
  "score.excellent": "Excellent",
  "score.good": "Good",
  "score.incomplete": "Incomplete",
  "dash.share_title": "Share your Calculator",
  "dash.share_desc": "Share this link or code with your clients so they can calculate a price estimate.",
  "dash.link_copied": "Link copied!",
  "dash.code_copied": "Code copied!",
  "dash.settings_title": "Pricing Settings",
  "dash.settings_desc":
    "Adjust your rates and multipliers here. Changes are applied immediately to the client calculator.",
  "dash.saved": "Saved!",
  "dash.settings_saved": "Your settings have been updated.",
  "dash.save_error": "Could not save settings.",
  "dash.view_calculator": "View Calculator",
  "dash.settings_label": "Settings",
  "dash.profile_studio": "Profile & Studio",
  "dash.calculator_config": "Calculator Configuration",
  "dash.pro_features": "Pro Features",
  "dash.faq_management": "FAQ Management",
  "dash.faq_desc": "Add frequently asked questions to show on your calculator page.",
  "dash.custom_branding": "Custom Branding",
  "dash.whatsapp_leads": "WhatsApp Leads",
  "dash.team_management": "Team management",
  "dash.subscription": "Subscription",
  "dash.subscription_desc": "Manage your plan and billing.",
  "dash.current_plan": "Current plan",
  "upgrade.view_plans": "View plans",
  "dash.no_leads": "No leads received yet.",
  "dash.leads_appear": "Once customers contact you via WhatsApp they will appear here.",
  "dash.anonymous_request": "Anonymous request",
  "dash.view": "View",
  "dash.design_cost_hint": "Set to 0 to disable design costs.",
  "dash.deposit_hint": "Set to 0 to hide deposit.",
  "dash.save_changes": "Save changes",
  "dash.cancel": "Cancel",
  "dash.add": "Add",
  "dash.delete": "Delete",
  "dash.new_faq": "Add new FAQ",
  "dash.ask_question": "Ask a question...",
  "dash.type_answer": "Type the answer...",
  "dash.no_faqs": "No FAQs yet. Add your first question below.",
  "dash.faq_added": "FAQ added",
  "dash.faq_updated": "FAQ updated",
  "dash.faq_deleted": "FAQ deleted",
  "dash.add_error": "Error adding",
  "dash.save_error_generic": "Error saving",
  "dash.delete_error": "Error deleting",
  "dash.suggest_style": "Suggest a Style",
  "dash.suggest_style_desc": "Missing a style? Suggest it and we may add it to the platform.",
  "dash.suggest_style_placeholder": "E.g. Chicano, New School...",
  "dash.style_suggested": "Style suggestion submitted!",
  "dash.submit": "Submit",
  "dash.upload_thumbnail": "Upload custom image",
  "dash.reset_to_default": "Reset to default",
  "dash.reset_all_thumbnails": "Reset all to default",
  "dash.thumbnail_uploaded": "Style image uploaded!",
  "dash.thumbnail_reset": "Image reset to default",
  "dash.thumbnails_reset_all": "All style images reset to defaults",
  "dash.invite_created": "Invite created!",
  "dash.invite_error": "Error creating invite",
  "dash.member_removed": "Member removed",
  "dash.link_copied_generic": "Link copied!",
  "dash.profile_saved": "Profile saved!",
  "dash.profile_save_error": "Error saving",
  "dash.calculator_ready": "🎉 Calculator ready!",
  "dash.share_with_clients": "Share your link with clients.",
  "dash.previous": "Previous",
  "dash.next": "Next",
  "dash.start_inkprice": "Start with InkPrice",
  "dash.step_of": "Step {current} of {total}",
  "dash.onboard_min_desc":
    "Set your minimum rate. This is the lowest price you charge for a tattoo, regardless of size.",
  "dash.onboard_hourly_desc": "Set your hourly rate. This is the basis for all price calculations.",
  "dash.onboard_sizes_desc":
    "We have set up default size categories for you. You can adjust these later in the dashboard.",
  "dash.onboard_styles_desc":
    "All common styles are activated. You can toggle styles on/off and adjust multipliers in the dashboard.",
  "dash.onboard_logo_desc": "You can upload a logo now or later via the Branding tab in your dashboard.",
  "dash.onboard_logo_later": "Upload logo later via Dashboard → Branding",
  "dash.onboard_finish_title": "Your calculator is ready!",
  "dash.onboard_finish_desc":
    "Share your unique link or code with clients. You can adjust all settings anytime in your dashboard.",
  "dash.your_link": "Your calculator link:",
  "dash.online_card": "This is your online business card — visible on the directory and at the top of your calculator.",
  "dash.view_my_calculator": "View my Calculator",
  "dash.impersonation_banner": "You are currently viewing the profile of {name}",
  "dash.stop_impersonation": "Stop Impersonation",
  "dash.dashboard_language": "Dashboard Language",
  "dash.dashboard_language_desc": "The language of your management dashboard.",

  // Dashboard tabs
  "tab.basic": "Basic",
  "tab.sizes": "Sizes",
  "tab.complexity": "Complexity",
  "tab.body_parts": "Body Parts",
  "tab.styles": "Styles",
  "tab.theme": "Theme",
  "tab.branding": "Branding",
  "tab.analytics": "Analytics",

  // Basic settings
  "basic.title": "Base Rate",
  "basic.desc": "Set your standard rates and costs",
  "basic.min_price": "Minimum price",
  "basic.min_price_desc": "The lowest price you charge",
  "basic.hourly_rate": "Hourly rate",
  "basic.hourly_rate_desc": "Your standard hourly rate",
  "basic.daily_rate": "Daily rate",
  "basic.daily_rate_placeholder": "Leave empty if not applicable",
  "basic.design_cost": "Design cost",
  "basic.fixed": "Fixed",
  "basic.hourly": "Per hour",
  "basic.deposit": "Deposit",
  "basic.percentage": "%",
  "basic.fixed_amount": "Fixed",
  "basic.consultation_email": "Consultation email",
  "basic.consultation_email_desc": "Email address for consultation requests",
  "basic.booking_url": "Booking URL",
  "basic.booking_url_desc": "External booking system URL",

  // Sizes
  "sizes.title": "Size Settings",
  "sizes.desc": "Set the estimated time per size category",
  "sizes.min_hours": "Min. hours",
  "sizes.max_hours": "Max. hours",
  "sizes.hours": "hours",

  // Complexity
  "complexity.title": "Complexity Multipliers",
  "complexity.desc": "Adjust the price factor based on design complexity",

  // Body parts
  "bodyparts.title": "Body Part Multipliers",
  "bodyparts.desc": "Some body parts are harder to tattoo",

  // Styles
  "styles.title": "Style Management",
  "styles.desc": "Activate or deactivate styles and adjust the price factor. Clients only see active styles.",

  // Theme
  "theme.title": "Color Theme",
  "theme.desc": "Customize the colors of your calculator to match your style",
  "theme.preview": "Preview",
  "theme.preview_desc": "This is how your calculator looks",
  "theme.hue": "Hue",
  "theme.saturation": "Saturation",
  "theme.lightness": "Lightness",
  "theme.presets": "Quick select",

  // Branding
  "branding.title": "Branding",
  "branding.desc": "Upload your logo and set your language and currency",
  "branding.logo": "Logo",
  "branding.logo_desc": "Upload your studio logo (max 2MB, PNG or JPG)",
  "branding.upload": "Upload logo",
  "branding.remove": "Remove",
  "branding.language": "Calculator language",
  "branding.currency": "Currency",
  "branding.qr_code": "QR Code",
  "branding.qr_desc": "Clients can scan this QR code to go directly to your calculator.",
  "branding.download_qr": "Download QR",

  // Subscription
  "sub.free": "Free",
  "sub.pro": "Pro",
  "sub.studio": "Studio",
  "sub.upgrade": "Upgrade",
  "sub.pro_feature": "Pro Feature",
  "sub.studio_feature": "Studio Feature",
  "sub.unlock_desc": "Upgrade your plan to unlock this feature.",

  // Auth
  "auth.login": "Log in",
  "auth.register": "Register",
  "auth.artist_name": "Artist name",
  "auth.email": "Email",
  "auth.password": "Password",
  "auth.submit_login": "Log in",
  "auth.submit_register": "Create account",
  "auth.switch_register": "No account yet? Register here",
  "auth.switch_login": "Already have an account? Log in here",
  "auth.back_calc": "← Back to price calculator",
  "auth.login_failed": "Login failed",
  "auth.wrong_credentials": "Incorrect email or password",
  "auth.account_exists": "Account already exists",
  "auth.already_registered": "This email is already registered. Try logging in.",
  "auth.account_created": "Account created!",
  "auth.check_email": "Check your email to confirm your account.",
  "auth.invalid_email": "Invalid email address",
  "auth.password_min": "Password must be at least 6 characters",
  "auth.name_min": "Name must be at least 2 characters",
  "auth.dashboard_subtitle": "Artist Dashboard",
  "auth.invite_invalid": "Invite invalid or expired",
  "auth.invite_expired": "Invite has expired",
  "auth.invite_accepted": "🎉 You have been added to the studio!",

  // Size labels
  "size.mini": "Mini (< 5 cm)",
  "size.small": "Small (5-10 cm)",
  "size.medium": "Medium (10-20 cm)",
  "size.large": "Large (20-35 cm)",
  "size.xl": "Extra Large (35+ cm)",

  // Complexity labels
  "complexity.simple": "Simple",
  "complexity.medium": "Medium",
  "complexity.detailed": "Detailed",

  // Body part labels
  "body.arm": "Arm (general)",
  "body.forearm": "Forearm",
  "body.upper_arm": "Upper arm",
  "body.leg": "Leg (general)",
  "body.thigh": "Thigh",
  "body.calf": "Calf",
  "body.back": "Back",
  "body.chest": "Chest",
  "body.ribs": "Ribs",
  "body.stomach": "Stomach",
  "body.hand": "Hand",
  "body.foot": "Foot",
  "body.neck": "Neck",
  "body.face": "Face",
  "body.shoulder": "Shoulder",
  "body.hip": "Hip",
  "body.finger": "Finger",
  "body.other": "Other",
  "body.selected": "Selected",
  "body.front": "Front",
  "body.back_view": "Back",
  "body.front_view": "Front",
  "body.back_view_btn": "Back",

  // Style labels
  "style.black_grey": "Black & Grey",
  "style.full_color": "Full Color",
  "style.realism": "Realism",
  "style.fine_line": "Fine Line / Minimal",
  "style.traditional": "Traditional",
  "style.neo_traditional": "Neo Traditional",
  "style.watercolor": "Watercolor",
  "style.tribal": "Tribal",
  "style.japanese": "Japanese",
  "style.geometric": "Geometric",
  "style.lettering": "Lettering",
  "style.script": "Script",
  "style.dotwork": "Dotwork",
  "style.mandala": "Mandala",
  "style.blackwork": "Blackwork",
  "style.illustrative": "Illustrative",
  "style.surrealism": "Surrealism",
  "style.ornamental": "Ornamental",
  "style.micro_realism": "Micro Realism",
  "style.trash_polka": "Trash Polka",
  "style.other": "Other",
  "style.chicano": "Chicano",
  "style.cyber_sigilism": "Cyber Sigilism",
  "style.patchwork": "Patchwork",
  "style.sketch": "Sketch",
  "style.abstract": "Abstract",
  "style.minimalist": "Minimalist",
  "style.american_traditional": "American Traditional",
  "style.new_school": "New School",
  "style.biomechanical": "Biomechanical",
  "style.dark_art": "Dark Art",
  "style.anime": "Anime",
  "style.kawaii": "Kawaii",
  "style.celtic": "Celtic",
  "style.polynesian": "Polynesian",
  "style.maori": "Maori",
  "style.haida": "Haida",
  "style.etching": "Etching",
  "style.woodcut": "Woodcut",
  "style.portrait": "Portrait",
  "style.pet_portrait": "Pet Portrait",
  "style.floral": "Floral",
  "style.botanical": "Botanical",
  "style.sacred_geometry": "Sacred Geometry",
  "style.horror": "Horror",
  "style.fantasy": "Fantasy",
  "style.mythology": "Mythology",
  "style.sleeve_art": "Sleeve Art",
  "style.hand_poke": "Hand Poke",
  "style.stick_and_poke": "Stick and Poke",
  "style.negative_space": "Negative Space",
  "style.white_ink": "White Ink",
  "style.uv_ink": "UV Ink",

  // Preset colors
  "color.gold": "Gold",
  "color.red": "Red",
  "color.blue": "Blue",
  "color.green": "Green",
  "color.purple": "Purple",
  "color.pink": "Pink",
  "color.turquoise": "Turquoise",
  "color.orange": "Orange",

  // WhatsApp Lead
  "wa.title": "Send to Artist via WhatsApp",
  "wa.subtitle": "Send your estimate directly to",
  "wa.name": "Your name (optional)",
  "wa.name_placeholder": "Your name",
  "wa.message": "Message (optional)",
  "wa.message_placeholder": "Describe your idea, inspiration or specifics...",
  "wa.photo": "Reference photo (optional)",
  "wa.upload_photo": "Upload photo",
  "wa.file_too_large": "File too large",
  "wa.summary": "Summary:",
  "wa.send": "Send via WhatsApp",
  "wa.generating_plan": "Generating your tattoo plan...",
  "info.location": "Location",
  "wa.sent_title": "Message sent!",
  "wa.sent_desc": "Your request has been sent to {name} via WhatsApp.",
  "wa.upload_failed": "Upload failed",
  "wa.upload_failed_desc": "Could not upload the reference photo. Please try again.",
  "wa.save_failed": "Save request failed",
  "wa.success": "Sent!",
  "wa.success_desc": "Your request has been created and WhatsApp has been opened.",
  "wa.error": "Something went wrong",
  "wa.error_desc": "Unknown error while sending. Check your internet connection and try again.",
  "wa.msg_header": "🖊 New Tattoo Request via Inkvaluator",
  "wa.msg_name": "Name",
  "wa.msg_price": "💰 Estimated price",
  "wa.msg_size": "📏 Size",
  "wa.msg_style": "🎨 Style",
  "wa.msg_placement": "📍 Placement",
  "wa.msg_complexity": "⚙️ Complexity",
  "wa.msg_message": "💬 Message",
  "wa.msg_details": "🔗 View all details",
  "wa.msg_greeting": "Hi {name}, I calculated a price estimate:",
  "wa.msg_name_label": "Name",
  "wa.msg_style_label": "🎨 Style",
  "wa.msg_bodypart_label": "📍 Body part",
  "wa.msg_size_label": "📏 Size",
  "wa.msg_complexity_label": "⚙️ Complexity",
  "wa.msg_time_label": "⏱ Estimated time",
  "wa.msg_total_label": "💰 Total",
  "wa.msg_deposit_label": "💳 Deposit",
  "wa.msg_details_label": "🔗 Details",

  // Lead View
  "lead.not_found": "Lead not found",
  "lead.invalid_link": "This link is invalid or expired.",
  "lead.back_home": "Back to Home",
  "lead.details": "Lead Details",
  "lead.title": "Tattoo Request",
  "lead.for": "For",
  "lead.customer": "Customer",
  "lead.estimated_price": "Estimated price",
  "lead.size": "Size",
  "lead.style": "Style",
  "lead.placement": "Placement",
  "lead.complexity": "Complexity",
  "lead.customer_message": "Message from customer",
  "lead.reference_photo": "Reference photo",

  // Consult CTA
  "consult.title": "Ready to start?",
  "consult.desc": "Request a consultation to discuss your tattoo and get a final price.",
  "consult.book": "Book consultation",
  "consult.email": "Request consultation",
  "consult.email_subject": "Consultation request via Inkvaluator",

  // PDF Export
  "pdf.title": "Price Estimate",
  "pdf.download": "Download PDF",
  "pdf.your_selection": "Your selection",

  // Admin
  "admin.title": "Super Admin",

  // Upgrade
  "upgrade.title": "Choose your Plan",
  "upgrade.free_desc": "Perfect to get started",
  "upgrade.pro_desc": "Everything for professionals",
  "upgrade.studio_desc": "For studios with multiple artists",
  "upgrade.your_plan": "Your plan",
  "upgrade.manage": "Manage subscription",
  "upgrade.per_month": "/month",
  "upgrade.monthly": "Monthly",
  "upgrade.yearly": "Yearly",
  "upgrade.mo": "mo",
  "upgrade.year": "year",
  "upgrade.cancel_anytime": "Cancel anytime. No hidden fees.",
  "billing.expires": "Expires",
  "billing.change": "Change",
  "billing.next_invoice": "Next invoice",
  "billing.invoice_history": "Invoice history",
  "upgrade.choose": "Choose your",
  "upgrade.plan_word": "Plan",
  "upgrade.subtitle": "From solo artist to full studio — pick the plan that fits your business.",
  "upgrade.your_plan_badge": "Your plan",
  "upgrade.perfect_start": "Perfect to get started",
  "upgrade.everything_pro": "Everything for professionals",
  "upgrade.for_studios": "For tattoo studios & teams",
  "upgrade.manage_sub": "Manage subscription",
  "upgrade.switch_pro": "Switch to Pro",
  "upgrade.upgrade_pro": "Upgrade to Pro",
  "upgrade.upgrade_studio": "Upgrade to Studio",
  "upgrade.current_plan": "Current plan",
  "upgrade.renews_on": "Renews on",
  "upgrade.cancel_anytime_long": "Cancel anytime. No hidden fees.",
  "upgrade.questions": "Questions? Reach out via the dashboard.",
  "upgrade.promo_placeholder": "Discount code (optional)",
  "upgrade.discount": "discount",

  // Analytics
  "analytics.title": "Analytics",
  "analytics.desc": "Insights into your calculator usage",
  "analytics.calculations": "Calculations",
  "analytics.total_calculations": "Total calculations",
  "analytics.consult_requests": "Consult requests",
  "analytics.conversion_rate": "Conversion rate",
  "analytics.this_month": "This month",
  "analytics.avg_price": "Avg. Estimated Price",
  "analytics.top_style": "Most Popular Style",
  "analytics.top_body_part": "Most Popular Body Part",
  "analytics.per_month": "Calculations per Month",
  "analytics.popular_styles": "Popular styles",
  "analytics.popular_body_parts": "Popular body parts",
  "analytics.avg_estimate_trend": "Average estimate trend",
  "analytics.style_distribution": "Style Distribution",
  "analytics.body_part_distribution": "Body Part Distribution",
  "analytics.no_data": "No data yet. Share your calculator to start collecting insights.",
  "analytics.directory_leads": "Directory Leads",
  "analytics.directory_leads_desc": "Clicks from the discover page",

  // Studio
  "studio.title": "Studio Management",
  "studio.team": "Team",
  "studio.team_members": "Team Members",
  "studio.invite": "Invite Artist",
  "studio.invite_desc": "Generate an invite link to add an artist to your studio.",
  "studio.role": "Role",
  "studio.members": "Members",
  "studio.branding": "Studio Branding",
  "studio.pending_invites": "Pending invites",
  "studio.no_members": "No team members yet. Invite artists to join.",
  "studio.generate_link": "Generate Link",
  "studio.email_optional": "Email (optional)",
  "studio.expires": "Expires",
  "studio.unknown": "Unknown",

  // Feature labels
  "feature.calculator": "Custom price calculator",
  "feature.qr": "QR code & shareable link",
  "feature.styles_free": "Unlimited styles from Master List",
  "feature.styles": "Unlimited styles & AI scanner",
  "feature.ai_scanner": "AI Scanner (incl. Body Placement)",
  "feature.whatsapp_leads": "WhatsApp Leads",
  "feature.logo_branding": "Custom Branding (Logo + 8 Presets)",
  "feature.google_reviews": "Google Review Sync",
  "feature.portfolio_showcase": "Portfolio Showcase",
  "feature.verified_badge": "'Verified' Artist Badge",
  "feature.referral_system": "Referral Program",
  "feature.logo": "Logo upload",
  "feature.theme": "Custom color theme",
  "feature.language": "Multi-language (17 languages)",
  "feature.currency": "Currency switch (13 currencies)",
  "feature.analytics": "Usage analytics",
  "feature.pdf": "PDF Export",
  "feature.whitelabel": "White-label branding",
  "feature.map_visibility": "Worldwide map visibility",
  "feature.multiartist": "Multi-artist management",
  "feature.centralanalytics": "Central analytics dashboard",
  "feature.teammanagement": "Team & role management",
  "feature.shared_branding": "Shared studio branding",

  // Style+City page titles
  "style_city.page_title": "Best {style} Tattoo Artists in {city}",
  "style_city.h1_prefix": "Best",
  "style_city.h1_suffix": "Tattoo Artists in",

  // Blog
  "blog.title": "Blog",
  "blog.no_posts": "No articles published yet.",
  "blog.seo_title": "Blog | Inkvaluator",
  "blog.seo_desc": "Tips, trends and insights about tattoo pricing and the tattoo industry.",
  "blog.read_more": "Read more",
  "blog.published_on": "Published on",
  "blog.back": "Back to blog",
  "blog.not_found": "Article not found",
  "blog.translation_notice": "This article is not yet available in your language. Showing the original version.",

  // Landing extras
  "landing.start_free_btn": "Start Free",
  "landing.upgrade_btn": "Upgrade",
  "landing.professionals": "For professionals",
  "landing.studios_teams": "For studios & teams",
  "landing.trust_bar_prefix": "Trusted by",
  "landing.trust_bar_suffix": "tattoo artists worldwide",
  "nav.discover": "Discover",
  "nav.calculator": "Calculator",
  "nav.artists": "Artists",
  "nav.my_calculator": "My Calculator",
  "nav.manage": "Manage",

  // Look & Feel
  "laf.title": "Look & Feel",
  "laf.desc": "Customize the look of your public calculator page.",
  "laf.color_mode": "Color mode",
  "laf.dark_mode": "Dark mode",
  "laf.light_mode": "Light mode",
  "laf.background": "Background",
  "laf.solid_color": "Solid color",
  "laf.image_url": "Image URL",
  "laf.preview": "Preview",
  "laf.calculator_preview": "Calculator Preview",
  "laf.new_layout": "New layout available!",
  "laf.new_layout_desc": "A new layout (v2) is available with improved styling. Want to update?",
  "laf.update": "Update",
  "laf.saved": "Look & Feel saved",
  "laf.reset_success": "Theme reset to default",
  "admin.stripe_managed":
    "Subscriptions are automatically managed via Stripe — changes are visible after the next webhook sync",
  "calc.view_tattoo_plan": "View your Tattoo Plan ✓",
  "wa.plan_message": "Here is my Tattoo Plan:",
  "lead.tattoo_plan": "Tattoo Plan",
  "lead.made_with": "Made with Inkvaluator",
  "lead.contact_artist": "Contact {artist}",
  "lead.share": "Share",
  "lead.share_title": "My Tattoo Plan",
  "lead.link_copied": "Link copied!",
  "lead.deposit_label": "Deposit",
  "lead.pay_deposit": "Pay deposit {amount}",
  "lead.deposit_request_title": "Request deposit arrangement",
  "lead.deposit_request_desc": "Ask the artist if a deposit of ~{amount} is possible to secure the appointment.",
  "lead.deposit_request_wa": "Send request via WhatsApp",
  "lead.for_you": "You",
  "lead.for_label": "For",
  "lead.back_to_calculator": "Back to calculator",
  "lead.session_duration": "Session duration",
  "lead.session_estimate": "~{hours} hrs",
  "lead.deposit_wa_msg":
    "Hello {artist}, I viewed my Tattoo Plan on Inkvaluator. Would you be open to a deposit arrangement of ~{amount} to secure the appointment?\nPlan: {url}",
  "blog.no_translation": "No translation available",
  "flash.tab": "Flash",
  "flash.upload": "Upload flash design",
  "flash.title": "Title",
  "flash.price": "Price",
  "flash.size": "Size (cm)",
  "flash.available": "Available",
  "flash.featured": "Featured",
  "flash.limit_used": "of {max} flash designs used",
  "flash.delete_confirm": "Delete flash design?",
  "flash.reserve_whatsapp": "Reserve via WhatsApp",
  "flash.whatsapp_msg": "I'm interested in your flash tattoo:",
  "flash.matching_designs": "Matching flash designs",
  "flash.only_with_flash": "Only artists with flash designs",
  "groei.score": "Profile score",
  "groei.views": "profile views",
  "groei.sessions": "calculator sessions",
  "groei.leads": "leads",
  "groei.this_week": "this week",
  "groei.improve": "Improve your profile",
  "groei.add_photos": "Add more portfolio photos",
  "groei.add_whatsapp": "Set up your WhatsApp number",
  "groei.add_bio": "Write a longer bio",
  "groei.add_avatar": "Upload a profile photo",
  "groei.add_rate": "Set your hourly rate",
  "groei.add_city": "Add your city",
  "groei.get_leads": "Share your calculator to get leads",
  "groei.collapse": "Collapse",
  "groei.expand": "Expand",
  "groei.tip_portfolio": "Artists with 6+ photos get 3x more calculator sessions",
  "groei.tip_whatsapp": "Clients with a WhatsApp button convert 5x better",
  "groei.tip_leads": "Share your calculator link to receive your first lead",
  "groei.tip_avatar": "A profile photo increases your credibility",
  "groei.tip_action_photos": "Add photos",
  "groei.tip_action_whatsapp": "Set up WhatsApp",
  "groei.tip_action_share": "Share calculator",
  "groei.tip_action_avatar": "Upload photo",
  "milestone.eerste_lead": "First lead received!",
  "milestone.tien_leads": "10 leads reached!",
  "milestone.profiel_compleet": "Profile 100% complete!",
  "milestone.eerste_flash": "First flash design uploaded!",
  "milestone.profile_complete": "🎉 Profile complete! Your profile now ranks at maximum in search results.",
  "groei.completeness_tip_prefix": "Tip:",
  "owner_hint.incomplete": "Your profile is {score}% complete",
  "owner_hint.complete": "Profile complete ✓",
  "owner_hint.cta": "Complete it →",
  "wa.name_required": "Name is required",
  "wa.plan_message_full": "View my full Tattoo Plan: {url}",
  "laf.presets": "Premium Theme Presets",
  "laf.font_selector": "Calculator Font",
  "laf.border_color": "Border & Accent Color",
  "laf.glow_color": "Glow / Shadow Effect",
  "laf.glow_preview": "Glow Preview",
  "laf.overlay_opacity": "Overlay Opacity",
  "laf.overlay_opacity_desc": "Darken the background image for better text readability.",
  "laf.bg_attachment": "Background Scroll",
  "laf.bg_fixed": "Fixed (Parallax)",
  "laf.bg_scroll": "Scroll",

  // ConsultSettings
  "consult_settings.title": "Consultation Settings",
  "consult_settings.desc": "Configure how clients can request a consultation from your calculator page.",
  "consult_settings.booking_url_label": "Booking URL",
  "consult_settings.booking_url_placeholder": "https://calendly.com/your-name",
  "consult_settings.booking_url_desc": "Link to your booking system (Calendly, Acuity, etc.)",
  "consult_settings.email_label": "Consultation email",
  "consult_settings.email_placeholder": "bookings@yourstudio.com",
  "consult_settings.email_desc": "Fallback email if no booking URL is set",

  // OnboardingWizard
  "onboard.step_minimum": "Minimum rate",
  "onboard.step_hourly": "Hourly rate",
  "onboard.step_sizes": "Confirm sizes",
  "onboard.step_styles": "Set styles",
  "onboard.step_logo": "Upload logo",
  "onboard.step_finish": "Calculator ready!",
  "onboarding.verified_found": "GGD-verified profile found",
  "onboarding.welcome_back": "Welcome back",
  "onboarding.check_details": "We found your studio. Please verify the details are correct.",
  "onboarding.confirm_studio": "Yes, this is my studio",
  "onboarding.not_your_studio": "Not your studio? Contact us at",
  "onboarding.ggd_verified_text": "GGD permit verified — this studio is registered with RIVM",
  "onboarding.set_rates": "Set your rates",
  "onboarding.rates_desc": "Clients will see a fair price estimate based on your rates. You can always adjust this.",
  "onboarding.hourly_rate": "Hourly rate",
  "onboarding.minimum_price": "Minimum price",
  "onboarding.market_avg": "Market average in",
  "onboarding.per_hour": "per hour",
  "onboarding.minimum": "minimum",
  "onboarding.your_styles": "Your specialties",
  "onboarding.styles_desc": "Clients search by style. Select what you offer.",
  "onboarding.styles_skip": "Skip if you offer all styles",
  "onboarding.activate": "Activate profile",
  "onboarding.creating": "Creating your profile...",
  "onboarding.adjust_later": "You can always adjust this in your dashboard",
  "onboarding.welcome": "Welcome to Inkvaluator",
  "onboarding.welcome_desc": "Your profile will be live in 3 minutes. We set up your calculator so clients can request a price estimate right away.",
  "onboarding.your_city": "What city do you work in?",
  "onboarding.city_placeholder": "e.g. Amsterdam",
  "onboarding.city_hint": "This helps clients in your area find you",
  "onboarding.get_started": "Get started",
  "onboarding.profile_live": "Your profile is live! 🎉",
  "onboarding.profile_live_desc": "Clients can now request a price estimate via your unique link. Share it with your next client.",
  "onboarding.your_link": "Your calculator link",
  "onboarding.copy": "Copy",
  "onboarding.copied": "Copied",
  "onboarding.to_dashboard": "Go to dashboard",
  "onboarding.view_profile": "View my profile",
  "onboarding.styles_selected": "style(s) selected",
  "onboarding.back": "Back",
  "onboarding.next": "Next",
  "onboarding.wait": "Just a moment...",

  // ProfileSettings
  "profile.identity": "Identity",
  "profile.identity_desc": "Your name and studio name as shown on your public page.",
  "profile.studio_name": "Studio name",
  "profile.studio_placeholder": "E.g. Ink House Amsterdam",
  "profile.location": "Location",
  "profile.location_desc": "Your address for the directory and route planners.",
  "profile.studio_address_info": "Your studio address is automatically inherited. Fill in below to override.",
  "profile.street": "Street and number",
  "profile.postal_code": "Postal code",
  "profile.city": "City",
  "profile.contact_socials": "Contact & Socials",
  "profile.contact_desc": "How clients can reach and find you.",
  "profile.phone_label": "Phone / WhatsApp",
  "profile.phone_hint": "This number is used for the WhatsApp button on your calculator.",
  "profile.booking_hint": "Link to your booking system (Calendly, Acuity, etc.)",
  "profile.calculator_language": "Calculator Language",
  "profile.calculator_language_desc": "The language your clients see on the calculator.",

  // AI Scanner
  "ai.file_too_large": "File too large",
  "ai.max_size": "Max 10MB",
  "ai.rate_limit": "Limit reached",
  "ai.rate_limit_desc": "Max 3 scans per 24 hours.",
  "ai.scan_complete": "✨ AI Scan complete",
  "ai.scan_applied": "The calculator has been automatically adjusted.",
  "ai.scan_failed": "Scan failed",
  "ai.try_again": "Please try again",
  "ai.title": "AI Scanner",
  "ai.desc": "Upload a photo and let AI determine the settings",
  "ai.upload_prompt": "Take a photo or upload a reference image",
  "ai.analyzing": "AI is analyzing...",
  "ai.result_title": "AI Result",
  "ai.result_size": "Size",
  "ai.result_complexity": "Complexity",
  "ai.result_style": "Style",
  "ai.result_color": "Color",
  "ai.yes": "Yes",
  "ai.no": "No",
  "ai.choose_other": "Choose another photo",
  "ai.phase_detecting": "Detecting complexity...",
  "ai.phase_style": "Analyzing style...",
  "ai.phase_placement": "Detecting body placement...",
  "ai.phase_finalizing": "Finalizing estimate...",
  "ai.result_placement": "Placement",

  // Style+City pages
  "style_city.meta_desc": "Find {count} {style} tattoo artists in {city}. Compare prices and book your session.",
  "style_city.tattoo_in": "Tattoo in",
  "style_city.subtitle": "Browse {count} verified {style} artists in {city}.",
  "style_city.no_artists": "No {style} artists found in {city} yet.",
  "style_city.all_in_city": "All artists in {city}",
  "city.artists_in": "artists in",

  // Embed badge
  "embed.verified_badge": "Verified Badge",
  "embed.badge_desc":
    "Place this badge on your website to show you're a verified Inkvaluator artist. Links back to your profile.",

  // Analytics upsell
  "analytics.upsell_title": "Regional Style Opportunities",
  "analytics.upsell_desc": "Popular styles in your region that you haven't activated yet — potential upsell!",
  "analytics.upsell_demand": "{count} calculations in your region",
  "analytics.upsell_activate": "Activate →",

  // Coach Advice
  "analytics.coach_title": "Business Coach",
  "analytics.coach_desc": "Personalised growth tips based on your data",
  "analytics.coach_missing_style": "You're missing leads for {style} in {city}. Consider activating this style!",
  "analytics.coach_low_conversion": "Your conversion rate is {rate}%. Try adding more contact channels to boost it.",
  "analytics.coach_no_consults":
    "You have {calc} calculations but 0 consult requests. Make sure your WhatsApp or email is set up!",
  "analytics.coach_growing": "Great job! Your calculations grew {pct}% this month compared to last month.",
  "analytics.coach_top_style": "Your most requested style is {style}. Highlight it in your portfolio!",

  // Live ticker
  "discover.live_ticker": "Someone in {city} just calculated a price for a {style} tattoo",

  // Landing
  "landing.calculate_price": "Calculate Price",

  // Success page
  "success.welcome": "Welcome to the Club,",
  "success.desc": "Your {plan} features are activated immediately. Let's get started.",
  "success.active": "Active — all features unlocked",
  "success.syncing": "Activating your plan…",
  "success.setup_whatsapp": "Set up WhatsApp Leads",
  "success.go_dashboard": "Go to Dashboard",
  "success.view_details": "View plan details",

  // Discover
  "discover.subtitle": "Discover Studios & Artists",
  "discover.title": "Discover",
  "discover.seo_title": "Find a Tattoo Artist Near You | Inkvaluator",
  "discover.seo_desc": "Discover tattoo artists near you. Browse portfolios, compare prices and get an instant price estimate.",
  "discover.title_accent": "Artists",
  "discover.description": "Find tattoo artists and studios near you. View their price calculator and get in touch.",
  "discover.search_placeholder": "Search by city, artist or studio...",
  "discover.map_title": "Artists on the map",
  "discover.hide_map": "Hide map",
  "discover.show_map": "Show map",
  "discover.map_loading": "Loading map...",
  "discover.no_artists": "No artists found",
  "discover.no_artists_for": 'No artists found for "{query}".',
  "discover.playground": "Playground",
  "discover.artist_login": "Artist Login",
  "discover.call": "Call",
  "discover.route": "Route",
  "discover.view_calculator": "View Calculator",

  // Footer
  "footer.tagline": "The smartest tattoo price calculator for artists and clients.",
  "footer.links": "Links",
  "footer.discover_artists": "Discover Artists",
  "footer.artist_login": "Artist Login",
  "footer.blog": "Blog",
  "footer.privacy": "Privacy Policy",
  "footer.app": "App",
  "footer.coming_soon": "Coming soon on:",
  "footer.built_with": "Built with Lovable",
  "footer.copyright": "© {year} InkValuator. All rights reserved.",
  "footer.language_label": "Language & App",
  "footer.pwa_badge": "PWA optimized (click for info)",
  "footer.all_rights": "All rights reserved.",
  "footer.install_title": "Install",
  "footer.install_desc":
    "Use Inkvaluator as a real app on your phone. Fast, secure and without downloading from the store.",
  "footer.install_ios_title": "iPhone / iPad (iOS)",
  "footer.install_ios_desc": 'Tap the Share button at the bottom and choose "Add to Home Screen".',
  "footer.install_android_title": "Android",
  "footer.install_android_desc": 'Tap the three dots in the top right and choose "Install app".',
  "footer.install_ok": "Got it!",

  // Unsaved changes
  "dash.unsaved_changes": "You have unsaved changes",

  // Embed
  "embed.title": "Embed Widget",
  "embed.desc": "Place your calculator on your own website with an iframe or floating button.",
  "embed.iframe_desc":
    "Paste this in your HTML to display the calculator as an embedded element. The navigation bar and footer are hidden automatically.",
  "embed.floating_desc":
    "Paste this just before the </body> tag. A golden button will appear at the bottom right that opens your calculator in a modal.",
  "embed.copy_code": "Copy Code",

  // Demo
  "demo.subtitle": "Playground",
  "demo.title": "Try the",
  "demo.title_accent": "Calculator",
  "demo.description":
    "Adjust the variables and discover how the tattoo price calculator works. Set your own hourly rate and start costs.",
  "demo.your_rates": "Your rates",
  "demo.rates_desc": "Adjust the sliders to experiment with different pricing models",
  "demo.hourly_rate": "Hourly rate",
  "demo.start_cost": "Fixed start costs",
  "demo.size": "Size",
  "demo.complexity": "Complexity",
  "demo.placement": "Placement",
  "demo.estimated_price": "Estimated price",
  "demo.est_time": "Estimated time (incl. detail)",
  "demo.start_costs": "Start costs",
  "demo.total_multiplier": "Total multiplier",
  "demo.disclaimer": "This is a demo. The actual price depends on the artist and specific design.",
  "demo.artist_cta": "Are you an artist?",
  "demo.artist_cta_desc": "Use this tool on your own site and let clients make an estimate directly.",
  "demo.register_free": "Register for free",
  "demo.find_studio": "Find a studio nearby",
  "demo.footer_note": "This is a demo with standard rates. The real price is calculated via the calculator of an artist in your city.",
  "demo.view_artists": "View artists",

  // City landing
  "city.title": "Tattoo artists in {city}",
  "city.description": "Find tattoo artists and studios in {city}. Compare prices and book a consultation.",
  "city.no_artists": "No artists found in {city}.",
  "city.discover_all": "Discover all artists",
  "city.back": "All cities",
  "city.avg_rate": "Average hourly rate: {rate}.",
  "city.market_indicator": "Market indication for {city}",
  "city.meta_desc":
    "Find the best tattoo artists in {city}. Average hourly rate in this city: {rate}. Calculate your price instantly with local specialists.",

  // Map
  "map.view_calculator": "View Calculator",
  "map.unknown_location": "Unknown location",
  "map.artists_count": "artists",
  "map.world_title": "Artists",
  "map.world_accent": "Worldwide",
  "map.in_countries": "in {count} countries",
  "map.filtered_by": "Filtered by",
  "map.clear_filter": "Clear filter",

  // SEO
  "seo.landing_title": "Inkvaluator – Tattoo Price Calculator",
  "seo.landing_desc":
    "Calculate your tattoo price instantly based on style, size and placement. Find tattoo artists near you.",
  "seo.calculator_title": "Tattoo Price Calculator",
  "seo.calculator_desc":
    "Get an instant price estimate for your tattoo from {artist} based on craftsmanship, style and placement.",
  "seo.profile_desc_city": "View the profile of",
  "seo.in_city": "in",
  "seo.from_price": "From",
  "seo.calculator_cta": "Calculate your tattoo price estimate instantly.",
  "calc.next_slot": "Next slot",
  "calc.book_appointment": "Book appointment",
  "calc.open_for_bookings": "Open for bookings",

  // Footer extra
  "footer.terms": "Terms of Service",
  "footer.support": "Support",
  "footer.how_it_works": "How it works",

  // How It Works page
  "hiw.seo_title": "AI Tattoo Price Calculator – Automate Your Studio | Inkvaluator",
  "hiw.seo_desc":
    "Calculate tattoo prices with AI. Automate price estimates, generate smart leads and boost your studio with professional branding.",
  "hiw.hero_title": "Focus on the Ink, Not the Invoice",
  "hiw.hero_p1":
    "Are you spending more time answering DMs about prices than drawing your next masterpiece? Let's be honest: you're a tattoo artist, not a customer service agent.",
  "hiw.hero_p2":
    'Every "How much does this cost?" question you have to answer manually is time you\'re not spending on your art.',
  "hiw.hero_p3":
    "Inkvaluator is the digital assistant that filters your business. While you're in the zone, the tool calculates accurate price estimates for your clients, gathers all the necessary info and drops a ready-made request straight into your WhatsApp.",
  "hiw.hero_p4":
    'No more "price shoppers" who have no budget after chatting for three hours. Only serious leads, professional quotes and branding that shows you take your craft seriously.',
  "hiw.hero_cta": "Ready to reclaim your chair time?",
  "hiw.faq_title": "Frequently Asked Questions",
  "hiw.faq_cat1": "The Basics: How does this help me?",
  "hiw.faq_q1": "Is this yet another complicated app?",
  "hiw.faq_a1":
    "No. It's a super-fast calculator you put in your Instagram bio or embed on your site. You set your rules once (hourly rate, start costs, styles) and the tool does the rest.",
  "hiw.faq_q2": "How does it concretely save me time?",
  "hiw.faq_a2":
    'Instead of asking ten times: "How big? Where on your body? What style?" — you get one WhatsApp message with all of that already filled in, including the reference photo and the price the client has already accepted.',
  "hiw.faq_cat2": "Pricing & Settings",
  "hiw.faq_q3": "Can I set my own prices?",
  "hiw.faq_a3":
    "Absolutely. You're the boss. You set your own hourly rate and determine what you charge for different sizes and complexities. The tool calculates exactly the way you would at your workstation.",
  "hiw.faq_q4": "What if a tattoo is much more complex than average?",
  "hiw.faq_a4":
    "Our built-in AI Scanner recognises the details in a photo. It sees the difference between a simple fine-line text and a hyper-realistic portrait and adjusts the price estimate accordingly.",
  "hiw.faq_cat3": "Appearance & Branding",
  "hiw.faq_q5": "Doesn't it look like a generic tool?",
  "hiw.faq_a5":
    "Definitely not. With the Studio plan the tool is fully white-label. That means: your logo, your colours and your style. There are 8 professional theme presets ready for you (from Luxury Dark to Modern Light) so the calculator seamlessly matches your shop.",
  "hiw.faq_q6": "How professional does this look to my client?",
  "hiw.faq_a6":
    'Very professional. The client can download the quote as a sleek PDF. That builds trust and prevents disputes afterwards about "what we had agreed on".',
  "hiw.faq_cat4": "Growth & Reach",
  "hiw.faq_q7": "I want more clients from my area, does this help?",
  "hiw.faq_a7":
    "Yes! Inkvaluator has an interactive world map. Clients can search by country, province and city. The more you use the tool, the better your discoverability becomes for people looking for an artist in your area.",
  "hiw.backend_title": "Behind the Scenes: The Power of the Backend",
  "hiw.backend_intro":
    "For the tech lovers and control freaks: Inkvaluator is packed with advanced features that take your studio to the next level.",
  "hiw.card_pricing_title": "Dynamic Pricing",
  "hiw.card_pricing_text":
    "Adjust prices and Stripe Price IDs via the admin module without touching a single line of code. Everything syncs instantly with your checkout.",
  "hiw.card_ai_title": "AI Vision Integration",
  "hiw.card_ai_text":
    "The AI Scanner uses image analysis to translate patterns, colour usage and complexity into labour hours.",
  "hiw.card_stripe_title": "Stripe Customer Portal",
  "hiw.card_stripe_text":
    "A fully automated subscription system. Upgrading to Pro, downloading invoices or managing your subscription happens in a secure environment.",
  "hiw.card_analytics_title": "Analytics Dashboard",
  "hiw.card_analytics_text":
    "Get deep insight into your conversions. See how many people use your calculator and what your potential revenue is.",
  "hiw.card_blog_title": "Blog & SEO CMS",
  "hiw.card_blog_text": "Built-in system to publish articles that boost your Google discoverability.",
  "hiw.card_global_title": "Global Architecture",
  "hiw.card_global_text": "Fully multilingual support (NL/EN/etc.) where the client's preferred language is preserved.",
  "hiw.card_mobile_title": "Mobile Ready (PWA)",
  "hiw.card_mobile_text":
    "Inkvaluator is a Progressive Web App (PWA). Install the tool directly to your home screen via the browser. Lightning fast, secure and no storage space required.",
  "hiw.cta_title": "What are you waiting for?",
  "hiw.cta_accent": "Stop calculating, start creating.",
  "hiw.cta_button": "Try Inkvaluator for free",

  // Smart WhatsApp Templates
  "wa.template_high_greeting":
    "Hi {name}, great news! I've already calculated a detailed price estimate for my tattoo:",
  "wa.template_high_vault": "🔗 I saved this in my Tattoo Vault: {url}",
  "wa.template_low_greeting":
    "Hi {name}, I'm exploring options for a tattoo. Could you provide more details on availability and pricing?",
  "wa.template_low_note": "Based on the calculator, here's a rough idea:",
  "wa.followup_btn": "Follow up",
  "wa.followup_msg": "Hi! I'd like to follow up on my tattoo request. Is there any update?",

  // Vault Compare
  "vault.compare": "Compare",
  "vault.compare_title": "Compare Estimates",
  "vault.select_to_compare": "Select 2 items to compare",
  "vault.comparing": "Comparing",
  "vault.close_compare": "Close compare",

  // Team Leaderboard
  "analytics.team_title": "Team Leaderboard",
  "analytics.team_desc": "Performance ranking of artists in your studio",
  "analytics.team_calculations": "Calculations",
  "analytics.team_lead_value": "Lead Value",
  "privacy.seo_title": "Privacy Policy | Inkvaluator",
  "privacy.seo_desc":
    "Read the Inkvaluator privacy policy. We respect your privacy and protect your data in accordance with GDPR guidelines.",
  "privacy.title": "Privacy Policy",
  "privacy.updated": "Last updated:",
  "privacy.s1_title": "1. What data do we collect?",
  "privacy.s1_intro": "Inkvaluator collects data necessary for the functioning of the platform:",
  "privacy.s1_artists_label": "For Artists",
  "privacy.s1_artists":
    "Name, email address, studio name, address details (for the interactive world map) and portfolio information.",
  "privacy.s1_visitors_label": "For Visitors",
  "privacy.s1_visitors":
    "Data entered into the calculator (size, style, price indications) for generating estimates and analytical purposes.",
  "privacy.s1_leads_label": "Leads",
  "privacy.s1_leads": "When you contact an artist, your message and any reference photos are securely processed.",
  "privacy.s2_title": "2. Use of Data",
  "privacy.s2_intro":
    "Your privacy comes first. We never sell or rent personal data to third parties. We use data exclusively for:",
  "privacy.s2_matching": "Matching clients with the right tattoo artists via our map.",
  "privacy.s2_algorithms": "Continuously improving our pricing algorithms.",
  "privacy.s2_pwa": "The technical operation of the app as a Progressive Web App (PWA).",
  "privacy.s3_title": "3. International Legislation (GDPR)",
  "privacy.s3_intro": "In accordance with European legislation, you have the following rights:",
  "privacy.s3_access_label": "Right of access",
  "privacy.s3_access": "You can request which data we hold about you.",
  "privacy.s3_erasure_label": "Right to be forgotten",
  "privacy.s3_erasure": "You can request complete deletion of your account and data.",
  "privacy.s3_portability_label": "Data portability",
  "privacy.s3_portability": "You can request your data in a readable format.",
  "privacy.s4_title": "4. Cookies & PWA",
  "privacy.s4_text":
    "Inkvaluator uses functional caching (PWA) to enable offline use. We do not use tracking cookies or third-party advertising pixels.",
  "privacy.contact_title": "Questions about your privacy?",
  "privacy.contact_text": "Contact our data protection officer directly:",

  // Upgrade extras
  "upgrade.test_account_active": "Test account — features activated",

  // Terms of Service page
  "tos.seo_title": "Terms of Service | Inkvaluator",
  "tos.seo_desc":
    "Read the Terms of Service for Inkvaluator. Information about using our tattoo price calculator, subscriptions and liability.",
  "tos.title": "Terms of Service",
  "tos.updated": "Last updated:",
  "tos.s1_title": "1. Definitions",
  "tos.s1_platform": "Platform: Inkvaluator, a SaaS platform available via inkvaluator.com and associated subdomains.",
  "tos.s1_user": "User / Artist: Any tattoo artist or studio that creates an account on the Platform.",
  "tos.s1_visitor": "Visitor: Any person who uses the public calculator or discover section of the Platform.",
  "tos.s1_service":
    "Service: The functionalities offered by Inkvaluator, including the tattoo price calculator, lead management, AI scanner and white-label tools.",
  "tos.s2_title": "2. Use of the Calculator",
  "tos.s2_p1":
    "The price estimates generated by Inkvaluator are indicative only and do not constitute a binding offer, quote or price guarantee. The actual price of a tattoo is always determined by the artist, based on a personal consultation.",
  "tos.s2_p2":
    "Inkvaluator is not liable for decisions made based on generated price estimates, nor for any disputes between artists and their clients regarding prices.",
  "tos.s3_title": "3. Subscriptions & Payments",
  "tos.s3_intro": "Inkvaluator offers three tiers: Free, Pro and Studio.",
  "tos.s3_billing": "Paid subscriptions are billed monthly via Stripe and are automatically renewed.",
  "tos.s3_cancel":
    "You can cancel your subscription at any time via the Stripe customer portal in your dashboard. After cancellation you retain access to paid features until the end of the current billing period.",
  "tos.s3_refund":
    "Refunds are not possible for billing periods that have already commenced, unless otherwise required by applicable law.",
  "tos.s3_price_change":
    "Inkvaluator reserves the right to change prices. Any price changes will be communicated at least 30 days in advance.",
  "tos.s4_title": "4. Data Ownership",
  "tos.s4_p1":
    "All data entered by an Artist (pricing settings, profile data, logos) remains the property of the Artist. Inkvaluator obtains a limited licence to use this data for delivering the Service.",
  "tos.s4_p2":
    "Anonymous, aggregated usage data (such as the number of calculations per region) may be used by Inkvaluator for statistical analysis and product improvement, without being traceable to individual users.",
  "tos.s5_title": "5. Liability",
  "tos.s5_p1":
    'Inkvaluator provides the Service "as is". We make every effort to ensure maximum availability and accuracy, but provide no guarantee of uninterrupted or error-free operation of the Platform.',
  "tos.s5_indirect": "Inkvaluator is not liable for indirect damages, lost profits or consequential damages.",
  "tos.s5_cap":
    "Inkvaluator's total liability is limited to the amount the User has paid in subscription fees in the three months preceding the claim.",
  "tos.s5_content": "Inkvaluator is not responsible for the content that artists place on their profile.",
  "tos.s6_title": "6. Account & Termination",
  "tos.s6_credentials": "Users are responsible for keeping their login credentials confidential.",
  "tos.s6_suspend":
    "Inkvaluator reserves the right to suspend or delete accounts in case of abuse, fraud or breach of these terms.",
  "tos.s6_delete": "Upon termination of an account, personal data will be deleted in accordance with the",
  "tos.s6_privacy_link": "Privacy Policy",
  "tos.s7_title": "7. Intellectual Property",
  "tos.s7_p1":
    'All intellectual property rights to the Platform, software, design and the brand name "Inkvaluator" belong to Inkvaluator. It is not permitted to copy, reverse-engineer or use parts of the Platform for competing services.',
  "tos.s8_title": "8. Applicable Law",
  "tos.s8_p1":
    "These Terms of Service are governed by Dutch law. Disputes will be exclusively submitted to the competent court in the Netherlands.",
  "tos.contact_title": "Questions about these terms?",
  "tos.contact_text": "Contact us at:",

  // Support page
  "support.seo_title": "Support | Inkvaluator",
  "support.seo_desc": "Need help with Inkvaluator? Check our frequently asked questions or contact our support team.",
  "support.title": "Support",
  "support.faq_title": "Frequently Asked Questions",
  "support.faq_q1": "How do I set my rates?",
  "support.faq_a1":
    'Go to your Dashboard and click "Pricing". Here you can set your hourly rate, minimum price, deposit and design costs. You can also configure advanced settings for size, complexity, body part and style multipliers.',
  "support.faq_q2": "How does the Stripe integration for subscriptions work?",
  "support.faq_a2":
    'When you upgrade to Pro or Studio, you are redirected to a secure Stripe checkout. After payment your subscription is automatically activated. You can manage your subscription (cancel, view invoices) via the "Manage Subscription" button in your dashboard.',
  "support.faq_q3": "What is the WhatsApp module?",
  "support.faq_a3":
    "With the WhatsApp module (available from Pro) visitors can contact you directly via WhatsApp after calculating a price. You configure your phone number in the dashboard and Inkvaluator automatically generates a message with the tattoo details.",
  "support.faq_q4": "How do I share my calculator with clients?",
  "support.faq_a4":
    'In your dashboard under "Share" you will find a direct link to your personal calculator, a QR code and an embed code to place the calculator on your own website.',
  "support.faq_q5": "Can I cancel my subscription?",
  "support.faq_a5":
    'Yes, you can cancel your subscription at any time via "Manage Subscription" in your dashboard. After cancellation you retain access to all Pro features until the end of your current billing period.',
  "support.faq_q6": "How does the AI Image Scanner work?",
  "support.faq_a6":
    "The AI Scanner (Pro feature) analyses a photo of a tattoo design and automatically estimates size, complexity and style. This helps visitors get a price estimate faster.",
  "support.faq_q7": "I found a bug, what now?",
  "support.faq_a7":
    "Send an email to support@inkvaluator.com with a description of the problem, screenshots if possible, and the device/browser you are using. We will respond as quickly as possible.",
  "support.contact_title": "Contact",
  "support.contact_text": "Question not listed? Contact our team directly:",
  "support.form_name": "Name",
  "support.form_name_placeholder": "Your name",
  "support.form_email": "Email",
  "support.form_message": "Message",
  "support.form_message_placeholder": "Describe your question or problem...",
  "support.form_submit": "Send message",
  "support.form_subject": "Support request from {name}",
  "support.toast_title": "Email client opened",
  "support.toast_desc": "Your email application is being opened with your message.",

  // API limits
  "api.limit_reached": "You have reached the daily limit for AI scans. Create an account or try again tomorrow.",

  // Landing FAQ
  "landing.faq_title": "How It Works",
  "landing.faq_subtitle": "Everything you need to know about Inkvaluator",
  "landing.faq_q1": "How does the price calculator work?",
  "landing.faq_a1":
    "You configure your hourly rate, minimum price, and multipliers for size, complexity, body placement, and style. Clients visit your unique link and get an instant, accurate price estimate — no back-and-forth needed.",
  "landing.faq_q2": "Is Inkvaluator really free?",
  "landing.faq_a2":
    "Yes! The Free plan includes a fully functional calculator, QR code, and all tattoo styles. Upgrade to Pro for custom branding, analytics, AI scanner, and multi-language support.",
  "landing.faq_q3": "How does the AI Scanner work?",
  "landing.faq_a3":
    "Upload a reference photo and our AI analyzes size, complexity, color, and style in seconds. The calculator automatically adjusts based on the analysis — saving you time on every estimate.",
  "landing.faq_q4": "Can clients contact me directly?",
  "landing.faq_a4":
    "Absolutely. After calculating a price, clients can send a pre-filled WhatsApp message with all tattoo details, including reference photos. You receive a ready-to-go lead with no effort.",
  "landing.faq_q5": "What about multi-language support?",
  "landing.faq_a5":
    "Inkvaluator supports 17 languages and 13 currencies. Your calculator automatically adapts to your client's language and local currency, making it perfect for international artists.",
  "landing.faq_q6": "How do I get more clients?",
  "landing.faq_a6":
    "Pro and Studio artists appear on our interactive world map and discover page. Clients can find you by city, country, or style — boosting your visibility and bringing in organic leads.",

  // Account Security / MFA
  "profile.mfa_title": "Account Security",
  "profile.mfa_desc": "Protect your account with two-factor authentication.",
  "profile.mfa_totp_title": "Authenticator App (TOTP)",
  "profile.mfa_enable": "Enable 2FA",
  "profile.mfa_scan_qr": "Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)",
  "profile.mfa_secret_key": "Secret key (manual entry)",
  "profile.mfa_enter_code": "Enter the 6-digit code from your app",
  "profile.mfa_verify": "Verify",
  "profile.mfa_authenticator": "Authenticator App",
  "profile.mfa_active": "Active",
  "profile.mfa_enabled_success": "✅ Two-factor authentication enabled!",
  "profile.mfa_disabled_success": "Two-factor authentication disabled.",
  "profile.mfa_enroll_error": "Could not enable 2FA",
  "profile.mfa_verify_error": "Verification failed. Check your code and try again.",
  "profile.mfa_unenroll_error": "Could not disable 2FA",
  "profile.recently_updated": "Recently updated",

  // Admin Price Management (super admin only)
  "admin.price_2fa_required": "2FA verification required",
  "admin.price_2fa_required_desc": "You must enter your authenticator code before you can change prices.",
  "admin.price_2fa_verify": "Verify",
  "admin.price_2fa_verified": "✅ 2FA verified — price management unlocked",
  "admin.price_2fa_dialog_title": "2FA Verification",
  "admin.price_2fa_dialog_desc": "Enter the 6-digit code from your authenticator app to unlock price management.",
  "admin.price_2fa_not_enabled": "⚠️ 2FA not enabled",
  "admin.price_2fa_not_enabled_desc": "You must first enable 2FA via Account Security.",
  "admin.price_2fa_failed": "Verification failed",
  "admin.price_2fa_success": "✅ Verified",
  "admin.price_2fa_success_desc": "2FA verification successful. You can now manage prices.",
  "admin.price_eur_title": "Change EUR Base Prices",
  "admin.price_eur_desc": "Adjust EUR prices. All other currencies are automatically converted and synced.",
  "admin.price_pro_monthly": "Pro monthly (€)",
  "admin.price_studio_monthly": "Studio monthly (€)",
  "admin.price_pro_yearly": "Pro yearly (€)",
  "admin.price_studio_yearly": "Studio yearly (€)",
  "admin.price_update_button": "Update prices & auto-sync",
  "admin.price_update_hint":
    "⚡ Only fill in the prices you want to change. Empty fields are skipped. After updating, all 14 currencies are automatically converted with nicely rounded amounts.",
  "admin.price_update_success": "✅ Prices updated & synced",
  "admin.price_update_fill_one": "Fill in at least one price",
  "admin.price_manual_sync_title": "Manual Currency Sync",
  "admin.price_manual_sync_desc":
    "Recalculate currency prices without changing the EUR price (e.g. when exchange rates change).",
  "admin.price_preview": "Preview (Dry Run)",
  "admin.price_sync": "Sync & Create Prices",
  "admin.status_new": "New",
  "admin.status_emailed": "Emailed",
  "admin.status_registered": "Registered",
  "admin.status_archived": "Archived",
  "admin.status_all": "All",
  "admin.tips_loading": "Loading...",
  "admin.tips_empty": "No tips received yet.",
  "admin.tips_status_changed": "Status changed to",
  "admin.tips_status_error": "Error changing status",
  "admin.tips_copied": "Outreach message copied!",
  "admin.tips_mail_artist": "Email artist",
  "admin.tips_outreach": "Outreach",
  "admin.tips_copied_label": "Copied",
  "admin.tips_tipped_by": "Tipped by:",
  "landing.hero_subline": "+ tattoo studios worldwide",
  "landing.trust_claimed": "Active artists",

  // Referral
  "referral.title": "Refer a Colleague",
  "referral.desc": "Invite a colleague to Inkvaluator and earn discount on your subscription for every sign-up.",
  "referral.generate": "Generate Referral Link",
  "referral.code_created": "Referral link created!",
  "referral.share_link": "Share this link with a colleague:",
  "referral.reward_info": "For each colleague that signs up via your link, you earn 10% discount on your next renewal.",
  "referral.invited": "Invited",
  "referral.earned": "Discount earned",
  "referral.redeemed": "Redeemed referrals",
  "referral.colleague_joined": "A colleague joined via your link",
  "referral.reward_applied": "Reward applied",
  "referral.pending_reward": "Pending",
  "referral.progress_label": "{n} more colleague(s) until your next 10% discount",
  "referral.max_reached": "🎉 Maximum 50% discount reached!",
  "referral.whatsapp_message": "Hey! I use Inkvaluator for my tattoo pricing. Try it for free: {link}",
  "referral.link_copied_ig": "Link copied — paste it in your Instagram bio or DM!",
  "referral.leaderboard_title": "Top Referrers",
  "referral.leaderboard_entry": "Artist from {city}",

  // PWA Install Banner
  "pwa.install_title": "Install as App",
  "pwa.install_benefit": "Use the app for faster AI scans",
  "pwa.install_button": "Install Now",
  "pwa.ios_instruction": 'Tap Share (□↑) then "Add to Home Screen"',
  "pwa.android_instruction": 'Tap the menu (⋮) and choose "Install app"',

  // Studio routing
  "studio.routed_to": "→ Routed to {name} based on style match",
  "referral.reward_generated": "Promo code generated for referral reward!",

  // Channel toggles
  "consult_settings.channels_title": "Lead Channels",
  "consult_settings.channels_desc": "Choose which contact methods are visible to clients on your calculator.",

  // Google Reviews & Studio Search
  "profile.google_reviews": "Google Reviews",
  "profile.google_reviews_desc": "Link your Google Business profile to show star ratings on your artist card.",
  "profile.google_place_id": "Google Place ID",
  "profile.google_place_id_hint":
    "Find your Place ID at developers.google.com/maps/documentation/places/web-service/place-id",
  "profile.search_studio": "Search your Studio",
  "profile.search_studio_placeholder": "Search by studio name...",
  "profile.search_studio_hint": "Search for your tattoo studio to automatically link your Google reviews.",
  "profile.studio_linked": "Studio successfully linked!",
  "profile.studio_not_found": "No studios found. Try a different search term.",

  // SW update
  "sw.update_available": "New version available. Click to refresh.",
  "sw.refresh": "Refresh",

  // Tip Your Artist
  "tip.title": "Tip Your Artist",
  "tip.desc": "Know a tattoo artist who should be on Inkvaluator? Tip them!",
  "tip.artist_name": "Artist name",
  "tip.artist_placeholder": "E.g. John Ink",
  "tip.instagram_label": "Instagram (optional)",
  "tip.whatsapp_label": "WhatsApp (optional)",
  "tip.your_name": "Your name (optional)",
  "tip.your_name_placeholder": "Your name",
  "tip.share_message":
    "Hi {artist}, I wanted to calculate my tattoo price with you on Inkvaluator, but couldn't find you! Check inkvaluator.com so I can see your prices. Greetings, {client}",
  "tip.copied_clipboard": "Message copied to clipboard! Paste it in their DM.",
  "tip.success_title": "Thank you!",
  "tip.success_desc": "You are helping {artist} professionalize. 🙏",

  // Pricing Education
  "pricing_edu.button": "How does this calculation work?",
  "pricing_edu.title": "How the Price is Calculated",
  "pricing_edu.intro":
    "The price of a tattoo is determined by several factors that together reflect the craftsmanship and time involved.",
  "pricing_edu.time_title": "Time & Size",
  "pricing_edu.time_desc":
    "Larger tattoos take more time. The artist's hourly rate is multiplied by the estimated hours for your size.",
  "pricing_edu.complexity_title": "Complexity & Detail",
  "pricing_edu.complexity_desc":
    "More detailed designs require more precision and time. A photorealistic portrait takes longer than a simple symbol.",
  "pricing_edu.placement_title": "Body Placement",
  "pricing_edu.placement_desc":
    "Some body parts are more sensitive or harder to reach (like ribs or hands), requiring extra care and time.",
  "pricing_edu.style_title": "Tattoo Style",
  "pricing_edu.style_desc":
    "Each style requires specific techniques. Realism and Japanese styles generally take more time than fine line.",
  "pricing_edu.disclaimer":
    "This is always an estimate. The final price is determined after a personal consultation with the artist.",

  // Total value counter
  "landing.total_value_prefix": "Already",
  "landing.total_value_suffix": "in tattoo art calculated",

  // Google sync
  "profile.sync_now": "Sync now",
  "profile.sync_success": "Google Reviews synced!",
  "profile.sync_error": "Could not sync reviews",

  // Profile contact / price / claim / sticky (Fase 1B)
  "profile.contact.call": "Call",
  "profile.contact.website": "Website",
  "profile.contact.route": "Directions",
  "profile.contact.book": "Book now",
  "profile.contact.whatsapp": "WhatsApp",
  "profile.portfolio.title": "Portfolio",
  "profile.portfolio.locked_title": "Portfolio visible after claiming",
  "profile.portfolio.locked_subtitle": "Add your work and let clients get to know you",
  "profile.portfolio.claim_cta": "Claim this profile",
  "profile.price.from": "Est. price: from",
  "profile.price.hourly": "/hr",
  "profile.price.average_label": "(national average)",
  "profile.price.calculate_cta": "Calculate your price →",
  "profile.claim.title": "Are you the owner?",
  "profile.claim.subtitle": "Claim this profile for free and receive leads.",
  "profile.claim.cta": "Claim for free →",
  "profile.sticky.calculate": "Calculate price",

  // Vault
  "vault.seo_title": "My Tattoo Plans | Inkvaluator",
  "vault.seo_desc": "View and manage your saved tattoo plans.",
  "vault.title": "My Tattoo",
  "vault.title_accent": "Plans",
  "vault.desc": "Your saved tattoo plans — stored locally on your device.",
  "vault.empty": "No plans saved yet. Use a calculator and save your result!",
  "vault.try_calculator": "Find an artist",
  "vault.items_saved": "estimates saved",
  "vault.clear_all": "Clear all",
  "vault.open_calculator": "Open",
  "vault.save_btn": "Save plan",
  "vault.saved_title": "Plan saved!",
  "vault.saved_desc": "Your estimate has been saved to your plans.",

  // Compare
  "compare.title": "Price Comparison",
  "compare.standard_project": "15cm Realism · Arm",
  "compare.no_pricing": "No pricing data",
  "compare.disclaimer": "Comparison based on a standard 15cm realism tattoo on the arm. Actual prices may vary.",
  "compare.compare_btn": "Compare",
  "compare.add": "Add to comparison",

  // Trending
  "trending.title": "Trending this week",

  // Verified quality
  "discover.verified_quality": "Meets Inkvaluator quality standards",

  // Admin Insights
  "admin.insights": "Insights",
  "admin.tips": "Artist Tips",
  "admin.potential_revenue": "Potential Revenue",
  "admin.wa_by_country": "WhatsApp Leads by Country",
  "admin.artist_growth": "Artist Growth (Weekly)",
  "admin.top_styles": "Top 5 Styles (Worldwide)",
  "admin.top_body_parts": "Top 5 Body Parts (Worldwide)",
  "admin.no_data": "No data yet",

  // Verified badge
  "admin.verified": "Verified",
  "admin.verified_removed": "Verified badge removed",
  "admin.verified_granted": "Verified badge granted",
  "discover.verified": "Inkvaluator Verified",

  // Tip management
  "admin.tip_all": "All",
  "admin.tip_new": "New",
  "admin.tip_emailed": "Emailed",
  "admin.tip_registered": "Registered",
  "admin.tip_archived": "Archived",
  "admin.tip_tipped_by": "Tipped by",
  "admin.tip_mail_artist": "Mail artist",
  "admin.tip_no_tips": "No tips received yet.",

  // Founder spots
  "landing.founder_spots_left": "spots left",

  // Auto-verified
  "verified.auto_eligible": "Qualifies for auto-verified",
  "verified.criteria": "Automatic criteria: onboarding complete, Google rating > 4.0, 10+ calculations",
  "verified.manual_override": "Manual override by admin",

  // Studio roles
  "studio.role_artist": "Artist",
  "studio.role_manager": "Manager",
  "studio.role_owner": "Owner",
  "studio.role_updated": "Role updated",
  "studio.role_desc_artist": "Can view leads only",
  "studio.role_desc_manager": "Can manage pricing settings",
  "studio.pending": "Pending",

  // Tax
  "checkout.tax_included": "Tax calculated automatically",
  "checkout.currency_auto": "Currency set based on your profile",

  // Claim CTA
  "claim.question": "Are you a tattoo artist in {city}? Claim your spot on the map!",
  "claim.cta": "Claim your spot",

  // Outreach
  "outreach.copy": "Copy outreach message",
  "outreach.copied": "Outreach message copied!",
  "outreach.generate": "Generate Outreach Message",

  // Portfolio showcase
  "discover.view_portfolio": "View Work",
  "discover.hide_portfolio": "Hide Work",

  // Admin notifications
  "admin.notifications": "Notifications",
  "admin.no_notifications": "No notifications.",
  "admin.mark_read": "Mark as read",
  "admin.mark_all_read": "Mark all as read",
  "admin.notif_new_artist": "New artist signed up",
  "admin.notif_new_tip": "New artist tip",
  "admin.notif_new_style": "New style suggestion",

  // Dashboard help tooltips
  "help.min_price":
    "This is the absolute minimum price for any session, regardless of size or complexity. Even a tiny tattoo won't go below this price.",
  "help.hourly_rate": "Your standard hourly rate forms the basis of all price calculations. Time × rate = base price.",
  "help.daily_rate":
    "Optional cap: if a session exceeds this amount based on hours, the daily rate is applied instead — a better deal for larger pieces.",
  "help.design_cost": "A fixed or hourly fee for designing and preparing the tattoo artwork before the session.",
  "help.deposit": "The deposit secures the appointment and is deducted from the total. Set to 0 to disable.",
  "help.multipliers":
    "Multipliers adjust the price based on style, complexity, and body placement. They work multiplicatively (e.g. ×1.2 for style × ×1.1 for complexity).",
  "help.lead_channels":
    "WhatsApp leads send all calculated details (style, size, price) directly to your phone. Email leads go to your consultation email.",
  "help.google_place_id":
    "Link your Google Business profile to automatically show your rating and reviews on your calculator and profile. Find your Place ID via Google Maps.",
  "help.sizes":
    "Define the estimated time range (min/max hours) for each size category. This directly affects the price calculation.",
  "help.complexity":
    "Adjust price multipliers for design complexity. More detailed designs require more time and skill.",
  "help.body_parts":
    "Some body parts are harder to tattoo due to sensitivity, curves, or difficult access. Adjust multipliers accordingly.",
  "help.styles":
    "Toggle styles on/off and set price multipliers. Specialized styles that require more expertise can have higher multipliers.",
  "help.logo": "Your logo appears on your calculator page and in the artist directory. Max 2MB, PNG/JPG/WebP.",
  "help.currency": "The currency shown on your calculator. Choose the currency your clients pay in.",
  "help.calculator_language": "The language of your public calculator page — what your clients see.",
  "help.dashboard_language": "The language of this management dashboard — only visible to you.",
  "help.artist_name": "Your professional name as shown on your calculator and in the artist directory.",
  "help.studio_name": "Optional studio name. Shown alongside your artist name on your profile.",
  "help.location": "Your studio location is used for the artist map, geo-pricing, and local SEO.",

  // Price Engine tab
  // Lead Quality Score
  "lead.quality_score": "Lead Quality",
  "lead.score_1": "Low priority",
  "lead.score_2": "Some interest",
  "lead.score_3": "Moderate",
  "lead.score_4": "High quality",
  "lead.score_5": "Premium lead",
  "lead.status_new": "New",
  "lead.status_contacted": "In contact",
  "lead.status_booked": "Booked",
  "lead.change_status": "Status",
  "lead.add_price": "Add final price",
  "push.test_body": "Push notifications are LIVE! You will now receive alerts for new leads.",
  "push.send_test": "Send Test Notification",
  "push.active_desc": "You receive notifications when high-quality leads arrive.",
  "push.blocked": "Notifications are blocked in your browser settings.",
  "push.enable_desc": "Get notified when high-quality leads come in.",
  "push.label": "Push notifications",
  "vault.live_status": "Live Status",
  "vault.status_new_desc": "Your request has been received",
  "vault.status_contacted_desc": "The artist has seen your message",
  "vault.status_booked_desc": "Your appointment is confirmed!",
  "vault.link_copied": "🔗 Link copied!",
  "analytics.actual_revenue": "Actual Revenue",
  "push.welcome_body": "Welcome! You will now receive alerts when high-quality leads arrive.",
  "push.denied_info": "Stay informed about new leads — Enable notifications in your browser settings.",
  "style_city.popular_styles": "Popular styles in {city}",

  // Verified tooltip
  "verified.tooltip": "Craftsmanship verified by Inkvaluator",

  // API diagnostics
  "api.google_denied": "Google Places API is not enabled. Contact your administrator.",
  "api.studio_search_error": "Could not search studios. Try again later.",

  // Social sharing
  "share.button": "Share",
  "share.title": "My Tattoo Price Estimate",
  "share.text": "My new tattoo at {artist} will cost approximately {price}. Check it out!",

  // Embed badge SEO
  "embed.badge_seo_hint": "💡 This badge creates a dofollow backlink to your Inkvaluator profile — great for SEO.",

  // Studio routing
  "studio.match_intro": "Based on your choice of",
  "studio.match_specialist": "we recommend our specialist",

  // Tab
  "tab.price_engine": "Price Engine",
  "tab.price_engine_desc": "Manage all price multipliers: sizes, complexity, body parts and styles.",

  // Analytics enhancements
  "analytics.revenue_funnel": "Estimated Revenue Funnel",
  "analytics.revenue_funnel_desc": "Total value of all calculations vs. actual WhatsApp leads.",
  "analytics.funnel_calculations": "All Calculations",
  "analytics.funnel_leads": "Actual Leads",
  "analytics.funnel_whatsapp": "WhatsApp Contacts",
  "analytics.lead_quality": "Lead Quality Distribution",
  "analytics.lead_quality_desc": "How your incoming leads score on our quality scale (1-5 stars).",
  "analytics.body_heatmap": "Popular Body Parts Heatmap",
  "analytics.body_heatmap_desc": "Where your clients want their tattoos — the bigger the number, the hotter the zone.",

  // Vault enhancements
  "vault.inspiration_title": "Inspiration",
  "vault.inspiration_desc": "Based on your saved estimates, you might also like these styles:",
  "vault.try_style": "Try this →",
  "vault.status_concept": "Concept",
  "vault.status_sent": "Request sent",

  // Reviews showcase
  "reviews.rating": "rating",
  "reviews.verified_reviews": "verified reviews",
  "reviews.read_all": "Read all reviews on Google",

  // Discover
  "discover.swipe": "swipe",

  // Profile fallback
  "profile.manual_place_id": "Studio not found? Enter your Place ID manually.",

  // Story Generator
  "story.title": "Instagram Story Generator",
  "story.desc": "Generate a premium Black & Gold story image with your QR code.",
  "story.generate": "Generate Story",
  "story.regenerate": "Regenerate",
  "story.download": "Download",
  "story.share": "Share",
  "story.downloaded": "Story downloaded!",
  "story.cta_line1": "Calculate Your Price",
  "story.cta_line2": "Scan the QR code below",
  "story.scan_prompt": "↑ Scan to get your price estimate",

  // Trends filter
  "trends.filter_country": "Filter by country",
  "trends.all_countries": "All countries",
  "trends.analyzed": "calculations analyzed",
  "trends.realtime": "Updated in real-time",
  "trends.no_data": "Not enough data yet.",
  "trends.subtitle": "Discover the most popular styles and body placements worldwide.",

  // Success badge
  "success.badge_title": "Boost your SEO",
  "success.badge_desc": "Place the Verified Badge on your website for a dofollow backlink to your Inkvaluator profile.",
  "success.badge_cta": "Get Embed Code",

  // Style Match
  "match.style_experts": "Experts in {style} near {city}",

  // Vault Review Booster
  "vault.review_booster_title": "Tattoo done? Help your artist!",
  "vault.review_booster_desc": "Leave a Google review and help other clients find this artist.",
  "vault.review_booster_btn": "Write a Google Review",

  // Checklist
  "checklist.title": "Verified Status",
  "checklist.profile_complete": "Profile complete",
  "checklist.google_linked": "Google Place ID linked",
  "checklist.badge_detected": "Verified Badge on website",
  "checklist.push_enabled": "Push notifications enabled",
  "checklist.not_yet": "Not yet",
  "checklist.active": "Active",

  // Navigation
  "nav.home": "Home",

  // Lead extras
  "lead.estimate_header": "Tattoo Estimate",

  // Discover extras
  "discover.verified_badge": "VERIFIED",

  // Analytics extras
  "analytics.total_calc_value": "Total Calculated Value",
  "analytics.total_lead_value": "Total Lead Value",
  "analytics.whatsapp_leads": "WhatsApp Leads",
  "analytics.team_leaderboard": "Team Leaderboard",

  // Trends extras
  "trends.title": "Tattoo",
  "trends.title_accent": "Trends",
  "trends.top_styles": "Top Styles",
  "trends.top_body_parts": "Popular Body Parts",

  // Style Match
  "match.title": "Matching Artists Near You",
  "match.score": "Match",
  "match.view": "View Profile",
  "match.cta_title": "Not sure where to start?",
  "match.cta_subtitle": "Tell us what you're looking for — we'll find the best tattoo artists near you.",
  "match.cta_button": "Find your artist",
  "match.badge_location": "Location-based",
  "match.badge_style": "Style matching",
  "match.badge_budget": "Budget filter",
  "match.step_location": "Step 1 — Location",
  "match.step_distance": "Step 2 — Max travel distance",
  "match.step_style": "Step 3 — Style preference",
  "match.step_budget": "Step 4 — Budget",
  "match.style_any": "Clear selection",
  "match.budget_any": "No max",
  "match.results_found": "artists found",
  "match.no_results": "No artists found in this area.",
  "match.no_results_hint": "Increase your search radius or adjust your filters.",
  "match.loading": "Searching artists...",
  "match.no_location": "Enter a location to get started.",
  "match.match_score": "match",
  "match.score_distance": "Distance",
  "match.score_quality": "Quality",
  "match.score_style": "Style",
  "match.score_budget": "Budget",
  "match.view_profile": "View profile",
  "match.claimed_badge": "Claimed profile",
  "match.unclaimed_badge": "Studio profile",
  "match.show_more": "Show more",
  "match.location_placeholder": "Enter your city...",
  "match.use_gps": "Use my location",
  "match.searching_from": "Searching from",
  "match.location_not_found": "City not found — try a different name",
  "match.gps_denied": "GPS access denied — enter a city instead",
  "match.gps_loading": "Getting location...",
  "match.change_location": "Change location",
  "match.enter_city": "Or enter a city",
  "match.summary_good": "Good match",
  "match.summary_ok": "Decent match",
  "match.summary_low": "Partial match",
  "match.show_breakdown": "Show breakdown",
  "match.hide_breakdown": "Hide breakdown",
  "match.sort_best": "Best match",
  "match.sort_distance": "Closest",
  "match.sort_rating": "Highest rated",
  "match.sort_lowest_price": "Lowest price",
  "match.sort_price_note": "Prices are only available for claimed artists. Studios without price data are shown at the bottom.",
  "match.from_price": "From",
  "match.wizard_step1_title": "Where are you looking?",
  "match.wizard_use_location": "Use my location",
  "match.wizard_step2_title": "How far will you travel?",
  "match.wizard_step3_title": "Which style speaks to you?",
  "match.wizard_step4_title": "What is your budget?",
  "match.wizard_skip": "Skip",
  "match.wizard_next": "Next",
  "match.wizard_show_matches": "Show my matches",
  "match.spotlight_label": "Your best match",
  "match.styles_unknown": "Styles unknown",
  "match.price_on_request": "Price on request",
  "match.expand_radius": "Expand to {km} km",
  "match.page_title": "Find a tattoo artist",
  "match.page_subtitle": "Set your preferences and find the best match near you.",
  "match.results_found": "artists found",
  "match.expand_more_within": "more within",
  "match.breakdown_distance": "Distance",
  "match.breakdown_style": "Style",
  "match.breakdown_budget": "Budget",
  "match.breakdown_quality": "Quality",
  "match.breakdown_unknown": "unknown",
  "match.why_this_match": "Why this match?",
  "match.results_count": "{n} matches found",
  "hero.subtitle_v2": "Compare tattoo artists by location, style and budget. Discover studios near you and book your appointment.",
  "hero.cta_discover": "Discover studios",

  // Urgency & Availability
  "urgency.recent_requests": "requests this week",
  "urgency.wait_time": "Est. wait",
  "urgency.weeks": "weeks",
  "urgency.high_demand": "High demand",
  "urgency.popular": "Popular",

  // Market Benchmark
  "benchmark.above_market": "above market average",
  "benchmark.below_market": "below market average",
  "benchmark.at_market": "At market average",
  "benchmark.market_avg": "Market avg",

  // Portfolio Preview
  "portfolio.style_examples": "Style examples",

  // Landing v2
  "landing.seo_title": "Calculate tattoo prices — Find artists in your city | Inkvaluator",
  "landing.seo_description":
    "Calculate what your tattoo costs instantly. Find tattoo artists near you. Free price calculator for studios across Europe.",
  "landing.hero_eyebrow": "",
  "landing.hero_title_line1": "What does",
  "landing.hero_title_line2": "your",
  "landing.hero_title_line3": "tattoo cost?",
  "landing.hero_subtitle":
    "Calculate a fair price estimate, find an artist near you, and get in touch directly via WhatsApp.",
  "landing.hero_search_placeholder": "Search by city or studio...",
  "landing.hero_popular": "Popular",
  "landing.hero_cta_primary": "Calculate your price",
  "landing.hero_cta_secondary": "View artists",
  "landing.artist_card_eyebrow": "For tattoo artists",
  "landing.artist_card_title": "Put your studio on the map",
  "landing.artist_card_body":
    "Your studio is probably already listed on Inkvaluator. Claim your profile in two minutes and receive direct requests from clients who already know what they want.",
  "landing.artist_card_stat_studios": "studios",
  "landing.artist_card_stat_cities": "cities",
  "landing.artist_card_stat_spots": "Founder spots",
  "landing.artist_card_cta": "Claim your profile for free",
  "landing.artist_card_founder_note": "first 100 studios get Pro for free",
  "landing.proof_studios": "Tattoo studios",
  "landing.proof_cities": "Cities worldwide",
  "landing.proof_leads": "Clients helped",
  "landing.proof_rating": "Average rating",
  "landing.map_section_label": "Studios near you",
  "landing.plan_eyebrow": "Your Tattoo Plan",
  "landing.plan_title": "No price question. A plan.",
  "landing.plan_body":
    "Inkvaluator generates a personal Tattoo Plan with your style, size, placement and price estimate. Send it directly to an artist — so you and the artist are aligned on style, size and budget from the start.",
  "landing.plan_step1": "Fill in the calculator — style, size, detail level",
  "landing.plan_step2": "Your Tattoo Plan is automatically created",
  "landing.plan_step3": "Send the plan via WhatsApp to an artist",
  "landing.plan_cta": "Create your Tattoo Plan",
  "landing.plan_card_title": "Tattoo Plan",
  "landing.plan_card_style": "Style",
  "landing.plan_card_size": "Size",
  "landing.plan_card_detail": "Detail level",
  "landing.plan_card_detail_val": "High",
  "landing.plan_card_placement": "Placement",
  "landing.plan_card_placement_val": "Upper arm",
  "landing.plan_card_estimate": "Estimated price",
  "landing.plan_card_send": "Send to artist via WhatsApp",
  "landing.how_eyebrow": "How it works",
  "landing.how_title": "From idea to appointment",
  "landing.how_subtitle": "Three steps. No account. Instant results.",
  "landing.how_step1_title": "Check the price",
  "landing.how_step1_desc": "Get a fair price estimate based on your tattoo idea — no surprises.",
  "landing.how_step1_cta": "Start the calculator",
  "landing.how_step2_title": "Find an artist",
  "landing.how_step2_desc": "Search by city or style, browse portfolios and read reviews from real clients.",
  "landing.how_step2_cta": "Discover artists",
  "landing.how_step3_title": "Book a session",
  "landing.how_step3_desc": "Contact the artist directly via WhatsApp or email and schedule your consultation.",
  "landing.map_eyebrow": "Coverage",
  "landing.map_title": "Artists near you",
  "landing.map_subtitle": "Studios across cities. Gold pin = claimed profile with active calculator.",
  "landing.map_loading": "Loading map...",
  "landing.map_legend_claimed": "Claimed — calculator active",
  "landing.map_legend_unclaimed": "Unclaimed",
  "landing.styles_eyebrow": "Styles",
  "landing.styles_title": "Find your style",
  "landing.styles_traditional": "Bold lines, vivid colors",
  "landing.styles_realism": "Photorealistic detail",
  "landing.styles_japanese": "Classic Japanese motifs",
  "landing.styles_fineline": "Delicate, minimalist lines",
  "landing.styles_blackwork": "Pure black, maximum impact",
  "landing.styles_watercolor": "Fluid and colorful",
  "landing.artist_eyebrow": "Are you a tattoo artist?",
  "landing.artist_title": "Let clients find you",
  "landing.artist_body": "Over 31,000 studios are already on Inkvaluator. Claim your free profile in two minutes and get discovered by thousands of potential clients — no credit card needed.",
  "landing.artist_perk1": "Clients send their Tattoo Plan — you know instantly what they want",
  "landing.artist_perk2": "Receive a deposit with every request via the platform",
  "landing.artist_perk3": "Grow organically via city map and search engines",
  "landing.artist_perk4": "First 100 studios get Pro for free — no hidden costs",
  "landing.artist_prop1_title": "New clients, automatically",
  "landing.artist_prop1_sub": "Your profile works 24/7 for you.",
  "landing.artist_prop2_title": "Grow your reach",
  "landing.artist_prop2_sub": "Get discovered by clients worldwide.",
  "landing.artist_prop3_title": "Free to start",
  "landing.artist_prop3_sub": "Live in 2 minutes, no credit card needed.",
  "landing.trust_coverage": "Coverage",
  "landing.artist_cta_claim": "Claim your profile",
  "landing.artist_cta_register": "Create account",
  "landing.founder_subtitle": "Exclusive for the first 100 studios",
  "landing.founder_perk1": "Pro package free while we grow",
  "landing.founder_perk2": "Direct influence on new features",
  "landing.founder_perk3": "Priority in search results",
  "landing.founder_perk4": "Personal contact with the developer",
  "landing.founder_claimed": "claimed",
  "landing.cities_eyebrow": "Cities",
  "landing.cities_title": "Tattoo studios by city",
  "landing.cities_all": "All cities",
  "landing.cities_studios": "studios",
  "landing.cities_from": "from",
  "landing.cities_claimed": "claimed",
  "landing.blog_eyebrow": "Knowledge base",
  "landing.blog_title": "Everything about tattoo prices",
  "landing.blog_all": "All articles",
  "landing.blog_read": "Read more",
  "landing.final_eyebrow": "Ready?",
  "landing.final_title_line1": "Ready for your next tattoo?",
  "landing.final_title_line2": "",
  "landing.final_subtitle": "No account needed. Free. Instant results.",
  "landing.final_cta_primary": "Start the calculator",
  "landing.final_cta_secondary": "Claim your studio",
  // Deposit settings
  "settings.deposit_title": "Deposit",
  "settings.deposit_desc": "Ask clients to pay a deposit when submitting a request.",
  "settings.deposit_none": "None",
  "settings.deposit_percentage": "Percentage",
  "settings.deposit_fixed": "Fixed amount",
  "settings.deposit_percentage_hint": "of the total amount",
  "settings.deposit_fixed_hint": "fixed deposit",
  "settings.deposit_preview_label": "Client sees",
  "settings.deposit_preview_pct": "Deposit: {value}% of the total amount",
  // ─── SEO Tattoo pages ───────────────────────────────
  "nav.studios": "Studios",
  "nav.studios_count": "studios",
  "city.stats_studios": "Studios in {city}",
  "city.stats_on_inkvaluator": "On Inkvaluator",
  "city.size_small": "Small (5–10 cm)",
  "city.size_medium": "Medium (10–20 cm)",
  "city.size_large": "Large (20 cm+)",
  "city.size_small_ex": "Text, minimalist, small symbol",
  "city.size_medium_ex": "Portrait, flowers, geometric",
  "city.size_large_ex": "Sleeve, back, full leg",
  "city.time_1_2": "1–2 hrs",
  "city.time_2_5": "2–5 hrs",
  "city.time_5plus": "5+ hrs",
  "city.most_popular": "Most popular",
  "city.price_indication_note": "Indication based on national averages",
  "city.price_factors_title": "Factors that determine the price",
  "city.factor_style": "Style",
  "city.factor_style_desc": "Realism and full-color cost more than blackwork or fine-line",
  "city.factor_placement": "Placement",
  "city.factor_placement_desc": "Ribs and hands are technically harder than arm or back",
  "city.factor_detail": "Detail level",
  "city.factor_detail_desc": "More detail = more time = higher price",
  "city.factor_experience": "Artist experience",
  "city.factor_experience_desc": "Senior artists charge more but offer more consistent quality",
  "city.updated": "Updated",
  "city.view_all_artists": "View all artists",
  "city.calc_button_short": "Calculate price",
  "country.h1": "Tattoo studios in {country}",
  "country.coming_soon": "Coming soon",
  "country.popular_styles": "Popular styles in {country}",
  "style.h1": "{style} tattoo in {city}",
  "style.subtitle": "{count} artists in {city} specialised in {style}. Calculate your {style} tattoo price directly.",
  "style.artists_h2": "{style} artists in {city}",
  "style.calc_button": "Calculate {style} price →",
  "style.empty": "No {style} artists claimed in {city} yet.",
  "style.all_studios": "All tattoo studios in {city}",
  "style.page_title": "{style} tattoo in {city} — artists & prices | Inkvaluator",
  "style.page_desc": "{count} {style} artists in {city}. Compare portfolios and calculate your tattoo price.",
  "studio.reviews": "reviews",
  "studio.claim_owner": "Are you the owner of {name}?",
  "studio.claim_desc":
    "Claim this profile for free and add a calculator. You will receive leads from clients in {city} directly.",
  "studio.claim_cta": "Claim this profile for free →",
  "studio.claim_cta_short": "Claim your studio →",
  "studio.view_with_calc": "View studios with calculator",
  "studio.artists_nearby": "Artists in {city} with an online calculator",
  "studio.deposit": "deposit",
  "studio.meta_desc": "{name} in {city}. View ratings, prices and claim this profile to receive leads.",
  "tattoo_index.search_placeholder": "Search a city or tattoo artist...",
  "tattoo_index.h1": "Find a tattoo studio near you",
  // ─── Map legend + list ──────────────────────────────
  "map.legend_pro": "Pro & Studio",
  "map.legend_claimed": "Claimed",
  "map.legend_unclaimed": "Unclaimed",
  "map.legend_on_inkvaluator": "On Inkvaluator",
  "map.view_studio": "View studio →",
  "map.list_pro_section": "✦ Pro & Studio",
  "map.list_claimed_section": "Claimed",
  "map.list_unclaimed_section": "Unclaimed",
  "map.list_count_singular": "studio in view",
  "map.list_count_plural": "studios in view",
  "map.list_scroll_hint": "Pan the map to explore",
  "map.list_empty": "No studios in this area",
  "map.list_empty_hint": "Zoom out or pan the map",
  "map.list_full_profile": "Full profile",
  // ─── City section titles ─────────────────────────────
  "city.map_section_title": "Tattoo studios in {city}",
  "city.faq_section_title": "Frequently asked questions about tattoos in {city}",
  "settings.deposit_preview_fixed": "Deposit: €{value} at booking",
  "footer.data_deletion": "Data Deletion",
  "deletion.seo_title": "Delete My Data – Inkvaluator",
  "deletion.seo_desc": "Request deletion of your personal data. GDPR, CCPA and LGPD compliant.",
  "deletion.title": "Delete My Data",
  "deletion.intro": "At Inkvaluator we respect your right to control your personal data. Under the GDPR (EU), CCPA (California), LGPD (Brazil) and other applicable privacy laws, you have the right to request the deletion of your personal data.",
  "deletion.what_title": "What data will be deleted?",
  "deletion.what_account": "Your account and profile information",
  "deletion.what_portfolio": "Portfolio images and uploaded content",
  "deletion.what_leads": "Lead and calculator history",
  "deletion.what_bookings": "Booking and payment records (where legally permitted)",
  "deletion.what_analytics": "Analytics and usage data linked to your account",
  "deletion.retention_title": "Data retention & exceptions",
  "deletion.retention_text": "Some data may be retained for legal, tax or compliance purposes as required by law. This includes financial transaction records (up to 7 years for tax obligations) and data necessary for fraud prevention or ongoing disputes.",
  "deletion.timeline_title": "Processing timeline",
  "deletion.timeline_gdpr": "Within 30 days of your request",
  "deletion.timeline_ccpa": "Within 45 days of your request",
  "deletion.timeline_lgpd": "Within 15 business days",
  "deletion.timeline_note": "You will receive a confirmation email once your data has been deleted.",
  "deletion.form_title": "Request data deletion",
  "deletion.form_email": "Email address associated with your account",
  "deletion.form_message": "Additional details (optional)",
  "deletion.form_placeholder": "Describe what data you want deleted...",
  "deletion.form_sending": "Sending...",
  "deletion.form_submit": "Submit deletion request",
  "deletion.form_alt": "You can also email us directly at",
  "deletion.success": "Your request has been received. We will process it within 30 days.",
  "deletion.error": "Something went wrong. Please email us directly.",

  // Sprint 1: cityContent + faq keys (Pijler 1B/1C/2C)
  "cityContent.intro_a": "Inkvaluator brings all tattoo studios in {city} together in one place, with up-to-date prices, styles and reviews.",
  "cityContent.intro_b": "Looking for the perfect tattoo studio in {city}? Compare {count} studios directly on price, style and availability.",
  "cityContent.intro_c": "{city} has {count} tattoo studios — from traditional to hyperrealism. Find your match on Inkvaluator.",
  "cityContent.intro_d": "Whether it is your first or twentieth tattoo: in {city} you will find a specialist for every style and budget on Inkvaluator.",
  "cityContent.priceSection_title": "Tattoo prices in {city} ({year})",
  "cityContent.priceSection_body": "The amounts below are indicative market prices for tattoos in {city}, based on common rates (last updated {updated}). The actual price depends on size, style, placement and the artist's experience.",
  "cityContent.stylesSection_title": "Popular tattoo styles in {city}",
  "cityContent.stylesSection_subtitle": "Based on {count} studios · updated {updated}",
  "cityContent.neighborhoodsSection_title": "Studios by neighbourhood in {city}",
  "cityContent.neighborhoodsSection_subtitle": "Most studios are located in:",
  "cityContent.nearbySection_title": "Other nearby cities",
  "cityContent.nearbySection_subtitle": "Also popular with visitors to {city}:",
  "cityContent.ggdSection_title": "GGD-certified tattoo studios in {city}",
  "cityContent.ggdSection_body": "Of the {studioCount} tattoo studios in {city}, {verifiedCount} hold a GGD certificate (source: RIVM register). A GGD certificate means the studio meets Dutch government hygiene and safety requirements.",
  "cityContent.ggdSection_source": "Source: RIVM Tattoo and piercing register",
  "cityContent.faq_ggd_question": "How many tattoo studios in {city} have a GGD certificate?",
  "cityContent.faq_ggd_answer": "Of the {studioCount} tattoo studios in {city}, {verifiedCount} hold a GGD certificate according to the RIVM register.",
  "nearby.title": "Nearby tattoo cities",
  "nearby.studios_count": "studios",
  "faq.count_q": "How many tattoo shops are there in {city}?",
  "faq.count_a": "{city} has {count} registered tattoo studios on Inkvaluator (last updated {updated}).",
  "faq.price_q": "What does a tattoo cost in {city}?",
  "faq.price_a": "Tattoo prices in {city} range from {min_small} for a small piece to {avg_large}–{max_large} for a large work. Average medium tattoo: {avg_medium}. Last updated {updated}.",
  "faq.style_q": "What is the most popular tattoo style in {city}?",
  "faq.style_a": "{style} is the most requested style in {city}. Use the Inkvaluator calculator for a price estimate per style.",
  "faq.book_q": "How do you book a tattoo in {city}?",
  "faq.book_a": "On {platform} you can compare studios in {city} directly on style, price and availability — and book online with a deposit.",

  "profile.tab_portfolio": "Portfolio",
  "profile.tab_studio": "Studio",
  "profile.tab_book": "Book",
  "profile.tab_calculator": "Price estimate",
  "profile.tab_over": "About",
  "profile.tab_flash": "Flash",
  "profile.tab_reviews": "Reviews",
  "profile.tab_faq": "FAQ",
  "profile.no_portfolio": "No portfolio images added yet.",
  "profile.no_portfolio_hint": "The artist can set this up via their dashboard.",
  "profile.studio_status": "Studio status",
  "profile.open_now": "Open now",
  "profile.closed": "Closed",
  "profile.studio_rules": "Studio rules",
  "rules.deposit_title": "Deposit",
  "rules.deposit_body": "A deposit of €150 is required to confirm your appointment. This amount will be deducted from the total price on the day of the session.",
  "rules.cancellation_title": "Cancellation",
  "rules.cancellation_body": "Cancellation is free up to 48 hours in advance. Cancellations within 48 hours or no-shows forfeit the deposit.",
  "rules.guests_title": "Guests",
  "rules.guests_body": "A maximum of 1 guest per client is welcome. Guests stay in the lounge during the session.",
  "rules.age_title": "Age & ID",
  "rules.age_body": "We only tattoo persons aged 18 and over. A valid ID is required.",
  "rules.health_title": "Health",
  "rules.health_body": "Come well-rested and well-fed to your appointment. Report any relevant medical conditions, medication or allergies when booking.",
  "rules.coverup_title": "Cover-ups",
  "rules.coverup_body": "Cover-ups are possible after prior consultation. Always send photos of the existing tattoo before the appointment.",
  "rules.design_title": "Design & adjustments",
  "rules.design_body": "The final design will be presented on the day of the session. Minor adjustments are possible, major changes may lead to a new appointment.",
  "rules.aftercare_title": "Aftercare",
  "rules.aftercare_body": "Follow the aftercare instructions you receive after the session carefully. We are not liable for consequences of improper care.",
  "profile.facilities": "Facilities",
  "profile.price_indication": "Price indication",
  "profile.hourly_rate_label": "/hr hourly rate",
  "profile.directions": "Directions",
  "profile.day_mon": "Monday",
  "profile.day_tue": "Tuesday",
  "profile.day_wed": "Wednesday",
  "profile.day_thu": "Thursday",
  "profile.day_fri": "Friday",
  "profile.day_sat": "Saturday",
  "profile.day_sun": "Sunday",
  "profile.facility_parking": "Parking",
  "profile.facility_wheelchair": "Accessible",
  "profile.facility_private_room": "Private room",
  "profile.facility_wifi": "WiFi",
  "profile.facility_card_payment": "Card payment",
  "profile.facility_drinks": "Drinks",
  "profile.facility_sterile_cert": "Sterile cert.",
  "profile.facility_child_friendly": "Child-friendly",
  "profile.facility_cover_up": "Cover-up",
  "profile.facility_custom_design": "Custom design",
  "profile.facility_same_day": "Same day",
  "profile.size_small": "Small · < 10 cm",
  "profile.size_medium": "Medium · 10–20 cm",
  "profile.size_large": "Large · 20+ cm",
  "profile.book_appointment": "Book appointment",
  "profile.book_desc": "Choose an available date and time slot. You will receive a confirmation by email.",
  "profile.earliest_slot": "Earliest available slot:",
  "profile.choose_slot": "Choose a date and time slot",
  "profile.calculator": "Calculator",
  "profile.estimate": "Estimate",
  "profile.back": "Back",
  "profile.next": "Next",
  "profile.view_price": "View price",
  "profile.all_reviews_google": "View all reviews on Google",
  "profile.write_review": "Write a review",
  "profile.reviews": "reviews",
  "profile.not_found_title": "Artist not found",
  "profile.not_found_desc": "This profile no longer exists. Find an artist near you.",
  "profile.find_studio": "Find a tattoo studio →",
  "profile.studio_artists": "Artists at this studio",
  "profile.artists_count": "artists",
  "profile.calc_price": "Calculate price →",
  "profile.recent_works": "Recent works",
  "profile.works_count": "works",
  "profile.all_filter": "All",
  "profile.navigate_to": "Navigate to",
  "profile.price_calculator": "Price calculator",
  "profile.or_whatsapp": "Or send a WhatsApp message",
  "profile.schedule_appointment": "Schedule appointment",
  "profile.per_hour": "/hr",
  "profile.artist_label": "Artist",
  "profile.tattoo_artist": "Tattoo artist",
  "profile.book_label": "Book",
  "profile.open_maps": "Open Maps",
  "profile.live_traffic": "Live traffic",
  "profile.find_on_google": "Find on Google",
  "booking.title": "Book an appointment with {artist}",
  "booking.confirmed_title": "Booking requested!",
  "booking.choose_date": "Choose an available date",
  "booking.choose_time": "Choose a time slot",
  "booking.other_day": "Choose another day",
  "booking.other_slot": "Other time slot",
  "booking.name": "Name",
  "booking.name_placeholder": "Your name",
  "booking.email": "Email address",
  "booking.email_placeholder": "your@email.com",
  "booking.phone": "Phone / WhatsApp",
  "booking.style": "Tattoo style",
  "booking.style_placeholder": "Choose a style...",
  "booking.no_preference": "— No preference —",
  "booking.description": "Tattoo description",
  "booking.description_placeholder": "Tell us about your desired tattoo...",
  "booking.reference_photo": "Reference photo (optional)",
  "booking.upload_photo": "Upload photo",
  "booking.deposit_notice": "A deposit of €{amount} is required when booking. The artist will contact you with payment instructions.",
  "booking.fill_required": "Please fill in name and email",
  "booking.submit": "Request appointment",
  "booking.success_title": "Request sent!",
  "booking.success_desc": "{artist} will receive your request and confirm the appointment as soon as possible. You will receive a confirmation at",
  "booking.close": "Close",
  "booking.available": "Available",
  "booking.unavailable": "Unavailable",
  // ─── Missing translations: new features (Sprint 2–4) ──
  "analytics.bookings": "Bookings",
  "analytics.bookings_summary": "Bookings summary",
  "analytics.business_coach": "Business Coach",
  "analytics.coach_actions_desc": "Personalised action plan based on your data",
  "analytics.funnel_desc": "Calculations → Leads → Bookings",
  "analytics.month_progress": "Month progress",
  "analytics.month_summary": "Month summary",
  "auth.email_placeholder": "your@email.com",
  "availability.session_label": "Session",
  "booking.at": "at",
  "bookings.title": "Bookings",
  "bookings.count": "{count} bookings",
  "bookings.new_count": "{count} new",
  "bookings.no_bookings": "No bookings yet",
  "bookings.manage_desc": "Manage and confirm client appointments",
  "bookings.filter_all": "All",
  "bookings.filter_pending": "Pending",
  "bookings.filter_confirmed": "Confirmed",
  "bookings.filter_cancelled": "Cancelled",
  "calc.fill_all_steps": "Please complete all steps",
  "calc.from": "From",
  "city.filter_ggd": "GGD Permit",
  "city.filter_ggd_hint": "Only show studios with a GGD/RIVM permit",
  "compare.toggle": "Compare configurations",
  "compare.lock_config": "Lock this configuration",
  "compare.clear": "Clear comparison",
  "compare.difference": "Difference",
  "dash.currency_auto_set": "Currency set automatically based on your location",
  "dash.loading": "Loading your dashboard...",
  "deposit.mode_none": "No deposit",
  "deposit.mode_fixed": "Fixed amount",
  "deposit.mode_percentage": "Percentage",
  "discover.filters": "Filters",
  "discover.filter_style": "Style",
  "discover.filter_city": "City",
  "discover.filter_country": "Country",
  "discover.filter_rating": "Rating",
  "discover.all_styles": "All styles",
  "discover.all_cities": "All cities",
  "discover.all_countries": "All countries",
  "discover.any": "Any",
  "discover.clear_filters": "Clear filters",
  "discover.results": "{count} results",
  "discover.no_results_filter": "No artists match your filters",
  "discover.top_cities": "Top cities",
  "discover.nearby_cities": "Nearby cities",
  "discover.stat_studios": "studios",
  "discover.stat_cities": "cities",
  "discover.stat_countries": "countries",
  "discover.seo_intro_title": "Find your perfect tattoo artist",
  "discover.seo_intro_text": "Discover verified tattoo artists and studios. Compare styles, prices and availability.",
  "discover.request_city_title": "Request your city",
  "discover.request_city_desc": "Don't see your city? We'll add it soon.",
  "flash.color_label": "Colour type",
  "flash.color_black_grey": "Black & grey",
  "flash.color_color": "Colour",
  "flash.color_both": "Both",
  "groei.hide_tips": "Hide tips",
  "header.admin": "Admin",
  "header.toggle_menu": "Toggle menu",
  "header.toggle_theme": "Toggle theme",
  "hero.eyebrow": "Inkvaluator",
  "hero.title": "Your Tattoo Journey Starts Here",
  "hero.subtitle": "Find your artist. Know your price.",
  "home.copy": "Copy link",
  "home.copied": "Copied!",
  "laf.reset": "Reset to default",
  "map.studios_count": "{count} studios",
  "nav.cta_discover": "Explore Artists",
  "nav.explore_map": "Explore Map",
  "privacy.deletion_link": "/data-deletion",
  "privacy.deletion_link_text": "Submit a Data Deletion Request",
  "profile.about_title": "About",
  "profile.about_desc": "Tell clients about your style, specialisations and experience.",
  "profile.about_placeholder": "Write a short bio about yourself as a tattoo artist...",
  "profile.details_title": "Profile Details",
  "profile.details_desc": "Your name, location, hourly rate and working hours.",
  "profile.facilities_title": "Studio Facilities",
  "profile.facilities_desc": "Show clients what facilities your studio offers.",
  "profile.cover_up_title": "Cover-up specialist",
  "profile.cover_up_desc": "Indicate whether you specialise in cover-up tattoos.",
  "profile.gender_label": "Gender",
  "profile.gender_male": "Male",
  "profile.gender_female": "Female",
  "profile.gender_non_binary": "Non-binary",
  "profile.gender_prefer_not": "Prefer not to say",
  "profile.gender_unspecified": "Unspecified",
  "profile.instagram_feed": "Instagram feed",
  "profile.portfolio_connected": "Portfolio connected",
  "push.test_sent": "Push notification sent!",
  "session.title": "Session Planner",
  "session.session_label": "Session",
  "session.total_duration": "Total session duration",
  "session.weeks": "weeks",
  "session.weeks_healing": "weeks healing time",
  "settings.opening_hours_title": "Opening Hours",
  "settings.opening_hours_desc": "Set your studio opening hours",
  "settings.studio_rules_title": "Studio Rules",
  "settings.studio_rules_desc": "Set the rules and policies visible on your public profile",
  "smart_search.cities": "Cities",
  "smart_search.artists": "Artists",
  "smart_search.studios": "Studios",
  "smart_search.no_results": "No results for",
  "smart_search.request_city": "Want to request this city?",
  "landing.search_near_me": "Near me",
  "inkswipe.page_title": "InkSwipe — Discover tattoo artists near you",
  "inkswipe.page_description": "Swipe through tattoo photos and discover the best artists near you.",
  "inkswipe.geo.title": "Tattoos near you",
  "inkswipe.geo.subtitle": "We show photos from artists within your region.",
  "inkswipe.geo.radius_label": "Radius",
  "inkswipe.geo.no_geolocation": "Location not available on this device.",
  "inkswipe.geo.denied": "Location access denied. Please update your browser settings.",
  "inkswipe.geo.locating": "Locating...",
  "inkswipe.geo.cta": "Use my location",
  "inkswipe.header.change_location": "Change location",
  "inkswipe.header.login": "Log in",
  "inkswipe.feed.loading": "Loading feed...",
  "inkswipe.action.skip": "Skip",
  "inkswipe.action.like": "Like",
  "inkswipe.soft_prompt.title": "Save your favourites",
  "inkswipe.soft_prompt.body": "Create a free account and save every tattoo you love. Find your perfect artist again, anytime.",
  "inkswipe.soft_prompt.cta_signup": "Create account — free",
  "inkswipe.soft_prompt.cta_login": "Log in",
  "inkswipe.soft_prompt.skip": "Continue without account",
  "inkswipe.soft_prompt.dismiss": "Close",
  "inkswipe.intake.title": "Your style preference",
  "inkswipe.intake.subtitle": "Select styles that appeal to you.",
  "inkswipe.intake.close": "Close",
  "inkswipe.intake.skip": "Skip",
  "inkswipe.intake.save": "Save",
  "inkswipe.intake.saving": "Saving...",
  "inkswipe.account.page_title": "My account — InkSwipe",
  "inkswipe.account.title": "My account",
  "inkswipe.account.edit_prefs": "Edit preferences",
  "inkswipe.account.logout": "Log out",
  "inkswipe.account.likes_title": "Saved tattoos",
  "inkswipe.account.no_likes": "No saved tattoos yet.",
  "inkswipe.account.go_swipe": "Start swiping",
  "inkswipe.feed.empty": "No tattoos found in your region.",
  "inkswipe.feed.error": "Could not load feed.",
  "inkswipe.feed.retry": "Try again",
  "inkswipe.feed.end": "That's all for now!",
  "inkswipe.nav.title": "InkSwipe",
  "inkswipe.nav.description": "Swipe through tattoo photos near you",
  // Instagram OAuth
  "instagram.connect": "Connect Instagram",
  "instagram.connected": "Instagram connected",
  "instagram.disconnect": "Disconnect",
  "instagram.sync_now": "Sync now",
  "instagram.sync_success": "Instagram synced!",
  "instagram.sync_error": "Sync failed. Try again.",
  "instagram.not_connected": "Not connected yet",
  "instagram.photos_synced": "photos",
  "instagram.videos_synced": "videos",
  "instagram.connect_desc": "Connect your Instagram account to automatically sync your work to InkSwipe.",
  "swipe.video.play": "Play video",
  "swipe.video.close": "Close video",
  "directory.verified_label": "Verified artists",
  "directory.verified_title": "Artists on Inkvaluator",
  "directory.nearby_label": "In your area",
  "directory.nearby_title": "Studios nearby",
  "directory.hero_label": "Find your artist",
  "discover.directory.filter_distance": "Distance",
  "discover.directory.dist_10km": "< 10 km",
  "discover.directory.dist_25km": "< 25 km",
  "discover.directory.dist_50km": "< 50 km",
  "discover.directory.dist_100km": "< 100 km",
  "discover.directory.dist_250km": "< 250 km",
  "discover.directory.dist_200km": "< 200 km",
  "discover.directory.load_more": "Load more",
  "filters.city": "City",
  "filters.distance": "Distance",
  "filters.distance.all": "All distances",
  "directory.count": "studios found",
  "directory.loadMore": "Load more",
  "city.matchCta": "Not sure which artist suits you?",
  "city.matchCtaLink": "Use the free artist matcher",
  "directory.matchBanner": "Want a personalised recommendation based on location, style and budget?",
  "directory.matchBannerLink": "Use the matcher →",
  // Preview sheet
  "preview.close": "Close",
  "preview.closed": "Closed",
  "preview.open": "Open",
  "preview.until": "until",
  "preview.from_price": "From",
  "preview.view_profile": "View full profile",
  "preview.claim_cta": "Is this your studio? Claim for free",
  "preview.claim_title": "Is this your studio?",
  "preview.claim_sub": "Claim this profile for free and add your portfolio",
  "profile.closed.permanent": "This studio is permanently closed",
  "profile.closed.temporary": "This studio is temporarily closed",
  "profile.closed.subtitle": "Do you know the owner? Let them know they can claim their profile.",
  "profile.summary.studio": "tattoo studio",
  "profile.faq.title": "Frequently asked questions",
  "profile.faq.cost.q": "What does a tattoo cost at {name}?",
  "profile.faq.ggd.q": "Does {name} have a GGD permit?",
  "profile.faq.location.q": "Where is {name} located?",
  "profile.faq.contact.q": "How do I contact {name}?",
  "profile.updated": "Updated",
  "dashboard.hub.completeness_title": "Profile Completeness",
  "dashboard.hub.completeness_desc": "Complete your profile to rank higher in search results.",
  "dashboard.hub.fields_complete": "fields filled in",
  "dashboard.hub.next_step": "Next step",
  "dashboard.hub.basis_title": "Basics",
  "dashboard.hub.basis_desc": "Bio, experience, languages and specializations.",
  "dashboard.hub.bio_label": "Bio",
  "dashboard.hub.bio_hint": "Tell clients who you are and what your style is. Minimum 80 characters for completeness.",
  "dashboard.hub.bio_placeholder": "Describe yourself as an artist...",
  "dashboard.hub.years_exp_label": "Years of experience",
  "dashboard.hub.years_suffix": "years",
  "dashboard.hub.languages_label": "Languages spoken",
  "dashboard.hub.languages_hint": "Press Enter or comma to add a language.",
  "dashboard.hub.languages_placeholder": "E.g. Dutch, English...",
  "dashboard.hub.specializations_label": "Specializations",
  "dashboard.hub.specializations_hint": "Styles and techniques you specialize in.",
  "dashboard.hub.specializations_placeholder": "E.g. Blackwork, Fine line...",
  "dashboard.profile.field_google_place_id_label": "Google Business linked",
  "dashboard.profile.field_google_place_id_cta": "Link your Google location",
  "dashboard.profile.field_bio_label": "Bio",
  "dashboard.profile.field_bio_cta": "Write a bio (min. 80 characters)",
  "dashboard.profile.field_portfolio_label": "Portfolio photos",
  "dashboard.profile.field_portfolio_cta": "Add 3+ portfolio photos",
  "dashboard.profile.field_specializations_label": "Specializations",
  "dashboard.profile.field_specializations_cta": "Add your specializations",
  "dashboard.profile.field_price_examples_label": "Price examples",
  "dashboard.profile.field_price_examples_cta": "Add price examples",
  "dashboard.profile.field_years_experience_label": "Years of experience",
  "dashboard.profile.field_years_experience_cta": "Add years of experience",
  "dashboard.profile.field_languages_label": "Languages",
  "dashboard.profile.field_languages_cta": "Add spoken languages",
  "dashboard.profile.field_opening_hours_label": "Opening hours",
  "dashboard.profile.field_opening_hours_cta": "Set your opening hours",
  "dashboard.profile.field_certifications_label": "Certifications",
  "dashboard.profile.field_certifications_cta": "Add a certification",
  "dashboard.profile.field_artist_faqs_label": "FAQ",
  "dashboard.profile.field_artist_faqs_cta": "Add at least 1 FAQ",
  "dashboard.profile.field_awards_label": "Awards & press",
  "dashboard.profile.field_awards_cta": "Add awards",
  "dashboard.profile.google_search_title": "Link Google Business",
  "dashboard.profile.google_search_desc": "Search for your studio to display Google ratings on your profile.",
  "dashboard.profile.google_no_fixed_location": "I don't work from a fixed studio location",
  "dashboard.profile.google_opted_out_label": "You've indicated you work independently without a fixed location. This counts toward your profile score.",
  "repeater.add_certification": "Add certification",
  "repeater.add_price_example": "Add price example",
  "repeater.add_award": "Add award",
  "repeater.empty_certifications": "No certifications yet",
  "repeater.empty_prices": "No price examples yet",
  "repeater.empty_awards": "No awards yet",
  "repeater.name": "Name",
  "repeater.issuer": "Issuer",
  "repeater.year": "Year",
  "repeater.description": "Description",
  "repeater.price_from": "From",
  "repeater.price_to": "To",
  "repeater.title": "Title",
  "repeater.link": "Link",
  "branding.bio_managed_in_profile": "Manage your bio via Profile → Basics.",
};

// ─── DUTCH (complete) ──────────────────────────────────────────
const nl: Record<string, string> = {
  "app.name": "Inkvaluator",
  "app.tagline": "Bereken je Tattoo Prijs",
  "app.powered_by": "Powered by",
  "currency.aria_label": "Wijzig valuta",
  "currency.title": "Weergave-valuta",
  "currency.rates_of": "Koersen van {date}",
  "currency.approx_tooltip": "Originele prijs {original} — wisselkoers van {date}",
  "currency.approx_prefix": "≈",
  back: "Terug",
  save: "Opslaan",
  copy: "Kopiëren",
  copied: "Gekopieerd!",
  loading: "Laden...",
  error: "Fout",
  optional: "optioneel",
  "landing.hero": "Bereken je",
  "landing.hero_accent": "Tattoo Prijs",
  "landing.subtitle": "Voer de code van je tattoo artist in om een gepersonaliseerde prijsindicatie te krijgen.",
  "landing.code_title": "Calculator Code",
  "landing.code_desc": "Je hebt een unieke code ontvangen van je tattoo artist",
  "landing.code_placeholder": "Bijv. A3F9K2",
  "landing.start": "Start Calculator",
  "landing.invalid_code": "Voer een geldige code in",
  "landing.or": "of",
  "landing.artist_cta": "Ben je tattoo artist? Maak je eigen calculator.",
  "landing.artist_register": "Registreer als artiest",
  "landing.artist_login": "Artiest login",
  "landing.hero_title": "Bereken je",
  "landing.hero_title_accent": "Tattoo Prijs",
  "landing.hero_desc": "Ontdek in seconden wat jouw volgende tattoo kost. Probeer het direct uit.",
  "landing.hero_ai_speed": "Van foto naar prijsindicatie in seconden — powered by AI",
  "landing.try_it": "Probeer Zelf!",
  "landing.hero_cta": "PROBEER DE CALCULATOR",
  "landing.showcase_title": "Premium",
  "landing.showcase_accent": "Stijlen",
  "landing.showcase_desc": "Ontdek de populairste tattoo-stijlen van artiesten wereldwijd.",
  "landing.estimated_price": "Geschatte prijs",
  "landing.your_rates": "Jouw tarieven",
  "landing.rates_desc": "Verschuif de sliders en zie de prijs veranderen",
  "landing.hourly_rate": "Uurprijs",
  "landing.start_cost": "Vaste startkosten",
  "landing.size_label": "Grootte",
  "landing.complexity_label": "Complexiteit",
  "landing.body_placement": "Plaatsing op het lichaam",
  "landing.demo_disclaimer": "Dit is een demo — de werkelijke prijs hangt af van de artiest en het ontwerp.",
  "landing.find_artist": "Vind jouw perfecte",
  "landing.find_artist_accent": "artiest",
  "landing.nearby_desc": "Pro & Studio artiesten bij jou in de buurt",
  "landing.nearby_soon": "Binnenkort verschijnen hier artiesten bij jou in de buurt.",
  "landing.discover_all": "Ontdek Alle Artiesten",
  "landing.artist_or_studio": "Ben je een artiest of studio?",
  "landing.artist_question": "Ben je een artiest of studio?",
  "landing.promo_placeholder": "Kortingscode (optioneel)",
  "landing.view_options": "Bekijk mogelijkheden & prijzen",
  "landing.start_free": "Start gratis",
  "landing.discount_code": "Kortingscode (optioneel)",
  "landing.upgrade_to": "Upgrade naar",
  "landing.discover": "Ontdek",
  "landing.login": "Login",
  "landing.founder_desc": "50% korting voor de eerste 50 aanmeldingen",
  "landing.claim_now": "Claim nu →",
  "landing.how_it_works_cta": "Bekijk hoe het werkt voor jouw studio",
  "calc.estimate": "Geschatte Prijs",
  "calc.size": "Grootte",
  "calc.size_desc": "Hoe groot wordt je tattoo?",
  "calc.complexity": "Complexiteit",
  "calc.complexity_desc": "Hoe gedetailleerd is het ontwerp?",
  "calc.body_part": "Lichaamsdeel",
  "calc.body_part_desc": "Klik op het lichaamsdeel waar je tattoo komt",
  "calc.style": "Stijl",
  "calc.style_desc": "Welke tattoo-stijl wil je?",
  "calc.budget": "Budget",
  "calc.budget_desc": "Wat is je maximale budget?",
  "calc.budget_placeholder": "Bijv. 300",
  "calc.get_estimate": "Krijg een realistische prijsindicatie van",
  "calc.no_artist": "Geen artiest gevonden met deze code of link.",
  "calc.not_configured": "De prijscalculator is nog niet geconfigureerd door de artiest.",
  "calc.back_home": "Terug naar homepage",
  "calc.artist_login": "Artiest? Log in",
  "calc.add_color": "Kleur toevoegen?",
  "calc.color_surcharge": "+20% toeslag voor kleurwerk",
  "calc.color_desc": "Kleur voegt extra tijd en kosten toe",
  "calc.ai_scan": "AI Afbeelding Scan",
  "calc.manual": "Handmatig invoeren",
  "calc.details": "Detailniveau",
  "calc.details_desc": "Hoe gedetailleerd is de tatoeage?",
  "calc.start": "Start",
  "calc.start_title": "Hoe wil je beginnen?",
  "calc.start_subtitle": "Upload een referentieafbeelding of voer details handmatig in",
  "calc.rough_estimate": "Grove schatting",
  "calc.calculated_price": "Berekende prijs",
  "calc.view_price_cta": "Bekijk jouw prijsoverzicht",
  "calc.no_photo": "Geen referentiefoto toegevoegd",
  "calc.save_for_later": "Opslaan voor later",
  "calc.link_copied": "Link gekopieerd!",
  "calc.compare_configs": "Vergelijk configuraties",
  "calc.compare_desc": "Vergelijk twee prijsconfiguraties naast elkaar",
  "whatsapp.add_photo": "Voeg je foto toe",
  "whatsapp.photos_label": "Referentiefoto's — max. 3",
  "calc.share": "Delen",
  "calc.more_options": "Meer opties",
  "landing.find_artist_worldwide": "Vind jouw perfecte artiest, wereldwijd",
  "calc.info_style":
    "De gekozen stijl beïnvloedt de prijs. Stijlen zoals Realisme of Japans vereisen meer precisie en tijd, waardoor ze duurder zijn dan eenvoudigere stijlen.",
  "calc.info_placement":
    "Sommige lichaamsdelen zijn moeilijker te bereiken of gevoeliger, wat extra zorg en tijd van de artiest vraagt.",
  "calc.info_size":
    "De grootte bepaalt het aantal basisuren. Een mini-tattoo duurt minder dan een uur, terwijl XL-stukken meerdere sessies kunnen beslaan.",
  "calc.info_detail":
    "Meer detail betekent meer precisiewerk, schaduw en fijne lijnen — wat allemaal de sessieduur verhoogt.",
  "calc.info_color":
    "Kleurwerk vereist extra inktsoorten, meerdere lagen en langere genezing. Dit voegt doorgaans ~20% toe aan de totale tijd.",
  "share.native_title": "Mijn Tattoo Schatting – Inkvaluator",
  "coaching.title": "Advies",
  "coaching.color_tip":
    "Kleur toevoegen verhoogt de tijd met ~20%. Stijlen zoals Watercolor of Neo-Traditional bevatten standaard kleur.",
  "coaching.complexity_tip":
    "Gedetailleerde ontwerpen kosten aanzienlijk meer tijd. Overweeg 'medium' detail om kosten te verlagen.",
  "coaching.session_tip": "Deze tatoeage vereist meerdere sessies. Plan 2-4 weken hersteltijd tussen sessies.",
  "coaching.placement_tip":
    "Dit lichaamsdeel is lastiger om te tatoeëren, wat extra tijd kost. Armen en benen zijn over het algemeen makkelijkere plaatsen.",
  "coaching.daily_rate_tip":
    "Een dagtarief wordt toegepast voor dit grotere stuk — dit is vaak voordeliger dan uurtarief voor langere sessies.",
  "coaching.xl_tip":
    "Extra-large stukken zijn grote projecten. Overweeg om met een kleiner deel te beginnen en later uit te breiden.",
  "laf.calculator_mode": "Calculator weergave",
  "laf.calculator_mode_desc": "Kies hoe bezoekers je calculator zien",
  "laf.mode_steps": "Stap voor stap",
  "laf.mode_single": "Alles in één",
  "laf.mode_toggle": "Laat bezoeker kiezen",
  "layout.visitor_toggle": "Weergave kiezen",

  // Stepper
  "step.next": "Verder",
  "step.view_result": "Bekijk Prijs",
  "step.details": "Details",
  "step.details_desc": "Hoe gedetailleerd is je ontwerp?",
  "layout.stepper": "Stap voor stap",
  "layout.form": "Alles in één",

  "breakdown.title": "Prijsopbouw",
  "breakdown.labor": "Arbeid (tijd)",
  "breakdown.est_hours": "Geschatte uren",
  "breakdown.subtotal": "Subtotaal",
  "breakdown.complexity": "Complexiteit",
  "breakdown.placement": "Plaatsing",
  "breakdown.style": "Stijl",
  "breakdown.design_cost": "Ontwerpkosten",
  "breakdown.deposit": "Aanbetaling",
  "breakdown.total_range": "Totaal bereik",
  "breakdown.disclaimer":
    "Dit is een indicatie, geen definitieve prijs. De uiteindelijke prijs wordt bepaald na een consult met de artiest.",
  "breakdown.budget_good": "Je budget past ruim binnen deze inschatting!",
  "breakdown.budget_okay": "Je budget past binnen de ondergrens van deze inschatting.",
  "breakdown.budget_low": "Overweeg een kleinere of simpeler tattoo voor dit budget.",
  "breakdown.full_disclaimer":
    "Disclaimer: Dit is een automatische prijsindicatie. De definitieve prijs wordt bepaald door de artiest na persoonlijk overleg.",
  "breakdown.estimate_label": "Schatting:",
  "breakdown.day_sessions": "{count} dagsessie(s) van ±{hours} uur",
  "breakdown.incl_style_detail": "incl. stijl & detail",
  "breakdown.daily_rate_applied": "Voordelig dagtarief toegepast",
  "breakdown.daily_rate_tooltip": "Gunstiger dagtarief toegepast wegens tijdsduur",
  "breakdown.base_price_label": "Vanaf prijs (Formaat & Plaatsing)",
  "breakdown.style_detail_surcharge": "Stijl & Detail toeslag",
  "breakdown.color_surcharge_label": "Kleur toeslag (+20% tijd)",
  "breakdown.total_indication": "Totaal Indicatie",
  "breakdown.required_deposit": "Vereiste aanbetaling",
  "breakdown.hours_unit": "uur",
  "tooltip.labor":
    "Gebaseerd op de geschatte tijd die nodig is voor deze grootte tattoo, vermenigvuldigd met het uurtarief van de artiest.",
  "tooltip.complexity": "Complexere ontwerpen (meer detail, schaduwen, texturen) kosten meer tijd en precisie.",
  "tooltip.placement":
    "Sommige lichaamsdelen zijn gevoeliger of moeilijker bereikbaar, wat extra tijd en zorg vereist.",
  "tooltip.style": "Verschillende stijlen vragen om specifieke technieken en expertise, wat de prijs beïnvloedt.",
  "tooltip.design": "Kosten voor het ontwerpen en voorbereiden van je unieke tattoo.",
  "tooltip.deposit": "De aanbetaling reserveert je afspraak en wordt verrekend met de totaalprijs.",
  "faq.title": "Veelgestelde Vragen",
  "faq.deposit_q": "Waarom een aanbetaling?",
  "faq.deposit_a": "De aanbetaling reserveert je afspraak en dekt de ontwerptijd.",
  "faq.bigger_q": "Wat als mijn tattoo groter wordt?",
  "faq.bigger_a": "De prijs kan wijzigen na het consult als het ontwerp aangepast wordt.",
  "faq.consult_q": "Is een consult gratis?",
  "faq.consult_a": "Neem contact op met de studio voor informatie over consulten.",
  "dash.welcome": "Welkom bij Inkvaluator",
  "dash.setup_desc": "Laten we je prijsinstellingen configureren",
  "dash.setup_info": "Dit duurt maar een paar minuten. Je kunt alles later nog aanpassen.",
  "dash.start_config": "Start configuratie",
  "dash.logout": "Uitloggen",
  "dash.view_profile": "Bekijk profiel",
  "home.tab_home": "Home",
  "home.quick_actions": "Snelle acties",
  "home.no_city": "Stad nog niet ingesteld",
  "home.tile_profile_desc": "Bio, foto's & locatie",
  "home.tile_leads_desc": "Binnenkomende aanvragen",
  "home.tile_analytics_desc": "Bereik & conversie",
  "home.tile_basic_desc": "Uurtarief & aanbetaling",
  "home.tile_engine_desc": "Stijlen & multipliers",
  "home.tile_branding_desc": "Look & feel aanpassen",
  "home.stats_this_month": "Deze maand",
  "home.stat_leads": "Leads",
  "home.stat_calcs": "Berekeningen",
  "home.stat_conversion": "Conversie",
  "home.stat_avg_lead": "Gem. leadwaarde",
  "home.leads_this_month": "deze maand",
  "leads.filter_all": "Alle",
  "leads.filter_photo": "Met foto",
  "leads.filter_quality": "Hoge kwaliteit",
  "leads.drawer_estimate": "Prijsindicatie",
  "leads.drawer_style": "Stijl",
  "leads.drawer_size": "Formaat",
  "leads.drawer_placement": "Plaatsing",
  "leads.drawer_complexity": "Complexiteit",
  "leads.drawer_message": "Bericht van klant",
  "leads.drawer_reference": "Referentiefoto",
  "leads.drawer_status": "Status",
  "leads.drawer_save_price": "Opslaan",
  "leads.drawer_whatsapp": "Stuur WhatsApp bericht",
  "leads.drawer_no_phone": "Geen telefoonnummer beschikbaar voor WhatsApp",
  "score.profile_score": "Profiel score",
  "score.excellent": "Uitstekend",
  "score.good": "Goed",
  "score.incomplete": "Onvolledig",
  "dash.share_title": "Deel je Calculator",
  "dash.share_desc": "Deel deze link of code met je klanten zodat zij een prijsindicatie kunnen berekenen.",
  "dash.link_copied": "Link gekopieerd!",
  "dash.code_copied": "Code gekopieerd!",
  "dash.settings_title": "Prijsinstellingen",
  "dash.settings_desc":
    "Pas hier je tarieven en multipliers aan. Wijzigingen worden direct toegepast in de klant-calculator.",
  "dash.saved": "Opgeslagen!",
  "dash.settings_saved": "Je basisinstellingen zijn bijgewerkt.",
  "dash.save_error": "Kon instellingen niet opslaan.",
  "dash.view_calculator": "Bekijk Calculator",
  "dash.settings_label": "Instellingen",
  "dash.profile_studio": "Profiel & Studio",
  "dash.calculator_config": "Calculator Configuratie",
  "dash.pro_features": "Pro Functies",
  "dash.faq_management": "FAQ Beheer",
  "dash.faq_desc": "Voeg veelgestelde vragen toe die op je calculator-pagina worden getoond.",
  "dash.custom_branding": "Custom Branding",
  "dash.whatsapp_leads": "WhatsApp Leads",
  "dash.team_management": "Teambeheer",
  "dash.subscription": "Abonnement",
  "dash.subscription_desc": "Beheer je abonnement en facturatie.",
  "dash.current_plan": "Huidig plan",
  "upgrade.view_plans": "Bekijk plannen",
  "dash.no_leads": "Nog geen leads ontvangen.",
  "dash.leads_appear": "Zodra klanten je via WhatsApp contacteren verschijnen ze hier.",
  "dash.anonymous_request": "Anonieme aanvraag",
  "dash.view": "Bekijk",
  "dash.design_cost_hint": "Zet op 0 om ontwerpkosten uit te schakelen.",
  "dash.deposit_hint": "Zet op 0 om aanbetaling te verbergen.",
  "dash.save_changes": "Wijzigingen opslaan",
  "dash.cancel": "Annuleren",
  "dash.add": "Toevoegen",
  "dash.delete": "Verwijderen",
  "dash.new_faq": "Nieuwe FAQ toevoegen",
  "dash.ask_question": "Stel een vraag...",
  "dash.type_answer": "Typ het antwoord...",
  "dash.no_faqs": "Nog geen FAQ's. Voeg je eerste vraag toe hieronder.",
  "dash.faq_added": "FAQ toegevoegd",
  "dash.faq_updated": "FAQ bijgewerkt",
  "dash.faq_deleted": "FAQ verwijderd",
  "dash.add_error": "Fout bij toevoegen",
  "dash.save_error_generic": "Fout bij opslaan",
  "dash.delete_error": "Fout bij verwijderen",
  "dash.suggest_style": "Stijl voorstellen",
  "dash.suggest_style_desc": "Mis je een stijl? Stel deze voor en wij voegen hem mogelijk toe.",
  "dash.suggest_style_placeholder": "Bijv. Chicano, New School...",
  "dash.style_suggested": "Stijlsuggestie ingediend!",
  "dash.submit": "Indienen",
  "dash.upload_thumbnail": "Upload eigen afbeelding",
  "dash.reset_to_default": "Terug naar standaard",
  "dash.reset_all_thumbnails": "Alles resetten naar standaard",
  "dash.thumbnail_uploaded": "Stijlafbeelding geüpload!",
  "dash.thumbnail_reset": "Afbeelding teruggezet naar standaard",
  "dash.thumbnails_reset_all": "Alle stijlafbeeldingen teruggezet naar standaard",
  "dash.invite_created": "Uitnodiging aangemaakt!",
  "dash.invite_error": "Fout bij aanmaken uitnodiging",
  "dash.member_removed": "Lid verwijderd",
  "dash.link_copied_generic": "Link gekopieerd!",
  "dash.profile_saved": "Profiel opgeslagen!",
  "dash.profile_save_error": "Fout bij opslaan",
  "dash.calculator_ready": "🎉 Calculator klaar!",
  "dash.share_with_clients": "Deel je link met klanten.",
  "dash.previous": "Vorige",
  "dash.next": "Volgende",
  "dash.start_inkprice": "Start met InkPrice",
  "dash.step_of": "Stap {current} van {total}",
  "dash.onboard_min_desc":
    "Stel je minimumtarief in. Dit is de laagste prijs die je voor een tattoo berekent, ongeacht de grootte.",
  "dash.onboard_hourly_desc": "Stel je uurtarief in. Dit is de basis voor alle prijsberekeningen.",
  "dash.onboard_sizes_desc":
    "We hebben standaard grootte-categorieën voor je ingesteld. Je kunt deze later aanpassen in het dashboard.",
  "dash.onboard_styles_desc":
    "Alle gangbare stijlen zijn geactiveerd. Je kunt stijlen aan/uitzetten en multipliers aanpassen in het dashboard.",
  "dash.onboard_logo_desc": "Je kunt nu of later een logo uploaden via het Branding-tabblad in je dashboard.",
  "dash.onboard_logo_later": "Logo uploaden kan later via Dashboard → Branding",
  "dash.onboard_finish_title": "Je calculator is klaar!",
  "dash.onboard_finish_desc":
    "Deel je unieke link of code met klanten. Je kunt alle instellingen altijd aanpassen in je dashboard.",
  "dash.your_link": "Je calculator link:",
  "dash.online_card": "Dit is je online visitekaartje — zichtbaar op de directory en bovenaan je calculator.",
  "dash.view_my_calculator": "Bekijk mijn Calculator",
  "dash.impersonation_banner": "Je bekijkt momenteel het profiel van {name}",
  "dash.stop_impersonation": "Stop Impersonatie",
  "dash.dashboard_language": "Taal Dashboard",
  "dash.dashboard_language_desc": "De taal van je beheerpagina.",
  "tab.basic": "Basis",
  "tab.sizes": "Groottes",
  "tab.complexity": "Complexiteit",
  "tab.body_parts": "Lichaamsdelen",
  "tab.styles": "Stijlen",
  "tab.theme": "Thema",
  "tab.branding": "Branding",
  "tab.analytics": "Analytics",
  "basic.title": "Basistarief",
  "basic.desc": "Stel je standaardtarieven en kosten in",
  "basic.min_price": "Minimumtarief",
  "basic.min_price_desc": "De laagste prijs die je berekent",
  "basic.hourly_rate": "Uurtarief",
  "basic.hourly_rate_desc": "Je standaard uurtarief",
  "basic.daily_rate": "Dagtarief",
  "basic.daily_rate_placeholder": "Laat leeg indien niet van toepassing",
  "basic.design_cost": "Ontwerpkosten",
  "basic.fixed": "Vast",
  "basic.hourly": "Per uur",
  "basic.deposit": "Aanbetaling",
  "basic.percentage": "%",
  "basic.fixed_amount": "Vast",
  "basic.consultation_email": "Consultatie e-mail",
  "basic.consultation_email_desc": "E-mailadres voor consultatieverzoeken",
  "basic.booking_url": "Boekings-URL",
  "basic.booking_url_desc": "URL van extern boekingssysteem",
  "sizes.title": "Grootte-instellingen",
  "sizes.desc": "Stel de geschatte tijd per groottecategorie in",
  "sizes.min_hours": "Min. uren",
  "sizes.max_hours": "Max. uren",
  "sizes.hours": "uur",
  "complexity.title": "Complexiteit Multipliers",
  "complexity.desc": "Pas de prijsfactor aan op basis van de complexiteit van het ontwerp",
  "bodyparts.title": "Lichaamsdeel Multipliers",
  "bodyparts.desc": "Sommige lichaamsdelen zijn moeilijker te tatoeëren",
  "styles.title": "Stijl Beheer",
  "styles.desc": "Activeer of deactiveer stijlen en pas de prijsfactor aan. Klanten zien alleen actieve stijlen.",
  "theme.title": "Kleurenthema",
  "theme.desc": "Pas de kleuren van je calculator aan zodat deze bij je stijl past",
  "theme.preview": "Preview",
  "theme.preview_desc": "Zo ziet je calculator eruit",
  "theme.hue": "Tint",
  "theme.saturation": "Verzadiging",
  "theme.lightness": "Helderheid",
  "theme.presets": "Snelkeuze",
  "branding.title": "Branding",
  "branding.desc": "Upload je logo en stel je taal en valuta in",
  "branding.logo": "Logo",
  "branding.logo_desc": "Upload je studio logo (max 2MB, PNG of JPG)",
  "branding.upload": "Logo uploaden",
  "branding.remove": "Verwijderen",
  "branding.language": "Taal calculator",
  "branding.currency": "Valuta",
  "branding.qr_code": "QR Code",
  "branding.qr_desc": "Klanten kunnen deze QR-code scannen om direct naar je calculator te gaan.",
  "branding.download_qr": "Download QR",
  "sub.free": "Gratis",
  "sub.pro": "Pro",
  "sub.studio": "Studio",
  "sub.upgrade": "Upgrade",
  "sub.pro_feature": "Pro Feature",
  "sub.studio_feature": "Studio Feature",
  "sub.unlock_desc": "Upgrade je plan om deze feature te ontgrendelen.",
  "auth.login": "Inloggen",
  "auth.register": "Registreren",
  "auth.artist_name": "Artiestnaam",
  "auth.email": "E-mail",
  "auth.password": "Wachtwoord",
  "auth.submit_login": "Inloggen",
  "auth.submit_register": "Account aanmaken",
  "auth.switch_register": "Nog geen account? Registreer hier",
  "auth.switch_login": "Al een account? Log hier in",
  "auth.back_calc": "← Terug naar prijscalculator",
  "auth.login_failed": "Inloggen mislukt",
  "auth.wrong_credentials": "Onjuist e-mailadres of wachtwoord",
  "auth.account_exists": "Account bestaat al",
  "auth.already_registered": "Dit e-mailadres is al geregistreerd. Probeer in te loggen.",
  "auth.account_created": "Account aangemaakt!",
  "auth.check_email": "Controleer je e-mail om je account te bevestigen.",
  "auth.invalid_email": "Ongeldig e-mailadres",
  "auth.password_min": "Wachtwoord moet minimaal 6 karakters bevatten",
  "auth.name_min": "Naam moet minimaal 2 karakters bevatten",
  "auth.dashboard_subtitle": "Artiest Dashboard",
  "auth.invite_invalid": "Uitnodiging ongeldig of verlopen",
  "auth.invite_expired": "Uitnodiging is verlopen",
  "auth.invite_accepted": "🎉 Je bent toegevoegd aan de studio!",
  "size.mini": "Mini (< 5 cm)",
  "size.small": "Klein (5-10 cm)",
  "size.medium": "Medium (10-20 cm)",
  "size.large": "Groot (20-35 cm)",
  "size.xl": "Extra Large (35+ cm)",
  "complexity.simple": "Simpel",
  "complexity.medium": "Gemiddeld",
  "complexity.detailed": "Gedetailleerd",
  "body.arm": "Arm (algemeen)",
  "body.forearm": "Onderarm",
  "body.upper_arm": "Bovenarm",
  "body.leg": "Been (algemeen)",
  "body.thigh": "Bovenbeen",
  "body.calf": "Kuit",
  "body.back": "Rug",
  "body.chest": "Borst",
  "body.ribs": "Ribben",
  "body.stomach": "Buik",
  "body.hand": "Hand",
  "body.foot": "Voet",
  "body.neck": "Nek",
  "body.face": "Gezicht",
  "body.shoulder": "Schouder",
  "body.hip": "Heup",
  "body.finger": "Vinger",
  "body.other": "Overig",
  "body.selected": "Geselecteerd",
  "body.front": "Voorkant",
  "body.back_view": "Achterkant",
  "body.front_view": "Voorkant",
  "body.back_view_btn": "Achterkant",
  "style.black_grey": "Black & Grey",
  "style.full_color": "Full Colour",
  "style.realism": "Realisme",
  "style.fine_line": "Fine Line / Minimal",
  "style.traditional": "Traditional",
  "style.neo_traditional": "Neo Traditional",
  "style.watercolor": "Watercolor",
  "style.tribal": "Tribal",
  "style.japanese": "Japanese",
  "style.geometric": "Geometric",
  "style.lettering": "Lettering",
  "style.script": "Script",
  "style.dotwork": "Dotwork",
  "style.mandala": "Mandala",
  "style.blackwork": "Blackwork",
  "style.illustrative": "Illustrative",
  "style.surrealism": "Surrealism",
  "style.ornamental": "Ornamental",
  "style.micro_realism": "Micro Realism",
  "style.trash_polka": "Trash Polka",
  "style.other": "Overig",
  "style.chicano": "Chicano",
  "style.cyber_sigilism": "Cyber Sigilism",
  "style.patchwork": "Patchwork",
  "style.sketch": "Schets",
  "style.abstract": "Abstract",
  "style.minimalist": "Minimalistisch",
  "style.american_traditional": "American Traditional",
  "style.new_school": "New School",
  "style.biomechanical": "Biomechanisch",
  "style.dark_art": "Dark Art",
  "style.anime": "Anime",
  "style.kawaii": "Kawaii",
  "style.celtic": "Keltisch",
  "style.polynesian": "Polynesisch",
  "style.maori": "Maori",
  "style.haida": "Haida",
  "style.etching": "Etswerk",
  "style.woodcut": "Houtsnede",
  "style.portrait": "Portret",
  "style.pet_portrait": "Dierenportret",
  "style.floral": "Floraal",
  "style.botanical": "Botanisch",
  "style.sacred_geometry": "Heilige Geometrie",
  "style.horror": "Horror",
  "style.fantasy": "Fantasy",
  "style.mythology": "Mythologie",
  "style.sleeve_art": "Sleeve Art",
  "style.hand_poke": "Hand Poke",
  "style.stick_and_poke": "Stick and Poke",
  "style.negative_space": "Negatieve Ruimte",
  "style.white_ink": "Witte Inkt",
  "style.uv_ink": "UV Inkt",
  "color.gold": "Goud",
  "color.red": "Rood",
  "color.blue": "Blauw",
  "color.green": "Groen",
  "color.purple": "Paars",
  "color.pink": "Roze",
  "color.turquoise": "Turquoise",
  "color.orange": "Oranje",
  "wa.title": "Verstuur naar Artiest via WhatsApp",
  "wa.subtitle": "Stuur je berekening direct naar",
  "wa.name": "Je naam (optioneel)",
  "wa.name_placeholder": "Je naam",
  "wa.message": "Bericht (optioneel)",
  "wa.message_placeholder": "Beschrijf je idee, inspiratie of bijzonderheden...",
  "wa.photo": "Referentiefoto (optioneel)",
  "wa.upload_photo": "Upload foto",
  "wa.file_too_large": "Bestand te groot",
  "wa.summary": "Samenvatting:",
  "wa.send": "Verstuur via WhatsApp",
  "wa.generating_plan": "Tattoo plan wordt aangemaakt...",
  "info.location": "Locatie",
  "wa.sent_title": "Bericht verstuurd!",
  "wa.sent_desc": "Je aanvraag is naar {name} gestuurd via WhatsApp.",
  "wa.upload_failed": "Upload mislukt",
  "wa.upload_failed_desc": "Kon de referentiefoto niet uploaden. Probeer het opnieuw.",
  "wa.save_failed": "Aanvraag opslaan mislukt",
  "wa.success": "Verstuurd!",
  "wa.success_desc": "Je aanvraag is succesvol aangemaakt en WhatsApp is geopend.",
  "wa.error": "Er ging iets mis",
  "wa.error_desc": "Onbekende fout bij het versturen. Controleer je internetverbinding en probeer het opnieuw.",
  "wa.msg_header": "🖊 Nieuwe Tattoo Aanvraag via Inkvaluator",
  "wa.msg_name": "Naam",
  "wa.msg_price": "💰 Geschatte prijs",
  "wa.msg_size": "📏 Grootte",
  "wa.msg_style": "🎨 Stijl",
  "wa.msg_placement": "📍 Plaatsing",
  "wa.msg_complexity": "⚙️ Complexiteit",
  "wa.msg_message": "💬 Bericht",
  "wa.msg_details": "🔗 Bekijk alle details",
  "wa.msg_greeting": "Hoi {name}, ik heb een prijsindicatie berekend:",
  "wa.msg_name_label": "Naam",
  "wa.msg_style_label": "🎨 Stijl",
  "wa.msg_bodypart_label": "📍 Lichaamsdeel",
  "wa.msg_size_label": "📏 Formaat",
  "wa.msg_complexity_label": "⚙️ Complexiteit",
  "wa.msg_time_label": "⏱ Geschatte tijd",
  "wa.msg_total_label": "💰 Totaalbedrag",
  "wa.msg_deposit_label": "💳 Aanbetaling",
  "wa.msg_details_label": "🔗 Details",
  "lead.not_found": "Lead niet gevonden",
  "lead.invalid_link": "Deze link is ongeldig of verlopen.",
  "lead.back_home": "Terug naar Home",
  "lead.details": "Lead Details",
  "lead.title": "Tattoo Aanvraag",
  "lead.for": "Voor",
  "lead.customer": "Klant",
  "lead.estimated_price": "Geschatte prijs",
  "lead.size": "Grootte",
  "lead.style": "Stijl",
  "lead.placement": "Plaatsing",
  "lead.complexity": "Complexiteit",
  "lead.customer_message": "Bericht van klant",
  "lead.reference_photo": "Referentiefoto",
  "consult.title": "Klaar om te starten?",
  "consult.desc": "Vraag een consult aan om je tattoo te bespreken en een definitieve prijs te krijgen.",
  "consult.book": "Consult boeken",
  "consult.email": "Consult aanvragen",
  "consult.email_subject": "Consult aanvraag via Inkvaluator",
  "pdf.title": "Prijsindicatie",
  "pdf.download": "Download PDF",
  "pdf.your_selection": "Jouw selectie",
  "admin.title": "Super Admin",
  "upgrade.title": "Kies je Plan",
  "upgrade.free_desc": "Perfect om te starten",
  "upgrade.pro_desc": "Alles voor professionals",
  "upgrade.studio_desc": "Voor studio's met meerdere artiesten",
  "upgrade.your_plan": "Jouw plan",
  "upgrade.manage": "Abonnement beheren",
  "upgrade.per_month": "/maand",
  "upgrade.monthly": "Maandelijks",
  "upgrade.yearly": "Jaarlijks",
  "upgrade.mo": "mnd",
  "upgrade.year": "jaar",
  "upgrade.cancel_anytime": "Je kunt op elk moment opzeggen. Geen verborgen kosten.",
  "billing.expires": "Verloopt",
  "billing.change": "Wijzigen",
  "billing.next_invoice": "Volgende factuur",
  "billing.invoice_history": "Factuurgeschiedenis",
  "upgrade.choose": "Kies je",
  "upgrade.plan_word": "Plan",
  "upgrade.subtitle": "Van solo artiest tot volledige studio — kies het plan dat bij je past.",
  "upgrade.your_plan_badge": "Jouw plan",
  "upgrade.perfect_start": "Perfect om te starten",
  "upgrade.everything_pro": "Alles voor professionals",
  "upgrade.for_studios": "Voor tattoo studio's & teams",
  "upgrade.manage_sub": "Abonnement beheren",
  "upgrade.switch_pro": "Overstappen naar Pro",
  "upgrade.upgrade_pro": "Upgraden naar Pro",
  "upgrade.upgrade_studio": "Upgraden naar Studio",
  "upgrade.current_plan": "Huidig plan",
  "upgrade.renews_on": "Verlengd op",
  "upgrade.cancel_anytime_long": "Je kunt op elk moment opzeggen. Geen verborgen kosten.",
  "upgrade.questions": "Vragen? Neem contact op via het dashboard.",
  "upgrade.promo_placeholder": "Kortingscode (optioneel)",
  "upgrade.discount": "korting",
  "analytics.title": "Analytics",
  "analytics.desc": "Inzicht in het gebruik van je calculator",
  "analytics.calculations": "Berekeningen",
  "analytics.total_calculations": "Totaal berekeningen",
  "analytics.consult_requests": "Consult aanvragen",
  "analytics.conversion_rate": "Conversie ratio",
  "analytics.this_month": "Deze maand",
  "analytics.avg_price": "Gem. Geschatte Prijs",
  "analytics.top_style": "Populairste Stijl",
  "analytics.top_body_part": "Populairste Lichaamsdeel",
  "analytics.per_month": "Berekeningen per Maand",
  "analytics.popular_styles": "Populaire stijlen",
  "analytics.popular_body_parts": "Populaire lichaamsdelen",
  "analytics.avg_estimate_trend": "Gemiddelde prijsindicatie trend",
  "analytics.style_distribution": "Stijl Verdeling",
  "analytics.body_part_distribution": "Lichaamsdeel Verdeling",
  "analytics.no_data": "Nog geen data. Deel je calculator om inzichten te verzamelen.",
  "analytics.directory_leads": "Directory Leads",
  "analytics.directory_leads_desc": "Kliks vanuit de ontdekken-pagina",
  "studio.title": "Studio Beheer",
  "studio.team": "Team",
  "studio.team_members": "Teamleden",
  "studio.invite": "Artiest Uitnodigen",
  "studio.invite_desc": "Genereer een uitnodigingslink om een artiest aan je studio te koppelen.",
  "studio.role": "Rol",
  "studio.members": "Leden",
  "studio.branding": "Studio Branding",
  "studio.pending_invites": "Openstaande uitnodigingen",
  "studio.no_members": "Nog geen teamleden. Nodig artiesten uit om lid te worden.",
  "studio.generate_link": "Genereer Link",
  "studio.email_optional": "E-mail (optioneel)",
  "studio.expires": "Verloopt",
  "studio.unknown": "Onbekend",
  "feature.calculator": "Custom prijscalculator",
  "feature.qr": "QR-code & deelbare link",
  "feature.styles_free": "Onbeperkte stijlen uit de Master List",
  "feature.styles": "Onbeperkte stijlen & AI scanner",
  "feature.ai_scanner": "AI Scanner (incl. Lichaamsplaatsing)",
  "feature.whatsapp_leads": "WhatsApp Leads",
  "feature.logo_branding": "Custom Branding (Logo + 8 Presets)",
  "feature.google_reviews": "Google Review Sync",
  "feature.portfolio_showcase": "Portfolio Showcase",
  "feature.verified_badge": "'Verified' Artiest Badge",
  "feature.referral_system": "Doorverwijsprogramma",
  "feature.logo": "Logo uploaden",
  "feature.theme": "Eigen kleurenthema",
  "feature.language": "Meertalig (17 talen)",
  "feature.currency": "Valuta wisselen (13 valuta)",
  "feature.analytics": "Gebruiksanalyse",
  "feature.pdf": "PDF Export",
  "feature.whitelabel": "White-label branding",
  "feature.map_visibility": "Wereldwijde kaart zichtbaarheid",
  "feature.multiartist": "Multi-artiest beheer",
  "feature.centralanalytics": "Centraal analytics dashboard",
  "feature.teammanagement": "Team- & rolbeheer",
  "feature.shared_branding": "Gedeelde studio branding",
  "style_city.page_title": "Beste {style} Tattoo Artiesten in {city}",
  "style_city.h1_prefix": "Beste",
  "style_city.h1_suffix": "Tattoo Artiesten in",
  "blog.title": "Blog",
  "blog.no_posts": "Nog geen artikelen gepubliceerd.",
  "blog.seo_title": "Blog | Inkvaluator",
  "blog.seo_desc": "Tips, trends en inzichten over tattoo pricing en de tattoo-industrie.",
  "blog.read_more": "Lees meer",
  "blog.published_on": "Gepubliceerd op",
  "blog.back": "Terug naar blog",
  "blog.not_found": "Artikel niet gevonden",
  "blog.translation_notice": "Dit artikel is nog niet beschikbaar in jouw taal. Je ziet de originele versie.",
  "landing.start_free_btn": "Start gratis",
  "landing.upgrade_btn": "Upgrade",
  "landing.professionals": "Voor professionals",
  "landing.studios_teams": "Voor studio's & teams",
  "landing.trust_bar_prefix": "Vertrouwd door",
  "landing.trust_bar_suffix": "tattoo artiesten wereldwijd",
  "nav.discover": "Ontdekken",
  "nav.calculator": "Calculator",
  "nav.artists": "Artiesten",
  "nav.my_calculator": "Mijn Calculator",
  "nav.manage": "Beheer",
  "footer.blog": "Blog",
  "laf.title": "Look & Feel",
  "laf.desc": "Pas het uiterlijk van je publieke calculator-pagina aan.",
  "laf.color_mode": "Kleurmodus",
  "laf.dark_mode": "Donkere modus",
  "laf.light_mode": "Lichte modus",
  "laf.background": "Achtergrond",
  "laf.solid_color": "Effen kleur",
  "laf.image_url": "Afbeelding URL",
  "laf.preview": "Preview",
  "laf.calculator_preview": "Calculator Preview",
  "laf.new_layout": "Nieuwe layout beschikbaar!",
  "laf.new_layout_desc": "Er is een nieuwe layout (v2) beschikbaar met verbeterde styling. Wil je updaten?",
  "laf.update": "Updaten",
  "laf.saved": "Look & Feel opgeslagen",
  "laf.reset_success": "Thema teruggezet naar standaard",
  "admin.stripe_managed":
    "Abonnementen worden automatisch beheerd via Stripe — wijzigingen zijn zichtbaar na de volgende webhook sync",
  "calc.view_tattoo_plan": "Bekijk jouw Tattoo Plan ✓",
  "wa.plan_message": "Hier is mijn Tattoo Plan:",
  "lead.tattoo_plan": "Tattoo Plan",
  "lead.made_with": "Gemaakt met Inkvaluator",
  "lead.contact_artist": "Neem contact op met {artist}",
  "lead.share": "Deel",
  "lead.share_title": "Mijn Tattoo Plan",
  "lead.link_copied": "Link gekopieerd!",
  "lead.deposit_label": "Aanbetaling",
  "lead.pay_deposit": "Betaal aanbetaling {amount}",
  "lead.deposit_request_title": "Depositregeling aanvragen",
  "lead.deposit_request_desc":
    "Vraag de artiest of een aanbetaling van ~{amount} mogelijk is om de afspraak vast te zetten.",
  "lead.deposit_request_wa": "Stuur aanvraag via WhatsApp",
  "lead.for_you": "Jou",
  "lead.for_label": "Voor",
  "lead.back_to_calculator": "Terug naar calculator",
  "lead.session_duration": "Sessieduur",
  "lead.session_estimate": "~{hours} uur",
  "lead.deposit_wa_msg":
    "Hallo {artist}, ik heb mijn Tattoo Plan bekeken op Inkvaluator. Zou je open staan voor een depositregeling van ~{amount} om de afspraak vast te zetten?\nPlan: {url}",
  "blog.no_translation": "Geen vertaling beschikbaar",
  "flash.tab": "Flash",
  "flash.upload": "Flash design uploaden",
  "flash.title": "Titel",
  "flash.price": "Prijs",
  "flash.size": "Grootte (cm)",
  "flash.available": "Beschikbaar",
  "flash.featured": "Uitgelicht",
  "flash.limit_used": "van {max} flash designs gebruikt",
  "flash.delete_confirm": "Flash design verwijderen?",
  "flash.reserve_whatsapp": "Reserveer via WhatsApp",
  "flash.whatsapp_msg": "Ik ben geïnteresseerd in jouw flash tattoo:",
  "flash.matching_designs": "Passende flash designs",
  "flash.only_with_flash": "Alleen artiesten met flash designs",
  "groei.score": "Profiel score",
  "groei.views": "profielbezoeken",
  "groei.sessions": "calculatorsessies",
  "groei.leads": "leads",
  "groei.this_week": "deze week",
  "groei.improve": "Verbeter je profiel",
  "groei.add_photos": "Voeg meer portfolio foto's toe",
  "groei.add_whatsapp": "Stel je WhatsApp nummer in",
  "groei.add_bio": "Schrijf een langere bio",
  "groei.add_avatar": "Upload een profielfoto",
  "groei.add_rate": "Stel je uurtarief in",
  "groei.add_city": "Voeg je stad toe",
  "groei.get_leads": "Deel je calculator om leads te ontvangen",
  "groei.collapse": "Inklappen",
  "groei.expand": "Uitklappen",
  "groei.tip_portfolio": "Artiesten met 6+ foto's krijgen 3x meer calculator sessies",
  "groei.tip_whatsapp": "Klanten met een WhatsApp knop converteren 5x beter",
  "groei.tip_leads": "Deel je calculator link om je eerste lead te ontvangen",
  "groei.tip_avatar": "Een profielfoto verhoogt je geloofwaardigheid",
  "groei.tip_action_photos": "Foto's toevoegen",
  "groei.tip_action_whatsapp": "WhatsApp instellen",
  "groei.tip_action_share": "Calculator delen",
  "groei.tip_action_avatar": "Foto uploaden",
  "milestone.eerste_lead": "Eerste lead ontvangen!",
  "milestone.tien_leads": "10 leads bereikt!",
  "milestone.profiel_compleet": "Profiel 100% compleet!",
  "milestone.eerste_flash": "Eerste flash design geüpload!",
  "milestone.profile_complete": "🎉 Profiel compleet! Je profiel scoort nu maximaal in zoekresultaten.",
  "groei.completeness_tip_prefix": "Tip:",
  "owner_hint.incomplete": "Je profiel is {score}% compleet",
  "owner_hint.complete": "Profiel compleet ✓",
  "owner_hint.cta": "Maak het af →",
  "wa.name_required": "Naam is verplicht",
  "wa.plan_message_full": "Bekijk mijn volledige Tattoo Plan: {url}",
  "laf.overlay_opacity": "Overlay Dekking",
  "laf.overlay_opacity_desc": "Verdonker de achtergrondafbeelding voor betere leesbaarheid.",
  "laf.bg_attachment": "Achtergrond Scroll",
  "laf.bg_fixed": "Vast (Parallax)",
  "laf.bg_scroll": "Scroll",
  "consult_settings.title": "Consultatie Instellingen",
  "consult_settings.desc": "Stel in hoe klanten een consultatie kunnen aanvragen via je calculator-pagina.",
  "consult_settings.booking_url_label": "Boekings-URL",
  "consult_settings.booking_url_placeholder": "https://calendly.com/jouw-naam",
  "consult_settings.booking_url_desc": "Link naar je afsprakensysteem (Calendly, Acuity, etc.)",
  "consult_settings.email_label": "Consultatie e-mail",
  "consult_settings.email_placeholder": "boekingen@jestudio.nl",
  "consult_settings.email_desc": "Fallback e-mail als er geen boekings-URL is ingesteld",
  "onboard.step_minimum": "Minimumtarief",
  "onboard.step_hourly": "Uurtarief",
  "onboard.step_sizes": "Groottes bevestigen",
  "onboard.step_styles": "Stijlen instellen",
  "onboard.step_logo": "Logo uploaden",
  "onboard.step_finish": "Calculator klaar!",
  "onboarding.verified_found": "GGD-geverifieerd profiel gevonden",
  "onboarding.welcome_back": "Welkom terug",
  "onboarding.check_details": "We hebben jouw studio gevonden. Controleer of de gegevens kloppen.",
  "onboarding.confirm_studio": "Ja, dit is mijn studio",
  "onboarding.not_your_studio": "Niet jouw studio? Neem contact op via",
  "onboarding.ggd_verified_text": "GGD-vergunning geverifieerd — dit studio staat geregistreerd bij het RIVM",
  "onboarding.set_rates": "Stel je tarieven in",
  "onboarding.rates_desc": "Klanten zien een eerlijke prijsindicatie gebaseerd op jouw tarieven. Je kunt dit altijd nog aanpassen.",
  "onboarding.hourly_rate": "Uurtarief",
  "onboarding.minimum_price": "Minimumprijs",
  "onboarding.market_avg": "Marktgemiddelde in",
  "onboarding.per_hour": "per uur",
  "onboarding.minimum": "minimum",
  "onboarding.your_styles": "Jouw specialiteiten",
  "onboarding.styles_desc": "Klanten zoeken op stijl. Selecteer wat je aanbiedt.",
  "onboarding.styles_skip": "Sla over als je alle stijlen aanbiedt",
  "onboarding.activate": "Profiel activeren",
  "onboarding.creating": "Profiel wordt aangemaakt...",
  "onboarding.adjust_later": "Je kunt dit later altijd aanpassen in je dashboard",
  "onboarding.welcome": "Welkom bij Inkvaluator",
  "onboarding.welcome_desc": "In 3 minuten staat jouw profiel live. We stellen je calculator in zodat klanten direct een prijsindicatie kunnen opvragen.",
  "onboarding.your_city": "In welke stad werk jij?",
  "onboarding.city_placeholder": "bijv. Amsterdam",
  "onboarding.city_hint": "Dit helpt klanten in jouw omgeving jou te vinden",
  "onboarding.get_started": "Aan de slag",
  "onboarding.profile_live": "Jouw profiel is live! 🎉",
  "onboarding.profile_live_desc": "Klanten kunnen nu een prijsindicatie opvragen via jouw unieke link. Deel hem met je volgende klant.",
  "onboarding.your_link": "Jouw calculator link",
  "onboarding.copy": "Kopieer",
  "onboarding.copied": "Gekopieerd",
  "onboarding.to_dashboard": "Naar mijn dashboard",
  "onboarding.view_profile": "Bekijk mijn profiel",
  "onboarding.styles_selected": "stijl(en) geselecteerd",
  "onboarding.back": "Terug",
  "onboarding.next": "Verder",
  "onboarding.wait": "Even geduld...",
  "profile.identity": "Identiteit",
  "profile.identity_desc": "Je naam en studionaam zoals die verschijnen op je publieke pagina.",
  "profile.studio_name": "Studionaam",
  "profile.studio_placeholder": "Bijv. Ink House Amsterdam",
  "profile.location": "Locatie",
  "profile.location_desc": "Je adresgegevens voor de directory en routeplanners.",
  "profile.studio_address_info":
    "Je studio-adres wordt automatisch overgenomen. Vul hieronder afwijkende gegevens in om deze te overschrijven.",
  "profile.street": "Straat en huisnummer",
  "profile.postal_code": "Postcode",
  "profile.city": "Stad",
  "profile.contact_socials": "Contact & Socials",
  "profile.contact_desc": "Hoe klanten je kunnen bereiken en vinden.",
  "profile.phone_label": "Telefoonnummer / WhatsApp",
  "profile.phone_hint": "Dit nummer wordt gebruikt voor de WhatsApp-knop op je calculator.",
  "profile.booking_hint": "Link naar je afsprakensysteem (Calendly, Acuity, etc.)",
  "profile.calculator_language": "Taal van je Calculator",
  "profile.calculator_language_desc": "De taal die je klanten zien op de calculator.",
  "ai.file_too_large": "Bestand te groot",
  "ai.max_size": "Max 10MB",
  "ai.rate_limit": "Limiet bereikt",
  "ai.rate_limit_desc": "Max 3 scans per 24 uur.",
  "ai.scan_complete": "✨ AI Scan compleet",
  "ai.scan_applied": "De calculator is automatisch aangepast.",
  "ai.scan_failed": "Scan mislukt",
  "ai.try_again": "Probeer het opnieuw",
  "ai.title": "AI Scanner",
  "ai.desc": "Upload een foto en laat AI de instellingen bepalen",
  "ai.upload_prompt": "Maak een foto of upload een referentie-afbeelding",
  "ai.analyzing": "AI analyseert...",
  "ai.result_title": "AI Resultaat",
  "ai.result_size": "Grootte",
  "ai.result_complexity": "Complexiteit",
  "ai.result_style": "Stijl",
  "ai.result_color": "Kleur",
  "ai.yes": "Ja",
  "ai.no": "Nee",
  "ai.choose_other": "Andere foto kiezen",
  "ai.phase_detecting": "Complexiteit detecteren...",
  "ai.phase_style": "Stijl analyseren...",
  "ai.phase_placement": "Lichaamsplaatsing detecteren...",
  "ai.phase_finalizing": "Schatting afronden...",
  "ai.result_placement": "Plaatsing",

  "style_city.meta_desc": "Vind {count} {style} tattoo artiesten in {city}. Vergelijk prijzen en boek je sessie.",
  "style_city.tattoo_in": "Tattoo in",
  "style_city.subtitle": "Bekijk {count} geverifieerde {style} artiesten in {city}.",
  "style_city.no_artists": "Nog geen {style} artiesten gevonden in {city}.",
  "style_city.all_in_city": "Alle artiesten in {city}",
  "city.artists_in": "artiesten in",

  "embed.verified_badge": "Verified Badge",
  "embed.badge_desc":
    "Plaats deze badge op je website om te tonen dat je een geverifieerde Inkvaluator artiest bent. Linkt terug naar je profiel.",

  "analytics.upsell_title": "Regionale Stijlkansen",
  "analytics.upsell_desc":
    "Populaire stijlen in jouw regio waarvoor je nog geen multipliers hebt ingesteld — upsell kans!",
  "analytics.upsell_demand": "{count} berekeningen in jouw regio",
  "analytics.upsell_activate": "Activeer →",
  "analytics.coach_title": "Business Coach",
  "analytics.coach_desc": "Persoonlijke groei-tips op basis van jouw data",
  "analytics.coach_missing_style": "Je mist leads voor {style} in {city}. Overweeg deze stijl te activeren!",
  "analytics.coach_low_conversion": "Je conversie is {rate}%. Voeg meer contactkanalen toe om dit te verhogen.",
  "analytics.coach_no_consults":
    "Je hebt {calc} berekeningen maar 0 consultaanvragen. Zorg dat WhatsApp of e-mail is ingesteld!",
  "analytics.coach_growing": "Goed bezig! Je berekeningen zijn {pct}% gestegen deze maand.",
  "analytics.coach_top_style": "Je meest gevraagde stijl is {style}. Highlight dit in je portfolio!",
  "discover.live_ticker": "Iemand in {city} heeft zojuist een prijs berekend voor een {style} tattoo",
  "landing.calculate_price": "Bereken Prijs",
  "success.welcome": "Welkom bij de Club,",
  "success.desc": "Je {plan}-functies zijn direct geactiveerd. Laten we meteen aan de slag gaan.",
  "success.active": "Actief — alle functies ontgrendeld",
  "success.syncing": "Je plan wordt geactiveerd…",
  "success.setup_whatsapp": "Stel WhatsApp Leads in",
  "success.go_dashboard": "Naar je Dashboard",
  "success.view_details": "Bekijk je plan details",
  "discover.subtitle": "Ontdek Studios & Artiesten",
  "discover.title": "Ontdek",
  "discover.seo_title": "Vind een Tattoo Artist bij jou in de buurt | Inkvaluator",
  "discover.seo_desc": "Ontdek tattoo artiesten bij jou in de buurt. Bekijk portfolios, vergelijk prijzen en bereken direct een tattooprijsindicatie.",
  "discover.title_accent": "Artiesten",
  "discover.description":
    "Vind tattoo-artiesten en studio's bij jou in de buurt. Bekijk hun prijscalculator en neem contact op.",
  "discover.search_placeholder": "Zoek op stad, artiest of studio...",
  "discover.map_title": "Artiesten op de kaart",
  "discover.hide_map": "Verberg kaart",
  "discover.show_map": "Toon kaart",
  "discover.map_loading": "Kaart laden...",
  "discover.no_artists": "Geen artiesten gevonden",
  "discover.no_artists_for": 'Geen artiesten gevonden voor "{query}".',
  "discover.playground": "Playground",
  "discover.artist_login": "Artiest Login",
  "discover.call": "Bellen",
  "discover.route": "Route",
  "discover.view_calculator": "Bekijk Calculator",
  "footer.tagline": "De slimste tattoo prijscalculator voor artiesten en klanten.",
  "footer.links": "Links",
  "footer.discover_artists": "Ontdek Artiesten",
  "footer.artist_login": "Artiest Login",
  "footer.privacy": "Privacybeleid",
  "footer.app": "App",
  "footer.coming_soon": "Binnenkort beschikbaar op:",
  "footer.built_with": "Gebouwd met Lovable",
  "footer.copyright": "© {year} InkValuator. Alle rechten voorbehouden.",
  "footer.language_label": "Taal & App",
  "footer.pwa_badge": "Geoptimaliseerd voor PWA (Klik voor info)",
  "footer.all_rights": "Alle rechten voorbehouden.",
  "footer.install_title": "Installeren",
  "footer.install_desc":
    "Gebruik Inkvaluator als een echte app op je telefoon. Snel, veilig en zonder download uit de store.",
  "footer.install_ios_title": "iPhone / iPad (iOS)",
  "footer.install_ios_desc": 'Tik op de Deel-knop onderaan en kies "Zet op beginscherm".',
  "footer.install_android_title": "Android",
  "footer.install_android_desc": 'Tik op de drie puntjes rechtsboven en kies "App installeren".',
  "footer.install_ok": "Duidelijk!",
  "dash.unsaved_changes": "Je hebt onopgeslagen wijzigingen",
  "embed.title": "Embed Widget",
  "embed.desc": "Plaats je calculator op je eigen website met een iframe of floating button.",
  "embed.iframe_desc":
    "Plak dit in je HTML om de calculator als embedded element weer te geven. De navigatiebalk en footer worden automatisch verborgen.",
  "embed.floating_desc":
    "Plak dit vlak voor de </body> tag. Er verschijnt een gouden knop rechtsonder die je calculator opent in een modal.",
  "embed.copy_code": "Kopieer Code",
  "demo.subtitle": "Playground",
  "demo.title": "Probeer de",
  "demo.title_accent": "Calculator",
  "demo.description":
    "Pas de variabelen aan en ontdek hoe de tattoo-prijscalculator werkt. Stel je eigen uurprijs en startkosten in.",
  "demo.your_rates": "Jouw tarieven",
  "demo.rates_desc": "Pas de sliders aan om met verschillende prijsmodellen te experimenteren",
  "demo.hourly_rate": "Uurprijs",
  "demo.start_cost": "Vaste startkosten",
  "demo.size": "Grootte",
  "demo.complexity": "Complexiteit",
  "demo.placement": "Plaatsing",
  "demo.estimated_price": "Geschatte prijs",
  "demo.est_time": "Geschatte tijd (incl. detail)",
  "demo.start_costs": "Startkosten",
  "demo.total_multiplier": "Totale multiplier",
  "demo.disclaimer": "Dit is een demo. De werkelijke prijs hangt af van de artiest en het specifieke ontwerp.",
  "demo.artist_cta": "Ben jij een artiest?",
  "demo.artist_cta_desc": "Gebruik deze tool op jouw eigen site en laat klanten direct een inschatting maken.",
  "demo.register_free": "Gratis aanmelden",
  "demo.find_studio": "Zoek een studio in de buurt",
  "demo.footer_note": "Dit is een demo met standaard tarieven. De echte prijs bereken je via de calculator van een artiest in jouw stad.",
  "demo.view_artists": "Bekijk artiesten",
  "city.title": "Tattoo artiesten in {city}",
  "city.description": "Vind tattoo artiesten en studio's in {city}. Vergelijk prijzen en boek een consultatie.",
  "city.no_artists": "Geen artiesten gevonden in {city}.",
  "city.discover_all": "Ontdek alle artiesten",
  "city.back": "Alle steden",
  "city.avg_rate": "Gemiddeld uurtarief: {rate}.",
  "city.market_indicator": "Markt-indicatie voor {city}",
  "city.meta_desc":
    "Vind de beste tattoo artiesten in {city}. Gemiddelde uurprijs in deze stad: {rate}. Bereken direct je prijs bij lokale specialisten.",

  // Map
  "map.view_calculator": "Bekijk Calculator",
  "map.unknown_location": "Onbekende locatie",
  "map.artists_count": "artiesten",
  "map.world_title": "Artiesten",
  "map.world_accent": "Wereldwijd",
  "map.in_countries": "in {count} landen",
  "map.filtered_by": "Gefilterd op",
  "map.clear_filter": "Filter wissen",

  // SEO
  "seo.landing_title": "Inkvaluator – Tattoo Prijscalculator",
  "seo.landing_desc":
    "Bereken direct je tattoo prijs op basis van stijl, grootte en plaatsing. Vind tattoo artiesten bij jou in de buurt.",
  "seo.calculator_title": "Tattoo Prijscalculator",
  "seo.calculator_desc":
    "Ontvang direct een prijsindicatie voor je tattoo bij {artist} op basis van vakmanschap, stijl en plaatsing.",
  "seo.profile_desc_city": "Bekijk het profiel van",
  "seo.in_city": "in",
  "seo.from_price": "Vanaf",
  "seo.calculator_cta": "Bereken direct je tattooprijsindicatie.",
  "calc.next_slot": "Vroegste slot",
  "calc.book_appointment": "Boek afspraak",
  "calc.open_for_bookings": "Open voor boekingen",

  // Footer extra
  "footer.terms": "Algemene Voorwaarden",
  "footer.support": "Support",
  "footer.how_it_works": "Hoe werkt het?",

  // How It Works page
  "hiw.seo_title": "Hoe werkt het? – Inkvaluator",
  "hiw.seo_desc":
    "Ontdek hoe Inkvaluator tatoeëerders helpt met automatische prijsindicaties, slimme leads en professionele branding.",
  "hiw.hero_title": "Focus op de Inkt, Niet op de Factuur",
  "hiw.hero_p1":
    "Ben je meer tijd kwijt aan het beantwoorden van DM's over prijzen dan aan het tekenen van je volgende masterpiece? Laten we eerlijk zijn: je bent een tatoeëerder, geen klantenservice-medewerker.",
  "hiw.hero_p2":
    'Elke "Wat kost dit?"-vraag die je handmatig moet beantwoorden, is tijd die je niet aan je kunst besteedt.',
  "hiw.hero_p3":
    "Inkvaluator is de digitale assistent die jouw business filtert. Terwijl jij in de zone zit, berekent de tool nauwkeurige prijsindicaties voor je klanten, verzamelt alle nodige info en dropt een kant-en-klare aanvraag in je WhatsApp.",
  "hiw.hero_p4":
    'Geen "prijskijkers" meer die na drie uur praten toch geen budget hebben. Alleen nog serieuze leads, professionele prijsopgaven en een branding die laat zien dat jij je vak serieus neemt.',
  "hiw.hero_cta": "Klaar om je stoeltijd terug te winnen?",
  "hiw.faq_title": "Veelgestelde Vragen",
  "hiw.faq_cat1": "De Basis: Hoe helpt dit mij?",
  "hiw.faq_q1": "Is dit weer de zoveelste ingewikkelde app?",
  "hiw.faq_a1":
    "Nee. Het is een supersnelle calculator die je in je Instagram-bio zet of op je site plakt. Je stelt één keer je regels in (uurtarief, startkosten, stijlen) en de tool doet de rest.",
  "hiw.faq_q2": "Hoe bespaart het me concreet tijd?",
  "hiw.faq_a2":
    'In plaats van tien keer te vragen: "Hoe groot? Waar op je lichaam? Welke stijl?", krijg jij één WhatsApp-bericht waarin dit allemaal al staat, inclusief de referentiefoto en de prijs die de klant al heeft geaccepteerd.',
  "hiw.faq_cat2": "Geld & Instellingen",
  "hiw.faq_q3": "Kan ik mijn eigen prijzen bepalen?",
  "hiw.faq_a3":
    "Absoluut. Jij bent de baas. Je stelt je eigen uurtarief in en bepaalt wat je rekent voor verschillende groottes en complexiteiten. De tool rekent precies zoals jij dat zou doen aan je tekentafel.",
  "hiw.faq_q4": "Wat als een tattoo veel ingewikkelder is dan gemiddeld?",
  "hiw.faq_a4":
    "Onze ingebouwde AI Scanner herkent de details in een foto. Hij ziet het verschil tussen een simpele 'fine line' tekst en een hyperrealistisch portret en past de prijsinschatting daar direct op aan.",
  "hiw.faq_cat3": "Uitstraling & Branding",
  "hiw.faq_q5": "Ziet het er niet uit als een standaard tooltje?",
  "hiw.faq_a5":
    "Zeker niet. Met het Studio-plan is de tool volledig 'white-label'. Dat betekent: jouw logo, jouw kleuren en jouw stijl. Er staan 8 professionele thema-presets voor je klaar (van Luxury Dark tot Modern Light) zodat de calculator naadloos aansluit bij jouw shop.",
  "hiw.faq_q6": "Hoe professioneel komt dit over op mijn klant?",
  "hiw.faq_a6":
    'Heel professioneel. De klant kan de prijsopgave downloaden als een strakke PDF. Dat geeft vertrouwen en voorkomt discussies achteraf over "wat we ook alweer hadden afgesproken".',
  "hiw.faq_cat4": "Groei & Bereik",
  "hiw.faq_q7": "Ik wil meer klanten uit mijn regio, helpt dit?",
  "hiw.faq_a7":
    "Ja! Inkvaluator heeft een interactieve wereldkaart. Klanten kunnen zoeken op land, provincie en stad. Hoe meer je de tool gebruikt, hoe beter je vindbaar wordt voor mensen die specifiek in jouw stad een artiest zoeken.",
  "hiw.backend_title": "Achter de Schermen: De Kracht van de Backend",
  "hiw.backend_intro":
    "Voor de tech-liefhebbers en de controlfreaks: Inkvaluator zit vol geavanceerde features die je studio naar een hoger niveau tillen.",
  "hiw.card_pricing_title": "Dynamisch Prijsbeheer",
  "hiw.card_pricing_text":
    "Via de app_settings in de Admin-module pas je prijzen en Stripe Price ID's aan zonder een regel code aan te raken. Alles is direct gesynchroniseerd met je checkout.",
  "hiw.card_ai_title": "AI Vision Integratie",
  "hiw.card_ai_text":
    "De AI Scanner maakt gebruik van beeldanalyse om patronen, kleurgebruik en complexiteit te vertalen naar arbeidsuren.",
  "hiw.card_stripe_title": "Stripe Customer Portal",
  "hiw.card_stripe_text":
    "Een volledig geautomatiseerd abonnementssysteem. Upgraden naar Pro, je facturen downloaden of je abonnement beheren/opzeggen gebeurt in een beveiligde omgeving.",
  "hiw.card_analytics_title": "Analytics Dashboard",
  "hiw.card_analytics_text":
    "Krijg diepgaand inzicht in je conversies. Zie hoeveel mensen je calculator gebruiken en wat je potentiële omzet is.",
  "hiw.card_blog_title": "Blog & SEO CMS",
  "hiw.card_blog_text": "Ingebouwd systeem om artikelen te publiceren die jouw vindbaarheid in Google vergroten.",
  "hiw.card_global_title": "Global Architecture",
  "hiw.card_global_text":
    "Volledig meertalig ondersteund (NL/EN/etc.) waarbij de voorkeurstaal van de klant behouden blijft.",
  "hiw.card_mobile_title": "Mobile Ready (PWA)",
  "hiw.card_mobile_text":
    "Inkvaluator is een Progressive Web App (PWA). Installeer de tool direct op je beginscherm via de browser. Razendsnel, veilig en zonder opslagruimte te verbruiken.",
  "hiw.cta_title": "Waar wacht je nog op?",
  "hiw.cta_accent": "Stop met rekenen, start met creëren.",
  "hiw.cta_button": "Probeer Inkvaluator nu gratis",

  "wa.template_high_greeting":
    "Hoi {name}, goed nieuws! Ik heb al een gedetailleerde prijsindicatie berekend voor mijn tattoo:",
  "wa.template_high_vault": "🔗 Ik heb dit opgeslagen in mijn Tattoo Kluis: {url}",
  "wa.template_low_greeting":
    "Hoi {name}, ik oriënteer me op een tattoo. Kun je meer vertellen over beschikbaarheid en prijzen?",
  "wa.template_low_note": "Op basis van de calculator een eerste idee:",
  "wa.followup_btn": "Opvolgen",
  "wa.followup_msg": "Hoi! Ik wil graag mijn tattoo-aanvraag opvolgen. Is er al nieuws?",
  "vault.compare": "Vergelijken",
  "vault.compare_title": "Vergelijk Schattingen",
  "vault.select_to_compare": "Selecteer 2 items om te vergelijken",
  "vault.comparing": "Vergelijken",
  "vault.close_compare": "Sluiten",
  "analytics.team_title": "Team Ranglijst",
  "analytics.team_desc": "Prestatie-ranking van artiesten in je studio",
  "analytics.team_calculations": "Berekeningen",
  "analytics.team_lead_value": "Lead Waarde",

  // Privacy page
  "privacy.seo_title": "Privacybeleid | Inkvaluator",
  "privacy.seo_desc":
    "Lees het privacybeleid van Inkvaluator. Wij respecteren je privacy en beschermen je gegevens conform AVG/GDPR richtlijnen.",
  "privacy.title": "Privacybeleid",
  "privacy.updated": "Laatst bijgewerkt:",
  "privacy.s1_title": "1. Welke gegevens verzamelen wij?",
  "privacy.s1_intro": "Inkvaluator verzamelt gegevens die noodzakelijk zijn voor het functioneren van het platform:",
  "privacy.s1_artists_label": "Voor Artiesten",
  "privacy.s1_artists":
    "Naam, e-mailadres, studionaam, adresgegevens (voor de interactieve wereldkaart) en portfolio-informatie.",
  "privacy.s1_visitors_label": "Voor Bezoekers",
  "privacy.s1_visitors":
    "Gegevens ingevoerd in de calculator (grootte, stijl, prijsindicaties) voor het genereren van schattingen en analytische doeleinden.",
  "privacy.s1_leads_label": "Leads",
  "privacy.s1_leads":
    "Wanneer je contact opneemt met een artiest, worden je bericht en eventuele referentiefoto's veilig verwerkt.",
  "privacy.s2_title": "2. Gebruik van Gegevens",
  "privacy.s2_intro":
    "Jouw privacy staat voorop. Wij verkopen of verhuren nooit persoonlijke gegevens aan derden. Wij gebruiken data uitsluitend voor:",
  "privacy.s2_matching": "Het matchen van klanten met de juiste tattoo-artiesten via onze kaart.",
  "privacy.s2_algorithms": "Het continu verbeteren van onze prijs-algoritmes.",
  "privacy.s2_pwa": "De technische werking van de app als Progressive Web App (PWA).",
  "privacy.s3_title": "3. Internationale Wetgeving (GDPR/AVG)",
  "privacy.s3_intro": "Conform de Europese wetgeving heb je de volgende rechten:",
  "privacy.s3_access_label": "Recht op inzage",
  "privacy.s3_access": "Je kunt opvragen welke gegevens wij van je hebben.",
  "privacy.s3_erasure_label": "Recht op vergeten te worden",
  "privacy.s3_erasure": "Je kunt verzoeken om volledige verwijdering van je account en data.",
  "privacy.s3_portability_label": "Dataportabiliteit",
  "privacy.s3_portability": "Je kunt je gegevens in een leesbaar formaat opvragen.",
  "privacy.s4_title": "4. Cookies & PWA",
  "privacy.s4_text":
    "Inkvaluator maakt gebruik van functionele caching (PWA) om offline gebruik mogelijk te maken. Wij gebruiken geen tracking-cookies of advertentie-pixel van derden.",
  "privacy.contact_title": "Vragen over je privacy?",
  "privacy.contact_text": "Neem direct contact op met onze functionaris gegevensbescherming:",

  // Upgrade extras
  "upgrade.test_account_active": "Testaccount — functies geactiveerd",

  // Terms of Service page
  "tos.seo_title": "Algemene Voorwaarden | Inkvaluator",
  "tos.seo_desc":
    "Lees de Algemene Voorwaarden van Inkvaluator. Informatie over het gebruik van onze tattoo-prijscalculator, abonnementen en aansprakelijkheid.",
  "tos.title": "Algemene Voorwaarden",
  "tos.updated": "Laatst bijgewerkt:",
  "tos.s1_title": "1. Definities",
  "tos.s1_platform":
    "Platform: Inkvaluator, een SaaS-platform beschikbaar via inkvaluator.com en bijbehorende subdomeinen.",
  "tos.s1_user": "Gebruiker / Artiest: Elke tattoo-artiest of studio die een account aanmaakt op het Platform.",
  "tos.s1_visitor":
    "Bezoeker: Elke persoon die de publieke calculator of het ontdek-gedeelte van het Platform gebruikt.",
  "tos.s1_service":
    "Dienst: De door Inkvaluator aangeboden functionaliteiten, waaronder de tattoo-prijscalculator, lead-management, AI-scanner en white-label tools.",
  "tos.s2_title": "2. Gebruik van de Calculator",
  "tos.s2_p1":
    "De prijsindicaties gegenereerd door Inkvaluator zijn uitsluitend indicatief en vormen geen bindend aanbod, offerte of prijsgarantie. De daadwerkelijke prijs van een tattoo wordt altijd bepaald door de artiest zelf, op basis van een persoonlijk consult.",
  "tos.s2_p2":
    "Inkvaluator is niet aansprakelijk voor beslissingen die worden genomen op basis van de gegenereerde prijsindicaties, noch voor eventuele geschillen tussen artiesten en hun klanten over prijzen.",
  "tos.s3_title": "3. Abonnementen & Betalingen",
  "tos.s3_intro": "Inkvaluator biedt drie niveaus aan: Free, Pro en Studio.",
  "tos.s3_billing": "Betaalde abonnementen worden maandelijks gefactureerd via Stripe en worden automatisch verlengd.",
  "tos.s3_cancel":
    "Je kunt je abonnement op elk moment opzeggen via het Stripe-klantportaal in je dashboard. Na opzegging behoud je toegang tot de betaalde functies tot het einde van de lopende facturatieperiode.",
  "tos.s3_refund":
    "Restitutie is niet mogelijk voor reeds aangevangen facturatieperiodes, tenzij anders bepaald door toepasselijk recht.",
  "tos.s3_price_change":
    "Inkvaluator behoudt zich het recht voor om tarieven te wijzigen. Eventuele prijswijzigingen worden minimaal 30 dagen van tevoren gecommuniceerd.",
  "tos.s4_title": "4. Eigendom van Data",
  "tos.s4_p1":
    "Alle gegevens die een Artiest invoert (prijsinstellingen, profielgegevens, logo's) blijven eigendom van de Artiest. Inkvaluator verkrijgt een beperkte licentie om deze gegevens te gebruiken voor het leveren van de Dienst.",
  "tos.s4_p2":
    "Anonieme, geaggregeerde gebruiksdata (zoals het aantal berekeningen per regio) kunnen door Inkvaluator worden gebruikt voor statistische analyses en productverbetering, zonder dat deze herleidbaar zijn tot individuele gebruikers.",
  "tos.s5_title": "5. Aansprakelijkheid",
  "tos.s5_p1":
    'Inkvaluator levert de Dienst "as is". Wij spannen ons in voor maximale beschikbaarheid en correctheid, maar geven geen garantie op ononderbroken of foutloze werking van het Platform.',
  "tos.s5_indirect": "Inkvaluator is niet aansprakelijk voor indirecte schade, gederfde winst of gevolgschade.",
  "tos.s5_cap":
    "De totale aansprakelijkheid van Inkvaluator is beperkt tot het bedrag dat de Gebruiker in de drie maanden voorafgaand aan de claim aan abonnementsgelden heeft betaald.",
  "tos.s5_content": "Inkvaluator is niet verantwoordelijk voor de inhoud die artiesten op hun profiel plaatsen.",
  "tos.s6_title": "6. Account & Beëindiging",
  "tos.s6_credentials": "Gebruikers zijn verantwoordelijk voor het geheimhouden van hun inloggegevens.",
  "tos.s6_suspend":
    "Inkvaluator behoudt zich het recht voor om accounts te schorsen of te verwijderen bij misbruik, fraude of schending van deze voorwaarden.",
  "tos.s6_delete": "Bij beëindiging van een account worden persoonsgegevens verwijderd conform het",
  "tos.s6_privacy_link": "Privacybeleid",
  "tos.s7_title": "7. Intellectueel Eigendom",
  "tos.s7_p1":
    'Alle intellectuele eigendomsrechten op het Platform, de software, het ontwerp en de merknaam "Inkvaluator" berusten bij Inkvaluator. Het is niet toegestaan om onderdelen van het Platform te kopiëren, reverse-engineeren of te gebruiken voor concurrerende diensten.',
  "tos.s8_title": "8. Toepasselijk Recht",
  "tos.s8_p1":
    "Op deze Algemene Voorwaarden is het Nederlands recht van toepassing. Geschillen worden bij uitsluiting voorgelegd aan de bevoegde rechter in Nederland.",
  "tos.contact_title": "Vragen over deze voorwaarden?",
  "tos.contact_text": "Neem contact op via:",

  // Support page
  "support.seo_title": "Support | Inkvaluator",
  "support.seo_desc":
    "Hulp nodig met Inkvaluator? Bekijk onze veelgestelde vragen of neem contact op met ons supportteam.",
  "support.title": "Support",
  "support.faq_title": "Veelgestelde Vragen",
  "support.faq_q1": "Hoe stel ik mijn tarieven in?",
  "support.faq_a1":
    "Ga naar je Dashboard en klik op 'Prijzen'. Hier kun je je uurtarief, minimum prijs, aanbetaling en ontwerpkosten instellen. Je kunt ook geavanceerde instellingen configureren voor grootte, complexiteit, lichaamsdeel en stijl-vermenigvuldigers.",
  "support.faq_q2": "Hoe werkt de Stripe-koppeling voor abonnementen?",
  "support.faq_a2":
    "Als je upgradet naar Pro of Studio, word je doorgestuurd naar een beveiligde Stripe checkout. Na betaling wordt je abonnement automatisch geactiveerd. Je kunt je abonnement beheren (opzeggen, facturen bekijken) via de knop 'Abonnement Beheren' in je dashboard.",
  "support.faq_q3": "Wat is de WhatsApp-module?",
  "support.faq_a3":
    "Met de WhatsApp-module (beschikbaar vanaf Pro) kunnen bezoekers na het berekenen van een prijs direct contact opnemen via WhatsApp. Je configureert je telefoonnummer in het dashboard en Inkvaluator genereert automatisch een bericht met de tattoo-details.",
  "support.faq_q4": "Hoe deel ik mijn calculator met klanten?",
  "support.faq_a4":
    "In je dashboard vind je onder 'Delen' een directe link naar je persoonlijke calculator, een QR-code en een embed-code om de calculator op je eigen website te plaatsen.",
  "support.faq_q5": "Kan ik mijn abonnement opzeggen?",
  "support.faq_a5":
    "Ja, je kunt je abonnement op elk moment opzeggen via 'Abonnement Beheren' in je dashboard. Na opzegging behoud je toegang tot alle Pro-functies tot het einde van je lopende facturatieperiode.",
  "support.faq_q6": "Hoe werkt de AI Image Scanner?",
  "support.faq_a6":
    "De AI Scanner (Pro-functie) analyseert een foto van een tattoo-ontwerp en schat automatisch de grootte, complexiteit en stijl in. Dit helpt bezoekers om sneller een prijsindicatie te krijgen.",
  "support.faq_q7": "Ik heb een bug gevonden, wat nu?",
  "support.faq_a7":
    "Stuur een e-mail naar support@inkvaluator.com met een beschrijving van het probleem, screenshots indien mogelijk, en het apparaat/de browser die je gebruikt. Wij reageren zo snel mogelijk.",
  "support.contact_title": "Contact",
  "support.contact_text": "Staat je vraag er niet bij? Neem direct contact op met ons team:",
  "support.form_name": "Naam",
  "support.form_name_placeholder": "Je naam",
  "support.form_email": "E-mail",
  "support.form_message": "Bericht",
  "support.form_message_placeholder": "Beschrijf je vraag of probleem...",
  "support.form_submit": "Verstuur bericht",
  "support.form_subject": "Support aanvraag van {name}",
  "support.toast_title": "E-mail client geopend",
  "support.toast_desc": "Je e-mailprogramma wordt geopend met je bericht.",

  // API limits
  "api.limit_reached":
    "Je hebt de dagelijkse limiet voor AI-scans bereikt. Maak een account aan of probeer het morgen opnieuw.",

  // Landing FAQ
  "landing.faq_title": "Hoe het werkt",
  "landing.faq_subtitle": "Alles wat je moet weten over Inkvaluator",
  "landing.faq_q1": "Hoe werkt de prijscalculator?",
  "landing.faq_a1":
    "Je stelt je uurtarief, minimumprijs en vermenigvuldigers in voor grootte, complexiteit, plaatsing en stijl. Klanten bezoeken je unieke link en krijgen direct een nauwkeurige prijsindicatie — zonder eindeloos heen en weer berichten.",
  "landing.faq_q2": "Is Inkvaluator echt gratis?",
  "landing.faq_a2":
    "Ja! Het gratis plan bevat een volledig werkende calculator, QR-code en alle tattoostijlen. Upgrade naar Pro voor custom branding, analytics, AI-scanner en meertalige ondersteuning.",
  "landing.faq_q3": "Hoe werkt de AI Scanner?",
  "landing.faq_a3":
    "Upload een referentiefoto en onze AI analyseert in seconden de grootte, complexiteit, kleur en stijl. De calculator past zich automatisch aan op basis van de analyse — dit bespaart je tijd bij elke offerte.",
  "landing.faq_q4": "Kunnen klanten mij direct contacteren?",
  "landing.faq_a4":
    "Absoluut. Na het berekenen van een prijs kunnen klanten een vooraf ingevuld WhatsApp-bericht sturen met alle tattoo-details, inclusief referentiefoto's. Je ontvangt een kant-en-klare lead zonder moeite.",
  "landing.faq_q5": "Hoe zit het met meertalige ondersteuning?",
  "landing.faq_a5":
    "Inkvaluator ondersteunt 17 talen en 13 valuta's. Je calculator past zich automatisch aan op de taal en lokale valuta van je klant, perfect voor internationale artiesten.",
  "landing.faq_q6": "Hoe krijg ik meer klanten?",
  "landing.faq_a6":
    "Pro en Studio artiesten verschijnen op onze interactieve wereldkaart en ontdek-pagina. Klanten kunnen je vinden op stad, land of stijl — dit vergroot je zichtbaarheid en brengt organische leads binnen.",

  // Account Security / MFA
  "profile.mfa_title": "Accountbeveiliging",
  "profile.mfa_desc": "Bescherm je account met tweestapsverificatie.",
  "profile.mfa_totp_title": "Authenticator App (TOTP)",
  "profile.mfa_enable": "2FA inschakelen",
  "profile.mfa_scan_qr": "Scan deze QR-code met je authenticator app (Google Authenticator, Authy, etc.)",
  "profile.mfa_secret_key": "Geheime sleutel (handmatige invoer)",
  "profile.mfa_enter_code": "Voer de 6-cijferige code in vanuit je app",
  "profile.mfa_verify": "Verifiëren",
  "profile.mfa_authenticator": "Authenticator App",
  "profile.mfa_active": "Actief",
  "profile.mfa_enabled_success": "✅ Tweestapsverificatie ingeschakeld!",
  "profile.mfa_disabled_success": "Tweestapsverificatie uitgeschakeld.",
  "profile.mfa_enroll_error": "Kon 2FA niet inschakelen",
  "profile.mfa_verify_error": "Verificatie mislukt. Controleer je code en probeer het opnieuw.",
  "profile.mfa_unenroll_error": "Kon 2FA niet uitschakelen",
  "profile.recently_updated": "Recent bijgewerkt",

  // Admin Price Management (super admin only)
  "admin.price_2fa_required": "2FA verificatie vereist",
  "admin.price_2fa_required_desc": "Je moet je authenticator-code invoeren voordat je prijzen kunt wijzigen.",
  "admin.price_2fa_verify": "Verifiëren",
  "admin.price_2fa_verified": "✅ 2FA geverifieerd — prijsbeheer ontgrendeld",
  "admin.price_2fa_dialog_title": "2FA Verificatie",
  "admin.price_2fa_dialog_desc":
    "Voer de 6-cijferige code in vanuit je authenticator-app om prijsbeheer te ontgrendelen.",
  "admin.price_2fa_not_enabled": "⚠️ 2FA niet ingeschakeld",
  "admin.price_2fa_not_enabled_desc": "Je moet eerst 2FA inschakelen via Account Beveiliging.",
  "admin.price_2fa_failed": "Verificatie mislukt",
  "admin.price_2fa_success": "✅ Geverifieerd",
  "admin.price_2fa_success_desc": "2FA verificatie geslaagd. Je kunt nu prijzen beheren.",
  "admin.price_eur_title": "EUR Basisprijzen wijzigen",
  "admin.price_eur_desc":
    "Pas de EUR-prijzen aan. Alle andere valuta's worden automatisch omgerekend en gesynchroniseerd.",
  "admin.price_pro_monthly": "Pro maandelijks (€)",
  "admin.price_studio_monthly": "Studio maandelijks (€)",
  "admin.price_pro_yearly": "Pro jaarlijks (€)",
  "admin.price_studio_yearly": "Studio jaarlijks (€)",
  "admin.price_update_button": "Prijzen bijwerken & automatisch synchroniseren",
  "admin.price_update_hint":
    "⚡ Vul alleen de prijzen in die je wilt wijzigen. Lege velden worden overgeslagen. Na het bijwerken worden alle 14 valuta's automatisch omgerekend met mooie afgeronde bedragen.",
  "admin.price_update_success": "✅ Prijzen bijgewerkt & gesynchroniseerd",
  "admin.price_update_fill_one": "Vul minstens één prijs in",
  "admin.price_manual_sync_title": "Handmatige Currency Sync",
  "admin.price_manual_sync_desc":
    "Herbereken valutaprijzen zonder de EUR-prijs te wijzigen (bijv. bij koerswijzigingen).",
  "admin.price_preview": "Preview (Dry Run)",
  "admin.price_sync": "Sync & Create Prices",
  "admin.status_new": "Nieuw",
  "admin.status_emailed": "Gemaild",
  "admin.status_registered": "Aangemeld",
  "admin.status_archived": "Gearchiveerd",
  "admin.status_all": "Alle",
  "admin.tips_loading": "Laden...",
  "admin.tips_empty": "Nog geen tips ontvangen.",
  "admin.tips_status_changed": "Status gewijzigd naar",
  "admin.tips_status_error": "Fout bij wijzigen status",
  "admin.tips_copied": "Outreach bericht gekopieerd!",
  "admin.tips_mail_artist": "Mail artiest",
  "admin.tips_outreach": "Outreach",
  "admin.tips_copied_label": "Gekopieerd",
  "admin.tips_tipped_by": "Getypt door:",
  "landing.hero_subline": "+ tattoostudio's wereldwijd",
  "landing.trust_claimed": "Actieve artiesten",

  // Referral
  "referral.title": "Verwijs een Collega",
  "referral.desc": "Nodig een collega uit voor Inkvaluator en verdien korting op je abonnement voor elke aanmelding.",
  "referral.generate": "Genereer Verwijslink",
  "referral.code_created": "Verwijslink aangemaakt!",
  "referral.share_link": "Deel deze link met een collega:",
  "referral.reward_info":
    "Voor elke collega die zich aanmeldt via jouw link, verdien je 10% korting op je volgende verlenging.",
  "referral.invited": "Uitgenodigd",
  "referral.earned": "Korting verdiend",
  "referral.redeemed": "Verzilverde verwijzingen",
  "referral.colleague_joined": "Een collega is via jouw link geregistreerd",
  "referral.reward_applied": "Beloning toegepast",
  "referral.pending_reward": "In afwachting",
  "referral.progress_label": "Nog {n} collega('s) tot je volgende 10% korting",
  "referral.max_reached": "🎉 Maximale 50% korting bereikt!",
  "referral.whatsapp_message": "Hey! Ik gebruik Inkvaluator voor mijn tattoo-prijzen. Probeer het gratis: {link}",
  "referral.link_copied_ig": "Link gekopieerd — plak hem in je Instagram bio of DM!",
  "referral.leaderboard_title": "Top Verwijzers",
  "referral.leaderboard_entry": "Artiest uit {city}",

  // PWA Install Banner
  "pwa.install_title": "Installeren als App",
  "pwa.install_benefit": "Gebruik de app voor snellere AI-scans",
  "pwa.install_button": "Nu Installeren",
  "pwa.ios_instruction": 'Tik op Deel (□↑) en kies "Zet op beginscherm"',
  "pwa.android_instruction": 'Tik op het menu (⋮) en kies "App installeren"',

  // Studio routing
  "studio.routed_to": "→ Doorgestuurd naar {name} op basis van stijl",
  "referral.reward_generated": "Promotiecode aangemaakt als verwijsbeloning!",

  // Channel toggles
  "consult_settings.channels_title": "Lead Kanalen",
  "consult_settings.channels_desc": "Kies welke contactmethoden zichtbaar zijn voor klanten op je calculator.",

  // Google Reviews & Studio Search
  "profile.google_reviews": "Google Reviews",
  "profile.google_reviews_desc": "Koppel je Google Bedrijfsprofiel om sterren te tonen op je artiestkaart.",
  "profile.google_place_id": "Google Place ID",
  "profile.google_place_id_hint":
    "Vind je Place ID op developers.google.com/maps/documentation/places/web-service/place-id",
  "profile.search_studio": "Zoek je Studio",
  "profile.search_studio_placeholder": "Zoek op studionaam...",
  "profile.search_studio_hint": "Zoek je tattoo studio om automatisch je Google reviews te koppelen.",
  "profile.studio_linked": "Studio succesvol gekoppeld!",
  "profile.studio_not_found": "Geen studio's gevonden. Probeer een andere zoekterm.",

  // SW update
  "sw.update_available": "Nieuwe versie beschikbaar. Klik om te vernieuwen.",
  "sw.refresh": "Vernieuwen",

  // Tip Your Artist
  "tip.title": "Tip je Artiest",
  "tip.desc": "Ken je een tattoo artiest die op Inkvaluator zou moeten staan? Tip ze!",
  "tip.artist_name": "Naam artiest",
  "tip.artist_placeholder": "Bijv. Jan Ink",
  "tip.instagram_label": "Instagram (optioneel)",
  "tip.whatsapp_label": "WhatsApp (optioneel)",
  "tip.your_name": "Jouw naam (optioneel)",
  "tip.your_name_placeholder": "Jouw naam",
  "tip.share_message":
    "Hoi {artist}, ik wilde mijn prijs bij jou berekenen op Inkvaluator, maar kon je niet vinden! Check inkvaluator.com zodat ik je prijzen kan zien. Groetjes, {client}",
  "tip.copied_clipboard": "Bericht gekopieerd! Plak het in hun DM.",
  "tip.success_title": "Bedankt!",
  "tip.success_desc": "Je helpt {artist} te professionaliseren. 🙏",

  // Pricing Education
  "pricing_edu.button": "Hoe werkt deze berekening?",
  "pricing_edu.title": "Hoe de Prijs Wordt Berekend",
  "pricing_edu.intro":
    "De prijs van een tattoo wordt bepaald door meerdere factoren die samen het vakmanschap en de benodigde tijd weerspiegelen.",
  "pricing_edu.time_title": "Tijd & Grootte",
  "pricing_edu.time_desc":
    "Grotere tattoos kosten meer tijd. Het uurtarief van de artiest wordt vermenigvuldigd met het geschatte aantal uren voor jouw formaat.",
  "pricing_edu.complexity_title": "Complexiteit & Detail",
  "pricing_edu.complexity_desc":
    "Gedetailleerdere ontwerpen vereisen meer precisie en tijd. Een fotorealistisch portret duurt langer dan een eenvoudig symbool.",
  "pricing_edu.placement_title": "Plaatsing op het Lichaam",
  "pricing_edu.placement_desc":
    "Sommige lichaamsdelen zijn gevoeliger of moeilijker bereikbaar (zoals ribben of handen), wat extra zorg en tijd vereist.",
  "pricing_edu.style_title": "Tattoo Stijl",
  "pricing_edu.style_desc":
    "Elke stijl vereist specifieke technieken. Realisme en Japanse stijlen kosten doorgaans meer tijd dan fine line.",
  "pricing_edu.disclaimer":
    "Dit is altijd een schatting. De definitieve prijs wordt bepaald na een persoonlijk consult met de artiest.",

  // Total value counter
  "landing.total_value_prefix": "Al voor",
  "landing.total_value_suffix": "aan tattoo-kunst berekend",

  // Google sync
  "profile.sync_now": "Sync nu",
  "profile.sync_success": "Google Reviews gesynchroniseerd!",
  "profile.sync_error": "Reviews synchroniseren mislukt",

  // Profile contact / price / claim / sticky (Fase 1B)
  "profile.contact.call": "Bellen",
  "profile.contact.website": "Website",
  "profile.contact.route": "Route",
  "profile.contact.book": "Boek direct",
  "profile.contact.whatsapp": "WhatsApp",
  "profile.portfolio.title": "Portfolio",
  "profile.portfolio.locked_title": "Portfolio zichtbaar na claimen",
  "profile.portfolio.locked_subtitle": "Voeg jouw werk toe en laat klanten kennismaken",
  "profile.portfolio.claim_cta": "Claim dit profiel",
  "profile.price.from": "Richtprijs: vanaf",
  "profile.price.hourly": "/uur",
  "profile.price.average_label": "(landelijk gemiddelde)",
  "profile.price.calculate_cta": "Bereken jouw prijs →",
  "profile.claim.title": "Ben jij de eigenaar?",
  "profile.claim.subtitle": "Claim dit profiel gratis en ontvang leads.",
  "profile.claim.cta": "Claim gratis →",
  "profile.sticky.calculate": "Bereken prijs",

  // Vault
  "vault.seo_title": "Mijn Tattoo Plannen | Inkvaluator",
  "vault.seo_desc": "Bekijk en beheer je opgeslagen tattoo plannen.",
  "vault.title": "Mijn Tattoo",
  "vault.title_accent": "Plannen",
  "vault.desc": "Je opgeslagen tattoo plannen — lokaal opgeslagen op dit apparaat.",
  "vault.empty": "Nog geen plannen opgeslagen. Gebruik een calculator en sla je resultaat op!",
  "vault.try_calculator": "Vind een artiest",
  "vault.items_saved": "berekeningen opgeslagen",
  "vault.clear_all": "Alles wissen",
  "vault.open_calculator": "Open",
  "vault.save_btn": "Sla plan op",
  "vault.saved_title": "Plan opgeslagen!",
  "vault.saved_desc": "Je berekening is opgeslagen in je plannen.",
  "compare.title": "Prijsvergelijking",
  "compare.standard_project": "15cm Realisme · Arm",
  "compare.no_pricing": "Geen prijsdata",
  "compare.disclaimer":
    "Vergelijking op basis van een standaard 15cm realisme tattoo op de arm. Werkelijke prijzen kunnen afwijken.",
  "compare.compare_btn": "Vergelijken",
  "compare.add": "Toevoegen aan vergelijking",
  "trending.title": "Trending deze week",
  "discover.verified_quality": "Voldoet aan Inkvaluator kwaliteitseisen",

  // Admin Insights
  "admin.insights": "Inzichten",
  "admin.tips": "Artiest Tips",
  "admin.potential_revenue": "Potentiële Omzet",
  "admin.wa_by_country": "WhatsApp Leads per Land",
  "admin.artist_growth": "Artiestgroei (Wekelijks)",
  "admin.top_styles": "Top 5 Stijlen (Wereldwijd)",
  "admin.top_body_parts": "Top 5 Lichaamsdelen (Wereldwijd)",
  "admin.no_data": "Nog geen data",
  "admin.verified": "Geverifieerd",
  "admin.verified_removed": "Verified badge verwijderd",
  "admin.verified_granted": "Verified badge toegekend",
  "discover.verified": "Inkvaluator Geverifieerd",
  "admin.tip_all": "Alle",
  "admin.tip_new": "Nieuw",
  "admin.tip_emailed": "Gemaild",
  "admin.tip_registered": "Aangemeld",
  "admin.tip_archived": "Gearchiveerd",
  "admin.tip_tipped_by": "Getypt door",
  "admin.tip_mail_artist": "Mail artiest",
  "admin.tip_no_tips": "Nog geen tips ontvangen.",

  // Founder spots
  "landing.founder_spots_left": "plekken over",

  // Auto-verified
  "verified.auto_eligible": "Komt in aanmerking voor automatische verificatie",
  "verified.criteria": "Automatische criteria: onboarding voltooid, Google rating > 4.0, 10+ berekeningen",
  "verified.manual_override": "Handmatige override door admin",

  // Studio roles
  "studio.role_artist": "Artiest",
  "studio.role_manager": "Manager",
  "studio.role_owner": "Eigenaar",
  "studio.role_updated": "Rol bijgewerkt",
  "studio.role_desc_artist": "Kan alleen leads bekijken",
  "studio.role_desc_manager": "Kan prijsinstellingen beheren",
  "studio.pending": "In afwachting",

  // Tax
  "checkout.tax_included": "BTW automatisch berekend",
  "checkout.currency_auto": "Valuta ingesteld op basis van je profiel",

  // Claim CTA
  "claim.question": "Ben jij een tattoo artiest in {city}? Claim je plek op de kaart!",
  "claim.cta": "Claim je plek",

  // Outreach
  "outreach.copy": "Kopieer outreach bericht",
  "outreach.copied": "Outreach bericht gekopieerd!",
  "outreach.generate": "Genereer Outreach Bericht",

  // Portfolio showcase
  "discover.view_portfolio": "Bekijk Werk",
  "discover.hide_portfolio": "Verberg Werk",

  // Admin notifications
  "admin.notifications": "Meldingen",
  "admin.no_notifications": "Geen meldingen.",
  "admin.mark_read": "Gelezen",
  "admin.mark_all_read": "Alles gelezen markeren",
  "admin.notif_new_artist": "Nieuwe artiest aangemeld",
  "admin.notif_new_tip": "Nieuwe artist tip",
  "admin.notif_new_style": "Nieuwe stijlsuggestie",

  // Dashboard help tooltips
  "help.min_price":
    "Dit is de absolute minimumprijs voor elke sessie, ongeacht grootte of complexiteit. Zelfs een kleine tattoo gaat niet onder deze prijs.",
  "help.hourly_rate": "Je standaard uurtarief vormt de basis van alle prijsberekeningen. Tijd × tarief = basisprijs.",
  "help.daily_rate":
    "Optioneel plafond: als een sessie dit bedrag overschrijdt op basis van uren, wordt het dagtarief toegepast.",
  "help.design_cost": "Een vast of per-uur tarief voor het ontwerpen en voorbereiden van het tattoo-ontwerp.",
  "help.deposit":
    "De aanbetaling reserveert de afspraak en wordt van het totaal afgetrokken. Zet op 0 om uit te schakelen.",
  "help.multipliers":
    "Multipliers passen de prijs aan op basis van stijl, complexiteit en lichaamsdeel. Ze werken vermenigvuldigend (bijv. ×1.2 × ×1.1).",
  "help.lead_channels":
    "WhatsApp-leads sturen alle berekende details (stijl, grootte, prijs) direct naar je telefoon. E-mail leads gaan naar je consultatie-e-mail.",
  "help.google_place_id":
    "Koppel je Google Business-profiel om automatisch je beoordeling en reviews te tonen. Vind je Place ID via Google Maps.",
  "help.sizes":
    "Definieer de geschatte tijdrange (min/max uren) per groottecategorie. Dit beïnvloedt direct de prijsberekening.",
  "help.complexity": "Pas prijsmultipliers aan voor ontwerpscomplexiteit. Gedetailleerdere ontwerpen kosten meer tijd.",
  "help.body_parts": "Sommige lichaamsdelen zijn moeilijker te tatoeëren. Pas multipliers dienovereenkomstig aan.",
  "help.styles":
    "Schakel stijlen in/uit en stel prijsmultipliers in. Gespecialiseerde stijlen kunnen hogere multipliers hebben.",
  "help.logo": "Je logo verschijnt op je calculatorpagina en in de artiestenmap. Max 2MB, PNG/JPG/WebP.",
  "help.currency": "De valuta op je calculator. Kies de valuta waarin je klanten betalen.",
  "help.calculator_language": "De taal van je publieke calculatorpagina — wat je klanten zien.",
  "help.dashboard_language": "De taal van dit beheerdashboard — alleen zichtbaar voor jou.",
  "help.artist_name": "Je professionele naam zoals getoond op je calculator en in de artiestenmap.",
  "help.studio_name": "Optionele studionaam. Getoond naast je artiestennaam op je profiel.",
  "help.location": "Je studiolocatie wordt gebruikt voor de artiestenkaart, geo-pricing en lokale SEO.",
  "tab.price_engine": "Prijs-Engine",
  "tab.price_engine_desc": "Beheer alle prijsmultipliers: groottes, complexiteit, lichaamsdelen en stijlen.",

  // Lead Quality Score
  "lead.quality_score": "Leadkwaliteit",
  "lead.score_1": "Lage prioriteit",
  "lead.score_2": "Enige interesse",
  "lead.score_3": "Gemiddeld",
  "lead.score_4": "Hoge kwaliteit",
  "lead.score_5": "Premium lead",
  "lead.status_new": "Nieuw",
  "lead.status_contacted": "In contact",
  "lead.status_booked": "Geboekt",
  "lead.change_status": "Status",
  "lead.add_price": "Prijs invullen",
  "push.test_body": "Pushmeldingen zijn LIVE! Je ontvangt nu meldingen bij nieuwe leads.",
  "push.send_test": "Stuur Test Notificatie",
  "push.active_desc": "Je ontvangt meldingen bij nieuwe leads met een hoge score.",
  "push.blocked": "Notificaties zijn geblokkeerd in je browserinstellingen.",
  "push.enable_desc": "Ontvang meldingen wanneer er leads met hoge kwaliteit binnenkomen.",
  "push.label": "Pushmeldingen",
  "vault.live_status": "Live Status",
  "vault.status_new_desc": "Je aanvraag is ontvangen",
  "vault.status_contacted_desc": "De artiest heeft je bericht gezien",
  "vault.status_booked_desc": "Je afspraak is bevestigd!",
  "vault.link_copied": "🔗 Link gekopieerd!",
  "analytics.actual_revenue": "Werkelijke Omzet",
  "push.welcome_body": "Welkom! Je ontvangt nu meldingen bij nieuwe leads met een hoge score.",
  "push.denied_info": "Blijf op de hoogte van nieuwe leads — Activeer meldingen in je browserinstellingen.",
  "style_city.popular_styles": "Populaire stijlen in {city}",

  // Verified tooltip
  "verified.tooltip": "Vakmanschap geverifieerd door Inkvaluator",

  // API diagnostics
  "api.google_denied": "Google Places API is niet geactiveerd. Neem contact op met je beheerder.",
  "api.studio_search_error": "Kon studio's niet zoeken. Probeer het later opnieuw.",

  // Social sharing
  "share.button": "Delen",
  "share.title": "Mijn Tattoo Prijsindicatie",
  "share.text": "Mijn nieuwe tattoo bij {artist} gaat ongeveer {price} kosten. Check het ook!",

  // Embed badge SEO
  "embed.badge_seo_hint": "💡 Deze badge creëert een dofollow-backlink naar je Inkvaluator-profiel — goed voor SEO.",

  // Studio routing
  "studio.match_intro": "Op basis van je keuze voor",
  "studio.match_specialist": "raden wij onze specialist aan",

  // City page (TattooCity)
  "city.page_title": "Tattoo {city} — {count} studio's en prijzen | Inkvaluator",
  "city.page_desc":
    "Vergelijk {count} tattoostudio's in {city}. Bekijk prijzen, stijlen en reviews. Bereken direct de kosten van jouw tattoo.",
  "city.hero_subtitle": "{count} tattoostudio's in {city}. Vergelijk artiesten, bekijk prijzen en bereken direct.",
  "city.cta_primary": "Bereken jouw prijs in {city}",
  "city.cta_secondary": "Bereken direct de prijs van jouw tattoo bij deze artiesten",
  "city.calc_button": "Bereken prijs →",
  "city.price_section_title": "Wat kost een tattoo in {city}?",
  "city.price_section_subtitle":
    "Prijsindicaties gebaseerd op calculaties via Inkvaluator door artiesten in {city} en omgeving.",
  "city.calc_exact_button": "Bereken jouw exacte prijs",
  "city.studios_subtitle": "Overzicht van bekende tattoostudio's in {city}",
  "city.view_button": "Bekijk →",
  "city.other_cities_title": "Tattoo artiesten in andere steden",
  "city.faq_price_q": "Wat kost een tattoo in {city}?",
  "city.faq_price_a":
    "De prijs van een tattoo in {city} begint vanaf {from} voor een kleine tattoo (5–10 cm). Een medium tattoo (10–20 cm) kost {medium}. Een groot stuk (20 cm+) begint vanaf {large}. Gebruik de Inkvaluator-calculator voor een nauwkeurige schatting.",
  "city.faq_count_q": "Hoeveel tattooshops zijn er in {city}?",
  "city.faq_count_a": "Er zijn momenteel {count} tattoostudio's bekend in {city} via Inkvaluator.",
  "city.faq_styles_q": "Welke tattoostijlen zijn populair in {city}?",
  "city.faq_styles_a": "De meest gevraagde stijlen in {city} zijn {styles}.",
  "city.faq_deposit_q": "Hoeveel aanbetaling betaal ik bij een tattooshop in {city}?",
  "city.faq_deposit_a":
    "De meeste tattoostudio's in {city} vragen een aanbetaling van 10–25% bij het inplannen van een afspraak.",
  "city.faq_choose_q": "Hoe kies ik de juiste tattooshop in {city}?",
  "city.faq_choose_a":
    "Bekijk portfolio's van artiesten in {city} op Inkvaluator, vergelijk prijzen en stijlen, en lees Google-beoordelingen.",
  "city.faq_calc_q": "Kan ik online de prijs van een tattoo in {city} berekenen?",
  "city.faq_calc_a":
    "Ja. Via Inkvaluator kun je de prijs van je tattoo berekenen bij artiesten in {city}. Vul stijl, grootte, lichaamsdeel en detailniveau in voor een eerlijke prijsindicatie.",
  "tattoo_index.page_title": "Tattooshops per land | Inkvaluator",
  "tattoo_index.page_desc":
    "Ontdek tattoostudio's in jouw land en stad. Vergelijk artiesten, bekijk portfolio's en bereken de prijs van jouw tattoo.",
  "tattoo_index.subtitle":
    "Kies je land en ontdek tattoostudio's in jouw stad. Vergelijk artiesten, bekijk portfolio's en bereken de prijs van jouw tattoo.",
  "tattoo_index.stats": "{studios} studio's · {cities} steden",
  "tattoo_index.view_cities": "Bekijk steden",
  "tattoo_country.page_title": "Tattooshops in {country} — {count} studio's | Inkvaluator",
  "tattoo_country.page_desc":
    "Ontdek {studios} tattoostudio's in {cities} steden in {country}. Vergelijk artiesten en bereken de kosten van jouw tattoo.",
  "tattoo_country.stats": "{studios} studio's in {cities} steden",
  // Analytics enhancements
  "analytics.revenue_funnel": "Geschatte Omzet Funnel",
  "analytics.revenue_funnel_desc": "Totale waarde van alle berekeningen vs. daadwerkelijke WhatsApp-leads.",
  "analytics.funnel_calculations": "Alle Berekeningen",
  "analytics.funnel_leads": "Daadwerkelijke Leads",
  "analytics.funnel_whatsapp": "WhatsApp Contacten",
  "analytics.lead_quality": "Lead Kwaliteitsverdeling",
  "analytics.lead_quality_desc": "Hoe je binnenkomende leads scoren op onze kwaliteitsschaal (1-5 sterren).",
  "analytics.body_heatmap": "Populaire Lichaamsdelen Heatmap",
  "analytics.body_heatmap_desc": "Waar je klanten hun tattoo willen — hoe groter het getal, hoe populairder.",

  // Vault enhancements
  "vault.inspiration_title": "Inspiratie",
  "vault.inspiration_desc": "Op basis van je opgeslagen schattingen vind je deze stijlen misschien ook leuk:",
  "vault.try_style": "Probeer dit →",
  "vault.status_concept": "Concept",
  "vault.status_sent": "Aanvraag verstuurd",

  // Reviews showcase
  "reviews.rating": "beoordeling",
  "reviews.verified_reviews": "geverifieerde reviews",
  "reviews.read_all": "Lees alle reviews op Google",

  // Discover
  "discover.swipe": "veeg",

  // Profile fallback
  "profile.manual_place_id": "Studio niet gevonden? Voer je Place ID handmatig in.",

  // Story Generator
  "story.title": "Instagram Story Generator",
  "story.desc": "Genereer een premium Black & Gold story-afbeelding met je QR-code.",
  "story.generate": "Genereer Story",
  "story.regenerate": "Opnieuw genereren",
  "story.download": "Download",
  "story.share": "Delen",
  "story.downloaded": "Story gedownload!",
  "story.cta_line1": "Bereken Je Prijs",
  "story.cta_line2": "Scan de QR-code hieronder",
  "story.scan_prompt": "↑ Scan voor je prijsindicatie",

  // Trends filter
  "trends.filter_country": "Filter op land",
  "trends.all_countries": "Alle landen",
  "trends.analyzed": "berekeningen geanalyseerd",
  "trends.realtime": "Realtime bijgewerkt",

  // Success badge
  "success.badge_title": "Boost je SEO",
  "success.badge_desc":
    "Plaats de Verified Badge op je website voor een dofollow backlink naar je Inkvaluator profiel.",
  "success.badge_cta": "Embed Code ophalen",

  // Style Match
  "match.style_experts": "Experts in {style} nabij {city}",

  // Vault Review Booster
  "vault.review_booster_title": "Tattoo gezet? Help je artiest!",
  "vault.review_booster_desc": "Laat een Google Review achter en help andere klanten deze artiest te vinden.",
  "vault.review_booster_btn": "Schrijf een Google Review",

  // Checklist
  "checklist.title": "Verificatiestatus",
  "checklist.profile_complete": "Profiel compleet",
  "checklist.google_linked": "Google Place ID gekoppeld",
  "checklist.badge_detected": "Verified Badge op website",
  "checklist.push_enabled": "Pushmeldingen geactiveerd",
  "checklist.not_yet": "Nog niet",
  "checklist.active": "Actief",
  "nav.home": "Home",
  "lead.estimate_header": "Tattoo Schatting",
  "discover.verified_badge": "GEVERIFIEERD",
  "laf.presets": "Premium Themapresets",
  "laf.font_selector": "Calculator Lettertype",
  "laf.border_color": "Rand- & Accentkleur",
  "laf.glow_color": "Glow / Schaduweffect",
  "laf.glow_preview": "Glow Voorbeeld",
  "trends.no_data": "Nog niet genoeg data.",
  "trends.subtitle": "Ontdek de populairste stijlen en lichaamsplaatsingen wereldwijd.",
  "analytics.total_calc_value": "Totale Berekende Waarde",
  "analytics.total_lead_value": "Totale Leadwaarde",
  "analytics.whatsapp_leads": "WhatsApp Leads",
  "analytics.team_leaderboard": "Team Ranglijst",
  "trends.title": "Tattoo",
  "trends.title_accent": "Trends",
  "trends.top_styles": "Populairste Stijlen",
  "trends.top_body_parts": "Populaire Lichaamsplaatsen",
  "match.title": "Passende Artiesten bij jou in de buurt",
  "match.score": "Match",
  "match.view": "Bekijk Profiel",
  "match.cta_title": "Weet je niet waar je moet beginnen?",
  "match.cta_subtitle": "Vertel ons wat je zoekt — wij vinden de beste tattoo-artiesten bij jou in de buurt.",
  "match.cta_button": "Vind jouw artiest",
  "match.badge_location": "Locatie-gebaseerd",
  "match.badge_style": "Stijl-matching",
  "match.badge_budget": "Budget-filter",
  "match.step_location": "Stap 1 — Locatie",
  "match.step_distance": "Stap 2 — Max reisafstand",
  "match.step_style": "Stap 3 — Stijlvoorkeur",
  "match.step_budget": "Stap 4 — Budget",
  "match.style_any": "Wis selectie",
  "match.budget_any": "Geen max",
  "match.results_found": "artiesten gevonden",
  "match.no_results": "Geen artiesten gevonden in dit gebied.",
  "match.no_results_hint": "Vergroot je zoekradius of pas je filters aan.",
  "match.loading": "Artiesten zoeken...",
  "match.no_location": "Voer een locatie in om te beginnen.",
  "match.match_score": "match",
  "match.score_distance": "Afstand",
  "match.score_quality": "Kwaliteit",
  "match.score_style": "Stijl",
  "match.score_budget": "Budget",
  "match.view_profile": "Bekijk profiel",
  "match.claimed_badge": "Geclaimd profiel",
  "match.unclaimed_badge": "Studio profiel",
  "match.show_more": "Toon meer",
  "match.location_placeholder": "Voer je stad in...",
  "match.use_gps": "Gebruik mijn locatie",
  "match.searching_from": "Zoeken vanuit",
  "match.location_not_found": "Stad niet gevonden — probeer een andere naam",
  "match.gps_denied": "GPS-toegang geweigerd — voer een stad in",
  "match.gps_loading": "Locatie ophalen...",
  "match.change_location": "Locatie wijzigen",
  "match.enter_city": "Of voer een stad in",
  "match.summary_good": "Goede match",
  "match.summary_ok": "Redelijke match",
  "match.summary_low": "Gedeeltelijke match",
  "match.show_breakdown": "Toon details",
  "match.hide_breakdown": "Verberg details",
  "match.sort_best": "Beste match",
  "match.sort_distance": "Dichtstbij",
  "match.sort_rating": "Hoogste rating",
  "match.sort_lowest_price": "Laagste prijs",
  "match.sort_price_note": "Prijzen zijn alleen beschikbaar voor geclaimde artiesten. Studio's zonder prijsdata staan onderaan.",
  "match.from_price": "Vanaf",
  "match.wizard_step1_title": "Waar zoek je?",
  "match.wizard_use_location": "Gebruik mijn locatie",
  "match.wizard_step2_title": "Hoe ver wil je reizen?",
  "match.wizard_step3_title": "Welke stijl spreekt je aan?",
  "match.wizard_step4_title": "Wat is je budget?",
  "match.wizard_skip": "Overslaan",
  "match.wizard_next": "Volgende",
  "match.wizard_show_matches": "Toon mijn matches",
  "match.spotlight_label": "Jouw beste match",
  "match.styles_unknown": "Stijlen onbekend",
  "match.price_on_request": "Prijs op aanvraag",
  "match.expand_radius": "Vergroot naar {km} km",
  "match.page_title": "Vind een tattoo-artiest",
  "match.page_subtitle": "Stel je voorkeuren in en vind de beste match bij jou in de buurt.",
  "match.results_found": "artiesten gevonden",
  "match.expand_more_within": "meer binnen",
  "match.breakdown_distance": "Afstand",
  "match.breakdown_style": "Stijl",
  "match.breakdown_budget": "Budget",
  "match.breakdown_quality": "Kwaliteit",
  "match.breakdown_unknown": "onbekend",
  "match.why_this_match": "Waarom deze match?",
  "match.results_count": "{n} matches gevonden",
  "hero.subtitle_v2": "Vergelijk tattoo-artiesten op locatie, stijl en budget. Ontdek studio's in jouw buurt en plan je afspraak.",
  "hero.cta_discover": "Ontdek studio's",

  // Urgency & Availability
  "urgency.recent_requests": "aanvragen deze week",
  "urgency.wait_time": "Geschatte wachttijd",
  "urgency.weeks": "weken",
  "urgency.high_demand": "Veel vraag",
  "urgency.popular": "Populair",

  // Market Benchmark
  "benchmark.above_market": "boven marktgemiddelde",
  "benchmark.below_market": "onder marktgemiddelde",
  "benchmark.at_market": "Op marktgemiddelde",
  "benchmark.market_avg": "Marktgemiddelde",

  // Portfolio Preview
  "portfolio.style_examples": "Stijlvoorbeelden",

  // Landing v2
  "landing.seo_title": "Tattoo prijzen berekenen — Vind artiesten in jouw stad | Inkvaluator",
  "landing.seo_description":
    "Bereken direct wat jouw tattoo kost. Vind tattoo artists bij jou in de buurt. Gratis prijscalculator voor studios in Nederland en Europa.",
  "landing.hero_eyebrow": "",
  "landing.hero_title_line1": "Wat kost",
  "landing.hero_title_line2": "jouw",
  "landing.hero_title_line3": "tattoo?",
  "landing.hero_subtitle":
    "Bereken een eerlijke prijsschatting, vind een artiest bij jou in de buurt, en neem direct contact op via WhatsApp.",
  "landing.hero_search_placeholder": "Zoek op stad of studio...",
  "landing.hero_popular": "Populair",
  "landing.hero_cta_primary": "Bereken jouw prijs",
  "landing.hero_cta_secondary": "Bekijk artiesten",
  "landing.artist_card_eyebrow": "Voor tattoo artists",
  "landing.artist_card_title": "Zet jouw studio op de kaart",
  "landing.artist_card_body":
    "Jouw studio staat waarschijnlijk al op Inkvaluator. Claim je profiel in twee minuten en ontvang directe aanvragen van klanten die al weten wat ze willen.",
  "landing.artist_card_stat_studios": "studios",
  "landing.artist_card_stat_cities": "steden",
  "landing.artist_card_stat_spots": "Founder spots",
  "landing.artist_card_cta": "Claim jouw profiel gratis",
  "landing.artist_card_founder_note": "eerste 100 studios krijgen Pro gratis",
  "landing.proof_studios": "Tattoo studios",
  "landing.proof_cities": "Steden wereldwijd",
  "landing.proof_leads": "Klanten geholpen",
  "landing.proof_rating": "Gemiddelde beoordeling",
  "landing.map_section_label": "Studios bij jou in de buurt",
  "landing.plan_eyebrow": "Jouw Tattoo Plan",
  "landing.plan_title": "Geen prijsvraag. Een plan.",
  "landing.plan_body":
    "Inkvaluator genereert een persoonlijk Tattoo Plan met jouw stijl, grootte, locatie en prijsschatting. Je stuurt dit direct naar een artiest — zodat jullie samen op één lijn zitten over stijl, grootte en budget.",
  "landing.plan_step1": "Vul de calculator in — stijl, grootte, detailniveau",
  "landing.plan_step2": "Je Tattoo Plan wordt automatisch aangemaakt",
  "landing.plan_step3": "Stuur het plan via WhatsApp naar een artiest",
  "landing.plan_cta": "Maak jouw Tattoo Plan",
  "landing.plan_card_title": "Tattoo Plan",
  "landing.plan_card_style": "Stijl",
  "landing.plan_card_size": "Formaat",
  "landing.plan_card_detail": "Detailniveau",
  "landing.plan_card_detail_val": "Hoog",
  "landing.plan_card_placement": "Plaatsing",
  "landing.plan_card_placement_val": "Bovenarm",
  "landing.plan_card_estimate": "Geschatte prijs",
  "landing.plan_card_send": "Stuur naar artiest via WhatsApp",
  "landing.how_eyebrow": "Zo werkt het",
  "landing.how_title": "Van idee naar afspraak",
  "landing.how_subtitle": "Drie stappen. Geen account. Direct resultaat.",
  "landing.how_step1_title": "Bekijk de prijs",
  "landing.how_step1_desc": "Ontvang een eerlijke prijsindicatie op basis van jouw tattooidee — geen verrassingen achteraf.",
  "landing.how_step1_cta": "Start de calculator",
  "landing.how_step2_title": "Vind een artiest",
  "landing.how_step2_desc": "Zoek op stad of stijl, bekijk portfolio's en lees reviews van echte klanten.",
  "landing.how_step2_cta": "Ontdek artiesten",
  "landing.how_step3_title": "Plan een afspraak",
  "landing.how_step3_desc": "Neem direct contact op via WhatsApp of e-mail en plan jouw consult.",
  "landing.search_near_me": "In de buurt",
  "smart_search.cities": "Steden",
  "smart_search.artists": "Artiesten",
  "smart_search.studios": "Studio's",
  "smart_search.no_results": "Geen resultaten voor",
  "smart_search.request_city": "Wil je deze stad aanvragen?",
  "landing.map_eyebrow": "Dekking",
  "landing.map_title": "Artiesten in jouw buurt",
  "landing.map_subtitle": "Studios in steden. Gouden pin = geclaimd profiel met actieve calculator.",
  "landing.map_loading": "Kaart laden...",
  "landing.map_legend_claimed": "Geclaimd — calculator actief",
  "landing.map_legend_unclaimed": "Ongeclaimd",
  "landing.styles_eyebrow": "Stijlen",
  "landing.styles_title": "Vind jouw stijl",
  "landing.styles_traditional": "Dikke lijnen, levendige kleuren",
  "landing.styles_realism": "Fotorealistisch detail",
  "landing.styles_japanese": "Klassieke Japanse motieven",
  "landing.styles_fineline": "Delicate, minimalistische lijnen",
  "landing.styles_blackwork": "Puur zwart, maximale impact",
  "landing.styles_watercolor": "Vloeiend en kleurrijk",
  "landing.artist_eyebrow": "Ben jij een tattoo artist?",
  "landing.artist_title": "Laat klanten jou vinden",
  "landing.artist_body": "Meer dan 31.000 studios staan al op Inkvaluator. Claim je gratis profiel in twee minuten en word gevonden door duizenden potentiele klanten — geen creditcard nodig.",
  "landing.artist_perk1": "Klanten sturen hun Tattoo Plan — jij weet meteen wat ze willen",
  "landing.artist_perk2": "Ontvang een aanbetaling bij elke aanvraag via het platform",
  "landing.artist_perk3": "Groei organisch via stadskaart en zoekmachines",
  "landing.artist_perk4": "Eerste 100 studios krijgen Pro gratis — geen verborgen kosten",
  "landing.artist_prop1_title": "Nieuwe klanten, automatisch",
  "landing.artist_prop1_sub": "Jouw profiel werkt 24/7 voor jou.",
  "landing.artist_prop2_title": "Groei je bereik",
  "landing.artist_prop2_sub": "Word gevonden door klanten wereldwijd.",
  "landing.artist_prop3_title": "Gratis te starten",
  "landing.artist_prop3_sub": "Profiel live in 2 minuten, geen creditcard nodig.",
  "landing.trust_coverage": "Actief in",
  "landing.artist_cta_claim": "Claim je profiel",
  "landing.artist_cta_register": "Account aanmaken",
  "landing.founder_subtitle": "Exclusief voor de eerste 100 studios",
  "landing.founder_perk1": "Pro-pakket gratis zolang we groeien",
  "landing.founder_perk2": "Directe invloed op nieuwe functies",
  "landing.founder_perk3": "Prioriteit in zoekresultaten",
  "landing.founder_perk4": "Persoonlijk contact met de ontwikkelaar",
  "landing.founder_claimed": "geclaimd",
  "landing.cities_eyebrow": "Steden",
  "landing.cities_title": "Tattoo studios per stad",
  "landing.cities_all": "Alle steden",
  "landing.cities_studios": "studios",
  "landing.cities_from": "vanaf",
  "landing.cities_claimed": "geclaimd",
  "landing.blog_eyebrow": "Kennisbank",
  "landing.blog_title": "Alles over tattoo prijzen",
  "landing.blog_all": "Alle artikelen",
  "landing.blog_read": "Lees meer",
  "landing.final_eyebrow": "Klaar?",
  "landing.final_title_line1": "Klaar voor jouw volgende tattoo?",
  "landing.final_title_line2": "",
  "landing.final_subtitle": "Geen account nodig. Gratis. Direct resultaat.",
  "landing.final_cta_primary": "Bereken wat jouw tattoo kost",
  "landing.final_cta_secondary": "Claim je studio",
  // Deposit settings
  "settings.deposit_title": "Aanbetaling",
  "settings.deposit_desc": "Vraag klanten een aanbetaling te doen bij het indienen van een aanvraag.",
  "settings.deposit_none": "Geen",
  "settings.deposit_percentage": "Percentage",
  "settings.deposit_fixed": "Vast bedrag",
  "settings.deposit_percentage_hint": "van het totaalbedrag",
  "settings.deposit_fixed_hint": "vaste aanbetaling",
  "settings.deposit_preview_label": "Klant ziet",
  "settings.deposit_preview_pct": "Aanbetaling: {value}% van het totaalbedrag",
  // ─── SEO Tattoo pages (nieuwe keys) ─────────────────
  "nav.studios": "Studio's",
  "nav.studios_count": "studio's",
  "city.stats_studios": "Studio's in {city}",
  // ─── Map legend + list ──────────────────────────────
  "map.legend_pro": "Pro & Studio",
  "map.legend_claimed": "Geclaimd",
  "map.legend_unclaimed": "Ongeclaimd",
  "map.legend_on_inkvaluator": "Op Inkvaluator",
  "map.view_studio": "Bekijk studio →",
  "map.list_pro_section": "✦ Pro & Studio",
  "map.list_claimed_section": "Geclaimd",
  "map.list_unclaimed_section": "Ongeclaimd",
  "map.list_count_singular": "studio in beeld",
  "map.list_count_plural": "studio's in beeld",
  "map.list_scroll_hint": "Verschuif de kaart om meer te zien",
  "map.list_empty": "Geen studios in dit gebied",
  "map.list_empty_hint": "Zoom uit of verschuif de kaart",
  "map.list_full_profile": "Volledig profiel",
  // ─── City section titles ─────────────────────────────
  "city.map_section_title": "Tattooshops in {city}",
  "city.faq_section_title": "Veelgestelde vragen over tattoos in {city}",
  "city.stats_on_inkvaluator": "Op Inkvaluator",
  "city.size_small": "Klein (5–10 cm)",
  "city.size_medium": "Medium (10–20 cm)",
  "city.size_large": "Groot (20 cm+)",
  "city.size_small_ex": "Tekst, minimalistisch, klein symbool",
  "city.size_medium_ex": "Portret, bloemen, geometrisch",
  "city.size_large_ex": "Sleeve, rug, volledig been",
  "city.time_1_2": "1–2 uur",
  "city.time_2_5": "2–5 uur",
  "city.time_5plus": "5+ uur",
  "city.most_popular": "Meest gevraagd",
  "city.price_indication_note": "Indicatie op basis van landelijke gemiddelden",
  "city.price_factors_title": "Factoren die de prijs bepalen",
  "city.factor_style": "Stijl",
  "city.factor_style_desc": "Realism en full-color kosten meer dan blackwork of fine-line",
  "city.factor_placement": "Lichaamsdeel",
  "city.factor_placement_desc": "Ribben en handen zijn technisch moeilijker dan arm of rug",
  "city.factor_detail": "Detailniveau",
  "city.factor_detail_desc": "Meer detail = meer tijd = hogere prijs",
  "city.factor_experience": "Ervaring artiest",
  "city.factor_experience_desc": "Senior artiesten vragen meer, maar bieden consistentere kwaliteit",
  "city.updated": "Bijgewerkt",
  "city.calc_button_short": "Bereken",
  "settings.deposit_preview_fixed": "Aanbetaling: €{value} bij boeking",
  // ── Landing Page Redesign keys ──
  "landing.hero_eyebrow_artists": "Artiesten",
  "landing.hero_eyebrow_prices": "Prijzen",
  "landing.hero_eyebrow_styles": "Stijlen",
  "landing.hero_title_new_1": "Vind Jouw",
  "landing.hero_title_new_2": "Perfecte",
  "landing.hero_title_new_3": "Tattoo Artiest",
  "landing.hero_subtitle_new": "Vergelijk portfolio's, stijlen en prijzen. Vind de beste artiest bij jou in de buurt.",
  "landing.value_eyebrow": "Jouw tattoo-reis",
  "landing.value_title": "Alles wat je nodig hebt voor je volgende tattoo",
  "landing.value_subtitle": "Of het je eerste is of je twintigste — wij maken het proces moeiteloos.",
  "landing.value_card1_title": "Vind Jouw Artiest",
  "landing.value_card1_desc": "Doorzoek geverifieerde artiesten op stijl, locatie en prijs. Vergelijk portfolio's en boek met vertrouwen.",
  "landing.value_card1_stat": "artiesten",
  "landing.value_card2_title": "Bereken Je Prijs",
  "landing.value_card2_desc": "Krijg een realistische prijsindicatie op basis van grootte, stijl, detail en locatie. Geen verrassingen.",
  "landing.value_card2_stat": "Gratis berekenen",
  "landing.value_card3_title": "Ontdek Stijlen",
  "landing.value_card3_desc": "Van bold Traditional tot delicate Fine Line. Ontdek welke stijl bij jou past met echte voorbeelden.",
  "landing.value_card3_stat": "stijlen",
  "landing.value_artist_cta": "Ben je tattoo artiest? Claim je gratis profiel →",
  "landing.styles_title_new": "Vind Jouw Stijl",
  "landing.styles_subtitle": "Van bold Traditional tot delicate Fine Line. Ontdek de esthetiek die bij jou past.",
  "landing.styles_all_cta": "Bekijk alle stijlen",
  "landing.reviews_eyebrow": "Ervaringen",
  "landing.reviews_title": "Wat klanten zeggen",
  "landing.testimonial_1": "Via de prijscalculator wist ik precies wat ik kon verwachten. De artiest in mijn buurt was perfect match voor mijn stijl.",
  "landing.testimonial_2": "Eerste tattoo en ik was zenuwachtig. Door de stijlvergelijking en prijsindicatie voelde ik me goed voorbereid.",
  "landing.testimonial_3": "Ik heb 3 studio's vergeleken via Inkvaluator. De transparantie over prijzen en stijlen is nergens anders zo goed.",
  "landing.reviews_verified": "Geverifieerde artiesten",
  "landing.reviews_instant": "Direct vergelijken",
  "landing.reviews_clients": "tevreden klanten",
  "landing.final_cta_find": "Vind een artiest",
  "landing.proof_styles": "Stijlen",
  "nav.find_artists": "Vind Artiesten",
  "nav.by_location": "Op Locatie",
  "nav.by_location_desc": "Vind tattoo studio's en artiesten bij jou in de buurt",
  "nav.by_style": "Op Stijl",
  "nav.by_style_desc": "Zoek artiesten die gespecialiseerd zijn in jouw stijl",
  "nav.browse_studios": "Ontdekken",
  "nav.browse_studios_desc": "Bekijk alle studio's op de kaart en vergelijk",
  "nav.tools": "Tools",
  "nav.price_calculator": "Prijscalculator",
  "nav.price_calculator_desc": "Bereken wat jouw tattoo gaat kosten op basis van stijl, grootte en locatie",
  "nav.style_guide": "Stijlgids",
  "nav.style_guide_desc": "50+ tattoo stijlen uitgelegd met voorbeelden en prijsindicaties",
  "nav.for_artists": "Voor Artiesten",
  "nav.free_profile": "Gratis Profiel",
  "nav.free_profile_desc": "Claim je studio profiel en ontvang direct klantaanvragen",
  "nav.artist_resources": "Blog & Resources",
  "nav.artist_resources_desc": "Tips, trends en gidsen om je studio te laten groeien",
  "nav.artist_register": "Account Aanmaken",
  "nav.join_artist": "Claim Profiel",
  "footer.explore": "Verkennen",
  "footer.find_artist": "Vind een Artiest",
  "footer.price_calculator": "Prijscalculator",
  "footer.style_guide": "Stijlgids",
  "footer.popular_cities": "Populaire Steden",
  "footer.all_cities": "Alle steden →",
  "footer.tattoo_styles": "Tattoo Stijlen",
  "footer.all_styles": "Alle stijlen →",
  "footer.resources": "Resources",
  "footer.claim_profile": "Claim je Profiel",
  "footer.tagline_expanded": "Vind de beste tattoo artiesten, vergelijk stijlen en prijzen, en boek met vertrouwen.",
  "footer.data_deletion": "Gegevens verwijderen",
  "deletion.seo_title": "Mijn gegevens verwijderen – Inkvaluator",
  "deletion.seo_desc": "Vraag verwijdering van je persoonlijke gegevens aan. Conform AVG, CCPA en LGPD.",
  "deletion.title": "Mijn gegevens verwijderen",
  "deletion.intro": "Bij Inkvaluator respecteren we je recht om controle te hebben over je persoonlijke gegevens. Op grond van de AVG (EU), CCPA (Californië), LGPD (Brazilië) en andere toepasselijke privacywetten heb je het recht om verwijdering van je persoonsgegevens te verzoeken.",
  "deletion.what_title": "Welke gegevens worden verwijderd?",
  "deletion.what_account": "Je account- en profielinformatie",
  "deletion.what_portfolio": "Portfolioafbeeldingen en geüploade content",
  "deletion.what_leads": "Lead- en calculatorgeschiedenis",
  "deletion.what_bookings": "Boekings- en betalingsgegevens (waar wettelijk toegestaan)",
  "deletion.what_analytics": "Analytics en gebruiksgegevens gekoppeld aan je account",
  "deletion.retention_title": "Gegevensbewaring & uitzonderingen",
  "deletion.retention_text": "Sommige gegevens kunnen worden bewaard voor juridische, fiscale of nalevingsdoeleinden zoals vereist door de wet. Dit omvat financiële transactiegegevens (tot 7 jaar voor belastingverplichtingen) en gegevens die nodig zijn voor fraudepreventie of lopende geschillen.",
  "deletion.timeline_title": "Verwerkingstermijn",
  "deletion.timeline_gdpr": "Binnen 30 dagen na je verzoek",
  "deletion.timeline_ccpa": "Binnen 45 dagen na je verzoek",
  "deletion.timeline_lgpd": "Binnen 15 werkdagen",
  "deletion.timeline_note": "Je ontvangt een bevestigingsmail zodra je gegevens zijn verwijderd.",
  "deletion.form_title": "Verzoek tot gegevensverwijdering",
  "deletion.form_email": "E-mailadres gekoppeld aan je account",
  "deletion.form_message": "Aanvullende details (optioneel)",
  "deletion.form_placeholder": "Beschrijf welke gegevens je wilt laten verwijderen...",
  "deletion.form_sending": "Verzenden...",
  "deletion.form_submit": "Verwijderingsverzoek indienen",
  "deletion.form_alt": "Je kunt ons ook direct mailen op",
  "deletion.success": "Je verzoek is ontvangen. We verwerken het binnen 30 dagen.",
  "deletion.error": "Er is iets misgegaan. Mail ons alsjeblieft direct.",

  // Sprint 1: cityContent + faq keys (Pijler 1B/1C/2C)
  "cityContent.intro_a": "Inkvaluator brengt alle tattoostudio's in {city} samen op één plek, compleet met actuele prijzen, stijlen en reviews.",
  "cityContent.intro_b": "Op zoek naar de perfecte tattoostudio in {city}? Vergelijk {count} studio's direct op prijs, stijl en beschikbaarheid.",
  "cityContent.intro_c": "{city} telt {count} tattoostudio's — van traditioneel tot hyperrealism. Vind jouw match via Inkvaluator.",
  "cityContent.intro_d": "Of je nu je eerste of twintigste tattoo wil: in {city} vind je via Inkvaluator een specialist voor elke stijl en elk budget.",
  "cityContent.priceSection_title": "Tattooprijzen in {city} ({year})",
  "cityContent.priceSection_body": "Onderstaande bedragen zijn indicatieve marktprijzen voor tattoos in {city}, gebaseerd op gangbare Nederlandse tarieven (bijgewerkt {updated}). De werkelijke prijs hangt af van grootte, stijl, plaatsing en de ervaring van de artiest.",
  "cityContent.stylesSection_title": "Populaire tattoostijlen in {city}",
  "cityContent.stylesSection_subtitle": "Gebaseerd op {count} studio's · bijgewerkt {updated}",
  "cityContent.neighborhoodsSection_title": "Studio's per wijk in {city}",
  "cityContent.neighborhoodsSection_subtitle": "De meeste studio's vind je in:",
  "cityContent.nearbySection_title": "Andere steden in de buurt",
  "cityContent.nearbySection_subtitle": "Ook populair bij bezoekers van {city}:",
  "cityContent.ggdSection_title": "GGD-gecertificeerde tattooshops in {city}",
  "cityContent.ggdSection_body": "Van de {studioCount} tattooshops in {city} zijn er {verifiedCount} in bezit van een GGD-vergunning (bron: RIVM-register). Een GGD-vergunning betekent dat de studio voldoet aan de hygiëne- en veiligheidseisen van de Nederlandse overheid.",
  "cityContent.ggdSection_source": "Bron: RIVM Tattoo- en piercingregister",
  "cityContent.faq_ggd_question": "Hoeveel tattooshops in {city} hebben een GGD-vergunning?",
  "cityContent.faq_ggd_answer": "Van de {studioCount} tattooshops in {city} zijn er {verifiedCount} in bezit van een GGD-vergunning volgens het RIVM-register.",
  "nearby.title": "Tattoosteden in de buurt",
  "nearby.studios_count": "studio's",
  "faq.count_q": "Hoeveel tattooshops zijn er in {city}?",
  "faq.count_a": "{city} telt {count} geregistreerde tattoostudio's op Inkvaluator (bijgewerkt {updated}).",
  "faq.price_q": "Wat kost een tattoo in {city}?",
  "faq.price_a": "Tattooprijzen in {city} variëren van {min_small} voor een klein werk tot {avg_large}–{max_large} voor een groot stuk. Gemiddeld een medium tattoo: {avg_medium}. Bijgewerkt {updated}.",
  "faq.style_q": "Welke tattoostijl is het populairst in {city}?",
  "faq.style_a": "{style} is de meest gevraagde stijl in {city}. Gebruik de Inkvaluator calculator voor een prijsindicatie per stijl.",
  "faq.book_q": "Hoe boek je een tattoo in {city}?",
  "faq.book_a": "Via {platform} vergelijk je direct studio's in {city} op stijl, prijs en beschikbaarheid — en boek je online met aanbetaling.",

  // Profile tabs & info
  "profile.tab_portfolio": "Portfolio",
  "profile.tab_studio": "Studio",
  "profile.tab_book": "Boeken",
  "profile.tab_calculator": "Prijsindicatie",
  "profile.tab_over": "Over",
  "profile.tab_flash": "Flash",
  "profile.tab_reviews": "Reviews",
  "profile.tab_faq": "FAQ",
  "profile.studio_rules": "Studioregels",
  "profile.directions": "Route",
  "profile.facility_parking": "Parkeren",
  "profile.facility_wheelchair": "Rolstoeltoegankelijk",
  "profile.facility_private_room": "Privé ruimte",
  "profile.facility_wifi": "WiFi",
  "profile.facility_card_payment": "Pinbetaling",
  "profile.facility_drinks": "Drankjes",
  "profile.facility_sterile_cert": "Steriel gecertificeerd",
  "profile.facility_child_friendly": "Kindvriendelijk",
  "profile.facility_cover_up": "Cover-up specialist",
  "profile.facility_custom_design": "Custom ontwerp",
  "profile.facility_same_day": "Dezelfde dag",
  "profile.write_review": "Schrijf een review",
  "profile.reviews": "reviews",
  "profile.studio_artists": "Artiesten in deze studio",
  "profile.artists_count": "artiesten",
  // ─── Missing NL translations: new features (Sprint 2–4) ──
  "analytics.bookings": "Boekingen",
  "analytics.bookings_summary": "Boekingen overzicht",
  "analytics.business_coach": "Business Coach",
  "analytics.coach_actions_desc": "Gepersonaliseerd actieplan op basis van jouw data",
  "analytics.funnel_desc": "Berekeningen → Leads → Boekingen",
  "analytics.month_progress": "Maandvoortgang",
  "analytics.month_summary": "Maand samenvatting",
  "auth.email_placeholder": "jouw@email.nl",
  "availability.session_label": "Sessie",
  "booking.at": "om",
  "bookings.title": "Boekingen",
  "bookings.count": "{count} boekingen",
  "bookings.new_count": "{count} nieuw",
  "bookings.no_bookings": "Nog geen boekingen",
  "bookings.manage_desc": "Beheer en bevestig afspraken met klanten",
  "bookings.filter_all": "Alle",
  "bookings.filter_pending": "In behandeling",
  "bookings.filter_confirmed": "Bevestigd",
  "bookings.filter_cancelled": "Geannuleerd",
  "calc.fill_all_steps": "Vul alle stappen in",
  "calc.from": "Vanaf",
  "city.filter_ggd": "GGD Vergunning",
  "city.filter_ggd_hint": "Toon alleen studio's met een GGD/RIVM vergunning",
  "compare.toggle": "Configuraties vergelijken",
  "compare.lock_config": "Configuratie vergrendelen",
  "compare.clear": "Vergelijking wissen",
  "compare.difference": "Verschil",
  "dash.currency_auto_set": "Valuta automatisch ingesteld op basis van je locatie",
  "dash.loading": "Dashboard laden...",
  "deposit.mode_none": "Geen aanbetaling",
  "deposit.mode_fixed": "Vast bedrag",
  "deposit.mode_percentage": "Percentage",
  "discover.filters": "Filters",
  "discover.filter_style": "Stijl",
  "discover.filter_city": "Stad",
  "discover.filter_country": "Land",
  "discover.filter_rating": "Beoordeling",
  "discover.all_styles": "Alle stijlen",
  "discover.all_cities": "Alle steden",
  "discover.all_countries": "Alle landen",
  "discover.any": "Alle",
  "discover.clear_filters": "Filters wissen",
  "discover.results": "{count} resultaten",
  "discover.no_results_filter": "Geen artiesten gevonden voor deze filters",
  "discover.top_cities": "Topsteden",
  "discover.nearby_cities": "Steden in de buurt",
  "discover.stat_studios": "studio's",
  "discover.stat_cities": "steden",
  "discover.stat_countries": "landen",
  "discover.seo_intro_title": "Vind jouw perfecte tattoo artiest",
  "discover.seo_intro_text": "Ontdek geverifieerde tattoo artiesten en studio's. Vergelijk stijlen, prijzen en beschikbaarheid.",
  "discover.request_city_title": "Vraag jouw stad aan",
  "discover.request_city_desc": "Staat jouw stad er niet bij? We voegen hem snel toe.",
  "flash.color_label": "Kleurtype",
  "flash.color_black_grey": "Zwart & grijs",
  "flash.color_color": "Kleur",
  "flash.color_both": "Beide",
  "groei.hide_tips": "Tips verbergen",
  "header.admin": "Admin",
  "header.toggle_menu": "Menu openen/sluiten",
  "header.toggle_theme": "Thema wisselen",
  "hero.eyebrow": "Inkvaluator",
  "hero.title": "Jouw Tattoo Reis Begint Hier",
  "hero.subtitle": "Vind jouw artiest. Ken je prijs.",
  "home.copy": "Link kopiëren",
  "home.copied": "Gekopieerd!",
  "laf.reset": "Terugzetten naar standaard",
  "map.studios_count": "{count} studio's",
  "nav.cta_discover": "Artiesten Verkennen",
  "nav.explore_map": "Kaart Verkennen",
  "privacy.deletion_link": "/gegevens-verwijderen",
  "privacy.deletion_link_text": "Stuur een verzoek tot gegevensverwijdering",
  "profile.about_title": "Over mij",
  "profile.about_desc": "Vertel klanten over je stijl, specialisaties en ervaring.",
  "profile.about_placeholder": "Schrijf een korte bio over jezelf als tattoo artiest...",
  "profile.details_title": "Profielgegevens",
  "profile.details_desc": "Je naam, locatie, uurtarief en werktijden.",
  "profile.facilities_title": "Studiofaciliteiten",
  "profile.facilities_desc": "Laat klanten zien welke faciliteiten jouw studio biedt.",
  "profile.cover_up_title": "Cover-up specialist",
  "profile.cover_up_desc": "Geef aan of je gespecialiseerd bent in cover-up tattoos.",
  "profile.gender_label": "Geslacht",
  "profile.gender_male": "Man",
  "profile.gender_female": "Vrouw",
  "profile.gender_non_binary": "Non-binair",
  "profile.gender_prefer_not": "Zeg ik liever niet",
  "profile.gender_unspecified": "Niet opgegeven",
  "profile.instagram_feed": "Instagram feed",
  "profile.portfolio_connected": "Portfolio gekoppeld",
  "push.test_sent": "Pushmelding verzonden!",
  "session.title": "Sessie Planner",
  "session.session_label": "Sessie",
  "session.total_duration": "Totale sessieduur",
  "session.weeks": "weken",
  "session.weeks_healing": "weken hersteltijd",
  "settings.opening_hours_title": "Openingstijden",
  "settings.opening_hours_desc": "Stel de openingstijden van je studio in",
  "settings.studio_rules_title": "Studioregels",
  "settings.studio_rules_desc": "Stel de regels en beleidsregels in die zichtbaar zijn op je publieke profiel",
  // ─── booking.* NL (existed in EN, adding NL) ──────────
  "booking.title": "Afspraak maken bij {artist}",
  "booking.confirmed_title": "Boeking aangevraagd!",
  "booking.choose_date": "Kies een beschikbare datum",
  "booking.choose_time": "Kies een tijdslot",
  "booking.other_day": "Kies een andere dag",
  "booking.other_slot": "Ander tijdslot",
  "booking.name": "Naam",
  "booking.name_placeholder": "Jouw naam",
  "booking.email": "E-mailadres",
  "booking.email_placeholder": "jouw@email.nl",
  "booking.phone": "Telefoon / WhatsApp",
  "booking.style": "Tattoostijl",
  "booking.style_placeholder": "Kies een stijl...",
  "booking.no_preference": "— Geen voorkeur —",
  "booking.description": "Tattoo omschrijving",
  "booking.description_placeholder": "Vertel ons over jouw gewenste tattoo...",
  "booking.reference_photo": "Referentiefoto (optioneel)",
  "booking.upload_photo": "Foto uploaden",
  "booking.deposit_notice": "Bij het boeken is een aanbetaling van €{amount} vereist. De artiest neemt contact met je op voor de betaalinstructies.",
  "booking.fill_required": "Vul naam en e-mailadres in",
  "booking.submit": "Afspraak aanvragen",
  "booking.success_title": "Verzoek verstuurd!",
  "booking.success_desc": "{artist} ontvangt jouw verzoek en bevestigt de afspraak zo snel mogelijk. Je ontvangt een bevestiging op",
  "booking.close": "Sluiten",
  "booking.available": "Beschikbaar",
  "booking.unavailable": "Niet beschikbaar",
  // ─── profile.* NL gaps ────────────────────────────────
  "profile.all_filter": "Alle",
  "profile.all_reviews_google": "Bekijk alle reviews op Google",
  "profile.back": "Terug",
  "profile.calc_price": "Prijs berekenen →",
  "profile.closed": "Gesloten",
  "profile.day_mon": "Maandag",
  "profile.day_tue": "Dinsdag",
  "profile.day_wed": "Woensdag",
  "profile.day_thu": "Donderdag",
  "profile.day_fri": "Vrijdag",
  "profile.day_sat": "Zaterdag",
  "profile.day_sun": "Zondag",
  "profile.estimate": "Schatting",
  "profile.facilities": "Faciliteiten",
  "profile.find_on_google": "Vind op Google",
  "profile.find_studio": "Vind een tattoostudio →",
  "profile.hourly_rate_label": "/u uurtarief",
  "profile.live_traffic": "Live verkeer",
  "profile.navigate_to": "Navigeer naar",
  "profile.next": "Volgende",
  "profile.no_portfolio": "Nog geen portfolioafbeeldingen toegevoegd.",
  "profile.no_portfolio_hint": "De artiest kan dit instellen via het dashboard.",
  "profile.not_found_title": "Artiest niet gevonden",
  "profile.not_found_desc": "Dit profiel bestaat niet meer. Vind een artiest bij jou in de buurt.",
  "profile.open_maps": "Open Maps",
  "profile.open_now": "Nu open",
  "profile.price_calculator": "Prijscalculator",
  "profile.price_indication": "Prijsindicatie",
  "profile.recent_works": "Recente werken",
  "profile.schedule_appointment": "Afspraak plannen",
  "profile.size_small": "Klein · < 10 cm",
  "profile.size_medium": "Medium · 10–20 cm",
  "profile.size_large": "Groot · 20+ cm",
  "profile.studio_status": "Studiostatus",
  "profile.view_price": "Bekijk prijs",
  "profile.works_count": "werken",
  "profile.artist_label": "Artiest",
  "profile.book_appointment": "Afspraak boeken",
  "profile.book_desc": "Kies een beschikbare datum en tijdslot. Je ontvangt een bevestiging per e-mail.",
  "profile.book_label": "Boeken",
  "profile.calculator": "Calculator",
  "profile.choose_slot": "Kies een datum en tijdslot",
  "profile.earliest_slot": "Vroegste beschikbare slot:",
  "profile.or_whatsapp": "Of stuur een WhatsApp-bericht",
  "profile.per_hour": "/u",
  "profile.tattoo_artist": "Tattoo artiest",
  "studio.claim_cta_short": "Claim jouw studio →",
  "tattoo_index.h1": "Vind een tattoostudio bij jou in de buurt",
  "tattoo_index.search_placeholder": "Zoek een stad of tattoo artiest...",
  // ─── city / country / style / studio / rules NL gaps ──
  "city.view_all_artists": "Bekijk alle artiesten",
  "country.h1": "Tattoostudio's in {country}",
  "country.coming_soon": "Binnenkort beschikbaar",
  "country.popular_styles": "Populaire stijlen in {country}",
  "style.h1": "{style} tattoo in {city}",
  "style.subtitle": "{count} artiesten in {city} gespecialiseerd in {style}. Bereken jouw {style} tattoo prijs direct.",
  "style.artists_h2": "{style} artiesten in {city}",
  "style.calc_button": "Bereken {style} prijs →",
  "style.empty": "Nog geen {style} artiesten geclaimd in {city}.",
  "style.all_studios": "Alle tattoostudio's in {city}",
  "style.page_title": "{style} tattoo in {city} — artiesten & prijzen | Inkvaluator",
  "style.page_desc": "{count} {style} artiesten in {city}. Vergelijk portfolios en bereken je tattooprijs.",
  "rules.deposit_title": "Aanbetaling",
  "rules.deposit_body": "Een aanbetaling van €150 is vereist om je afspraak te bevestigen. Dit bedrag wordt in mindering gebracht op de totaalprijs op de dag van de sessie.",
  "rules.cancellation_title": "Annulering",
  "rules.cancellation_body": "Annulering is gratis tot 48 uur van tevoren. Annuleringen binnen 48 uur of no-shows verlies je de aanbetaling.",
  "rules.guests_title": "Gasten",
  "rules.guests_body": "Maximaal 1 gast per klant is welkom. Gasten verblijven in de lounge tijdens de sessie.",
  "rules.age_title": "Leeftijd & legitimatie",
  "rules.age_body": "Wij tatoeëren uitsluitend personen van 18 jaar en ouder. Een geldig legitimatiebewijs is vereist.",
  "rules.health_title": "Gezondheid",
  "rules.health_body": "Kom uitgerust en gevoed naar je afspraak. Meld relevante medische aandoeningen, medicatie of allergieën bij het boeken.",
  "rules.coverup_title": "Cover-ups",
  "rules.coverup_body": "Cover-ups zijn mogelijk na voorafgaand overleg. Stuur altijd foto's van de bestaande tattoo voor de afspraak.",
  "rules.design_title": "Ontwerp & aanpassingen",
  "rules.design_body": "Het definitieve ontwerp wordt gepresenteerd op de dag van de sessie. Kleine aanpassingen zijn mogelijk, grote wijzigingen kunnen leiden tot een nieuwe afspraak.",
  "rules.aftercare_title": "Nazorg",
  "rules.aftercare_body": "Volg de nazorginstructies die je na de sessie ontvangt nauwgezet op. Wij zijn niet aansprakelijk voor gevolgen van onjuiste verzorging.",
  "studio.artists_nearby": "Artiesten in {city} met een online calculator",
  "studio.claim_cta": "Claim dit profiel gratis →",
  "studio.claim_desc": "Claim dit profiel gratis en voeg een calculator toe. Je ontvangt leads van klanten in {city} direct in je inbox.",
  "studio.claim_owner": "Ben jij de eigenaar van {name}?",
  "studio.deposit": "aanbetaling",
  "studio.meta_desc": "{name} in {city}. Bekijk beoordelingen, prijzen en claim dit profiel om leads te ontvangen.",
  "studio.reviews": "reviews",
  "studio.view_with_calc": "Bekijk studio's met calculator",
  "inkswipe.page_title": "InkSwipe — Ontdek tattoo artiesten bij jou in de buurt",
  "inkswipe.page_description": "Swipe door tattoo foto's en ontdek de beste artiesten bij jou in de buurt.",
  "inkswipe.geo.title": "Tattoos bij jou in de buurt",
  "inkswipe.geo.subtitle": "We tonen foto's van artiesten binnen jouw regio.",
  "inkswipe.geo.radius_label": "Straal",
  "inkswipe.geo.no_geolocation": "Locatie niet beschikbaar op dit apparaat.",
  "inkswipe.geo.denied": "Locatietoegang geweigerd. Pas je browserinstellingen aan.",
  "inkswipe.geo.locating": "Locatie bepalen...",
  "inkswipe.geo.cta": "Gebruik mijn locatie",
  "inkswipe.header.change_location": "Locatie wijzigen",
  "inkswipe.header.login": "Inloggen",
  "inkswipe.feed.loading": "Feed wordt geladen...",
  "inkswipe.action.skip": "Overslaan",
  "inkswipe.action.like": "Vind ik leuk",
  "inkswipe.soft_prompt.title": "Sla je favorieten op",
  "inkswipe.soft_prompt.body": "Maak gratis een account aan en bewaar alle tattoos die je mooi vindt. Zo vind je altijd de perfecte artiest terug.",
  "inkswipe.soft_prompt.cta_signup": "Account aanmaken — gratis",
  "inkswipe.soft_prompt.cta_login": "Inloggen",
  "inkswipe.soft_prompt.skip": "Doorgaan zonder account",
  "inkswipe.soft_prompt.dismiss": "Sluiten",
  "inkswipe.intake.title": "Jouw stijlvoorkeur",
  "inkswipe.intake.subtitle": "Selecteer stijlen die je aanspreken.",
  "inkswipe.intake.close": "Sluiten",
  "inkswipe.intake.skip": "Overslaan",
  "inkswipe.intake.save": "Opslaan",
  "inkswipe.intake.saving": "Opslaan...",
  "inkswipe.account.page_title": "Mijn account — InkSwipe",
  "inkswipe.account.title": "Mijn account",
  "inkswipe.account.edit_prefs": "Voorkeuren bewerken",
  "inkswipe.account.logout": "Uitloggen",
  "inkswipe.account.likes_title": "Opgeslagen tattoos",
  "inkswipe.account.no_likes": "Nog geen opgeslagen tattoos.",
  "inkswipe.account.go_swipe": "Ga swipen",
  "inkswipe.feed.empty": "Geen tattoos gevonden in jouw regio.",
  "inkswipe.feed.error": "Feed kon niet geladen worden.",
  "inkswipe.feed.retry": "Opnieuw proberen",
  "inkswipe.feed.end": "Dat was alles voor nu!",
  "inkswipe.nav.title": "InkSwipe",
  "inkswipe.nav.description": "Swipe door tattoo foto's bij jou in de buurt",
  // Instagram OAuth
  "instagram.connect": "Instagram koppelen",
  "instagram.connected": "Instagram gekoppeld",
  "instagram.disconnect": "Ontkoppelen",
  "instagram.sync_now": "Nu synchroniseren",
  "instagram.sync_success": "Instagram gesynchroniseerd!",
  "instagram.sync_error": "Synchronisatie mislukt. Probeer opnieuw.",
  "instagram.not_connected": "Nog niet gekoppeld",
  "instagram.photos_synced": "foto's",
  "instagram.videos_synced": "video's",
  "instagram.connect_desc": "Koppel je Instagram-account om je werk automatisch te synchroniseren naar InkSwipe.",
  "swipe.video.play": "Video afspelen",
  "swipe.video.close": "Video sluiten",
  "directory.verified_label": "Geverifieerde artiesten",
  "directory.verified_title": "Artiesten op Inkvaluator",
  "directory.nearby_label": "In jouw regio",
  "directory.nearby_title": "Studios in de buurt",
  "directory.hero_label": "Vind jouw artiest",
  "discover.directory.filter_distance": "Afstand",
  "discover.directory.dist_10km": "< 10 km",
  "discover.directory.dist_25km": "< 25 km",
  "discover.directory.dist_50km": "< 50 km",
  "discover.directory.dist_100km": "< 100 km",
  "discover.directory.dist_250km": "< 250 km",
  "discover.directory.dist_200km": "< 200 km",
  "discover.directory.load_more": "Meer laden",
  "filters.city": "Stad",
  "filters.distance": "Afstand",
  "filters.distance.all": "Alle afstanden",
  "directory.count": "studio's gevonden",
  "directory.loadMore": "Meer laden",
  "city.matchCta": "Niet zeker welke artiest bij je past?",
  "city.matchCtaLink": "Gebruik de gratis artiest-matcher",
  "directory.matchBanner": "Wil je een gepersonaliseerde aanbeveling op basis van locatie, stijl en budget?",
  "directory.matchBannerLink": "Gebruik de matcher →",
  // Preview sheet
  "preview.close": "Sluiten",
  "preview.closed": "Gesloten",
  "preview.open": "Open",
  "preview.until": "tot",
  "preview.from_price": "Vanaf",
  "preview.view_profile": "Bekijk volledig profiel",
  "preview.claim_cta": "Is dit jouw studio? Claim gratis",
  "preview.claim_title": "Is dit jouw studio?",
  "preview.claim_sub": "Claim dit profiel gratis en voeg je portfolio toe",
  "profile.closed.permanent": "Deze studio is permanent gesloten",
  "profile.closed.temporary": "Deze studio is tijdelijk gesloten",
  "profile.closed.subtitle": "Ken je de eigenaar? Laat hen weten dat ze hun profiel kunnen claimen.",
  "profile.summary.studio": "tattoostudio",
  "profile.faq.title": "Veelgestelde vragen",
  "profile.faq.cost.q": "Wat kost een tattoo bij {name}?",
  "profile.faq.ggd.q": "Heeft {name} een GGD-vergunning?",
  "profile.faq.location.q": "Waar zit {name}?",
  "profile.faq.contact.q": "Hoe neem ik contact op met {name}?",
  "profile.updated": "Bijgewerkt",
  "dashboard.hub.completeness_title": "Profiel Compleetheid",
  "dashboard.hub.completeness_desc": "Vul je profiel aan om hoger te scoren in zoekresultaten.",
  "dashboard.hub.fields_complete": "velden ingevuld",
  "dashboard.hub.next_step": "Volgende stap",
  "dashboard.hub.basis_title": "Basis",
  "dashboard.hub.basis_desc": "Bio, ervaring, talen en specialisaties.",
  "dashboard.hub.bio_label": "Bio",
  "dashboard.hub.bio_hint": "Vertel klanten wie je bent en wat je stijl is. Minimaal 80 tekens voor compleetheid.",
  "dashboard.hub.bio_placeholder": "Beschrijf jezelf als artiest...",
  "dashboard.hub.years_exp_label": "Jaren ervaring",
  "dashboard.hub.years_suffix": "jaar",
  "dashboard.hub.languages_label": "Gesproken talen",
  "dashboard.hub.languages_hint": "Druk Enter of komma om een taal toe te voegen.",
  "dashboard.hub.languages_placeholder": "Bijv. Nederlands, Engels...",
  "dashboard.hub.specializations_label": "Specialisaties",
  "dashboard.hub.specializations_hint": "Stijlen en technieken waar je je in specialiseert.",
  "dashboard.hub.specializations_placeholder": "Bijv. Blackwork, Fine line...",
  "dashboard.profile.field_google_place_id_label": "Google Business gekoppeld",
  "dashboard.profile.field_google_place_id_cta": "Koppel je Google-locatie",
  "dashboard.profile.field_bio_label": "Bio",
  "dashboard.profile.field_bio_cta": "Schrijf een bio (min. 80 tekens)",
  "dashboard.profile.field_portfolio_label": "Portfolio foto's",
  "dashboard.profile.field_portfolio_cta": "Voeg 3+ portfoliofoto's toe",
  "dashboard.profile.field_specializations_label": "Specialisaties",
  "dashboard.profile.field_specializations_cta": "Voeg je specialisaties toe",
  "dashboard.profile.field_price_examples_label": "Prijsvoorbeelden",
  "dashboard.profile.field_price_examples_cta": "Voeg prijsvoorbeelden toe",
  "dashboard.profile.field_years_experience_label": "Jaren ervaring",
  "dashboard.profile.field_years_experience_cta": "Vul je ervaringsjaren in",
  "dashboard.profile.field_languages_label": "Talen",
  "dashboard.profile.field_languages_cta": "Voeg gesproken talen toe",
  "dashboard.profile.field_opening_hours_label": "Openingstijden",
  "dashboard.profile.field_opening_hours_cta": "Stel je openingstijden in",
  "dashboard.profile.field_certifications_label": "Certificeringen",
  "dashboard.profile.field_certifications_cta": "Voeg een certificering toe",
  "dashboard.profile.field_artist_faqs_label": "FAQ",
  "dashboard.profile.field_artist_faqs_cta": "Voeg minimaal 1 FAQ toe",
  "dashboard.profile.field_awards_label": "Awards & pers",
  "dashboard.profile.field_awards_cta": "Awards toevoegen",
  "dashboard.profile.google_search_title": "Google Business koppelen",
  "dashboard.profile.google_search_desc": "Zoek je studio om Google-beoordelingen op je profiel te tonen.",
  "dashboard.profile.google_no_fixed_location": "Ik werk niet vanuit een vaste studio-locatie",
  "dashboard.profile.google_opted_out_label": "Je hebt aangegeven als solo-artist te werken zonder vaste locatie. Dit telt mee voor je profielscore.",
  "repeater.add_certification": "Certificering toevoegen",
  "repeater.add_price_example": "Voorbeeld toevoegen",
  "repeater.add_award": "Award toevoegen",
  "repeater.empty_certifications": "Nog geen certificeringen",
  "repeater.empty_prices": "Nog geen voorbeeldprijzen",
  "repeater.empty_awards": "Nog geen awards of pers",
  "repeater.name": "Naam",
  "repeater.issuer": "Uitgever",
  "repeater.year": "Jaar",
  "repeater.description": "Omschrijving",
  "repeater.price_from": "Vanaf",
  "repeater.price_to": "Tot",
  "repeater.title": "Titel",
  "repeater.link": "Link",
  "branding.bio_managed_in_profile": "Beheer je bio via Profiel → Basis.",
};

// ─── GERMAN (complete) ─────────────────────────────────────────
const de: Record<string, string> = {
  ...en,
  "app.tagline": "Berechne deinen Tattoo-Preis",
  "currency.aria_label": "Währung ändern",
  "currency.title": "Anzeigewährung",
  "currency.rates_of": "Kurse vom {date}",
  "currency.approx_tooltip": "Originalpreis {original} — Wechselkurs vom {date}",
  back: "Zurück",
  save: "Speichern",
  copy: "Kopieren",
  copied: "Kopiert!",
  loading: "Laden...",
  error: "Fehler",
  optional: "optional",
  "footer.explore": "Entdecken",
  "footer.find_artist": "Künstler finden",
  "footer.price_calculator": "Preisrechner",
  "footer.style_guide": "Stilguide",
  "footer.popular_cities": "Beliebte Städte",
  "footer.all_cities": "Alle Städte →",
  "footer.tattoo_styles": "Tattoo-Stile",
  "footer.all_styles": "Alle Stile →",
  "footer.resources": "Ressourcen",
  "footer.claim_profile": "Profil beanspruchen",
  "footer.tagline_expanded": "Finde die besten Tattoo-Künstler, vergleiche Stile und Preise und buche mit Vertrauen.",
  "footer.data_deletion": "Datenlöschung",
  "landing.hero": "Berechne deinen",
  "landing.hero_accent": "Tattoo Preis",
  "landing.subtitle": "Gib den Code deines Tätowierers ein, um eine persönliche Preisschätzung zu erhalten.",
  "landing.code_title": "Calculator Code",
  "landing.code_desc": "Du hast einen einzigartigen Code von deinem Tätowierer erhalten",
  "landing.code_placeholder": "Z.B. A3F9K2",
  "landing.start": "Calculator starten",
  "landing.invalid_code": "Gib einen gültigen Code ein",
  "landing.or": "oder",
  "landing.artist_cta": "Bist du Tätowierer? Erstelle deinen eigenen Calculator.",
  "landing.artist_register": "Als Künstler registrieren",
  "landing.artist_login": "Künstler Login",
  "landing.hero_title": "Berechne deinen",
  "landing.hero_title_accent": "Tattoo Preis",
  "landing.hero_desc": "Erfahre in Sekunden, was dein nächstes Tattoo kostet. Probiere es direkt aus.",
  "landing.hero_ai_speed": "Vom Foto zur Preisschätzung in Sekunden — powered by AI",
  "feature.styles_free": "Unbegrenzte Stile aus der Master List",
  "feature.ai_scanner": "AI Scanner (inkl. Körperplatzierung)",
  "feature.whatsapp_leads": "WhatsApp Leads",
  "feature.logo_branding": "Custom Branding (Logo + 8 Presets)",
  "feature.google_reviews": "Google Review Sync",
  "feature.portfolio_showcase": "Portfolio Showcase",
  "feature.verified_badge": "'Verified' Künstler Badge",
  "feature.referral_system": "Empfehlungsprogramm",
  "feature.shared_branding": "Geteiltes Studio-Branding",
  "style_city.page_title": "Beste {style} Tattoo-Künstler in {city}",
  "style_city.h1_prefix": "Beste",
  "style_city.h1_suffix": "Tattoo-Künstler in",
  "landing.try_it": "Jetzt ausprobieren!",
  "landing.estimated_price": "Geschätzter Preis",
  "landing.your_rates": "Deine Preise",
  "landing.rates_desc": "Verschiebe die Regler und sieh die Preisänderung",
  "landing.hourly_rate": "Stundensatz",
  "landing.start_cost": "Fixe Startkosten",
  "landing.size_label": "Größe",
  "landing.complexity_label": "Komplexität",
  "landing.body_placement": "Platzierung am Körper",
  "landing.find_artist": "Finde deinen perfekten",
  "landing.find_artist_accent": "Künstler",
  "landing.nearby_soon": "Bald erscheinen hier Künstler in deiner Nähe.",
  "landing.discover_all": "Alle Künstler entdecken",
  "landing.artist_question": "Bist du Künstler oder Studio?",
  "landing.view_options": "Optionen & Preise anzeigen",
  "landing.start_free": "Kostenlos starten",
  "landing.discover": "Entdecken",
  "landing.login": "Anmelden",
  "landing.founder_desc": "50% Rabatt für die ersten 50 Anmeldungen",
  "landing.claim_now": "Jetzt sichern →",
  "calc.estimate": "Geschätzter Preis",
  "calc.start_title": "Wie möchtest du beginnen?",
  "calc.start_subtitle": "Lade ein Referenzbild hoch oder gib die Details manuell ein",
  "calc.manual": "Manuell eingeben",
  "calc.ai_scan": "KI-Bildscan",
  "profile.tab_portfolio": "Portfolio",
  "profile.tab_studio": "Studio",
  "profile.tab_book": "Buchen",
  "profile.tab_calculator": "Preisschätzung",
  "profile.no_portfolio": "Noch keine Portfolio-Bilder hinzugefügt.",
  "profile.no_portfolio_hint": "Der Künstler kann dies über das Dashboard einrichten.",
  "profile.studio_status": "Studio-Status",
  "profile.open_now": "Jetzt geöffnet",
  "profile.closed": "Geschlossen",
  "profile.studio_rules": "Studioregeln",
  "rules.deposit_title": "Anzahlung",
  "rules.deposit_body": "Eine Anzahlung von €150 ist erforderlich, um deinen Termin zu bestätigen. Dieser Betrag wird am Tag der Sitzung vom Gesamtpreis abgezogen.",
  "rules.cancellation_title": "Stornierung",
  "rules.cancellation_body": "Eine Stornierung ist bis 48 Stunden im Voraus kostenlos möglich. Bei Stornierungen innerhalb von 48 Stunden oder Nichterscheinen verfällt die Anzahlung.",
  "rules.guests_title": "Begleitpersonen",
  "rules.guests_body": "Maximal 1 Begleitperson pro Kunde ist willkommen. Begleitpersonen warten während der Sitzung im Lounge-Bereich.",
  "rules.age_title": "Alter & Ausweis",
  "rules.age_body": "Wir tätowieren ausschließlich Personen ab 18 Jahren. Ein gültiger Ausweis ist erforderlich.",
  "rules.health_title": "Gesundheit",
  "rules.health_body": "Komm ausgeruht und gut gegessen zu deinem Termin. Teile bei der Buchung alle relevanten Erkrankungen, Medikamente oder Allergien mit.",
  "rules.coverup_title": "Cover-ups",
  "rules.coverup_body": "Cover-ups sind nach vorheriger Absprache möglich. Sende vor dem Termin immer Fotos des bestehenden Tattoos.",
  "rules.design_title": "Design & Änderungen",
  "rules.design_body": "Das endgültige Design wird am Tag der Sitzung präsentiert. Kleine Anpassungen sind möglich, größere Änderungen können zu einem neuen Termin führen.",
  "rules.aftercare_title": "Nachsorge",
  "rules.aftercare_body": "Befolge die Nachsorgehinweise, die du nach der Sitzung erhältst, sorgfältig. Wir haften nicht für Folgen unsachgemäßer Pflege.",
  "profile.facilities": "Ausstattung",
  "profile.price_indication": "Preisindikation",
  "profile.hourly_rate_label": "/Std. Stundensatz",
  "profile.directions": "Wegbeschreibung",
  "profile.day_mon": "Montag",
  "profile.day_tue": "Dienstag",
  "profile.day_wed": "Mittwoch",
  "profile.day_thu": "Donnerstag",
  "profile.day_fri": "Freitag",
  "profile.day_sat": "Samstag",
  "profile.day_sun": "Sonntag",
  "profile.facility_parking": "Parkplatz",
  "profile.facility_wheelchair": "Barrierefrei",
  "profile.facility_private_room": "Privatraum",
  "profile.facility_wifi": "WLAN",
  "profile.facility_card_payment": "Kartenzahlung",
  "profile.facility_drinks": "Getränke",
  "profile.facility_sterile_cert": "Sterilitätszert.",
  "profile.facility_child_friendly": "Kinderfreundlich",
  "profile.facility_cover_up": "Cover-up",
  "profile.facility_custom_design": "Custom Design",
  "profile.facility_same_day": "Am selben Tag",
  "profile.size_small": "Klein · < 10 cm",
  "profile.size_medium": "Mittel · 10–20 cm",
  "profile.size_large": "Groß · 20+ cm",
  "profile.book_appointment": "Termin buchen",
  "profile.book_desc": "Wähle ein verfügbares Datum und Zeitfenster. Du erhältst eine Bestätigung per E-Mail.",
  "profile.earliest_slot": "Frühester verfügbarer Termin:",
  "profile.choose_slot": "Datum und Zeitfenster wählen",
  "profile.calculator": "Rechner",
  "profile.estimate": "Schätzung",
  "profile.back": "Zurück",
  "profile.next": "Weiter",
  "profile.view_price": "Preis ansehen",
  "profile.all_reviews_google": "Alle Bewertungen auf Google ansehen",
  "profile.write_review": "Bewertung schreiben",
  "profile.reviews": "Bewertungen",
  "profile.not_found_title": "Künstler nicht gefunden",
  "profile.not_found_desc": "Dieses Profil existiert nicht mehr. Finde einen Künstler in deiner Nähe.",
  "profile.find_studio": "Tattoo-Studio finden →",
  "profile.studio_artists": "Künstler dieses Studios",
  "profile.artists_count": "Künstler",
  "profile.calc_price": "Preis berechnen →",
  "profile.recent_works": "Aktuelle Arbeiten",
  "profile.works_count": "Arbeiten",
  "profile.all_filter": "Alle",
  "profile.navigate_to": "Navigieren zu",
  "profile.price_calculator": "Preisrechner",
  "profile.or_whatsapp": "Oder sende eine WhatsApp-Nachricht",
  "profile.schedule_appointment": "Termin vereinbaren",
  "profile.per_hour": "/Std.",
  "profile.artist_label": "Künstler",
  "profile.tattoo_artist": "Tattoo-Künstler",
  "profile.book_label": "Buchen",
  "profile.open_maps": "Karte öffnen",
  "profile.live_traffic": "Live-Verkehr",
  "profile.find_on_google": "Auf Google suchen",
  "booking.title": "Termin buchen bei {artist}",
  "booking.confirmed_title": "Buchung angefragt!",
  "booking.choose_date": "Wähle ein verfügbares Datum",
  "booking.choose_time": "Wähle ein Zeitfenster",
  "booking.other_day": "Anderen Tag wählen",
  "booking.other_slot": "Anderes Zeitfenster",
  "booking.name": "Name",
  "booking.name_placeholder": "Dein Name",
  "booking.email": "E-Mail-Adresse",
  "booking.email_placeholder": "dein@email.de",
  "booking.phone": "Telefonnummer / WhatsApp",
  "booking.style": "Tattoo-Stil",
  "booking.style_placeholder": "Wähle einen Stil...",
  "booking.no_preference": "— Keine Präferenz —",
  "booking.description": "Tattoo-Beschreibung",
  "booking.description_placeholder": "Erzähl etwas über dein gewünschtes Tattoo...",
  "booking.reference_photo": "Referenzfoto (optional)",
  "booking.upload_photo": "Foto hochladen",
  "booking.deposit_notice": "Bei der Buchung ist eine Anzahlung von €{amount} erforderlich. Der Künstler kontaktiert dich mit den Zahlungsdetails.",
  "booking.fill_required": "Bitte Name und E-Mail ausfüllen",
  "booking.submit": "Termin anfragen",
  "booking.success_title": "Anfrage gesendet!",
  "booking.success_desc": "{artist} erhält deine Anfrage und bestätigt den Termin so schnell wie möglich. Du erhältst eine Bestätigung an",
  "booking.close": "Schließen",
  "booking.available": "Verfügbar",
  "booking.unavailable": "Nicht verfügbar",
  "calc.size": "Größe",
  "calc.size_desc": "Wie groß wird dein Tattoo?",
  "calc.complexity": "Komplexität",
  "calc.complexity_desc": "Wie detailliert ist das Design?",
  "calc.body_part": "Körperteil",
  "calc.body_part_desc": "Klicke auf die Körperstelle deines Tattoos",
  "calc.style": "Stil",
  "calc.style_desc": "Welchen Tattoo-Stil möchtest du?",
  "calc.budget": "Budget",
  "calc.budget_desc": "Was ist dein maximales Budget?",
  "calc.budget_placeholder": "Z.B. 300",
  "calc.get_estimate": "Erhalte eine realistische Preisschätzung von",
  "calc.no_artist": "Kein Künstler mit diesem Code oder Link gefunden.",
  "calc.not_configured": "Der Preisrechner wurde vom Künstler noch nicht konfiguriert.",
  "calc.back_home": "Zurück zur Startseite",
  "calc.artist_login": "Künstler? Anmelden",
  "calc.add_color": "Farbe hinzufügen?",
  "calc.color_surcharge": "+20% Aufschlag für Farbarbeit",
  "breakdown.title": "Preisaufschlüsselung",
  "breakdown.labor": "Arbeit (Zeit)",
  "breakdown.est_hours": "Geschätzte Stunden",
  "breakdown.subtotal": "Zwischensumme",
  "breakdown.complexity": "Komplexität",
  "breakdown.placement": "Platzierung",
  "breakdown.style": "Stil",
  "breakdown.design_cost": "Designkosten",
  "breakdown.deposit": "Anzahlung",
  "breakdown.total_range": "Gesamtbereich",
  "breakdown.disclaimer":
    "Dies ist eine Schätzung, kein Endpreis. Der tatsächliche Preis wird nach einer Beratung mit dem Künstler festgelegt.",
  "breakdown.budget_good": "Dein Budget passt gut in diese Schätzung!",
  "breakdown.budget_okay": "Dein Budget passt in den unteren Bereich dieser Schätzung.",
  "breakdown.budget_low": "Erwäge ein kleineres oder einfacheres Tattoo für dieses Budget.",
  "breakdown.full_disclaimer":
    "Haftungsausschluss: Dies ist eine automatische Preisschätzung. Der endgültige Preis wird vom Künstler nach einem persönlichen Gespräch festgelegt.",
  "breakdown.estimate_label": "Schätzung:",
  "breakdown.base_price_label": "Startpreis (Größe & Platzierung)",
  "breakdown.style_detail_surcharge": "Stil- & Detailzuschlag",
  "breakdown.color_surcharge_label": "Farbzuschlag (+20% Zeit)",
  "breakdown.total_indication": "Gesamtindikation",
  "breakdown.required_deposit": "Erforderliche Anzahlung",
  "breakdown.hours_unit": "Stunden",
  "tooltip.labor":
    "Basierend auf der geschätzten Zeit für diese Tattoo-Größe, multipliziert mit dem Stundensatz des Künstlers.",
  "tooltip.complexity": "Komplexere Designs (mehr Details, Schatten, Texturen) erfordern mehr Zeit und Präzision.",
  "tooltip.placement":
    "Einige Körperstellen sind empfindlicher oder schwerer erreichbar und erfordern zusätzliche Zeit und Sorgfalt.",
  "tooltip.style": "Verschiedene Stile erfordern spezifische Techniken und Fachkenntnisse, die den Preis beeinflussen.",
  "tooltip.design": "Kosten für das Entwerfen und Vorbereiten deines einzigartigen Tattoos.",
  "tooltip.deposit": "Die Anzahlung reserviert deinen Termin und wird vom Gesamtpreis abgezogen.",
  "faq.title": "Häufig gestellte Fragen",
  "faq.deposit_q": "Warum eine Anzahlung?",
  "faq.deposit_a": "Die Anzahlung reserviert deinen Termin und deckt die Designzeit ab.",
  "faq.bigger_q": "Was wenn mein Tattoo größer wird?",
  "faq.bigger_a": "Der Preis kann sich nach der Beratung ändern, wenn das Design angepasst wird.",
  "faq.consult_q": "Ist eine Beratung kostenlos?",
  "faq.consult_a": "Kontaktiere das Studio für Informationen über Beratungen.",
  "dash.welcome": "Willkommen bei Inkvaluator",
  "dash.setup_desc": "Lass uns deine Preiseinstellungen konfigurieren",
  "dash.setup_info": "Das dauert nur wenige Minuten. Du kannst alles später ändern.",
  "dash.start_config": "Konfiguration starten",
  "dash.logout": "Abmelden",
  "dash.share_title": "Teile deinen Calculator",
  "dash.share_desc": "Teile diesen Link oder Code mit deinen Kunden.",
  "dash.link_copied": "Link kopiert!",
  "dash.code_copied": "Code kopiert!",
  "dash.saved": "Gespeichert!",
  "dash.settings_saved": "Deine Einstellungen wurden aktualisiert.",
  "dash.save_error": "Einstellungen konnten nicht gespeichert werden.",
  "dash.view_calculator": "Calculator anzeigen",
  "dash.settings_label": "Einstellungen",
  "dash.profile_studio": "Profil & Studio",
  "dash.calculator_config": "Calculator Konfiguration",
  "dash.pro_features": "Pro Funktionen",
  "dash.faq_management": "FAQ Verwaltung",
  "dash.custom_branding": "Custom Branding",
  "dash.whatsapp_leads": "WhatsApp Leads",
  "dash.team_management": "Teamverwaltung",
  "dash.save_changes": "Änderungen speichern",
  "dash.cancel": "Abbrechen",
  "dash.add": "Hinzufügen",
  "dash.delete": "Löschen",
  "dash.previous": "Zurück",
  "dash.next": "Weiter",
  "tab.basic": "Basis",
  "tab.sizes": "Größen",
  "tab.complexity": "Komplexität",
  "tab.body_parts": "Körperteile",
  "tab.styles": "Stile",
  "tab.theme": "Thema",
  "tab.branding": "Branding",
  "tab.analytics": "Analytics",
  "basic.title": "Grundtarif",
  "basic.desc": "Setze deine Standardtarife und Kosten",
  "basic.min_price": "Mindestpreis",
  "basic.min_price_desc": "Der niedrigste Preis den du berechnest",
  "basic.hourly_rate": "Stundensatz",
  "basic.hourly_rate_desc": "Dein Standard-Stundensatz",
  "basic.daily_rate": "Tagessatz",
  "basic.design_cost": "Designkosten",
  "basic.fixed": "Fest",
  "basic.hourly": "Pro Stunde",
  "basic.deposit": "Anzahlung",
  "size.mini": "Mini (< 5 cm)",
  "size.small": "Klein (5-10 cm)",
  "size.medium": "Mittel (10-20 cm)",
  "size.large": "Groß (20-35 cm)",
  "size.xl": "Extra Groß (35+ cm)",
  "complexity.simple": "Einfach",
  "complexity.medium": "Mittel",
  "complexity.detailed": "Detailliert",
  "body.arm": "Arm (allgemein)",
  "body.forearm": "Unterarm",
  "body.upper_arm": "Oberarm",
  "body.leg": "Bein (allgemein)",
  "body.thigh": "Oberschenkel",
  "body.calf": "Wade",
  "body.back": "Rücken",
  "body.chest": "Brust",
  "body.ribs": "Rippen",
  "body.stomach": "Bauch",
  "body.hand": "Hand",
  "body.foot": "Fuß",
  "body.neck": "Hals",
  "body.face": "Gesicht",
  "body.shoulder": "Schulter",
  "body.hip": "Hüfte",
  "body.finger": "Finger",
  "body.other": "Sonstiges",
  "body.selected": "Ausgewählt",
  "body.front": "Vorderseite",
  "body.back_view": "Rückseite",
  "body.front_view": "Vorderseite",
  "body.back_view_btn": "Rückseite",
  "style.black_grey": "Schwarz & Grau",
  "style.full_color": "Vollfarbe",
  "style.realism": "Realismus",
  "style.fine_line": "Fine Line / Minimal",
  "style.traditional": "Traditional",
  "style.neo_traditional": "Neo Traditional",
  "style.watercolor": "Aquarell",
  "style.tribal": "Tribal",
  "style.japanese": "Japanisch",
  "style.geometric": "Geometrisch",
  "style.lettering": "Lettering",
  "style.script": "Schrift",
  "style.dotwork": "Dotwork",
  "style.mandala": "Mandala",
  "style.blackwork": "Blackwork",
  "style.illustrative": "Illustrativ",
  "style.surrealism": "Surrealismus",
  "style.ornamental": "Ornamental",
  "style.micro_realism": "Mikro-Realismus",
  "style.trash_polka": "Trash Polka",
  "style.other": "Sonstiges",
  "consult.title": "Bereit anzufangen?",
  "consult.desc": "Fordere eine Beratung an, um dein Tattoo zu besprechen.",
  "consult.book": "Beratung buchen",
  "consult.email": "Beratung anfragen",
  "pdf.title": "Preisschätzung",
  "pdf.download": "PDF herunterladen",
  "pdf.your_selection": "Deine Auswahl",
  "upgrade.choose": "Wähle dein",
  "upgrade.plan_word": "Abo",
  "upgrade.per_month": "/Monat",
  "upgrade.monthly": "Monatlich",
  "upgrade.yearly": "Jährlich",
  "upgrade.mo": "Mo",
  "upgrade.year": "Jahr",
  "billing.expires": "Läuft ab",
  "billing.change": "Ändern",
  "billing.next_invoice": "Nächste Rechnung",
  "billing.invoice_history": "Rechnungsverlauf",
  "upgrade.subtitle": "Vom Solo-Künstler zum kompletten Studio — wähle das Abo das zu dir passt.",
  "upgrade.perfect_start": "Perfekt zum Starten",
  "upgrade.everything_pro": "Alles für Profis",
  "upgrade.for_studios": "Für Tattoo-Studios & Teams",
  "upgrade.upgrade_pro": "Auf Pro upgraden",
  "upgrade.upgrade_studio": "Auf Studio upgraden",
  "upgrade.manage_sub": "Abo verwalten",
  "upgrade.cancel_anytime_long": "Jederzeit kündbar. Keine versteckten Kosten.",
  "upgrade.discount": "Rabatt",
  "auth.login": "Anmelden",
  "auth.register": "Registrieren",
  "auth.artist_name": "Künstlername",
  "auth.email": "E-Mail",
  "auth.password": "Passwort",
  "auth.submit_login": "Anmelden",
  "auth.submit_register": "Konto erstellen",
  "auth.switch_register": "Noch kein Konto? Hier registrieren",
  "auth.switch_login": "Bereits ein Konto? Hier anmelden",
  "auth.login_failed": "Anmeldung fehlgeschlagen",
  "auth.wrong_credentials": "Falsche E-Mail oder Passwort",
  "auth.account_created": "Konto erstellt!",
  "auth.check_email": "Überprüfe deine E-Mail zur Bestätigung.",
  "discover.subtitle": "Studios & Künstler entdecken",
  "discover.title": "Entdecken",
  "discover.title_accent": "Künstler",
  "discover.description": "Finde Tätowierer und Studios in deiner Nähe.",
  "discover.search_placeholder": "Suche nach Stadt, Künstler oder Studio...",
  "discover.no_artists": "Keine Künstler gefunden",
  "discover.view_calculator": "Calculator anzeigen",
  "footer.tagline": "Der smarteste Tattoo-Preisrechner für Künstler und Kunden.",
  "footer.links": "Links",
  "footer.discover_artists": "Künstler entdecken",
  "footer.artist_login": "Künstler Login",
  "footer.privacy": "Datenschutz",
  "footer.language_label": "Sprache & App",
  "footer.pwa_badge": "PWA-optimiert (Klick für Info)",
  "footer.all_rights": "Alle Rechte vorbehalten.",
  "footer.install_title": "Installieren",
  "footer.install_desc":
    "Nutze Inkvaluator als echte App auf deinem Handy. Schnell, sicher und ohne Download aus dem Store.",
  "footer.install_ios_title": "iPhone / iPad (iOS)",
  "footer.install_ios_desc": 'Tippe auf die Teilen-Taste unten und wähle "Zum Startbildschirm".',
  "footer.install_android_title": "Android",
  "footer.install_android_desc": 'Tippe auf die drei Punkte oben rechts und wähle "App installieren".',
  "footer.install_ok": "Verstanden!",
  "success.welcome": "Willkommen im Club,",
  "success.go_dashboard": "Zum Dashboard",
  "success.syncing": "Dein Plan wird aktiviert…",
  "landing.how_it_works_cta": "Erfahre wie es für dein Studio funktioniert",
  // Footer extra
  "footer.terms": "Allgemeine Geschäftsbedingungen",
  "footer.support": "Support",
  "footer.how_it_works": "Wie funktioniert es?",
  // How It Works
  "hiw.seo_title": "Wie funktioniert es? – Inkvaluator",
  "hiw.seo_desc":
    "Erfahre, wie Inkvaluator Tätowierern mit automatischen Preisschätzungen, smarten Leads und professionellem Branding hilft.",
  "hiw.hero_title": "Fokus auf die Tinte, nicht auf die Rechnung",
  "hiw.hero_p1":
    "Verbringst du mehr Zeit damit, DMs über Preise zu beantworten, als dein nächstes Meisterwerk zu zeichnen? Seien wir ehrlich: Du bist Tätowierer, kein Kundenservice-Mitarbeiter.",
  "hiw.hero_p2":
    'Jede „Was kostet das?"-Frage, die du manuell beantworten musst, ist Zeit, die du nicht in deine Kunst investierst.',
  "hiw.hero_p3":
    "Inkvaluator ist der digitale Assistent, der dein Business filtert. Während du in der Zone bist, berechnet das Tool präzise Preisschätzungen für deine Kunden, sammelt alle nötigen Infos und schickt eine fertige Anfrage direkt in dein WhatsApp.",
  "hiw.hero_p4":
    'Keine „Preisgucker" mehr, die nach drei Stunden Gespräch kein Budget haben. Nur noch ernsthafte Leads, professionelle Angebote und ein Branding, das zeigt, dass du dein Handwerk ernst nimmst.',
  "hiw.hero_cta": "Bereit, deine Stuhlzeit zurückzugewinnen?",
  "hiw.faq_title": "Häufig gestellte Fragen",
  "hiw.faq_cat1": "Die Basics: Wie hilft mir das?",
  "hiw.faq_q1": "Ist das wieder so eine komplizierte App?",
  "hiw.faq_a1":
    "Nein. Es ist ein superschneller Rechner, den du in deine Instagram-Bio setzt oder auf deiner Website einbettest. Du stellst einmal deine Regeln ein (Stundensatz, Startkosten, Stile) und das Tool erledigt den Rest.",
  "hiw.faq_q2": "Wie spart es mir konkret Zeit?",
  "hiw.faq_a2":
    'Statt zehnmal zu fragen: „Wie groß? Wo am Körper? Welcher Stil?" bekommst du eine WhatsApp-Nachricht, in der alles bereits steht, inklusive Referenzfoto und dem Preis, den der Kunde bereits akzeptiert hat.',
  "hiw.faq_cat2": "Geld & Einstellungen",
  "hiw.faq_q3": "Kann ich meine eigenen Preise festlegen?",
  "hiw.faq_a3":
    "Absolut. Du bist der Boss. Du legst deinen eigenen Stundensatz fest und bestimmst, was du für verschiedene Größen und Komplexitäten berechnest. Das Tool rechnet genau so, wie du es an deinem Arbeitstisch tun würdest.",
  "hiw.faq_q4": "Was, wenn ein Tattoo viel komplexer ist als der Durchschnitt?",
  "hiw.faq_a4":
    "Unser integrierter AI Scanner erkennt die Details auf einem Foto. Er sieht den Unterschied zwischen einem einfachen Fine-Line-Text und einem hyperrealistischen Porträt und passt die Preisschätzung entsprechend an.",
  "hiw.faq_cat3": "Aussehen & Branding",
  "hiw.faq_q5": "Sieht es nicht aus wie ein Standard-Tool?",
  "hiw.faq_a5":
    "Definitiv nicht. Mit dem Studio-Plan ist das Tool vollständig White-Label. Das heißt: dein Logo, deine Farben und dein Stil. Es stehen 8 professionelle Theme-Presets bereit (von Luxury Dark bis Modern Light), sodass der Rechner nahtlos zu deinem Shop passt.",
  "hiw.faq_q6": "Wie professionell wirkt das auf meinen Kunden?",
  "hiw.faq_a6":
    'Sehr professionell. Der Kunde kann das Angebot als elegantes PDF herunterladen. Das schafft Vertrauen und verhindert spätere Diskussionen über „was wir vereinbart hatten".',
  "hiw.faq_cat4": "Wachstum & Reichweite",
  "hiw.faq_q7": "Ich möchte mehr Kunden aus meiner Region – hilft das?",
  "hiw.faq_a7":
    "Ja! Inkvaluator hat eine interaktive Weltkarte. Kunden können nach Land, Provinz und Stadt suchen. Je mehr du das Tool nutzt, desto besser wirst du von Leuten gefunden, die speziell in deiner Stadt einen Künstler suchen.",
  "hiw.backend_title": "Hinter den Kulissen: Die Kraft des Backends",
  "hiw.backend_intro":
    "Für die Technikliebhaber und Kontrollfreaks: Inkvaluator steckt voller fortschrittlicher Features, die dein Studio auf das nächste Level bringen.",
  "hiw.card_pricing_title": "Dynamisches Preismanagement",
  "hiw.card_pricing_text":
    "Passe Preise und Stripe Price IDs über das Admin-Modul an, ohne eine Zeile Code zu berühren. Alles synchronisiert sich sofort mit deiner Kasse.",
  "hiw.card_ai_title": "AI Vision Integration",
  "hiw.card_ai_text":
    "Der AI Scanner nutzt Bildanalyse, um Muster, Farbverwendung und Komplexität in Arbeitsstunden umzurechnen.",
  "hiw.card_stripe_title": "Stripe Customer Portal",
  "hiw.card_stripe_text":
    "Ein voll automatisiertes Abo-System. Upgraden auf Pro, Rechnungen herunterladen oder dein Abo verwalten – alles in einer sicheren Umgebung.",
  "hiw.card_analytics_title": "Analytics Dashboard",
  "hiw.card_analytics_text":
    "Erhalte tiefe Einblicke in deine Konversionen. Sieh, wie viele Leute deinen Rechner nutzen und was dein potenzieller Umsatz ist.",
  "hiw.card_blog_title": "Blog & SEO CMS",
  "hiw.card_blog_text": "Integriertes System zum Veröffentlichen von Artikeln, die deine Google-Sichtbarkeit steigern.",
  "hiw.card_global_title": "Globale Architektur",
  "hiw.card_global_text":
    "Vollständige Mehrsprachigkeit (NL/EN/etc.), wobei die Sprachpräferenz des Kunden erhalten bleibt.",
  "hiw.card_mobile_title": "Mobile Ready (PWA)",
  "hiw.card_mobile_text":
    "Inkvaluator ist eine Progressive Web App (PWA). Installiere das Tool direkt auf deinem Startbildschirm über den Browser. Blitzschnell, sicher und ohne Speicherplatz zu verbrauchen.",
  "hiw.cta_title": "Worauf wartest du noch?",
  "hiw.cta_accent": "Hör auf zu rechnen, fang an zu kreieren.",
  "hiw.cta_button": "Inkvaluator jetzt kostenlos testen",
  // Terms of Service
  "tos.seo_title": "Allgemeine Geschäftsbedingungen | Inkvaluator",
  "tos.seo_desc":
    "Lesen Sie die Allgemeinen Geschäftsbedingungen von Inkvaluator. Informationen zur Nutzung unseres Tattoo-Preisrechners, Abonnements und Haftung.",
  "tos.title": "Allgemeine Geschäftsbedingungen",
  "tos.updated": "Zuletzt aktualisiert:",
  "tos.s1_title": "1. Definitionen",
  "tos.s1_platform":
    "Platform: Inkvaluator, eine SaaS-Plattform verfügbar über inkvaluator.com und zugehörige Subdomains.",
  "tos.s1_user": "Benutzer / Künstler: Jeder Tätowierer oder jedes Studio, das ein Konto auf der Plattform erstellt.",
  "tos.s1_visitor":
    "Besucher: Jede Person, die den öffentlichen Rechner oder den Entdecken-Bereich der Plattform nutzt.",
  "tos.s1_service":
    "Dienst: Die von Inkvaluator angebotenen Funktionen, einschließlich Tattoo-Preisrechner, Lead-Management, AI-Scanner und White-Label-Tools.",
  "tos.s2_title": "2. Nutzung des Rechners",
  "tos.s2_p1":
    "Die von Inkvaluator generierten Preisschätzungen sind ausschließlich indikativ und stellen kein bindendes Angebot, keine Offerte oder Preisgarantie dar. Der tatsächliche Preis eines Tattoos wird immer vom Künstler auf Basis eines persönlichen Beratungsgesprächs bestimmt.",
  "tos.s2_p2":
    "Inkvaluator haftet nicht für Entscheidungen, die auf Basis der generierten Preisschätzungen getroffen werden, noch für etwaige Streitigkeiten zwischen Künstlern und deren Kunden bezüglich Preisen.",
  "tos.s3_title": "3. Abonnements & Zahlungen",
  "tos.s3_intro": "Inkvaluator bietet drei Stufen: Free, Pro und Studio.",
  "tos.s3_billing": "Bezahlte Abonnements werden monatlich über Stripe abgerechnet und automatisch verlängert.",
  "tos.s3_cancel":
    "Du kannst dein Abonnement jederzeit über das Stripe-Kundenportal in deinem Dashboard kündigen. Nach der Kündigung behältst du Zugang zu den bezahlten Funktionen bis zum Ende des aktuellen Abrechnungszeitraums.",
  "tos.s3_refund":
    "Erstattungen sind für bereits begonnene Abrechnungszeiträume nicht möglich, sofern nicht durch geltendes Recht anders vorgeschrieben.",
  "tos.s3_price_change":
    "Inkvaluator behält sich das Recht vor, Preise zu ändern. Preisänderungen werden mindestens 30 Tage im Voraus kommuniziert.",
  "tos.s4_title": "4. Dateneigentum",
  "tos.s4_p1":
    "Alle vom Künstler eingegebenen Daten (Preiseinstellungen, Profildaten, Logos) bleiben Eigentum des Künstlers. Inkvaluator erhält eine begrenzte Lizenz zur Nutzung dieser Daten für die Erbringung des Dienstes.",
  "tos.s4_p2":
    "Anonyme, aggregierte Nutzungsdaten (wie die Anzahl der Berechnungen pro Region) können von Inkvaluator für statistische Analysen und Produktverbesserung verwendet werden, ohne dass sie auf einzelne Benutzer zurückgeführt werden können.",
  "tos.s5_title": "5. Haftung",
  "tos.s5_p1":
    'Inkvaluator stellt den Dienst „wie besehen" bereit. Wir bemühen uns um maximale Verfügbarkeit und Genauigkeit, geben aber keine Garantie für den ununterbrochenen oder fehlerfreien Betrieb der Plattform.',
  "tos.s5_indirect": "Inkvaluator haftet nicht für indirekte Schäden, entgangenen Gewinn oder Folgeschäden.",
  "tos.s5_cap":
    "Die Gesamthaftung von Inkvaluator ist auf den Betrag begrenzt, den der Benutzer in den drei Monaten vor dem Anspruch an Abonnementgebühren gezahlt hat.",
  "tos.s5_content": "Inkvaluator ist nicht verantwortlich für Inhalte, die Künstler auf ihrem Profil platzieren.",
  "tos.s6_title": "6. Konto & Kündigung",
  "tos.s6_credentials": "Benutzer sind dafür verantwortlich, ihre Anmeldedaten vertraulich zu halten.",
  "tos.s6_suspend":
    "Inkvaluator behält sich das Recht vor, Konten bei Missbrauch, Betrug oder Verstoß gegen diese Bedingungen zu sperren oder zu löschen.",
  "tos.s6_delete": "Bei Kündigung eines Kontos werden personenbezogene Daten gemäß der",
  "tos.s6_privacy_link": "Datenschutzerklärung",
  "tos.s7_title": "7. Geistiges Eigentum",
  "tos.s7_p1":
    'Alle geistigen Eigentumsrechte an der Plattform, der Software, dem Design und dem Markennamen „Inkvaluator" liegen bei Inkvaluator. Es ist nicht gestattet, Teile der Plattform zu kopieren, zurückzuentwickeln oder für konkurrierende Dienste zu verwenden.',
  "tos.s8_title": "8. Anwendbares Recht",
  "tos.s8_p1":
    "Diese Allgemeinen Geschäftsbedingungen unterliegen dem niederländischen Recht. Streitigkeiten werden ausschließlich dem zuständigen Gericht in den Niederlanden vorgelegt.",
  "tos.contact_title": "Fragen zu diesen Bedingungen?",
  "tos.contact_text": "Kontaktiere uns unter:",
  // Support
  "support.seo_title": "Support | Inkvaluator",
  "support.seo_desc":
    "Brauchst du Hilfe mit Inkvaluator? Schau dir unsere häufig gestellten Fragen an oder kontaktiere unser Support-Team.",
  "support.title": "Support",
  "support.faq_title": "Häufig gestellte Fragen",
  "support.faq_q1": "Wie stelle ich meine Tarife ein?",
  "support.faq_a1":
    'Gehe zu deinem Dashboard und klicke auf „Preise". Hier kannst du deinen Stundensatz, Mindestpreis, Anzahlung und Designkosten einstellen. Du kannst auch erweiterte Einstellungen für Größe, Komplexität, Körperteil und Stilmultiplikatoren konfigurieren.',
  "support.faq_q2": "Wie funktioniert die Stripe-Integration für Abonnements?",
  "support.faq_a2":
    'Wenn du auf Pro oder Studio upgradest, wirst du zu einem sicheren Stripe-Checkout weitergeleitet. Nach der Zahlung wird dein Abonnement automatisch aktiviert. Du kannst dein Abonnement (kündigen, Rechnungen einsehen) über den Button „Abonnement verwalten" in deinem Dashboard verwalten.',
  "support.faq_q3": "Was ist das WhatsApp-Modul?",
  "support.faq_a3":
    "Mit dem WhatsApp-Modul (ab Pro verfügbar) können Besucher dich nach der Preisberechnung direkt über WhatsApp kontaktieren. Du konfigurierst deine Telefonnummer im Dashboard und Inkvaluator generiert automatisch eine Nachricht mit den Tattoo-Details.",
  "support.faq_q4": "Wie teile ich meinen Rechner mit Kunden?",
  "support.faq_a4":
    'In deinem Dashboard findest du unter „Teilen" einen direkten Link zu deinem persönlichen Rechner, einen QR-Code und einen Embed-Code, um den Rechner auf deiner eigenen Website zu platzieren.',
  "support.faq_q5": "Kann ich mein Abonnement kündigen?",
  "support.faq_a5":
    'Ja, du kannst dein Abonnement jederzeit über „Abonnement verwalten" in deinem Dashboard kündigen. Nach der Kündigung behältst du Zugang zu allen Pro-Funktionen bis zum Ende deines aktuellen Abrechnungszeitraums.',
  "support.faq_q6": "Wie funktioniert der AI Image Scanner?",
  "support.faq_a6":
    "Der AI Scanner (Pro-Funktion) analysiert ein Foto eines Tattoo-Designs und schätzt automatisch Größe, Komplexität und Stil ein. Dies hilft Besuchern, schneller eine Preisschätzung zu erhalten.",
  "support.faq_q7": "Ich habe einen Bug gefunden, was nun?",
  "support.faq_a7":
    "Sende eine E-Mail an support@inkvaluator.com mit einer Beschreibung des Problems, Screenshots wenn möglich und dem Gerät/Browser, den du verwendest. Wir antworten so schnell wie möglich.",
  "support.contact_title": "Kontakt",
  "support.contact_text": "Frage nicht dabei? Kontaktiere unser Team direkt:",
  "support.form_name": "Name",
  "support.form_name_placeholder": "Dein Name",
  "support.form_email": "E-Mail",
  "support.form_message": "Nachricht",
  "support.form_message_placeholder": "Beschreibe deine Frage oder dein Problem...",
  "support.form_submit": "Nachricht senden",
  "support.form_subject": "Supportanfrage von {name}",
  "support.toast_title": "E-Mail-Client geöffnet",
  "support.toast_desc": "Deine E-Mail-Anwendung wird mit deiner Nachricht geöffnet.",
  // Privacy
  "privacy.seo_title": "Datenschutzerklärung | Inkvaluator",
  "privacy.title": "Datenschutzerklärung",
  "privacy.updated": "Zuletzt aktualisiert:",
  "privacy.s1_title": "1. Welche Daten erheben wir?",
  "privacy.s1_intro": "Inkvaluator erhebt Daten, die für den Betrieb der Plattform erforderlich sind:",
  "privacy.s1_artists_label": "Für Künstler",
  "privacy.s1_artists": "Name, E-Mail, Studioname, Adresse und Portfolio.",
  "privacy.s1_visitors_label": "Für Besucher",
  "privacy.s1_visitors": "IP-Adresse (anonymisiert), Browser-Typ, Seitenaufrufe.",
  "privacy.s1_leads_label": "Für Leads",
  "privacy.s1_leads": "Name (optional), Nachricht, ausgewählte Tattoo-Details.",
  "privacy.s2_title": "2. Wie verwenden wir Ihre Daten?",
  "privacy.s2_intro": "Wir verwenden Ihre Daten ausschließlich für:",
  "privacy.s2_matching": "Zuordnung von Kunden zu Künstlern.",
  "privacy.s2_algorithms": "Verbesserung unserer Preisalgorithmen.",
  "privacy.s2_pwa": "Optimierung unserer PWA-Erfahrung.",
  "privacy.s3_title": "3. Ihre Rechte (DSGVO)",
  "privacy.s3_intro": "Gemäß der DSGVO haben Sie folgende Rechte:",
  "privacy.s3_access_label": "Auskunft",
  "privacy.s3_access": "Sie können eine Kopie Ihrer Daten anfordern.",
  "privacy.s3_erasure_label": "Löschung",
  "privacy.s3_erasure": "Sie können die Löschung Ihrer Daten verlangen.",
  "privacy.s3_portability_label": "Datenübertragbarkeit",
  "privacy.s3_portability": "Sie können Ihre Daten in einem standardisierten Format exportieren.",
  "privacy.s4_title": "4. Datenspeicherung",
  "privacy.s4_text":
    "Daten werden sicher in der EU gespeichert und verschlüsselt. Wir bewahren Ihre Daten nur so lange auf, wie es für die Erbringung unseres Dienstes erforderlich ist.",
  "privacy.contact_title": "Kontakt",
  "privacy.contact_text": "Bei Fragen zum Datenschutz:",
  "privacy.seo_desc": "Lesen Sie die Datenschutzerklärung von Inkvaluator.",
  // Dashboard i18n
  "dash.unsaved_changes": "Du hast ungespeicherte Änderungen",
  "dash.dashboard_language": "Dashboard Sprache",
  "dash.dashboard_language_desc": "Die Sprache deiner Verwaltungsoberfläche.",
  "auth.dashboard_subtitle": "Künstler-Dashboard",
  "discover.hide_map": "Karte ausblenden",
  "discover.show_map": "Karte anzeigen",
  "discover.map_loading": "Karte wird geladen...",
  "discover.call": "Anrufen",
  "discover.route": "Route",
  "discover.view_portfolio": "Werk ansehen",
  "discover.hide_portfolio": "Werk ausblenden",
  "admin.notifications": "Benachrichtigungen",
  "admin.no_notifications": "Keine Benachrichtigungen.",
  "admin.mark_read": "Gelesen",
  "admin.mark_all_read": "Alle als gelesen markieren",
  "admin.notif_new_artist": "Neuer Künstler angemeldet",
  "admin.notif_new_tip": "Neuer Künstler-Tipp",
  "admin.notif_new_style": "Neuer Stilvorschlag",
  "help.min_price": "Dies ist der absolute Mindestpreis für jede Sitzung, unabhängig von Größe oder Komplexität.",
  "help.hourly_rate":
    "Dein Standard-Stundensatz bildet die Grundlage aller Preisberechnungen. Zeit × Satz = Grundpreis.",
  "help.daily_rate": "Optionale Obergrenze: Überschreitet eine Sitzung diesen Betrag, wird der Tagessatz angewendet.",
  "help.design_cost": "Eine feste oder stündliche Gebühr für das Entwerfen und Vorbereiten des Tattoo-Designs.",
  "help.deposit": "Die Anzahlung sichert den Termin und wird vom Gesamtpreis abgezogen. Auf 0 setzen zum Deaktivieren.",
  "help.multipliers": "Multiplikatoren passen den Preis basierend auf Stil, Komplexität und Körperstelle an.",
  "help.lead_channels":
    "WhatsApp-Leads senden alle berechneten Details direkt auf dein Handy. E-Mail-Leads gehen an deine Beratungs-E-Mail.",
  "help.google_place_id":
    "Verknüpfe dein Google Business-Profil, um automatisch Bewertungen auf deinem Profil anzuzeigen.",
  "help.sizes": "Definiere den geschätzten Zeitraum (Min/Max Stunden) pro Größenkategorie.",
  "help.complexity": "Passe Preismultiplikatoren für die Designkomplexität an.",
  "help.body_parts": "Manche Körperstellen sind schwieriger zu tätowieren. Passe die Multiplikatoren entsprechend an.",
  "help.styles": "Aktiviere/deaktiviere Stile und setze Preismultiplikatoren.",
  "help.logo": "Dein Logo erscheint auf deiner Rechnerseite und im Künstlerverzeichnis. Max 2MB.",
  "help.currency": "Die Währung auf deinem Rechner. Wähle die Währung, in der deine Kunden bezahlen.",
  "help.calculator_language": "Die Sprache deiner öffentlichen Rechnerseite — was deine Kunden sehen.",
  "help.dashboard_language": "Die Sprache dieses Verwaltungs-Dashboards — nur für dich sichtbar.",
  "help.artist_name": "Dein professioneller Name, wie er auf deinem Rechner und im Verzeichnis angezeigt wird.",
  "help.studio_name": "Optionaler Studioname. Wird neben deinem Künstlernamen angezeigt.",
  "help.location": "Dein Studiostandort wird für die Künstlerkarte, Geo-Pricing und lokale SEO verwendet.",
  "tab.price_engine": "Preis-Engine",
  "tab.price_engine_desc": "Verwalte alle Preismultiplikatoren: Größen, Komplexität, Körperteile und Stile.",
  // New keys Day 17-20
  "vault.inspiration_title": "Inspiration",
  "vault.inspiration_desc": "Basierend auf deinen gespeicherten Schätzungen könnten dir auch diese Stile gefallen:",
  "vault.try_style": "Ausprobieren →",
  "vault.status_concept": "Konzept",
  "vault.status_sent": "Anfrage gesendet",
  "reviews.rating": "Bewertung",
  "reviews.verified_reviews": "verifizierte Bewertungen",
  "reviews.read_all": "Alle Bewertungen auf Google lesen",
  "discover.swipe": "wischen",
  "profile.manual_place_id": "Studio nicht gefunden? Gib deine Place ID manuell ein.",
  "analytics.revenue_funnel": "Umsatztrichter",
  "analytics.total_calc_value": "Berechneter Gesamtwert",
  "analytics.total_lead_value": "Lead-Wert",
  "analytics.whatsapp_leads": "WhatsApp-Leads",
  "analytics.lead_quality": "Lead-Qualität",
  "analytics.body_heatmap": "Körperteil-Heatmap",
  "analytics.body_heatmap_desc": "Wo deine Kunden tätowiert werden wollen — je höher, desto beliebter.",
  "analytics.team_leaderboard": "Team-Rangliste",
  "analytics.team_calculations": "Berechnungen",
  "analytics.team_lead_value": "Lead-Wert",
  "vault.compare": "Vergleichen",
  "vault.compare_title": "Vergleich",
  "vault.select_to_compare": "Wähle 2 Schätzungen zum Vergleichen",
  "trends.title": "Tattoo",
  "trends.title_accent": "Trends",
  "trends.subtitle": "Entdecke die beliebtesten Stile und Körperstellen weltweit.",
  "trends.top_styles": "Top-Stile",
  "trends.top_body_parts": "Beliebte Körperstellen",
  "trends.no_data": "Noch nicht genügend Daten.",
  "match.title": "Passende Künstler in deiner Nähe",
  "match.score": "Match",
  "match.view": "Profil ansehen",
  "urgency.recent_requests": "Anfragen diese Woche",
  "urgency.wait_time": "Geschätzte Wartezeit",
  "urgency.weeks": "Wochen",
  "urgency.high_demand": "Hohe Nachfrage",
  "urgency.popular": "Beliebt",
  "benchmark.above_market": "über dem Marktdurchschnitt",
  "benchmark.below_market": "unter dem Marktdurchschnitt",
  "benchmark.at_market": "Im Marktdurchschnitt",
  "benchmark.market_avg": "Marktdurchschnitt",
  "portfolio.style_examples": "Stilbeispiele",
  "story.title": "Instagram Story Generator",
  "story.desc": "Erstelle ein Premium Black & Gold Story-Bild mit deinem QR-Code.",
  "story.generate": "Story erstellen",
  "story.regenerate": "Neu erstellen",
  "story.download": "Herunterladen",
  "story.share": "Teilen",
  "story.downloaded": "Story heruntergeladen!",
  "story.cta_line1": "Berechne Deinen Preis",
  "story.cta_line2": "Scanne den QR-Code unten",
  "story.scan_prompt": "↑ Scannen für deine Preisschätzung",
  "trends.filter_country": "Nach Land filtern",
  "trends.all_countries": "Alle Länder",
  "trends.analyzed": "Berechnungen analysiert",
  "trends.realtime": "Echtzeit aktualisiert",
  "match.style_experts": "Experten für {style} in {city}",
  "vault.review_booster_title": "Tattoo fertig? Hilf deinem Künstler!",
  "vault.review_booster_desc": "Hinterlasse eine Google-Bewertung und hilf anderen, diesen Künstler zu finden.",
  "vault.review_booster_btn": "Google-Bewertung schreiben",
  "checklist.title": "Verifizierungsstatus",
  "checklist.profile_complete": "Profil vollständig",
  "checklist.google_linked": "Google Place ID verknüpft",
  "checklist.badge_detected": "Verified Badge auf Website",
  "checklist.push_enabled": "Push-Benachrichtigungen aktiviert",
  "checklist.not_yet": "Noch nicht",
  "checklist.active": "Aktiv",
  "nav.home": "Startseite",
  "lead.estimate_header": "Tattoo Schätzung",
  "discover.verified_badge": "VERIFIZIERT",
  "lead.status_new": "Neu",
  "lead.status_contacted": "In Kontakt",
  "lead.status_booked": "Gebucht",
  "lead.change_status": "Status",
  "lead.add_price": "Endpreis eingeben",
  "push.test_body": "Push-Benachrichtigungen sind LIVE! Du erhältst jetzt Benachrichtigungen bei neuen Leads.",
  "push.send_test": "Test-Benachrichtigung senden",
  "push.active_desc": "Du erhältst Benachrichtigungen bei hochwertigen Leads.",
  "push.blocked": "Benachrichtigungen sind in deinen Browsereinstellungen blockiert.",
  "push.enable_desc": "Erhalte Benachrichtigungen, wenn hochwertige Leads eingehen.",
  "push.label": "Push-Benachrichtigungen",
  "push.welcome_body": "Willkommen! Du erhältst jetzt Benachrichtigungen bei hochwertigen Leads.",
  "push.denied_info":
    "Bleib auf dem Laufenden über neue Leads — Aktiviere Benachrichtigungen in deinen Browsereinstellungen.",
  "vault.live_status": "Live Status",
  "vault.status_new_desc": "Deine Anfrage wurde empfangen",
  "vault.status_contacted_desc": "Der Künstler hat deine Nachricht gesehen",
  "vault.status_booked_desc": "Dein Termin ist bestätigt!",
  "vault.link_copied": "🔗 Link kopiert!",
  "analytics.actual_revenue": "Tatsächlicher Umsatz",
  "style_city.popular_styles": "Beliebte Stile in {city}",
  "step.next": "Weiter",
  "step.view_result": "Preis anzeigen",
  "step.details": "Details",
  "step.details_desc": "Wie detailliert ist dein Design?",
  "layout.stepper": "Schritt für Schritt",
  "layout.form": "Alles auf einmal",
  "calc.view_price_cta": "Preisübersicht ansehen",
  "calc.rough_estimate": "Grobe Schätzung",
  "calc.calculated_price": "Berechneter Preis",
  "calc.save_for_later": "Für später speichern",
  "calc.share": "Teilen",
  "calc.more_options": "Mehr Optionen",
  "calc.compare_configs": "Konfigurationen vergleichen",
  "calc.compare_desc": "Zwei Preiskonfigurationen nebeneinander vergleichen",
  "calc.no_photo": "Kein Referenzfoto hinzugefügt",
  "calc.link_copied": "Link kopiert!",
  "whatsapp.add_photo": "Füge dein Foto hinzu",
  "whatsapp.photos_label": "Referenzfotos — max. 3",
  "laf.reset_success": "Thema auf Standard zurückgesetzt",
  "admin.stripe_managed":
    "Abonnements werden automatisch über Stripe verwaltet — Änderungen sind nach dem nächsten Webhook-Sync sichtbar",
  "calc.view_tattoo_plan": "Dein Tattoo Plan ansehen ✓",
  "wa.plan_message": "Hier ist mein Tattoo Plan:",
  "lead.tattoo_plan": "Tattoo Plan",
  "lead.made_with": "Erstellt mit Inkvaluator",
  "lead.contact_artist": "Kontaktiere {artist}",
  "lead.share": "Teilen",
  "lead.share_title": "Mein Tattoo Plan",
  "lead.link_copied": "Link kopiert!",
  "lead.deposit_label": "Anzahlung",
  "lead.pay_deposit": "Anzahlung {amount} bezahlen",
  "lead.deposit_request_title": "Anzahlungsvereinbarung anfragen",
  "lead.deposit_request_desc": "Fragen Sie den Künstler, ob eine Anzahlung von ~{amount} möglich ist.",
  "lead.deposit_request_wa": "Anfrage per WhatsApp senden",
  "lead.for_you": "Dich",
  "lead.for_label": "Für",
  "lead.back_to_calculator": "Zurück zum Rechner",
  "lead.session_duration": "Sitzungsdauer",
  "lead.session_estimate": "~{hours} Std.",
  "lead.deposit_wa_msg":
    "Hallo {artist}, ich habe meinen Tattoo Plan auf Inkvaluator angesehen. Wäre eine Anzahlung von ~{amount} möglich?\nPlan: {url}",
  // Blog
  "blog.title": "Blog",
  "blog.no_posts": "Noch keine Artikel veröffentlicht.",
  "blog.seo_title": "Blog | Inkvaluator",
  "blog.seo_desc": "Tipps, Trends und Einblicke zu Tattoo-Preisen und der Tattoo-Branche.",
  "blog.read_more": "Mehr lesen",
  "blog.published_on": "Veröffentlicht am",
  "blog.back": "Zurück zum Blog",
  "blog.not_found": "Artikel nicht gefunden",
  "blog.translation_notice":
    "Dieser Artikel ist noch nicht in deiner Sprache verfügbar. Du siehst die Originalversion.",
  "blog.no_translation": "Keine Übersetzung verfügbar",
  "flash.tab": "Flash",
  "flash.upload": "Flash-Design hochladen",
  "flash.title": "Titel",
  "flash.price": "Preis",
  "flash.size": "Größe (cm)",
  "flash.available": "Verfügbar",
  "flash.featured": "Empfohlen",
  "flash.limit_used": "von {max} Flash-Designs verwendet",
  "flash.delete_confirm": "Flash-Design löschen?",
  "flash.reserve_whatsapp": "Via WhatsApp reservieren",
  "flash.whatsapp_msg": "Ich interessiere mich für dein Flash-Tattoo:",
  "flash.matching_designs": "Passende Flash-Designs",
  "flash.only_with_flash": "Nur Künstler mit Flash-Designs",
  "groei.score": "Profilbewertung",
  "groei.views": "Profilaufrufe",
  "groei.sessions": "Rechner-Sitzungen",
  "groei.leads": "Leads",
  "groei.this_week": "diese Woche",
  "groei.improve": "Verbessere dein Profil",
  "groei.add_photos": "Füge mehr Portfolio-Fotos hinzu",
  "groei.add_whatsapp": "Richte deine WhatsApp-Nummer ein",
  "groei.add_bio": "Schreibe eine längere Bio",
  "groei.add_avatar": "Lade ein Profilbild hoch",
  "groei.add_rate": "Lege deinen Stundensatz fest",
  "groei.add_city": "Füge deine Stadt hinzu",
  "groei.get_leads": "Teile deinen Rechner um Leads zu erhalten",
  "groei.collapse": "Einklappen",
  "groei.expand": "Ausklappen",
  "groei.tip_portfolio": "Künstler mit 6+ Fotos erhalten 3x mehr Rechner-Sitzungen",
  "groei.tip_whatsapp": "Kunden mit WhatsApp-Button konvertieren 5x besser",
  "groei.tip_leads": "Teile deinen Rechner-Link um deinen ersten Lead zu erhalten",
  "groei.tip_avatar": "Ein Profilbild erhöht deine Glaubwürdigkeit",
  "groei.tip_action_photos": "Fotos hinzufügen",
  "groei.tip_action_whatsapp": "WhatsApp einrichten",
  "groei.tip_action_share": "Rechner teilen",
  "groei.tip_action_avatar": "Foto hochladen",
  "milestone.eerste_lead": "Erster Lead erhalten!",
  "milestone.tien_leads": "10 Leads erreicht!",
  "milestone.profiel_compleet": "Profil 100% komplett!",
  "milestone.eerste_flash": "Erstes Flash-Design hochgeladen!",
  "wa.name_required": "Name ist erforderlich",
  "wa.plan_message_full": "Sieh dir meinen vollständigen Tattoo Plan an: {url}",

  // Landing v2
  "landing.seo_title": "Tattoo-Preise berechnen — Finde Künstler in deiner Stadt | Inkvaluator",
  "landing.seo_description":
    "Berechne sofort, was dein Tattoo kostet. Finde Tattoo-Künstler in deiner Nähe. Kostenloser Preisrechner für Studios in Europa.",
  "landing.hero_eyebrow": "",
  "landing.hero_title_line1": "Was kostet",
  "landing.hero_title_line2": "dein",
  "landing.hero_title_line3": "Tattoo?",
  "landing.hero_subtitle":
    "Berechne eine faire Preisschätzung, finde einen Künstler in deiner Nähe und nimm direkt Kontakt auf via WhatsApp.",
  "landing.hero_search_placeholder": "Suche nach Stadt oder Studio...",
  "landing.hero_popular": "Beliebt",
  "landing.hero_cta_primary": "Berechne deinen Preis",
  "landing.hero_cta_secondary": "Künstler ansehen",
  "landing.artist_card_eyebrow": "Für Tattoo-Künstler",
  "landing.artist_card_title": "Bringe dein Studio auf die Karte",
  "landing.artist_card_body":
    "Dein Studio ist wahrscheinlich bereits auf Inkvaluator gelistet. Beanspruche dein Profil in zwei Minuten und erhalte direkte Anfragen von Kunden.",
  "landing.artist_card_stat_studios": "Studios",
  "landing.artist_card_stat_cities": "Städte",
  "landing.artist_card_stat_spots": "Founder Plätze",
  "landing.artist_card_cta": "Profil kostenlos beanspruchen",
  "landing.artist_card_founder_note": "Die ersten 100 Studios erhalten Pro kostenlos",
  "landing.proof_studios": "Tattoo-Studios",
  "landing.proof_cities": "Städte in Europa",
  "landing.proof_leads": "Kunden geholfen",
  "landing.proof_rating": "Durchschnittliche Bewertung",
  "landing.plan_eyebrow": "Dein Tattoo Plan",
  "landing.plan_title": "Keine Preisfrage. Ein Plan.",
  "landing.plan_body":
    "Inkvaluator erstellt einen persönlichen Tattoo Plan mit deinem Stil, Größe, Platzierung und Preisschätzung. Sende ihn direkt an einen Künstler — der weiß sofort, was du willst.",
  "landing.plan_step1": "Fülle den Rechner aus — Stil, Größe, Detailgrad",
  "landing.plan_step2": "Dein Tattoo Plan wird automatisch erstellt",
  "landing.plan_step3": "Sende den Plan per WhatsApp an einen Künstler",
  "landing.plan_cta": "Erstelle deinen Tattoo Plan",
  "landing.plan_card_title": "Tattoo Plan",
  "landing.plan_card_style": "Stil",
  "landing.plan_card_size": "Größe",
  "landing.plan_card_detail": "Detailgrad",
  "landing.plan_card_detail_val": "Hoch",
  "landing.plan_card_placement": "Platzierung",
  "landing.plan_card_placement_val": "Oberarm",
  "landing.plan_card_estimate": "Geschätzter Preis",
  "landing.plan_card_send": "An Künstler per WhatsApp senden",
  "landing.how_eyebrow": "So funktioniert es",
  "landing.how_title": "Von der Idee zum Termin",
  "landing.how_subtitle": "Drei Schritte. Kein Konto. Sofortige Ergebnisse.",
  "landing.how_step1_title": "Preis berechnen",
  "landing.how_step1_desc":
    "Wähle Stil, Größe und Detailgrad. Du erhältst sofort eine faire Preisschätzung basierend auf echten Daten aus deiner Region.",
  "landing.how_step1_cta": "Rechner starten",
  "landing.how_step2_title": "Künstler finden",
  "landing.how_step2_desc": "Durchsuche Künstler in deiner Stadt. Filtere nach Stil, Preis oder Bewertung.",
  "landing.how_step2_cta": "Künstler ansehen",
  "landing.how_step3_title": "Direkt Kontakt aufnehmen",
  "landing.how_step3_desc":
    "Sende deinen Tattoo Plan per WhatsApp. Der Künstler weiß genau, was du willst und was es kosten darf.",
  "landing.map_eyebrow": "Abdeckung",
  "landing.map_title": "Künstler in deiner Nähe",
  "landing.map_subtitle": "Studios in Städten. Goldener Pin = beanspruchtes Profil mit aktivem Rechner.",
  "landing.map_loading": "Karte wird geladen...",
  "landing.map_legend_claimed": "Beansprucht — Rechner aktiv",
  "landing.map_legend_unclaimed": "Nicht beansprucht",
  "landing.styles_eyebrow": "Stile",
  "landing.styles_title": "Finde deinen Stil",
  "landing.styles_traditional": "Kräftige Linien, lebendige Farben",
  "landing.styles_realism": "Fotorealistisches Detail",
  "landing.styles_japanese": "Klassische japanische Motive",
  "landing.styles_fineline": "Zarte, minimalistische Linien",
  "landing.styles_blackwork": "Pures Schwarz, maximale Wirkung",
  "landing.styles_watercolor": "Fließend und farbenfroh",
  "landing.artist_eyebrow": "Bist du Tattoo-Künstler?",
  "landing.artist_title": "Bringe dein Studio auf die Karte",
  "landing.artist_body":
    "Dein Studio ist wahrscheinlich bereits auf Inkvaluator gelistet. Beanspruche dein Profil in zwei Minuten per Telefonverifizierung und erhalte Leads von Kunden mit fertigem Tattoo Plan.",
  "landing.artist_perk1": "Kunden senden ihren Tattoo Plan — du weißt sofort, was sie wollen",
  "landing.artist_perk2": "Erhalte eine Anzahlung bei jeder Anfrage über die Plattform",
  "landing.artist_perk3": "Wachse organisch über Stadtkarte und Suchmaschinen",
  "landing.artist_perk4": "Die ersten 100 Studios erhalten Pro kostenlos — keine versteckten Kosten",
  "landing.artist_cta_claim": "Profil beanspruchen",
  "landing.artist_cta_register": "Konto erstellen",
  "landing.founder_subtitle": "Exklusiv für die ersten 100 Studios",
  "landing.founder_perk1": "Pro-Paket kostenlos, solange wir wachsen",
  "landing.founder_perk2": "Direkter Einfluss auf neue Funktionen",
  "landing.founder_perk3": "Priorität in Suchergebnissen",
  "landing.founder_perk4": "Persönlicher Kontakt mit dem Entwickler",
  "landing.founder_claimed": "beansprucht",
  "landing.cities_eyebrow": "Städte",
  "landing.cities_title": "Tattoo-Studios nach Stadt",
  "landing.cities_all": "Alle Städte",
  "landing.cities_studios": "Studios",
  "landing.cities_from": "ab",
  "landing.cities_claimed": "beansprucht",
  "landing.blog_eyebrow": "Wissensdatenbank",
  "landing.blog_title": "Alles über Tattoo-Preise",
  "landing.blog_all": "Alle Artikel",
  "landing.blog_read": "Weiterlesen",
  "landing.final_eyebrow": "Bereit?",
  "landing.final_title_line1": "Berechne, was dein",
  "landing.final_title_line2": "Tattoo kostet",
  "landing.final_subtitle": "Kein Konto nötig. Kostenlos. Sofortige Ergebnisse.",
  "landing.final_cta_primary": "Rechner starten",
  "landing.final_cta_secondary": "Studio beanspruchen",
  // Deposit settings
  "settings.deposit_title": "Anzahlung",
  "settings.deposit_desc": "Bitte Kunden, bei einer Anfrage eine Anzahlung zu leisten.",
  "settings.deposit_none": "Keine",
  "settings.deposit_percentage": "Prozentsatz",
  "settings.deposit_fixed": "Festbetrag",
  "settings.deposit_percentage_hint": "des Gesamtbetrags",
  "settings.deposit_fixed_hint": "feste Anzahlung",
  "settings.deposit_preview_label": "Kunde sieht",
  "settings.deposit_preview_pct": "Anzahlung: {value}% des Gesamtbetrags",
  // ─── SEO Tattoo pages ───────────────────────────────
  "nav.studios": "Studios",
  "nav.studios_count": "Studios",
  "city.stats_studios": "Studios in {city}",
  "city.stats_on_inkvaluator": "Auf Inkvaluator",
  "city.size_small": "Klein (5–10 cm)",
  "city.size_medium": "Mittel (10–20 cm)",
  "city.size_large": "Groß (20 cm+)",
  "city.size_small_ex": "Text, minimalistisch, kleines Symbol",
  "city.size_medium_ex": "Portrait, Blumen, geometrisch",
  "city.size_large_ex": "Sleeve, Rücken, ganzes Bein",
  "city.time_1_2": "1–2 Std.",
  "city.time_2_5": "2–5 Std.",
  "city.time_5plus": "5+ Std.",
  "city.most_popular": "Am beliebtesten",
  "city.price_indication_note": "Schätzung basierend auf nationalen Durchschnittswerten",
  "city.price_factors_title": "Faktoren die den Preis bestimmen",
  "city.factor_style": "Stil",
  "city.factor_style_desc": "Realismus und Vollfarbe kosten mehr als Blackwork oder Fine-Line",
  "city.factor_placement": "Körperstelle",
  "city.factor_placement_desc": "Rippen und Hände sind technisch anspruchsvoller als Arm oder Rücken",
  "city.factor_detail": "Detailgrad",
  "city.factor_detail_desc": "Mehr Detail = mehr Zeit = höherer Preis",
  "city.factor_experience": "Künstlererfahrung",
  "city.factor_experience_desc": "Erfahrene Künstler verlangen mehr, bieten aber konsistentere Qualität",
  "city.updated": "Aktualisiert",
  "city.view_all_artists": "Alle Künstler anzeigen",
  "city.calc_button_short": "Preis berechnen",
  "city.view_button": "Ansehen →",
  "country.h1": "Tattoo-Studios in {country}",
  "country.coming_soon": "Demnächst verfügbar",
  "country.popular_styles": "Beliebte Stile in {country}",
  "style.h1": "{style} Tattoo in {city}",
  "style.subtitle":
    "{count} Künstler in {city} spezialisiert auf {style}. Berechne direkt den Preis deines {style} Tattoos.",
  "style.artists_h2": "{style} Künstler in {city}",
  "style.calc_button": "{style} Preis berechnen →",
  "style.empty": "Noch keine {style} Künstler in {city} beansprucht.",
  "style.all_studios": "Alle Tattoo-Studios in {city}",
  "style.page_title": "{style} Tattoo in {city} — Künstler & Preise | Inkvaluator",
  "style.page_desc": "{count} {style} Künstler in {city}. Portfolios vergleichen und Tattoo-Preis berechnen.",
  "studio.reviews": "Bewertungen",
  "studio.claim_owner": "Bist du der Inhaber von {name}?",
  "studio.claim_desc": "Beanspruche dieses Profil kostenlos. Du erhältst direkte Anfragen von Kunden in {city}.",
  "studio.claim_cta": "Profil kostenlos beanspruchen →",
  "studio.claim_cta_short": "Dein Studio beanspruchen →",
  "studio.view_with_calc": "Studios mit Preisrechner ansehen",
  "studio.artists_nearby": "Künstler in {city} mit Online-Preisrechner",
  "studio.deposit": "Anzahlung",
  "studio.meta_desc": "{name} in {city}. Bewertungen und Preise ansehen, Profil beanspruchen.",
  "tattoo_index.search_placeholder": "Stadt oder Tätowierer suchen...",
  "tattoo_index.h1": "Tattoo-Studio in deiner Nähe finden",
  "map.legend_pro": "Pro & Studio",
  "map.legend_claimed": "Beansprucht",
  "map.legend_unclaimed": "Nicht beansprucht",
  "map.legend_on_inkvaluator": "Auf Inkvaluator",
  "map.view_studio": "Studio ansehen →",
  "map.list_pro_section": "✦ Pro & Studio",
  "map.list_claimed_section": "Beansprucht",
  "map.list_unclaimed_section": "Nicht beansprucht",
  "map.list_count_singular": "Studio sichtbar",
  "map.list_count_plural": "Studios sichtbar",
  "map.list_scroll_hint": "Karte verschieben zum Erkunden",
  "map.list_empty": "Keine Studios in diesem Bereich",
  "map.list_empty_hint": "Herauszoomen oder Karte verschieben",
  "map.list_full_profile": "Vollständiges Profil",
  "city.map_section_title": "Tattoo-Studios in {city}",
  "city.faq_section_title": "Häufig gestellte Fragen zu Tattoos in {city}",
  "settings.deposit_preview_fixed": "Anzahlung: €{value} bei Buchung",
};

// ─── FRENCH (complete) ─────────────────────────────────────────
const fr: Record<string, string> = {
  ...en,
  "app.tagline": "Calculez le prix de votre tatouage",
  "currency.aria_label": "Changer de devise",
  "currency.title": "Devise d'affichage",
  "currency.rates_of": "Taux du {date}",
  "currency.approx_tooltip": "Prix d'origine {original} — taux de change du {date}",
  back: "Retour",
  save: "Enregistrer",
  copy: "Copier",
  copied: "Copié!",
  loading: "Chargement...",
  error: "Erreur",
  optional: "optionnel",
  "landing.hero": "Calculez le prix de",
  "landing.hero_accent": "votre Tatouage",
  "landing.subtitle": "Entrez le code de votre tatoueur pour obtenir une estimation personnalisée.",
  "landing.start": "Démarrer le calculateur",
  "landing.invalid_code": "Entrez un code valide",
  "landing.or": "ou",
  "landing.artist_cta": "Vous êtes tatoueur? Créez votre propre calculateur.",
  "landing.hero_title": "Calculez le prix de",
  "landing.hero_title_accent": "votre Tatouage",
  "landing.hero_desc": "Découvrez en quelques secondes le prix de votre prochain tatouage.",
  "landing.try_it": "Essayez vous-même!",
  "landing.estimated_price": "Prix estimé",
  "landing.your_rates": "Vos tarifs",
  "landing.rates_desc": "Déplacez les curseurs et voyez le prix changer",
  "landing.hourly_rate": "Tarif horaire",
  "landing.start_cost": "Frais de démarrage fixes",
  "landing.size_label": "Taille",
  "landing.complexity_label": "Complexité",
  "landing.body_placement": "Placement corporel",
  "landing.find_artist": "Trouvez votre",
  "landing.find_artist_accent": "artiste parfait",
  "landing.nearby_soon": "Bientôt des artistes près de chez vous apparaîtront ici.",
  "landing.discover_all": "Découvrir tous les artistes",
  "landing.artist_question": "Êtes-vous un artiste ou un studio?",
  "landing.view_options": "Voir les options et tarifs",
  "landing.start_free": "Commencer gratuitement",
  "landing.discover": "Découvrir",
  "landing.login": "Connexion",
  "landing.founder_desc": "50% de réduction pour les 50 premières inscriptions",
  "landing.claim_now": "Réclamez maintenant →",
  "calc.estimate": "Prix estimé",
  "calc.size": "Taille",
  "calc.size_desc": "Quelle sera la taille de votre tatouage?",
  "calc.complexity": "Complexité",
  "calc.complexity_desc": "Quel est le niveau de détail du design?",
  "calc.body_part": "Partie du corps",
  "calc.body_part_desc": "Cliquez sur la partie du corps pour votre tatouage",
  "calc.style": "Style",
  "calc.style_desc": "Quel style de tatouage souhaitez-vous?",
  "calc.budget": "Budget",
  "calc.budget_desc": "Quel est votre budget maximum?",
  "calc.budget_placeholder": "Ex. 300",
  "calc.get_estimate": "Obtenez une estimation réaliste de",
  "calc.no_artist": "Aucun artiste trouvé avec ce code ou lien.",
  "calc.not_configured": "Le calculateur de prix n'a pas encore été configuré par l'artiste.",
  "calc.back_home": "Retour à l'accueil",
  "calc.artist_login": "Artiste? Connectez-vous",
  "calc.add_color": "Ajouter de la couleur?",
  "calc.color_surcharge": "+20% supplément pour le travail couleur",
  "breakdown.title": "Détail du prix",
  "breakdown.labor": "Main d'œuvre (temps)",
  "breakdown.est_hours": "Heures estimées",
  "breakdown.subtotal": "Sous-total",
  "breakdown.complexity": "Complexité",
  "breakdown.placement": "Emplacement",
  "breakdown.style": "Style",
  "breakdown.design_cost": "Coût du design",
  "breakdown.deposit": "Acompte",
  "breakdown.total_range": "Fourchette totale",
  "breakdown.disclaimer":
    "Ceci est une estimation, pas un prix final. Le prix définitif sera déterminé après consultation.",
  "breakdown.total_indication": "Indication totale",
  "breakdown.required_deposit": "Acompte requis",
  "breakdown.hours_unit": "heures",
  "faq.title": "Questions fréquentes",
  "faq.deposit_q": "Pourquoi un acompte?",
  "faq.deposit_a": "L'acompte réserve votre rendez-vous et couvre le temps de design.",
  "dash.welcome": "Bienvenue sur Inkvaluator",
  "dash.logout": "Déconnexion",
  "dash.saved": "Enregistré!",
  "dash.save_changes": "Enregistrer les modifications",
  "dash.cancel": "Annuler",
  "dash.add": "Ajouter",
  "dash.delete": "Supprimer",
  "dash.previous": "Précédent",
  "dash.next": "Suivant",
  "tab.basic": "Base",
  "tab.sizes": "Tailles",
  "tab.complexity": "Complexité",
  "tab.body_parts": "Parties du corps",
  "tab.styles": "Styles",
  "basic.title": "Tarif de base",
  "basic.min_price": "Prix minimum",
  "basic.hourly_rate": "Tarif horaire",
  "basic.daily_rate": "Tarif journalier",
  "basic.design_cost": "Coût du design",
  "basic.fixed": "Fixe",
  "basic.hourly": "Par heure",
  "basic.deposit": "Acompte",
  "size.mini": "Mini (< 5 cm)",
  "size.small": "Petit (5-10 cm)",
  "size.medium": "Moyen (10-20 cm)",
  "size.large": "Grand (20-35 cm)",
  "size.xl": "Très Grand (35+ cm)",
  "complexity.simple": "Simple",
  "complexity.medium": "Moyen",
  "complexity.detailed": "Détaillé",
  "body.arm": "Bras (général)",
  "body.forearm": "Avant-bras",
  "body.upper_arm": "Bras supérieur",
  "body.leg": "Jambe (général)",
  "body.thigh": "Cuisse",
  "body.calf": "Mollet",
  "body.back": "Dos",
  "body.chest": "Poitrine",
  "body.ribs": "Côtes",
  "body.stomach": "Ventre",
  "body.hand": "Main",
  "body.foot": "Pied",
  "body.neck": "Cou",
  "body.face": "Visage",
  "profile.tab_portfolio": "Portfolio",
  "profile.tab_studio": "Studio",
  "profile.tab_book": "Buchen",
  "profile.tab_calculator": "Preisschätzung",
  "profile.no_portfolio": "Noch keine Portfolio-Bilder hinzugefügt.",
  "profile.no_portfolio_hint": "Der Künstler kann dies über das Dashboard einrichten.",
  "profile.studio_status": "Studio Status",
  "profile.open_now": "Jetzt geöffnet",
  "profile.closed": "Geschlossen",
  "profile.studio_rules": "Studioregeln",
  "rules.deposit_title": "Anzahlung",
  "rules.deposit_body": "Eine Anzahlung von 150 € ist erforderlich, um Ihren Termin zu bestätigen. Dieser Betrag wird am Tag der Sitzung vom Gesamtpreis abgezogen.",
  "rules.cancellation_title": "Stornierung",
  "rules.cancellation_body": "Eine kostenlose Stornierung ist bis 48 Stunden im Voraus möglich. Bei Stornierungen innerhalb von 48 Stunden oder Nichterscheinen verfällt die Anzahlung.",
  "rules.guests_title": "Gäste",
  "rules.guests_body": "Maximal 1 Begleitperson pro Kunde ist willkommen. Gäste bleiben während der Sitzung in der Lounge.",
  "rules.age_title": "Alter & Ausweis",
  "rules.age_body": "Wir tätowieren ausschließlich Personen ab 18 Jahren. Ein gültiger Ausweis ist erforderlich.",
  "rules.health_title": "Gesundheit",
  "rules.health_body": "Kommen Sie ausgeruht und satt zu Ihrem Termin. Melden Sie bei der Buchung alle relevanten Erkrankungen, Medikamente oder Allergien.",
  "rules.coverup_title": "Cover-ups",
  "rules.coverup_body": "Cover-ups sind nach vorheriger Absprache möglich. Senden Sie immer Fotos des bestehenden Tattoos vor dem Termin.",
  "rules.design_title": "Design & Änderungen",
  "rules.design_body": "Das endgültige Design wird am Tag der Sitzung präsentiert. Kleine Anpassungen sind möglich, größere Änderungen können zu einem neuen Termin führen.",
  "rules.aftercare_title": "Nachsorge",
  "rules.aftercare_body": "Befolgen Sie die Nachsorgeanweisungen, die Sie nach der Sitzung erhalten, sorgfältig. Wir haften nicht für Folgen unsachgemäßer Pflege.",
  "profile.facilities": "Ausstattung",
  "profile.price_indication": "Preisindikation",
  "profile.hourly_rate_label": "/Std. Stundensatz",
  "profile.directions": "Wegbeschreibung",
  "profile.day_mon": "Montag",
  "profile.day_tue": "Dienstag",
  "profile.day_wed": "Mittwoch",
  "profile.day_thu": "Donnerstag",
  "profile.day_fri": "Freitag",
  "profile.day_sat": "Samstag",
  "profile.day_sun": "Sonntag",
  "profile.facility_parking": "Parkplatz",
  "profile.facility_wheelchair": "Barrierefrei",
  "profile.facility_private_room": "Privatzimmer",
  "profile.facility_wifi": "WLAN",
  "profile.facility_card_payment": "Kartenzahlung",
  "profile.facility_drinks": "Getränke",
  "profile.facility_sterile_cert": "Sterilitätszert.",
  "profile.facility_child_friendly": "Kinderfreundlich",
  "profile.facility_cover_up": "Cover-up",
  "profile.facility_custom_design": "Custom Design",
  "profile.facility_same_day": "Am selben Tag",
  "profile.size_small": "Klein · < 10 cm",
  "profile.size_medium": "Mittel · 10–20 cm",
  "profile.size_large": "Groß · 20+ cm",
  "profile.book_appointment": "Termin buchen",
  "profile.book_desc": "Wähle ein verfügbares Datum und Zeitfenster. Du erhältst eine Bestätigung per E-Mail.",
  "profile.earliest_slot": "Frühester verfügbarer Slot:",
  "profile.choose_slot": "Datum und Zeitfenster wählen",
  "profile.calculator": "Rechner",
  "profile.estimate": "Schätzung",
  "profile.back": "Zurück",
  "profile.next": "Weiter",
  "profile.view_price": "Preis ansehen",
  "profile.all_reviews_google": "Alle Bewertungen auf Google ansehen",
  "profile.write_review": "Bewertung schreiben",
  "profile.reviews": "Bewertungen",
  "profile.not_found_title": "Künstler nicht gefunden",
  "profile.not_found_desc": "Dieses Profil existiert nicht (mehr). Finde einen Künstler in deiner Nähe.",
  "profile.find_studio": "Tattoostudio finden →",
  "profile.studio_artists": "Künstler dieses Studios",
  "profile.artists_count": "Künstler",
  "profile.calc_price": "Preis berechnen →",
  "profile.recent_works": "Aktuelle Werke",
  "profile.works_count": "Werke",
  "profile.all_filter": "Alle",
  "profile.navigate_to": "Navigieren zu",
  "profile.price_calculator": "Preisrechner",
  "profile.or_whatsapp": "Oder sende eine WhatsApp-Nachricht",
  "profile.schedule_appointment": "Termin vereinbaren",
  "profile.per_hour": "/Std.",
  "profile.artist_label": "Künstler",
  "profile.tattoo_artist": "Tattoo-Künstler",
  "profile.book_label": "Buchen",
  "profile.open_maps": "Karte öffnen",
  "profile.live_traffic": "Live-Verkehr",
  "profile.find_on_google": "Auf Google suchen",
  "booking.title": "Termin buchen bei {artist}",
  "booking.confirmed_title": "Buchung angefragt!",
  "booking.choose_date": "Wähle ein verfügbares Datum",
  "booking.choose_time": "Wähle ein Zeitfenster",
  "booking.other_day": "Anderen Tag wählen",
  "booking.other_slot": "Anderes Zeitfenster",
  "booking.name": "Name",
  "booking.name_placeholder": "Dein Name",
  "booking.email": "E-Mail-Adresse",
  "booking.email_placeholder": "deine@email.de",
  "booking.phone": "Telefon / WhatsApp",
  "booking.style": "Tattoo-Stil",
  "booking.style_placeholder": "Stil wählen...",
  "booking.no_preference": "— Keine Präferenz —",
  "booking.description": "Tattoo-Beschreibung",
  "booking.description_placeholder": "Erzähle uns von deinem gewünschten Tattoo...",
  "booking.reference_photo": "Referenzfoto (optional)",
  "booking.upload_photo": "Foto hochladen",
  "booking.deposit_notice": "Bei der Buchung ist eine Anzahlung von €{amount} erforderlich. Der Künstler wird dich bezüglich der Zahlung kontaktieren.",
  "booking.fill_required": "Bitte Name und E-Mail ausfüllen",
  "booking.submit": "Termin anfragen",
  "booking.success_title": "Anfrage gesendet!",
  "booking.success_desc": "{artist} erhält deine Anfrage und bestätigt den Termin so schnell wie möglich. Du erhältst eine Bestätigung an",
  "booking.close": "Schließen",
  "booking.available": "Verfügbar",
  "booking.unavailable": "Nicht verfügbar",
  "body.shoulder": "Épaule",
  "body.hip": "Hanche",
  "body.finger": "Doigt",
  "body.other": "Autre",
  "body.selected": "Sélectionné",
  "body.front": "Avant",
  "body.back_view": "Arrière",
  "body.front_view": "Avant",
  "body.back_view_btn": "Arrière",
  "style.black_grey": "Noir et Gris",
  "style.full_color": "Couleur complète",
  "style.realism": "Réalisme",
  "style.fine_line": "Fine Line / Minimal",
  "style.traditional": "Traditionnel",
  "style.neo_traditional": "Néo-traditionnel",
  "style.watercolor": "Aquarelle",
  "style.tribal": "Tribal",
  "style.japanese": "Japonais",
  "style.geometric": "Géométrique",
  "style.lettering": "Lettrage",
  "style.script": "Écriture",
  "style.dotwork": "Dotwork",
  "style.mandala": "Mandala",
  "style.blackwork": "Blackwork",
  "style.illustrative": "Illustratif",
  "style.surrealism": "Surréalisme",
  "style.ornamental": "Ornemental",
  "style.micro_realism": "Micro-réalisme",
  "style.trash_polka": "Trash Polka",
  "style.other": "Autre",
  "consult.title": "Prêt à commencer?",
  "consult.book": "Réserver une consultation",
  "pdf.title": "Devis",
  "pdf.download": "Télécharger PDF",
  "upgrade.choose": "Choisissez votre",
  "upgrade.plan_word": "Abonnement",
  "upgrade.per_month": "/mois",
  "upgrade.monthly": "Mensuel",
  "upgrade.yearly": "Annuel",
  "upgrade.mo": "mois",
  "upgrade.year": "an",
  "billing.expires": "Expire",
  "billing.change": "Modifier",
  "billing.next_invoice": "Prochaine facture",
  "billing.invoice_history": "Historique des factures",
  "upgrade.perfect_start": "Parfait pour commencer",
  "upgrade.everything_pro": "Tout pour les professionnels",
  "upgrade.for_studios": "Pour les studios de tatouage & équipes",
  "upgrade.upgrade_pro": "Passer en Pro",
  "upgrade.upgrade_studio": "Passer en Studio",
  "upgrade.discount": "réduction",
  "upgrade.cancel_anytime_long": "Annulation à tout moment. Pas de frais cachés.",
  "auth.login": "Connexion",
  "auth.register": "Inscription",
  "auth.submit_login": "Se connecter",
  "auth.submit_register": "Créer un compte",
  "discover.title": "Découvrir",
  "discover.title_accent": "Artistes",
  "discover.search_placeholder": "Rechercher par ville, artiste ou studio...",
  "discover.no_artists": "Aucun artiste trouvé",
  "discover.view_calculator": "Voir le calculateur",
  "footer.tagline": "Le calculateur de prix de tatouage le plus intelligent pour artistes et clients.",
  "footer.links": "Liens",
  "footer.discover_artists": "Découvrir des artistes",
  "footer.artist_login": "Connexion artiste",
  "footer.privacy": "Politique de confidentialité",
  "footer.language_label": "Langue & App",
  "footer.pwa_badge": "Optimisé PWA (cliquez pour info)",
  "footer.all_rights": "Tous droits réservés.",
  "footer.install_title": "Installer",
  "footer.install_ok": "Compris!",
  "footer.install_desc": "Utilisez Inkvaluator comme une vraie app sur votre téléphone.",
  "footer.install_ios_title": "iPhone / iPad (iOS)",
  "footer.install_ios_desc": "Appuyez sur le bouton Partager en bas et choisissez \"Sur l'écran d'accueil\".",
  "footer.install_android_title": "Android",
  "footer.install_android_desc": 'Appuyez sur les trois points en haut à droite et choisissez "Installer l\'app".',
  "success.welcome": "Bienvenue au Club,",
  "success.go_dashboard": "Aller au tableau de bord",
  "landing.how_it_works_cta": "Découvrez comment ça fonctionne pour votre studio",
  "footer.terms": "Conditions générales",
  "footer.support": "Support",
  "footer.how_it_works": "Comment ça marche ?",
  "hiw.seo_title": "Comment ça marche ? – Inkvaluator",
  "hiw.seo_desc":
    "Découvrez comment Inkvaluator aide les tatoueurs avec des estimations automatiques, des leads intelligents et un branding professionnel.",
  "hiw.hero_title": "Concentrez-vous sur l'encre, pas sur la facture",
  "hiw.hero_p1":
    "Passez-vous plus de temps à répondre aux DMs sur les prix qu'à dessiner votre prochain chef-d'œuvre ? Soyons honnêtes : vous êtes tatoueur, pas agent de service client.",
  "hiw.hero_p2":
    "Chaque question « Combien ça coûte ? » à laquelle vous devez répondre manuellement est du temps que vous ne consacrez pas à votre art.",
  "hiw.hero_p3":
    "Inkvaluator est l'assistant numérique qui filtre votre business. Pendant que vous êtes dans la zone, l'outil calcule des estimations précises pour vos clients, recueille toutes les infos nécessaires et envoie une demande prête à l'emploi directement dans votre WhatsApp.",
  "hiw.hero_p4":
    "Fini les « touristes de prix » qui n'ont pas de budget après trois heures de discussion. Uniquement des leads sérieux, des devis professionnels et un branding qui montre que vous prenez votre métier au sérieux.",
  "hiw.hero_cta": "Prêt à récupérer votre temps de travail ?",
  "hiw.faq_title": "Questions fréquentes",
  "hiw.faq_cat1": "Les bases : comment cela m'aide-t-il ?",
  "hiw.faq_q1": "C'est encore une appli compliquée ?",
  "hiw.faq_a1":
    "Non. C'est un calculateur ultra-rapide que vous mettez dans votre bio Instagram ou intégrez sur votre site. Vous configurez vos règles une fois (tarif horaire, frais de démarrage, styles) et l'outil fait le reste.",
  "hiw.faq_q2": "Comment cela me fait-il concrètement gagner du temps ?",
  "hiw.faq_a2":
    "Au lieu de demander dix fois : « Quelle taille ? Où sur le corps ? Quel style ? », vous recevez un message WhatsApp avec tout déjà rempli, y compris la photo de référence et le prix que le client a déjà accepté.",
  "hiw.faq_cat2": "Tarifs & paramètres",
  "hiw.faq_q3": "Puis-je fixer mes propres prix ?",
  "hiw.faq_a3":
    "Absolument. Vous êtes le patron. Vous définissez votre propre tarif horaire et déterminez ce que vous facturez pour différentes tailles et complexités. L'outil calcule exactement comme vous le feriez à votre poste de travail.",
  "hiw.faq_q4": "Et si un tatouage est beaucoup plus complexe que la moyenne ?",
  "hiw.faq_a4":
    "Notre scanner IA intégré reconnaît les détails sur une photo. Il voit la différence entre un simple texte fine line et un portrait hyperréaliste et ajuste l'estimation en conséquence.",
  "hiw.faq_cat3": "Apparence & branding",
  "hiw.faq_q5": "Ça ne ressemble pas à un outil générique ?",
  "hiw.faq_a5":
    "Absolument pas. Avec le plan Studio, l'outil est entièrement en marque blanche. Cela signifie : votre logo, vos couleurs et votre style. 8 thèmes professionnels sont prêts (de Luxury Dark à Modern Light) pour que le calculateur s'intègre parfaitement à votre boutique.",
  "hiw.faq_q6": "À quel point cela fait-il professionnel pour mon client ?",
  "hiw.faq_a6":
    "Très professionnel. Le client peut télécharger le devis en PDF élégant. Cela inspire confiance et évite les discussions ultérieures sur « ce que nous avions convenu ».",
  "hiw.faq_cat4": "Croissance & visibilité",
  "hiw.faq_q7": "Je veux plus de clients de ma région, est-ce que ça aide ?",
  "hiw.faq_a7":
    "Oui ! Inkvaluator dispose d'une carte mondiale interactive. Les clients peuvent chercher par pays, province et ville. Plus vous utilisez l'outil, meilleure sera votre visibilité pour ceux qui cherchent un artiste dans votre ville.",
  "hiw.backend_title": "Dans les coulisses : la puissance du backend",
  "hiw.backend_intro":
    "Pour les amateurs de technologie et les perfectionnistes : Inkvaluator est rempli de fonctionnalités avancées qui propulsent votre studio au niveau supérieur.",
  "hiw.card_pricing_title": "Gestion dynamique des prix",
  "hiw.card_pricing_text":
    "Ajustez les prix et les IDs Stripe via le module admin sans toucher une ligne de code. Tout se synchronise instantanément avec votre caisse.",
  "hiw.card_ai_title": "Intégration AI Vision",
  "hiw.card_ai_text":
    "Le scanner IA utilise l'analyse d'image pour traduire motifs, utilisation des couleurs et complexité en heures de travail.",
  "hiw.card_stripe_title": "Portail Client Stripe",
  "hiw.card_stripe_text":
    "Un système d'abonnement entièrement automatisé. Passer en Pro, télécharger des factures ou gérer votre abonnement dans un environnement sécurisé.",
  "hiw.card_analytics_title": "Tableau de bord analytique",
  "hiw.card_analytics_text":
    "Obtenez un aperçu approfondi de vos conversions. Voyez combien de personnes utilisent votre calculateur et quel est votre revenu potentiel.",
  "hiw.card_blog_title": "Blog & SEO CMS",
  "hiw.card_blog_text": "Système intégré pour publier des articles qui améliorent votre visibilité Google.",
  "hiw.card_global_title": "Architecture globale",
  "hiw.card_global_text": "Support multilingue complet (NL/EN/etc.) avec conservation de la langue préférée du client.",
  "hiw.card_mobile_title": "Prêt pour le mobile (PWA)",
  "hiw.card_mobile_text":
    "Inkvaluator est une Progressive Web App (PWA). Installez l'outil directement sur votre écran d'accueil via le navigateur. Ultra-rapide, sécurisé et sans consommer d'espace de stockage.",
  "hiw.cta_title": "Qu'attendez-vous ?",
  "hiw.cta_accent": "Arrêtez de calculer, commencez à créer.",
  "hiw.cta_button": "Essayez Inkvaluator gratuitement",
  "tos.seo_title": "Conditions générales | Inkvaluator",
  "tos.seo_desc":
    "Lisez les conditions générales d'Inkvaluator. Informations sur l'utilisation de notre calculateur de prix de tatouage, les abonnements et la responsabilité.",
  "tos.title": "Conditions générales",
  "tos.updated": "Dernière mise à jour :",
  "tos.s1_title": "1. Définitions",
  "tos.s1_platform":
    "Plateforme : Inkvaluator, une plateforme SaaS disponible via inkvaluator.com et ses sous-domaines associés.",
  "tos.s1_user": "Utilisateur / Artiste : Tout tatoueur ou studio qui crée un compte sur la Plateforme.",
  "tos.s1_visitor":
    "Visiteur : Toute personne qui utilise le calculateur public ou la section découverte de la Plateforme.",
  "tos.s1_service":
    "Service : Les fonctionnalités offertes par Inkvaluator, y compris le calculateur de prix de tatouage, la gestion des leads, le scanner IA et les outils en marque blanche.",
  "tos.s2_title": "2. Utilisation du calculateur",
  "tos.s2_p1":
    "Les estimations de prix générées par Inkvaluator sont uniquement indicatives et ne constituent pas une offre contraignante, un devis ou une garantie de prix. Le prix réel d'un tatouage est toujours déterminé par l'artiste, sur la base d'une consultation personnelle.",
  "tos.s2_p2":
    "Inkvaluator n'est pas responsable des décisions prises sur la base des estimations générées, ni des litiges entre artistes et clients concernant les prix.",
  "tos.s3_title": "3. Abonnements & paiements",
  "tos.s3_intro": "Inkvaluator propose trois niveaux : Free, Pro et Studio.",
  "tos.s3_billing":
    "Les abonnements payants sont facturés mensuellement via Stripe et sont automatiquement renouvelés.",
  "tos.s3_cancel":
    "Vous pouvez annuler votre abonnement à tout moment via le portail client Stripe dans votre tableau de bord. Après annulation, vous conservez l'accès aux fonctionnalités payantes jusqu'à la fin de la période de facturation en cours.",
  "tos.s3_refund":
    "Les remboursements ne sont pas possibles pour les périodes de facturation déjà commencées, sauf disposition contraire du droit applicable.",
  "tos.s3_price_change":
    "Inkvaluator se réserve le droit de modifier ses prix. Toute modification sera communiquée au moins 30 jours à l'avance.",
  "tos.s4_title": "4. Propriété des données",
  "tos.s4_p1":
    "Toutes les données saisies par un Artiste (paramètres de prix, données de profil, logos) restent la propriété de l'Artiste. Inkvaluator obtient une licence limitée pour utiliser ces données afin de fournir le Service.",
  "tos.s4_p2":
    "Les données d'utilisation anonymes et agrégées peuvent être utilisées par Inkvaluator pour des analyses statistiques et l'amélioration du produit.",
  "tos.s5_title": "5. Responsabilité",
  "tos.s5_p1":
    "Inkvaluator fournit le Service « en l'état ». Nous nous efforçons d'assurer une disponibilité et une précision maximales, mais ne garantissons pas un fonctionnement ininterrompu ou sans erreur.",
  "tos.s5_indirect":
    "Inkvaluator n'est pas responsable des dommages indirects, pertes de profits ou dommages consécutifs.",
  "tos.s5_cap":
    "La responsabilité totale d'Inkvaluator est limitée au montant payé par l'Utilisateur en frais d'abonnement au cours des trois mois précédant la réclamation.",
  "tos.s5_content": "Inkvaluator n'est pas responsable du contenu que les artistes placent sur leur profil.",
  "tos.s6_title": "6. Compte & résiliation",
  "tos.s6_credentials": "Les utilisateurs sont responsables de la confidentialité de leurs identifiants de connexion.",
  "tos.s6_suspend":
    "Inkvaluator se réserve le droit de suspendre ou supprimer des comptes en cas d'abus, de fraude ou de violation de ces conditions.",
  "tos.s6_delete": "En cas de résiliation d'un compte, les données personnelles seront supprimées conformément à la",
  "tos.s6_privacy_link": "Politique de confidentialité",
  "tos.s7_title": "7. Propriété intellectuelle",
  "tos.s7_p1":
    "Tous les droits de propriété intellectuelle sur la Plateforme, le logiciel, le design et la marque « Inkvaluator » appartiennent à Inkvaluator.",
  "tos.s8_title": "8. Droit applicable",
  "tos.s8_p1":
    "Les présentes conditions générales sont régies par le droit néerlandais. Les litiges seront exclusivement soumis au tribunal compétent aux Pays-Bas.",
  "tos.contact_title": "Questions sur ces conditions ?",
  "tos.contact_text": "Contactez-nous à :",
  "support.seo_title": "Support | Inkvaluator",
  "support.seo_desc": "Besoin d'aide avec Inkvaluator ? Consultez notre FAQ ou contactez notre équipe support.",
  "support.title": "Support",
  "support.faq_title": "Questions fréquentes",
  "support.faq_q1": "Comment définir mes tarifs ?",
  "support.faq_a1":
    "Allez dans votre tableau de bord et cliquez sur « Tarifs ». Vous pouvez y définir votre tarif horaire, prix minimum, acompte et frais de design.",
  "support.faq_q2": "Comment fonctionne l'intégration Stripe ?",
  "support.faq_a2":
    "Lorsque vous passez en Pro ou Studio, vous êtes redirigé vers un paiement Stripe sécurisé. Après le paiement, votre abonnement est automatiquement activé.",
  "support.faq_q3": "Qu'est-ce que le module WhatsApp ?",
  "support.faq_a3":
    "Avec le module WhatsApp (disponible à partir de Pro), les visiteurs peuvent vous contacter directement via WhatsApp après avoir calculé un prix.",
  "support.faq_q4": "Comment partager mon calculateur avec les clients ?",
  "support.faq_a4":
    "Dans votre tableau de bord sous « Partager », vous trouverez un lien direct, un code QR et un code d'intégration pour votre site web.",
  "support.faq_q5": "Puis-je annuler mon abonnement ?",
  "support.faq_a5":
    "Oui, vous pouvez annuler à tout moment via « Gérer l'abonnement » dans votre tableau de bord. Vous conservez l'accès jusqu'à la fin de votre période de facturation.",
  "support.faq_q6": "Comment fonctionne le scanner IA ?",
  "support.faq_a6":
    "Le scanner IA (fonction Pro) analyse une photo de design de tatouage et estime automatiquement la taille, la complexité et le style.",
  "support.faq_q7": "J'ai trouvé un bug, que faire ?",
  "support.faq_a7":
    "Envoyez un e-mail à support@inkvaluator.com avec une description du problème, des captures d'écran si possible et votre appareil/navigateur.",
  "support.contact_title": "Contact",
  "support.contact_text": "Question non listée ? Contactez notre équipe directement :",
  "support.form_name": "Nom",
  "support.form_name_placeholder": "Votre nom",
  "support.form_email": "E-mail",
  "support.form_message": "Message",
  "support.form_message_placeholder": "Décrivez votre question ou problème...",
  "support.form_submit": "Envoyer le message",
  "support.form_subject": "Demande de support de {name}",
  "support.toast_title": "Client e-mail ouvert",
  "support.toast_desc": "Votre application e-mail s'ouvre avec votre message.",
  // Privacy
  "privacy.seo_title": "Politique de confidentialité | Inkvaluator",
  "privacy.seo_desc": "Lisez la politique de confidentialité d'Inkvaluator.",
  "privacy.title": "Politique de confidentialité",
  "privacy.updated": "Dernière mise à jour :",
  "privacy.s1_title": "1. Quelles données collectons-nous ?",
  "privacy.s1_intro": "Inkvaluator collecte les données nécessaires au fonctionnement de la plateforme :",
  "privacy.s1_artists_label": "Pour les artistes",
  "privacy.s1_artists": "Nom, e-mail, nom du studio, adresse et portfolio.",
  "privacy.s1_visitors_label": "Pour les visiteurs",
  "privacy.s1_visitors": "Données saisies dans le calculateur pour générer des estimations.",
  "privacy.s1_leads_label": "Leads",
  "privacy.s1_leads": "Lorsque vous contactez un artiste, votre message et vos photos sont traités en toute sécurité.",
  "privacy.s2_title": "2. Utilisation des données",
  "privacy.s2_intro": "Votre vie privée est notre priorité. Nous ne vendons jamais vos données à des tiers.",
  "privacy.s2_matching": "Mise en relation des clients avec les bons artistes.",
  "privacy.s2_algorithms": "Amélioration de nos algorithmes de prix.",
  "privacy.s2_pwa": "Fonctionnement technique de la PWA.",
  "privacy.s3_title": "3. Législation internationale (RGPD)",
  "privacy.s3_intro": "Conformément à la législation européenne, vous disposez des droits suivants :",
  "privacy.s3_access_label": "Droit d'accès",
  "privacy.s3_access": "Vous pouvez demander quelles données nous détenons.",
  "privacy.s3_erasure_label": "Droit à l'oubli",
  "privacy.s3_erasure": "Vous pouvez demander la suppression complète de votre compte et de vos données.",
  "privacy.s3_portability_label": "Portabilité des données",
  "privacy.s3_portability": "Vous pouvez demander vos données dans un format lisible.",
  "privacy.s4_title": "4. Cookies et PWA",
  "privacy.s4_text": "Inkvaluator utilise un cache fonctionnel (PWA). Nous n'utilisons pas de cookies de suivi.",
  "privacy.contact_title": "Questions sur votre vie privée ?",
  "privacy.contact_text": "Contactez notre délégué à la protection des données :",
  // Dashboard extras
  "dash.unsaved_changes": "Vous avez des modifications non enregistrées",
  "dash.dashboard_language": "Langue du tableau de bord",
  "dash.dashboard_language_desc": "La langue de votre interface de gestion.",
  "auth.dashboard_subtitle": "Tableau de bord artiste",
  "dash.impersonation_banner": "Vous consultez actuellement le profil de {name}",
  "dash.stop_impersonation": "Arrêter l'usurpation",
  // Discover extras
  "discover.subtitle": "Découvrir Studios & Artistes",
  "discover.description":
    "Trouvez des tatoueurs et des studios près de chez vous. Consultez leur calculateur de prix et contactez-les.",
  "discover.hide_map": "Masquer la carte",
  "discover.show_map": "Afficher la carte",
  "discover.map_loading": "Chargement de la carte...",
  "discover.call": "Appeler",
  "discover.route": "Itinéraire",
  "discover.map_title": "Artistes sur la carte",
  "discover.no_artists_for": 'Aucun artiste trouvé pour "{query}".',
  "discover.playground": "Playground",
  "discover.artist_login": "Connexion artiste",
  // Feature labels
  "feature.calculator": "Calculateur de prix personnalisé",
  "feature.qr": "QR code & lien partageable",
  "feature.styles": "Styles illimités & scanner IA",
  "feature.logo": "Upload du logo",
  "feature.theme": "Thème de couleur personnalisé",
  "feature.language": "Multilingue (17 langues)",
  "feature.currency": "Changement de devise (13 devises)",
  "feature.analytics": "Analyses d'utilisation",
  "feature.pdf": "Export PDF",
  "feature.whitelabel": "Branding marque blanche",
  "feature.map_visibility": "Visibilité sur la carte mondiale",
  "feature.multiartist": "Gestion multi-artistes",
  "feature.centralanalytics": "Tableau de bord analytique central",
  "feature.teammanagement": "Gestion d'équipe & rôles",
  // Map
  "map.view_calculator": "Voir le calculateur",
  "map.unknown_location": "Lieu inconnu",
  "map.artists_count": "artistes",
  "map.world_title": "Artistes",
  "map.world_accent": "dans le monde",
  "map.in_countries": "dans {count} pays",
  // Blog
  "blog.title": "Blog",
  "blog.no_posts": "Aucun article publié.",
  "blog.seo_title": "Blog | Inkvaluator",
  "blog.seo_desc": "Conseils, tendances et informations sur les prix du tatouage et l'industrie du tatouage.",
  "blog.read_more": "Lire la suite",
  "blog.published_on": "Publié le",
  "blog.back": "Retour au blog",
  "blog.not_found": "Article introuvable",
  "blog.translation_notice":
    "Cet article n'est pas encore disponible dans votre langue. Affichage de la version originale.",
  // Landing extras
  "landing.start_free_btn": "Commencer gratuitement",
  "landing.upgrade_btn": "Améliorer",
  "landing.professionals": "Pour les professionnels",
  "landing.studios_teams": "Pour les studios & équipes",
  "landing.artist_or_studio": "Êtes-vous un artiste ou un studio ?",
  // Demo
  "demo.subtitle": "Playground",
  "demo.title": "Essayez le",
  "demo.title_accent": "Calculateur",
  "demo.description": "Ajustez les variables et découvrez le fonctionnement du calculateur de prix.",
  "demo.your_rates": "Vos tarifs",
  "demo.rates_desc": "Ajustez les curseurs pour expérimenter",
  "demo.hourly_rate": "Tarif horaire",
  "demo.start_cost": "Frais de démarrage fixes",
  "demo.size": "Taille",
  "demo.complexity": "Complexité",
  "demo.placement": "Placement",
  "demo.estimated_price": "Prix estimé",
  "demo.est_time": "Temps estimé (incl. détail)",
  "demo.start_costs": "Frais de démarrage",
  "demo.total_multiplier": "Multiplicateur total",
  "demo.disclaimer": "Ceci est une démo. Le prix réel dépend de l'artiste et du design.",
  "demo.artist_cta": "Êtes-vous artiste ?",
  "demo.artist_cta_desc": "Utilisez cet outil sur votre propre site.",
  "demo.register_free": "S'inscrire gratuitement",
  "demo.find_studio": "Trouver un studio près de chez vous",
  // Embed
  "embed.title": "Widget intégrable",
  "embed.desc": "Placez votre calculateur sur votre propre site web.",
  "embed.copy_code": "Copier le code",
  // Profile
  "profile.identity": "Identité",
  "profile.identity_desc": "Votre nom et nom de studio sur votre page publique.",
  "profile.studio_name": "Nom du studio",
  "profile.location": "Localisation",
  "profile.location_desc": "Votre adresse pour le répertoire et les itinéraires.",
  "profile.city": "Ville",
  "profile.street": "Rue et numéro",
  "profile.postal_code": "Code postal",
  "profile.contact_socials": "Contact & Réseaux",
  "profile.contact_desc": "Comment les clients peuvent vous trouver.",
  "profile.phone_label": "Téléphone / WhatsApp",
  "profile.calculator_language": "Langue du calculateur",
  "profile.calculator_language_desc": "La langue que vos clients voient sur le calculateur.",
  // New keys Day 17-20
  "vault.inspiration_title": "Inspiration",
  "vault.inspiration_desc": "En fonction de vos estimations enregistrées, ces styles pourraient vous plaire :",
  "vault.try_style": "Essayer →",
  "vault.status_concept": "Concept",
  "vault.status_sent": "Demande envoyée",
  "reviews.rating": "note",
  "reviews.verified_reviews": "avis vérifiés",
  "reviews.read_all": "Lire tous les avis sur Google",
  "discover.swipe": "glisser",
  "profile.manual_place_id": "Studio introuvable ? Entrez votre Place ID manuellement.",
  "analytics.revenue_funnel": "Entonnoir de revenus",
  "analytics.total_calc_value": "Valeur calculée totale",
  "analytics.total_lead_value": "Valeur des leads",
  "analytics.whatsapp_leads": "Leads WhatsApp",
  "analytics.lead_quality": "Qualité des leads",
  "analytics.body_heatmap": "Carte thermique corporelle",
  "analytics.body_heatmap_desc": "Où vos clients veulent leur tatouage — plus c'est élevé, plus c'est populaire.",
  "analytics.team_leaderboard": "Classement de l'équipe",
  "vault.compare": "Comparer",
  "vault.compare_title": "Comparaison",
  "vault.select_to_compare": "Sélectionnez 2 estimations à comparer",
  "trends.title": "Tendances",
  "trends.title_accent": "Tatouage",
  "trends.subtitle": "Découvrez les styles et placements les plus populaires dans le monde.",
  "trends.top_styles": "Top styles",
  "trends.top_body_parts": "Parties du corps populaires",
  "trends.no_data": "Pas encore assez de données.",
  "match.title": "Artistes correspondants près de chez vous",
  "match.score": "Match",
  "match.view": "Voir le profil",
  "urgency.recent_requests": "demandes cette semaine",
  "urgency.wait_time": "Attente estimée",
  "urgency.weeks": "semaines",
  "urgency.high_demand": "Forte demande",
  "urgency.popular": "Populaire",
  "benchmark.above_market": "au-dessus de la moyenne",
  "benchmark.below_market": "en dessous de la moyenne",
  "benchmark.at_market": "Dans la moyenne",
  "benchmark.market_avg": "Moyenne du marché",
  "portfolio.style_examples": "Exemples de style",
  "story.title": "Générateur de Story Instagram",
  "story.desc": "Créez une story premium Black & Gold avec votre QR code.",
  "story.generate": "Générer la Story",
  "story.regenerate": "Régénérer",
  "story.download": "Télécharger",
  "story.share": "Partager",
  "story.downloaded": "Story téléchargée !",
  "story.cta_line1": "Calculez Votre Prix",
  "story.cta_line2": "Scannez le QR code ci-dessous",
  "story.scan_prompt": "↑ Scannez pour votre estimation",
  "trends.filter_country": "Filtrer par pays",
  "trends.all_countries": "Tous les pays",
  "trends.analyzed": "calculs analysés",
  "trends.realtime": "Mis à jour en temps réel",
  "match.style_experts": "Experts en {style} près de {city}",
  "vault.review_booster_title": "Tatouage terminé ? Aidez votre artiste !",
  "vault.review_booster_desc": "Laissez un avis Google et aidez d'autres clients à trouver cet artiste.",
  "vault.review_booster_btn": "Écrire un avis Google",
  "checklist.title": "Statut de vérification",
  "checklist.profile_complete": "Profil complet",
  "checklist.google_linked": "Google Place ID lié",
  "checklist.badge_detected": "Badge vérifié sur le site",
  "checklist.push_enabled": "Notifications push activées",
  "checklist.not_yet": "Pas encore",
  "checklist.active": "Actif",
  "lead.status_new": "Nouveau",
  "lead.status_contacted": "En contact",
  "lead.status_booked": "Réservé",
  "lead.change_status": "Statut",
  "lead.add_price": "Ajouter le prix final",
  "push.test_body": "Les notifications push sont LIVE ! Vous recevrez des alertes pour les nouveaux prospects.",
  "push.send_test": "Envoyer une notification test",
  "push.active_desc": "Vous recevez des notifications pour les prospects de qualité.",
  "push.blocked": "Les notifications sont bloquées dans les paramètres de votre navigateur.",
  "push.enable_desc": "Recevez des notifications lorsque des prospects de qualité arrivent.",
  "push.label": "Notifications push",
  "push.welcome_body": "Bienvenue ! Vous recevrez des alertes pour les prospects de qualité.",
  "push.denied_info":
    "Restez informé des nouveaux prospects — Activez les notifications dans les paramètres de votre navigateur.",
  "vault.live_status": "Statut en direct",
  "vault.status_new_desc": "Votre demande a été reçue",
  "vault.status_contacted_desc": "L'artiste a vu votre message",
  "vault.status_booked_desc": "Votre rendez-vous est confirmé !",
  "vault.link_copied": "🔗 Lien copié !",
  "analytics.actual_revenue": "Revenu Réel",
  "style_city.popular_styles": "Styles populaires à {city}",
  "nav.home": "Accueil",
  "lead.estimate_header": "Estimation Tatouage",
  "discover.verified_badge": "VÉRIFIÉ",
  // WhatsApp Lead
  "wa.title": "Envoyer à l'artiste via WhatsApp",
  "wa.subtitle": "Envoyez votre estimation directement à",
  "wa.name": "Votre nom (optionnel)",
  "wa.name_placeholder": "Votre nom",
  "wa.message": "Message (optionnel)",
  "wa.message_placeholder": "Décrivez votre idée, inspiration ou détails...",
  "wa.photo": "Photo de référence (optionnel)",
  "wa.upload_photo": "Télécharger une photo",
  "wa.file_too_large": "Fichier trop volumineux",
  "wa.summary": "Résumé :",
  "wa.send": "Envoyer via WhatsApp",
  "wa.sent_title": "Message envoyé !",
  "wa.sent_desc": "Votre demande a été envoyée à {name} via WhatsApp.",
  "wa.upload_failed": "Échec du téléchargement",
  "wa.upload_failed_desc": "Impossible de télécharger la photo. Veuillez réessayer.",
  "wa.save_failed": "Échec de la sauvegarde",
  "wa.success": "Envoyé !",
  "wa.success_desc": "Votre demande a été créée et WhatsApp a été ouvert.",
  "wa.error": "Une erreur est survenue",
  "wa.error_desc": "Erreur inconnue. Vérifiez votre connexion et réessayez.",
  "wa.msg_greeting": "Bonjour {name}, j'ai calculé une estimation de prix :",
  "wa.msg_name_label": "Nom",
  "wa.msg_style_label": "🎨 Style",
  "wa.msg_bodypart_label": "📍 Partie du corps",
  "wa.msg_size_label": "📏 Taille",
  "wa.msg_complexity_label": "⚙️ Complexité",
  "wa.msg_time_label": "⏱ Temps estimé",
  "wa.msg_total_label": "💰 Total",
  "wa.msg_deposit_label": "💳 Acompte",
  "wa.msg_details_label": "🔗 Détails",
  "wa.template_high_greeting":
    "Bonjour {name}, bonne nouvelle ! J'ai calculé une estimation détaillée pour mon tatouage :",
  "wa.template_high_vault": "🔗 J'ai sauvegardé ceci dans mon Coffre Tatouage : {url}",
  "wa.template_low_greeting":
    "Bonjour {name}, je m'intéresse à un tatouage. Pouvez-vous me renseigner sur vos disponibilités et tarifs ?",
  "wa.template_low_note": "Basé sur le calculateur, une première idée :",
  // Tooltips
  "tooltip.labor":
    "Basé sur le temps estimé pour cette taille de tatouage, multiplié par le tarif horaire de l'artiste.",
  "tooltip.complexity":
    "Les designs plus complexes (plus de détails, ombres, textures) nécessitent plus de temps et de précision.",
  "tooltip.placement":
    "Certaines parties du corps sont plus sensibles ou difficiles d'accès, nécessitant plus de temps et de soin.",
  "tooltip.style": "Différents styles nécessitent des techniques et expertises spécifiques, ce qui affecte le prix.",
  "tooltip.design": "Coûts de conception et préparation de votre tatouage unique.",
  "tooltip.deposit": "L'acompte réserve votre rendez-vous et sera déduit du prix total.",
  // Pricing Education
  "pricing_edu.button": "Comment ce calcul fonctionne-t-il ?",
  "pricing_edu.title": "Comment le prix est calculé",
  "pricing_edu.intro":
    "Le prix d'un tatouage est déterminé par plusieurs facteurs reflétant le savoir-faire et le temps nécessaire.",
  "pricing_edu.time_title": "Temps & Taille",
  "pricing_edu.time_desc":
    "Les tatouages plus grands prennent plus de temps. Le tarif horaire est multiplié par les heures estimées.",
  "pricing_edu.complexity_title": "Complexité & Détail",
  "pricing_edu.complexity_desc": "Les designs détaillés nécessitent plus de précision et de temps.",
  "pricing_edu.placement_title": "Placement corporel",
  "pricing_edu.placement_desc": "Certaines zones sont plus sensibles ou difficiles d'accès, nécessitant plus de soin.",
  "pricing_edu.style_title": "Style de tatouage",
  "pricing_edu.style_desc":
    "Chaque style nécessite des techniques spécifiques. Le réalisme et le japonais prennent généralement plus de temps.",
  "pricing_edu.disclaimer":
    "Ceci est toujours une estimation. Le prix final est déterminé après consultation personnelle avec l'artiste.",
  // AI Scanner
  "ai.title": "Scanner IA",
  "ai.desc": "Téléchargez une photo et laissez l'IA déterminer les paramètres",
  "ai.upload_prompt": "Prenez une photo ou téléchargez une image de référence",
  "ai.analyzing": "L'IA analyse...",
  "ai.result_title": "Résultat IA",
  "ai.result_size": "Taille",
  "ai.result_complexity": "Complexité",
  "ai.result_style": "Style",
  "ai.result_color": "Couleur",
  "ai.yes": "Oui",
  "ai.no": "Non",
  "ai.choose_other": "Choisir une autre photo",
  "ai.result_placement": "Placement",
  "ai.phase_detecting": "Détection de la complexité...",
  "ai.phase_style": "Analyse du style...",
  "ai.phase_placement": "Détection du placement...",
  "ai.phase_finalizing": "Finalisation de l'estimation...",
  "ai.file_too_large": "Fichier trop volumineux",
  "ai.max_size": "Max 10 Mo",
  "ai.rate_limit": "Limite atteinte",
  "ai.rate_limit_desc": "Max 3 scans par 24 heures.",
  "ai.scan_complete": "✨ Scan IA terminé",
  "ai.scan_applied": "Le calculateur a été automatiquement ajusté.",
  "ai.scan_failed": "Scan échoué",
  "ai.try_again": "Veuillez réessayer",
  // Referral
  "referral.title": "Recommander un collègue",
  "referral.desc": "Invitez un collègue et gagnez une réduction sur votre abonnement.",
  "referral.generate": "Générer un lien de parrainage",
  "referral.share_link": "Partagez ce lien avec un collègue :",
  "referral.reward_info": "Pour chaque collègue inscrit via votre lien, vous gagnez 10% de réduction.",
  "referral.invited": "Invité",
  "referral.earned": "Réduction gagnée",
  "referral.redeemed": "Parrainages échangés",
  // PWA
  "pwa.install_title": "Installer comme app",
  "pwa.install_benefit": "Utilisez l'app pour des scans IA plus rapides",
  "pwa.install_button": "Installer maintenant",
  // Batch 3+4: Dashboard management, onboarding, settings, landing FAQs, vault, analytics, studio, help
  "tab.theme": "Thème",
  "tab.branding": "Marque",
  "tab.analytics": "Analyses",
  "tab.price_engine": "Moteur de prix",
  "tab.price_engine_desc": "Gérez tous les multiplicateurs de prix : tailles, complexité, parties du corps et styles.",
  "basic.desc": "Définissez vos tarifs et coûts standard",
  "basic.min_price_desc": "Le prix le plus bas que vous facturez",
  "basic.hourly_rate_desc": "Votre tarif horaire standard",
  "basic.daily_rate_placeholder": "Laissez vide si non applicable",
  "basic.percentage": "%",
  "basic.fixed_amount": "Fixe",
  "basic.consultation_email": "E-mail de consultation",
  "basic.consultation_email_desc": "Adresse e-mail pour les demandes de consultation",
  "basic.booking_url": "URL de réservation",
  "basic.booking_url_desc": "URL du système de réservation externe",
  "sizes.title": "Paramètres de taille",
  "sizes.desc": "Définissez le temps estimé par catégorie de taille",
  "sizes.min_hours": "Min. heures",
  "sizes.max_hours": "Max. heures",
  "sizes.hours": "heures",
  "complexity.title": "Multiplicateurs de complexité",
  "complexity.desc": "Ajustez le facteur de prix selon la complexité",
  "bodyparts.title": "Multiplicateurs parties du corps",
  "bodyparts.desc": "Certaines parties du corps sont plus difficiles à tatouer",
  "styles.title": "Gestion des styles",
  "styles.desc": "Activez ou désactivez des styles et ajustez le facteur de prix.",
  "theme.title": "Thème de couleur",
  "theme.desc": "Personnalisez les couleurs de votre calculateur",
  "theme.preview": "Aperçu",
  "theme.preview_desc": "Voici à quoi ressemble votre calculateur",
  "theme.hue": "Teinte",
  "theme.saturation": "Saturation",
  "theme.lightness": "Luminosité",
  "theme.presets": "Sélection rapide",
  "branding.title": "Marque",
  "branding.desc": "Téléchargez votre logo et définissez votre langue et devise",
  "branding.logo": "Logo",
  "branding.logo_desc": "Logo de studio (max 2 Mo, PNG ou JPG)",
  "branding.upload": "Télécharger un logo",
  "branding.remove": "Supprimer",
  "branding.language": "Langue du calculateur",
  "branding.currency": "Devise",
  "branding.qr_code": "Code QR",
  "branding.qr_desc": "Les clients scannent ce code pour accéder à votre calculateur.",
  "branding.download_qr": "Télécharger le QR",
  "onboard.step_minimum": "Tarif minimum",
  "onboard.step_hourly": "Tarif horaire",
  "onboard.step_sizes": "Confirmer les tailles",
  "onboard.step_styles": "Définir les styles",
  "onboard.step_logo": "Télécharger un logo",
  "onboard.step_finish": "Calculateur prêt !",
  "dash.onboard_min_desc": "Définissez votre tarif minimum.",
  "dash.onboard_hourly_desc": "Définissez votre tarif horaire. C'est la base de tous les calculs.",
  "dash.onboard_sizes_desc": "Catégories de taille par défaut configurées. Ajustez-les plus tard.",
  "dash.onboard_styles_desc": "Tous les styles courants sont activés. Activez/désactivez-les dans le tableau de bord.",
  "dash.onboard_logo_desc": "Téléchargez un logo maintenant ou plus tard via l'onglet Marque.",
  "dash.onboard_logo_later": "Télécharger plus tard via Tableau de bord → Marque",
  "dash.onboard_finish_title": "Votre calculateur est prêt !",
  "dash.onboard_finish_desc": "Partagez votre lien unique. Modifiez les paramètres à tout moment.",
  "dash.your_link": "Lien de votre calculateur :",
  "dash.online_card": "Votre carte de visite en ligne.",
  "dash.view_my_calculator": "Voir mon calculateur",
  "dash.upload_thumbnail": "Image personnalisée",
  "dash.reset_to_default": "Réinitialiser",
  "dash.reset_all_thumbnails": "Tout réinitialiser",
  "dash.thumbnail_uploaded": "Image téléchargée !",
  "dash.thumbnail_reset": "Image réinitialisée",
  "dash.thumbnails_reset_all": "Toutes les images réinitialisées",
  "dash.invite_created": "Invitation créée !",
  "dash.invite_error": "Erreur d'invitation",
  "dash.member_removed": "Membre supprimé",
  "dash.link_copied_generic": "Lien copié !",
  "dash.profile_saved": "Profil enregistré !",
  "dash.profile_save_error": "Erreur d'enregistrement",
  "dash.calculator_ready": "🎉 Calculateur prêt !",
  "dash.share_with_clients": "Partagez votre lien.",
  "dash.step_of": "Étape {current} sur {total}",
  "dash.start_inkprice": "Commencer avec InkPrice",
  "laf.title": "Apparence",
  "laf.desc": "Personnalisez l'apparence de votre calculateur public.",
  "laf.color_mode": "Mode couleur",
  "laf.dark_mode": "Mode sombre",
  "laf.light_mode": "Mode clair",
  "laf.background": "Arrière-plan",
  "laf.solid_color": "Couleur unie",
  "laf.image_url": "URL de l'image",
  "laf.preview": "Aperçu",
  "laf.saved": "Apparence enregistrée",
  "laf.presets": "Thèmes premium",
  "laf.font_selector": "Police du calculateur",
  "laf.overlay_opacity": "Opacité de l'overlay",
  "laf.bg_fixed": "Fixe (Parallaxe)",
  "laf.bg_scroll": "Défilement",
  "consult_settings.title": "Paramètres de consultation",
  "consult_settings.desc": "Configurez la prise de contact.",
  "consult_settings.booking_url_label": "URL de réservation",
  "consult_settings.email_label": "E-mail de consultation",
  "consult_settings.channels_title": "Canaux de leads",
  "consult_settings.channels_desc": "Méthodes de contact visibles.",
  "help.min_price": "Prix minimum absolu pour chaque session.",
  "help.hourly_rate": "Votre tarif horaire constitue la base de tous les calculs.",
  "help.multipliers": "Les multiplicateurs ajustent le prix selon le style, la complexité et le placement.",
  "help.sizes": "Définissez la plage de temps pour chaque taille.",
  "help.complexity": "Ajustez les multiplicateurs pour la complexité.",
  "help.body_parts": "Certaines zones nécessitent des multiplicateurs plus élevés.",
  "help.styles": "Activez/désactivez des styles et définissez les multiplicateurs.",
  "help.logo": "Votre logo apparaît sur votre calculateur et dans le répertoire. Max 2 Mo.",
  "help.currency": "La devise affichée sur votre calculateur.",
  "help.calculator_language": "La langue de votre calculateur public.",
  "help.dashboard_language": "La langue de ce tableau de bord — visible uniquement par vous.",
  "profile.mfa_title": "Sécurité du compte",
  "profile.mfa_desc": "Protégez votre compte avec la 2FA.",
  "profile.mfa_enable": "Activer 2FA",
  "profile.mfa_verify": "Vérifier",
  "profile.mfa_active": "Actif",
  "profile.mfa_enabled_success": "✅ 2FA activée !",
  "profile.google_reviews": "Avis Google",
  "profile.google_reviews_desc": "Liez votre profil Google Business.",
  "profile.sync_now": "Synchroniser",
  "profile.sync_success": "Avis Google synchronisés !",
  "landing.faq_title": "FAQ",
  "landing.faq_subtitle": "Tout sur Inkvaluator",
  "landing.faq_q1": "Comment fonctionne le calculateur ?",
  "landing.faq_a1": "Configurez votre tarif et multiplicateurs. Les clients obtiennent une estimation instantanée.",
  "landing.faq_q2": "Inkvaluator est-il gratuit ?",
  "landing.faq_a2": "Oui ! Le plan Free inclut un calculateur complet. Passez en Pro pour plus.",
  "landing.faq_q3": "Comment fonctionne le scanner IA ?",
  "landing.faq_a3": "Téléchargez une photo et l'IA analyse taille, complexité et style en secondes.",
  "landing.faq_q4": "Les clients peuvent-ils me contacter ?",
  "landing.faq_a4": "Oui, via WhatsApp avec un message pré-rempli.",
  "landing.faq_q5": "Support multilingue ?",
  "landing.faq_a5": "17 langues et 13 devises, parfait pour les artistes internationaux.",
  "landing.faq_q6": "Comment obtenir plus de clients ?",
  "landing.faq_a6": "Artistes Pro/Studio sur la carte mondiale et page découverte.",
  "city.title": "Tatoueurs à {city}",
  "city.description": "Trouvez des tatoueurs à {city}.",
  "city.no_artists": "Aucun artiste trouvé à {city}.",
  "city.discover_all": "Tous les artistes",
  "seo.landing_title": "Inkvaluator – Calculateur de Prix de Tatouage",
  "seo.landing_desc": "Calculez le prix de votre tatouage instantanément.",
  "vault.seo_title": "Mon Coffre Tatouage | Inkvaluator",
  "vault.title": "Mon Coffre",
  "vault.title_accent": "Tatouage",
  "vault.desc": "Vos estimations sauvegardées.",
  "vault.empty": "Aucune estimation. Utilisez un calculateur !",
  "vault.save_btn": "Sauvegarder",
  "vault.saved_title": "Sauvegardé !",
  "vault.saved_desc": "Estimation sauvegardée.",
  "success.active": "Actif — tout débloqué",
  "success.setup_whatsapp": "Configurer WhatsApp",
  "success.syncing": "Activation de votre plan…",
  "success.view_details": "Voir les détails",
  "analytics.title": "Analyses",
  "analytics.desc": "Utilisation de votre calculateur",
  "analytics.total_calculations": "Total des calculs",
  "analytics.conversion_rate": "Taux de conversion",
  "analytics.this_month": "Ce mois-ci",
  "analytics.no_data": "Pas encore de données.",
  "studio.title": "Gestion du studio",
  "studio.team": "Équipe",
  "studio.invite": "Inviter un artiste",
  "studio.invite_desc": "Générez un lien d'invitation.",
  "studio.generate_link": "Générer un lien",
  "studio.no_members": "Aucun membre. Invitez des artistes.",
  "color.gold": "Or",
  "color.red": "Rouge",
  "color.blue": "Bleu",
  "color.green": "Vert",
  "color.purple": "Violet",
  "color.pink": "Rose",
  "color.turquoise": "Turquoise",
  "color.orange": "Orange",
  "tip.title": "Recommander un artiste",
  "tip.desc": "Connaissez-vous un tatoueur qui devrait être sur Inkvaluator ?",
  "share.button": "Partager",
  "sw.update_available": "Nouvelle version disponible.",
  "embed.iframe_desc": "Collez ceci dans votre HTML.",
  "embed.floating_desc": "Collez ceci avant le </body>.",
  "embed.verified_badge": "Badge vérifié",
  "embed.badge_desc": "Placez ce badge sur votre site.",
  "compare.title": "Comparaison de prix",
  "compare.disclaimer": "Comparaison basée sur un tatouage standard.",
  "landing.calculate_price": "Calculer le prix",
  "landing.total_value_prefix": "Déjà",
  "landing.total_value_suffix": "en art du tatouage calculé",
  "step.next": "Continuer",
  "step.view_result": "Voir le prix",
  "step.details": "Détails",
  "step.details_desc": "Quel est le niveau de détail de votre design ?",
  "layout.stepper": "Étape par étape",
  "layout.form": "Tout en une fois",
  "calc.view_price_cta": "Voir l'aperçu des prix",
  "calc.rough_estimate": "Estimation approximative",
  "calc.calculated_price": "Prix calculé",
  "calc.save_for_later": "Enregistrer pour plus tard",
  "calc.share": "Partager",
  "calc.more_options": "Plus d'options",
  "calc.compare_configs": "Comparer les configurations",
  "calc.compare_desc": "Comparer deux configurations de prix côte à côte",
  "calc.no_photo": "Aucune photo de référence ajoutée",
  "calc.link_copied": "Lien copié !",
  "whatsapp.add_photo": "Ajoutez votre photo",
  "whatsapp.photos_label": "Photos de référence — max. 3",
  "laf.reset_success": "Thème réinitialisé par défaut",
  "admin.stripe_managed":
    "Les abonnements sont gérés automatiquement via Stripe — les modifications sont visibles après la prochaine synchronisation webhook",
  "calc.view_tattoo_plan": "Voir votre Tattoo Plan ✓",
  "wa.plan_message": "Voici mon Tattoo Plan :",
  "lead.tattoo_plan": "Tattoo Plan",
  "lead.made_with": "Créé avec Inkvaluator",
  "lead.contact_artist": "Contacter {artist}",
  "lead.share": "Partager",
  "lead.share_title": "Mon Tattoo Plan",
  "lead.link_copied": "Lien copié !",
  "lead.deposit_label": "Acompte",
  "lead.pay_deposit": "Payer l'acompte {amount}",
  "lead.deposit_request_title": "Demander un arrangement d'acompte",
  "lead.deposit_request_desc": "Demandez à l'artiste si un acompte de ~{amount} est possible.",
  "lead.deposit_request_wa": "Envoyer la demande via WhatsApp",
  "lead.for_you": "Vous",
  "lead.for_label": "Pour",
  "lead.back_to_calculator": "Retour au calculateur",
  "lead.session_duration": "Durée de séance",
  "lead.session_estimate": "~{hours}h",
  "lead.deposit_wa_msg":
    "Bonjour {artist}, j'ai consulté mon Tattoo Plan sur Inkvaluator. Serait-il possible de convenir d'un acompte de ~{amount} ?\nPlan : {url}",
  "blog.no_translation": "Aucune traduction disponible",
  "flash.tab": "Flash",
  "flash.upload": "Télécharger un flash",
  "flash.title": "Titre",
  "flash.price": "Prix",
  "flash.size": "Taille (cm)",
  "flash.available": "Disponible",
  "flash.featured": "En vedette",
  "flash.limit_used": "sur {max} flash designs utilisés",
  "flash.delete_confirm": "Supprimer le flash ?",
  "flash.reserve_whatsapp": "Réserver via WhatsApp",
  "flash.whatsapp_msg": "Je suis intéressé par votre flash tattoo :",
  "flash.matching_designs": "Flash designs correspondants",
  "flash.only_with_flash": "Uniquement les artistes avec flash",
  "groei.score": "Score du profil",
  "groei.views": "vues du profil",
  "groei.sessions": "sessions calculateur",
  "groei.leads": "leads",
  "groei.this_week": "cette semaine",
  "groei.improve": "Améliorez votre profil",
  "groei.add_photos": "Ajoutez plus de photos portfolio",
  "groei.add_whatsapp": "Configurez votre numéro WhatsApp",
  "groei.add_bio": "Écrivez une bio plus longue",
  "groei.add_avatar": "Téléchargez une photo de profil",
  "groei.add_rate": "Définissez votre tarif horaire",
  "groei.add_city": "Ajoutez votre ville",
  "groei.get_leads": "Partagez votre calculateur pour obtenir des leads",
  "groei.collapse": "Réduire",
  "groei.expand": "Développer",
  "groei.tip_portfolio": "Les artistes avec 6+ photos obtiennent 3x plus de sessions",
  "groei.tip_whatsapp": "Les clients avec un bouton WhatsApp convertissent 5x mieux",
  "groei.tip_leads": "Partagez votre lien calculateur pour recevoir votre premier lead",
  "groei.tip_avatar": "Une photo de profil augmente votre crédibilité",
  "groei.tip_action_photos": "Ajouter des photos",
  "groei.tip_action_whatsapp": "Configurer WhatsApp",
  "groei.tip_action_share": "Partager le calculateur",
  "groei.tip_action_avatar": "Télécharger une photo",
  "milestone.eerste_lead": "Premier lead reçu !",
  "milestone.tien_leads": "10 leads atteints !",
  "milestone.profiel_compleet": "Profil 100% complet !",
  "milestone.eerste_flash": "Premier flash design téléchargé !",
  "wa.name_required": "Le nom est requis",
  "wa.plan_message_full": "Voir mon Tattoo Plan complet : {url}",

  // Landing v2
  "landing.seo_title": "Calculer les prix de tatouage — Trouvez des artistes dans votre ville | Inkvaluator",
  "landing.seo_description":
    "Calculez instantanément le coût de votre tatouage. Trouvez des tatoueurs près de chez vous. Calculateur de prix gratuit pour les studios en Europe.",
  "landing.hero_eyebrow": "",
  "landing.hero_title_line1": "Combien coûte",
  "landing.hero_title_line2": "votre",
  "landing.hero_title_line3": "tatouage ?",
  "landing.hero_subtitle":
    "Calculez une estimation juste, trouvez un artiste près de chez vous et contactez-le directement via WhatsApp.",
  "landing.hero_search_placeholder": "Rechercher par ville ou studio...",
  "landing.hero_popular": "Populaire",
  "landing.hero_cta_primary": "Calculer votre prix",
  "landing.hero_cta_secondary": "Voir les artistes",
  "landing.artist_card_eyebrow": "Pour les tatoueurs",
  "landing.artist_card_title": "Mettez votre studio sur la carte",
  "landing.artist_card_body":
    "Votre studio est probablement déjà listé sur Inkvaluator. Revendiquez votre profil en deux minutes et recevez des demandes directes de clients.",
  "landing.artist_card_stat_studios": "studios",
  "landing.artist_card_stat_cities": "villes",
  "landing.artist_card_stat_spots": "Places Founder",
  "landing.artist_card_cta": "Revendiquer votre profil gratuitement",
  "landing.artist_card_founder_note": "les 100 premiers studios obtiennent Pro gratuitement",
  "landing.proof_studios": "Studios de tatouage",
  "landing.proof_cities": "Villes en Europe",
  "landing.proof_leads": "Clients aidés",
  "landing.proof_rating": "Note moyenne",
  "landing.plan_eyebrow": "Votre Tattoo Plan",
  "landing.plan_title": "Pas de question de prix. Un plan.",
  "landing.plan_body":
    "Inkvaluator génère un Tattoo Plan personnel avec votre style, taille, emplacement et estimation de prix. Envoyez-le directement à un artiste — il sait exactement ce que vous voulez.",
  "landing.plan_step1": "Remplissez le calculateur — style, taille, niveau de détail",
  "landing.plan_step2": "Votre Tattoo Plan est créé automatiquement",
  "landing.plan_step3": "Envoyez le plan via WhatsApp à un artiste",
  "landing.plan_cta": "Créer votre Tattoo Plan",
  "landing.plan_card_title": "Tattoo Plan",
  "landing.plan_card_style": "Style",
  "landing.plan_card_size": "Taille",
  "landing.plan_card_detail": "Niveau de détail",
  "landing.plan_card_detail_val": "Élevé",
  "landing.plan_card_placement": "Emplacement",
  "landing.plan_card_placement_val": "Bras supérieur",
  "landing.plan_card_estimate": "Prix estimé",
  "landing.plan_card_send": "Envoyer à l'artiste via WhatsApp",
  "landing.how_eyebrow": "Comment ça marche",
  "landing.how_title": "De l'idée au rendez-vous",
  "landing.how_subtitle": "Trois étapes. Pas de compte. Résultats instantanés.",
  "landing.how_step1_title": "Calculer le prix",
  "landing.how_step1_desc":
    "Choisissez le style, la taille et le niveau de détail. Obtenez une estimation juste basée sur des données réelles de votre région.",
  "landing.how_step1_cta": "Démarrer le calculateur",
  "landing.how_step2_title": "Trouver un artiste",
  "landing.how_step2_desc": "Parcourez les artistes de votre ville. Filtrez par style, prix ou note.",
  "landing.how_step2_cta": "Voir les artistes",
  "landing.how_step3_title": "Contactez directement",
  "landing.how_step3_desc":
    "Envoyez votre Tattoo Plan via WhatsApp. L'artiste sait exactement ce que vous voulez et combien cela devrait coûter.",
  "landing.map_eyebrow": "Couverture",
  "landing.map_title": "Artistes près de chez vous",
  "landing.map_subtitle": "Studios dans des villes. Pin doré = profil revendiqué avec calculateur actif.",
  "landing.map_loading": "Chargement de la carte...",
  "landing.map_legend_claimed": "Revendiqué — calculateur actif",
  "landing.map_legend_unclaimed": "Non revendiqué",
  "landing.styles_eyebrow": "Styles",
  "landing.styles_title": "Trouvez votre style",
  "landing.styles_traditional": "Lignes épaisses, couleurs vives",
  "landing.styles_realism": "Détail photoréaliste",
  "landing.styles_japanese": "Motifs japonais classiques",
  "landing.styles_fineline": "Lignes délicates et minimalistes",
  "landing.styles_blackwork": "Noir pur, impact maximum",
  "landing.styles_watercolor": "Fluide et coloré",
  "landing.artist_eyebrow": "Êtes-vous tatoueur ?",
  "landing.artist_title": "Mettez votre studio sur la carte",
  "landing.artist_body":
    "Votre studio est probablement déjà listé sur Inkvaluator. Revendiquez votre profil en deux minutes via vérification téléphonique et recevez des leads de clients avec un Tattoo Plan prêt.",
  "landing.artist_perk1": "Les clients envoient leur Tattoo Plan — vous savez instantanément ce qu'ils veulent",
  "landing.artist_perk2": "Recevez un acompte à chaque demande via la plateforme",
  "landing.artist_perk3": "Grandissez organiquement via la carte de ville et les moteurs de recherche",
  "landing.artist_perk4": "Les 100 premiers studios obtiennent Pro gratuitement — pas de coûts cachés",
  "landing.artist_cta_claim": "Revendiquer votre profil",
  "landing.artist_cta_register": "Créer un compte",
  "landing.founder_subtitle": "Exclusif pour les 100 premiers studios",
  "landing.founder_perk1": "Package Pro gratuit tant que nous grandissons",
  "landing.founder_perk2": "Influence directe sur les nouvelles fonctionnalités",
  "landing.founder_perk3": "Priorité dans les résultats de recherche",
  "landing.founder_perk4": "Contact personnel avec le développeur",
  "landing.founder_claimed": "revendiqué",
  "landing.cities_eyebrow": "Villes",
  "landing.cities_title": "Studios de tatouage par ville",
  "landing.cities_all": "Toutes les villes",
  "landing.cities_studios": "studios",
  "landing.cities_from": "à partir de",
  "landing.cities_claimed": "revendiqué",
  "landing.blog_eyebrow": "Base de connaissances",
  "landing.blog_title": "Tout sur les prix de tatouage",
  "landing.blog_all": "Tous les articles",
  "landing.blog_read": "Lire la suite",
  "landing.final_eyebrow": "Prêt ?",
  "landing.final_title_line1": "Calculez combien votre",
  "landing.final_title_line2": "tatouage coûte",
  "landing.final_subtitle": "Pas de compte nécessaire. Gratuit. Résultats instantanés.",
  "landing.final_cta_primary": "Démarrer le calculateur",
  "landing.final_cta_secondary": "Revendiquer votre studio",
  // Deposit settings
  "settings.deposit_title": "Acompte",
  "settings.deposit_desc": "Demandez aux clients de verser un acompte lors de la soumission d'une demande.",
  "settings.deposit_none": "Aucun",
  "settings.deposit_percentage": "Pourcentage",
  "settings.deposit_fixed": "Montant fixe",
  "settings.deposit_percentage_hint": "du montant total",
  "settings.deposit_fixed_hint": "acompte fixe",
  "settings.deposit_preview_label": "Le client voit",
  "settings.deposit_preview_pct": "Acompte : {value}% du montant total",
  // ─── SEO Tattoo pages ───────────────────────────────
  "nav.studios": "Studios",
  "nav.studios_count": "studios",
  "city.stats_studios": "Studios à {city}",
  "city.stats_on_inkvaluator": "Sur Inkvaluator",
  "city.size_small": "Petit (5–10 cm)",
  "city.size_medium": "Moyen (10–20 cm)",
  "city.size_large": "Grand (20 cm+)",
  "city.size_small_ex": "Texte, minimaliste, petit symbole",
  "city.size_medium_ex": "Portrait, fleurs, géométrique",
  "city.size_large_ex": "Sleeve, dos, jambe entière",
  "city.time_1_2": "1–2 h",
  "city.time_2_5": "2–5 h",
  "city.time_5plus": "5+ h",
  "city.most_popular": "Le plus demandé",
  "city.price_indication_note": "Estimation basée sur les moyennes nationales",
  "city.price_factors_title": "Facteurs qui déterminent le prix",
  "city.factor_style": "Style",
  "city.factor_style_desc": "Le réalisme et la couleur coûtent plus que le blackwork ou fine-line",
  "city.factor_placement": "Emplacement",
  "city.factor_placement_desc": "Les côtes et les mains sont techniquement plus difficiles que le bras ou le dos",
  "city.factor_detail": "Niveau de détail",
  "city.factor_detail_desc": "Plus de détail = plus de temps = prix plus élevé",
  "city.factor_experience": "Expérience de l'artiste",
  "city.factor_experience_desc": "Les artistes seniors demandent plus mais offrent une qualité plus constante",
  "city.updated": "Mis à jour",
  "city.view_all_artists": "Voir tous les artistes",
  "city.calc_button_short": "Calculer le prix",
  "city.view_button": "Voir →",
  "country.h1": "Studios de tatouage en {country}",
  "country.coming_soon": "Bientôt disponible",
  "country.popular_styles": "Styles populaires en {country}",
  "style.h1": "Tatouage {style} à {city}",
  "style.subtitle": "{count} artistes à {city} spécialisés en {style}. Calculez directement votre prix.",
  "style.artists_h2": "Artistes {style} à {city}",
  "style.calc_button": "Calculer prix {style} →",
  "style.empty": "Aucun artiste {style} revendiqué à {city} pour l'instant.",
  "style.all_studios": "Tous les studios de tatouage à {city}",
  "style.page_title": "Tatouage {style} à {city} — artistes & prix | Inkvaluator",
  "style.page_desc": "{count} artistes {style} à {city}. Comparez les portfolios et calculez votre prix.",
  "studio.reviews": "avis",
  "studio.claim_owner": "Êtes-vous le propriétaire de {name}?",
  "studio.claim_desc": "Revendiquez ce profil gratuitement. Vous recevrez des demandes directes de clients à {city}.",
  "studio.claim_cta": "Revendiquer ce profil gratuitement →",
  "studio.claim_cta_short": "Revendiquer votre studio →",
  "studio.view_with_calc": "Voir les studios avec calculateur",
  "studio.artists_nearby": "Artistes à {city} avec calculateur en ligne",
  "studio.deposit": "acompte",
  "studio.meta_desc": "{name} à {city}. Voir les avis, les prix et revendiquer ce profil.",
  "tattoo_index.search_placeholder": "Rechercher une ville ou un tatoueur...",
  "tattoo_index.h1": "Trouver un studio de tatouage près de chez vous",
  "map.legend_pro": "Pro & Studio",
  "map.legend_claimed": "Revendiqué",
  "map.legend_unclaimed": "Non revendiqué",
  "map.legend_on_inkvaluator": "Sur Inkvaluator",
  "map.view_studio": "Voir le studio →",
  "map.list_pro_section": "✦ Pro & Studio",
  "map.list_claimed_section": "Revendiqué",
  "map.list_unclaimed_section": "Non revendiqué",
  "map.list_count_singular": "studio visible",
  "map.list_count_plural": "studios visibles",
  "map.list_scroll_hint": "Déplacez la carte pour explorer",
  "map.list_empty": "Aucun studio dans cette zone",
  "map.list_empty_hint": "Dézoomez ou déplacez la carte",
  "map.list_full_profile": "Profil complet",
  "city.map_section_title": "Studios de tatouage à {city}",
  "city.faq_section_title": "Questions fréquentes sur les tatouages à {city}",
  "settings.deposit_preview_fixed": "Acompte : €{value} à la réservation",
};

// ─── SPANISH (complete) ────────────────────────────────────────
const es: Record<string, string> = {
  ...en,
  "app.tagline": "Calcula el precio de tu tatuaje",
  back: "Volver",
  save: "Guardar",
  copy: "Copiar",
  copied: "¡Copiado!",
  loading: "Cargando...",
  error: "Error",
  optional: "opcional",
  "landing.hero": "Calcula el precio de",
  "landing.hero_accent": "tu Tatuaje",
  "landing.subtitle": "Introduce el código de tu tatuador para obtener una estimación personalizada.",
  "landing.start": "Iniciar calculadora",
  "landing.invalid_code": "Introduce un código válido",
  "landing.or": "o",
  "landing.artist_cta": "¿Eres tatuador? Crea tu propia calculadora.",
  "landing.hero_title": "Calcula el precio de",
  "landing.hero_title_accent": "tu Tatuaje",
  "landing.hero_desc": "Descubre en segundos cuánto cuesta tu próximo tatuaje. Pruébalo directamente.",
  "landing.try_it": "¡Pruébalo!",
  "landing.estimated_price": "Precio estimado",
  "landing.your_rates": "Tus tarifas",
  "landing.rates_desc": "Mueve los controles y observa cómo cambia el precio",
  "landing.hourly_rate": "Tarifa por hora",
  "landing.start_cost": "Costes fijos iniciales",
  "landing.size_label": "Tamaño",
  "landing.complexity_label": "Complejidad",
  "landing.body_placement": "Ubicación corporal",
  "landing.find_artist": "Encuentra tu",
  "landing.find_artist_accent": "artista perfecto",
  "landing.nearby_soon": "Pronto aparecerán artistas cerca de ti.",
  "landing.discover_all": "Descubrir todos los artistas",
  "landing.artist_question": "¿Eres artista o estudio?",
  "landing.view_options": "Ver opciones y precios",
  "landing.start_free": "Empezar gratis",
  "landing.discover": "Descubrir",
  "landing.login": "Iniciar sesión",
  "landing.founder_desc": "50% de descuento para los primeros 50 registros",
  "landing.claim_now": "Reclámalo ahora →",
  // ── Landing Page Redesign keys ──
  "landing.hero_eyebrow_artists": "Artistas",
  "landing.hero_eyebrow_prices": "Precios",
  "landing.hero_eyebrow_styles": "Estilos",
  "landing.hero_title_new_1": "Encuentra a tu",
  "landing.hero_title_new_2": "artista",
  "landing.hero_title_new_3": "de tatuajes ideal",
  "landing.hero_subtitle_new": "Compara portfolios, estilos y precios. Encuentra al mejor artista cerca de ti.",
  "landing.value_eyebrow": "Tu viaje hacia el tatuaje",
  "landing.value_title": "Todo lo que necesitas para tu próximo tatuaje",
  "landing.value_subtitle": "Ya sea el primero o el vigésimo: hacemos que el proceso sea sencillo.",
  "landing.value_card1_title": "Encuentra a tu artista",
  "landing.value_card1_desc": "Explora artistas verificados por estilo, ubicación y precio. Compara portfolios y reserva con confianza.",
  "landing.value_card1_stat": "artistas",
  "landing.value_card2_title": "Calcula tu precio",
  "landing.value_card2_desc": "Obtén una estimación realista según tamaño, estilo, detalle y ubicación. Sin sorpresas.",
  "landing.value_card2_stat": "Estimación gratis",
  "landing.value_card3_title": "Descubre estilos",
  "landing.value_card3_desc": "Desde Traditional potente hasta Fine Line delicado. Descubre qué estilo encaja contigo con ejemplos reales.",
  "landing.value_card3_stat": "estilos",
  "landing.value_artist_cta": "¿Eres tatuador? Reclama tu perfil gratis →",
  "landing.styles_title_new": "Encuentra tu estilo",
  "landing.styles_subtitle": "Desde Traditional potente hasta Fine Line delicado. Descubre la estética que más va contigo.",
  "landing.styles_all_cta": "Ver todos los estilos",
  "landing.reviews_eyebrow": "Experiencias",
  "landing.reviews_title": "Lo que dicen los clientes",
  "landing.testimonial_1": "La calculadora de precios me dio una idea clara de qué esperar. El artista cerca de mí encajaba perfecto con mi estilo.",
  "landing.testimonial_2": "Era mi primer tatuaje y estaba nervioso. La comparación de estilos y la estimación del precio me hicieron sentir preparado.",
  "landing.testimonial_3": "Comparé 3 estudios con Inkvaluator. En ningún otro sitio hay tanta transparencia sobre precios y estilos.",
  "calc.estimate": "Precio estimado",
  "calc.size": "Tamaño",
  "calc.size_desc": "¿Qué tamaño tendrá tu tatuaje?",
  "calc.complexity": "Complejidad",
  "calc.complexity_desc": "¿Qué tan detallado es el diseño?",
  "calc.body_part": "Parte del cuerpo",
  "calc.body_part_desc": "Haz clic en la parte del cuerpo donde irá tu tatuaje",
  "calc.style": "Estilo",
  "calc.style_desc": "¿Qué estilo de tatuaje quieres?",
  "calc.budget": "Presupuesto",
  "calc.budget_desc": "¿Cuál es tu presupuesto máximo?",
  "calc.budget_placeholder": "Ej. 300",
  "calc.get_estimate": "Obtén una estimación realista de",
  "calc.no_artist": "No se encontró ningún artista con este código o enlace.",
  "calc.back_home": "Volver al inicio",
  "calc.artist_login": "¿Artista? Inicia sesión",
  "calc.add_color": "¿Añadir color?",
  "calc.color_surcharge": "+20% recargo por trabajo a color",
  "breakdown.title": "Desglose del precio",
  "breakdown.labor": "Mano de obra (tiempo)",
  "breakdown.est_hours": "Horas estimadas",
  "breakdown.subtotal": "Subtotal",
  "breakdown.complexity": "Complejidad",
  "breakdown.placement": "Ubicación",
  "breakdown.style": "Estilo",
  "breakdown.design_cost": "Coste del diseño",
  "breakdown.deposit": "Señal",
  "breakdown.total_range": "Rango total",
  "breakdown.disclaimer":
    "Esta es una estimación, no un precio final. El precio definitivo se determinará tras una consulta con el artista.",
  "breakdown.total_indication": "Indicación total",
  "breakdown.required_deposit": "Señal requerida",
  "breakdown.hours_unit": "horas",
  "faq.title": "Preguntas frecuentes",
  "dash.welcome": "Bienvenido a Inkvaluator",
  "dash.logout": "Cerrar sesión",
  "dash.saved": "¡Guardado!",
  "dash.save_changes": "Guardar cambios",
  "dash.cancel": "Cancelar",
  "dash.add": "Añadir",
  "dash.delete": "Eliminar",
  "dash.previous": "Anterior",
  "dash.next": "Siguiente",
  "tab.basic": "Básico",
  "tab.sizes": "Tamaños",
  "tab.complexity": "Complejidad",
  "tab.body_parts": "Partes del cuerpo",
  "tab.styles": "Estilos",
  "basic.title": "Tarifa base",
  "basic.min_price": "Precio mínimo",
  "basic.hourly_rate": "Tarifa por hora",
  "basic.daily_rate": "Tarifa diaria",
  "basic.design_cost": "Coste del diseño",
  "basic.fixed": "Fijo",
  "basic.hourly": "Por hora",
  "basic.deposit": "Señal",
  "size.mini": "Mini (< 5 cm)",
  "size.small": "Pequeño (5-10 cm)",
  "size.medium": "Mediano (10-20 cm)",
  "size.large": "Grande (20-35 cm)",
  "size.xl": "Extra Grande (35+ cm)",
  "complexity.simple": "Simple",
  "complexity.medium": "Medio",
  "complexity.detailed": "Detallado",
  "body.arm": "Brazo (general)",
  "body.forearm": "Antebrazo",
  "body.upper_arm": "Brazo superior",
  "body.leg": "Pierna (general)",
  "body.thigh": "Muslo",
  "body.calf": "Pantorrilla",
  "body.back": "Espalda",
  "body.chest": "Pecho",
  "body.ribs": "Costillas",
  "body.stomach": "Abdomen",
  "body.hand": "Mano",
  "body.foot": "Pie",
  "body.neck": "Cuello",
  "body.face": "Cara",
  "body.shoulder": "Hombro",
  "body.hip": "Cadera",
  "body.finger": "Dedo",
  "body.other": "Otro",
  "body.selected": "Seleccionado",
  "body.front": "Frente",
  "body.back_view": "Espalda",
  "body.front_view": "Frente",
  "body.back_view_btn": "Espalda",
  "style.black_grey": "Negro y Gris",
  "style.full_color": "Color completo",
  "style.realism": "Realismo",
  "style.fine_line": "Línea fina / Minimal",
  "style.traditional": "Tradicional",
  "style.neo_traditional": "Neo Tradicional",
  "style.watercolor": "Acuarela",
  "style.tribal": "Tribal",
  "style.japanese": "Japonés",
  "style.geometric": "Geométrico",
  "style.lettering": "Lettering",
  "style.script": "Caligrafía",
  "style.dotwork": "Dotwork",
  "style.mandala": "Mandala",
  "style.blackwork": "Blackwork",
  "style.illustrative": "Ilustrativo",
  "style.surrealism": "Surrealismo",
  "style.ornamental": "Ornamental",
  "style.micro_realism": "Micro Realismo",
  "style.trash_polka": "Trash Polka",
  "style.other": "Otro",
  "consult.title": "¿Listo para empezar?",
  "consult.book": "Reservar consulta",
  "pdf.title": "Estimación de precio",
  "pdf.download": "Descargar PDF",
  "upgrade.choose": "Elige tu",
  "upgrade.plan_word": "Plan",
  "upgrade.per_month": "/mes",
  "upgrade.monthly": "Mensual",
  "upgrade.yearly": "Anual",
  "upgrade.mo": "mes",
  "upgrade.year": "año",
  "billing.expires": "Expira",
  "billing.change": "Cambiar",
  "billing.next_invoice": "Próxima factura",
  "billing.invoice_history": "Historial de facturas",
  "upgrade.perfect_start": "Perfecto para empezar",
  "upgrade.everything_pro": "Todo para profesionales",
  "upgrade.for_studios": "Para estudios de tatuaje & equipos",
  "upgrade.upgrade_pro": "Mejorar a Pro",
  "upgrade.upgrade_studio": "Mejorar a Studio",
  "upgrade.discount": "descuento",
  "upgrade.cancel_anytime_long": "Cancela en cualquier momento. Sin costes ocultos.",
  "auth.login": "Iniciar sesión",
  "auth.register": "Registrarse",
  "auth.submit_login": "Iniciar sesión",
  "auth.submit_register": "Crear cuenta",
  "discover.title": "Descubrir",
  "discover.title_accent": "Artistas",
  "discover.search_placeholder": "Buscar por ciudad, artista o estudio...",
  "discover.no_artists": "No se encontraron artistas",
  "discover.view_calculator": "Ver calculadora",
  "footer.tagline": "La calculadora de precios de tatuajes más inteligente para artistas y clientes.",
  "footer.links": "Enlaces",
  "footer.discover_artists": "Descubrir artistas",
  "footer.artist_login": "Login artista",
  "profile.tab_portfolio": "Portfolio",
  "profile.tab_studio": "Studio",
  "profile.tab_book": "Réserver",
  "profile.tab_calculator": "Estimation",
  "profile.no_portfolio": "Aucune image de portfolio ajoutée pour l'instant.",
  "profile.no_portfolio_hint": "L'artiste peut configurer cela via le tableau de bord.",
  "profile.studio_status": "Statut du studio",
  "profile.open_now": "Ouvert",
  "profile.closed": "Fermé",
  "profile.studio_rules": "Règles du studio",
  "rules.deposit_title": "Acompte",
  "rules.deposit_body": "Un acompte de 150 € est requis pour confirmer votre rendez-vous. Ce montant sera déduit du prix total le jour de la séance.",
  "rules.cancellation_title": "Annulation",
  "rules.cancellation_body": "L'annulation est gratuite jusqu'à 48 heures à l'avance. Les annulations dans les 48 heures ou les absences entraînent la perte de l'acompte.",
  "rules.guests_title": "Accompagnants",
  "rules.guests_body": "Un maximum d'1 accompagnant par client est le bienvenu. Les accompagnants restent dans le salon pendant la séance.",
  "rules.age_title": "Âge & Pièce d'identité",
  "rules.age_body": "Nous tatouons uniquement les personnes de 18 ans et plus. Une pièce d'identité valide est obligatoire.",
  "rules.health_title": "Santé",
  "rules.health_body": "Venez reposé et bien nourri à votre rendez-vous. Signalez lors de la réservation toute condition médicale, médicament ou allergie pertinente.",
  "rules.coverup_title": "Cover-ups",
  "rules.coverup_body": "Les cover-ups sont possibles après consultation préalable. Envoyez toujours des photos du tatouage existant avant le rendez-vous.",
  "rules.design_title": "Design & modifications",
  "rules.design_body": "Le design final sera présenté le jour de la séance. De petits ajustements sont possibles, des modifications majeures peuvent entraîner un nouveau rendez-vous.",
  "rules.aftercare_title": "Soins après-séance",
  "rules.aftercare_body": "Suivez attentivement les instructions de soins que vous recevez après la séance. Nous ne sommes pas responsables des conséquences d'un soin inapproprié.",
  "profile.facilities": "Équipements",
  "profile.price_indication": "Indication de prix",
  "profile.hourly_rate_label": "/h tarif horaire",
  "profile.directions": "Itinéraire",
  "profile.day_mon": "Lundi",
  "profile.day_tue": "Mardi",
  "profile.day_wed": "Mercredi",
  "profile.day_thu": "Jeudi",
  "profile.day_fri": "Vendredi",
  "profile.day_sat": "Samedi",
  "profile.day_sun": "Dimanche",
  "profile.facility_parking": "Parking",
  "profile.facility_wheelchair": "Accessible",
  "profile.facility_private_room": "Salle privée",
  "profile.facility_wifi": "WiFi",
  "profile.facility_card_payment": "Paiement par carte",
  "profile.facility_drinks": "Boissons",
  "profile.facility_sterile_cert": "Cert. stérilité",
  "profile.facility_child_friendly": "Adapté aux enfants",
  "profile.facility_cover_up": "Cover-up",
  "profile.facility_custom_design": "Design sur mesure",
  "profile.facility_same_day": "Le jour même",
  "profile.size_small": "Petit · < 10 cm",
  "profile.size_medium": "Moyen · 10–20 cm",
  "profile.size_large": "Grand · 20+ cm",
  "profile.book_appointment": "Prendre rendez-vous",
  "profile.book_desc": "Choisissez une date et un créneau horaire disponible. Vous recevrez une confirmation par e-mail.",
  "profile.earliest_slot": "Prochain créneau disponible :",
  "profile.choose_slot": "Choisir une date et un créneau",
  "profile.calculator": "Calculateur",
  "profile.estimate": "Estimation",
  "profile.back": "Retour",
  "profile.next": "Suivant",
  "profile.view_price": "Voir le prix",
  "profile.all_reviews_google": "Voir tous les avis sur Google",
  "profile.write_review": "Écrire un avis",
  "profile.reviews": "avis",
  "profile.not_found_title": "Artiste introuvable",
  "profile.not_found_desc": "Ce profil n'existe plus. Trouvez un artiste près de chez vous.",
  "profile.find_studio": "Trouver un studio de tatouage →",
  "profile.studio_artists": "Artistes de ce studio",
  "profile.artists_count": "artistes",
  "profile.calc_price": "Calculer le prix →",
  "profile.recent_works": "Œuvres récentes",
  "profile.works_count": "œuvres",
  "profile.all_filter": "Tout",
  "profile.navigate_to": "Naviguer vers",
  "profile.price_calculator": "Calculateur de prix",
  "profile.or_whatsapp": "Ou envoyez un message WhatsApp",
  "profile.schedule_appointment": "Planifier un rendez-vous",
  "profile.per_hour": "/h",
  "profile.artist_label": "Artiste",
  "profile.tattoo_artist": "Tatoueur",
  "profile.book_label": "Réserver",
  "profile.open_maps": "Ouvrir la carte",
  "profile.live_traffic": "Trafic en direct",
  "profile.find_on_google": "Rechercher sur Google",
  "booking.title": "Réserver un rendez-vous chez {artist}",
  "booking.confirmed_title": "Réservation demandée !",
  "booking.choose_date": "Choisissez une date disponible",
  "booking.choose_time": "Choisissez un créneau horaire",
  "booking.other_day": "Choisir un autre jour",
  "booking.other_slot": "Autre créneau",
  "booking.name": "Nom",
  "booking.name_placeholder": "Votre nom",
  "booking.email": "Adresse e-mail",
  "booking.email_placeholder": "votre@email.fr",
  "booking.phone": "Téléphone / WhatsApp",
  "booking.style": "Style de tatouage",
  "booking.style_placeholder": "Choisir un style...",
  "booking.no_preference": "— Pas de préférence —",
  "booking.description": "Description du tatouage",
  "booking.description_placeholder": "Décrivez votre tatouage souhaité...",
  "booking.reference_photo": "Photo de référence (optionnel)",
  "booking.upload_photo": "Télécharger une photo",
  "booking.deposit_notice": "Un acompte de €{amount} est requis à la réservation. L'artiste vous contactera pour les instructions de paiement.",
  "booking.fill_required": "Veuillez remplir le nom et l'e-mail",
  "booking.submit": "Demander un rendez-vous",
  "booking.success_title": "Demande envoyée !",
  "booking.success_desc": "{artist} recevra votre demande et confirmera le rendez-vous dès que possible. Vous recevrez une confirmation à",
  "booking.close": "Fermer",
  "booking.available": "Disponible",
  "booking.unavailable": "Indisponible",
  "footer.privacy": "Política de privacidad",
  "footer.language_label": "Idioma & App",
  "footer.pwa_badge": "Optimizado PWA (clic para info)",
  "footer.all_rights": "Todos los derechos reservados.",
  "footer.install_title": "Instalar",
  "footer.install_ok": "¡Entendido!",
  "footer.install_desc": "Usa Inkvaluator como una app real en tu teléfono.",
  "footer.install_ios_title": "iPhone / iPad (iOS)",
  "footer.install_ios_desc": 'Toca el botón Compartir en la parte inferior y elige "Añadir a pantalla de inicio".',
  "footer.install_android_title": "Android",
  "footer.install_android_desc": 'Toca los tres puntos arriba a la derecha y elige "Instalar app".',
  "success.welcome": "¡Bienvenido al Club,",
  "success.go_dashboard": "Ir al panel",
  "landing.how_it_works_cta": "Descubre cómo funciona para tu estudio",
  "footer.terms": "Términos de servicio",
  "footer.support": "Soporte",
  "footer.how_it_works": "¿Cómo funciona?",
  "hiw.seo_title": "¿Cómo funciona? – Inkvaluator",
  "hiw.seo_desc":
    "Descubre cómo Inkvaluator ayuda a los tatuadores con estimaciones automáticas, leads inteligentes y branding profesional.",
  "hiw.hero_title": "Enfócate en la tinta, no en la factura",
  "hiw.hero_p1":
    "¿Pasas más tiempo respondiendo DMs sobre precios que dibujando tu próxima obra maestra? Seamos honestos: eres tatuador, no agente de servicio al cliente.",
  "hiw.hero_p2":
    'Cada pregunta "¿Cuánto cuesta?" que tienes que responder manualmente es tiempo que no dedicas a tu arte.',
  "hiw.hero_p3":
    "Inkvaluator es el asistente digital que filtra tu negocio. Mientras estás en la zona, la herramienta calcula estimaciones precisas, recopila toda la info y envía una solicitud lista directamente a tu WhatsApp.",
  "hiw.hero_p4":
    'Se acabaron los "curiosos de precios" sin presupuesto. Solo leads serios, presupuestos profesionales y un branding que muestra que te tomas en serio tu oficio.',
  "hiw.hero_cta": "¿Listo para recuperar tu tiempo de trabajo?",
  "hiw.faq_title": "Preguntas frecuentes",
  "hiw.faq_cat1": "Lo básico: ¿cómo me ayuda?",
  "hiw.faq_q1": "¿Es otra app complicada?",
  "hiw.faq_a1":
    "No. Es una calculadora ultrarrápida que pones en tu bio de Instagram o integras en tu web. Configuras tus reglas una vez y la herramienta hace el resto.",
  "hiw.faq_q2": "¿Cómo me ahorra tiempo concretamente?",
  "hiw.faq_a2":
    'En lugar de preguntar diez veces: "¿Qué tamaño? ¿Dónde? ¿Qué estilo?", recibes un mensaje de WhatsApp con todo completado, incluyendo la foto de referencia y el precio aceptado.',
  "hiw.faq_cat2": "Precios y configuración",
  "hiw.faq_q3": "¿Puedo fijar mis propios precios?",
  "hiw.faq_a3":
    "Absolutamente. Tú eres el jefe. Estableces tu tarifa por hora y determinas lo que cobras por diferentes tamaños y complejidades.",
  "hiw.faq_q4": "¿Y si un tatuaje es mucho más complejo de lo normal?",
  "hiw.faq_a4":
    "Nuestro escáner IA integrado reconoce los detalles en una foto y ajusta la estimación de precio según la complejidad.",
  "hiw.faq_cat3": "Apariencia y branding",
  "hiw.faq_q5": "¿No parece una herramienta genérica?",
  "hiw.faq_a5":
    "Para nada. Con el plan Studio la herramienta es completamente de marca blanca: tu logo, tus colores y tu estilo. Hay 8 temas profesionales listos.",
  "hiw.faq_q6": "¿Qué tan profesional se ve para mi cliente?",
  "hiw.faq_a6":
    "Muy profesional. El cliente puede descargar el presupuesto como un PDF elegante, lo que genera confianza.",
  "hiw.faq_cat4": "Crecimiento y alcance",
  "hiw.faq_q7": "Quiero más clientes de mi zona, ¿esto ayuda?",
  "hiw.faq_a7":
    "¡Sí! Inkvaluator tiene un mapa mundial interactivo. Los clientes pueden buscar por país, provincia y ciudad.",
  "hiw.backend_title": "Tras bambalinas: el poder del backend",
  "hiw.backend_intro":
    "Para los amantes de la tecnología: Inkvaluator está lleno de funciones avanzadas que llevan tu estudio al siguiente nivel.",
  "hiw.card_pricing_title": "Gestión dinámica de precios",
  "hiw.card_pricing_text":
    "Ajusta precios e IDs de Stripe desde el módulo admin sin tocar código. Todo se sincroniza al instante.",
  "hiw.card_ai_title": "Integración AI Vision",
  "hiw.card_ai_text": "El escáner IA analiza patrones, uso de color y complejidad para calcular horas de trabajo.",
  "hiw.card_stripe_title": "Portal de Cliente Stripe",
  "hiw.card_stripe_text":
    "Sistema de suscripción totalmente automatizado. Actualiza, descarga facturas o gestiona tu suscripción de forma segura.",
  "hiw.card_analytics_title": "Panel de análisis",
  "hiw.card_analytics_text": "Obtén información profunda sobre tus conversiones y potencial de ingresos.",
  "hiw.card_blog_title": "Blog y SEO CMS",
  "hiw.card_blog_text": "Sistema integrado para publicar artículos que mejoran tu visibilidad en Google.",
  "hiw.card_global_title": "Arquitectura global",
  "hiw.card_global_text": "Soporte multilingüe completo con conservación del idioma preferido del cliente.",
  "hiw.card_mobile_title": "Listo para móvil (PWA)",
  "hiw.card_mobile_text":
    "Inkvaluator es una Progressive Web App (PWA). Instala la herramienta directamente en tu pantalla de inicio desde el navegador. Ultra rápido, seguro y sin ocupar espacio de almacenamiento.",
  "hiw.cta_title": "¿A qué esperas?",
  "hiw.cta_accent": "Deja de calcular, empieza a crear.",
  "hiw.cta_button": "Prueba Inkvaluator gratis",
  "tos.seo_title": "Términos de servicio | Inkvaluator",
  "tos.seo_desc": "Lee los términos de servicio de Inkvaluator.",
  "tos.title": "Términos de servicio",
  "tos.updated": "Última actualización:",
  "tos.s1_title": "1. Definiciones",
  "tos.s1_platform": "Plataforma: Inkvaluator, una plataforma SaaS disponible en inkvaluator.com.",
  "tos.s1_user": "Usuario / Artista: Cualquier tatuador o estudio que cree una cuenta en la Plataforma.",
  "tos.s1_visitor": "Visitante: Cualquier persona que utilice la calculadora pública o la sección de descubrimiento.",
  "tos.s1_service":
    "Servicio: Las funcionalidades ofrecidas por Inkvaluator, incluyendo la calculadora de precios, gestión de leads, escáner IA y herramientas de marca blanca.",
  "tos.s2_title": "2. Uso de la calculadora",
  "tos.s2_p1":
    "Las estimaciones de precio son solo indicativas y no constituyen una oferta vinculante ni garantía de precio.",
  "tos.s2_p2":
    "Inkvaluator no es responsable de decisiones basadas en las estimaciones ni de disputas entre artistas y clientes.",
  "tos.s3_title": "3. Suscripciones y pagos",
  "tos.s3_intro": "Inkvaluator ofrece tres niveles: Free, Pro y Studio.",
  "tos.s3_billing":
    "Las suscripciones de pago se facturan mensualmente a través de Stripe y se renuevan automáticamente.",
  "tos.s3_cancel":
    "Puedes cancelar tu suscripción en cualquier momento. Después de cancelar, mantienes acceso hasta el final del período de facturación.",
  "tos.s3_refund": "No se realizan reembolsos por períodos de facturación ya iniciados, salvo que la ley lo requiera.",
  "tos.s3_price_change": "Inkvaluator se reserva el derecho de cambiar precios con un aviso mínimo de 30 días.",
  "tos.s4_title": "4. Propiedad de datos",
  "tos.s4_p1": "Todos los datos ingresados por un Artista permanecen como propiedad del Artista.",
  "tos.s4_p2":
    "Los datos de uso anónimos y agregados pueden utilizarse para análisis estadístico y mejora del producto.",
  "tos.s5_title": "5. Responsabilidad",
  "tos.s5_p1": 'Inkvaluator proporciona el Servicio "tal cual".',
  "tos.s5_indirect": "Inkvaluator no es responsable de daños indirectos, lucro cesante o daños consecuentes.",
  "tos.s5_cap": "La responsabilidad total está limitada al monto pagado en los tres meses anteriores.",
  "tos.s5_content": "Inkvaluator no es responsable del contenido publicado por los artistas.",
  "tos.s6_title": "6. Cuenta y terminación",
  "tos.s6_credentials": "Los usuarios son responsables de mantener confidenciales sus credenciales.",
  "tos.s6_suspend": "Inkvaluator se reserva el derecho de suspender o eliminar cuentas en caso de abuso.",
  "tos.s6_delete": "Al terminar una cuenta, los datos personales se eliminarán conforme a la",
  "tos.s6_privacy_link": "Política de privacidad",
  "tos.s7_title": "7. Propiedad intelectual",
  "tos.s7_p1": "Todos los derechos de propiedad intelectual pertenecen a Inkvaluator.",
  "tos.s8_title": "8. Ley aplicable",
  "tos.s8_p1": "Estos términos se rigen por la ley neerlandesa.",
  "tos.contact_title": "¿Preguntas sobre estos términos?",
  "tos.contact_text": "Contáctanos en:",
  "support.seo_title": "Soporte | Inkvaluator",
  "support.seo_desc": "¿Necesitas ayuda? Consulta nuestras preguntas frecuentes o contacta a nuestro equipo.",
  "support.title": "Soporte",
  "support.faq_title": "Preguntas frecuentes",
  "support.faq_q1": "¿Cómo configuro mis tarifas?",
  "support.faq_a1":
    'Ve a tu panel y haz clic en "Precios". Ahí puedes configurar tu tarifa por hora, precio mínimo y más.',
  "support.faq_q2": "¿Cómo funciona la integración con Stripe?",
  "support.faq_a2":
    "Al actualizar a Pro o Studio, serás redirigido a un pago seguro de Stripe. Tu suscripción se activa automáticamente.",
  "support.faq_q3": "¿Qué es el módulo de WhatsApp?",
  "support.faq_a3":
    "Con el módulo WhatsApp (desde Pro), los visitantes pueden contactarte directamente vía WhatsApp tras calcular un precio.",
  "support.faq_q4": "¿Cómo comparto mi calculadora?",
  "support.faq_a4":
    'En tu panel, bajo "Compartir", encontrarás un enlace directo, código QR y código para integrar en tu web.',
  "support.faq_q5": "¿Puedo cancelar mi suscripción?",
  "support.faq_a5":
    "Sí, puedes cancelar en cualquier momento. Mantendrás acceso hasta el final de tu período de facturación.",
  "support.faq_q6": "¿Cómo funciona el escáner de IA?",
  "support.faq_a6": "El escáner IA analiza una foto y estima automáticamente tamaño, complejidad y estilo.",
  "support.faq_q7": "Encontré un error, ¿qué hago?",
  "support.faq_a7": "Envía un email a support@inkvaluator.com con una descripción del problema y capturas de pantalla.",
  "support.contact_title": "Contacto",
  "support.contact_text": "¿Tu pregunta no está aquí? Contacta a nuestro equipo:",
  "support.form_name": "Nombre",
  "support.form_name_placeholder": "Tu nombre",
  "support.form_email": "Correo electrónico",
  "support.form_message": "Mensaje",
  "support.form_message_placeholder": "Describe tu pregunta o problema...",
  "support.form_submit": "Enviar mensaje",
  "support.form_subject": "Solicitud de soporte de {name}",
  "support.toast_title": "Cliente de correo abierto",
  "support.toast_desc": "Tu aplicación de correo se está abriendo con tu mensaje.",
  "privacy.seo_title": "Política de privacidad | Inkvaluator",
  "privacy.seo_desc": "Lea la política de privacidad de Inkvaluator.",
  "privacy.title": "Política de privacidad",
  "privacy.updated": "Última actualización:",
  "privacy.s1_title": "1. ¿Qué datos recopilamos?",
  "privacy.s1_intro": "Inkvaluator recopila datos necesarios para el funcionamiento de la plataforma:",
  "privacy.s1_artists_label": "Para artistas",
  "privacy.s1_artists": "Nombre, email, nombre del estudio, dirección y portafolio.",
  "privacy.s1_visitors_label": "Para visitantes",
  "privacy.s1_visitors": "Datos ingresados en la calculadora para generar estimaciones.",
  "privacy.s1_leads_label": "Leads",
  "privacy.s1_leads": "Al contactar a un artista, su mensaje y fotos se procesan de forma segura.",
  "privacy.s2_title": "2. Uso de datos",
  "privacy.s2_intro": "Su privacidad es nuestra prioridad. Nunca vendemos datos a terceros.",
  "privacy.s2_matching": "Conectar clientes con los artistas adecuados.",
  "privacy.s2_algorithms": "Mejorar nuestros algoritmos de precios.",
  "privacy.s2_pwa": "Funcionamiento técnico de la PWA.",
  "privacy.s3_title": "3. Legislación internacional (RGPD)",
  "privacy.s3_intro": "Conforme a la legislación europea, usted tiene los siguientes derechos:",
  "privacy.s3_access_label": "Derecho de acceso",
  "privacy.s3_access": "Puede solicitar qué datos tenemos.",
  "privacy.s3_erasure_label": "Derecho al olvido",
  "privacy.s3_erasure": "Puede solicitar la eliminación completa de su cuenta y datos.",
  "privacy.s3_portability_label": "Portabilidad de datos",
  "privacy.s3_portability": "Puede solicitar sus datos en un formato legible.",
  "privacy.s4_title": "4. Cookies y PWA",
  "privacy.s4_text": "Inkvaluator utiliza caché funcional (PWA). No usamos cookies de seguimiento.",
  "privacy.contact_title": "¿Preguntas sobre su privacidad?",
  "privacy.contact_text": "Contacte con nuestro delegado de protección de datos:",
  "dash.unsaved_changes": "Tienes cambios sin guardar",
  "dash.dashboard_language": "Idioma del panel",
  "dash.dashboard_language_desc": "El idioma de tu interfaz de gestión.",
  "auth.dashboard_subtitle": "Panel del artista",
  "dash.impersonation_banner": "Estás viendo el perfil de {name}",
  "dash.stop_impersonation": "Dejar de suplantar",
  "discover.subtitle": "Descubre Estudios y Artistas",
  "discover.description": "Encuentra tatuadores y estudios cerca de ti. Consulta su calculadora y contacta.",
  "discover.hide_map": "Ocultar mapa",
  "discover.show_map": "Mostrar mapa",
  "discover.map_loading": "Cargando mapa...",
  "discover.call": "Llamar",
  "discover.route": "Ruta",
  "discover.map_title": "Artistas en el mapa",
  "discover.no_artists_for": 'No se encontraron artistas para "{query}".',
  "discover.playground": "Playground",
  "discover.artist_login": "Login artista",
  "feature.calculator": "Calculadora de precios personalizada",
  "feature.qr": "Código QR y enlace compartible",
  "feature.styles": "Estilos ilimitados y escáner IA",
  "feature.logo": "Subida de logo",
  "feature.theme": "Tema de color personalizado",
  "feature.language": "Multilingüe (17 idiomas)",
  "feature.currency": "Cambio de divisa (13 divisas)",
  "feature.analytics": "Análisis de uso",
  "feature.pdf": "Exportación PDF",
  "feature.whitelabel": "Marca blanca",
  "feature.map_visibility": "Visibilidad en el mapa mundial",
  "feature.multiartist": "Gestión multi-artista",
  "feature.centralanalytics": "Panel analítico central",
  "feature.teammanagement": "Gestión de equipo y roles",
  "map.view_calculator": "Ver calculadora",
  "map.unknown_location": "Ubicación desconocida",
  "map.artists_count": "artistas",
  "map.world_title": "Artistas",
  "map.world_accent": "en todo el mundo",
  "map.in_countries": "en {count} países",
  "blog.title": "Blog",
  "blog.no_posts": "Aún no hay artículos publicados.",
  "blog.read_more": "Leer más",
  "blog.published_on": "Publicado el",
  "blog.back": "Volver al blog",
  "blog.not_found": "Artículo no encontrado",
  "blog.translation_notice": "Este artículo aún no está disponible en tu idioma. Se muestra la versión original.",
  "landing.artist_or_studio": "¿Eres artista o estudio?",
  "demo.subtitle": "Playground",
  "demo.title": "Prueba la",
  "demo.title_accent": "Calculadora",
  "demo.description": "Ajusta las variables y descubre cómo funciona la calculadora de precios.",
  "demo.your_rates": "Tus tarifas",
  "demo.hourly_rate": "Tarifa por hora",
  "demo.start_cost": "Costes iniciales fijos",
  "demo.estimated_price": "Precio estimado",
  "demo.disclaimer": "Esto es una demo. El precio real depende del artista y del diseño.",
  "demo.artist_cta": "¿Eres artista?",
  "demo.register_free": "Registrarse gratis",
  "demo.find_studio": "Encontrar un estudio cerca",
  "profile.identity": "Identidad",
  "profile.studio_name": "Nombre del estudio",
  "profile.location": "Ubicación",
  "profile.city": "Ciudad",
  "profile.calculator_language": "Idioma de la calculadora",
  "embed.title": "Widget integrable",
  "embed.copy_code": "Copiar código",
  "vault.status_concept": "Concepto",
  "vault.status_sent": "Solicitud enviada",
  "reviews.rating": "valoración",
  "reviews.verified_reviews": "reseñas verificadas",
  "reviews.read_all": "Leer todas las reseñas en Google",
  "discover.swipe": "deslizar",
  "profile.manual_place_id": "¿Estudio no encontrado? Introduce tu Place ID manualmente.",
  "vault.inspiration_title": "Inspiración",
  "vault.try_style": "Probar →",
  "vault.compare": "Comparar",
  "vault.compare_title": "Comparación",
  "vault.select_to_compare": "Selecciona 2 estimaciones para comparar",
  "trends.title": "Tendencias",
  "trends.title_accent": "Tatuajes",
  "trends.subtitle": "Descubre los estilos y ubicaciones más populares del mundo.",
  "trends.top_styles": "Estilos populares",
  "trends.top_body_parts": "Partes del cuerpo populares",
  "match.title": "Artistas compatibles cerca de ti",
  "match.score": "Match",
  "match.view": "Ver perfil",
  "urgency.recent_requests": "solicitudes esta semana",
  "urgency.wait_time": "Espera estimada",
  "urgency.weeks": "semanas",
  "urgency.high_demand": "Alta demanda",
  "urgency.popular": "Popular",
  "benchmark.above_market": "por encima del promedio",
  "benchmark.below_market": "por debajo del promedio",
  "benchmark.at_market": "En el promedio",
  "benchmark.market_avg": "Promedio del mercado",
  "portfolio.style_examples": "Ejemplos de estilo",
  "story.title": "Generador de Stories Instagram",
  "story.desc": "Crea una story premium Black & Gold con tu código QR.",
  "story.generate": "Generar Story",
  "story.regenerate": "Regenerar",
  "story.download": "Descargar",
  "story.share": "Compartir",
  "story.downloaded": "¡Story descargada!",
  "story.cta_line1": "Calcula Tu Precio",
  "story.cta_line2": "Escanea el código QR",
  "story.scan_prompt": "↑ Escanea para tu estimación",
  "trends.filter_country": "Filtrar por país",
  "trends.all_countries": "Todos los países",
  "trends.analyzed": "cálculos analizados",
  "trends.realtime": "Actualizado en tiempo real",
  "match.style_experts": "Expertos en {style} cerca de {city}",
  "vault.review_booster_title": "¿Tatuaje hecho? ¡Ayuda a tu artista!",
  "vault.review_booster_desc": "Deja una reseña en Google y ayuda a otros a encontrar este artista.",
  "vault.review_booster_btn": "Escribir reseña en Google",
  "checklist.title": "Estado de verificación",
  "checklist.profile_complete": "Perfil completo",
  "checklist.google_linked": "Google Place ID vinculado",
  "checklist.badge_detected": "Badge verificado en web",
  "checklist.push_enabled": "Notificaciones push activadas",
  "checklist.not_yet": "Aún no",
  "checklist.active": "Activo",
  "lead.status_new": "Nuevo",
  "lead.status_contacted": "En contacto",
  "lead.status_booked": "Reservado",
  "lead.change_status": "Estado",
  "lead.add_price": "Añadir precio final",
  "push.test_body": "¡Las notificaciones push están ACTIVAS! Recibirás alertas de nuevos leads.",
  "push.send_test": "Enviar notificación de prueba",
  "push.active_desc": "Recibes notificaciones cuando llegan leads de calidad.",
  "push.blocked": "Las notificaciones están bloqueadas en la configuración de tu navegador.",
  "push.enable_desc": "Recibe notificaciones cuando lleguen leads de calidad.",
  "push.label": "Notificaciones push",
  "push.welcome_body": "¡Bienvenido! Recibirás alertas cuando lleguen leads de calidad.",
  "push.denied_info":
    "Mantente informado sobre nuevos leads — Activa las notificaciones en la configuración de tu navegador.",
  "vault.live_status": "Estado en vivo",
  "vault.status_new_desc": "Tu solicitud ha sido recibida",
  "vault.status_contacted_desc": "El artista ha visto tu mensaje",
  "vault.status_booked_desc": "¡Tu cita está confirmada!",
  "vault.link_copied": "🔗 ¡Enlace copiado!",
  "analytics.actual_revenue": "Ingresos Reales",
  "style_city.popular_styles": "Estilos populares en {city}",
  "nav.home": "Inicio",
  "lead.estimate_header": "Estimación de Tatuaje",
  "discover.verified_badge": "VERIFICADO",
  "wa.title": "Enviar al artista por WhatsApp",
  "wa.subtitle": "Envía tu estimación directamente a",
  "wa.name": "Tu nombre (opcional)",
  "wa.name_placeholder": "Tu nombre",
  "wa.message": "Mensaje (opcional)",
  "wa.message_placeholder": "Describe tu idea, inspiración o detalles...",
  "wa.photo": "Foto de referencia (opcional)",
  "wa.upload_photo": "Subir foto",
  "wa.file_too_large": "Archivo demasiado grande",
  "wa.summary": "Resumen:",
  "wa.send": "Enviar por WhatsApp",
  "wa.sent_title": "¡Mensaje enviado!",
  "wa.sent_desc": "Tu solicitud ha sido enviada a {name} por WhatsApp.",
  "wa.upload_failed": "Error al subir",
  "wa.upload_failed_desc": "No se pudo subir la foto. Inténtalo de nuevo.",
  "wa.save_failed": "Error al guardar",
  "wa.success": "¡Enviado!",
  "wa.success_desc": "Tu solicitud ha sido creada y WhatsApp se ha abierto.",
  "wa.error": "Algo salió mal",
  "wa.error_desc": "Error desconocido. Verifica tu conexión e inténtalo de nuevo.",
  "wa.msg_greeting": "Hola {name}, he calculado una estimación de precio:",
  "wa.msg_name_label": "Nombre",
  "wa.msg_style_label": "🎨 Estilo",
  "wa.msg_bodypart_label": "📍 Parte del cuerpo",
  "wa.msg_size_label": "📏 Tamaño",
  "wa.msg_complexity_label": "⚙️ Complejidad",
  "wa.msg_time_label": "⏱ Tiempo estimado",
  "wa.msg_total_label": "💰 Total",
  "wa.msg_deposit_label": "💳 Depósito",
  "wa.msg_details_label": "🔗 Detalles",
  "wa.template_high_greeting": "¡Hola {name}, buenas noticias! He calculado una estimación detallada para mi tatuaje:",
  "wa.template_high_vault": "🔗 Lo he guardado en mi Bóveda de Tatuajes: {url}",
  "wa.template_low_greeting": "Hola {name}, me interesa un tatuaje. ¿Puedes informarme sobre disponibilidad y precios?",
  "wa.template_low_note": "Basado en la calculadora, una primera idea:",
  "tooltip.labor": "Basado en el tiempo estimado para este tamaño, multiplicado por la tarifa por hora del artista.",
  "tooltip.complexity": "Diseños más complejos requieren más tiempo y precisión.",
  "tooltip.placement": "Algunas partes del cuerpo son más sensibles o difíciles de alcanzar.",
  "tooltip.style": "Diferentes estilos requieren técnicas específicas que afectan el precio.",
  "tooltip.design": "Costes de diseño y preparación de tu tatuaje único.",
  "tooltip.deposit": "El depósito reserva tu cita y se deduce del precio total.",
  "pricing_edu.button": "¿Cómo funciona este cálculo?",
  "pricing_edu.title": "Cómo se calcula el precio",
  "pricing_edu.intro":
    "El precio de un tatuaje está determinado por varios factores que reflejan la artesanía y el tiempo.",
  "pricing_edu.time_title": "Tiempo y Tamaño",
  "pricing_edu.time_desc": "Los tatuajes más grandes tardan más tiempo.",
  "pricing_edu.complexity_title": "Complejidad y Detalle",
  "pricing_edu.complexity_desc": "Los diseños detallados requieren más precisión.",
  "pricing_edu.placement_title": "Ubicación corporal",
  "pricing_edu.placement_desc": "Algunas zonas son más sensibles o de difícil acceso.",
  "pricing_edu.style_title": "Estilo de tatuaje",
  "pricing_edu.style_desc": "Cada estilo requiere técnicas específicas.",
  "pricing_edu.disclaimer": "Esto es siempre una estimación. El precio final se determina tras consulta personal.",
  "ai.title": "Escáner IA",
  "ai.desc": "Sube una foto y deja que la IA determine los ajustes",
  "ai.upload_prompt": "Toma una foto o sube una imagen de referencia",
  "ai.analyzing": "La IA está analizando...",
  "ai.result_title": "Resultado IA",
  "ai.result_size": "Tamaño",
  "ai.result_complexity": "Complejidad",
  "ai.result_style": "Estilo",
  "ai.result_color": "Color",
  "ai.yes": "Sí",
  "ai.no": "No",
  "ai.choose_other": "Elegir otra foto",
  "ai.result_placement": "Ubicación",
  "ai.phase_detecting": "Detectando complejidad...",
  "ai.phase_style": "Analizando estilo...",
  "ai.phase_placement": "Detectando ubicación...",
  "ai.phase_finalizing": "Finalizando estimación...",
  "ai.file_too_large": "Archivo demasiado grande",
  "ai.max_size": "Máx 10 MB",
  "ai.scan_complete": "✨ Escaneo IA completado",
  "ai.scan_applied": "La calculadora se ha ajustado automáticamente.",
  "ai.scan_failed": "Escaneo fallido",
  "ai.try_again": "Inténtalo de nuevo",
  "referral.title": "Recomienda a un colega",
  "referral.desc": "Invita a un colega y gana descuento en tu suscripción.",
  "referral.generate": "Generar enlace de referido",
  "referral.share_link": "Comparte este enlace:",
  "pwa.install_title": "Instalar como app",
  "pwa.install_button": "Instalar ahora",
  // Batch 3+4
  "tab.theme": "Tema",
  "tab.branding": "Marca",
  "tab.analytics": "Analíticas",
  "tab.price_engine": "Motor de precios",
  "tab.price_engine_desc": "Gestiona todos los multiplicadores de precio.",
  "basic.desc": "Establece tus tarifas y costes estándar",
  "basic.min_price_desc": "El precio más bajo que cobras",
  "basic.hourly_rate_desc": "Tu tarifa por hora estándar",
  "basic.daily_rate_placeholder": "Deja vacío si no aplica",
  "basic.percentage": "%",
  "basic.fixed_amount": "Fijo",
  "basic.consultation_email": "Email de consulta",
  "basic.consultation_email_desc": "Email para solicitudes de consulta",
  "basic.booking_url": "URL de reserva",
  "basic.booking_url_desc": "URL del sistema de reservas externo",
  "sizes.title": "Ajustes de tamaño",
  "sizes.desc": "Define el tiempo estimado por categoría de tamaño",
  "sizes.min_hours": "Min. horas",
  "sizes.max_hours": "Máx. horas",
  "sizes.hours": "horas",
  "complexity.title": "Multiplicadores de complejidad",
  "complexity.desc": "Ajusta el factor de precio según la complejidad",
  "bodyparts.title": "Multiplicadores de partes del cuerpo",
  "bodyparts.desc": "Algunas zonas son más difíciles de tatuar",
  "styles.title": "Gestión de estilos",
  "styles.desc": "Activa o desactiva estilos y ajusta el factor de precio.",
  "theme.title": "Tema de color",
  "theme.desc": "Personaliza los colores de tu calculadora",
  "theme.preview": "Vista previa",
  "theme.preview_desc": "Así se ve tu calculadora",
  "theme.hue": "Tono",
  "theme.saturation": "Saturación",
  "theme.lightness": "Luminosidad",
  "theme.presets": "Selección rápida",
  "branding.title": "Marca",
  "branding.desc": "Sube tu logo y configura idioma y moneda",
  "branding.logo": "Logo",
  "branding.logo_desc": "Logo de estudio (máx 2 MB, PNG o JPG)",
  "branding.upload": "Subir logo",
  "branding.remove": "Eliminar",
  "branding.language": "Idioma de la calculadora",
  "branding.currency": "Moneda",
  "branding.qr_code": "Código QR",
  "branding.qr_desc": "Los clientes escanean este código para tu calculadora.",
  "branding.download_qr": "Descargar QR",
  "onboard.step_minimum": "Tarifa mínima",
  "onboard.step_hourly": "Tarifa por hora",
  "onboard.step_sizes": "Confirmar tamaños",
  "onboard.step_styles": "Definir estilos",
  "onboard.step_logo": "Subir logo",
  "onboard.step_finish": "¡Calculadora lista!",
  "dash.onboard_min_desc": "Establece tu tarifa mínima.",
  "dash.onboard_hourly_desc": "Establece tu tarifa por hora. Es la base de todos los cálculos.",
  "dash.onboard_sizes_desc": "Categorías de tamaño configuradas por defecto. Ajusta después.",
  "dash.onboard_styles_desc": "Todos los estilos activados. Actívalos/desactívalos en el panel.",
  "dash.onboard_logo_desc": "Sube un logo ahora o después en la pestaña Marca.",
  "dash.onboard_logo_later": "Subir logo después en Panel → Marca",
  "dash.onboard_finish_title": "¡Tu calculadora está lista!",
  "dash.onboard_finish_desc": "Comparte tu enlace único. Ajusta los parámetros en cualquier momento.",
  "dash.your_link": "Enlace de tu calculadora:",
  "dash.online_card": "Tu tarjeta de visita online.",
  "dash.view_my_calculator": "Ver mi calculadora",
  "dash.upload_thumbnail": "Imagen personalizada",
  "dash.reset_to_default": "Restablecer",
  "dash.reset_all_thumbnails": "Restablecer todo",
  "dash.thumbnail_uploaded": "¡Imagen subida!",
  "dash.thumbnail_reset": "Imagen restablecida",
  "dash.thumbnails_reset_all": "Todas las imágenes restablecidas",
  "dash.invite_created": "¡Invitación creada!",
  "dash.invite_error": "Error al crear invitación",
  "dash.member_removed": "Miembro eliminado",
  "dash.link_copied_generic": "¡Enlace copiado!",
  "dash.profile_saved": "¡Perfil guardado!",
  "dash.profile_save_error": "Error al guardar",
  "dash.calculator_ready": "🎉 ¡Calculadora lista!",
  "dash.share_with_clients": "Comparte tu enlace.",
  "dash.step_of": "Paso {current} de {total}",
  "dash.start_inkprice": "Empezar con InkPrice",
  "laf.title": "Apariencia",
  "laf.desc": "Personaliza la apariencia de tu calculadora pública.",
  "laf.color_mode": "Modo de color",
  "laf.dark_mode": "Modo oscuro",
  "laf.light_mode": "Modo claro",
  "laf.background": "Fondo",
  "laf.solid_color": "Color sólido",
  "laf.image_url": "URL de imagen",
  "laf.preview": "Vista previa",
  "laf.saved": "Apariencia guardada",
  "laf.presets": "Temas premium",
  "laf.font_selector": "Fuente de la calculadora",
  "laf.overlay_opacity": "Opacidad del overlay",
  "laf.bg_fixed": "Fijo (Parallax)",
  "laf.bg_scroll": "Desplazamiento",
  "consult_settings.title": "Ajustes de consulta",
  "consult_settings.desc": "Configura el contacto de clientes.",
  "consult_settings.booking_url_label": "URL de reserva",
  "consult_settings.email_label": "Email de consulta",
  "consult_settings.channels_title": "Canales de leads",
  "consult_settings.channels_desc": "Métodos de contacto visibles.",
  "help.min_price": "Precio mínimo absoluto por sesión.",
  "help.hourly_rate": "Tu tarifa por hora es la base de todos los cálculos.",
  "help.multipliers": "Los multiplicadores ajustan el precio según estilo, complejidad y ubicación.",
  "help.sizes": "Define el rango de tiempo para cada tamaño.",
  "help.complexity": "Ajusta multiplicadores de complejidad.",
  "help.body_parts": "Algunas zonas requieren multiplicadores más altos.",
  "help.styles": "Activa/desactiva estilos y define multiplicadores.",
  "help.logo": "Tu logo aparece en tu calculadora y en el directorio. Máx 2 MB.",
  "help.currency": "La moneda mostrada en tu calculadora.",
  "help.calculator_language": "El idioma de tu calculadora pública.",
  "help.dashboard_language": "El idioma de este panel — solo visible para ti.",
  "profile.mfa_title": "Seguridad de la cuenta",
  "profile.mfa_desc": "Protege tu cuenta con 2FA.",
  "profile.mfa_enable": "Activar 2FA",
  "profile.mfa_verify": "Verificar",
  "profile.mfa_active": "Activo",
  "profile.mfa_enabled_success": "✅ ¡2FA activada!",
  "profile.google_reviews": "Reseñas de Google",
  "profile.google_reviews_desc": "Vincula tu perfil de Google Business.",
  "profile.sync_now": "Sincronizar",
  "profile.sync_success": "¡Reseñas de Google sincronizadas!",
  "landing.faq_title": "Preguntas frecuentes",
  "landing.faq_subtitle": "Todo sobre Inkvaluator",
  "landing.faq_q1": "¿Cómo funciona la calculadora?",
  "landing.faq_a1": "Configura tu tarifa y multiplicadores. Los clientes obtienen un presupuesto instantáneo.",
  "landing.faq_q2": "¿Es gratis?",
  "landing.faq_a2": "¡Sí! El plan Free incluye calculadora completa. Pasa a Pro para más.",
  "landing.faq_q3": "¿Cómo funciona el escáner IA?",
  "landing.faq_a3": "Sube una foto y la IA analiza tamaño, complejidad y estilo en segundos.",
  "landing.faq_q4": "¿Pueden los clientes contactarme?",
  "landing.faq_a4": "Sí, por WhatsApp con un mensaje prellenado.",
  "landing.faq_q5": "¿Soporte multilingüe?",
  "landing.faq_a5": "17 idiomas y 13 monedas para artistas internacionales.",
  "landing.faq_q6": "¿Cómo consigo más clientes?",
  "landing.faq_a6": "Artistas Pro/Studio en el mapa mundial y página de descubrimiento.",
  "city.title": "Tatuadores en {city}",
  "city.description": "Encuentra tatuadores en {city}.",
  "city.no_artists": "No se encontraron artistas en {city}.",
  "city.discover_all": "Todos los artistas",
  "seo.landing_title": "Inkvaluator – Calculadora de Precios de Tatuaje",
  "seo.landing_desc": "Calcula el precio de tu tatuaje al instante.",
  "vault.seo_title": "Mi Bóveda de Tatuaje | Inkvaluator",
  "vault.title": "Mi",
  "vault.title_accent": "Bóveda",
  "vault.desc": "Tus presupuestos guardados.",
  "vault.empty": "Sin presupuestos. ¡Usa una calculadora!",
  "vault.save_btn": "Guardar",
  "vault.saved_title": "¡Guardado!",
  "vault.saved_desc": "Presupuesto guardado.",
  "success.active": "Activo — todo desbloqueado",
  "success.setup_whatsapp": "Configurar WhatsApp",
  "success.syncing": "Activando tu plan…",
  "success.view_details": "Ver detalles del plan",
  "analytics.title": "Analíticas",
  "analytics.desc": "Uso de tu calculadora",
  "analytics.total_calculations": "Total de cálculos",
  "analytics.conversion_rate": "Tasa de conversión",
  "analytics.this_month": "Este mes",
  "analytics.no_data": "Sin datos aún.",
  "studio.title": "Gestión del estudio",
  "studio.team": "Equipo",
  "studio.invite": "Invitar artista",
  "studio.invite_desc": "Genera un enlace de invitación.",
  "studio.generate_link": "Generar enlace",
  "studio.no_members": "Sin miembros. Invita artistas.",
  "color.gold": "Oro",
  "color.red": "Rojo",
  "color.blue": "Azul",
  "color.green": "Verde",
  "color.purple": "Morado",
  "color.pink": "Rosa",
  "color.turquoise": "Turquesa",
  "color.orange": "Naranja",
  "tip.title": "Recomendar artista",
  "tip.desc": "¿Conoces un tatuador que debería estar en Inkvaluator?",
  "share.button": "Compartir",
  "sw.update_available": "Nueva versión disponible.",
  "embed.iframe_desc": "Pega esto en tu HTML.",
  "embed.floating_desc": "Pega esto antes del </body>.",
  "embed.verified_badge": "Badge verificado",
  "embed.badge_desc": "Coloca este badge en tu web.",
  "compare.title": "Comparación de precios",
  "compare.disclaimer": "Comparación basada en un tatuaje estándar.",
  "landing.calculate_price": "Calcular precio",
  "landing.total_value_prefix": "Ya",
  "landing.total_value_suffix": "en arte del tatuaje calculado",
  "step.next": "Continuar",
  "step.view_result": "Ver precio",
  "step.details": "Detalles",
  "step.details_desc": "¿Qué tan detallado es tu diseño?",
  "layout.stepper": "Paso a paso",
  "layout.form": "Todo a la vez",
  "calc.view_price_cta": "Ver resumen de precios",
  "calc.rough_estimate": "Estimación aproximada",
  "calc.calculated_price": "Precio calculado",
  "calc.save_for_later": "Guardar para después",
  "calc.share": "Compartir",
  "calc.more_options": "Más opciones",
  "calc.compare_configs": "Comparar configuraciones",
  "calc.compare_desc": "Comparar dos configuraciones de precios lado a lado",
  "calc.no_photo": "Sin foto de referencia añadida",
  "calc.link_copied": "¡Enlace copiado!",
  "whatsapp.add_photo": "Añade tu foto",
  "whatsapp.photos_label": "Fotos de referencia — máx. 3",
  "laf.reset_success": "Tema restablecido a los valores predeterminados",
  "admin.stripe_managed":
    "Las suscripciones se gestionan automáticamente a través de Stripe — los cambios son visibles después de la próxima sincronización webhook",
  "calc.view_tattoo_plan": "Ver tu Tattoo Plan ✓",
  "wa.plan_message": "Aquí está mi Tattoo Plan:",
  "lead.tattoo_plan": "Tattoo Plan",
  "lead.made_with": "Creado con Inkvaluator",
  "lead.contact_artist": "Contactar a {artist}",
  "lead.share": "Compartir",
  "lead.share_title": "Mi Tattoo Plan",
  "lead.link_copied": "¡Enlace copiado!",
  "lead.deposit_label": "Depósito",
  "lead.pay_deposit": "Pagar depósito {amount}",
  "lead.deposit_request_title": "Solicitar acuerdo de depósito",
  "lead.deposit_request_desc": "Pregunta al artista si un depósito de ~{amount} es posible.",
  "lead.deposit_request_wa": "Enviar solicitud por WhatsApp",
  "lead.for_you": "Ti",
  "lead.for_label": "Para",
  "lead.back_to_calculator": "Volver a la calculadora",
  "lead.deposit_wa_msg":
    "Hola {artist}, he revisado mi Tattoo Plan en Inkvaluator. ¿Sería posible un depósito de ~{amount}?\nPlan: {url}",
  "blog.no_translation": "No hay traducción disponible",
  "flash.tab": "Flash",
  "flash.upload": "Subir diseño flash",
  "flash.title": "Título",
  "flash.price": "Precio",
  "flash.size": "Tamaño (cm)",
  "flash.available": "Disponible",
  "flash.featured": "Destacado",
  "flash.limit_used": "de {max} diseños flash usados",
  "flash.delete_confirm": "¿Eliminar diseño flash?",
  "flash.reserve_whatsapp": "Reservar por WhatsApp",
  "flash.whatsapp_msg": "Me interesa tu flash tattoo:",
  "flash.matching_designs": "Flash designs similares",
  "flash.only_with_flash": "Solo artistas con flash",
  "groei.score": "Puntuación del perfil",
  "groei.views": "visitas al perfil",
  "groei.sessions": "sesiones de calculadora",
  "groei.leads": "leads",
  "groei.this_week": "esta semana",
  "groei.improve": "Mejora tu perfil",
  "groei.add_photos": "Añade más fotos de portfolio",
  "groei.add_whatsapp": "Configura tu número de WhatsApp",
  "groei.add_bio": "Escribe una bio más larga",
  "groei.add_avatar": "Sube una foto de perfil",
  "groei.add_rate": "Establece tu tarifa por hora",
  "groei.add_city": "Añade tu ciudad",
  "groei.get_leads": "Comparte tu calculadora para obtener leads",
  "groei.collapse": "Contraer",
  "groei.expand": "Expandir",
  "groei.tip_portfolio": "Artistas con 6+ fotos obtienen 3x más sesiones",
  "groei.tip_whatsapp": "Clientes con botón WhatsApp convierten 5x mejor",
  "groei.tip_leads": "Comparte tu enlace de calculadora para recibir tu primer lead",
  "groei.tip_avatar": "Una foto de perfil aumenta tu credibilidad",
  "groei.tip_action_photos": "Añadir fotos",
  "groei.tip_action_whatsapp": "Configurar WhatsApp",
  "groei.tip_action_share": "Compartir calculadora",
  "groei.tip_action_avatar": "Subir foto",
  "milestone.eerste_lead": "¡Primer lead recibido!",
  "milestone.tien_leads": "¡10 leads alcanzados!",
  "milestone.profiel_compleet": "¡Perfil 100% completo!",
  "milestone.eerste_flash": "¡Primer flash design subido!",
  "wa.name_required": "El nombre es obligatorio",
  "wa.plan_message_full": "Ver mi Tattoo Plan completo: {url}",
};

// ─── ITALIAN (complete) ────────────────────────────────────────
const it: Record<string, string> = {
  ...en,
  "app.tagline": "Calcola il prezzo del tuo tatuaggio",
  back: "Indietro",
  save: "Salva",
  copy: "Copia",
  copied: "Copiato!",
  loading: "Caricamento...",
  error: "Errore",
  optional: "opzionale",
  "landing.hero": "Calcola il prezzo del",
  "landing.hero_accent": "tuo Tatuaggio",
  "landing.subtitle": "Inserisci il codice del tuo tatuatore per ottenere un preventivo personalizzato.",
  "landing.start": "Avvia calcolatore",
  "landing.invalid_code": "Inserisci un codice valido",
  "landing.or": "o",
  "landing.artist_cta": "Sei un tatuatore? Crea il tuo calcolatore.",
  "landing.hero_title": "Calcola il prezzo del",
  "landing.hero_title_accent": "tuo Tatuaggio",
  "landing.hero_desc": "Scopri in pochi secondi quanto costa il tuo prossimo tatuaggio.",
  "landing.try_it": "Provalo!",
  "landing.estimated_price": "Prezzo stimato",
  "landing.your_rates": "Le tue tariffe",
  "landing.rates_desc": "Sposta i cursori e osserva il cambio di prezzo",
  "landing.hourly_rate": "Tariffa oraria",
  "landing.start_cost": "Costi fissi iniziali",
  "landing.size_label": "Dimensione",
  "landing.complexity_label": "Complessità",
  "landing.body_placement": "Posizionamento corporeo",
  "landing.find_artist": "Trova il tuo",
  "landing.find_artist_accent": "artista perfetto",
  "landing.nearby_soon": "Presto appariranno artisti vicino a te.",
  "landing.discover_all": "Scopri tutti gli artisti",
  "landing.artist_question": "Sei un artista o uno studio?",
  "landing.view_options": "Visualizza opzioni e prezzi",
  "landing.start_free": "Inizia gratis",
  "landing.discover": "Scopri",
  "landing.login": "Accedi",
  "landing.founder_desc": "50% di sconto per le prime 50 iscrizioni",
  "landing.claim_now": "Richiedilo ora →",
  "calc.estimate": "Prezzo stimato",
  "calc.size": "Dimensione",
  "calc.size_desc": "Quanto sarà grande il tuo tatuaggio?",
  "calc.complexity": "Complessità",
  "calc.complexity_desc": "Quanto è dettagliato il design?",
  "calc.body_part": "Parte del corpo",
  "calc.body_part_desc": "Clicca sulla parte del corpo dove sarà il tatuaggio",
  "calc.style": "Stile",
  "calc.style_desc": "Quale stile di tatuaggio vuoi?",
  "calc.budget": "Budget",
  "calc.budget_desc": "Qual è il tuo budget massimo?",
  "calc.budget_placeholder": "Es. 300",
  "calc.back_home": "Torna alla home",
  "calc.artist_login": "Artista? Accedi",
  "calc.add_color": "Aggiungere colore?",
  "calc.color_surcharge": "+20% supplemento per lavoro a colori",
  "breakdown.title": "Dettaglio prezzo",
  "breakdown.labor": "Manodopera (tempo)",
  "breakdown.subtotal": "Subtotale",
  "breakdown.design_cost": "Costo del design",
  "breakdown.deposit": "Acconto",
  "breakdown.total_indication": "Indicazione totale",
  "breakdown.hours_unit": "ore",
  "faq.title": "Domande frequenti",
  "dash.welcome": "Benvenuto su Inkvaluator",
  "dash.logout": "Esci",
  "dash.saved": "Salvato!",
  "dash.save_changes": "Salva modifiche",
  "dash.cancel": "Annulla",
  "dash.add": "Aggiungi",
  "dash.delete": "Elimina",
  "dash.previous": "Precedente",
  "dash.next": "Avanti",
  "tab.basic": "Base",
  "tab.sizes": "Dimensioni",
  "tab.complexity": "Complessità",
  "tab.body_parts": "Parti del corpo",
  "tab.styles": "Stili",
  "basic.title": "Tariffa base",
  "basic.min_price": "Prezzo minimo",
  "basic.hourly_rate": "Tariffa oraria",
  "basic.daily_rate": "Tariffa giornaliera",
  "basic.design_cost": "Costo del design",
  "basic.fixed": "Fisso",
  "basic.hourly": "All'ora",
  "basic.deposit": "Acconto",
  "size.mini": "Mini (< 5 cm)",
  "size.small": "Piccolo (5-10 cm)",
  "size.medium": "Medio (10-20 cm)",
  "size.large": "Grande (20-35 cm)",
  "size.xl": "Extra Grande (35+ cm)",
  "complexity.simple": "Semplice",
  "complexity.medium": "Medio",
  "complexity.detailed": "Dettagliato",
  "body.arm": "Braccio (generale)",
  "body.forearm": "Avambraccio",
  "body.upper_arm": "Braccio superiore",
  "body.leg": "Gamba (generale)",
  "body.thigh": "Coscia",
  "body.calf": "Polpaccio",
  "body.back": "Schiena",
  "body.chest": "Petto",
  "body.ribs": "Costole",
  "body.stomach": "Addome",
  "body.hand": "Mano",
  "body.foot": "Piede",
  "body.neck": "Collo",
  "body.face": "Viso",
  "body.shoulder": "Spalla",
  "body.hip": "Anca",
  "body.finger": "Dito",
  "body.other": "Altro",
  "body.selected": "Selezionato",
  "body.front": "Davanti",
  "body.back_view": "Dietro",
  "body.front_view": "Davanti",
  "body.back_view_btn": "Dietro",
  "style.black_grey": "Nero e Grigio",
  "style.full_color": "Colore pieno",
  "style.realism": "Realismo",
  "style.fine_line": "Fine Line / Minimal",
  "style.traditional": "Tradizionale",
  "style.neo_traditional": "Neo Tradizionale",
  "style.watercolor": "Acquerello",
  "style.tribal": "Tribale",
  "style.japanese": "Giapponese",
  "style.geometric": "Geometrico",
  "style.lettering": "Lettering",
  "style.script": "Scrittura",
  "style.dotwork": "Dotwork",
  "style.mandala": "Mandala",
  "style.blackwork": "Blackwork",
  "style.illustrative": "Illustrativo",
  "style.surrealism": "Surrealismo",
  "style.ornamental": "Ornamentale",
  "style.micro_realism": "Micro Realismo",
  "style.trash_polka": "Trash Polka",
  "style.other": "Altro",
  "consult.title": "Pronto per iniziare?",
  "consult.book": "Prenota consulenza",
  "pdf.title": "Preventivo",
  "pdf.download": "Scarica PDF",
  "upgrade.choose": "Scegli il tuo",
  "upgrade.plan_word": "Piano",
  "upgrade.per_month": "/mese",
  "upgrade.monthly": "Mensile",
  "upgrade.yearly": "Annuale",
  "upgrade.mo": "mese",
  "upgrade.year": "anno",
  "billing.expires": "Scade",
  "billing.change": "Modifica",
  "billing.next_invoice": "Prossima fattura",
  "billing.invoice_history": "Cronologia fatture",
  "upgrade.perfect_start": "Perfetto per iniziare",
  "upgrade.everything_pro": "Tutto per i professionisti",
  "upgrade.for_studios": "Per studi di tatuaggio & team",
  "upgrade.upgrade_pro": "Passa a Pro",
  "upgrade.upgrade_studio": "Passa a Studio",
  "upgrade.discount": "sconto",
  "auth.login": "Accedi",
  "auth.register": "Registrati",
  "discover.title": "Scopri",
  "discover.title_accent": "Artisti",
  "discover.no_artists": "Nessun artista trovato",
  "discover.view_calculator": "Vedi calcolatore",
  "footer.tagline": "Il calcolatore di prezzi per tatuaggi più intelligente per artisti e clienti.",
  "footer.links": "Link",
  "footer.discover_artists": "Scopri artisti",
  "footer.artist_login": "Login artista",
  "footer.privacy": "Privacy",
  "footer.language_label": "Lingua & App",
  "footer.pwa_badge": "Ottimizzato PWA (clicca per info)",
  "footer.all_rights": "Tutti i diritti riservati.",
  "footer.install_title": "Installare",
  "footer.install_ok": "Capito!",
  "success.welcome": "Benvenuto nel Club,",
  "success.go_dashboard": "Vai alla dashboard",
  "landing.how_it_works_cta": "Scopri come funziona per il tuo studio",
  "footer.terms": "Termini di servizio",
  "footer.support": "Supporto",
  "footer.how_it_works": "Come funziona?",
  "hiw.seo_title": "Come funziona? – Inkvaluator",
  "hiw.seo_desc":
    "Scopri come Inkvaluator aiuta i tatuatori con stime automatiche, lead intelligenti e branding professionale.",
  "hiw.hero_title": "Concentrati sull'inchiostro, non sulla fattura",
  "hiw.hero_p1":
    "Passi più tempo a rispondere ai DM sui prezzi che a disegnare il tuo prossimo capolavoro? Siamo onesti: sei un tatuatore, non un addetto al servizio clienti.",
  "hiw.hero_p2":
    'Ogni domanda "Quanto costa?" a cui devi rispondere manualmente è tempo che non dedichi alla tua arte.',
  "hiw.hero_p3":
    "Inkvaluator è l'assistente digitale che filtra il tuo business. Mentre sei nella zona, lo strumento calcola stime precise, raccoglie tutte le info e invia una richiesta pronta direttamente nel tuo WhatsApp.",
  "hiw.hero_p4":
    'Basta con i "curiosi di prezzi" senza budget. Solo lead seri, preventivi professionali e un branding che mostra che prendi sul serio il tuo mestiere.',
  "hiw.hero_cta": "Pronto a riprenderti il tuo tempo di lavoro?",
  "hiw.faq_title": "Domande frequenti",
  "hiw.faq_cat1": "Le basi: come mi aiuta?",
  "hiw.faq_q1": "È un'altra app complicata?",
  "hiw.faq_a1":
    "No. È un calcolatore velocissimo da mettere nella tua bio Instagram o integrare nel sito. Configuri le regole una volta e lo strumento fa il resto.",
  "hiw.faq_q2": "Come mi fa risparmiare tempo concretamente?",
  "hiw.faq_a2":
    'Invece di chiedere dieci volte: "Che dimensione? Dove? Che stile?", ricevi un messaggio WhatsApp con tutto già compilato, inclusa la foto di riferimento e il prezzo accettato.',
  "hiw.faq_cat2": "Prezzi e impostazioni",
  "hiw.faq_q3": "Posso impostare i miei prezzi?",
  "hiw.faq_a3":
    "Assolutamente. Sei tu il capo. Imposti la tua tariffa oraria e determini cosa addebiti per diverse dimensioni e complessità.",
  "hiw.faq_q4": "E se un tatuaggio è molto più complesso della media?",
  "hiw.faq_a4":
    "Il nostro scanner IA integrato riconosce i dettagli in una foto e regola la stima di prezzo di conseguenza.",
  "hiw.faq_cat3": "Aspetto e branding",
  "hiw.faq_q5": "Non sembra uno strumento generico?",
  "hiw.faq_a5":
    "Assolutamente no. Con il piano Studio lo strumento è completamente white-label: il tuo logo, i tuoi colori e il tuo stile. 8 temi professionali pronti.",
  "hiw.faq_q6": "Quanto professionale appare al mio cliente?",
  "hiw.faq_a6": "Molto professionale. Il cliente può scaricare il preventivo come PDF elegante, che ispira fiducia.",
  "hiw.faq_cat4": "Crescita e visibilità",
  "hiw.faq_q7": "Voglio più clienti dalla mia zona, questo aiuta?",
  "hiw.faq_a7":
    "Sì! Inkvaluator ha una mappa mondiale interattiva. I clienti possono cercare per paese, provincia e città.",
  "hiw.backend_title": "Dietro le quinte: la potenza del backend",
  "hiw.backend_intro":
    "Per gli amanti della tecnologia: Inkvaluator è pieno di funzionalità avanzate che portano il tuo studio al livello successivo.",
  "hiw.card_pricing_title": "Gestione dinamica dei prezzi",
  "hiw.card_pricing_text": "Modifica prezzi e ID Stripe dal modulo admin senza toccare codice.",
  "hiw.card_ai_title": "Integrazione AI Vision",
  "hiw.card_ai_text": "Lo scanner IA analizza modelli, uso del colore e complessità per calcolare le ore di lavoro.",
  "hiw.card_stripe_title": "Portale Cliente Stripe",
  "hiw.card_stripe_text":
    "Sistema di abbonamento automatizzato. Aggiorna, scarica fatture o gestisci il tuo abbonamento in modo sicuro.",
  "hiw.card_analytics_title": "Dashboard analitica",
  "hiw.card_analytics_text": "Ottieni informazioni approfondite sulle conversioni e il potenziale di fatturato.",
  "hiw.card_blog_title": "Blog e SEO CMS",
  "hiw.card_blog_text": "Sistema integrato per pubblicare articoli che migliorano la visibilità su Google.",
  "hiw.card_global_title": "Architettura globale",
  "hiw.card_global_text": "Supporto multilingue completo con conservazione della lingua preferita del cliente.",
  "hiw.card_mobile_title": "Pronto per il mobile (PWA)",
  "hiw.card_mobile_text":
    "Inkvaluator è una Progressive Web App (PWA). Installa lo strumento direttamente sulla schermata home dal browser. Velocissimo, sicuro e senza occupare spazio di archiviazione.",
  "hiw.cta_title": "Cosa aspetti?",
  "hiw.cta_accent": "Smetti di calcolare, inizia a creare.",
  "hiw.cta_button": "Prova Inkvaluator gratis",
  "tos.seo_title": "Termini di servizio | Inkvaluator",
  "tos.seo_desc": "Leggi i termini di servizio di Inkvaluator.",
  "tos.title": "Termini di servizio",
  "tos.updated": "Ultimo aggiornamento:",
  "tos.s1_title": "1. Definizioni",
  "tos.s1_platform": "Piattaforma: Inkvaluator, una piattaforma SaaS disponibile su inkvaluator.com.",
  "tos.s1_user": "Utente / Artista: Qualsiasi tatuatore o studio che crea un account sulla Piattaforma.",
  "tos.s1_visitor": "Visitatore: Qualsiasi persona che utilizza il calcolatore pubblico o la sezione scoperta.",
  "tos.s1_service":
    "Servizio: Le funzionalità offerte da Inkvaluator, inclusi calcolatore prezzi, gestione lead, scanner IA e strumenti white-label.",
  "tos.s2_title": "2. Uso del calcolatore",
  "tos.s2_p1":
    "Le stime di prezzo sono solo indicative e non costituiscono un'offerta vincolante o garanzia di prezzo.",
  "tos.s2_p2":
    "Inkvaluator non è responsabile per decisioni basate sulle stime né per controversie tra artisti e clienti.",
  "tos.s3_title": "3. Abbonamenti e pagamenti",
  "tos.s3_intro": "Inkvaluator offre tre livelli: Free, Pro e Studio.",
  "tos.s3_billing":
    "Gli abbonamenti a pagamento vengono fatturati mensilmente tramite Stripe e rinnovati automaticamente.",
  "tos.s3_cancel":
    "Puoi cancellare il tuo abbonamento in qualsiasi momento. Dopo la cancellazione, mantieni l'accesso fino alla fine del periodo di fatturazione.",
  "tos.s3_refund": "Non sono previsti rimborsi per periodi di fatturazione già iniziati.",
  "tos.s3_price_change": "Inkvaluator si riserva il diritto di modificare i prezzi con preavviso di almeno 30 giorni.",
  "tos.s4_title": "4. Proprietà dei dati",
  "tos.s4_p1": "Tutti i dati inseriti da un Artista rimangono di proprietà dell'Artista.",
  "tos.s4_p2": "I dati di utilizzo anonimi e aggregati possono essere utilizzati per analisi statistiche.",
  "tos.s5_title": "5. Responsabilità",
  "tos.s5_p1": 'Inkvaluator fornisce il Servizio "così com\'è".',
  "tos.s5_indirect": "Inkvaluator non è responsabile per danni indiretti o mancati guadagni.",
  "tos.s5_cap": "La responsabilità totale è limitata all'importo pagato nei tre mesi precedenti.",
  "tos.s5_content": "Inkvaluator non è responsabile per i contenuti pubblicati dagli artisti.",
  "tos.s6_title": "6. Account e cessazione",
  "tos.s6_credentials": "Gli utenti sono responsabili della riservatezza delle credenziali.",
  "tos.s6_suspend": "Inkvaluator si riserva il diritto di sospendere o eliminare account in caso di abuso.",
  "tos.s6_delete": "Alla cessazione di un account, i dati personali verranno eliminati secondo la",
  "tos.s6_privacy_link": "Informativa sulla privacy",
  "tos.s7_title": "7. Proprietà intellettuale",
  "tos.s7_p1": "Tutti i diritti di proprietà intellettuale appartengono a Inkvaluator.",
  "tos.s8_title": "8. Legge applicabile",
  "tos.s8_p1": "Questi termini sono regolati dalla legge olandese.",
  "tos.contact_title": "Domande su questi termini?",
  "tos.contact_text": "Contattaci a:",
  "support.seo_title": "Supporto | Inkvaluator",
  "support.seo_desc": "Hai bisogno di aiuto? Consulta le nostre FAQ o contatta il nostro team.",
  "support.title": "Supporto",
  "support.faq_title": "Domande frequenti",
  "support.faq_q1": "Come imposto le mie tariffe?",
  "support.faq_a1": 'Vai alla dashboard e clicca su "Prezzi". Lì puoi impostare tariffa oraria, prezzo minimo e altro.',
  "support.faq_q2": "Come funziona l'integrazione Stripe?",
  "support.faq_a2":
    "Passando a Pro o Studio, verrai reindirizzato a un pagamento Stripe sicuro. L'abbonamento si attiva automaticamente.",
  "support.faq_q3": "Cos'è il modulo WhatsApp?",
  "support.faq_a3":
    "Con il modulo WhatsApp (da Pro), i visitatori possono contattarti via WhatsApp dopo aver calcolato un prezzo.",
  "support.faq_q4": "Come condivido il mio calcolatore?",
  "support.faq_a4": 'Nella dashboard sotto "Condividi" trovi un link diretto, codice QR e codice di integrazione.',
  "support.faq_q5": "Posso cancellare il mio abbonamento?",
  "support.faq_a5":
    "Sì, puoi cancellare in qualsiasi momento. Mantieni l'accesso fino alla fine del periodo di fatturazione.",
  "support.faq_q6": "Come funziona lo scanner IA?",
  "support.faq_a6": "Lo scanner IA analizza una foto e stima automaticamente dimensione, complessità e stile.",
  "support.faq_q7": "Ho trovato un bug, cosa faccio?",
  "support.faq_a7": "Invia un'email a support@inkvaluator.com con descrizione del problema e screenshot.",
  "support.contact_title": "Contatti",
  "support.contact_text": "La tua domanda non è qui? Contatta il nostro team:",
  "support.form_name": "Nome",
  "support.form_name_placeholder": "Il tuo nome",
  "support.form_email": "Email",
  "support.form_message": "Messaggio",
  "support.form_message_placeholder": "Descrivi la tua domanda o problema...",
  "support.form_submit": "Invia messaggio",
  "support.form_subject": "Richiesta di supporto da {name}",
  "support.toast_title": "Client email aperto",
  "support.toast_desc": "La tua applicazione email si sta aprendo con il messaggio.",
  "privacy.seo_title": "Informativa sulla privacy | Inkvaluator",
  "privacy.seo_desc": "Leggi l'informativa sulla privacy di Inkvaluator.",
  "privacy.title": "Informativa sulla privacy",
  "privacy.updated": "Ultimo aggiornamento:",
  "privacy.s1_title": "1. Quali dati raccogliamo?",
  "privacy.s1_intro": "Inkvaluator raccoglie i dati necessari per il funzionamento della piattaforma:",
  "privacy.s1_artists_label": "Per gli artisti",
  "privacy.s1_artists": "Nome, email, nome dello studio, indirizzo e portfolio.",
  "privacy.s1_visitors_label": "Per i visitatori",
  "privacy.s1_visitors": "Dati inseriti nel calcolatore per generare stime.",
  "privacy.s1_leads_label": "Lead",
  "privacy.s1_leads": "Quando contatti un artista, il tuo messaggio e le foto vengono elaborati in modo sicuro.",
  "privacy.s2_title": "2. Utilizzo dei dati",
  "privacy.s2_intro": "La tua privacy è la nostra priorità. Non vendiamo mai dati a terzi.",
  "privacy.s2_matching": "Abbinamento clienti con gli artisti giusti.",
  "privacy.s2_algorithms": "Miglioramento dei nostri algoritmi di prezzo.",
  "privacy.s2_pwa": "Funzionamento tecnico della PWA.",
  "privacy.s3_title": "3. Legislazione internazionale (GDPR)",
  "privacy.s3_intro": "Ai sensi della legislazione europea, hai i seguenti diritti:",
  "privacy.s3_access_label": "Diritto di accesso",
  "privacy.s3_access": "Puoi richiedere quali dati conserviamo.",
  "privacy.s3_erasure_label": "Diritto all'oblio",
  "privacy.s3_erasure": "Puoi richiedere la cancellazione completa del tuo account e dei tuoi dati.",
  "privacy.s3_portability_label": "Portabilità dei dati",
  "privacy.s3_portability": "Puoi richiedere i tuoi dati in un formato leggibile.",
  "privacy.s4_title": "4. Cookie e PWA",
  "privacy.s4_text": "Inkvaluator utilizza la cache funzionale (PWA). Non utilizziamo cookie di tracciamento.",
  "privacy.contact_title": "Domande sulla tua privacy?",
  "privacy.contact_text": "Contatta il nostro responsabile della protezione dei dati:",
  "dash.unsaved_changes": "Hai modifiche non salvate",
  "dash.dashboard_language": "Lingua della dashboard",
  "dash.dashboard_language_desc": "La lingua della tua interfaccia di gestione.",
  "auth.dashboard_subtitle": "Dashboard artista",
  "dash.impersonation_banner": "Stai visualizzando il profilo di {name}",
  "dash.stop_impersonation": "Ferma impersonazione",
  "discover.subtitle": "Scopri Studi & Artisti",
  "discover.description": "Trova tatuatori e studi vicino a te. Consulta il loro calcolatore e contattali.",
  "discover.hide_map": "Nascondi mappa",
  "discover.show_map": "Mostra mappa",
  "discover.map_loading": "Caricamento mappa...",
  "discover.call": "Chiama",
  "discover.route": "Percorso",
  "discover.map_title": "Artisti sulla mappa",
  "feature.calculator": "Calcolatore prezzi personalizzato",
  "feature.qr": "Codice QR e link condivisibile",
  "feature.styles": "Stili illimitati e scanner IA",
  "feature.logo": "Caricamento logo",
  "feature.theme": "Tema colore personalizzato",
  "feature.language": "Multilingue (17 lingue)",
  "feature.currency": "Cambio valuta (13 valute)",
  "feature.analytics": "Analisi di utilizzo",
  "feature.pdf": "Esportazione PDF",
  "feature.whitelabel": "Branding white-label",
  "feature.map_visibility": "Visibilità sulla mappa mondiale",
  "feature.multiartist": "Gestione multi-artista",
  "feature.centralanalytics": "Dashboard analitica centrale",
  "feature.teammanagement": "Gestione team e ruoli",
  "map.view_calculator": "Vedi calcolatore",
  "map.unknown_location": "Posizione sconosciuta",
  "map.artists_count": "artisti",
  "map.world_title": "Artisti",
  "map.world_accent": "nel mondo",
  "map.in_countries": "in {count} paesi",
  "blog.title": "Blog",
  "blog.no_posts": "Nessun articolo pubblicato.",
  "blog.read_more": "Leggi di più",
  "blog.published_on": "Pubblicato il",
  "blog.back": "Torna al blog",
  "blog.not_found": "Articolo non trovato",
  "blog.translation_notice":
    "Questo articolo non è ancora disponibile nella tua lingua. Viene mostrata la versione originale.",
  "landing.artist_or_studio": "Sei un artista o uno studio?",
  "demo.subtitle": "Playground",
  "demo.title": "Prova il",
  "demo.title_accent": "Calcolatore",
  "demo.description": "Regola le variabili e scopri come funziona il calcolatore dei prezzi.",
  "demo.your_rates": "Le tue tariffe",
  "demo.hourly_rate": "Tariffa oraria",
  "demo.start_cost": "Costi fissi iniziali",
  "demo.estimated_price": "Prezzo stimato",
  "demo.disclaimer": "Questa è una demo. Il prezzo reale dipende dall'artista e dal design.",
  "demo.artist_cta": "Sei un artista?",
  "demo.register_free": "Registrati gratis",
  "demo.find_studio": "Trova uno studio vicino",
  "profile.identity": "Identità",
  "profile.studio_name": "Nome dello studio",
  "profile.location": "Posizione",
  "profile.city": "Città",
  "profile.calculator_language": "Lingua del calcolatore",
  "embed.title": "Widget incorporabile",
  "embed.copy_code": "Copia codice",
  "vault.status_concept": "Bozza",
  "vault.status_sent": "Richiesta inviata",
  "reviews.rating": "valutazione",
  "reviews.verified_reviews": "recensioni verificate",
  "reviews.read_all": "Leggi tutte le recensioni su Google",
  "discover.swipe": "scorri",
  "profile.manual_place_id": "Studio non trovato? Inserisci il tuo Place ID manualmente.",
  "vault.inspiration_title": "Ispirazione",
  "vault.try_style": "Prova →",
  "vault.compare": "Confronta",
  "vault.compare_title": "Confronto",
  "vault.select_to_compare": "Seleziona 2 preventivi da confrontare",
  "trends.title": "Tendenze",
  "trends.title_accent": "Tatuaggi",
  "trends.subtitle": "Scopri gli stili e le posizioni più popolari al mondo.",
  "trends.top_styles": "Stili top",
  "trends.top_body_parts": "Parti del corpo popolari",
  "match.title": "Artisti compatibili vicino a te",
  "match.score": "Match",
  "match.view": "Vedi profilo",
  "urgency.recent_requests": "richieste questa settimana",
  "urgency.wait_time": "Attesa stimata",
  "urgency.weeks": "settimane",
  "urgency.high_demand": "Alta domanda",
  "urgency.popular": "Popolare",
  "benchmark.above_market": "sopra la media",
  "benchmark.below_market": "sotto la media",
  "benchmark.at_market": "Nella media",
  "benchmark.market_avg": "Media di mercato",
  "portfolio.style_examples": "Esempi di stile",
  "story.title": "Generatore di Story Instagram",
  "story.desc": "Crea una story premium Black & Gold con il tuo codice QR.",
  "story.generate": "Genera Story",
  "story.regenerate": "Rigenera",
  "story.download": "Scarica",
  "story.share": "Condividi",
  "story.downloaded": "Story scaricata!",
  "story.cta_line1": "Calcola Il Tuo Prezzo",
  "story.cta_line2": "Scansiona il codice QR",
  "story.scan_prompt": "↑ Scansiona per la tua stima",
  "trends.filter_country": "Filtra per paese",
  "trends.all_countries": "Tutti i paesi",
  "trends.analyzed": "calcoli analizzati",
  "trends.realtime": "Aggiornato in tempo reale",
  "match.style_experts": "Esperti in {style} vicino a {city}",
  "vault.review_booster_title": "Tatuaggio fatto? Aiuta il tuo artista!",
  "vault.review_booster_desc": "Lascia una recensione su Google e aiuta altri a trovare questo artista.",
  "vault.review_booster_btn": "Scrivi una recensione Google",
  "checklist.title": "Stato di verifica",
  "checklist.profile_complete": "Profilo completo",
  "checklist.google_linked": "Google Place ID collegato",
  "checklist.badge_detected": "Badge verificato sul sito",
  "checklist.push_enabled": "Notifiche push attivate",
  "checklist.not_yet": "Non ancora",
  "checklist.active": "Attivo",
  "lead.status_new": "Nuovo",
  "lead.status_contacted": "In contatto",
  "lead.status_booked": "Prenotato",
  "lead.change_status": "Stato",
  "lead.add_price": "Aggiungi prezzo finale",
  "push.test_body": "Le notifiche push sono ATTIVE! Riceverai avvisi per nuovi lead.",
  "push.send_test": "Invia notifica di test",
  "push.active_desc": "Ricevi notifiche quando arrivano lead di qualità.",
  "push.blocked": "Le notifiche sono bloccate nelle impostazioni del browser.",
  "push.enable_desc": "Ricevi notifiche quando arrivano lead di qualità.",
  "push.label": "Notifiche push",
  "push.welcome_body": "Benvenuto! Riceverai avvisi quando arrivano lead di qualità.",
  "push.denied_info": "Resta aggiornato sui nuovi lead — Attiva le notifiche nelle impostazioni del browser.",
  "vault.live_status": "Stato in diretta",
  "vault.status_new_desc": "La tua richiesta è stata ricevuta",
  "vault.status_contacted_desc": "L'artista ha visto il tuo messaggio",
  "vault.status_booked_desc": "Il tuo appuntamento è confermato!",
  "vault.link_copied": "🔗 Link copiato!",
  "analytics.actual_revenue": "Ricavi Effettivi",
  "style_city.popular_styles": "Stili popolari a {city}",
  "nav.home": "Home",
  "lead.estimate_header": "Preventivo Tatuaggio",
  "discover.verified_badge": "VERIFICATO",
  "wa.title": "Invia all'artista tramite WhatsApp",
  "wa.subtitle": "Invia la tua stima direttamente a",
  "wa.name": "Il tuo nome (opzionale)",
  "wa.name_placeholder": "Il tuo nome",
  "wa.message": "Messaggio (opzionale)",
  "wa.message_placeholder": "Descrivi la tua idea, ispirazione o dettagli...",
  "wa.photo": "Foto di riferimento (opzionale)",
  "wa.upload_photo": "Carica foto",
  "wa.file_too_large": "File troppo grande",
  "wa.summary": "Riepilogo:",
  "wa.send": "Invia tramite WhatsApp",
  "wa.sent_title": "Messaggio inviato!",
  "wa.sent_desc": "La tua richiesta è stata inviata a {name} tramite WhatsApp.",
  "wa.upload_failed": "Caricamento fallito",
  "wa.upload_failed_desc": "Impossibile caricare la foto. Riprova.",
  "wa.save_failed": "Salvataggio fallito",
  "wa.success": "Inviato!",
  "wa.success_desc": "La tua richiesta è stata creata e WhatsApp è stato aperto.",
  "wa.error": "Qualcosa è andato storto",
  "wa.error_desc": "Errore sconosciuto. Controlla la connessione e riprova.",
  "wa.msg_greeting": "Ciao {name}, ho calcolato un preventivo:",
  "wa.msg_name_label": "Nome",
  "wa.msg_style_label": "🎨 Stile",
  "wa.msg_bodypart_label": "📍 Parte del corpo",
  "wa.msg_size_label": "📏 Dimensione",
  "wa.msg_complexity_label": "⚙️ Complessità",
  "wa.msg_time_label": "⏱ Tempo stimato",
  "wa.msg_total_label": "💰 Totale",
  "wa.msg_deposit_label": "💳 Acconto",
  "wa.msg_details_label": "🔗 Dettagli",
  "wa.template_high_greeting":
    "Ciao {name}, ottime notizie! Ho calcolato un preventivo dettagliato per il mio tatuaggio:",
  "wa.template_high_vault": "🔗 L'ho salvato nel mio Vault Tatuaggi: {url}",
  "wa.template_low_greeting": "Ciao {name}, mi interessa un tatuaggio. Puoi dirmi disponibilità e prezzi?",
  "wa.template_low_note": "In base al calcolatore, una prima idea:",
  "tooltip.labor": "Basato sul tempo stimato per questa dimensione, moltiplicato per la tariffa oraria dell'artista.",
  "tooltip.complexity": "I design più complessi richiedono più tempo e precisione.",
  "tooltip.placement": "Alcune parti del corpo sono più sensibili o difficili da raggiungere.",
  "tooltip.style": "Stili diversi richiedono tecniche specifiche che influenzano il prezzo.",
  "tooltip.design": "Costi di progettazione e preparazione del tuo tatuaggio unico.",
  "tooltip.deposit": "L'acconto prenota il tuo appuntamento e viene detratto dal prezzo totale.",
  "pricing_edu.button": "Come funziona questo calcolo?",
  "pricing_edu.title": "Come viene calcolato il prezzo",
  "pricing_edu.intro":
    "Il prezzo di un tatuaggio è determinato da diversi fattori che riflettono l'artigianalità e il tempo.",
  "pricing_edu.time_title": "Tempo e Dimensione",
  "pricing_edu.time_desc": "I tatuaggi più grandi richiedono più tempo.",
  "pricing_edu.complexity_title": "Complessità e Dettaglio",
  "pricing_edu.complexity_desc": "I design dettagliati richiedono più precisione.",
  "pricing_edu.placement_title": "Posizionamento",
  "pricing_edu.placement_desc": "Alcune zone sono più sensibili o difficili da raggiungere.",
  "pricing_edu.style_title": "Stile del tatuaggio",
  "pricing_edu.style_desc": "Ogni stile richiede tecniche specifiche.",
  "pricing_edu.disclaimer":
    "Questa è sempre una stima. Il prezzo finale viene determinato dopo una consulenza personale.",
  "ai.title": "Scanner IA",
  "ai.desc": "Carica una foto e lascia che l'IA determini le impostazioni",
  "ai.upload_prompt": "Scatta una foto o carica un'immagine di riferimento",
  "ai.analyzing": "L'IA sta analizzando...",
  "ai.result_title": "Risultato IA",
  "ai.result_size": "Dimensione",
  "ai.result_complexity": "Complessità",
  "ai.result_style": "Stile",
  "ai.result_color": "Colore",
  "ai.yes": "Sì",
  "ai.no": "No",
  "ai.choose_other": "Scegli un'altra foto",
  "ai.result_placement": "Posizionamento",
  "ai.phase_detecting": "Rilevamento complessità...",
  "ai.phase_style": "Analisi stile...",
  "ai.phase_placement": "Rilevamento posizionamento...",
  "ai.phase_finalizing": "Finalizzazione stima...",
  "ai.scan_complete": "✨ Scansione IA completata",
  "ai.scan_applied": "Il calcolatore è stato aggiornato automaticamente.",
  "ai.scan_failed": "Scansione fallita",
  "ai.try_again": "Riprova",
  "referral.title": "Raccomanda un collega",
  "referral.desc": "Invita un collega e guadagna uno sconto.",
  "referral.generate": "Genera link di referral",
  "referral.share_link": "Condividi questo link:",
  "pwa.install_title": "Installa come app",
  "pwa.install_button": "Installa ora",
  // Batch 3+4
  "tab.theme": "Tema",
  "tab.branding": "Marchio",
  "tab.analytics": "Analisi",
  "tab.price_engine": "Motore prezzi",
  "tab.price_engine_desc": "Gestisci tutti i moltiplicatori di prezzo.",
  "basic.desc": "Imposta le tue tariffe e costi standard",
  "basic.min_price_desc": "Il prezzo più basso che applichi",
  "basic.hourly_rate_desc": "La tua tariffa oraria standard",
  "basic.daily_rate_placeholder": "Lascia vuoto se non applicabile",
  "basic.percentage": "%",
  "basic.fixed_amount": "Fisso",
  "basic.consultation_email": "Email di consulenza",
  "basic.consultation_email_desc": "Indirizzo email per le richieste",
  "basic.booking_url": "URL di prenotazione",
  "basic.booking_url_desc": "URL del sistema di prenotazione esterno",
  "sizes.title": "Impostazioni dimensione",
  "sizes.desc": "Imposta il tempo stimato per categoria di dimensione",
  "sizes.min_hours": "Min. ore",
  "sizes.max_hours": "Max. ore",
  "sizes.hours": "ore",
  "complexity.title": "Moltiplicatori di complessità",
  "complexity.desc": "Regola il fattore di prezzo in base alla complessità",
  "bodyparts.title": "Moltiplicatori parti del corpo",
  "bodyparts.desc": "Alcune zone sono più difficili da tatuare",
  "styles.title": "Gestione stili",
  "styles.desc": "Attiva o disattiva stili e regola il fattore di prezzo.",
  "theme.title": "Tema colore",
  "theme.desc": "Personalizza i colori del tuo calcolatore",
  "theme.preview": "Anteprima",
  "theme.preview_desc": "Ecco come appare il tuo calcolatore",
  "theme.hue": "Tonalità",
  "theme.saturation": "Saturazione",
  "theme.lightness": "Luminosità",
  "theme.presets": "Selezione rapida",
  "branding.title": "Marchio",
  "branding.desc": "Carica il logo e imposta lingua e valuta",
  "branding.logo": "Logo",
  "branding.logo_desc": "Logo dello studio (max 2 MB, PNG o JPG)",
  "branding.upload": "Carica logo",
  "branding.remove": "Rimuovi",
  "branding.language": "Lingua del calcolatore",
  "branding.currency": "Valuta",
  "branding.qr_code": "Codice QR",
  "branding.qr_desc": "I clienti scansionano questo codice per il tuo calcolatore.",
  "branding.download_qr": "Scarica QR",
  "onboard.step_minimum": "Tariffa minima",
  "onboard.step_hourly": "Tariffa oraria",
  "onboard.step_sizes": "Conferma dimensioni",
  "onboard.step_styles": "Imposta stili",
  "onboard.step_logo": "Carica logo",
  "onboard.step_finish": "Calcolatore pronto!",
  "dash.onboard_min_desc": "Imposta la tua tariffa minima.",
  "dash.onboard_hourly_desc": "Imposta la tua tariffa oraria. È la base di tutti i calcoli.",
  "dash.onboard_sizes_desc": "Categorie di dimensione predefinite configurate. Regola dopo.",
  "dash.onboard_styles_desc": "Tutti gli stili comuni attivati. Attiva/disattiva nella dashboard.",
  "dash.onboard_logo_desc": "Carica un logo ora o dopo nella scheda Marchio.",
  "dash.onboard_logo_later": "Carica logo dopo in Dashboard → Marchio",
  "dash.onboard_finish_title": "Il tuo calcolatore è pronto!",
  "dash.onboard_finish_desc": "Condividi il tuo link unico. Modifica le impostazioni in qualsiasi momento.",
  "dash.your_link": "Link del calcolatore:",
  "dash.online_card": "Il tuo biglietto da visita online.",
  "dash.view_my_calculator": "Vedi il mio calcolatore",
  "dash.upload_thumbnail": "Immagine personalizzata",
  "dash.reset_to_default": "Ripristina",
  "dash.reset_all_thumbnails": "Ripristina tutto",
  "dash.thumbnail_uploaded": "Immagine caricata!",
  "dash.thumbnail_reset": "Immagine ripristinata",
  "dash.thumbnails_reset_all": "Tutte le immagini ripristinate",
  "dash.invite_created": "Invito creato!",
  "dash.invite_error": "Errore invito",
  "dash.member_removed": "Membro rimosso",
  "dash.link_copied_generic": "Link copiato!",
  "dash.profile_saved": "Profilo salvato!",
  "dash.profile_save_error": "Errore salvataggio",
  "dash.calculator_ready": "🎉 Calcolatore pronto!",
  "dash.share_with_clients": "Condividi il tuo link.",
  "dash.step_of": "Passo {current} di {total}",
  "dash.start_inkprice": "Inizia con InkPrice",
  "laf.title": "Aspetto",
  "laf.desc": "Personalizza l'aspetto del tuo calcolatore pubblico.",
  "laf.color_mode": "Modalità colore",
  "laf.dark_mode": "Modalità scura",
  "laf.light_mode": "Modalità chiara",
  "laf.background": "Sfondo",
  "laf.solid_color": "Colore solido",
  "laf.image_url": "URL immagine",
  "laf.preview": "Anteprima",
  "laf.saved": "Aspetto salvato",
  "laf.presets": "Temi premium",
  "laf.font_selector": "Font del calcolatore",
  "laf.overlay_opacity": "Opacità overlay",
  "laf.bg_fixed": "Fisso (Parallax)",
  "laf.bg_scroll": "Scorrimento",
  "consult_settings.title": "Impostazioni consulenza",
  "consult_settings.desc": "Configura il contatto clienti.",
  "consult_settings.booking_url_label": "URL prenotazione",
  "consult_settings.email_label": "Email consulenza",
  "consult_settings.channels_title": "Canali lead",
  "consult_settings.channels_desc": "Metodi di contatto visibili.",
  "help.min_price": "Prezzo minimo assoluto per sessione.",
  "help.hourly_rate": "La tua tariffa oraria è la base di tutti i calcoli.",
  "help.multipliers": "I moltiplicatori regolano il prezzo per stile, complessità e posizionamento.",
  "help.sizes": "Definisci l'intervallo di tempo per ogni dimensione.",
  "help.complexity": "Regola i moltiplicatori per la complessità.",
  "help.body_parts": "Alcune zone richiedono moltiplicatori più alti.",
  "help.styles": "Attiva/disattiva stili e imposta i moltiplicatori.",
  "help.logo": "Il logo appare sul calcolatore e nel directory. Max 2 MB.",
  "help.currency": "La valuta mostrata sul calcolatore.",
  "help.calculator_language": "La lingua del calcolatore pubblico.",
  "help.dashboard_language": "La lingua di questa dashboard — visibile solo a te.",
  "profile.mfa_title": "Sicurezza account",
  "profile.mfa_desc": "Proteggi il tuo account con la 2FA.",
  "profile.mfa_enable": "Attiva 2FA",
  "profile.mfa_verify": "Verifica",
  "profile.mfa_active": "Attivo",
  "profile.mfa_enabled_success": "✅ 2FA attivata!",
  "profile.google_reviews": "Recensioni Google",
  "profile.google_reviews_desc": "Collega il tuo profilo Google Business.",
  "profile.sync_now": "Sincronizza",
  "profile.sync_success": "Recensioni Google sincronizzate!",
  "landing.faq_title": "FAQ",
  "landing.faq_subtitle": "Tutto su Inkvaluator",
  "landing.faq_q1": "Come funziona il calcolatore?",
  "landing.faq_a1": "Configura tariffa e moltiplicatori. I clienti ottengono un preventivo istantaneo.",
  "landing.faq_q2": "È gratuito?",
  "landing.faq_a2": "Sì! Il piano Free include un calcolatore completo. Passa a Pro per di più.",
  "landing.faq_q3": "Come funziona lo scanner IA?",
  "landing.faq_a3": "Carica una foto e l'IA analizza dimensione, complessità e stile in secondi.",
  "landing.faq_q4": "I clienti possono contattarmi?",
  "landing.faq_a4": "Sì, via WhatsApp con un messaggio precompilato.",
  "landing.faq_q5": "Supporto multilingue?",
  "landing.faq_a5": "17 lingue e 13 valute per artisti internazionali.",
  "landing.faq_q6": "Come ottenere più clienti?",
  "landing.faq_a6": "Artisti Pro/Studio sulla mappa mondiale e pagina scoperta.",
  "city.title": "Tatuatori a {city}",
  "city.description": "Trova tatuatori a {city}.",
  "city.no_artists": "Nessun artista trovato a {city}.",
  "city.discover_all": "Tutti gli artisti",
  "seo.landing_title": "Inkvaluator – Calcolatore Prezzi Tatuaggio",
  "seo.landing_desc": "Calcola il prezzo del tuo tatuaggio istantaneamente.",
  "vault.seo_title": "Il Mio Caveau Tatuaggio | Inkvaluator",
  "vault.title": "Il Mio",
  "vault.title_accent": "Caveau",
  "vault.desc": "I tuoi preventivi salvati.",
  "vault.empty": "Nessun preventivo. Usa un calcolatore!",
  "vault.save_btn": "Salva",
  "vault.saved_title": "Salvato!",
  "vault.saved_desc": "Preventivo salvato.",
  "success.active": "Attivo — tutto sbloccato",
  "success.setup_whatsapp": "Configura WhatsApp",
  "success.syncing": "Attivazione del piano…",
  "success.view_details": "Vedi dettagli piano",
  "analytics.title": "Analisi",
  "analytics.desc": "Utilizzo del calcolatore",
  "analytics.total_calculations": "Totale calcoli",
  "analytics.conversion_rate": "Tasso di conversione",
  "analytics.this_month": "Questo mese",
  "analytics.no_data": "Nessun dato ancora.",
  "studio.title": "Gestione studio",
  "studio.team": "Team",
  "studio.invite": "Invita artista",
  "studio.invite_desc": "Genera un link d'invito.",
  "studio.generate_link": "Genera link",
  "studio.no_members": "Nessun membro. Invita artisti.",
  "color.gold": "Oro",
  "color.red": "Rosso",
  "color.blue": "Blu",
  "color.green": "Verde",
  "color.purple": "Viola",
  "color.pink": "Rosa",
  "color.turquoise": "Turchese",
  "color.orange": "Arancione",
  "tip.title": "Raccomanda un artista",
  "tip.desc": "Conosci un tatuatore che dovrebbe essere su Inkvaluator?",
  "share.button": "Condividi",
  "sw.update_available": "Nuova versione disponibile.",
  "embed.iframe_desc": "Incolla questo nel tuo HTML.",
  "embed.floating_desc": "Incolla prima del </body>.",
  "embed.verified_badge": "Badge verificato",
  "embed.badge_desc": "Metti questo badge sul tuo sito.",
  "compare.title": "Confronto prezzi",
  "compare.disclaimer": "Confronto basato su un tatuaggio standard.",
  "landing.calculate_price": "Calcola il prezzo",
  "landing.total_value_prefix": "Già",
  "landing.total_value_suffix": "in arte del tatuaggio calcolato",
  "step.next": "Continua",
  "step.view_result": "Vedi prezzo",
  "step.details": "Dettagli",
  "step.details_desc": "Quanto è dettagliato il tuo design?",
  "layout.stepper": "Passo dopo passo",
  "layout.form": "Tutto in una volta",
  "calc.view_price_cta": "Vedi panoramica prezzi",
  "calc.rough_estimate": "Stima approssimativa",
  "calc.calculated_price": "Prezzo calcolato",
  "calc.save_for_later": "Salva per dopo",
  "calc.share": "Condividi",
  "calc.more_options": "Altre opzioni",
  "calc.compare_configs": "Confronta configurazioni",
  "calc.compare_desc": "Confronta due configurazioni di prezzo fianco a fianco",
  "calc.no_photo": "Nessuna foto di riferimento aggiunta",
  "calc.link_copied": "Link copiato!",
  "whatsapp.add_photo": "Aggiungi la tua foto",
  "whatsapp.photos_label": "Foto di riferimento — max. 3",
  "laf.reset_success": "Tema ripristinato ai valori predefiniti",
  "admin.stripe_managed":
    "Gli abbonamenti sono gestiti automaticamente tramite Stripe — le modifiche sono visibili dopo la prossima sincronizzazione webhook",
  "calc.view_tattoo_plan": "Vedi il tuo Tattoo Plan ✓",
  "wa.plan_message": "Ecco il mio Tattoo Plan:",
  "lead.tattoo_plan": "Tattoo Plan",
  "lead.made_with": "Creato con Inkvaluator",
  "lead.contact_artist": "Contatta {artist}",
  "lead.share": "Condividi",
  "lead.share_title": "Il mio Tattoo Plan",
  "lead.link_copied": "Link copiato!",
  "lead.deposit_label": "Acconto",
  "lead.pay_deposit": "Paga acconto {amount}",
  "lead.deposit_request_title": "Richiedi accordo di acconto",
  "lead.deposit_request_desc": "Chiedi all'artista se è possibile un acconto di ~{amount}.",
  "lead.deposit_request_wa": "Invia richiesta via WhatsApp",
  "lead.for_you": "Te",
  "lead.for_label": "Per",
  "lead.back_to_calculator": "Torna al calcolatore",
  "lead.deposit_wa_msg":
    "Ciao {artist}, ho consultato il mio Tattoo Plan su Inkvaluator. Sarebbe possibile un acconto di ~{amount}?\nPiano: {url}",
  "blog.no_translation": "Nessuna traduzione disponibile",
  "flash.tab": "Flash",
  "flash.upload": "Carica flash design",
  "flash.title": "Titolo",
  "flash.price": "Prezzo",
  "flash.size": "Dimensione (cm)",
  "flash.available": "Disponibile",
  "flash.featured": "In evidenza",
  "flash.limit_used": "di {max} flash design usati",
  "flash.delete_confirm": "Eliminare il flash design?",
  "flash.reserve_whatsapp": "Prenota via WhatsApp",
  "flash.whatsapp_msg": "Sono interessato al tuo flash tattoo:",
  "flash.matching_designs": "Flash design corrispondenti",
  "flash.only_with_flash": "Solo artisti con flash",
  "groei.score": "Punteggio profilo",
  "groei.views": "visite al profilo",
  "groei.sessions": "sessioni calcolatore",
  "groei.leads": "leads",
  "groei.this_week": "questa settimana",
  "groei.improve": "Migliora il tuo profilo",
  "groei.add_photos": "Aggiungi più foto al portfolio",
  "groei.add_whatsapp": "Imposta il tuo numero WhatsApp",
  "groei.add_bio": "Scrivi una bio più lunga",
  "groei.add_avatar": "Carica una foto profilo",
  "groei.add_rate": "Imposta la tua tariffa oraria",
  "groei.add_city": "Aggiungi la tua città",
  "groei.get_leads": "Condividi il tuo calcolatore per ottenere leads",
  "groei.collapse": "Comprimi",
  "groei.expand": "Espandi",
  "groei.tip_portfolio": "Artisti con 6+ foto ottengono 3x più sessioni",
  "groei.tip_whatsapp": "Clienti con pulsante WhatsApp convertono 5x meglio",
  "groei.tip_leads": "Condividi il link del calcolatore per ricevere il primo lead",
  "groei.tip_avatar": "Una foto profilo aumenta la tua credibilità",
  "groei.tip_action_photos": "Aggiungi foto",
  "groei.tip_action_whatsapp": "Configura WhatsApp",
  "groei.tip_action_share": "Condividi calcolatore",
  "groei.tip_action_avatar": "Carica foto",
  "milestone.eerste_lead": "Primo lead ricevuto!",
  "milestone.tien_leads": "10 lead raggiunti!",
  "milestone.profiel_compleet": "Profilo 100% completo!",
  "milestone.eerste_flash": "Primo flash design caricato!",
  "wa.name_required": "Il nome è obbligatorio",
  "wa.plan_message_full": "Vedi il mio Tattoo Plan completo: {url}",
};

// ─── PORTUGUESE (complete) ─────────────────────────────────────
const pt: Record<string, string> = {
  ...en,
  "app.tagline": "Calcule o preço da sua tatuagem",
  back: "Voltar",
  save: "Salvar",
  copy: "Copiar",
  copied: "Copiado!",
  loading: "Carregando...",
  error: "Erro",
  optional: "opcional",
  "landing.hero": "Calcule o preço da",
  "landing.hero_accent": "sua Tatuagem",
  "landing.subtitle": "Insira o código do seu tatuador para obter uma estimativa personalizada.",
  "landing.start": "Iniciar calculadora",
  "landing.invalid_code": "Insira um código válido",
  "landing.or": "ou",
  "landing.artist_cta": "É tatuador? Crie sua própria calculadora.",
  "landing.hero_title": "Calcule o preço da",
  "landing.hero_title_accent": "sua Tatuagem",
  "landing.hero_desc": "Descubra em segundos quanto custa sua próxima tatuagem.",
  "landing.try_it": "Experimente!",
  "landing.estimated_price": "Preço estimado",
  "landing.your_rates": "Suas tarifas",
  "landing.rates_desc": "Mova os controles e veja o preço mudar",
  "landing.hourly_rate": "Tarifa por hora",
  "landing.start_cost": "Custos fixos iniciais",
  "landing.size_label": "Tamanho",
  "landing.complexity_label": "Complexidade",
  "landing.body_placement": "Localização corporal",
  "landing.find_artist": "Encontre seu",
  "landing.find_artist_accent": "artista perfeito",
  "landing.nearby_soon": "Em breve artistas perto de você aparecerão aqui.",
  "landing.discover_all": "Descobrir todos os artistas",
  "landing.artist_question": "É artista ou estúdio?",
  "landing.view_options": "Ver opções e preços",
  "landing.start_free": "Começar grátis",
  "landing.discover": "Descobrir",
  "landing.login": "Entrar",
  "landing.founder_desc": "50% de desconto para os primeiros 50 cadastros",
  "landing.claim_now": "Reivindique agora →",
  "calc.estimate": "Preço estimado",
  "calc.size": "Tamanho",
  "calc.size_desc": "Qual será o tamanho da sua tatuagem?",
  "calc.complexity": "Complexidade",
  "calc.body_part": "Parte do corpo",
  "calc.style": "Estilo",
  "calc.budget": "Orçamento",
  "calc.back_home": "Voltar ao início",
  "calc.add_color": "Adicionar cor?",
  "calc.color_surcharge": "+20% sobretaxa para trabalho colorido",
  "breakdown.title": "Detalhamento do preço",
  "breakdown.labor": "Mão de obra (tempo)",
  "breakdown.subtotal": "Subtotal",
  "breakdown.design_cost": "Custo do design",
  "breakdown.deposit": "Sinal",
  "breakdown.total_indication": "Indicação total",
  "breakdown.hours_unit": "horas",
  "faq.title": "Perguntas frequentes",
  "dash.welcome": "Bem-vindo ao Inkvaluator",
  "dash.logout": "Sair",
  "dash.saved": "Salvo!",
  "dash.save_changes": "Salvar alterações",
  "dash.cancel": "Cancelar",
  "dash.add": "Adicionar",
  "dash.delete": "Excluir",
  "dash.previous": "Anterior",
  "dash.next": "Próximo",
  "tab.basic": "Básico",
  "tab.sizes": "Tamanhos",
  "tab.complexity": "Complexidade",
  "tab.body_parts": "Partes do corpo",
  "tab.styles": "Estilos",
  "basic.title": "Tarifa base",
  "basic.min_price": "Preço mínimo",
  "basic.hourly_rate": "Tarifa por hora",
  "basic.design_cost": "Custo do design",
  "basic.fixed": "Fixo",
  "basic.hourly": "Por hora",
  "basic.deposit": "Sinal",
  "size.mini": "Mini (< 5 cm)",
  "size.small": "Pequeno (5-10 cm)",
  "size.medium": "Médio (10-20 cm)",
  "size.large": "Grande (20-35 cm)",
  "size.xl": "Extra Grande (35+ cm)",
  "complexity.simple": "Simples",
  "complexity.medium": "Médio",
  "complexity.detailed": "Detalhado",
  "body.arm": "Braço (geral)",
  "body.forearm": "Antebraço",
  "body.upper_arm": "Braço superior",
  "body.leg": "Perna (geral)",
  "body.thigh": "Coxa",
  "body.calf": "Panturrilha",
  "body.back": "Costas",
  "body.chest": "Peito",
  "body.ribs": "Costelas",
  "body.stomach": "Abdômen",
  "body.hand": "Mão",
  "body.foot": "Pé",
  "body.neck": "Pescoço",
  "body.face": "Rosto",
  "body.shoulder": "Ombro",
  "body.hip": "Quadril",
  "body.finger": "Dedo",
  "body.other": "Outro",
  "body.selected": "Selecionado",
  "body.front": "Frente",
  "body.back_view": "Costas",
  "body.front_view": "Frente",
  "body.back_view_btn": "Costas",
  "style.black_grey": "Preto e Cinza",
  "style.full_color": "Cor completa",
  "style.realism": "Realismo",
  "style.fine_line": "Fine Line / Minimal",
  "style.traditional": "Tradicional",
  "style.neo_traditional": "Neo Tradicional",
  "style.watercolor": "Aquarela",
  "style.tribal": "Tribal",
  "style.japanese": "Japonês",
  "style.geometric": "Geométrico",
  "style.lettering": "Lettering",
  "style.script": "Caligrafia",
  "style.dotwork": "Dotwork",
  "style.mandala": "Mandala",
  "style.blackwork": "Blackwork",
  "style.illustrative": "Ilustrativo",
  "style.surrealism": "Surrealismo",
  "style.ornamental": "Ornamental",
  "style.micro_realism": "Micro Realismo",
  "style.trash_polka": "Trash Polka",
  "style.other": "Outro",
  "consult.title": "Pronto para começar?",
  "consult.book": "Agendar consulta",
  "pdf.title": "Orçamento",
  "pdf.download": "Baixar PDF",
  "upgrade.choose": "Escolha seu",
  "upgrade.plan_word": "Plano",
  "upgrade.per_month": "/mês",
  "upgrade.monthly": "Mensal",
  "upgrade.yearly": "Anual",
  "upgrade.mo": "mês",
  "upgrade.year": "ano",
  "billing.expires": "Expira",
  "billing.change": "Alterar",
  "billing.next_invoice": "Próxima fatura",
  "billing.invoice_history": "Histórico de faturas",
  "upgrade.perfect_start": "Perfeito para começar",
  "upgrade.everything_pro": "Tudo para profissionais",
  "upgrade.for_studios": "Para estúdios de tatuagem & equipes",
  "upgrade.upgrade_pro": "Mudar para Pro",
  "upgrade.upgrade_studio": "Mudar para Studio",
  "upgrade.discount": "desconto",
  "auth.login": "Entrar",
  "auth.register": "Registrar",
  "discover.title": "Descobrir",
  "discover.title_accent": "Artistas",
  "discover.no_artists": "Nenhum artista encontrado",
  "discover.view_calculator": "Ver calculadora",
  "footer.tagline": "A calculadora de preços de tatuagem mais inteligente para artistas e clientes.",
  "footer.links": "Links",
  "footer.discover_artists": "Descobrir artistas",
  "footer.artist_login": "Login artista",
  "footer.privacy": "Privacidade",
  "footer.language_label": "Idioma & App",
  "footer.pwa_badge": "Otimizado PWA (clique para info)",
  "footer.all_rights": "Todos os direitos reservados.",
  "footer.install_title": "Instalar",
  "footer.install_ok": "Entendi!",
  "success.welcome": "Bem-vindo ao Clube,",
  "success.go_dashboard": "Ir para o painel",
  "landing.how_it_works_cta": "Veja como funciona para o seu estúdio",
  "footer.terms": "Termos de serviço",
  "footer.support": "Suporte",
  "footer.how_it_works": "Como funciona?",
  "hiw.seo_title": "Como funciona? – Inkvaluator",
  "hiw.seo_desc":
    "Descubra como o Inkvaluator ajuda tatuadores com estimativas automáticas, leads inteligentes e branding profissional.",
  "hiw.hero_title": "Foque na tinta, não na fatura",
  "hiw.hero_p1":
    "Você gasta mais tempo respondendo DMs sobre preços do que desenhando sua próxima obra-prima? Sejamos honestos: você é tatuador, não atendente.",
  "hiw.hero_p2": 'Cada pergunta "Quanto custa?" que você responde manualmente é tempo que não dedica à sua arte.',
  "hiw.hero_p3":
    "O Inkvaluator é o assistente digital que filtra seu negócio. Enquanto você está na zona, a ferramenta calcula estimativas precisas, coleta todas as informações e envia uma solicitação pronta direto no seu WhatsApp.",
  "hiw.hero_p4":
    'Chega de "curiosos de preço" sem orçamento. Apenas leads sérios, orçamentos profissionais e um branding que mostra que você leva seu ofício a sério.',
  "hiw.hero_cta": "Pronto para recuperar seu tempo de trabalho?",
  "hiw.faq_title": "Perguntas frequentes",
  "hiw.faq_cat1": "O básico: como isso me ajuda?",
  "hiw.faq_q1": "É mais um app complicado?",
  "hiw.faq_a1":
    "Não. É uma calculadora ultrarrápida para sua bio do Instagram ou site. Configure suas regras uma vez e a ferramenta faz o resto.",
  "hiw.faq_q2": "Como economiza meu tempo concretamente?",
  "hiw.faq_a2":
    "Em vez de perguntar dez vezes, você recebe uma mensagem no WhatsApp com tudo preenchido, incluindo foto de referência e preço aceito.",
  "hiw.faq_cat2": "Preços e configurações",
  "hiw.faq_q3": "Posso definir meus próprios preços?",
  "hiw.faq_a3": "Com certeza. Você é o chefe. Define sua tarifa por hora e determina o que cobrar.",
  "hiw.faq_q4": "E se uma tatuagem for muito mais complexa?",
  "hiw.faq_a4": "Nosso scanner IA reconhece os detalhes na foto e ajusta a estimativa de preço automaticamente.",
  "hiw.faq_cat3": "Aparência e branding",
  "hiw.faq_q5": "Não parece uma ferramenta genérica?",
  "hiw.faq_a5":
    "De jeito nenhum. Com o plano Studio a ferramenta é completamente white-label: seu logo, suas cores, seu estilo.",
  "hiw.faq_q6": "Quão profissional isso parece para o cliente?",
  "hiw.faq_a6": "Muito profissional. O cliente pode baixar o orçamento como PDF elegante.",
  "hiw.faq_cat4": "Crescimento e alcance",
  "hiw.faq_q7": "Quero mais clientes da minha região, isso ajuda?",
  "hiw.faq_a7": "Sim! O Inkvaluator tem um mapa mundial interativo para busca por país, província e cidade.",
  "hiw.backend_title": "Nos bastidores: o poder do backend",
  "hiw.backend_intro": "Para os amantes de tecnologia: Inkvaluator está repleto de recursos avançados.",
  "hiw.card_pricing_title": "Gestão dinâmica de preços",
  "hiw.card_pricing_text": "Ajuste preços e IDs Stripe pelo módulo admin sem tocar em código.",
  "hiw.card_ai_title": "Integração AI Vision",
  "hiw.card_ai_text": "O scanner IA analisa padrões, cores e complexidade para calcular horas de trabalho.",
  "hiw.card_stripe_title": "Portal do Cliente Stripe",
  "hiw.card_stripe_text": "Sistema de assinatura automatizado. Upgrade, faturas e gestão em ambiente seguro.",
  "hiw.card_analytics_title": "Painel analítico",
  "hiw.card_analytics_text": "Obtenha insights sobre conversões e receita potencial.",
  "hiw.card_blog_title": "Blog e SEO CMS",
  "hiw.card_blog_text": "Sistema integrado para publicar artigos que melhoram sua visibilidade no Google.",
  "hiw.card_global_title": "Arquitetura global",
  "hiw.card_global_text": "Suporte multilíngue completo com preservação do idioma preferido.",
  "hiw.card_mobile_title": "Pronto para mobile (PWA)",
  "hiw.card_mobile_text":
    "Inkvaluator é uma Progressive Web App (PWA). Instale a ferramenta diretamente na tela inicial pelo navegador. Ultra rápido, seguro e sem ocupar espaço de armazenamento.",
  "hiw.cta_title": "O que está esperando?",
  "hiw.cta_accent": "Pare de calcular, comece a criar.",
  "hiw.cta_button": "Experimente Inkvaluator grátis",
  "tos.seo_title": "Termos de serviço | Inkvaluator",
  "tos.title": "Termos de serviço",
  "tos.updated": "Última atualização:",
  "tos.s1_title": "1. Definições",
  "tos.s1_platform": "Plataforma: Inkvaluator, uma plataforma SaaS disponível em inkvaluator.com.",
  "tos.s1_user": "Utilizador / Artista: Qualquer tatuador ou estúdio que crie uma conta na Plataforma.",
  "tos.s1_visitor": "Visitante: Qualquer pessoa que utilize a calculadora pública ou a seção de descoberta.",
  "tos.s1_service": "Serviço: As funcionalidades oferecidas pelo Inkvaluator.",
  "tos.s2_title": "2. Uso da calculadora",
  "tos.s2_p1": "As estimativas de preço são apenas indicativas e não constituem oferta vinculante.",
  "tos.s2_p2": "O Inkvaluator não é responsável por decisões baseadas nas estimativas.",
  "tos.s3_title": "3. Assinaturas e pagamentos",
  "tos.s3_intro": "O Inkvaluator oferece três níveis: Free, Pro e Studio.",
  "tos.s3_billing": "Assinaturas pagas são faturadas mensalmente via Stripe e renovadas automaticamente.",
  "tos.s3_cancel":
    "Você pode cancelar sua assinatura a qualquer momento. Após o cancelamento, mantém acesso até o final do período de faturamento.",
  "tos.s3_refund": "Reembolsos não são possíveis para períodos já iniciados.",
  "tos.s3_price_change": "O Inkvaluator reserva-se o direito de alterar preços com aviso de 30 dias.",
  "tos.s4_title": "4. Propriedade dos dados",
  "tos.s4_p1": "Todos os dados inseridos por um Artista permanecem propriedade do Artista.",
  "tos.s4_p2": "Dados de uso anônimos e agregados podem ser usados para análise estatística.",
  "tos.s5_title": "5. Responsabilidade",
  "tos.s5_p1": 'O Inkvaluator fornece o Serviço "como está".',
  "tos.s5_indirect": "O Inkvaluator não é responsável por danos indiretos.",
  "tos.s5_cap": "A responsabilidade total é limitada ao valor pago nos três meses anteriores.",
  "tos.s5_content": "O Inkvaluator não é responsável pelo conteúdo publicado pelos artistas.",
  "tos.s6_title": "6. Conta e encerramento",
  "tos.s6_credentials": "Os utilizadores são responsáveis pela confidencialidade das credenciais.",
  "tos.s6_suspend": "O Inkvaluator reserva-se o direito de suspender ou eliminar contas em caso de abuso.",
  "tos.s6_delete": "Ao encerrar uma conta, os dados pessoais serão eliminados conforme a",
  "tos.s6_privacy_link": "Política de privacidade",
  "tos.s7_title": "7. Propriedade intelectual",
  "tos.s7_p1": "Todos os direitos de propriedade intelectual pertencem ao Inkvaluator.",
  "tos.s8_title": "8. Lei aplicável",
  "tos.s8_p1": "Estes termos são regidos pela lei holandesa.",
  "tos.contact_title": "Perguntas sobre estes termos?",
  "tos.contact_text": "Contacte-nos em:",
  "support.seo_title": "Suporte | Inkvaluator",
  "support.title": "Suporte",
  "support.faq_title": "Perguntas frequentes",
  "support.faq_q1": "Como defino as minhas tarifas?",
  "support.faq_a1": 'Vá ao painel e clique em "Preços" para configurar tarifa horária, preço mínimo e mais.',
  "support.faq_q2": "Como funciona a integração Stripe?",
  "support.faq_a2": "Ao atualizar para Pro ou Studio, será redirecionado para um pagamento Stripe seguro.",
  "support.faq_q3": "O que é o módulo WhatsApp?",
  "support.faq_a3":
    "Com o módulo WhatsApp (desde Pro), visitantes podem contactá-lo via WhatsApp após calcular um preço.",
  "support.faq_q4": "Como partilho a minha calculadora?",
  "support.faq_a4": 'No painel, em "Partilhar", encontra um link direto, código QR e código de integração.',
  "support.faq_q5": "Posso cancelar a assinatura?",
  "support.faq_a5": "Sim, pode cancelar a qualquer momento e mantém acesso até o final do período de faturamento.",
  "support.faq_q6": "Como funciona o scanner IA?",
  "support.faq_a6": "O scanner IA analisa uma foto e estima automaticamente tamanho, complexidade e estilo.",
  "support.faq_q7": "Encontrei um bug, o que faço?",
  "support.faq_a7": "Envie um email para support@inkvaluator.com com descrição e screenshots.",
  "support.contact_title": "Contacto",
  "support.contact_text": "A sua pergunta não está aqui? Contacte a nossa equipa:",
  "support.form_name": "Nome",
  "support.form_name_placeholder": "O seu nome",
  "support.form_email": "Email",
  "support.form_message": "Mensagem",
  "support.form_message_placeholder": "Descreva a sua questão ou problema...",
  "support.form_submit": "Enviar mensagem",
  "support.form_subject": "Pedido de suporte de {name}",
  "support.toast_title": "Cliente de email aberto",
  "support.toast_desc": "A sua aplicação de email está a abrir com a sua mensagem.",
  "privacy.seo_title": "Política de privacidade | Inkvaluator",
  "privacy.seo_desc": "Leia a política de privacidade do Inkvaluator.",
  "privacy.title": "Política de privacidade",
  "privacy.updated": "Última atualização:",
  "privacy.s1_title": "1. Que dados recolhemos?",
  "privacy.s1_intro": "O Inkvaluator recolhe dados necessários para o funcionamento da plataforma:",
  "privacy.s1_artists_label": "Para artistas",
  "privacy.s1_artists": "Nome, email, nome do estúdio, morada e portfólio.",
  "privacy.s1_visitors_label": "Para visitantes",
  "privacy.s1_visitors": "Dados inseridos na calculadora para gerar estimativas.",
  "privacy.s1_leads_label": "Leads",
  "privacy.s1_leads": "Ao contactar um artista, a sua mensagem e fotos são processadas de forma segura.",
  "privacy.s2_title": "2. Utilização de dados",
  "privacy.s2_intro": "A sua privacidade é a nossa prioridade. Nunca vendemos dados a terceiros.",
  "privacy.s2_matching": "Ligar clientes aos artistas certos.",
  "privacy.s2_algorithms": "Melhorar os nossos algoritmos de preços.",
  "privacy.s2_pwa": "Funcionamento técnico da PWA.",
  "privacy.s3_title": "3. Legislação internacional (RGPD)",
  "privacy.s3_intro": "De acordo com a legislação europeia, tem os seguintes direitos:",
  "privacy.s3_access_label": "Direito de acesso",
  "privacy.s3_access": "Pode solicitar que dados temos.",
  "privacy.s3_erasure_label": "Direito ao esquecimento",
  "privacy.s3_erasure": "Pode solicitar a eliminação completa da sua conta e dados.",
  "privacy.s3_portability_label": "Portabilidade de dados",
  "privacy.s3_portability": "Pode solicitar os seus dados num formato legível.",
  "privacy.s4_title": "4. Cookies e PWA",
  "privacy.s4_text": "O Inkvaluator utiliza cache funcional (PWA). Não usamos cookies de rastreamento.",
  "privacy.contact_title": "Perguntas sobre a sua privacidade?",
  "privacy.contact_text": "Contacte o nosso responsável pela proteção de dados:",
  "dash.unsaved_changes": "Tem alterações por guardar",
  "dash.dashboard_language": "Idioma do painel",
  "dash.dashboard_language_desc": "O idioma da sua interface de gestão.",
  "auth.dashboard_subtitle": "Painel do artista",
  "dash.impersonation_banner": "Está a ver o perfil de {name}",
  "dash.stop_impersonation": "Parar impersonação",
  "discover.subtitle": "Descubra Estúdios & Artistas",
  "discover.description": "Encontre tatuadores e estúdios perto de si. Consulte a sua calculadora e entre em contacto.",
  "discover.hide_map": "Ocultar mapa",
  "discover.show_map": "Mostrar mapa",
  "discover.map_loading": "A carregar mapa...",
  "discover.call": "Ligar",
  "discover.route": "Rota",
  "discover.map_title": "Artistas no mapa",
  "feature.calculator": "Calculadora de preços personalizada",
  "feature.qr": "Código QR e link partilhável",
  "feature.styles": "Estilos ilimitados e scanner IA",
  "feature.logo": "Upload de logo",
  "feature.theme": "Tema de cor personalizado",
  "feature.language": "Multilingue (17 idiomas)",
  "feature.currency": "Troca de moeda (13 moedas)",
  "feature.analytics": "Análise de utilização",
  "feature.pdf": "Exportação PDF",
  "feature.whitelabel": "Branding white-label",
  "feature.map_visibility": "Visibilidade no mapa mundial",
  "feature.multiartist": "Gestão multi-artista",
  "feature.centralanalytics": "Painel analítico central",
  "feature.teammanagement": "Gestão de equipa e funções",
  "map.view_calculator": "Ver calculadora",
  "map.unknown_location": "Localização desconhecida",
  "map.artists_count": "artistas",
  "map.world_title": "Artistas",
  "map.world_accent": "no mundo",
  "map.in_countries": "em {count} países",
  "blog.title": "Blog",
  "blog.no_posts": "Ainda não há artigos publicados.",
  "blog.read_more": "Leia mais",
  "blog.published_on": "Publicado em",
  "blog.back": "Voltar ao blog",
  "blog.not_found": "Artigo não encontrado",
  "blog.translation_notice": "Este artigo ainda não está disponível no seu idioma. A versão original é exibida.",
  "landing.artist_or_studio": "É artista ou estúdio?",
  "demo.subtitle": "Playground",
  "demo.title": "Experimente a",
  "demo.title_accent": "Calculadora",
  "demo.estimated_price": "Preço estimado",
  "demo.disclaimer": "Isto é uma demo. O preço real depende do artista e do design.",
  "demo.artist_cta": "É artista?",
  "demo.register_free": "Registar grátis",
  "demo.find_studio": "Encontrar um estúdio perto",
  "profile.identity": "Identidade",
  "profile.studio_name": "Nome do estúdio",
  "profile.location": "Localização",
  "profile.city": "Cidade",
  "profile.calculator_language": "Idioma da calculadora",
  "embed.title": "Widget incorporável",
  "embed.copy_code": "Copiar código",
  "vault.status_concept": "Rascunho",
  "vault.status_sent": "Pedido enviado",
  "reviews.rating": "avaliação",
  "reviews.verified_reviews": "avaliações verificadas",
  "reviews.read_all": "Ler todas as avaliações no Google",
  "discover.swipe": "deslizar",
  "profile.manual_place_id": "Estúdio não encontrado? Insira seu Place ID manualmente.",
  "vault.inspiration_title": "Inspiração",
  "vault.try_style": "Experimentar →",
  "vault.compare": "Comparar",
  "vault.compare_title": "Comparação",
  "trends.title": "Tendências",
  "trends.title_accent": "Tatuagens",
  "trends.top_styles": "Estilos populares",
  "trends.top_body_parts": "Partes do corpo populares",
  "match.title": "Artistas compatíveis perto de si",
  "match.score": "Match",
  "match.view": "Ver perfil",
  "urgency.recent_requests": "pedidos esta semana",
  "urgency.wait_time": "Espera estimada",
  "urgency.weeks": "semanas",
  "urgency.high_demand": "Alta procura",
  "urgency.popular": "Popular",
  "benchmark.above_market": "acima da média",
  "benchmark.below_market": "abaixo da média",
  "benchmark.at_market": "Na média",
  "benchmark.market_avg": "Média do mercado",
  "portfolio.style_examples": "Exemplos de estilo",
  "story.title": "Gerador de Stories Instagram",
  "story.desc": "Crie uma story premium Black & Gold com o seu código QR.",
  "story.generate": "Gerar Story",
  "story.regenerate": "Regenerar",
  "story.download": "Baixar",
  "story.share": "Partilhar",
  "story.downloaded": "Story transferida!",
  "story.cta_line1": "Calcule O Seu Preço",
  "story.cta_line2": "Leia o código QR abaixo",
  "story.scan_prompt": "↑ Leia para a sua estimativa",
  "trends.filter_country": "Filtrar por país",
  "trends.all_countries": "Todos os países",
  "trends.analyzed": "cálculos analisados",
  "trends.realtime": "Atualizado em tempo real",
  "match.style_experts": "Especialistas em {style} perto de {city}",
  "vault.review_booster_title": "Tatuagem feita? Ajude o seu artista!",
  "vault.review_booster_desc": "Deixe uma avaliação no Google e ajude outros a encontrar este artista.",
  "vault.review_booster_btn": "Escrever avaliação Google",
  "checklist.title": "Estado de verificação",
  "checklist.profile_complete": "Perfil completo",
  "checklist.google_linked": "Google Place ID ligado",
  "checklist.badge_detected": "Badge verificado no site",
  "checklist.push_enabled": "Notificações push ativadas",
  "checklist.not_yet": "Ainda não",
  "checklist.active": "Ativo",
  "lead.status_new": "Novo",
  "lead.status_contacted": "Em contacto",
  "lead.status_booked": "Reservado",
  "lead.change_status": "Estado",
  "lead.add_price": "Adicionar preço final",
  "push.test_body": "As notificações push estão ATIVAS! Receberá alertas de novos leads.",
  "push.send_test": "Enviar notificação de teste",
  "push.active_desc": "Recebe notificações quando chegam leads de qualidade.",
  "push.blocked": "As notificações estão bloqueadas nas definições do navegador.",
  "push.enable_desc": "Receba notificações quando chegarem leads de qualidade.",
  "push.label": "Notificações push",
  "push.welcome_body": "Bem-vindo! Receberá alertas quando chegarem leads de qualidade.",
  "push.denied_info": "Mantenha-se informado sobre novos leads — Ative as notificações nas definições do navegador.",
  "vault.live_status": "Estado ao vivo",
  "vault.status_new_desc": "O seu pedido foi recebido",
  "vault.status_contacted_desc": "O artista viu a sua mensagem",
  "vault.status_booked_desc": "A sua consulta está confirmada!",
  "vault.link_copied": "🔗 Link copiado!",
  "analytics.actual_revenue": "Receita Real",
  "style_city.popular_styles": "Estilos populares em {city}",
  "nav.home": "Início",
  "lead.estimate_header": "Estimativa de Tatuagem",
  "discover.verified_badge": "VERIFICADO",
  "wa.title": "Enviar ao artista pelo WhatsApp",
  "wa.subtitle": "Envie sua estimativa diretamente para",
  "wa.name": "Seu nome (opcional)",
  "wa.name_placeholder": "Seu nome",
  "wa.message": "Mensagem (opcional)",
  "wa.message_placeholder": "Descreva sua ideia, inspiração ou detalhes...",
  "wa.photo": "Foto de referência (opcional)",
  "wa.upload_photo": "Carregar foto",
  "wa.file_too_large": "Arquivo muito grande",
  "wa.summary": "Resumo:",
  "wa.send": "Enviar pelo WhatsApp",
  "wa.sent_title": "Mensagem enviada!",
  "wa.sent_desc": "Sua solicitação foi enviada para {name} pelo WhatsApp.",
  "wa.upload_failed": "Falha no upload",
  "wa.upload_failed_desc": "Não foi possível carregar a foto. Tente novamente.",
  "wa.save_failed": "Falha ao salvar",
  "wa.success": "Enviado!",
  "wa.success_desc": "Sua solicitação foi criada e o WhatsApp foi aberto.",
  "wa.error": "Algo deu errado",
  "wa.error_desc": "Erro desconhecido. Verifique sua conexão e tente novamente.",
  "wa.msg_greeting": "Olá {name}, calculei uma estimativa de preço:",
  "wa.msg_name_label": "Nome",
  "wa.msg_style_label": "🎨 Estilo",
  "wa.msg_bodypart_label": "📍 Parte do corpo",
  "wa.msg_size_label": "📏 Tamanho",
  "wa.msg_complexity_label": "⚙️ Complexidade",
  "wa.msg_time_label": "⏱ Tempo estimado",
  "wa.msg_total_label": "💰 Total",
  "wa.msg_deposit_label": "💳 Sinal",
  "wa.msg_details_label": "🔗 Detalhes",
  "wa.template_high_greeting": "Olá {name}, boas notícias! Calculei uma estimativa detalhada para minha tatuagem:",
  "wa.template_high_vault": "🔗 Salvei no meu Cofre de Tatuagens: {url}",
  "wa.template_low_greeting":
    "Olá {name}, tenho interesse em uma tatuagem. Pode me informar sobre disponibilidade e preços?",
  "wa.template_low_note": "Com base na calculadora, uma primeira ideia:",
  "tooltip.labor": "Baseado no tempo estimado para este tamanho, multiplicado pela tarifa horária do artista.",
  "tooltip.complexity": "Designs mais complexos requerem mais tempo e precisão.",
  "tooltip.placement": "Algumas partes do corpo são mais sensíveis ou difíceis de alcançar.",
  "tooltip.style": "Estilos diferentes requerem técnicas específicas que afetam o preço.",
  "tooltip.design": "Custos de design e preparação da sua tatuagem única.",
  "tooltip.deposit": "O sinal reserva sua consulta e é deduzido do preço total.",
  "pricing_edu.button": "Como funciona este cálculo?",
  "pricing_edu.title": "Como o preço é calculado",
  "pricing_edu.intro": "O preço de uma tatuagem é determinado por vários fatores que refletem a habilidade e o tempo.",
  "pricing_edu.time_title": "Tempo e Tamanho",
  "pricing_edu.time_desc": "Tatuagens maiores levam mais tempo.",
  "pricing_edu.complexity_title": "Complexidade e Detalhe",
  "pricing_edu.complexity_desc": "Designs detalhados requerem mais precisão.",
  "pricing_edu.placement_title": "Localização corporal",
  "pricing_edu.placement_desc": "Algumas zonas são mais sensíveis ou de difícil acesso.",
  "pricing_edu.style_title": "Estilo de tatuagem",
  "pricing_edu.style_desc": "Cada estilo requer técnicas específicas.",
  "pricing_edu.disclaimer": "Esta é sempre uma estimativa. O preço final é determinado após consulta pessoal.",
  "ai.title": "Scanner IA",
  "ai.desc": "Carregue uma foto e deixe a IA determinar as configurações",
  "ai.analyzing": "A IA está analisando...",
  "ai.result_title": "Resultado IA",
  "ai.scan_complete": "✨ Scan IA concluído",
  "ai.scan_applied": "A calculadora foi ajustada automaticamente.",
  "ai.scan_failed": "Scan falhou",
  "ai.try_again": "Tente novamente",
  "referral.title": "Recomendar um colega",
  "referral.desc": "Convide um colega e ganhe desconto na assinatura.",
  "referral.generate": "Gerar link de indicação",
  "referral.share_link": "Compartilhe este link:",
  "pwa.install_title": "Instalar como app",
  "pwa.install_button": "Instalar agora",
  // Batch 3+4
  "tab.theme": "Tema",
  "tab.branding": "Marca",
  "tab.analytics": "Análises",
  "tab.price_engine": "Motor de preços",
  "tab.price_engine_desc": "Gerencie todos os multiplicadores de preço.",
  "basic.desc": "Defina suas tarifas e custos padrão",
  "basic.min_price_desc": "O preço mais baixo que cobra",
  "basic.hourly_rate_desc": "A sua tarifa horária padrão",
  "basic.daily_rate_placeholder": "Deixe vazio se não aplicável",
  "basic.percentage": "%",
  "basic.fixed_amount": "Fixo",
  "basic.consultation_email": "Email de consulta",
  "basic.consultation_email_desc": "Email para pedidos de consulta",
  "basic.booking_url": "URL de reserva",
  "basic.booking_url_desc": "URL do sistema de reservas externo",
  "sizes.title": "Configurações de tamanho",
  "sizes.desc": "Defina o tempo estimado por categoria de tamanho",
  "sizes.min_hours": "Min. horas",
  "sizes.max_hours": "Máx. horas",
  "sizes.hours": "horas",
  "complexity.title": "Multiplicadores de complexidade",
  "complexity.desc": "Ajuste o fator de preço pela complexidade",
  "bodyparts.title": "Multiplicadores partes do corpo",
  "bodyparts.desc": "Algumas zonas são mais difíceis de tatuar",
  "styles.title": "Gestão de estilos",
  "styles.desc": "Ative ou desative estilos e ajuste o fator de preço.",
  "theme.title": "Tema de cor",
  "theme.desc": "Personalize as cores da sua calculadora",
  "theme.preview": "Pré-visualização",
  "theme.preview_desc": "Assim fica a sua calculadora",
  "theme.hue": "Matiz",
  "theme.saturation": "Saturação",
  "theme.lightness": "Luminosidade",
  "theme.presets": "Seleção rápida",
  "branding.title": "Marca",
  "branding.desc": "Carregue o logo e defina idioma e moeda",
  "branding.logo": "Logo",
  "branding.logo_desc": "Logo do estúdio (máx 2 MB, PNG ou JPG)",
  "branding.upload": "Carregar logo",
  "branding.remove": "Remover",
  "branding.language": "Idioma da calculadora",
  "branding.currency": "Moeda",
  "branding.qr_code": "Código QR",
  "branding.qr_desc": "Os clientes escaneiam este código para a sua calculadora.",
  "branding.download_qr": "Descarregar QR",
  "onboard.step_minimum": "Tarifa mínima",
  "onboard.step_hourly": "Tarifa horária",
  "onboard.step_sizes": "Confirmar tamanhos",
  "onboard.step_styles": "Definir estilos",
  "onboard.step_logo": "Carregar logo",
  "onboard.step_finish": "Calculadora pronta!",
  "dash.onboard_min_desc": "Defina a sua tarifa mínima.",
  "dash.onboard_hourly_desc": "Defina a sua tarifa horária. É a base de todos os cálculos.",
  "dash.onboard_sizes_desc": "Categorias de tamanho padrão configuradas. Ajuste depois.",
  "dash.onboard_styles_desc": "Todos os estilos comuns ativados. Ative/desative no painel.",
  "dash.onboard_logo_desc": "Carregue um logo agora ou depois na aba Marca.",
  "dash.onboard_logo_later": "Carregar logo depois em Painel → Marca",
  "dash.onboard_finish_title": "A sua calculadora está pronta!",
  "dash.onboard_finish_desc": "Partilhe o seu link único. Ajuste as configurações a qualquer momento.",
  "dash.your_link": "Link da calculadora:",
  "dash.online_card": "O seu cartão de visita online.",
  "dash.view_my_calculator": "Ver a minha calculadora",
  "dash.upload_thumbnail": "Imagem personalizada",
  "dash.reset_to_default": "Repor",
  "dash.reset_all_thumbnails": "Repor tudo",
  "dash.thumbnail_uploaded": "Imagem carregada!",
  "dash.thumbnail_reset": "Imagem reposta",
  "dash.thumbnails_reset_all": "Todas as imagens repostas",
  "dash.invite_created": "Convite criado!",
  "dash.invite_error": "Erro ao criar convite",
  "dash.member_removed": "Membro removido",
  "dash.link_copied_generic": "Link copiado!",
  "dash.profile_saved": "Perfil guardado!",
  "dash.profile_save_error": "Erro ao guardar",
  "dash.calculator_ready": "🎉 Calculadora pronta!",
  "dash.share_with_clients": "Partilhe o seu link.",
  "dash.step_of": "Passo {current} de {total}",
  "dash.start_inkprice": "Começar com InkPrice",
  "laf.title": "Aparência",
  "laf.desc": "Personalize a aparência da sua calculadora pública.",
  "laf.color_mode": "Modo de cor",
  "laf.dark_mode": "Modo escuro",
  "laf.light_mode": "Modo claro",
  "laf.background": "Fundo",
  "laf.solid_color": "Cor sólida",
  "laf.image_url": "URL da imagem",
  "laf.preview": "Pré-visualização",
  "laf.saved": "Aparência guardada",
  "laf.presets": "Temas premium",
  "laf.font_selector": "Fonte da calculadora",
  "laf.overlay_opacity": "Opacidade do overlay",
  "laf.bg_fixed": "Fixo (Parallax)",
  "laf.bg_scroll": "Deslocamento",
  "consult_settings.title": "Definições de consulta",
  "consult_settings.desc": "Configure o contacto de clientes.",
  "consult_settings.booking_url_label": "URL de reserva",
  "consult_settings.email_label": "Email de consulta",
  "consult_settings.channels_title": "Canais de leads",
  "consult_settings.channels_desc": "Métodos de contacto visíveis.",
  "help.min_price": "Preço mínimo absoluto por sessão.",
  "help.hourly_rate": "A sua tarifa horária é a base de todos os cálculos.",
  "help.multipliers": "Os multiplicadores ajustam o preço por estilo, complexidade e posicionamento.",
  "help.sizes": "Defina o intervalo de tempo para cada tamanho.",
  "help.complexity": "Ajuste multiplicadores de complexidade.",
  "help.body_parts": "Algumas zonas requerem multiplicadores mais altos.",
  "help.styles": "Ative/desative estilos e defina multiplicadores.",
  "help.logo": "O logo aparece na calculadora e no diretório. Máx 2 MB.",
  "help.currency": "A moeda mostrada na calculadora.",
  "help.calculator_language": "O idioma da calculadora pública.",
  "help.dashboard_language": "O idioma deste painel — visível apenas para si.",
  "profile.mfa_title": "Segurança da conta",
  "profile.mfa_desc": "Proteja a sua conta com 2FA.",
  "profile.mfa_enable": "Ativar 2FA",
  "profile.mfa_verify": "Verificar",
  "profile.mfa_active": "Ativo",
  "profile.mfa_enabled_success": "✅ 2FA ativada!",
  "profile.google_reviews": "Avaliações Google",
  "profile.google_reviews_desc": "Vincule o seu perfil Google Business.",
  "profile.sync_now": "Sincronizar",
  "profile.sync_success": "Avaliações Google sincronizadas!",
  "landing.faq_title": "Perguntas frequentes",
  "landing.faq_subtitle": "Tudo sobre o Inkvaluator",
  "landing.faq_q1": "Como funciona a calculadora?",
  "landing.faq_a1": "Configure tarifa e multiplicadores. Os clientes obtêm um orçamento instantâneo.",
  "landing.faq_q2": "É gratuito?",
  "landing.faq_a2": "Sim! O plano Free inclui calculadora completa. Passe para Pro para mais.",
  "landing.faq_q3": "Como funciona o scanner IA?",
  "landing.faq_a3": "Carregue uma foto e a IA analisa tamanho, complexidade e estilo em segundos.",
  "landing.faq_q4": "Os clientes podem contactar-me?",
  "landing.faq_a4": "Sim, via WhatsApp com mensagem pré-preenchida.",
  "landing.faq_q5": "Suporte multilingue?",
  "landing.faq_a5": "17 idiomas e 13 moedas para artistas internacionais.",
  "landing.faq_q6": "Como obter mais clientes?",
  "landing.faq_a6": "Artistas Pro/Studio no mapa mundial e página de descoberta.",
  "city.title": "Tatuadores em {city}",
  "city.description": "Encontre tatuadores em {city}.",
  "city.no_artists": "Nenhum artista encontrado em {city}.",
  "city.discover_all": "Todos os artistas",
  "seo.landing_title": "Inkvaluator – Calculadora de Preços de Tatuagem",
  "seo.landing_desc": "Calcule o preço da sua tatuagem instantaneamente.",
  "vault.seo_title": "O Meu Cofre de Tatuagem | Inkvaluator",
  "vault.title": "O Meu",
  "vault.title_accent": "Cofre",
  "vault.desc": "Os seus orçamentos guardados.",
  "vault.empty": "Sem orçamentos. Use uma calculadora!",
  "vault.save_btn": "Guardar",
  "vault.saved_title": "Guardado!",
  "vault.saved_desc": "Orçamento guardado.",
  "success.active": "Ativo — tudo desbloqueado",
  "success.setup_whatsapp": "Configurar WhatsApp",
  "success.syncing": "Ativando seu plano…",
  "success.view_details": "Ver detalhes do plano",
  "analytics.title": "Análises",
  "analytics.desc": "Utilização da sua calculadora",
  "analytics.total_calculations": "Total de cálculos",
  "analytics.conversion_rate": "Taxa de conversão",
  "analytics.this_month": "Este mês",
  "analytics.no_data": "Sem dados ainda.",
  "studio.title": "Gestão do estúdio",
  "studio.team": "Equipa",
  "studio.invite": "Convidar artista",
  "studio.invite_desc": "Gere um link de convite.",
  "studio.generate_link": "Gerar link",
  "studio.no_members": "Sem membros. Convide artistas.",
  "color.gold": "Ouro",
  "color.red": "Vermelho",
  "color.blue": "Azul",
  "color.green": "Verde",
  "color.purple": "Roxo",
  "color.pink": "Rosa",
  "color.turquoise": "Turquesa",
  "color.orange": "Laranja",
  "tip.title": "Recomendar artista",
  "tip.desc": "Conhece um tatuador que deveria estar no Inkvaluator?",
  "share.button": "Partilhar",
  "sw.update_available": "Nova versão disponível.",
  "embed.iframe_desc": "Cole isto no seu HTML.",
  "embed.floating_desc": "Cole antes do </body>.",
  "embed.verified_badge": "Badge verificado",
  "embed.badge_desc": "Coloque este badge no seu site.",
  "compare.title": "Comparação de preços",
  "compare.disclaimer": "Comparação baseada num tatuagem padrão.",
  "landing.calculate_price": "Calcular preço",
  "landing.total_value_prefix": "Já",
  "landing.total_value_suffix": "em arte da tatuagem calculado",
  "step.next": "Continuar",
  "step.view_result": "Ver preço",
  "step.details": "Detalhes",
  "step.details_desc": "Qual o nível de detalhe do seu design?",
  "layout.stepper": "Passo a passo",
  "layout.form": "Tudo de uma vez",
  "calc.view_price_cta": "Ver resumo de preços",
  "calc.rough_estimate": "Estimativa aproximada",
  "calc.calculated_price": "Preço calculado",
  "calc.save_for_later": "Guardar para depois",
  "calc.share": "Partilhar",
  "calc.more_options": "Mais opções",
  "calc.compare_configs": "Comparar configurações",
  "calc.compare_desc": "Comparar duas configurações de preço lado a lado",
  "calc.no_photo": "Nenhuma foto de referência adicionada",
  "calc.link_copied": "Link copiado!",
  "whatsapp.add_photo": "Adicione sua foto",
  "whatsapp.photos_label": "Fotos de referência — máx. 3",
  "laf.reset_success": "Tema redefinido para o padrão",
  "admin.stripe_managed":
    "As assinaturas são gerenciadas automaticamente via Stripe — as alterações são visíveis após a próxima sincronização webhook",
  "calc.view_tattoo_plan": "Ver seu Tattoo Plan ✓",
  "wa.plan_message": "Aqui está meu Tattoo Plan:",
  "lead.tattoo_plan": "Tattoo Plan",
  "lead.made_with": "Feito com Inkvaluator",
  "lead.contact_artist": "Contatar {artist}",
  "lead.share": "Partilhar",
  "lead.share_title": "Meu Tattoo Plan",
  "lead.link_copied": "Link copiado!",
  "lead.deposit_label": "Sinal",
  "lead.pay_deposit": "Pagar sinal {amount}",
  "lead.deposit_request_title": "Solicitar acordo de sinal",
  "lead.deposit_request_desc": "Pergunte ao artista se um sinal de ~{amount} é possível.",
  "lead.deposit_request_wa": "Enviar pedido via WhatsApp",
  "lead.for_you": "Você",
  "lead.for_label": "Para",
  "lead.back_to_calculator": "Voltar à calculadora",
  "lead.deposit_wa_msg":
    "Olá {artist}, vi meu Tattoo Plan no Inkvaluator. Seria possível um sinal de ~{amount}?\nPlano: {url}",
  "blog.no_translation": "Nenhuma tradução disponível",
  "flash.tab": "Flash",
  "flash.upload": "Enviar flash design",
  "flash.title": "Título",
  "flash.price": "Preço",
  "flash.size": "Tamanho (cm)",
  "flash.available": "Disponível",
  "flash.featured": "Destaque",
  "flash.limit_used": "de {max} flash designs usados",
  "flash.delete_confirm": "Excluir flash design?",
  "flash.reserve_whatsapp": "Reservar via WhatsApp",
  "flash.whatsapp_msg": "Estou interessado no seu flash tattoo:",
  "flash.matching_designs": "Flash designs correspondentes",
  "flash.only_with_flash": "Apenas artistas com flash",
  "groei.score": "Pontuação do perfil",
  "groei.views": "visualizações do perfil",
  "groei.sessions": "sessões da calculadora",
  "groei.leads": "leads",
  "groei.this_week": "esta semana",
  "groei.improve": "Melhore seu perfil",
  "groei.add_photos": "Adicione mais fotos ao portfólio",
  "groei.add_whatsapp": "Configure seu número do WhatsApp",
  "groei.add_bio": "Escreva uma bio mais longa",
  "groei.add_avatar": "Envie uma foto de perfil",
  "groei.add_rate": "Defina sua taxa horária",
  "groei.add_city": "Adicione sua cidade",
  "groei.get_leads": "Compartilhe sua calculadora para obter leads",
  "groei.collapse": "Recolher",
  "groei.expand": "Expandir",
  "groei.tip_portfolio": "Artistas com 6+ fotos recebem 3x mais sessões",
  "groei.tip_whatsapp": "Clientes com botão WhatsApp convertem 5x melhor",
  "groei.tip_leads": "Compartilhe o link da calculadora para receber seu primeiro lead",
  "groei.tip_avatar": "Uma foto de perfil aumenta sua credibilidade",
  "groei.tip_action_photos": "Adicionar fotos",
  "groei.tip_action_whatsapp": "Configurar WhatsApp",
  "groei.tip_action_share": "Compartilhar calculadora",
  "groei.tip_action_avatar": "Enviar foto",
  "milestone.eerste_lead": "Primeiro lead recebido!",
  "milestone.tien_leads": "10 leads alcançados!",
  "milestone.profiel_compleet": "Perfil 100% completo!",
  "milestone.eerste_flash": "Primeiro flash design enviado!",
  "wa.name_required": "Nome é obrigatório",
  "wa.plan_message_full": "Veja meu Tattoo Plan completo: {url}",
};

// ─── SWEDISH ───────────────────────────────────────────────────
const sv: Record<string, string> = {
  ...en,
  "app.tagline": "Beräkna priset på din tatuering",
  back: "Tillbaka",
  save: "Spara",
  copy: "Kopiera",
  copied: "Kopierat!",
  loading: "Laddar...",
  error: "Fel",
  optional: "valfritt",
  "landing.hero": "Beräkna priset på",
  "landing.hero_accent": "din Tatuering",
  "landing.start": "Starta kalkylator",
  "calc.estimate": "Uppskattat pris",
  "landing.hero_title": "Beräkna priset på",
  "landing.hero_title_accent": "din Tatuering",
  "landing.hero_desc": "Upptäck på sekunder vad din nästa tatuering kostar.",
  "landing.try_it": "Prova själv!",
  "landing.discover": "Utforska",
  "landing.login": "Logga in",
  "calc.size": "Storlek",
  "calc.complexity": "Komplexitet",
  "calc.body_part": "Kroppsdel",
  "calc.style": "Stil",
  "size.mini": "Mini (< 5 cm)",
  "size.small": "Liten (5-10 cm)",
  "size.medium": "Medium (10-20 cm)",
  "size.large": "Stor (20-35 cm)",
  "size.xl": "Extra Stor (35+ cm)",
  "complexity.simple": "Enkel",
  "complexity.medium": "Medium",
  "complexity.detailed": "Detaljerad",
  "body.arm": "Arm (allmän)",
  "body.back": "Rygg",
  "body.chest": "Bröst",
  "body.neck": "Nacke",
  "body.face": "Ansikte",
  "body.shoulder": "Axel",
  "body.hand": "Hand",
  "body.foot": "Fot",
  "body.other": "Annat",
  "body.front": "Framsida",
  "body.back_view": "Baksida",
  "body.front_view": "Framsida",
  "body.back_view_btn": "Baksida",
  "style.black_grey": "Svart & Grå",
  "style.full_color": "Fullfärg",
  "style.realism": "Realism",
  "style.fine_line": "Fine Line / Minimal",
  "style.traditional": "Traditionell",
  "style.neo_traditional": "Neo Traditionell",
  "style.watercolor": "Akvarell",
  "style.tribal": "Tribal",
  "style.japanese": "Japansk",
  "style.geometric": "Geometrisk",
  "style.lettering": "Lettering",
  "style.script": "Skript",
  "style.dotwork": "Dotwork",
  "style.mandala": "Mandala",
  "style.blackwork": "Blackwork",
  "style.illustrative": "Illustrativ",
  "style.surrealism": "Surrealism",
  "style.ornamental": "Ornamental",
  "style.micro_realism": "Mikrorealism",
  "style.trash_polka": "Trash Polka",
  "style.other": "Annat",
  "footer.language_label": "Språk & App",
  "footer.all_rights": "Alla rättigheter förbehållna.",
  "footer.install_title": "Installera",
  "footer.install_ok": "Förstått!",
  "upgrade.choose": "Välj din",
  "upgrade.plan_word": "Plan",
  "upgrade.per_month": "/månad",
  "upgrade.monthly": "Månadsvis",
  "upgrade.yearly": "Årsvis",
  "upgrade.mo": "mån",
  "upgrade.year": "år",
  "billing.expires": "Löper ut",
  "billing.change": "Ändra",
  "billing.next_invoice": "Nästa faktura",
  "billing.invoice_history": "Fakturahistorik",
  "auth.login": "Logga in",
  "auth.register": "Registrera",
  "landing.how_it_works_cta": "Se hur det fungerar för din studio",
  "footer.terms": "Villkor",
  "footer.support": "Support",
  "footer.how_it_works": "Hur fungerar det?",
  "hiw.seo_title": "Hur fungerar det? – Inkvaluator",
  "hiw.seo_desc":
    "Upptäck hur Inkvaluator hjälper tatuerare med automatiska prisuppskattningar, smarta leads och professionell branding.",
  "hiw.hero_title": "Fokusera på bläcket, inte fakturan",
  "hiw.hero_p1":
    "Spenderar du mer tid på att svara på DMs om priser än att rita ditt nästa mästerverk? Låt oss vara ärliga: du är tatuerare, inte kundtjänst.",
  "hiw.hero_p2": 'Varje "Vad kostar det?"-fråga du måste svara manuellt är tid du inte lägger på din konst.',
  "hiw.hero_p3":
    "Inkvaluator är den digitala assistenten som filtrerar din verksamhet. Medan du är i zonen beräknar verktyget precisa prisuppskattningar, samlar all info och skickar en färdig förfrågan direkt till din WhatsApp.",
  "hiw.hero_p4":
    'Inga fler "priskikare" utan budget. Bara seriösa leads, professionella offerter och ett varumärke som visar att du tar ditt hantverk på allvar.',
  "hiw.hero_cta": "Redo att ta tillbaka din arbetstid?",
  "hiw.faq_title": "Vanliga frågor",
  "hiw.faq_cat1": "Grunderna: Hur hjälper det mig?",
  "hiw.faq_q1": "Är det här ännu en komplicerad app?",
  "hiw.faq_a1":
    "Nej. Det är en supersnabb kalkylator som du lägger i din Instagram-bio eller integrerar på din sida. Konfigurera reglerna en gång och verktyget gör resten.",
  "hiw.faq_q2": "Hur sparar det mig tid konkret?",
  "hiw.faq_a2":
    "Istället för att fråga tio gånger får du ett WhatsApp-meddelande med allt ifyllt, inklusive referensfoto och accepterat pris.",
  "hiw.faq_cat2": "Priser och inställningar",
  "hiw.faq_q3": "Kan jag sätta mina egna priser?",
  "hiw.faq_a3": "Absolut. Du bestämmer din timtaxa och vad du tar för olika storlekar.",
  "hiw.faq_q4": "Vad om en tatuering är mycket mer komplex?",
  "hiw.faq_a4": "Vår inbyggda AI-scanner känner igen detaljerna i ett foto och justerar prisuppskattningen.",
  "hiw.faq_cat3": "Utseende och varumärke",
  "hiw.faq_q5": "Ser det inte ut som ett generiskt verktyg?",
  "hiw.faq_a5": "Absolut inte. Med Studio-planen är verktyget helt white-label: din logotyp, dina färger.",
  "hiw.faq_q6": "Hur professionellt ser det ut för kunden?",
  "hiw.faq_a6": "Mycket professionellt. Kunden kan ladda ner offerten som en elegant PDF.",
  "hiw.faq_cat4": "Tillväxt och räckvidd",
  "hiw.faq_q7": "Jag vill ha fler kunder från min region, hjälper det?",
  "hiw.faq_a7": "Ja! Inkvaluator har en interaktiv världskarta för sökning efter land, provins och stad.",
  "hiw.backend_title": "Bakom kulisserna: backendets kraft",
  "hiw.backend_intro": "För teknikälskarna: Inkvaluator är fullpackad med avancerade funktioner.",
  "hiw.card_pricing_title": "Dynamisk prishantering",
  "hiw.card_pricing_text": "Justera priser och Stripe-IDn via adminmodulen utan kod.",
  "hiw.card_ai_title": "AI Vision-integration",
  "hiw.card_ai_text": "AI-scannern analyserar mönster, färg och komplexitet för att beräkna arbetstimmar.",
  "hiw.card_stripe_title": "Stripe kundportal",
  "hiw.card_stripe_text": "Helautomatiserat prenumerationssystem.",
  "hiw.card_analytics_title": "Analyspanel",
  "hiw.card_analytics_text": "Få djupa insikter om konverteringar och potentiella intäkter.",
  "hiw.card_blog_title": "Blogg & SEO CMS",
  "hiw.card_blog_text": "Inbyggt system för att publicera artiklar som förbättrar din Google-synlighet.",
  "hiw.card_global_title": "Global arkitektur",
  "hiw.card_global_text": "Fullt flerspråkigt stöd med bevarad språkpreferens.",
  "hiw.card_mobile_title": "Mobilredo (PWA)",
  "hiw.card_mobile_text":
    "Inkvaluator är en Progressive Web App (PWA). Installera verktyget direkt på din hemskärm via webbläsaren. Blixtsnabbt, säkert och utan att ta upp lagringsutrymme.",
  "hiw.cta_title": "Vad väntar du på?",
  "hiw.cta_accent": "Sluta räkna, börja skapa.",
  "hiw.cta_button": "Prova Inkvaluator gratis",
  "tos.seo_title": "Villkor | Inkvaluator",
  "tos.title": "Användarvillkor",
  "tos.updated": "Senast uppdaterad:",
  "tos.s1_title": "1. Definitioner",
  "tos.s1_platform": "Plattform: Inkvaluator, en SaaS-plattform tillgänglig via inkvaluator.com.",
  "tos.s1_user": "Användare / Artist: Varje tatuerare eller studio som skapar ett konto.",
  "tos.s1_visitor": "Besökare: Varje person som använder den offentliga kalkylatorn.",
  "tos.s1_service": "Tjänst: Funktionerna som erbjuds av Inkvaluator.",
  "tos.s2_title": "2. Användning av kalkylatorn",
  "tos.s2_p1": "Prisuppskattningarna är enbart indikativa.",
  "tos.s2_p2": "Inkvaluator ansvarar inte för beslut baserade på uppskattningarna.",
  "tos.s3_title": "3. Prenumerationer och betalningar",
  "tos.s3_intro": "Inkvaluator erbjuder tre nivåer: Free, Pro och Studio.",
  "tos.s3_billing": "Betalda prenumerationer faktureras månadsvis via Stripe.",
  "tos.s3_cancel": "Du kan avsluta din prenumeration när som helst.",
  "tos.s3_refund": "Återbetalningar är inte möjliga för redan påbörjade perioder.",
  "tos.s3_price_change": "Inkvaluator förbehåller sig rätten att ändra priser med 30 dagars varsel.",
  "tos.s4_title": "4. Dataägandeskap",
  "tos.s4_p1": "Alla data som en Artist anger förblir Artistens egendom.",
  "tos.s4_p2": "Anonyma, aggregerade data kan användas för statistisk analys.",
  "tos.s5_title": "5. Ansvar",
  "tos.s5_p1": 'Inkvaluator tillhandahåller Tjänsten "i befintligt skick".',
  "tos.s5_indirect": "Inkvaluator ansvarar inte för indirekta skador.",
  "tos.s5_cap": "Det totala ansvaret är begränsat till beloppet som betalats under de senaste tre månaderna.",
  "tos.s5_content": "Inkvaluator ansvarar inte för innehåll som artister publicerar.",
  "tos.s6_title": "6. Konto och uppsägning",
  "tos.s6_credentials": "Användare ansvarar för att hålla sina inloggningsuppgifter konfidentiella.",
  "tos.s6_suspend": "Inkvaluator förbehåller sig rätten att stänga av konton vid missbruk.",
  "tos.s6_delete": "Vid uppsägning raderas personuppgifter enligt",
  "tos.s6_privacy_link": "Integritetspolicy",
  "tos.s7_title": "7. Immateriella rättigheter",
  "tos.s7_p1": "Alla immateriella rättigheter tillhör Inkvaluator.",
  "tos.s8_title": "8. Tillämplig lag",
  "tos.s8_p1": "Dessa villkor regleras av nederländsk lag.",
  "tos.contact_title": "Frågor om dessa villkor?",
  "tos.contact_text": "Kontakta oss på:",
  "support.seo_title": "Support | Inkvaluator",
  "support.title": "Support",
  "support.faq_title": "Vanliga frågor",
  "support.faq_q1": "Hur ställer jag in mina tariffer?",
  "support.faq_a1": 'Gå till din panel och klicka på "Priser".',
  "support.faq_q2": "Hur fungerar Stripe-integrationen?",
  "support.faq_a2": "Vid uppgradering till Pro eller Studio dirigeras du till en säker Stripe-betalning.",
  "support.faq_q3": "Vad är WhatsApp-modulen?",
  "support.faq_a3": "Med WhatsApp-modulen (från Pro) kan besökare kontakta dig via WhatsApp efter prisberäkning.",
  "support.faq_q4": "Hur delar jag min kalkylator?",
  "support.faq_a4": 'I panelen under "Dela" hittar du en direktlänk, QR-kod och integrationskod.',
  "support.faq_q5": "Kan jag avsluta min prenumeration?",
  "support.faq_a5": "Ja, du kan avsluta när som helst och behåller tillgång till slutet av faktureringsperioden.",
  "support.faq_q6": "Hur fungerar AI-scannern?",
  "support.faq_a6": "AI-scannern analyserar ett foto och uppskattar automatiskt storlek, komplexitet och stil.",
  "support.faq_q7": "Jag hittade en bugg, vad gör jag?",
  "support.faq_a7": "Skicka ett mejl till support@inkvaluator.com med beskrivning och skärmdumpar.",
  "support.contact_title": "Kontakt",
  "support.contact_text": "Står inte din fråga med? Kontakta vårt team:",
  "support.form_name": "Namn",
  "support.form_name_placeholder": "Ditt namn",
  "support.form_email": "E-post",
  "support.form_message": "Meddelande",
  "support.form_message_placeholder": "Beskriv din fråga eller problem...",
  "support.form_submit": "Skicka meddelande",
  "support.form_subject": "Supportförfrågan från {name}",
  "support.toast_title": "E-postklient öppnad",
  "support.toast_desc": "Din e-postapp öppnas med ditt meddelande.",
  "privacy.seo_title": "Integritetspolicy | Inkvaluator",
  "privacy.title": "Integritetspolicy",
  "privacy.updated": "Senast uppdaterad:",
  "privacy.s1_title": "1. Vilka data samlar vi in?",
  "privacy.s1_intro": "Inkvaluator samlar in data som krävs för plattformens funktion:",
  "privacy.s1_artists_label": "För artister",
  "privacy.s1_artists": "Namn, e-post, studionamn, adress och portfölj.",
  "privacy.s1_visitors_label": "För besökare",
  "privacy.s1_visitors": "Data som matas in i kalkylatorn för att generera uppskattningar.",
  "privacy.s1_leads_label": "Leads",
  "privacy.s1_leads": "När du kontaktar en artist behandlas ditt meddelande och foton säkert.",
  "privacy.s2_title": "2. Dataanvändning",
  "privacy.s2_intro": "Din integritet är vår prioritet. Vi säljer aldrig data till tredje part.",
  "privacy.s2_matching": "Matcha kunder med rätt artister.",
  "privacy.s2_algorithms": "Förbättra våra prisalgoritmer.",
  "privacy.s2_pwa": "Teknisk funktion av PWA.",
  "privacy.s3_title": "3. Internationell lagstiftning (GDPR)",
  "privacy.s3_intro": "Enligt europeisk lag har du följande rättigheter:",
  "privacy.s3_access_label": "Rätt till åtkomst",
  "privacy.s3_access": "Du kan begära vilka data vi lagrar.",
  "privacy.s3_erasure_label": "Rätt att bli glömd",
  "privacy.s3_erasure": "Du kan begära fullständig radering av ditt konto och data.",
  "privacy.s3_portability_label": "Dataportabilitet",
  "privacy.s3_portability": "Du kan begära dina data i ett läsbart format.",
  "privacy.s4_title": "4. Cookies och PWA",
  "privacy.s4_text": "Inkvaluator använder funktionell cache (PWA). Vi använder inte spårningscookies.",
  "privacy.contact_title": "Frågor om din integritet?",
  "privacy.contact_text": "Kontakta vårt dataskyddsombud:",
  "privacy.seo_desc": "Läs Inkvaluators integritetspolicy.",
  "dash.unsaved_changes": "Du har osparade ändringar",
  "dash.dashboard_language": "Panelspråk",
  "dash.dashboard_language_desc": "Språket för din hanteringspanel.",
  "auth.dashboard_subtitle": "Artistpanel",
  "dash.impersonation_banner": "Du visar profilen för {name}",
  "dash.stop_impersonation": "Sluta imitera",
  "discover.subtitle": "Upptäck Studior & Artister",
  "discover.description": "Hitta tatuerare och studior nära dig.",
  "discover.hide_map": "Dölj karta",
  "discover.show_map": "Visa karta",
  "discover.map_loading": "Laddar karta...",
  "discover.call": "Ring",
  "discover.route": "Vägbeskrivning",
  "discover.title": "Upptäck",
  "discover.title_accent": "Artister",
  "discover.search_placeholder": "Sök efter stad, artist eller studio...",
  "discover.no_artists": "Inga artister hittades",
  "discover.view_calculator": "Visa kalkylator",
  "feature.calculator": "Anpassad priskalkylator",
  "feature.qr": "QR-kod & delbar länk",
  "feature.styles": "Obegränsade stilar & AI-scanner",
  "feature.logo": "Logotypuppladdning",
  "feature.theme": "Anpassat färgtema",
  "feature.language": "Flerspråkig (17 språk)",
  "feature.currency": "Valutaväxling (13 valutor)",
  "feature.analytics": "Användningsanalys",
  "feature.pdf": "PDF-export",
  "feature.whitelabel": "White-label-branding",
  "feature.map_visibility": "Synlighet på världskartan",
  "feature.multiartist": "Hantering av flera artister",
  "feature.centralanalytics": "Central analyspanel",
  "feature.teammanagement": "Team- & rollhantering",
  "map.view_calculator": "Visa kalkylator",
  "map.unknown_location": "Okänd plats",
  "map.artists_count": "artister",
  "map.world_title": "Artister",
  "map.world_accent": "Världen över",
  "map.in_countries": "i {count} länder",
  "footer.tagline": "Den smartaste priskalkylatorn för tatueringar.",
  "footer.links": "Länkar",
  "footer.discover_artists": "Upptäck artister",
  "footer.artist_login": "Artistinloggning",
  "footer.privacy": "Integritetspolicy",
  "footer.blog": "Blogg",
  "blog.title": "Blogg",
  "blog.no_posts": "Inga artiklar publicerade ännu.",
  "blog.read_more": "Läs mer",
  "blog.published_on": "Publicerad den",
  "blog.back": "Tillbaka till bloggen",
  "blog.not_found": "Artikeln hittades inte",
  "blog.translation_notice": "Den här artikeln är inte tillgänglig på ditt språk ännu. Originalversionen visas.",
  "landing.artist_or_studio": "Är du artist eller studio?",
  "landing.start_free_btn": "Börja gratis",
  "demo.subtitle": "Playground",
  "demo.title": "Testa",
  "demo.title_accent": "Kalkylatorn",
  "demo.estimated_price": "Uppskattat pris",
  "demo.disclaimer": "Detta är en demo. Det verkliga priset beror på artisten och designen.",
  "demo.artist_cta": "Är du artist?",
  "demo.register_free": "Registrera dig gratis",
  "demo.find_studio": "Hitta en studio nära dig",
  "profile.identity": "Identitet",
  "profile.studio_name": "Studionamn",
  "profile.location": "Plats",
  "profile.city": "Stad",
  "profile.calculator_language": "Kalkylatorspråk",
  "embed.title": "Inbäddningswidget",
  "embed.copy_code": "Kopiera kod",
  "upgrade.perfect_start": "Perfekt för att börja",
  "upgrade.everything_pro": "Allt för professionella",
  "upgrade.for_studios": "För tatueringsstudior & team",
  "upgrade.upgrade_pro": "Uppgradera till Pro",
  "upgrade.upgrade_studio": "Uppgradera till Studio",
  "upgrade.discount": "rabatt",
  "upgrade.cancel_anytime_long": "Avsluta när som helst. Inga dolda avgifter.",
  "vault.status_concept": "Utkast",
  "vault.status_sent": "Förfrågan skickad",
  "reviews.rating": "betyg",
  "reviews.verified_reviews": "verifierade recensioner",
  "reviews.read_all": "Läs alla recensioner på Google",
  "discover.swipe": "svep",
  "profile.manual_place_id": "Studio hittades inte? Ange ditt Place ID manuellt.",
  "vault.inspiration_title": "Inspiration",
  "vault.try_style": "Prova →",
  "vault.compare": "Jämför",
  "vault.compare_title": "Jämförelse",
  "trends.title": "Trender",
  "trends.title_accent": "Tatueringar",
  "trends.top_styles": "Populära stilar",
  "trends.top_body_parts": "Populära kroppsdelar",
  "match.title": "Matchande artister nära dig",
  "match.score": "Match",
  "match.view": "Visa profil",
  "urgency.recent_requests": "förfrågningar denna vecka",
  "urgency.wait_time": "Uppskattad väntetid",
  "urgency.weeks": "veckor",
  "urgency.high_demand": "Hög efterfrågan",
  "urgency.popular": "Populär",
  "benchmark.above_market": "över genomsnittet",
  "benchmark.below_market": "under genomsnittet",
  "benchmark.at_market": "I genomsnitt",
  "benchmark.market_avg": "Marknadsgenomsnitt",
  "portfolio.style_examples": "Stilexempel",
  "story.title": "Instagram Story-generator",
  "story.desc": "Skapa en premium Black & Gold story-bild med din QR-kod.",
  "story.generate": "Generera Story",
  "story.regenerate": "Generera om",
  "story.download": "Ladda ner",
  "story.share": "Dela",
  "story.downloaded": "Story nedladdad!",
  "story.cta_line1": "Beräkna Ditt Pris",
  "story.cta_line2": "Skanna QR-koden nedan",
  "story.scan_prompt": "↑ Skanna för din prisuppskattning",
  "trends.filter_country": "Filtrera efter land",
  "trends.all_countries": "Alla länder",
  "trends.analyzed": "beräkningar analyserade",
  "trends.realtime": "Uppdateras i realtid",
  "match.style_experts": "Experter på {style} nära {city}",
  "vault.review_booster_title": "Tatuering klar? Hjälp din artist!",
  "vault.review_booster_desc": "Lämna en Google-recension och hjälp andra att hitta denna artist.",
  "vault.review_booster_btn": "Skriv en Google-recension",
  "checklist.title": "Verifieringsstatus",
  "checklist.profile_complete": "Profil komplett",
  "checklist.google_linked": "Google Place ID länkat",
  "checklist.badge_detected": "Verifierad badge på webbplatsen",
  "checklist.push_enabled": "Push-notiser aktiverade",
  "checklist.not_yet": "Inte än",
  "checklist.active": "Aktiv",
  "lead.status_new": "Ny",
  "lead.status_contacted": "I kontakt",
  "lead.status_booked": "Bokad",
  "lead.change_status": "Status",
  "lead.add_price": "Lägg till slutpris",
  "push.test_body": "Push-notiser är LIVE! Du får nu aviseringar vid nya leads.",
  "push.send_test": "Skicka testnotis",
  "push.active_desc": "Du får notiser vid högkvalitativa leads.",
  "push.blocked": "Notiser är blockerade i dina webbläsarinställningar.",
  "push.enable_desc": "Få notiser när högkvalitativa leads kommer in.",
  "push.label": "Push-notiser",
  "push.welcome_body": "Välkommen! Du får nu notiser vid högkvalitativa leads.",
  "push.denied_info": "Håll dig uppdaterad om nya leads — Aktivera notiser i dina webbläsarinställningar.",
  "vault.live_status": "Live Status",
  "vault.status_new_desc": "Din förfrågan har mottagits",
  "vault.status_contacted_desc": "Artisten har sett ditt meddelande",
  "vault.status_booked_desc": "Din bokning är bekräftad!",
  "vault.link_copied": "🔗 Länk kopierad!",
  "analytics.actual_revenue": "Faktisk Intäkt",
  "style_city.popular_styles": "Populära stilar i {city}",
  "nav.home": "Hem",
  "lead.estimate_header": "Tatueringsuppskattning",
  "discover.verified_badge": "VERIFIERAD",
  "wa.title": "Skicka till artisten via WhatsApp",
  "wa.subtitle": "Skicka din uppskattning direkt till",
  "wa.name": "Ditt namn (valfritt)",
  "wa.name_placeholder": "Ditt namn",
  "wa.message": "Meddelande (valfritt)",
  "wa.message_placeholder": "Beskriv din idé, inspiration eller detaljer...",
  "wa.photo": "Referensfoto (valfritt)",
  "wa.upload_photo": "Ladda upp foto",
  "wa.file_too_large": "Filen är för stor",
  "wa.summary": "Sammanfattning:",
  "wa.send": "Skicka via WhatsApp",
  "wa.sent_title": "Meddelande skickat!",
  "wa.sent_desc": "Din förfrågan har skickats till {name} via WhatsApp.",
  "wa.upload_failed": "Uppladdning misslyckades",
  "wa.save_failed": "Kunde inte spara",
  "wa.success": "Skickat!",
  "wa.success_desc": "Din förfrågan har skapats och WhatsApp har öppnats.",
  "wa.error": "Något gick fel",
  "wa.error_desc": "Okänt fel. Kontrollera din anslutning och försök igen.",
  "wa.msg_greeting": "Hej {name}, jag har beräknat en prisuppskattning:",
  "wa.msg_name_label": "Namn",
  "wa.msg_style_label": "🎨 Stil",
  "wa.msg_bodypart_label": "📍 Kroppsdel",
  "wa.msg_size_label": "📏 Storlek",
  "wa.msg_complexity_label": "⚙️ Komplexitet",
  "wa.msg_time_label": "⏱ Beräknad tid",
  "wa.msg_total_label": "💰 Totalt",
  "wa.msg_deposit_label": "💳 Handpenning",
  "wa.msg_details_label": "🔗 Detaljer",
  "wa.template_high_greeting": "Hej {name}, goda nyheter! Jag har beräknat en detaljerad uppskattning:",
  "wa.template_high_vault": "🔗 Jag har sparat detta i mitt Tatueringsvalv: {url}",
  "wa.template_low_greeting":
    "Hej {name}, jag är intresserad av en tatuering. Kan du berätta om tillgänglighet och priser?",
  "wa.template_low_note": "Baserat på kalkylatorn, en första idé:",
  "tooltip.labor": "Baserat på beräknad tid för denna storlek, multiplicerat med artistens timtaxa.",
  "tooltip.complexity": "Mer komplexa designer kräver mer tid och precision.",
  "tooltip.placement": "Vissa kroppsdelar är känsligare eller svårare att nå.",
  "tooltip.style": "Olika stilar kräver specifika tekniker som påverkar priset.",
  "tooltip.design": "Kostnader för design och förberedelse av din unika tatuering.",
  "tooltip.deposit": "Handpenningen reserverar din tid och dras av från totalpriset.",
  "pricing_edu.button": "Hur fungerar beräkningen?",
  "pricing_edu.title": "Hur priset beräknas",
  "pricing_edu.intro": "Priset bestäms av flera faktorer som speglar hantverket och tiden.",
  "pricing_edu.time_title": "Tid & Storlek",
  "pricing_edu.time_desc": "Större tatueringar tar längre tid.",
  "pricing_edu.complexity_title": "Komplexitet & Detalj",
  "pricing_edu.complexity_desc": "Detaljerade designer kräver mer precision.",
  "pricing_edu.placement_title": "Kroppsplacering",
  "pricing_edu.placement_desc": "Vissa områden är känsligare eller svårare att nå.",
  "pricing_edu.style_title": "Tatueringsstil",
  "pricing_edu.style_desc": "Varje stil kräver specifika tekniker.",
  "pricing_edu.disclaimer": "Detta är alltid en uppskattning. Slutpriset bestäms efter personlig konsultation.",
  "ai.title": "AI-scanner",
  "ai.desc": "Ladda upp ett foto och låt AI bestämma inställningarna",
  "ai.analyzing": "AI analyserar...",
  "ai.scan_complete": "✨ AI-skanning klar",
  "ai.scan_failed": "Skanning misslyckades",
  "ai.try_again": "Försök igen",
  "referral.title": "Rekommendera en kollega",
  "referral.generate": "Generera referenslänk",
  "pwa.install_title": "Installera som app",
  "pwa.install_button": "Installera nu",
  // Batch 3+4
  "tab.theme": "Tema",
  "tab.branding": "Varumärke",
  "tab.analytics": "Analys",
  "tab.price_engine": "Prismotor",
  "tab.price_engine_desc": "Hantera alla prismultiplikatorer.",
  "basic.desc": "Ställ in dina standardpriser och kostnader",
  "basic.min_price_desc": "Det lägsta priset du tar",
  "basic.hourly_rate_desc": "Din standardtimtaxa",
  "basic.daily_rate_placeholder": "Lämna tomt om ej tillämpligt",
  "basic.percentage": "%",
  "basic.fixed_amount": "Fast",
  "basic.consultation_email": "Konsultations-email",
  "basic.consultation_email_desc": "Email för bokningsförfrågningar",
  "basic.booking_url": "Boknings-URL",
  "basic.booking_url_desc": "URL till externt bokningssystem",
  "sizes.title": "Storleksinställningar",
  "sizes.desc": "Ställ in beräknad tid per storlekskategori",
  "sizes.min_hours": "Min. timmar",
  "sizes.max_hours": "Max. timmar",
  "sizes.hours": "timmar",
  "complexity.title": "Komplexitetsmultiplikatorer",
  "complexity.desc": "Justera prisfaktorn baserat på komplexitet",
  "bodyparts.title": "Kroppsdelsmultiplikatorer",
  "bodyparts.desc": "Vissa kroppsdelar är svårare att tatuera",
  "styles.title": "Stilhantering",
  "styles.desc": "Aktivera/inaktivera stilar och justera prisfaktorn.",
  "theme.title": "Färgtema",
  "theme.desc": "Anpassa färgerna på din kalkylator",
  "theme.preview": "Förhandsvisning",
  "theme.preview_desc": "Så här ser din kalkylator ut",
  "theme.hue": "Nyans",
  "theme.saturation": "Mättnad",
  "theme.lightness": "Ljushet",
  "theme.presets": "Snabbval",
  "branding.title": "Varumärke",
  "branding.desc": "Ladda upp logotyp och ställ in språk och valuta",
  "branding.logo": "Logotyp",
  "branding.logo_desc": "Studiologo (max 2 MB, PNG eller JPG)",
  "branding.upload": "Ladda upp logotyp",
  "branding.remove": "Ta bort",
  "branding.language": "Kalkylatorspråk",
  "branding.currency": "Valuta",
  "branding.qr_code": "QR-kod",
  "branding.qr_desc": "Kunder skannar denna kod för din kalkylator.",
  "branding.download_qr": "Ladda ner QR",
  "onboard.step_minimum": "Minimipris",
  "onboard.step_hourly": "Timtaxa",
  "onboard.step_sizes": "Bekräfta storlekar",
  "onboard.step_styles": "Ställ in stilar",
  "onboard.step_logo": "Ladda upp logotyp",
  "onboard.step_finish": "Kalkylator klar!",
  "dash.onboard_min_desc": "Ställ in ditt minimipris.",
  "dash.onboard_hourly_desc": "Ställ in din timtaxa. Den är grunden för alla beräkningar.",
  "dash.onboard_sizes_desc": "Standardstorlekskategorier konfigurerade. Justera senare.",
  "dash.onboard_styles_desc": "Alla vanliga stilar aktiverade. Aktivera/inaktivera i panelen.",
  "dash.onboard_logo_desc": "Ladda upp logotyp nu eller senare via Varumärke-fliken.",
  "dash.onboard_logo_later": "Ladda upp logotyp senare via Panel → Varumärke",
  "dash.onboard_finish_title": "Din kalkylator är klar!",
  "dash.onboard_finish_desc": "Dela din unika länk. Justera inställningarna när som helst.",
  "dash.your_link": "Din kalkylatorlänk:",
  "dash.online_card": "Ditt digitala visitkort.",
  "dash.view_my_calculator": "Visa min kalkylator",
  "dash.upload_thumbnail": "Anpassad bild",
  "dash.reset_to_default": "Återställ",
  "dash.reset_all_thumbnails": "Återställ alla",
  "dash.thumbnail_uploaded": "Bild uppladdad!",
  "dash.thumbnail_reset": "Bild återställd",
  "dash.thumbnails_reset_all": "Alla bilder återställda",
  "dash.invite_created": "Inbjudan skapad!",
  "dash.invite_error": "Fel vid inbjudan",
  "dash.member_removed": "Medlem borttagen",
  "dash.link_copied_generic": "Länk kopierad!",
  "dash.profile_saved": "Profil sparad!",
  "dash.profile_save_error": "Fel vid sparande",
  "dash.calculator_ready": "🎉 Kalkylator klar!",
  "dash.share_with_clients": "Dela din länk.",
  "dash.step_of": "Steg {current} av {total}",
  "dash.start_inkprice": "Börja med InkPrice",
  "laf.title": "Utseende",
  "laf.desc": "Anpassa utseendet på din offentliga kalkylator.",
  "laf.color_mode": "Färgläge",
  "laf.dark_mode": "Mörkt läge",
  "laf.light_mode": "Ljust läge",
  "laf.background": "Bakgrund",
  "laf.solid_color": "Enfärgad",
  "laf.image_url": "Bild-URL",
  "laf.preview": "Förhandsvisning",
  "laf.saved": "Utseende sparat",
  "laf.presets": "Premiumteman",
  "laf.font_selector": "Kalkylatortypsnitt",
  "laf.overlay_opacity": "Överlagringsopacitet",
  "laf.bg_fixed": "Fast (Parallax)",
  "laf.bg_scroll": "Rullning",
  "consult_settings.title": "Konsultationsinställningar",
  "consult_settings.desc": "Konfigurera kundkontakt.",
  "consult_settings.booking_url_label": "Boknings-URL",
  "consult_settings.email_label": "Konsultations-email",
  "consult_settings.channels_title": "Leadkanaler",
  "consult_settings.channels_desc": "Synliga kontaktmetoder.",
  "help.min_price": "Absolut minimipris per session.",
  "help.hourly_rate": "Din timtaxa är grunden för alla beräkningar.",
  "help.multipliers": "Multiplikatorer justerar priset efter stil, komplexitet och placering.",
  "help.sizes": "Definiera tidsintervallet för varje storlek.",
  "help.complexity": "Justera multiplikatorer för komplexitet.",
  "help.body_parts": "Vissa zoner kräver högre multiplikatorer.",
  "help.styles": "Aktivera/inaktivera stilar och ställ in multiplikatorer.",
  "help.logo": "Din logotyp visas på kalkylatorn och i katalogen. Max 2 MB.",
  "help.currency": "Valutan som visas på kalkylatorn.",
  "help.calculator_language": "Språket på din offentliga kalkylator.",
  "help.dashboard_language": "Språket på denna panel — bara synligt för dig.",
  "profile.mfa_title": "Kontosäkerhet",
  "profile.mfa_desc": "Skydda ditt konto med 2FA.",
  "profile.mfa_enable": "Aktivera 2FA",
  "profile.mfa_verify": "Verifiera",
  "profile.mfa_active": "Aktiv",
  "profile.mfa_enabled_success": "✅ 2FA aktiverad!",
  "profile.google_reviews": "Google-recensioner",
  "profile.google_reviews_desc": "Länka din Google Business-profil.",
  "profile.sync_now": "Synkronisera",
  "profile.sync_success": "Google-recensioner synkroniserade!",
  "landing.faq_title": "Vanliga frågor",
  "landing.faq_subtitle": "Allt om Inkvaluator",
  "landing.faq_q1": "Hur fungerar kalkylatorn?",
  "landing.faq_a1": "Konfigurera taxa och multiplikatorer. Kunder får en omedelbar uppskattning.",
  "landing.faq_q2": "Är det gratis?",
  "landing.faq_a2": "Ja! Free-planen inkluderar en komplett kalkylator. Uppgradera till Pro för mer.",
  "landing.faq_q3": "Hur fungerar AI-scannern?",
  "landing.faq_a3": "Ladda upp ett foto och AI analyserar storlek, komplexitet och stil på sekunder.",
  "landing.faq_q4": "Kan kunder kontakta mig?",
  "landing.faq_a4": "Ja, via WhatsApp med ett förifyllt meddelande.",
  "landing.faq_q5": "Flerspråkigt stöd?",
  "landing.faq_a5": "17 språk och 13 valutor för internationella artister.",
  "landing.faq_q6": "Hur får jag fler kunder?",
  "landing.faq_a6": "Pro/Studio-artister på världskartan och upptäckarsidan.",
  "city.title": "Tatuerare i {city}",
  "city.description": "Hitta tatuerare i {city}.",
  "city.no_artists": "Inga artister hittades i {city}.",
  "city.discover_all": "Alla artister",
  "seo.landing_title": "Inkvaluator – Tatueringspriskalkylator",
  "seo.landing_desc": "Beräkna priset på din tatuering direkt.",
  "vault.seo_title": "Mitt Tatueringsvalv | Inkvaluator",
  "vault.title": "Mitt",
  "vault.title_accent": "Valv",
  "vault.desc": "Dina sparade uppskattningar.",
  "vault.empty": "Inga uppskattningar. Använd en kalkylator!",
  "vault.save_btn": "Spara",
  "vault.saved_title": "Sparat!",
  "vault.saved_desc": "Uppskattning sparad.",
  "success.active": "Aktiv — allt upplåst",
  "success.setup_whatsapp": "Konfigurera WhatsApp",
  "success.syncing": "Aktiverar ditt abonnemang…",
  "success.view_details": "Visa plandetaljer",
  "analytics.title": "Analys",
  "analytics.desc": "Användning av din kalkylator",
  "analytics.total_calculations": "Totalt antal beräkningar",
  "analytics.conversion_rate": "Konverteringsgrad",
  "analytics.this_month": "Denna månad",
  "analytics.no_data": "Ingen data ännu.",
  "studio.title": "Studiohantering",
  "studio.team": "Team",
  "studio.invite": "Bjud in artist",
  "studio.invite_desc": "Generera en inbjudningslänk.",
  "studio.generate_link": "Generera länk",
  "studio.no_members": "Inga medlemmar. Bjud in artister.",
  "color.gold": "Guld",
  "color.red": "Röd",
  "color.blue": "Blå",
  "color.green": "Grön",
  "color.purple": "Lila",
  "color.pink": "Rosa",
  "color.turquoise": "Turkos",
  "color.orange": "Orange",
  "tip.title": "Rekommendera en artist",
  "tip.desc": "Känner du en tatuerare som borde vara på Inkvaluator?",
  "share.button": "Dela",
  "sw.update_available": "Ny version tillgänglig.",
  "embed.iframe_desc": "Klistra in detta i din HTML.",
  "embed.floating_desc": "Klistra in före </body>.",
  "embed.verified_badge": "Verifierad badge",
  "embed.badge_desc": "Placera denna badge på din webbplats.",
  "compare.title": "Prisjämförelse",
  "compare.disclaimer": "Jämförelse baserad på en standardtatuering.",
  "landing.calculate_price": "Beräkna pris",
  "landing.total_value_prefix": "Redan",
  "landing.total_value_suffix": "i tatueringskonst beräknat",
  "step.next": "Fortsätt",
  "step.view_result": "Visa pris",
  "step.details": "Detaljer",
  "step.details_desc": "Hur detaljerad är din design?",
  "layout.stepper": "Steg för steg",
  "layout.form": "Allt på en gång",
  "calc.view_price_cta": "Se prisöversikt",
  "calc.rough_estimate": "Grov uppskattning",
  "calc.calculated_price": "Beräknat pris",
  "calc.save_for_later": "Spara för senare",
  "calc.share": "Dela",
  "calc.more_options": "Fler alternativ",
  "calc.compare_configs": "Jämför konfigurationer",
  "calc.compare_desc": "Jämför två priskonfigurationer sida vid sida",
  "calc.no_photo": "Ingen referensbild tillagd",
  "calc.link_copied": "Länk kopierad!",
  "whatsapp.add_photo": "Lägg till ditt foto",
  "whatsapp.photos_label": "Referensfoton — max. 3",
  "laf.reset_success": "Tema återställt till standard",
  "admin.stripe_managed": "Prenumerationer hanteras automatiskt via Stripe — ändringar syns efter nästa webhook-synk",
  "calc.view_tattoo_plan": "Se din Tattoo Plan ✓",
  "wa.plan_message": "Här är min Tattoo Plan:",
  "lead.tattoo_plan": "Tattoo Plan",
  "lead.made_with": "Skapad med Inkvaluator",
  "lead.contact_artist": "Kontakta {artist}",
  "lead.share": "Dela",
  "lead.share_title": "Min Tattoo Plan",
  "lead.link_copied": "Länk kopierad!",
  "lead.deposit_label": "Handpenning",
  "lead.pay_deposit": "Betala handpenning {amount}",
  "lead.deposit_request_title": "Begär handpenningsavtal",
  "lead.deposit_request_desc": "Fråga artisten om en handpenning på ~{amount} är möjlig.",
  "lead.deposit_request_wa": "Skicka förfrågan via WhatsApp",
  "lead.for_you": "Dig",
  "lead.for_label": "För",
  "lead.back_to_calculator": "Tillbaka till kalkylatorn",
  "lead.deposit_wa_msg":
    "Hej {artist}, jag har tittat på min Tattoo Plan på Inkvaluator. Skulle en handpenning på ~{amount} vara möjlig?\nPlan: {url}",
  "blog.no_translation": "Ingen översättning tillgänglig",
  "flash.tab": "Flash",
  "flash.upload": "Ladda upp flash-design",
  "flash.title": "Titel",
  "flash.price": "Pris",
  "flash.size": "Storlek (cm)",
  "flash.available": "Tillgänglig",
  "flash.featured": "Utvald",
  "flash.limit_used": "av {max} flash-designs använda",
  "flash.delete_confirm": "Ta bort flash-design?",
  "flash.reserve_whatsapp": "Reservera via WhatsApp",
  "flash.whatsapp_msg": "Jag är intresserad av din flash-tatuering:",
  "flash.matching_designs": "Matchande flash-designs",
  "flash.only_with_flash": "Bara artister med flash",
  "groei.score": "Profilpoäng",
  "groei.views": "profilvisningar",
  "groei.sessions": "kalkylatorsessioner",
  "groei.leads": "leads",
  "groei.this_week": "denna vecka",
  "groei.improve": "Förbättra din profil",
  "groei.add_photos": "Lägg till fler portfoliobilder",
  "groei.add_whatsapp": "Ställ in ditt WhatsApp-nummer",
  "groei.add_bio": "Skriv en längre bio",
  "groei.add_avatar": "Ladda upp en profilbild",
  "groei.add_rate": "Ange din timtaxa",
  "groei.add_city": "Lägg till din stad",
  "groei.get_leads": "Dela din kalkylator för att få leads",
  "groei.collapse": "Fäll ihop",
  "groei.expand": "Fäll ut",
  "groei.tip_portfolio": "Artister med 6+ foton får 3x fler sessioner",
  "groei.tip_whatsapp": "Kunder med WhatsApp-knapp konverterar 5x bättre",
  "groei.tip_leads": "Dela din kalkylatorlänk för att få din första lead",
  "groei.tip_avatar": "En profilbild ökar din trovärdighet",
  "groei.tip_action_photos": "Lägg till foton",
  "groei.tip_action_whatsapp": "Ställ in WhatsApp",
  "groei.tip_action_share": "Dela kalkylator",
  "groei.tip_action_avatar": "Ladda upp foto",
  "milestone.eerste_lead": "Första lead mottagen!",
  "milestone.tien_leads": "10 leads uppnådda!",
  "milestone.profiel_compleet": "Profil 100% komplett!",
  "milestone.eerste_flash": "Första flash-design uppladdad!",
  "wa.name_required": "Namn krävs",
  "wa.plan_message_full": "Se min fullständiga Tattoo Plan: {url}",
};

// ─── POLISH ────────────────────────────────────────────────────
const pl: Record<string, string> = {
  ...en,
  "app.tagline": "Oblicz cenę swojego tatuażu",
  back: "Wstecz",
  save: "Zapisz",
  copy: "Kopiuj",
  copied: "Skopiowano!",
  loading: "Ładowanie...",
  error: "Błąd",
  optional: "opcjonalnie",
  "landing.hero": "Oblicz cenę",
  "landing.hero_accent": "swojego Tatuażu",
  "landing.start": "Uruchom kalkulator",
  "calc.estimate": "Szacowana cena",
  "landing.hero_title": "Oblicz cenę",
  "landing.hero_title_accent": "swojego Tatuażu",
  "landing.hero_desc": "Sprawdź w kilka sekund, ile kosztuje Twój następny tatuaż.",
  "landing.try_it": "Wypróbuj!",
  "landing.discover": "Odkryj",
  "landing.login": "Zaloguj się",
  "calc.size": "Rozmiar",
  "calc.complexity": "Złożoność",
  "calc.body_part": "Część ciała",
  "calc.style": "Styl",
  "size.mini": "Mini (< 5 cm)",
  "size.small": "Mały (5-10 cm)",
  "size.medium": "Średni (10-20 cm)",
  "size.large": "Duży (20-35 cm)",
  "size.xl": "Bardzo Duży (35+ cm)",
  "complexity.simple": "Prosty",
  "complexity.medium": "Średni",
  "complexity.detailed": "Szczegółowy",
  "body.arm": "Ramię (ogólne)",
  "body.back": "Plecy",
  "body.chest": "Klatka piersiowa",
  "body.neck": "Szyja",
  "body.face": "Twarz",
  "body.shoulder": "Ramię",
  "body.hand": "Dłoń",
  "body.foot": "Stopa",
  "body.other": "Inne",
  "body.front": "Przód",
  "body.back_view": "Tył",
  "body.front_view": "Przód",
  "body.back_view_btn": "Tył",
  "style.black_grey": "Czarno-szary",
  "style.full_color": "Pełny kolor",
  "style.realism": "Realizm",
  "style.fine_line": "Fine Line / Minimal",
  "style.traditional": "Tradycyjny",
  "style.neo_traditional": "Neo Tradycyjny",
  "style.watercolor": "Akwarela",
  "style.tribal": "Tribal",
  "style.japanese": "Japoński",
  "style.geometric": "Geometryczny",
  "style.lettering": "Lettering",
  "style.script": "Kaligrafía",
  "style.dotwork": "Dotwork",
  "style.mandala": "Mandala",
  "style.blackwork": "Blackwork",
  "style.illustrative": "Ilustracyjny",
  "style.surrealism": "Surrealizm",
  "style.ornamental": "Ornamentalny",
  "style.micro_realism": "Mikro realizm",
  "style.trash_polka": "Trash Polka",
  "style.other": "Inne",
  "footer.language_label": "Język & App",
  "footer.all_rights": "Wszelkie prawa zastrzeżone.",
  "footer.install_title": "Zainstaluj",
  "footer.install_ok": "Rozumiem!",
  "upgrade.choose": "Wybierz swój",
  "upgrade.plan_word": "Plan",
  "upgrade.per_month": "/miesiąc",
  "upgrade.monthly": "Miesięcznie",
  "upgrade.yearly": "Rocznie",
  "upgrade.mo": "mies",
  "upgrade.year": "rok",
  "billing.expires": "Wygasa",
  "billing.change": "Zmień",
  "billing.next_invoice": "Następna faktura",
  "billing.invoice_history": "Historia faktur",
  "auth.login": "Zaloguj się",
  "auth.register": "Zarejestruj się",
  "landing.how_it_works_cta": "Zobacz jak to działa dla Twojego studia",
  "footer.terms": "Regulamin",
  "footer.support": "Wsparcie",
  "footer.how_it_works": "Jak to działa?",
  "hiw.seo_title": "Jak to działa? – Inkvaluator",
  "hiw.seo_desc":
    "Dowiedz się, jak Inkvaluator pomaga tatuażystom z automatycznymi wycenami, inteligentnymi leadami i profesjonalnym brandingiem.",
  "hiw.hero_title": "Skup się na tuszu, nie na fakturze",
  "hiw.hero_p1":
    "Spędzasz więcej czasu na odpowiadanie na DM-y o cenach niż na rysowanie? Bądźmy szczerzy: jesteś tatuażystą, nie obsługą klienta.",
  "hiw.hero_p2":
    'Każde pytanie "Ile to kosztuje?", na które musisz odpowiedzieć ręcznie, to czas, którego nie poświęcasz na sztukę.',
  "hiw.hero_p3":
    "Inkvaluator to cyfrowy asystent, który filtruje Twój biznes. Oblicza precyzyjne szacunki, zbiera informacje i wysyła gotowe zapytanie na WhatsApp.",
  "hiw.hero_p4": 'Koniec z "łowcami cen" bez budżetu. Tylko poważne leady i profesjonalne wyceny.',
  "hiw.hero_cta": "Gotowy odzyskać swój czas pracy?",
  "hiw.faq_title": "Często zadawane pytania",
  "hiw.faq_cat1": "Podstawy: Jak mi to pomaga?",
  "hiw.faq_q1": "To kolejna skomplikowana aplikacja?",
  "hiw.faq_a1":
    "Nie. To ultraszybki kalkulator do Twojego bio na Instagramie. Ustawiasz reguły raz i narzędzie robi resztę.",
  "hiw.faq_q2": "Jak konkretnie oszczędza mi czas?",
  "hiw.faq_a2": "Zamiast pytać dziesięć razy, dostajesz wiadomość WhatsApp z wszystkim wypełnionym.",
  "hiw.faq_cat2": "Ceny i ustawienia",
  "hiw.faq_q3": "Mogę ustawić własne ceny?",
  "hiw.faq_a3": "Oczywiście. Ty jesteś szefem. Ustalasz stawkę godzinową i ceny za różne rozmiary.",
  "hiw.faq_q4": "Co jeśli tatuaż jest dużo bardziej złożony?",
  "hiw.faq_a4": "Nasz wbudowany skaner AI rozpoznaje szczegóły na zdjęciu i dostosowuje wycenę.",
  "hiw.faq_cat3": "Wygląd i branding",
  "hiw.faq_q5": "Nie wygląda to jak generyczne narzędzie?",
  "hiw.faq_a5": "Absolutnie nie. Z planem Studio narzędzie jest w pełni white-label: Twoje logo, kolory i styl.",
  "hiw.faq_q6": "Jak profesjonalnie to wygląda dla klienta?",
  "hiw.faq_a6": "Bardzo profesjonalnie. Klient może pobrać wycenę jako elegancki PDF.",
  "hiw.faq_cat4": "Wzrost i zasięg",
  "hiw.faq_q7": "Chcę więcej klientów z mojego regionu, czy to pomaga?",
  "hiw.faq_a7": "Tak! Inkvaluator ma interaktywną mapę świata do wyszukiwania po kraju, województwie i mieście.",
  "hiw.backend_title": "Za kulisami: moc backendu",
  "hiw.backend_intro": "Dla miłośników technologii: Inkvaluator jest pełen zaawansowanych funkcji.",
  "hiw.card_pricing_title": "Dynamiczne zarządzanie cenami",
  "hiw.card_pricing_text": "Dostosuj ceny i ID Stripe z modułu admina bez kodu.",
  "hiw.card_ai_title": "Integracja AI Vision",
  "hiw.card_ai_text": "Skaner AI analizuje wzory, kolory i złożoność, aby obliczyć godziny pracy.",
  "hiw.card_stripe_title": "Portal klienta Stripe",
  "hiw.card_stripe_text": "W pełni zautomatyzowany system subskrypcji.",
  "hiw.card_analytics_title": "Panel analityczny",
  "hiw.card_analytics_text": "Uzyskaj wgląd w konwersje i potencjalne przychody.",
  "hiw.card_blog_title": "Blog i SEO CMS",
  "hiw.card_blog_text": "Wbudowany system do publikowania artykułów poprawiających widoczność w Google.",
  "hiw.card_global_title": "Architektura globalna",
  "hiw.card_global_text": "Pełne wsparcie wielojęzyczne z zachowaniem preferencji językowych.",
  "hiw.card_mobile_title": "Gotowy na mobile (PWA)",
  "hiw.card_mobile_text":
    "Inkvaluator to Progressive Web App (PWA). Zainstaluj narzędzie bezpośrednio na ekranie głównym przez przeglądarkę. Błyskawiczny, bezpieczny i bez zajmowania miejsca.",
  "hiw.cta_title": "Na co czekasz?",
  "hiw.cta_accent": "Przestań liczyć, zacznij tworzyć.",
  "hiw.cta_button": "Wypróbuj Inkvaluator za darmo",
  "tos.seo_title": "Regulamin | Inkvaluator",
  "tos.title": "Regulamin",
  "tos.updated": "Ostatnia aktualizacja:",
  "tos.s1_title": "1. Definicje",
  "tos.s1_platform": "Platforma: Inkvaluator, platforma SaaS dostępna na inkvaluator.com.",
  "tos.s1_user": "Użytkownik / Artysta: Każdy tatuażysta lub studio, które tworzy konto.",
  "tos.s1_visitor": "Odwiedzający: Każda osoba korzystająca z publicznego kalkulatora.",
  "tos.s1_service": "Usługa: Funkcjonalności oferowane przez Inkvaluator.",
  "tos.s2_title": "2. Korzystanie z kalkulatora",
  "tos.s2_p1": "Szacunki cenowe mają charakter wyłącznie orientacyjny.",
  "tos.s2_p2": "Inkvaluator nie ponosi odpowiedzialności za decyzje podjęte na podstawie szacunków.",
  "tos.s3_title": "3. Subskrypcje i płatności",
  "tos.s3_intro": "Inkvaluator oferuje trzy poziomy: Free, Pro i Studio.",
  "tos.s3_billing": "Płatne subskrypcje są rozliczane miesięcznie przez Stripe.",
  "tos.s3_cancel": "Możesz anulować subskrypcję w dowolnym momencie.",
  "tos.s3_refund": "Zwroty nie są możliwe za już rozpoczęte okresy.",
  "tos.s3_price_change": "Inkvaluator zastrzega sobie prawo do zmiany cen z 30-dniowym wyprzedzeniem.",
  "tos.s4_title": "4. Własność danych",
  "tos.s4_p1": "Wszystkie dane wprowadzone przez Artystę pozostają jego własnością.",
  "tos.s4_p2": "Anonimowe, zagregowane dane mogą być wykorzystywane do analizy statystycznej.",
  "tos.s5_title": "5. Odpowiedzialność",
  "tos.s5_p1": 'Inkvaluator dostarcza Usługę "w stanie, w jakim jest".',
  "tos.s5_indirect": "Inkvaluator nie ponosi odpowiedzialności za szkody pośrednie.",
  "tos.s5_cap": "Całkowita odpowiedzialność jest ograniczona do kwoty zapłaconej w ciągu ostatnich trzech miesięcy.",
  "tos.s5_content": "Inkvaluator nie odpowiada za treści publikowane przez artystów.",
  "tos.s6_title": "6. Konto i rozwiązanie",
  "tos.s6_credentials": "Użytkownicy są odpowiedzialni za poufność danych logowania.",
  "tos.s6_suspend": "Inkvaluator zastrzega sobie prawo do zawieszenia kont w przypadku nadużycia.",
  "tos.s6_delete": "Po rozwiązaniu konta dane osobowe zostaną usunięte zgodnie z",
  "tos.s6_privacy_link": "Polityką prywatności",
  "tos.s7_title": "7. Własność intelektualna",
  "tos.s7_p1": "Wszystkie prawa własności intelektualnej należą do Inkvaluator.",
  "tos.s8_title": "8. Prawo właściwe",
  "tos.s8_p1": "Niniejszy regulamin podlega prawu holenderskiemu.",
  "tos.contact_title": "Pytania dotyczące regulaminu?",
  "tos.contact_text": "Skontaktuj się z nami:",
  "support.seo_title": "Wsparcie | Inkvaluator",
  "support.title": "Wsparcie",
  "support.faq_title": "Często zadawane pytania",
  "support.faq_q1": "Jak ustawić moje stawki?",
  "support.faq_a1": 'Przejdź do panelu i kliknij "Ceny", aby skonfigurować stawkę godzinową i więcej.',
  "support.faq_q2": "Jak działa integracja Stripe?",
  "support.faq_a2": "Przy aktualizacji do Pro lub Studio zostaniesz przekierowany na bezpieczną płatność Stripe.",
  "support.faq_q3": "Co to jest moduł WhatsApp?",
  "support.faq_a3": "Z modułem WhatsApp (od Pro) odwiedzający mogą kontaktować się przez WhatsApp po obliczeniu ceny.",
  "support.faq_q4": "Jak udostępnić mój kalkulator?",
  "support.faq_a4": 'W panelu pod "Udostępnij" znajdziesz bezpośredni link, kod QR i kod integracji.',
  "support.faq_q5": "Czy mogę anulować subskrypcję?",
  "support.faq_a5": "Tak, możesz anulować w dowolnym momencie i zachowujesz dostęp do końca okresu rozliczeniowego.",
  "support.faq_q6": "Jak działa skaner AI?",
  "support.faq_a6": "Skaner AI analizuje zdjęcie i automatycznie szacuje rozmiar, złożoność i styl.",
  "support.faq_q7": "Znalazłem błąd, co robić?",
  "support.faq_a7": "Wyślij email na support@inkvaluator.com z opisem problemu i zrzutami ekranu.",
  "support.contact_title": "Kontakt",
  "support.contact_text": "Twoje pytanie nie jest na liście? Skontaktuj się z naszym zespołem:",
  "support.form_name": "Imię",
  "support.form_name_placeholder": "Twoje imię",
  "support.form_email": "Email",
  "support.form_message": "Wiadomość",
  "support.form_message_placeholder": "Opisz swoje pytanie lub problem...",
  "support.form_submit": "Wyślij wiadomość",
  "support.form_subject": "Zgłoszenie wsparcia od {name}",
  "support.toast_title": "Klient email otwarty",
  "support.toast_desc": "Twoja aplikacja email otwiera się z wiadomością.",
  "privacy.seo_title": "Polityka prywatności | Inkvaluator",
  "privacy.title": "Polityka prywatności",
  "privacy.updated": "Ostatnia aktualizacja:",
  "privacy.s1_title": "1. Jakie dane zbieramy?",
  "privacy.s1_intro": "Inkvaluator zbiera dane niezbędne do działania platformy:",
  "privacy.s1_artists_label": "Dla artystów",
  "privacy.s1_artists": "Imię, email, nazwa studia, adres i portfolio.",
  "privacy.s1_visitors_label": "Dla odwiedzających",
  "privacy.s1_visitors": "Dane wprowadzone do kalkulatora w celu generowania szacunków.",
  "privacy.s1_leads_label": "Leady",
  "privacy.s1_leads": "Gdy kontaktujesz się z artystą, Twoja wiadomość i zdjęcia są bezpiecznie przetwarzane.",
  "privacy.s2_title": "2. Wykorzystanie danych",
  "privacy.s2_intro": "Twoja prywatność jest naszym priorytetem. Nigdy nie sprzedajemy danych osobom trzecim.",
  "privacy.s2_matching": "Dopasowywanie klientów do odpowiednich artystów.",
  "privacy.s2_algorithms": "Ulepszanie algorytmów cenowych.",
  "privacy.s2_pwa": "Techniczne działanie PWA.",
  "privacy.s3_title": "3. Prawo międzynarodowe (RODO)",
  "privacy.s3_intro": "Zgodnie z europejskim prawem masz następujące prawa:",
  "privacy.s3_access_label": "Prawo dostępu",
  "privacy.s3_access": "Możesz zapytać, jakie dane przechowujemy.",
  "privacy.s3_erasure_label": "Prawo do bycia zapomnianym",
  "privacy.s3_erasure": "Możesz zażądać całkowitego usunięcia konta i danych.",
  "privacy.s3_portability_label": "Przenoszenie danych",
  "privacy.s3_portability": "Możesz zażądać swoich danych w czytelnym formacie.",
  "privacy.s4_title": "4. Pliki cookie i PWA",
  "privacy.s4_text": "Inkvaluator używa funkcjonalnego cache (PWA). Nie używamy śledzących plików cookie.",
  "privacy.contact_title": "Pytania o prywatność?",
  "privacy.contact_text": "Skontaktuj się z naszym inspektorem ochrony danych:",
  "privacy.seo_desc": "Przeczytaj politykę prywatności Inkvaluator.",
  "dash.unsaved_changes": "Masz niezapisane zmiany",
  "dash.dashboard_language": "Język panelu",
  "dash.dashboard_language_desc": "Język interfejsu zarządzania.",
  "auth.dashboard_subtitle": "Panel artysty",
  "dash.impersonation_banner": "Przeglądasz profil {name}",
  "dash.stop_impersonation": "Zakończ podszywanie",
  "discover.subtitle": "Odkryj Studia & Artystów",
  "discover.description": "Znajdź tatuażystów i studia w pobliżu.",
  "discover.hide_map": "Ukryj mapę",
  "discover.show_map": "Pokaż mapę",
  "discover.map_loading": "Ładowanie mapy...",
  "discover.call": "Zadzwoń",
  "discover.route": "Trasa",
  "discover.title": "Odkryj",
  "discover.title_accent": "Artystów",
  "discover.search_placeholder": "Szukaj po mieście, artyście lub studiu...",
  "discover.no_artists": "Nie znaleziono artystów",
  "discover.view_calculator": "Zobacz kalkulator",
  "feature.calculator": "Spersonalizowany kalkulator cen",
  "feature.qr": "Kod QR i link do udostępniania",
  "feature.styles": "Nielimitowane style i skaner AI",
  "feature.logo": "Wgranie logo",
  "feature.theme": "Własny motyw kolorystyczny",
  "feature.language": "Wielojęzyczność (17 języków)",
  "feature.currency": "Zmiana waluty (13 walut)",
  "feature.analytics": "Analiza użycia",
  "feature.pdf": "Eksport PDF",
  "feature.whitelabel": "Branding white-label",
  "feature.map_visibility": "Widoczność na mapie świata",
  "feature.multiartist": "Zarządzanie wieloma artystami",
  "feature.centralanalytics": "Centralny panel analityczny",
  "feature.teammanagement": "Zarządzanie zespołem i rolami",
  "map.view_calculator": "Zobacz kalkulator",
  "map.unknown_location": "Nieznana lokalizacja",
  "map.artists_count": "artystów",
  "map.world_title": "Artyści",
  "map.world_accent": "na świecie",
  "map.in_countries": "w {count} krajach",
  "footer.tagline": "Najinteligentniejszy kalkulator cen tatuaży.",
  "footer.links": "Linki",
  "footer.discover_artists": "Odkryj artystów",
  "footer.artist_login": "Logowanie artysty",
  "footer.privacy": "Polityka prywatności",
  "footer.blog": "Blog",
  "blog.title": "Blog",
  "blog.no_posts": "Nie opublikowano jeszcze żadnych artykułów.",
  "blog.read_more": "Czytaj więcej",
  "blog.published_on": "Opublikowano",
  "blog.back": "Wróć do bloga",
  "blog.not_found": "Nie znaleziono artykułu",
  "blog.translation_notice":
    "Ten artykuł nie jest jeszcze dostępny w Twoim języku. Wyświetlana jest oryginalna wersja.",
  "landing.artist_or_studio": "Jesteś artystą lub studiem?",
  "landing.start_free_btn": "Zacznij za darmo",
  "demo.subtitle": "Playground",
  "demo.title": "Wypróbuj",
  "demo.title_accent": "Kalkulator",
  "demo.estimated_price": "Szacowana cena",
  "demo.disclaimer": "To jest demo. Rzeczywista cena zależy od artysty i projektu.",
  "demo.artist_cta": "Jesteś artystą?",
  "demo.register_free": "Zarejestruj się za darmo",
  "demo.find_studio": "Znajdź studio w pobliżu",
  "profile.identity": "Tożsamość",
  "profile.studio_name": "Nazwa studia",
  "profile.location": "Lokalizacja",
  "profile.city": "Miasto",
  "profile.calculator_language": "Język kalkulatora",
  "embed.title": "Widget do osadzenia",
  "embed.copy_code": "Kopiuj kod",
  "upgrade.perfect_start": "Idealny na start",
  "upgrade.everything_pro": "Wszystko dla profesjonalistów",
  "upgrade.for_studios": "Dla studiów tatuażu i zespołów",
  "upgrade.upgrade_pro": "Przejdź na Pro",
  "upgrade.upgrade_studio": "Przejdź na Studio",
  "upgrade.discount": "zniżka",
  "upgrade.cancel_anytime_long": "Anuluj w dowolnym momencie. Bez ukrytych opłat.",
  "vault.status_concept": "Szkic",
  "vault.status_sent": "Zapytanie wysłane",
  "reviews.rating": "ocena",
  "reviews.verified_reviews": "zweryfikowane opinie",
  "reviews.read_all": "Przeczytaj wszystkie opinie na Google",
  "discover.swipe": "przesuń",
  "profile.manual_place_id": "Studio nie znalezione? Wprowadź swoje Place ID ręcznie.",
  "vault.inspiration_title": "Inspiracja",
  "vault.try_style": "Wypróbuj →",
  "vault.compare": "Porównaj",
  "vault.compare_title": "Porównanie",
  "trends.title": "Trendy",
  "trends.title_accent": "Tatuaże",
  "trends.top_styles": "Popularne style",
  "trends.top_body_parts": "Popularne części ciała",
  "match.title": "Pasujący artyści w pobliżu",
  "match.score": "Dopasowanie",
  "match.view": "Zobacz profil",
  "urgency.recent_requests": "zapytań w tym tygodniu",
  "urgency.wait_time": "Szacowany czas oczekiwania",
  "urgency.weeks": "tygodni",
  "urgency.high_demand": "Duży popyt",
  "urgency.popular": "Popularny",
  "benchmark.above_market": "powyżej średniej rynkowej",
  "benchmark.below_market": "poniżej średniej rynkowej",
  "benchmark.at_market": "W średniej rynkowej",
  "benchmark.market_avg": "Średnia rynkowa",
  "portfolio.style_examples": "Przykłady stylu",
  "story.title": "Generator Story na Instagram",
  "story.desc": "Wygeneruj premium Black & Gold story z kodem QR.",
  "story.generate": "Generuj Story",
  "story.regenerate": "Generuj ponownie",
  "story.download": "Pobierz",
  "story.share": "Udostępnij",
  "story.downloaded": "Story pobrana!",
  "story.cta_line1": "Oblicz Swoją Cenę",
  "story.cta_line2": "Zeskanuj kod QR poniżej",
  "story.scan_prompt": "↑ Zeskanuj, aby uzyskać wycenę",
  "trends.filter_country": "Filtruj po kraju",
  "trends.all_countries": "Wszystkie kraje",
  "trends.analyzed": "obliczeń przeanalizowanych",
  "trends.realtime": "Aktualizowane w czasie rzeczywistym",
  "match.style_experts": "Eksperci od {style} w {city}",
  "vault.review_booster_title": "Tatuaż gotowy? Pomóż artyście!",
  "vault.review_booster_desc": "Zostaw opinię na Google i pomóż innym znaleźć tego artystę.",
  "vault.review_booster_btn": "Napisz opinię Google",
  "checklist.title": "Status weryfikacji",
  "checklist.profile_complete": "Profil kompletny",
  "checklist.google_linked": "Google Place ID połączony",
  "checklist.badge_detected": "Odznaka zweryfikowana na stronie",
  "checklist.push_enabled": "Powiadomienia push włączone",
  "checklist.not_yet": "Jeszcze nie",
  "checklist.active": "Aktywne",
  "lead.status_new": "Nowy",
  "lead.status_contacted": "W kontakcie",
  "lead.status_booked": "Zarezerwowano",
  "lead.change_status": "Status",
  "lead.add_price": "Dodaj cenę końcową",
  "push.test_body": "Powiadomienia push są AKTYWNE! Otrzymasz alerty o nowych leadach.",
  "push.send_test": "Wyślij powiadomienie testowe",
  "push.active_desc": "Otrzymujesz powiadomienia o wartościowych leadach.",
  "push.blocked": "Powiadomienia są zablokowane w ustawieniach przeglądarki.",
  "push.enable_desc": "Otrzymuj powiadomienia, gdy pojawią się wartościowe leady.",
  "push.label": "Powiadomienia push",
  "push.welcome_body": "Witaj! Otrzymasz teraz alerty o wartościowych leadach.",
  "push.denied_info": "Bądź na bieżąco z nowymi leadami — Włącz powiadomienia w ustawieniach przeglądarki.",
  "vault.live_status": "Status na żywo",
  "vault.status_new_desc": "Twoje zapytanie zostało odebrane",
  "vault.status_contacted_desc": "Artysta widział Twoją wiadomość",
  "vault.status_booked_desc": "Twoja wizyta jest potwierdzona!",
  "vault.link_copied": "🔗 Link skopiowany!",
  "analytics.actual_revenue": "Rzeczywisty Przychód",
  "style_city.popular_styles": "Popularne style w {city}",
  "nav.home": "Strona główna",
  "lead.estimate_header": "Wycena Tatuażu",
  "discover.verified_badge": "ZWERYFIKOWANY",
  "wa.title": "Wyślij do artysty przez WhatsApp",
  "wa.subtitle": "Wyślij swoją wycenę bezpośrednio do",
  "wa.name": "Twoje imię (opcjonalnie)",
  "wa.name_placeholder": "Twoje imię",
  "wa.message": "Wiadomość (opcjonalnie)",
  "wa.message_placeholder": "Opisz swój pomysł, inspirację lub szczegóły...",
  "wa.photo": "Zdjęcie referencyjne (opcjonalnie)",
  "wa.upload_photo": "Wgraj zdjęcie",
  "wa.file_too_large": "Plik za duży",
  "wa.summary": "Podsumowanie:",
  "wa.send": "Wyślij przez WhatsApp",
  "wa.sent_title": "Wiadomość wysłana!",
  "wa.sent_desc": "Twoje zapytanie zostało wysłane do {name} przez WhatsApp.",
  "wa.upload_failed": "Błąd przesyłania",
  "wa.save_failed": "Błąd zapisu",
  "wa.success": "Wysłano!",
  "wa.success_desc": "Twoje zapytanie zostało utworzone i WhatsApp został otwarty.",
  "wa.error": "Coś poszło nie tak",
  "wa.error_desc": "Nieznany błąd. Sprawdź połączenie i spróbuj ponownie.",
  "wa.msg_greeting": "Cześć {name}, obliczyłem szacunkową cenę:",
  "wa.msg_name_label": "Imię",
  "wa.msg_style_label": "🎨 Styl",
  "wa.msg_bodypart_label": "📍 Część ciała",
  "wa.msg_size_label": "📏 Rozmiar",
  "wa.msg_complexity_label": "⚙️ Złożoność",
  "wa.msg_time_label": "⏱ Szacowany czas",
  "wa.msg_total_label": "💰 Suma",
  "wa.msg_deposit_label": "💳 Zaliczka",
  "wa.msg_details_label": "🔗 Szczegóły",
  "wa.template_high_greeting": "Cześć {name}, dobre wieści! Obliczyłem szczegółową wycenę mojego tatuażu:",
  "wa.template_high_vault": "🔗 Zapisałem to w moim Skarbcu Tatuaży: {url}",
  "wa.template_low_greeting": "Cześć {name}, interesuję się tatuażem. Czy możesz powiedzieć mi o dostępności i cenach?",
  "wa.template_low_note": "Na podstawie kalkulatora, pierwszy zarys:",
  "tooltip.labor": "Na podstawie szacowanego czasu, pomnożonego przez stawkę godzinową artysty.",
  "tooltip.complexity": "Bardziej złożone projekty wymagają więcej czasu i precyzji.",
  "tooltip.placement": "Niektóre części ciała są bardziej wrażliwe lub trudno dostępne.",
  "tooltip.style": "Różne style wymagają specyficznych technik wpływających na cenę.",
  "tooltip.design": "Koszty zaprojektowania i przygotowania Twojego tatuażu.",
  "tooltip.deposit": "Zaliczka rezerwuje termin i jest odliczana od ceny końcowej.",
  "pricing_edu.button": "Jak działa ta kalkulacja?",
  "pricing_edu.title": "Jak obliczana jest cena",
  "pricing_edu.intro": "Cena tatuażu zależy od kilku czynników odzwierciedlających rzemiosło i czas.",
  "pricing_edu.time_title": "Czas i Rozmiar",
  "pricing_edu.time_desc": "Większe tatuaże wymagają więcej czasu.",
  "pricing_edu.complexity_title": "Złożoność i Detal",
  "pricing_edu.complexity_desc": "Szczegółowe projekty wymagają więcej precyzji.",
  "pricing_edu.placement_title": "Umiejscowienie",
  "pricing_edu.placement_desc": "Niektóre strefy są bardziej wrażliwe.",
  "pricing_edu.style_title": "Styl tatuażu",
  "pricing_edu.style_desc": "Każdy styl wymaga specyficznych technik.",
  "pricing_edu.disclaimer": "To jest zawsze szacunek. Ostateczna cena jest ustalana po osobistej konsultacji.",
  "ai.title": "Skaner AI",
  "ai.desc": "Wgraj zdjęcie i pozwól AI ustalić parametry",
  "ai.analyzing": "AI analizuje...",
  "ai.scan_complete": "✨ Skan AI zakończony",
  "ai.scan_failed": "Skan nieudany",
  "ai.try_again": "Spróbuj ponownie",
  "referral.title": "Poleć kolegę",
  "referral.generate": "Generuj link polecający",
  "pwa.install_title": "Zainstaluj jako aplikację",
  "pwa.install_button": "Zainstaluj teraz",
  // Batch 3+4
  "tab.theme": "Motyw",
  "tab.branding": "Marka",
  "tab.analytics": "Analityka",
  "tab.price_engine": "Silnik cen",
  "basic.desc": "Ustaw swoje stawki i koszty",
  "basic.min_price_desc": "Najniższa cena",
  "basic.hourly_rate_desc": "Twoja standardowa stawka godzinowa",
  "sizes.title": "Ustawienia rozmiaru",
  "sizes.desc": "Ustaw szacowany czas na kategorię",
  "sizes.min_hours": "Min. godzin",
  "sizes.max_hours": "Maks. godzin",
  "sizes.hours": "godzin",
  "complexity.title": "Mnożniki złożoności",
  "complexity.desc": "Dostosuj czynnik ceny na podstawie złożoności",
  "bodyparts.title": "Mnożniki części ciała",
  "bodyparts.desc": "Niektóre części ciała są trudniejsze do tatuowania",
  "styles.title": "Zarządzanie stylami",
  "styles.desc": "Aktywuj lub dezaktywuj style i dostosuj czynnik ceny.",
  "theme.title": "Motyw kolorów",
  "theme.desc": "Dostosuj kolory swojego kalkulatora",
  "theme.preview": "Podgląd",
  "theme.hue": "Odcień",
  "theme.saturation": "Nasycenie",
  "theme.lightness": "Jasność",
  "theme.presets": "Szybki wybór",
  "branding.title": "Marka",
  "branding.desc": "Prześlij logo i ustaw język i walutę",
  "branding.logo": "Logo",
  "branding.upload": "Prześlij logo",
  "branding.remove": "Usuń",
  "branding.language": "Język kalkulatora",
  "branding.currency": "Waluta",
  "branding.qr_code": "Kod QR",
  "branding.download_qr": "Pobierz QR",
  "onboard.step_minimum": "Stawka minimalna",
  "onboard.step_hourly": "Stawka godzinowa",
  "onboard.step_sizes": "Potwierdź rozmiary",
  "onboard.step_styles": "Ustaw style",
  "onboard.step_logo": "Prześlij logo",
  "onboard.step_finish": "Kalkulator gotowy!",
  "dash.onboard_min_desc": "Ustaw swoją stawkę minimalną.",
  "dash.onboard_hourly_desc": "Ustaw swoją stawkę godzinową.",
  "dash.onboard_finish_title": "Twój kalkulator jest gotowy!",
  "dash.onboard_finish_desc": "Udostępnij swój unikalny link. Zmień ustawienia w dowolnym momencie.",
  "dash.your_link": "Link do kalkulatora:",
  "dash.online_card": "Twoja cyfrowa wizytówka.",
  "dash.view_my_calculator": "Zobacz mój kalkulator",
  "dash.upload_thumbnail": "Niestandardowy obraz",
  "dash.reset_to_default": "Przywróć",
  "dash.profile_saved": "Profil zapisany!",
  "dash.profile_save_error": "Błąd zapisu",
  "dash.calculator_ready": "🎉 Kalkulator gotowy!",
  "dash.share_with_clients": "Udostępnij swój link.",
  "dash.step_of": "Krok {current} z {total}",
  "dash.start_inkprice": "Rozpocznij z InkPrice",
  "laf.title": "Wygląd",
  "laf.desc": "Dostosuj wygląd publicznego kalkulatora.",
  "laf.color_mode": "Tryb kolorów",
  "laf.dark_mode": "Tryb ciemny",
  "laf.light_mode": "Tryb jasny",
  "laf.preview": "Podgląd",
  "laf.saved": "Wygląd zapisany",
  "consult_settings.title": "Ustawienia konsultacji",
  "consult_settings.channels_title": "Kanały leadów",
  "help.min_price": "Absolutne minimum za sesję.",
  "help.hourly_rate": "Twoja stawka godzinowa to podstawa wszystkich obliczeń.",
  "profile.mfa_title": "Bezpieczeństwo konta",
  "profile.mfa_enable": "Włącz 2FA",
  "profile.google_reviews": "Opinie Google",
  "profile.sync_now": "Synchronizuj",
  "landing.faq_title": "Najczęstsze pytania",
  "landing.faq_subtitle": "Wszystko o Inkvaluator",
  "landing.faq_q1": "Jak działa kalkulator?",
  "landing.faq_a1": "Skonfiguruj stawkę i mnożniki. Klienci otrzymują natychmiastową wycenę.",
  "landing.faq_q2": "Czy jest za darmo?",
  "landing.faq_a2": "Tak! Plan Free obejmuje pełny kalkulator.",
  "landing.faq_q3": "Jak działa skaner AI?",
  "landing.faq_a3": "Prześlij zdjęcie, a AI przeanalizuje rozmiar, złożoność i styl.",
  "city.title": "Tatuażyści w {city}",
  "city.no_artists": "Nie znaleziono artystów w {city}.",
  "seo.landing_title": "Inkvaluator – Kalkulator Cen Tatuażu",
  "vault.title": "Mój",
  "vault.title_accent": "Skarbiec",
  "vault.desc": "Twoje zapisane wyceny.",
  "vault.empty": "Brak wycen. Użyj kalkulatora!",
  "vault.save_btn": "Zapisz",
  "success.active": "Aktywny — wszystko odblokowane",
  "success.syncing": "Aktywacja planu…",
  "analytics.title": "Analityka",
  "analytics.desc": "Wykorzystanie kalkulatora",
  "analytics.total_calculations": "Łączna liczba obliczeń",
  "analytics.no_data": "Brak danych.",
  "studio.title": "Zarządzanie studiem",
  "studio.team": "Zespół",
  "studio.invite": "Zaproś artystę",
  "color.gold": "Złoto",
  "color.red": "Czerwony",
  "color.blue": "Niebieski",
  "color.green": "Zielony",
  "color.purple": "Fioletowy",
  "color.pink": "Różowy",
  "color.orange": "Pomarańczowy",
  "tip.title": "Poleć artystę",
  "share.button": "Udostępnij",
  "compare.title": "Porównanie cen",
  "landing.calculate_price": "Oblicz cenę",
  "landing.total_value_prefix": "Już",
  "landing.total_value_suffix": "w tatuażach obliczono",
  "step.next": "Dalej",
  "step.view_result": "Zobacz cenę",
  "step.details": "Szczegóły",
  "step.details_desc": "Jak szczegółowy jest Twój projekt?",
  "layout.stepper": "Krok po kroku",
  "layout.form": "Wszystko naraz",
  "calc.view_price_cta": "Zobacz przegląd cen",
  "calc.rough_estimate": "Przybliżona wycena",
  "calc.calculated_price": "Obliczona cena",
  "calc.save_for_later": "Zapisz na później",
  "calc.share": "Udostępnij",
  "calc.more_options": "Więcej opcji",
  "calc.compare_configs": "Porównaj konfiguracje",
  "calc.compare_desc": "Porównaj dwie konfiguracje cenowe obok siebie",
  "calc.no_photo": "Nie dodano zdjęcia referencyjnego",
  "calc.link_copied": "Link skopiowany!",
  "whatsapp.add_photo": "Dodaj swoje zdjęcie",
  "whatsapp.photos_label": "Zdjęcia referencyjne — maks. 3",
  "laf.reset_success": "Motyw przywrócony do domyślnego",
  "admin.stripe_managed":
    "Subskrypcje są zarządzane automatycznie przez Stripe — zmiany są widoczne po następnej synchronizacji webhook",
  "calc.view_tattoo_plan": "Zobacz swój Tattoo Plan ✓",
  "wa.plan_message": "Oto mój Tattoo Plan:",
  "lead.tattoo_plan": "Tattoo Plan",
  "lead.made_with": "Utworzono z Inkvaluator",
  "lead.contact_artist": "Skontaktuj się z {artist}",
  "lead.share": "Udostępnij",
  "lead.share_title": "Mój Tattoo Plan",
  "lead.link_copied": "Link skopiowany!",
  "lead.deposit_label": "Zaliczka",
  "lead.pay_deposit": "Zapłać zaliczkę {amount}",
  "lead.deposit_request_title": "Poproś o zaliczkę",
  "lead.deposit_request_desc": "Zapytaj artystę, czy zaliczka ~{amount} jest możliwa.",
  "lead.deposit_request_wa": "Wyślij zapytanie przez WhatsApp",
  "lead.for_you": "Ciebie",
  "lead.for_label": "Dla",
  "lead.back_to_calculator": "Wróć do kalkulatora",
  "lead.deposit_wa_msg":
    "Cześć {artist}, sprawdziłem mój Tattoo Plan na Inkvaluator. Czy zaliczka ~{amount} byłaby możliwa?\nPlan: {url}",
  "blog.no_translation": "Brak dostępnego tłumaczenia",
  "flash.tab": "Flash",
  "flash.upload": "Prześlij flash design",
  "flash.title": "Tytuł",
  "flash.price": "Cena",
  "flash.size": "Rozmiar (cm)",
  "flash.available": "Dostępny",
  "flash.featured": "Wyróżniony",
  "flash.limit_used": "z {max} flash designów użytych",
  "flash.delete_confirm": "Usunąć flash design?",
  "flash.reserve_whatsapp": "Zarezerwuj przez WhatsApp",
  "flash.whatsapp_msg": "Jestem zainteresowany Twoim flash tattoo:",
  "flash.matching_designs": "Pasujące flash designy",
  "flash.only_with_flash": "Tylko artyści z flash",
  "groei.score": "Wynik profilu",
  "groei.views": "wyświetlenia profilu",
  "groei.sessions": "sesje kalkulatora",
  "groei.leads": "leady",
  "groei.this_week": "ten tydzień",
  "groei.improve": "Ulepsz swój profil",
  "groei.add_photos": "Dodaj więcej zdjęć do portfolio",
  "groei.add_whatsapp": "Ustaw swój numer WhatsApp",
  "groei.add_bio": "Napisz dłuższy opis",
  "groei.add_avatar": "Prześlij zdjęcie profilowe",
  "groei.add_rate": "Ustaw swoją stawkę godzinową",
  "groei.add_city": "Dodaj swoje miasto",
  "groei.get_leads": "Udostępnij kalkulator aby otrzymać leady",
  "groei.collapse": "Zwiń",
  "groei.expand": "Rozwiń",
  "groei.tip_portfolio": "Artyści z 6+ zdjęciami otrzymują 3x więcej sesji",
  "groei.tip_whatsapp": "Klienci z przyciskiem WhatsApp konwertują 5x lepiej",
  "groei.tip_leads": "Udostępnij link do kalkulatora aby otrzymać pierwszy lead",
  "groei.tip_avatar": "Zdjęcie profilowe zwiększa twoją wiarygodność",
  "groei.tip_action_photos": "Dodaj zdjęcia",
  "groei.tip_action_whatsapp": "Skonfiguruj WhatsApp",
  "groei.tip_action_share": "Udostępnij kalkulator",
  "groei.tip_action_avatar": "Prześlij zdjęcie",
  "milestone.eerste_lead": "Pierwszy lead otrzymany!",
  "milestone.tien_leads": "10 leadów osiągnięte!",
  "milestone.profiel_compleet": "Profil 100% kompletny!",
  "milestone.eerste_flash": "Pierwszy flash design przesłany!",
  "wa.name_required": "Imię jest wymagane",
  "wa.plan_message_full": "Zobacz mój pełny Tattoo Plan: {url}",
};

// ─── JAPANESE (complete) ───────────────────────────────────────
const ja: Record<string, string> = {
  ...en,
  "app.tagline": "タトゥーの価格を計算",
  back: "戻る",
  save: "保存",
  copy: "コピー",
  copied: "コピーしました！",
  loading: "読み込み中...",
  error: "エラー",
  optional: "任意",
  "landing.hero": "計算する",
  "landing.hero_accent": "タトゥー価格",
  "landing.start": "計算を開始",
  "calc.estimate": "推定価格",
  "landing.hero_title": "計算する",
  "landing.hero_title_accent": "タトゥー価格",
  "landing.hero_desc": "あなたの次のタトゥーの費用を数秒で確認できます。",
  "landing.try_it": "試してみる！",
  "landing.discover": "発見",
  "landing.login": "ログイン",
  "landing.estimated_price": "推定価格",
  "landing.your_rates": "あなたの料金",
  "landing.hourly_rate": "時給",
  "landing.start_cost": "固定初期費用",
  "landing.size_label": "サイズ",
  "landing.complexity_label": "複雑さ",
  "landing.body_placement": "配置場所",
  "landing.find_artist": "理想の",
  "landing.find_artist_accent": "アーティストを探す",
  "landing.discover_all": "すべてのアーティストを見る",
  "calc.size": "サイズ",
  "calc.complexity": "複雑さ",
  "calc.body_part": "体の部位",
  "calc.style": "スタイル",
  "calc.budget": "予算",
  "calc.back_home": "ホームに戻る",
  "breakdown.title": "価格内訳",
  "breakdown.subtotal": "小計",
  "breakdown.deposit": "手付金",
  "breakdown.total_indication": "合計表示",
  "breakdown.hours_unit": "時間",
  "size.mini": "ミニ (< 5 cm)",
  "size.small": "小 (5-10 cm)",
  "size.medium": "中 (10-20 cm)",
  "size.large": "大 (20-35 cm)",
  "size.xl": "特大 (35+ cm)",
  "complexity.simple": "シンプル",
  "complexity.medium": "中程度",
  "complexity.detailed": "精密",
  "body.arm": "腕（全体）",
  "body.forearm": "前腕",
  "body.upper_arm": "上腕",
  "body.leg": "脚（全体）",
  "body.thigh": "太もも",
  "body.calf": "ふくらはぎ",
  "body.back": "背中",
  "body.chest": "胸",
  "body.ribs": "肋骨",
  "body.stomach": "お腹",
  "body.hand": "手",
  "body.foot": "足",
  "body.neck": "首",
  "body.face": "顔",
  "body.shoulder": "肩",
  "body.hip": "腰",
  "body.finger": "指",
  "body.other": "その他",
  "body.selected": "選択済み",
  "body.front": "前面",
  "body.back_view": "背面",
  "body.front_view": "前面",
  "body.back_view_btn": "背面",
  "style.black_grey": "ブラック＆グレー",
  "style.full_color": "フルカラー",
  "style.realism": "リアリズム",
  "style.fine_line": "ファインライン",
  "style.traditional": "トラディショナル",
  "style.neo_traditional": "ネオトラディショナル",
  "style.watercolor": "水彩",
  "style.tribal": "トライバル",
  "style.japanese": "和彫り",
  "style.geometric": "ジオメトリック",
  "style.lettering": "レタリング",
  "style.script": "スクリプト",
  "style.dotwork": "ドットワーク",
  "style.mandala": "マンダラ",
  "style.blackwork": "ブラックワーク",
  "style.illustrative": "イラストレーティブ",
  "style.surrealism": "シュルレアリスム",
  "style.ornamental": "オーナメンタル",
  "style.micro_realism": "マイクロリアリズム",
  "style.trash_polka": "トラッシュポルカ",
  "style.other": "その他",
  "consult.title": "始める準備はできましたか？",
  "consult.book": "相談を予約",
  "pdf.title": "見積もり",
  "pdf.download": "PDFダウンロード",
  "upgrade.choose": "選ぶ",
  "upgrade.plan_word": "プラン",
  "upgrade.per_month": "/月",
  "upgrade.monthly": "月額",
  "upgrade.yearly": "年額",
  "upgrade.mo": "月",
  "upgrade.year": "年",
  "billing.expires": "有効期限",
  "billing.change": "変更",
  "billing.next_invoice": "次の請求書",
  "billing.invoice_history": "請求書履歴",
  "upgrade.perfect_start": "始めるのに最適",
  "upgrade.everything_pro": "プロフェッショナル向け",
  "upgrade.upgrade_pro": "Proにアップグレード",
  "upgrade.upgrade_studio": "Studioにアップグレード",
  "upgrade.discount": "割引",
  "auth.login": "ログイン",
  "auth.register": "登録",
  "discover.title": "発見",
  "discover.title_accent": "アーティスト",
  "discover.no_artists": "アーティストが見つかりません",
  "footer.tagline": "アーティストとお客様のための最もスマートなタトゥー価格計算機。",
  "footer.links": "リンク",
  "footer.privacy": "プライバシー",
  "footer.language_label": "言語 & アプリ",
  "footer.all_rights": "全著作権所有。",
  "footer.install_title": "インストール",
  "footer.install_ok": "わかりました！",
  "dash.logout": "ログアウト",
  "dash.saved": "保存しました！",
  "dash.save_changes": "変更を保存",
  "dash.cancel": "キャンセル",
  "dash.add": "追加",
  "dash.delete": "削除",
  "dash.previous": "前へ",
  "dash.next": "次へ",
  "success.welcome": "クラブへようこそ、",
  "success.go_dashboard": "ダッシュボードへ",
  "landing.how_it_works_cta": "あなたのスタジオでの仕組みを見る",
  "footer.terms": "利用規約",
  "footer.support": "サポート",
  "footer.how_it_works": "仕組み",
  "hiw.seo_title": "仕組み – Inkvaluator",
  "hiw.seo_desc": "Inkvaluatorがタトゥーアーティストにどう役立つかをご覧ください。",
  "hiw.hero_title": "インクに集中、請求書は任せて",
  "hiw.hero_p1":
    "価格に関するDMの返信に、次の傑作を描く時間より多くの時間を費やしていませんか？正直に言いましょう：あなたはタトゥーアーティストであり、カスタマーサービスではありません。",
  "hiw.hero_p2": "手動で答える「いくらですか？」の質問すべてが、アートに費やせない時間です。",
  "hiw.hero_p3":
    "Inkvaluatorはあなたのビジネスをフィルタリングするデジタルアシスタントです。正確な見積もりを計算し、情報を収集し、WhatsAppに完成したリクエストを送信します。",
  "hiw.hero_p4":
    "予算のない「価格見物客」はもう終わり。真剣なリード、プロフェッショナルな見積書、そしてあなたの技術への真剣さを示すブランディングだけ。",
  "hiw.hero_cta": "作業時間を取り戻す準備はできましたか？",
  "hiw.faq_title": "よくある質問",
  "hiw.faq_cat1": "基本：これはどう役立つ？",
  "hiw.faq_q1": "また複雑なアプリですか？",
  "hiw.faq_a1":
    "いいえ。InstagramのプロフィールやサイトにPaste可能な超高速電卓です。ルールを一度設定すれば、ツールが残りを行います。",
  "hiw.faq_q2": "具体的にどう時間を節約できますか？",
  "hiw.faq_a2": "10回質問する代わりに、すべてが記入済みのWhatsAppメッセージを受け取ります。",
  "hiw.faq_cat2": "価格と設定",
  "hiw.faq_q3": "自分の価格を設定できますか？",
  "hiw.faq_a3": "もちろんです。時給とサイズ別の料金を自分で決められます。",
  "hiw.faq_q4": "タトゥーが平均よりもはるかに複雑な場合は？",
  "hiw.faq_a4": "内蔵のAIスキャナーが写真の詳細を認識し、価格見積もりを調整します。",
  "hiw.faq_cat3": "外観とブランディング",
  "hiw.faq_q5": "一般的なツールに見えませんか？",
  "hiw.faq_a5": "まったくそうではありません。Studioプランではツールは完全にホワイトラベルです。",
  "hiw.faq_q6": "クライアントにどれくらいプロフェッショナルに見えますか？",
  "hiw.faq_a6": "非常にプロフェッショナルです。クライアントはエレガントなPDFとして見積もりをダウンロードできます。",
  "hiw.faq_cat4": "成長とリーチ",
  "hiw.faq_q7": "地域のクライアントを増やしたいのですが、これは役立ちますか？",
  "hiw.faq_a7": "はい！Inkvaluatorには国、都道府県、市区町村で検索できるインタラクティブな世界地図があります。",
  "hiw.backend_title": "舞台裏：バックエンドの力",
  "hiw.backend_intro": "テクノロジー愛好家の方へ：Inkvaluatorは高度な機能が満載です。",
  "hiw.card_pricing_title": "動的価格管理",
  "hiw.card_pricing_text": "コードに触れずに管理モジュールから価格とStripe IDを調整。",
  "hiw.card_ai_title": "AIビジョン統合",
  "hiw.card_ai_text": "AIスキャナーがパターン、色使い、複雑さを分析して労働時間を計算。",
  "hiw.card_stripe_title": "Stripeカスタマーポータル",
  "hiw.card_stripe_text": "完全自動化されたサブスクリプションシステム。",
  "hiw.card_analytics_title": "分析ダッシュボード",
  "hiw.card_analytics_text": "コンバージョンと潜在的な収益に関する深い洞察を得られます。",
  "hiw.card_blog_title": "ブログ＆SEO CMS",
  "hiw.card_blog_text": "Google検索での可視性を向上させる記事公開システム。",
  "hiw.card_global_title": "グローバルアーキテクチャ",
  "hiw.card_global_text": "クライアントの言語設定を保持した完全な多言語サポート。",
  "hiw.card_mobile_title": "モバイル対応 (PWA)",
  "hiw.card_mobile_text":
    "InkvaluatorはProgressive Web App（PWA）です。ブラウザからホーム画面に直接インストールできます。超高速、安全、ストレージ不要。",
  "hiw.cta_title": "何を待っていますか？",
  "hiw.cta_accent": "計算をやめて、創造を始めましょう。",
  "hiw.cta_button": "Inkvaluatorを無料で試す",
  "tos.seo_title": "利用規約 | Inkvaluator",
  "tos.title": "利用規約",
  "tos.updated": "最終更新日：",
  "tos.s1_title": "1. 定義",
  "tos.s1_platform": "プラットフォーム：Inkvaluator、inkvaluator.comで利用可能なSaaSプラットフォーム。",
  "tos.s1_user": "ユーザー / アーティスト：プラットフォームにアカウントを作成するタトゥーアーティストまたはスタジオ。",
  "tos.s1_visitor": "訪問者：公開電卓またはディスカバリーセクションを使用する人。",
  "tos.s1_service": "サービス：Inkvaluatorが提供する機能。",
  "tos.s2_title": "2. 電卓の使用",
  "tos.s2_p1": "価格見積もりは参考値であり、拘束力のある提示ではありません。",
  "tos.s2_p2": "Inkvaluatorは見積もりに基づく判断について責任を負いません。",
  "tos.s3_title": "3. サブスクリプションと支払い",
  "tos.s3_intro": "Inkvaluatorは3つのレベルを提供：Free、Pro、Studio。",
  "tos.s3_billing": "有料サブスクリプションはStripeを通じて毎月請求されます。",
  "tos.s3_cancel": "いつでもサブスクリプションをキャンセルできます。",
  "tos.s3_refund": "開始済みの期間の返金はできません。",
  "tos.s3_price_change": "Inkvaluatorは30日前の通知で価格を変更する権利を留保します。",
  "tos.s4_title": "4. データの所有権",
  "tos.s4_p1": "アーティストが入力したデータはすべてアーティストの所有物です。",
  "tos.s4_p2": "匿名の集計データは統計分析に使用される場合があります。",
  "tos.s5_title": "5. 責任",
  "tos.s5_p1": "Inkvaluatorはサービスを「現状のまま」提供します。",
  "tos.s5_indirect": "Inkvaluatorは間接的損害について責任を負いません。",
  "tos.s5_cap": "総責任は過去3ヶ月間に支払われた金額に限定されます。",
  "tos.s5_content": "Inkvaluatorはアーティストが公開するコンテンツについて責任を負いません。",
  "tos.s6_title": "6. アカウントと終了",
  "tos.s6_credentials": "ユーザーはログイン情報の機密性を保持する責任があります。",
  "tos.s6_suspend": "Inkvaluatorは不正使用の場合にアカウントを停止する権利を留保します。",
  "tos.s6_delete": "アカウント終了時、個人データは以下に従って削除されます",
  "tos.s6_privacy_link": "プライバシーポリシー",
  "tos.s7_title": "7. 知的財産",
  "tos.s7_p1": "すべての知的財産権はInkvaluatorに帰属します。",
  "tos.s8_title": "8. 準拠法",
  "tos.s8_p1": "この利用規約はオランダ法に準拠します。",
  "tos.contact_title": "この規約についてご質問は？",
  "tos.contact_text": "お問い合わせ先：",
  "support.seo_title": "サポート | Inkvaluator",
  "support.title": "サポート",
  "support.faq_title": "よくある質問",
  "support.faq_q1": "料金はどう設定しますか？",
  "support.faq_a1": "ダッシュボードの「価格」をクリックして時給、最低価格などを設定してください。",
  "support.faq_q2": "Stripe連携はどう機能しますか？",
  "support.faq_a2": "ProまたはStudioにアップグレードすると、安全なStripe決済に移動します。",
  "support.faq_q3": "WhatsAppモジュールとは？",
  "support.faq_a3": "WhatsAppモジュール（Proから）で、訪問者が価格計算後にWhatsAppで連絡できます。",
  "support.faq_q4": "電卓をどう共有しますか？",
  "support.faq_a4": "ダッシュボードの「共有」に直接リンク、QRコード、埋め込みコードがあります。",
  "support.faq_q5": "サブスクリプションをキャンセルできますか？",
  "support.faq_a5": "はい、いつでもキャンセルでき、請求期間の終わりまでアクセスを維持できます。",
  "support.faq_q6": "AIスキャナーはどう機能しますか？",
  "support.faq_a6": "AIスキャナーが写真を分析し、サイズ、複雑さ、スタイルを自動推定します。",
  "support.faq_q7": "バグを見つけました。どうすれば？",
  "support.faq_a7": "support@inkvaluator.comに問題の説明とスクリーンショットを送ってください。",
  "support.contact_title": "お問い合わせ",
  "support.contact_text": "質問がリストにない？直接チームに連絡：",
  "support.form_name": "名前",
  "support.form_name_placeholder": "お名前",
  "support.form_email": "メール",
  "support.form_message": "メッセージ",
  "support.form_message_placeholder": "質問や問題を説明してください...",
  "support.form_submit": "メッセージを送信",
  "support.form_subject": "{name}からのサポートリクエスト",
  "support.toast_title": "メールクライアントが開きました",
  "support.toast_desc": "メールアプリがメッセージとともに開いています。",
  "privacy.seo_title": "プライバシーポリシー | Inkvaluator",
  "privacy.title": "プライバシーポリシー",
  "privacy.updated": "最終更新日：",
  "privacy.s1_title": "1. どのようなデータを収集しますか？",
  "privacy.s1_intro": "Inkvaluatorはプラットフォームの運営に必要なデータを収集します：",
  "privacy.s1_artists_label": "アーティスト向け",
  "privacy.s1_artists": "名前、メール、スタジオ名、住所、ポートフォリオ。",
  "privacy.s1_visitors_label": "訪問者向け",
  "privacy.s1_visitors": "見積もり生成のために計算機に入力されたデータ。",
  "privacy.s1_leads_label": "リード",
  "privacy.s1_leads": "アーティストに連絡すると、メッセージと写真は安全に処理されます。",
  "privacy.s2_title": "2. データの使用",
  "privacy.s2_intro": "あなたのプライバシーは最優先です。第三者にデータを販売することは決してありません。",
  "privacy.s2_matching": "お客様を適切なアーティストとマッチング。",
  "privacy.s2_algorithms": "価格アルゴリズムの改善。",
  "privacy.s2_pwa": "PWAの技術的動作。",
  "privacy.s3_title": "3. 国際法規（GDPR）",
  "privacy.s3_intro": "欧州法に基づき、以下の権利があります：",
  "privacy.s3_access_label": "アクセス権",
  "privacy.s3_access": "保持しているデータの確認を要求できます。",
  "privacy.s3_erasure_label": "削除する権利",
  "privacy.s3_erasure": "アカウントとデータの完全な削除を要求できます。",
  "privacy.s3_portability_label": "データポータビリティ",
  "privacy.s3_portability": "読み取り可能な形式でデータを要求できます。",
  "privacy.s4_title": "4. CookieとPWA",
  "privacy.s4_text": "Inkvaluatorは機能的キャッシュ（PWA）を使用しています。トラッキングCookieは使用しません。",
  "privacy.contact_title": "プライバシーに関する質問？",
  "privacy.contact_text": "データ保護責任者にお問い合わせください：",
  "privacy.seo_desc": "Inkvaluatorのプライバシーポリシーをお読みください。",
  "dash.unsaved_changes": "未保存の変更があります",
  "dash.dashboard_language": "ダッシュボード言語",
  "dash.dashboard_language_desc": "管理画面の言語。",
  "auth.dashboard_subtitle": "アーティストダッシュボード",
  "dash.impersonation_banner": "{name}のプロフィールを表示中",
  "dash.stop_impersonation": "偽装を停止",
  "discover.subtitle": "スタジオ＆アーティストを発見",
  "discover.description": "近くのタトゥーアーティストとスタジオを見つけましょう。",
  "discover.hide_map": "地図を隠す",
  "discover.show_map": "地図を表示",
  "discover.map_loading": "地図を読み込み中...",
  "discover.call": "電話",
  "discover.route": "ルート",
  "discover.search_placeholder": "都市、アーティスト、スタジオで検索...",
  "discover.view_calculator": "計算機を見る",
  "discover.map_title": "地図上のアーティスト",
  "feature.calculator": "カスタム価格計算機",
  "feature.qr": "QRコード＆共有リンク",
  "feature.styles": "無制限のスタイル＆AIスキャナー",
  "feature.logo": "ロゴアップロード",
  "feature.theme": "カスタムカラーテーマ",
  "feature.language": "多言語対応（17言語）",
  "feature.currency": "通貨切り替え（13通貨）",
  "feature.analytics": "使用分析",
  "feature.pdf": "PDFエクスポート",
  "feature.whitelabel": "ホワイトラベルブランディング",
  "feature.map_visibility": "世界地図での可視性",
  "feature.multiartist": "マルチアーティスト管理",
  "feature.centralanalytics": "中央分析ダッシュボード",
  "feature.teammanagement": "チーム＆ロール管理",
  "map.view_calculator": "計算機を見る",
  "map.unknown_location": "不明な場所",
  "map.artists_count": "アーティスト",
  "map.world_title": "アーティスト",
  "map.world_accent": "世界中",
  "map.in_countries": "{count}カ国で",
  "footer.discover_artists": "アーティストを発見",
  "footer.artist_login": "アーティストログイン",
  "footer.blog": "ブログ",
  "blog.title": "ブログ",
  "blog.no_posts": "まだ記事は公開されていません。",
  "blog.read_more": "続きを読む",
  "blog.published_on": "公開日",
  "blog.back": "ブログに戻る",
  "blog.not_found": "記事が見つかりません",
  "blog.translation_notice": "この記事はまだお使いの言語では利用できません。原文が表示されています。",
  "landing.artist_or_studio": "アーティストまたはスタジオですか？",
  "demo.subtitle": "プレイグラウンド",
  "demo.title": "試す",
  "demo.title_accent": "計算機",
  "demo.estimated_price": "推定価格",
  "demo.disclaimer": "これはデモです。実際の価格はアーティストとデザインによります。",
  "demo.artist_cta": "アーティストですか？",
  "demo.register_free": "無料登録",
  "demo.find_studio": "近くのスタジオを探す",
  "profile.identity": "アイデンティティ",
  "profile.studio_name": "スタジオ名",
  "profile.location": "場所",
  "profile.city": "都市",
  "profile.calculator_language": "計算機の言語",
  "embed.title": "埋め込みウィジェット",
  "embed.copy_code": "コードをコピー",
  "upgrade.for_studios": "タトゥースタジオ＆チーム向け",
  "upgrade.cancel_anytime_long": "いつでもキャンセル可能。隠れた費用なし。",
  "vault.status_concept": "下書き",
  "vault.status_sent": "リクエスト送信済み",
  "reviews.rating": "評価",
  "reviews.verified_reviews": "件の認証済みレビュー",
  "reviews.read_all": "Googleで全レビューを読む",
  "discover.swipe": "スワイプ",
  "profile.manual_place_id": "スタジオが見つかりません？Place IDを手動で入力してください。",
  "vault.inspiration_title": "インスピレーション",
  "vault.try_style": "試す →",
  "vault.compare": "比較",
  "vault.compare_title": "比較",
  "trends.title": "タトゥー",
  "trends.title_accent": "トレンド",
  "trends.top_styles": "人気のスタイル",
  "trends.top_body_parts": "人気の体の部位",
  "match.title": "近くのマッチするアーティスト",
  "match.score": "マッチ",
  "match.view": "プロフィールを見る",
  "urgency.recent_requests": "今週のリクエスト",
  "urgency.wait_time": "推定待ち時間",
  "urgency.weeks": "週間",
  "urgency.high_demand": "高い需要",
  "urgency.popular": "人気",
  "benchmark.above_market": "市場平均以上",
  "benchmark.below_market": "市場平均以下",
  "benchmark.at_market": "市場平均",
  "benchmark.market_avg": "市場平均",
  "portfolio.style_examples": "スタイル例",
  "story.title": "Instagram ストーリージェネレーター",
  "story.desc": "QRコード付きのプレミアムBlack & Goldストーリー画像を生成。",
  "story.generate": "ストーリーを生成",
  "story.regenerate": "再生成",
  "story.download": "ダウンロード",
  "story.share": "シェア",
  "story.downloaded": "ストーリーをダウンロードしました！",
  "story.cta_line1": "料金を計算",
  "story.cta_line2": "下のQRコードをスキャン",
  "story.scan_prompt": "↑ スキャンして見積もりを取得",
  "trends.filter_country": "国でフィルター",
  "trends.all_countries": "すべての国",
  "trends.analyzed": "件の計算を分析",
  "trends.realtime": "リアルタイム更新",
  "match.style_experts": "{city}の{style}エキスパート",
  "vault.review_booster_title": "タトゥー完了？アーティストを応援！",
  "vault.review_booster_desc": "Googleレビューを残して、他のお客様がこのアーティストを見つける手助けをしましょう。",
  "vault.review_booster_btn": "Googleレビューを書く",
  "checklist.title": "認証ステータス",
  "checklist.profile_complete": "プロフィール完了",
  "checklist.google_linked": "Google Place ID連携済み",
  "checklist.badge_detected": "ウェブサイトに認証バッジ",
  "checklist.push_enabled": "プッシュ通知有効",
  "checklist.not_yet": "まだ",
  "checklist.active": "有効",
  "lead.status_new": "新規",
  "lead.status_contacted": "連絡済み",
  "lead.status_booked": "予約済み",
  "lead.change_status": "ステータス",
  "lead.add_price": "最終価格を追加",
  "push.test_body": "プッシュ通知がライブです！新しいリードのアラートを受信します。",
  "push.send_test": "テスト通知を送信",
  "push.active_desc": "高品質なリードが届くと通知を受け取ります。",
  "push.blocked": "ブラウザの設定で通知がブロックされています。",
  "push.enable_desc": "高品質なリードが届いたときに通知を受け取りましょう。",
  "push.label": "プッシュ通知",
  "push.welcome_body": "ようこそ！高品質なリードが届くとアラートを受信します。",
  "push.denied_info": "新しいリードの情報を受け取る — ブラウザの設定で通知を有効にしてください。",
  "vault.live_status": "ライブステータス",
  "vault.status_new_desc": "リクエストが受信されました",
  "vault.status_contacted_desc": "アーティストがメッセージを確認しました",
  "vault.status_booked_desc": "予約が確定しました！",
  "vault.link_copied": "🔗 リンクをコピーしました！",
  "analytics.actual_revenue": "実際の収益",
  "style_city.popular_styles": "{city}の人気スタイル",
  "nav.home": "ホーム",
  "lead.estimate_header": "タトゥー見積もり",
  "discover.verified_badge": "認証済み",
  "wa.title": "WhatsAppでアーティストに送信",
  "wa.subtitle": "見積もりを直接送信",
  "wa.name": "お名前（任意）",
  "wa.name_placeholder": "お名前",
  "wa.message": "メッセージ（任意）",
  "wa.message_placeholder": "アイデア、インスピレーション、詳細を記述...",
  "wa.photo": "参考写真（任意）",
  "wa.upload_photo": "写真をアップロード",
  "wa.file_too_large": "ファイルが大きすぎます",
  "wa.summary": "概要：",
  "wa.send": "WhatsAppで送信",
  "wa.sent_title": "メッセージ送信完了！",
  "wa.sent_desc": "{name}へのリクエストがWhatsAppで送信されました。",
  "wa.upload_failed": "アップロード失敗",
  "wa.save_failed": "保存に失敗",
  "wa.success": "送信完了！",
  "wa.success_desc": "リクエストが作成され、WhatsAppが開きました。",
  "wa.error": "エラーが発生しました",
  "wa.error_desc": "不明なエラー。接続を確認して再試行してください。",
  "wa.msg_greeting": "{name}さん、こんにちは。価格見積もりを計算しました：",
  "wa.msg_name_label": "名前",
  "wa.msg_style_label": "🎨 スタイル",
  "wa.msg_bodypart_label": "📍 体の部位",
  "wa.msg_size_label": "📏 サイズ",
  "wa.msg_complexity_label": "⚙️ 複雑さ",
  "wa.msg_time_label": "⏱ 推定時間",
  "wa.msg_total_label": "💰 合計",
  "wa.msg_deposit_label": "💳 手付金",
  "wa.msg_details_label": "🔗 詳細",
  "wa.template_high_greeting": "{name}さん、朗報です！タトゥーの詳細な見積もりを計算しました：",
  "wa.template_high_vault": "🔗 タトゥーボルトに保存しました：{url}",
  "wa.template_low_greeting": "{name}さん、こんにちは。タトゥーに興味があります。空き状況と料金を教えてください。",
  "wa.template_low_note": "計算機に基づく最初のアイデア：",
  "tooltip.labor": "このサイズに必要な推定時間にアーティストの時給を掛けたものです。",
  "tooltip.complexity": "より複雑なデザインはより多くの時間と精度を必要とします。",
  "tooltip.placement": "一部の体の部位はより敏感で、追加の時間とケアが必要です。",
  "tooltip.style": "異なるスタイルには特定の技術が必要で、価格に影響します。",
  "tooltip.design": "ユニークなタトゥーのデザインと準備のコスト。",
  "tooltip.deposit": "手付金は予約を確保し、合計金額から差し引かれます。",
  "pricing_edu.button": "この計算はどのように機能しますか？",
  "pricing_edu.title": "価格の計算方法",
  "pricing_edu.intro": "タトゥーの価格は、技術と時間を反映する複数の要因によって決まります。",
  "pricing_edu.disclaimer": "これは常に見積もりです。最終価格はアーティストとの個人相談後に決定されます。",
  "ai.title": "AIスキャナー",
  "ai.desc": "写真をアップロードしてAIに設定を判定させましょう",
  "ai.analyzing": "AIが分析中...",
  "ai.scan_complete": "✨ AIスキャン完了",
  "ai.scan_failed": "スキャン失敗",
  "ai.try_again": "もう一度お試しください",
  "referral.title": "同僚を紹介",
  "referral.generate": "紹介リンクを生成",
  "pwa.install_title": "アプリとしてインストール",
  "pwa.install_button": "今すぐインストール",
  // Batch 3+4
  "tab.theme": "テーマ",
  "tab.branding": "ブランディング",
  "tab.analytics": "アナリティクス",
  "tab.price_engine": "価格エンジン",
  "basic.desc": "標準料金とコストを設定",
  "basic.min_price_desc": "最低請求額",
  "basic.hourly_rate_desc": "標準時給",
  "sizes.title": "サイズ設定",
  "sizes.desc": "サイズカテゴリごとの推定時間を設定",
  "sizes.min_hours": "最小時間",
  "sizes.max_hours": "最大時間",
  "sizes.hours": "時間",
  "complexity.title": "複雑さの乗数",
  "complexity.desc": "複雑さに基づいて価格を調整",
  "bodyparts.title": "体の部位の乗数",
  "bodyparts.desc": "一部の部位はタトゥーが難しい",
  "styles.title": "スタイル管理",
  "styles.desc": "スタイルの有効/無効と価格調整",
  "theme.title": "カラーテーマ",
  "theme.desc": "計算機の色をカスタマイズ",
  "theme.preview": "プレビュー",
  "theme.hue": "色相",
  "theme.saturation": "彩度",
  "theme.lightness": "明度",
  "theme.presets": "クイック選択",
  "branding.title": "ブランディング",
  "branding.desc": "ロゴをアップロードし、言語と通貨を設定",
  "branding.logo": "ロゴ",
  "branding.upload": "ロゴをアップロード",
  "branding.remove": "削除",
  "branding.language": "計算機の言語",
  "branding.currency": "通貨",
  "branding.qr_code": "QRコード",
  "branding.download_qr": "QRをダウンロード",
  "onboard.step_minimum": "最低料金",
  "onboard.step_hourly": "時給",
  "onboard.step_sizes": "サイズ確認",
  "onboard.step_styles": "スタイル設定",
  "onboard.step_logo": "ロゴアップロード",
  "onboard.step_finish": "計算機完成！",
  "dash.onboard_min_desc": "最低料金を設定してください。",
  "dash.onboard_hourly_desc": "時給を設定してください。すべての計算の基礎です。",
  "dash.onboard_finish_title": "計算機の準備ができました！",
  "dash.onboard_finish_desc": "ユニークなリンクを共有しましょう。設定はいつでも変更可能です。",
  "dash.your_link": "計算機リンク:",
  "dash.online_card": "あなたのデジタル名刺です。",
  "dash.view_my_calculator": "計算機を見る",
  "dash.upload_thumbnail": "カスタム画像",
  "dash.reset_to_default": "リセット",
  "dash.profile_saved": "プロフィール保存！",
  "dash.calculator_ready": "🎉 計算機完成！",
  "dash.step_of": "ステップ {current}/{total}",
  "dash.start_inkprice": "InkPriceを始める",
  "laf.title": "外観",
  "laf.desc": "公開計算機の外観をカスタマイズ",
  "laf.color_mode": "カラーモード",
  "laf.dark_mode": "ダークモード",
  "laf.light_mode": "ライトモード",
  "laf.preview": "プレビュー",
  "laf.saved": "外観が保存されました",
  "profile.mfa_title": "アカウントセキュリティ",
  "profile.mfa_enable": "2FAを有効化",
  "profile.google_reviews": "Googleレビュー",
  "profile.sync_now": "同期",
  "landing.faq_title": "よくある質問",
  "landing.faq_subtitle": "Inkvaluatorについて",
  "landing.faq_q1": "計算機はどう動きますか？",
  "landing.faq_a1": "料金と乗数を設定。クライアントは即座に見積もりを取得。",
  "landing.faq_q2": "無料ですか？",
  "landing.faq_a2": "はい！Freeプランには完全な計算機が含まれます。",
  "landing.faq_q3": "AIスキャナーは？",
  "landing.faq_a3": "写真をアップロードすると、AIがサイズ、複雑さ、スタイルを分析。",
  "city.title": "{city}のタトゥーアーティスト",
  "city.no_artists": "{city}にアーティストが見つかりません。",
  "seo.landing_title": "Inkvaluator – タトゥー価格計算機",
  "vault.title": "マイ",
  "vault.title_accent": "ボールト",
  "vault.desc": "保存済みの見積もり。",
  "vault.save_btn": "保存",
  "success.active": "アクティブ — 全機能解放",
  "success.syncing": "プランを有効化中…",
  "analytics.title": "アナリティクス",
  "analytics.desc": "計算機の利用状況",
  "analytics.total_calculations": "計算合計",
  "analytics.no_data": "データなし。",
  "studio.title": "スタジオ管理",
  "studio.team": "チーム",
  "studio.invite": "アーティストを招待",
  "color.gold": "ゴールド",
  "color.red": "赤",
  "color.blue": "青",
  "color.green": "緑",
  "color.purple": "紫",
  "color.pink": "ピンク",
  "color.orange": "オレンジ",
  "tip.title": "アーティストを推薦",
  "share.button": "共有",
  "compare.title": "価格比較",
  "landing.calculate_price": "価格を計算",
  "landing.total_value_prefix": "すでに",
  "landing.total_value_suffix": "のタトゥーアートが計算されました",
  "step.next": "次へ",
  "step.view_result": "価格を見る",
  "step.details": "詳細",
  "step.details_desc": "デザインの詳細度は？",
  "layout.stepper": "ステップごと",
  "layout.form": "一括表示",
  "calc.view_price_cta": "価格概要を見る",
  "calc.rough_estimate": "概算",
  "calc.calculated_price": "計算価格",
  "calc.save_for_later": "後で保存",
  "calc.share": "共有",
  "calc.more_options": "その他のオプション",
  "calc.compare_configs": "構成を比較",
  "calc.compare_desc": "2つの価格構成を並べて比較",
  "calc.no_photo": "参考写真が追加されていません",
  "calc.link_copied": "リンクがコピーされました！",
  "whatsapp.add_photo": "写真を追加",
  "whatsapp.photos_label": "参考写真 — 最大3枚",
  "laf.reset_success": "テーマをデフォルトにリセットしました",
  "admin.stripe_managed": "サブスクリプションはStripe経由で自動管理されます — 変更は次のWebhook同期後に反映されます",
  "calc.view_tattoo_plan": "タトゥープランを見る ✓",
  "wa.plan_message": "こちらが私のタトゥープランです：",
  "lead.tattoo_plan": "タトゥープラン",
  "lead.made_with": "Inkvaluatorで作成",
  "lead.contact_artist": "{artist}に連絡する",
  "lead.share": "共有",
  "lead.share_title": "私のタトゥープラン",
  "lead.link_copied": "リンクをコピーしました！",
  "lead.deposit_label": "デポジット",
  "lead.pay_deposit": "デポジット {amount} を支払う",
  "lead.deposit_request_title": "デポジットの取り決めをリクエスト",
  "lead.deposit_request_desc": "アーティストに~{amount}のデポジットが可能か確認してください。",
  "lead.deposit_request_wa": "WhatsAppでリクエストを送信",
  "lead.for_you": "あなた",
  "lead.for_label": "",
  "lead.back_to_calculator": "計算機に戻る",
  "lead.deposit_wa_msg":
    "{artist}さん、Inkvaluatorでタトゥープランを確認しました。~{amount}のデポジットは可能でしょうか？\nプラン: {url}",
  "blog.no_translation": "翻訳がありません",
  "flash.tab": "フラッシュ",
  "flash.upload": "フラッシュデザインをアップロード",
  "flash.title": "タイトル",
  "flash.price": "価格",
  "flash.size": "サイズ (cm)",
  "flash.available": "利用可能",
  "flash.featured": "おすすめ",
  "flash.limit_used": "{max}件中のフラッシュデザイン使用済み",
  "flash.delete_confirm": "フラッシュデザインを削除しますか？",
  "flash.reserve_whatsapp": "WhatsAppで予約",
  "flash.whatsapp_msg": "フラッシュタトゥーに興味があります：",
  "flash.matching_designs": "マッチするフラッシュデザイン",
  "flash.only_with_flash": "フラッシュのあるアーティストのみ",
  "groei.score": "プロフィールスコア",
  "groei.views": "プロフィール閲覧数",
  "groei.sessions": "計算セッション",
  "groei.leads": "リード",
  "groei.this_week": "今週",
  "groei.improve": "プロフィールを改善する",
  "groei.add_photos": "ポートフォリオ写真を追加",
  "groei.add_whatsapp": "WhatsApp番号を設定",
  "groei.add_bio": "長い自己紹介を書く",
  "groei.add_avatar": "プロフィール写真をアップロード",
  "groei.add_rate": "時間料金を設定",
  "groei.add_city": "都市を追加",
  "groei.get_leads": "計算機を共有してリードを獲得",
  "groei.collapse": "折りたたむ",
  "groei.expand": "展開する",
  "groei.tip_portfolio": "6枚以上の写真を持つアーティストは3倍のセッションを獲得",
  "groei.tip_whatsapp": "WhatsAppボタンのあるクライアントは5倍良く転換",
  "groei.tip_leads": "計算機リンクを共有して最初のリードを獲得",
  "groei.tip_avatar": "プロフィール写真は信頼性を高めます",
  "groei.tip_action_photos": "写真を追加",
  "groei.tip_action_whatsapp": "WhatsApp設定",
  "groei.tip_action_share": "計算機を共有",
  "groei.tip_action_avatar": "写真をアップロード",
  "milestone.eerste_lead": "最初のリード受信！",
  "milestone.tien_leads": "10リード達成！",
  "milestone.profiel_compleet": "プロフィール100%完了！",
  "milestone.eerste_flash": "初フラッシュデザインアップロード！",
  "wa.name_required": "名前は必須です",
  "wa.plan_message_full": "私のTattoo Planを見る: {url}",
};

// ─── KOREAN (complete) ────────────────────────────────────────
const ko: Record<string, string> = {
  ...en,
  "app.tagline": "타투 가격 계산",
  back: "뒤로",
  save: "저장",
  copy: "복사",
  copied: "복사됨!",
  loading: "로딩 중...",
  error: "오류",
  optional: "선택사항",
  "landing.hero": "계산하기",
  "landing.hero_accent": "타투 가격",
  "landing.start": "계산 시작",
  "calc.estimate": "예상 가격",
  "landing.hero_title": "계산하기",
  "landing.hero_title_accent": "타투 가격",
  "landing.hero_desc": "당신의 다음 타투 비용을 몇 초 만에 확인하세요.",
  "landing.try_it": "직접 해보세요!",
  "landing.discover": "탐색",
  "landing.login": "로그인",
  "landing.estimated_price": "예상 가격",
  "landing.your_rates": "요금",
  "landing.hourly_rate": "시간당 요금",
  "landing.start_cost": "고정 초기 비용",
  "landing.size_label": "크기",
  "landing.complexity_label": "복잡도",
  "landing.body_placement": "신체 배치",
  "landing.find_artist": "완벽한",
  "landing.find_artist_accent": "아티스트 찾기",
  "landing.discover_all": "모든 아티스트 보기",
  "calc.size": "크기",
  "calc.complexity": "복잡도",
  "calc.body_part": "신체 부위",
  "calc.style": "스타일",
  "calc.budget": "예산",
  "calc.back_home": "홈으로 돌아가기",
  "breakdown.title": "가격 내역",
  "breakdown.subtotal": "소계",
  "breakdown.deposit": "예약금",
  "breakdown.total_indication": "총 표시",
  "breakdown.hours_unit": "시간",
  "size.mini": "미니 (< 5 cm)",
  "size.small": "소 (5-10 cm)",
  "size.medium": "중 (10-20 cm)",
  "size.large": "대 (20-35 cm)",
  "size.xl": "특대 (35+ cm)",
  "complexity.simple": "간단",
  "complexity.medium": "중간",
  "complexity.detailed": "정밀",
  "body.arm": "팔 (전체)",
  "body.forearm": "전완",
  "body.upper_arm": "상완",
  "body.leg": "다리 (전체)",
  "body.thigh": "허벅지",
  "body.calf": "종아리",
  "body.back": "등",
  "body.chest": "가슴",
  "body.ribs": "갈비뼈",
  "body.stomach": "배",
  "body.hand": "손",
  "body.foot": "발",
  "body.neck": "목",
  "body.face": "얼굴",
  "body.shoulder": "어깨",
  "body.hip": "엉덩이",
  "body.finger": "손가락",
  "body.other": "기타",
  "body.selected": "선택됨",
  "body.front": "앞면",
  "body.back_view": "뒷면",
  "body.front_view": "앞면",
  "body.back_view_btn": "뒷면",
  "style.black_grey": "블랙 앤 그레이",
  "style.full_color": "풀 컬러",
  "style.realism": "리얼리즘",
  "style.fine_line": "파인 라인",
  "style.traditional": "트래디셔널",
  "style.neo_traditional": "네오 트래디셔널",
  "style.watercolor": "수채화",
  "style.tribal": "트라이벌",
  "style.japanese": "일본식",
  "style.geometric": "기하학적",
  "style.lettering": "레터링",
  "style.script": "스크립트",
  "style.dotwork": "도트워크",
  "style.mandala": "만다라",
  "style.blackwork": "블랙워크",
  "style.illustrative": "일러스트레이티브",
  "style.surrealism": "초현실주의",
  "style.ornamental": "장식적",
  "style.micro_realism": "마이크로 리얼리즘",
  "style.trash_polka": "트래시 폴카",
  "style.other": "기타",
  "consult.title": "시작할 준비가 되셨나요?",
  "consult.book": "상담 예약",
  "pdf.title": "견적서",
  "pdf.download": "PDF 다운로드",
  "upgrade.choose": "선택하세요",
  "upgrade.plan_word": "플랜",
  "upgrade.per_month": "/월",
  "upgrade.monthly": "월간",
  "upgrade.yearly": "연간",
  "upgrade.mo": "월",
  "upgrade.year": "년",
  "billing.expires": "만료",
  "billing.change": "변경",
  "billing.next_invoice": "다음 청구서",
  "billing.invoice_history": "청구서 내역",
  "upgrade.perfect_start": "시작하기에 완벽",
  "upgrade.everything_pro": "전문가를 위한 모든 것",
  "upgrade.upgrade_pro": "Pro로 업그레이드",
  "upgrade.upgrade_studio": "Studio로 업그레이드",
  "upgrade.discount": "할인",
  "auth.login": "로그인",
  "auth.register": "등록",
  "discover.title": "탐색",
  "discover.title_accent": "아티스트",
  "discover.no_artists": "아티스트를 찾을 수 없습니다",
  "footer.tagline": "아티스트와 고객을 위한 가장 스마트한 타투 가격 계산기.",
  "footer.links": "링크",
  "footer.privacy": "개인정보",
  "footer.language_label": "언어 & 앱",
  "footer.all_rights": "모든 권리 보유.",
  "footer.install_title": "설치",
  "footer.install_ok": "알겠습니다!",
  "dash.logout": "로그아웃",
  "dash.saved": "저장됨!",
  "dash.save_changes": "변경 사항 저장",
  "dash.cancel": "취소",
  "dash.add": "추가",
  "dash.delete": "삭제",
  "dash.previous": "이전",
  "dash.next": "다음",
  "success.welcome": "클럽에 오신 것을 환영합니다,",
  "success.go_dashboard": "대시보드로 이동",
  "landing.how_it_works_cta": "스튜디오에서 어떻게 작동하는지 확인하세요",
  "footer.terms": "이용약관",
  "footer.support": "지원",
  "footer.how_it_works": "어떻게 작동하나요?",
  "hiw.seo_title": "어떻게 작동하나요? – Inkvaluator",
  "hiw.seo_desc": "Inkvaluator가 타투 아티스트에게 어떻게 도움이 되는지 알아보세요.",
  "hiw.hero_title": "잉크에 집중하세요, 청구서 말고",
  "hiw.hero_p1":
    "가격에 대한 DM 답변에 다음 걸작을 그리는 것보다 더 많은 시간을 보내고 계신가요? 솔직히 말하면: 당신은 타투 아티스트이지 고객 서비스 직원이 아닙니다.",
  "hiw.hero_p2": '수동으로 답해야 하는 "이거 얼마예요?" 질문은 모두 예술에 쓰지 못하는 시간입니다.',
  "hiw.hero_p3":
    "Inkvaluator는 비즈니스를 필터링하는 디지털 어시스턴트입니다. 정확한 견적을 계산하고 정보를 수집하여 WhatsApp으로 완성된 요청을 보냅니다.",
  "hiw.hero_p4":
    '예산 없는 "가격 구경꾼"은 이제 그만. 진지한 리드, 전문적인 견적서, 그리고 당신의 기술에 대한 진지함을 보여주는 브랜딩만.',
  "hiw.hero_cta": "작업 시간을 되찾을 준비가 되셨나요?",
  "hiw.faq_title": "자주 묻는 질문",
  "hiw.faq_cat1": "기본: 어떻게 도움이 되나요?",
  "hiw.faq_q1": "또 다른 복잡한 앱인가요?",
  "hiw.faq_a1":
    "아닙니다. 인스타그램 프로필이나 사이트에 넣을 수 있는 초고속 계산기입니다. 규칙을 한 번 설정하면 도구가 나머지를 합니다.",
  "hiw.faq_q2": "구체적으로 어떻게 시간을 절약하나요?",
  "hiw.faq_a2": "10번 물어보는 대신, 모든 것이 채워진 WhatsApp 메시지를 받습니다.",
  "hiw.faq_cat2": "가격 및 설정",
  "hiw.faq_q3": "내 가격을 설정할 수 있나요?",
  "hiw.faq_a3": "물론이죠. 시간당 요금과 크기별 요금을 직접 결정할 수 있습니다.",
  "hiw.faq_q4": "타투가 평균보다 훨씬 복잡한 경우는요?",
  "hiw.faq_a4": "내장 AI 스캐너가 사진의 세부 사항을 인식하고 가격 견적을 조정합니다.",
  "hiw.faq_cat3": "외관 및 브랜딩",
  "hiw.faq_q5": "일반 도구처럼 보이지 않나요?",
  "hiw.faq_a5": "전혀 아닙니다. Studio 플랜에서는 완전한 화이트 라벨입니다.",
  "hiw.faq_q6": "고객에게 얼마나 전문적으로 보이나요?",
  "hiw.faq_a6": "매우 전문적입니다. 고객은 우아한 PDF로 견적을 다운로드할 수 있습니다.",
  "hiw.faq_cat4": "성장 및 도달 범위",
  "hiw.faq_q7": "지역 고객을 더 원하는데, 이것이 도움이 되나요?",
  "hiw.faq_a7": "네! Inkvaluator에는 국가, 도, 시로 검색할 수 있는 인터랙티브 세계 지도가 있습니다.",
  "hiw.backend_title": "무대 뒤: 백엔드의 힘",
  "hiw.backend_intro": "기술 애호가를 위해: Inkvaluator는 고급 기능으로 가득합니다.",
  "hiw.card_pricing_title": "동적 가격 관리",
  "hiw.card_pricing_text": "코드 없이 관리 모듈에서 가격과 Stripe ID를 조정하세요.",
  "hiw.card_ai_title": "AI 비전 통합",
  "hiw.card_ai_text": "AI 스캐너가 패턴, 색상, 복잡성을 분석하여 작업 시간을 계산합니다.",
  "hiw.card_stripe_title": "Stripe 고객 포털",
  "hiw.card_stripe_text": "완전 자동화된 구독 시스템.",
  "hiw.card_analytics_title": "분석 대시보드",
  "hiw.card_analytics_text": "전환율과 잠재 수익에 대한 깊은 인사이트를 얻으세요.",
  "hiw.card_blog_title": "블로그 & SEO CMS",
  "hiw.card_blog_text": "Google 검색 가시성을 높이는 기사 게시 시스템.",
  "hiw.card_global_title": "글로벌 아키텍처",
  "hiw.card_global_text": "고객 언어 설정이 유지되는 완전한 다국어 지원.",
  "hiw.card_mobile_title": "모바일 준비 (PWA)",
  "hiw.card_mobile_text":
    "Inkvaluator는 Progressive Web App(PWA)입니다. 브라우저에서 홈 화면에 직접 설치하세요. 초고속, 안전, 저장 공간 불필요.",
  "hiw.cta_title": "무엇을 기다리고 계세요?",
  "hiw.cta_accent": "계산은 그만, 창작을 시작하세요.",
  "hiw.cta_button": "Inkvaluator 무료로 체험하기",
  "tos.seo_title": "이용약관 | Inkvaluator",
  "tos.title": "이용약관",
  "tos.updated": "마지막 업데이트:",
  "tos.s1_title": "1. 정의",
  "tos.s1_platform": "플랫폼: inkvaluator.com에서 이용 가능한 SaaS 플랫폼 Inkvaluator.",
  "tos.s1_user": "사용자 / 아티스트: 플랫폼에 계정을 만드는 타투 아티스트 또는 스튜디오.",
  "tos.s1_visitor": "방문자: 공개 계산기나 탐색 섹션을 사용하는 모든 사람.",
  "tos.s1_service": "서비스: Inkvaluator가 제공하는 기능.",
  "tos.s2_title": "2. 계산기 사용",
  "tos.s2_p1": "가격 견적은 참고용이며 구속력 있는 제안이 아닙니다.",
  "tos.s2_p2": "Inkvaluator는 견적에 기반한 결정에 대해 책임을 지지 않습니다.",
  "tos.s3_title": "3. 구독 및 결제",
  "tos.s3_intro": "Inkvaluator는 Free, Pro, Studio 3가지 레벨을 제공합니다.",
  "tos.s3_billing": "유료 구독은 Stripe를 통해 월별로 청구됩니다.",
  "tos.s3_cancel": "언제든지 구독을 취소할 수 있습니다.",
  "tos.s3_refund": "이미 시작된 기간에 대한 환불은 불가능합니다.",
  "tos.s3_price_change": "Inkvaluator는 30일 전 통지로 가격을 변경할 권리가 있습니다.",
  "tos.s4_title": "4. 데이터 소유권",
  "tos.s4_p1": "아티스트가 입력한 모든 데이터는 아티스트의 소유입니다.",
  "tos.s4_p2": "익명의 집계 데이터는 통계 분석에 사용될 수 있습니다.",
  "tos.s5_title": "5. 책임",
  "tos.s5_p1": 'Inkvaluator는 서비스를 "있는 그대로" 제공합니다.',
  "tos.s5_indirect": "Inkvaluator는 간접 손해에 대해 책임을 지지 않습니다.",
  "tos.s5_cap": "총 책임은 이전 3개월간 지불한 금액으로 제한됩니다.",
  "tos.s5_content": "Inkvaluator는 아티스트가 게시하는 콘텐츠에 대해 책임을 지지 않습니다.",
  "tos.s6_title": "6. 계정 및 종료",
  "tos.s6_credentials": "사용자는 로그인 자격 증명의 기밀을 유지할 책임이 있습니다.",
  "tos.s6_suspend": "Inkvaluator는 남용 시 계정을 정지할 권리가 있습니다.",
  "tos.s6_delete": "계정 종료 시 개인 데이터는 다음에 따라 삭제됩니다",
  "tos.s6_privacy_link": "개인정보 보호정책",
  "tos.s7_title": "7. 지적 재산",
  "tos.s7_p1": "모든 지적 재산권은 Inkvaluator에 귀속됩니다.",
  "tos.s8_title": "8. 준거법",
  "tos.s8_p1": "이 이용약관은 네덜란드 법률에 의해 규율됩니다.",
  "tos.contact_title": "이 약관에 대한 질문이 있으신가요?",
  "tos.contact_text": "문의처:",
  "support.seo_title": "지원 | Inkvaluator",
  "support.title": "지원",
  "support.faq_title": "자주 묻는 질문",
  "support.faq_q1": "요금은 어떻게 설정하나요?",
  "support.faq_a1": '대시보드에서 "가격"을 클릭하여 시간당 요금, 최소 가격 등을 설정하세요.',
  "support.faq_q2": "Stripe 통합은 어떻게 작동하나요?",
  "support.faq_a2": "Pro 또는 Studio로 업그레이드하면 안전한 Stripe 결제로 이동합니다.",
  "support.faq_q3": "WhatsApp 모듈이 뭔가요?",
  "support.faq_a3": "WhatsApp 모듈(Pro부터)로 방문자가 가격 계산 후 WhatsApp으로 연락할 수 있습니다.",
  "support.faq_q4": "계산기를 어떻게 공유하나요?",
  "support.faq_a4": '대시보드 "공유"에서 직접 링크, QR 코드, 임베드 코드를 찾을 수 있습니다.',
  "support.faq_q5": "구독을 취소할 수 있나요?",
  "support.faq_a5": "네, 언제든지 취소 가능하며 청구 기간 끝까지 접근을 유지합니다.",
  "support.faq_q6": "AI 스캐너는 어떻게 작동하나요?",
  "support.faq_a6": "AI 스캐너가 사진을 분석하여 크기, 복잡성, 스타일을 자동 추정합니다.",
  "support.faq_q7": "버그를 발견했는데, 어떻게 하나요?",
  "support.faq_a7": "support@inkvaluator.com으로 문제 설명과 스크린샷을 보내주세요.",
  "support.contact_title": "연락처",
  "support.contact_text": "질문이 목록에 없나요? 팀에 직접 연락하세요:",
  "support.form_name": "이름",
  "support.form_name_placeholder": "이름",
  "support.form_email": "이메일",
  "support.form_message": "메시지",
  "support.form_message_placeholder": "질문이나 문제를 설명해주세요...",
  "support.form_submit": "메시지 보내기",
  "support.form_subject": "{name}의 지원 요청",
  "support.toast_title": "이메일 클라이언트 열림",
  "support.toast_desc": "이메일 앱이 메시지와 함께 열리고 있습니다.",
  "privacy.seo_title": "개인정보 보호정책 | Inkvaluator",
  "privacy.title": "개인정보 보호정책",
  "privacy.updated": "마지막 업데이트:",
  "privacy.s1_title": "1. 어떤 데이터를 수집하나요?",
  "privacy.s1_intro": "Inkvaluator는 플랫폼 운영에 필요한 데이터를 수집합니다:",
  "privacy.s1_artists_label": "아티스트용",
  "privacy.s1_artists": "이름, 이메일, 스튜디오 이름, 주소 및 포트폴리오.",
  "privacy.s1_visitors_label": "방문자용",
  "privacy.s1_visitors": "견적 생성을 위해 계산기에 입력된 데이터.",
  "privacy.s1_leads_label": "리드",
  "privacy.s1_leads": "아티스트에게 연락하면 메시지와 사진이 안전하게 처리됩니다.",
  "privacy.s2_title": "2. 데이터 사용",
  "privacy.s2_intro": "개인정보 보호가 최우선입니다. 제3자에게 데이터를 판매하지 않습니다.",
  "privacy.s2_matching": "고객을 적합한 아티스트와 매칭.",
  "privacy.s2_algorithms": "가격 알고리즘 개선.",
  "privacy.s2_pwa": "PWA 기술적 운영.",
  "privacy.s3_title": "3. 국제 법률 (GDPR)",
  "privacy.s3_intro": "유럽 법률에 따라 다음 권리가 있습니다:",
  "privacy.s3_access_label": "접근권",
  "privacy.s3_access": "보유 중인 데이터를 조회할 수 있습니다.",
  "privacy.s3_erasure_label": "삭제권",
  "privacy.s3_erasure": "계정과 데이터의 완전한 삭제를 요청할 수 있습니다.",
  "privacy.s3_portability_label": "데이터 이동성",
  "privacy.s3_portability": "읽을 수 있는 형식으로 데이터를 요청할 수 있습니다.",
  "privacy.s4_title": "4. 쿠키 및 PWA",
  "privacy.s4_text": "Inkvaluator는 기능적 캐싱(PWA)을 사용합니다. 추적 쿠키를 사용하지 않습니다.",
  "privacy.contact_title": "개인정보에 대한 질문?",
  "privacy.contact_text": "데이터 보호 담당자에게 문의하세요:",
  "privacy.seo_desc": "Inkvaluator의 개인정보 보호정책을 읽어보세요.",
  "dash.unsaved_changes": "저장되지 않은 변경 사항이 있습니다",
  "dash.dashboard_language": "대시보드 언어",
  "dash.dashboard_language_desc": "관리 인터페이스의 언어.",
  "auth.dashboard_subtitle": "아티스트 대시보드",
  "dash.impersonation_banner": "{name}의 프로필을 보고 있습니다",
  "dash.stop_impersonation": "가장 중지",
  "discover.subtitle": "스튜디오 & 아티스트 발견",
  "discover.description": "가까운 타투 아티스트와 스튜디오를 찾아보세요.",
  "discover.hide_map": "지도 숨기기",
  "discover.show_map": "지도 보기",
  "discover.map_loading": "지도 로딩 중...",
  "discover.call": "전화",
  "discover.route": "경로",
  "discover.search_placeholder": "도시, 아티스트 또는 스튜디오로 검색...",
  "discover.view_calculator": "계산기 보기",
  "discover.map_title": "지도의 아티스트",
  "feature.calculator": "맞춤형 가격 계산기",
  "feature.qr": "QR 코드 & 공유 링크",
  "feature.styles": "무제한 스타일 & AI 스캐너",
  "feature.logo": "로고 업로드",
  "feature.theme": "맞춤 색상 테마",
  "feature.language": "다국어 지원 (17개 언어)",
  "feature.currency": "통화 전환 (13개 통화)",
  "feature.analytics": "사용 분석",
  "feature.pdf": "PDF 내보내기",
  "feature.whitelabel": "화이트 라벨 브랜딩",
  "feature.map_visibility": "세계 지도 가시성",
  "feature.multiartist": "다중 아티스트 관리",
  "feature.centralanalytics": "중앙 분석 대시보드",
  "feature.teammanagement": "팀 & 역할 관리",
  "map.view_calculator": "계산기 보기",
  "map.unknown_location": "알 수 없는 위치",
  "map.artists_count": "아티스트",
  "map.world_title": "아티스트",
  "map.world_accent": "전 세계",
  "map.in_countries": "{count}개국에서",
  "footer.discover_artists": "아티스트 발견",
  "footer.artist_login": "아티스트 로그인",
  "footer.blog": "블로그",
  "blog.title": "블로그",
  "blog.no_posts": "아직 게시된 기사가 없습니다.",
  "blog.read_more": "더 읽기",
  "blog.published_on": "게시일",
  "blog.back": "블로그로 돌아가기",
  "blog.not_found": "기사를 찾을 수 없습니다",
  "blog.translation_notice": "이 기사는 아직 귀하의 언어로 제공되지 않습니다. 원본 버전이 표시됩니다.",
  "landing.artist_or_studio": "아티스트 또는 스튜디오인가요?",
  "demo.subtitle": "플레이그라운드",
  "demo.title": "체험하기",
  "demo.title_accent": "계산기",
  "demo.estimated_price": "예상 가격",
  "demo.disclaimer": "이것은 데모입니다. 실제 가격은 아티스트와 디자인에 따라 다릅니다.",
  "demo.artist_cta": "아티스트인가요?",
  "demo.register_free": "무료 등록",
  "demo.find_studio": "근처 스튜디오 찾기",
  "profile.identity": "신원",
  "profile.studio_name": "스튜디오 이름",
  "profile.location": "위치",
  "profile.city": "도시",
  "profile.calculator_language": "계산기 언어",
  "embed.title": "임베드 위젯",
  "embed.copy_code": "코드 복사",
  "upgrade.for_studios": "타투 스튜디오 & 팀용",
  "upgrade.cancel_anytime_long": "언제든지 취소 가능. 숨겨진 비용 없음.",
  "vault.status_concept": "초안",
  "vault.status_sent": "요청 전송됨",
  "reviews.rating": "평점",
  "reviews.verified_reviews": "인증된 리뷰",
  "reviews.read_all": "Google에서 모든 리뷰 읽기",
  "discover.swipe": "스와이프",
  "profile.manual_place_id": "스튜디오를 찾을 수 없나요? Place ID를 수동으로 입력하세요.",
  "vault.inspiration_title": "영감",
  "vault.try_style": "시도 →",
  "vault.compare": "비교",
  "vault.compare_title": "비교",
  "trends.title": "타투",
  "trends.title_accent": "트렌드",
  "trends.top_styles": "인기 스타일",
  "trends.top_body_parts": "인기 부위",
  "match.title": "가까운 매칭 아티스트",
  "match.score": "매치",
  "match.view": "프로필 보기",
  "urgency.recent_requests": "이번 주 요청",
  "urgency.wait_time": "예상 대기",
  "urgency.weeks": "주",
  "urgency.high_demand": "높은 수요",
  "urgency.popular": "인기",
  "benchmark.above_market": "시장 평균 이상",
  "benchmark.below_market": "시장 평균 이하",
  "benchmark.at_market": "시장 평균",
  "benchmark.market_avg": "시장 평균",
  "portfolio.style_examples": "스타일 예시",
  "story.title": "Instagram 스토리 생성기",
  "story.desc": "QR 코드가 포함된 프리미엄 Black & Gold 스토리 이미지를 생성하세요.",
  "story.generate": "스토리 생성",
  "story.regenerate": "재생성",
  "story.download": "다운로드",
  "story.share": "공유",
  "story.downloaded": "스토리 다운로드 완료!",
  "story.cta_line1": "가격 계산",
  "story.cta_line2": "아래 QR 코드를 스캔하세요",
  "story.scan_prompt": "↑ 스캔하여 견적 받기",
  "trends.filter_country": "국가별 필터",
  "trends.all_countries": "모든 국가",
  "trends.analyzed": "건 계산 분석",
  "trends.realtime": "실시간 업데이트",
  "match.style_experts": "{city}의 {style} 전문가",
  "vault.review_booster_title": "타투 완료? 아티스트를 도와주세요!",
  "vault.review_booster_desc": "Google 리뷰를 남기고 다른 고객이 이 아티스트를 찾도록 도와주세요.",
  "vault.review_booster_btn": "Google 리뷰 작성",
  "checklist.title": "인증 상태",
  "checklist.profile_complete": "프로필 완료",
  "checklist.google_linked": "Google Place ID 연결됨",
  "checklist.badge_detected": "웹사이트에 인증 배지",
  "checklist.push_enabled": "푸시 알림 활성화",
  "checklist.not_yet": "아직",
  "checklist.active": "활성",
  "lead.status_new": "새로운",
  "lead.status_contacted": "연락 중",
  "lead.status_booked": "예약됨",
  "lead.change_status": "상태",
  "lead.add_price": "최종 가격 추가",
  "push.test_body": "푸시 알림이 활성화되었습니다! 새로운 리드에 대한 알림을 받게 됩니다.",
  "push.send_test": "테스트 알림 보내기",
  "push.active_desc": "고품질 리드가 도착하면 알림을 받습니다.",
  "push.blocked": "브라우저 설정에서 알림이 차단되어 있습니다.",
  "push.enable_desc": "고품질 리드가 도착하면 알림을 받으세요.",
  "push.label": "푸시 알림",
  "push.welcome_body": "환영합니다! 고품질 리드가 도착하면 알림을 받게 됩니다.",
  "push.denied_info": "새로운 리드 소식을 놓치지 마세요 — 브라우저 설정에서 알림을 활성화하세요.",
  "vault.live_status": "실시간 상태",
  "vault.status_new_desc": "요청이 접수되었습니다",
  "vault.status_contacted_desc": "아티스트가 메시지를 확인했습니다",
  "vault.status_booked_desc": "예약이 확정되었습니다!",
  "vault.link_copied": "🔗 링크가 복사되었습니다!",
  "analytics.actual_revenue": "실제 수익",
  "style_city.popular_styles": "{city}의 인기 스타일",
  "nav.home": "홈",
  "lead.estimate_header": "타투 견적",
  "discover.verified_badge": "인증됨",
  "wa.title": "WhatsApp으로 아티스트에게 전송",
  "wa.subtitle": "견적을 직접 전송",
  "wa.name": "이름 (선택)",
  "wa.name_placeholder": "이름",
  "wa.message": "메시지 (선택)",
  "wa.message_placeholder": "아이디어, 영감 또는 세부사항을 설명하세요...",
  "wa.photo": "참조 사진 (선택)",
  "wa.upload_photo": "사진 업로드",
  "wa.file_too_large": "파일이 너무 큽니다",
  "wa.summary": "요약:",
  "wa.send": "WhatsApp으로 전송",
  "wa.sent_title": "메시지 전송 완료!",
  "wa.sent_desc": "{name}에게 WhatsApp으로 요청이 전송되었습니다.",
  "wa.upload_failed": "업로드 실패",
  "wa.save_failed": "저장 실패",
  "wa.success": "전송 완료!",
  "wa.success_desc": "요청이 생성되고 WhatsApp이 열렸습니다.",
  "wa.error": "오류가 발생했습니다",
  "wa.error_desc": "알 수 없는 오류입니다. 연결을 확인하고 다시 시도하세요.",
  "wa.msg_greeting": "안녕하세요 {name}, 가격 견적을 계산했습니다:",
  "wa.msg_name_label": "이름",
  "wa.msg_style_label": "🎨 스타일",
  "wa.msg_bodypart_label": "📍 신체 부위",
  "wa.msg_size_label": "📏 크기",
  "wa.msg_complexity_label": "⚙️ 복잡도",
  "wa.msg_time_label": "⏱ 예상 시간",
  "wa.msg_total_label": "💰 합계",
  "wa.msg_deposit_label": "💳 예약금",
  "wa.msg_details_label": "🔗 상세",
  "wa.template_high_greeting": "안녕하세요 {name}, 좋은 소식! 타투에 대한 상세 견적을 계산했습니다:",
  "wa.template_high_vault": "🔗 타투 보관함에 저장했습니다: {url}",
  "wa.template_low_greeting": "안녕하세요 {name}, 타투에 관심이 있습니다. 가용성과 가격에 대해 알려주실 수 있나요?",
  "wa.template_low_note": "계산기 기반 첫 번째 아이디어:",
  "tooltip.labor": "이 크기에 필요한 예상 시간에 아티스트의 시급을 곱한 것입니다.",
  "tooltip.complexity": "더 복잡한 디자인은 더 많은 시간과 정밀함이 필요합니다.",
  "tooltip.placement": "일부 신체 부위는 더 민감하거나 접근이 어려워 추가 시간이 필요합니다.",
  "tooltip.style": "다른 스타일은 가격에 영향을 미치는 특정 기술이 필요합니다.",
  "tooltip.design": "고유한 타투 디자인 및 준비 비용.",
  "tooltip.deposit": "예약금은 예약을 확보하며 총 가격에서 차감됩니다.",
  "pricing_edu.button": "이 계산은 어떻게 작동하나요?",
  "pricing_edu.title": "가격 계산 방법",
  "pricing_edu.intro": "타투 가격은 장인 정신과 시간을 반영하는 여러 요인에 의해 결정됩니다.",
  "pricing_edu.disclaimer": "이것은 항상 견적입니다. 최종 가격은 아티스트와의 개인 상담 후 결정됩니다.",
  "ai.title": "AI 스캐너",
  "ai.desc": "사진을 업로드하고 AI가 설정을 결정하게 하세요",
  "ai.analyzing": "AI가 분석 중...",
  "ai.scan_complete": "✨ AI 스캔 완료",
  "ai.scan_failed": "스캔 실패",
  "ai.try_again": "다시 시도하세요",
  "referral.title": "동료 추천",
  "referral.generate": "추천 링크 생성",
  "pwa.install_title": "앱으로 설치",
  "pwa.install_button": "지금 설치",
  // Batch 3+4
  "tab.theme": "테마",
  "tab.branding": "브랜딩",
  "tab.analytics": "분석",
  "tab.price_engine": "가격 엔진",
  "basic.desc": "기본 요금 및 비용 설정",
  "basic.min_price_desc": "최저 요금",
  "basic.hourly_rate_desc": "표준 시급",
  "sizes.title": "크기 설정",
  "sizes.desc": "크기별 예상 시간 설정",
  "sizes.min_hours": "최소 시간",
  "sizes.max_hours": "최대 시간",
  "sizes.hours": "시간",
  "complexity.title": "복잡도 배율",
  "complexity.desc": "복잡도에 따라 가격 조정",
  "bodyparts.title": "신체 부위 배율",
  "bodyparts.desc": "일부 부위는 타투가 더 어렵습니다",
  "styles.title": "스타일 관리",
  "styles.desc": "스타일 활성화/비활성화 및 가격 조정",
  "theme.title": "색상 테마",
  "theme.desc": "계산기 색상 커스터마이즈",
  "theme.preview": "미리보기",
  "theme.hue": "색조",
  "theme.saturation": "채도",
  "theme.lightness": "명도",
  "theme.presets": "빠른 선택",
  "branding.title": "브랜딩",
  "branding.desc": "로고 업로드 및 언어/통화 설정",
  "branding.logo": "로고",
  "branding.upload": "로고 업로드",
  "branding.remove": "제거",
  "branding.language": "계산기 언어",
  "branding.currency": "통화",
  "branding.qr_code": "QR 코드",
  "branding.download_qr": "QR 다운로드",
  "onboard.step_minimum": "최소 요금",
  "onboard.step_hourly": "시급",
  "onboard.step_sizes": "크기 확인",
  "onboard.step_styles": "스타일 설정",
  "onboard.step_logo": "로고 업로드",
  "onboard.step_finish": "계산기 완성!",
  "dash.onboard_min_desc": "최소 요금을 설정하세요.",
  "dash.onboard_hourly_desc": "시급을 설정하세요. 모든 계산의 기초입니다.",
  "dash.onboard_finish_title": "계산기가 준비되었습니다!",
  "dash.onboard_finish_desc": "고유 링크를 공유하세요. 설정은 언제든 변경 가능합니다.",
  "dash.your_link": "계산기 링크:",
  "dash.online_card": "디지털 명함입니다.",
  "dash.view_my_calculator": "내 계산기 보기",
  "dash.upload_thumbnail": "사용자 정의 이미지",
  "dash.reset_to_default": "초기화",
  "dash.profile_saved": "프로필 저장!",
  "dash.calculator_ready": "🎉 계산기 완성!",
  "dash.step_of": "단계 {current}/{total}",
  "dash.start_inkprice": "InkPrice 시작",
  "laf.title": "외관",
  "laf.desc": "공개 계산기의 외관 커스터마이즈",
  "profile.mfa_title": "계정 보안",
  "profile.mfa_enable": "2FA 활성화",
  "profile.google_reviews": "Google 리뷰",
  "profile.sync_now": "동기화",
  "landing.faq_title": "자주 묻는 질문",
  "landing.faq_subtitle": "Inkvaluator에 대해",
  "landing.faq_q1": "계산기는 어떻게 작동하나요?",
  "landing.faq_a1": "요금과 배율을 설정하면 고객이 즉시 견적을 받습니다.",
  "landing.faq_q2": "무료인가요?",
  "landing.faq_a2": "네! Free 플랜에는 완전한 계산기가 포함됩니다.",
  "city.title": "{city}의 타투 아티스트",
  "city.no_artists": "{city}에 아티스트가 없습니다.",
  "seo.landing_title": "Inkvaluator – 타투 가격 계산기",
  "vault.title": "나의",
  "vault.title_accent": "보관함",
  "vault.desc": "저장된 견적.",
  "vault.save_btn": "저장",
  "success.active": "활성 — 모든 기능 해제",
  "success.syncing": "플랜 활성화 중…",
  "analytics.title": "분석",
  "analytics.total_calculations": "총 계산",
  "analytics.no_data": "데이터 없음.",
  "studio.title": "스튜디오 관리",
  "studio.team": "팀",
  "studio.invite": "아티스트 초대",
  "color.gold": "골드",
  "color.red": "빨강",
  "color.blue": "파랑",
  "color.green": "녹색",
  "tip.title": "아티스트 추천",
  "share.button": "공유",
  "compare.title": "가격 비교",
  "landing.calculate_price": "가격 계산",
  "landing.total_value_prefix": "이미",
  "landing.total_value_suffix": "타투 아트가 계산되었습니다",
  "step.next": "다음",
  "step.view_result": "가격 보기",
  "step.details": "세부 사항",
  "step.details_desc": "디자인이 얼마나 정교한가요?",
  "layout.stepper": "단계별",
  "layout.form": "한 번에 보기",
  "calc.view_price_cta": "가격 개요 보기",
  "calc.rough_estimate": "대략적인 추정",
  "calc.calculated_price": "계산된 가격",
  "calc.save_for_later": "나중을 위해 저장",
  "calc.share": "공유",
  "calc.more_options": "더 많은 옵션",
  "calc.compare_configs": "구성 비교",
  "calc.compare_desc": "두 가격 구성을 나란히 비교",
  "calc.no_photo": "참조 사진이 추가되지 않았습니다",
  "calc.link_copied": "링크가 복사되었습니다!",
  "whatsapp.add_photo": "사진 추가",
  "whatsapp.photos_label": "참조 사진 — 최대 3장",
  "laf.reset_success": "테마가 기본값으로 재설정되었습니다",
  "admin.stripe_managed": "구독은 Stripe를 통해 자동으로 관리됩니다 — 변경 사항은 다음 웹훅 동기화 후에 표시됩니다",
  "calc.view_tattoo_plan": "타투 플랜 보기 ✓",
  "wa.plan_message": "여기 제 타투 플랜입니다:",
  "lead.tattoo_plan": "타투 플랜",
  "lead.made_with": "Inkvaluator로 제작",
  "lead.contact_artist": "{artist}에게 연락하기",
  "lead.share": "공유",
  "lead.share_title": "내 타투 플랜",
  "lead.link_copied": "링크가 복사되었습니다!",
  "lead.deposit_label": "보증금",
  "lead.pay_deposit": "보증금 {amount} 결제",
  "lead.deposit_request_title": "보증금 협의 요청",
  "lead.deposit_request_desc": "아티스트에게 ~{amount} 보증금이 가능한지 문의하세요.",
  "lead.deposit_request_wa": "WhatsApp으로 요청 보내기",
  "lead.for_you": "당신",
  "lead.for_label": "",
  "lead.back_to_calculator": "계산기로 돌아가기",
  "lead.deposit_wa_msg":
    "{artist}님, Inkvaluator에서 타투 플랜을 확인했습니다. ~{amount} 보증금이 가능할까요?\n플랜: {url}",
  "blog.no_translation": "번역을 사용할 수 없습니다",
  "flash.tab": "플래시",
  "flash.upload": "플래시 디자인 업로드",
  "flash.title": "제목",
  "flash.price": "가격",
  "flash.size": "크기 (cm)",
  "flash.available": "이용 가능",
  "flash.featured": "추천",
  "flash.limit_used": "{max}개 중 플래시 디자인 사용됨",
  "flash.delete_confirm": "플래시 디자인을 삭제하시겠습니까?",
  "flash.reserve_whatsapp": "WhatsApp으로 예약",
  "flash.whatsapp_msg": "플래시 타투에 관심이 있습니다:",
  "flash.matching_designs": "매칭 플래시 디자인",
  "flash.only_with_flash": "플래시가 있는 아티스트만",
  "groei.score": "프로필 점수",
  "groei.views": "프로필 조회수",
  "groei.sessions": "계산기 세션",
  "groei.leads": "리드",
  "groei.this_week": "이번 주",
  "groei.improve": "프로필 개선하기",
  "groei.add_photos": "포트폴리오 사진 추가",
  "groei.add_whatsapp": "WhatsApp 번호 설정",
  "groei.add_bio": "더 긴 소개 작성",
  "groei.add_avatar": "프로필 사진 업로드",
  "groei.add_rate": "시간당 요금 설정",
  "groei.add_city": "도시 추가",
  "groei.get_leads": "계산기를 공유하여 리드 받기",
  "groei.collapse": "접기",
  "groei.expand": "펼치기",
  "groei.tip_portfolio": "6장 이상 사진이 있는 아티스트는 3배 더 많은 세션 확보",
  "groei.tip_whatsapp": "WhatsApp 버튼이 있는 고객은 5배 더 잘 전환",
  "groei.tip_leads": "계산기 링크를 공유하여 첫 리드 받기",
  "groei.tip_avatar": "프로필 사진이 신뢰도를 높입니다",
  "groei.tip_action_photos": "사진 추가",
  "groei.tip_action_whatsapp": "WhatsApp 설정",
  "groei.tip_action_share": "계산기 공유",
  "groei.tip_action_avatar": "사진 업로드",
  "milestone.eerste_lead": "첫 리드 수신!",
  "milestone.tien_leads": "10 리드 달성!",
  "milestone.profiel_compleet": "프로필 100% 완료!",
  "milestone.eerste_flash": "첫 플래시 디자인 업로드!",
  "wa.name_required": "이름은 필수입니다",
  "wa.plan_message_full": "내 Tattoo Plan 보기: {url}",
};

// ─── RUSSIAN (complete) ──────────────────────────────────────────
const ru: Record<string, string> = {
  ...en,
  "app.tagline": "Рассчитай цену татуировки",
  back: "Назад",
  save: "Сохранить",
  copy: "Копировать",
  copied: "Скопировано!",
  loading: "Загрузка...",
  error: "Ошибка",
  optional: "необязательно",
  "landing.hero_title": "Рассчитай свою",
  "landing.hero_title_accent": "Цену Татуировки",
  "landing.hero_desc": "Узнайте за секунды, сколько стоит ваша следующая татуировка.",
  "landing.try_it": "Попробовать!",
  "landing.estimated_price": "Примерная цена",
  "landing.your_rates": "Ваши тарифы",
  "landing.rates_desc": "Двигайте ползунки и наблюдайте за изменением цены",
  "landing.hourly_rate": "Почасовая ставка",
  "landing.start_cost": "Фиксированные стартовые расходы",
  "landing.size_label": "Размер",
  "landing.complexity_label": "Сложность",
  "landing.body_placement": "Расположение на теле",
  "landing.find_artist": "Найдите своего идеального",
  "landing.find_artist_accent": "мастера",
  "landing.nearby_soon": "Скоро здесь появятся мастера рядом с вами.",
  "landing.discover_all": "Все мастера",
  "landing.artist_question": "Вы мастер или студия?",
  "landing.view_options": "Посмотреть варианты и цены",
  "landing.start_free": "Начать бесплатно",
  "landing.discover": "Исследовать",
  "landing.login": "Войти",
  "landing.founder_desc": "50% скидка для первых 50 регистраций",
  "landing.claim_now": "Забрать сейчас →",
  "landing.how_it_works_cta": "Узнайте, как это работает для вашей студии",
  "calc.estimate": "Примерная цена",
  "calc.size": "Размер",
  "calc.size_desc": "Какого размера будет ваша татуировка?",
  "calc.complexity": "Сложность",
  "calc.complexity_desc": "Насколько детализирован дизайн?",
  "calc.body_part": "Часть тела",
  "calc.body_part_desc": "Нажмите на часть тела для татуировки",
  "calc.style": "Стиль",
  "calc.style_desc": "Какой стиль татуировки вы хотите?",
  "calc.budget": "Бюджет",
  "calc.budget_desc": "Каков ваш максимальный бюджет?",
  "calc.budget_placeholder": "Напр. 300",
  "calc.get_estimate": "Получите реалистичную оценку от",
  "calc.no_artist": "Мастер с таким кодом или ссылкой не найден.",
  "calc.back_home": "На главную",
  "calc.artist_login": "Мастер? Войти",
  "calc.add_color": "Добавить цвет?",
  "calc.color_surcharge": "+20% надбавка за цветную работу",
  "breakdown.title": "Разбивка цены",
  "breakdown.labor": "Работа (время)",
  "breakdown.est_hours": "Расчётные часы",
  "breakdown.subtotal": "Промежуточный итог",
  "breakdown.complexity": "Сложность",
  "breakdown.placement": "Расположение",
  "breakdown.style": "Стиль",
  "breakdown.design_cost": "Стоимость дизайна",
  "breakdown.deposit": "Задаток",
  "breakdown.total_range": "Общий диапазон",
  "breakdown.disclaimer":
    "Это оценка, а не окончательная цена. Окончательная цена определяется после консультации с мастером.",
  "breakdown.total_indication": "Общий расчёт",
  "breakdown.required_deposit": "Необходимый задаток",
  "breakdown.hours_unit": "часов",
  "faq.title": "Часто задаваемые вопросы",
  "dash.welcome": "Добро пожаловать в Inkvaluator",
  "dash.logout": "Выйти",
  "dash.saved": "Сохранено!",
  "dash.save_changes": "Сохранить изменения",
  "dash.cancel": "Отменить",
  "dash.add": "Добавить",
  "dash.delete": "Удалить",
  "dash.previous": "Назад",
  "dash.next": "Далее",
  "tab.basic": "Основное",
  "tab.sizes": "Размеры",
  "tab.complexity": "Сложность",
  "tab.body_parts": "Части тела",
  "tab.styles": "Стили",
  "tab.theme": "Тема",
  "tab.branding": "Брендинг",
  "tab.analytics": "Аналитика",
  "basic.title": "Базовая ставка",
  "basic.min_price": "Минимальная цена",
  "basic.hourly_rate": "Почасовая ставка",
  "basic.daily_rate": "Дневная ставка",
  "basic.design_cost": "Стоимость дизайна",
  "basic.fixed": "Фикс.",
  "basic.hourly": "В час",
  "basic.deposit": "Задаток",
  "size.mini": "Мини (< 5 см)",
  "size.small": "Маленький (5-10 см)",
  "size.medium": "Средний (10-20 см)",
  "size.large": "Большой (20-35 см)",
  "size.xl": "Очень большой (35+ см)",
  "complexity.simple": "Простой",
  "complexity.medium": "Средний",
  "complexity.detailed": "Детализированный",
  "body.arm": "Рука (общ.)",
  "body.forearm": "Предплечье",
  "body.upper_arm": "Плечо",
  "body.leg": "Нога (общ.)",
  "body.thigh": "Бедро",
  "body.calf": "Голень",
  "body.back": "Спина",
  "body.chest": "Грудь",
  "body.ribs": "Рёбра",
  "body.stomach": "Живот",
  "body.hand": "Кисть",
  "body.foot": "Стопа",
  "body.neck": "Шея",
  "body.face": "Лицо",
  "body.shoulder": "Плечо",
  "body.hip": "Бедро",
  "body.finger": "Палец",
  "body.other": "Другое",
  "body.selected": "Выбрано",
  "body.front": "Спереди",
  "body.back_view": "Сзади",
  "body.front_view": "Спереди",
  "body.back_view_btn": "Сзади",
  "style.black_grey": "Чёрно-серый",
  "style.full_color": "Полноцветный",
  "style.realism": "Реализм",
  "style.fine_line": "Тонкая линия",
  "style.traditional": "Традиционный",
  "style.neo_traditional": "Нео-традиционный",
  "style.watercolor": "Акварель",
  "style.tribal": "Трайбл",
  "style.japanese": "Японский",
  "style.geometric": "Геометрический",
  "style.lettering": "Леттеринг",
  "style.script": "Скрипт",
  "style.dotwork": "Дотворк",
  "style.mandala": "Мандала",
  "style.blackwork": "Блэкворк",
  "style.illustrative": "Иллюстративный",
  "style.surrealism": "Сюрреализм",
  "style.ornamental": "Орнаментальный",
  "style.micro_realism": "Микро-реализм",
  "style.trash_polka": "Трэш Полька",
  "style.other": "Другое",
  "consult.title": "Готовы начать?",
  "consult.book": "Записаться на консультацию",
  "pdf.title": "Оценка стоимости",
  "pdf.download": "Скачать PDF",
  "upgrade.choose": "Выберите свой",
  "upgrade.plan_word": "Тариф",
  "upgrade.per_month": "/мес",
  "upgrade.perfect_start": "Идеально для начала",
  "upgrade.everything_pro": "Всё для профессионалов",
  "upgrade.for_studios": "Для тату-студий и команд",
  "upgrade.upgrade_pro": "Перейти на Pro",
  "upgrade.upgrade_studio": "Перейти на Studio",
  "upgrade.manage_sub": "Управлять подпиской",
  "upgrade.cancel_anytime_long": "Отмена в любое время. Без скрытых платежей.",
  "upgrade.discount": "скидка",
  "upgrade.test_account_active": "Тестовый аккаунт — функции активированы",
  "sub.free": "Бесплатно",
  "sub.pro": "Pro",
  "sub.studio": "Studio",
  "auth.login": "Войти",
  "auth.register": "Регистрация",
  "auth.artist_name": "Имя мастера",
  "auth.email": "Электронная почта",
  "auth.password": "Пароль",
  "auth.submit_login": "Войти",
  "auth.submit_register": "Создать аккаунт",
  "auth.switch_register": "Нет аккаунта? Зарегистрируйтесь",
  "auth.switch_login": "Уже есть аккаунт? Войдите",
  "discover.title": "Найти",
  "discover.title_accent": "Мастеров",
  "discover.search_placeholder": "Поиск по городу, мастеру или студии...",
  "discover.no_artists": "Мастера не найдены",
  "discover.view_calculator": "Открыть калькулятор",
  "footer.tagline": "Самый умный калькулятор цен на татуировки.",
  "footer.links": "Ссылки",
  "footer.discover_artists": "Найти мастеров",
  "footer.artist_login": "Вход для мастеров",
  "footer.privacy": "Политика конфиденциальности",
  "footer.language_label": "Язык",
  "footer.all_rights": "Все права защищены.",
  "footer.install_title": "Установить",
  "footer.install_ok": "Понятно!",
  "footer.terms": "Условия использования",
  "footer.support": "Поддержка",
  "footer.how_it_works": "Как это работает",
  "success.welcome": "Добро пожаловать в клуб,",
  "success.go_dashboard": "Перейти в панель",
  "hiw.seo_title": "Как это работает – Inkvaluator",
  "hiw.seo_desc": "Узнайте, как Inkvaluator помогает тату-мастерам.",
  "hiw.hero_title": "Сосредоточьтесь на чернилах, а не на счетах",
  "hiw.hero_p1": "Тратите больше времени на ответы о ценах, чем на создание шедевров?",
  "hiw.hero_p2":
    "Каждый вопрос «Сколько стоит?», на который вы отвечаете вручную, — это время, которое вы не тратите на искусство.",
  "hiw.hero_p3":
    "Inkvaluator — цифровой помощник, который фильтрует ваш бизнес. Пока вы в зоне, инструмент рассчитывает точные оценки и отправляет готовый запрос в WhatsApp.",
  "hiw.hero_p4": "Больше никаких «ценовых туристов» без бюджета. Только серьёзные лиды и профессиональные предложения.",
  "hiw.hero_cta": "Готовы вернуть своё рабочее время?",
  "hiw.faq_title": "Часто задаваемые вопросы",
  "hiw.faq_cat1": "Основы: как это мне помогает?",
  "hiw.faq_q1": "Это очередное сложное приложение?",
  "hiw.faq_a1":
    "Нет. Это быстрый калькулятор для Instagram-био или сайта. Настройте правила один раз, и инструмент сделает всё остальное.",
  "hiw.faq_q2": "Как это конкретно экономит время?",
  "hiw.faq_a2": "Вместо десяти вопросов вы получаете одно сообщение WhatsApp со всей информацией.",
  "hiw.faq_cat2": "Цены и настройки",
  "hiw.faq_q3": "Могу ли я установить свои цены?",
  "hiw.faq_a3": "Конечно. Вы сами устанавливаете часовую ставку и цены за разные размеры.",
  "hiw.faq_q4": "Что если татуировка намного сложнее?",
  "hiw.faq_a4": "Встроенный AI-сканер распознаёт детали на фото и корректирует оценку.",
  "hiw.faq_cat3": "Внешний вид и брендинг",
  "hiw.faq_q5": "Не выглядит ли это как обычный инструмент?",
  "hiw.faq_a5": "Нет. С планом Studio инструмент полностью white-label: ваш логотип, цвета и стиль.",
  "hiw.faq_q6": "Насколько профессионально это выглядит?",
  "hiw.faq_a6": "Очень профессионально. Клиент может скачать предложение в виде PDF.",
  "hiw.faq_cat4": "Рост и охват",
  "hiw.faq_q7": "Я хочу больше клиентов из моего региона, это поможет?",
  "hiw.faq_a7": "Да! У Inkvaluator есть интерактивная карта мира для поиска по стране, провинции и городу.",
  "hiw.backend_title": "За кулисами: мощь бэкенда",
  "hiw.backend_intro": "Для технарей: Inkvaluator полон продвинутых функций.",
  "hiw.card_pricing_title": "Динамическое ценообразование",
  "hiw.card_pricing_text": "Настраивайте цены через админ-модуль без кода.",
  "hiw.card_ai_title": "AI Vision интеграция",
  "hiw.card_ai_text": "AI-сканер анализирует паттерны, цвета и сложность для расчёта рабочих часов.",
  "hiw.card_stripe_title": "Портал клиента Stripe",
  "hiw.card_stripe_text": "Полностью автоматизированная система подписки.",
  "hiw.card_analytics_title": "Панель аналитики",
  "hiw.card_analytics_text": "Глубокие инсайты о конверсиях и потенциальном доходе.",
  "hiw.card_blog_title": "Блог и SEO CMS",
  "hiw.card_blog_text": "Встроенная система публикации статей для улучшения видимости в Google.",
  "hiw.card_global_title": "Глобальная архитектура",
  "hiw.card_global_text": "Полная мультиязычная поддержка с сохранением языковых предпочтений.",
  "hiw.card_mobile_title": "Мобильная версия (PWA)",
  "hiw.card_mobile_text":
    "Inkvaluator — это Progressive Web App (PWA). Установите инструмент прямо на домашний экран через браузер. Молниеносно, безопасно и без использования памяти.",
  "hiw.cta_title": "Чего вы ждёте?",
  "hiw.cta_accent": "Хватит считать, начните творить.",
  "hiw.cta_button": "Попробуйте Inkvaluator бесплатно",
  "tos.seo_title": "Условия использования | Inkvaluator",
  "tos.title": "Условия использования",
  "tos.updated": "Последнее обновление:",
  "tos.s1_title": "1. Определения",
  "tos.s1_platform": "Платформа: Inkvaluator, SaaS-платформа на inkvaluator.com.",
  "tos.s1_user": "Пользователь / Мастер: Любой тату-мастер или студия, создавшая аккаунт.",
  "tos.s1_visitor": "Посетитель: Любой, кто использует публичный калькулятор.",
  "tos.s1_service": "Сервис: Функции Inkvaluator.",
  "tos.s2_title": "2. Использование калькулятора",
  "tos.s2_p1": "Оценки цен являются ориентировочными.",
  "tos.s2_p2": "Inkvaluator не несёт ответственности за решения на основе оценок.",
  "tos.s3_title": "3. Подписки и платежи",
  "tos.s3_intro": "Inkvaluator предлагает три уровня: Free, Pro и Studio.",
  "tos.s3_billing": "Платные подписки выставляются ежемесячно через Stripe.",
  "tos.s3_cancel": "Вы можете отменить подписку в любое время.",
  "tos.s3_refund": "Возврат за начатые периоды невозможен.",
  "tos.s3_price_change": "Inkvaluator оставляет за собой право изменять цены с уведомлением за 30 дней.",
  "tos.s4_title": "4. Право собственности на данные",
  "tos.s4_p1": "Все данные мастера остаются его собственностью.",
  "tos.s4_p2": "Анонимные агрегированные данные могут использоваться для статистического анализа.",
  "tos.s5_title": "5. Ответственность",
  "tos.s5_p1": "Inkvaluator предоставляет сервис «как есть».",
  "tos.s5_indirect": "Inkvaluator не несёт ответственности за косвенные убытки.",
  "tos.s5_cap": "Общая ответственность ограничена суммой, уплаченной за последние 3 месяца.",
  "tos.s5_content": "Inkvaluator не отвечает за контент мастеров.",
  "tos.s6_title": "6. Аккаунт и прекращение",
  "tos.s6_credentials": "Пользователи отвечают за конфиденциальность учётных данных.",
  "tos.s6_suspend": "Inkvaluator оставляет за собой право приостанавливать аккаунты при злоупотреблениях.",
  "tos.s6_delete": "При удалении аккаунта персональные данные удаляются в соответствии с",
  "tos.s6_privacy_link": "Политикой конфиденциальности",
  "tos.s7_title": "7. Интеллектуальная собственность",
  "tos.s7_p1": "Все права интеллектуальной собственности принадлежат Inkvaluator.",
  "tos.s8_title": "8. Применимое право",
  "tos.s8_p1": "Настоящие условия регулируются законодательством Нидерландов.",
  "tos.contact_title": "Вопросы по условиям?",
  "tos.contact_text": "Свяжитесь с нами:",
  "privacy.seo_title": "Политика конфиденциальности | Inkvaluator",
  "privacy.title": "Политика конфиденциальности",
  "privacy.updated": "Последнее обновление:",
  "privacy.s1_title": "1. Какие данные мы собираем?",
  "privacy.s1_intro": "Inkvaluator собирает данные, необходимые для работы платформы:",
  "privacy.s1_artists_label": "Для мастеров",
  "privacy.s1_artists": "Имя, e-mail, название студии, адрес и портфолио.",
  "privacy.s1_visitors_label": "Для посетителей",
  "privacy.s1_visitors": "Данные, введённые в калькулятор для генерации оценок.",
  "privacy.s1_leads_label": "Лиды",
  "privacy.s1_leads": "При обращении к мастеру ваше сообщение и фото обрабатываются безопасно.",
  "privacy.s2_title": "2. Использование данных",
  "privacy.s2_intro": "Ваша конфиденциальность на первом месте. Мы никогда не продаём данные третьим лицам.",
  "privacy.s2_matching": "Подбор клиентов к подходящим мастерам.",
  "privacy.s2_algorithms": "Улучшение алгоритмов ценообразования.",
  "privacy.s2_pwa": "Техническая работа PWA.",
  "privacy.s3_title": "3. Международное законодательство (GDPR)",
  "privacy.s3_intro": "Согласно европейскому законодательству у вас есть следующие права:",
  "privacy.s3_access_label": "Право на доступ",
  "privacy.s3_access": "Вы можете запросить, какие данные мы храним.",
  "privacy.s3_erasure_label": "Право на удаление",
  "privacy.s3_erasure": "Вы можете запросить полное удаление аккаунта и данных.",
  "privacy.s3_portability_label": "Переносимость данных",
  "privacy.s3_portability": "Вы можете запросить данные в читаемом формате.",
  "privacy.s4_title": "4. Cookies и PWA",
  "privacy.s4_text": "Inkvaluator использует функциональное кэширование (PWA). Мы не используем трекинговые куки.",
  "privacy.contact_title": "Вопросы о конфиденциальности?",
  "privacy.contact_text": "Свяжитесь с нашим ответственным за защиту данных:",
  "support.seo_title": "Поддержка | Inkvaluator",
  "support.title": "Поддержка",
  "support.faq_title": "Часто задаваемые вопросы",
  "support.faq_q1": "Как настроить тарифы?",
  "support.faq_a1": "Перейдите в панель и нажмите «Цены» для настройки.",
  "support.faq_q2": "Как работает интеграция Stripe?",
  "support.faq_a2": "При обновлении до Pro или Studio вы перенаправляетесь на безопасный платёж Stripe.",
  "support.faq_q3": "Что такое модуль WhatsApp?",
  "support.faq_a3": "С модулем WhatsApp (от Pro) посетители могут связаться через WhatsApp после расчёта.",
  "support.faq_q4": "Как поделиться калькулятором?",
  "support.faq_a4": "В панели в разделе «Поделиться» есть ссылка, QR-код и код встраивания.",
  "support.faq_q5": "Можно ли отменить подписку?",
  "support.faq_a5": "Да, в любое время. Доступ сохраняется до конца периода.",
  "support.faq_q6": "Как работает AI-сканер?",
  "support.faq_a6": "AI-сканер анализирует фото и автоматически оценивает размер, сложность и стиль.",
  "support.faq_q7": "Нашёл баг, что делать?",
  "support.faq_a7": "Отправьте письмо на support@inkvaluator.com с описанием и скриншотами.",
  "support.contact_title": "Контакты",
  "support.contact_text": "Вопрос не в списке? Свяжитесь с командой:",
  "support.form_name": "Имя",
  "support.form_name_placeholder": "Ваше имя",
  "support.form_email": "E-mail",
  "support.form_message": "Сообщение",
  "support.form_message_placeholder": "Опишите вопрос или проблему...",
  "support.form_submit": "Отправить сообщение",
  "support.form_subject": "Запрос поддержки от {name}",
  "support.toast_title": "Почтовый клиент открыт",
  "support.toast_desc": "Ваше почтовое приложение открывается с сообщением.",
  "vault.status_concept": "Черновик",
  "vault.status_sent": "Запрос отправлен",
  "reviews.rating": "рейтинг",
  "reviews.verified_reviews": "подтверждённых отзывов",
  "reviews.read_all": "Читать все отзывы на Google",
  "discover.swipe": "свайп",
  "profile.manual_place_id": "Студия не найдена? Введите Place ID вручную.",
  "vault.inspiration_title": "Вдохновение",
  "vault.try_style": "Попробовать →",
  "vault.compare": "Сравнить",
  "vault.compare_title": "Сравнение",
  "trends.title": "Тату",
  "trends.title_accent": "Тренды",
  "trends.top_styles": "Популярные стили",
  "trends.top_body_parts": "Популярные части тела",
  "match.title": "Подходящие мастера рядом",
  "match.score": "Совпадение",
  "match.view": "Смотреть профиль",
  "urgency.recent_requests": "запросов на этой неделе",
  "urgency.wait_time": "Ожидание",
  "urgency.weeks": "недель",
  "urgency.high_demand": "Высокий спрос",
  "urgency.popular": "Популярно",
  "benchmark.above_market": "выше среднего по рынку",
  "benchmark.below_market": "ниже среднего по рынку",
  "benchmark.at_market": "На уровне рынка",
  "benchmark.market_avg": "Среднее по рынку",
  "portfolio.style_examples": "Примеры стиля",
  "story.title": "Генератор Instagram Stories",
  "story.desc": "Создайте премиум Black & Gold story с QR-кодом.",
  "story.generate": "Создать Story",
  "story.regenerate": "Пересоздать",
  "story.download": "Скачать",
  "story.share": "Поделиться",
  "story.downloaded": "Story скачана!",
  "story.cta_line1": "Рассчитай Цену",
  "story.cta_line2": "Отсканируй QR-код ниже",
  "story.scan_prompt": "↑ Сканируйте для расчёта",
  "trends.filter_country": "Фильтр по стране",
  "trends.all_countries": "Все страны",
  "trends.analyzed": "расчётов проанализировано",
  "trends.realtime": "Обновляется в реальном времени",
  "match.style_experts": "Эксперты по {style} рядом с {city}",
  "vault.review_booster_title": "Тату готова? Помоги мастеру!",
  "vault.review_booster_desc": "Оставьте отзыв на Google и помогите другим клиентам найти этого мастера.",
  "vault.review_booster_btn": "Написать отзыв на Google",
  "checklist.title": "Статус верификации",
  "checklist.profile_complete": "Профиль заполнен",
  "checklist.google_linked": "Google Place ID подключён",
  "checklist.badge_detected": "Значок верификации на сайте",
  "checklist.push_enabled": "Push-уведомления включены",
  "checklist.not_yet": "Ещё нет",
  "checklist.active": "Активно",
  "lead.status_new": "Новый",
  "lead.status_contacted": "На связи",
  "lead.status_booked": "Забронировано",
  "lead.change_status": "Статус",
  "lead.add_price": "Указать итоговую цену",
  "push.test_body": "Push-уведомления активны! Вы будете получать оповещения о новых лидах.",
  "push.send_test": "Отправить тестовое уведомление",
  "push.active_desc": "Вы получаете уведомления о качественных лидах.",
  "push.blocked": "Уведомления заблокированы в настройках браузера.",
  "push.enable_desc": "Получайте уведомления, когда поступают качественные лиды.",
  "push.label": "Push-уведомления",
  "push.welcome_body": "Добро пожаловать! Вы будете получать оповещения о качественных лидах.",
  "push.denied_info": "Будьте в курсе новых лидов — Включите уведомления в настройках браузера.",
  "vault.live_status": "Статус в реальном времени",
  "vault.status_new_desc": "Ваш запрос получен",
  "vault.status_contacted_desc": "Мастер увидел ваше сообщение",
  "vault.status_booked_desc": "Ваша запись подтверждена!",
  "vault.link_copied": "🔗 Ссылка скопирована!",
  "analytics.actual_revenue": "Фактический Доход",
  "style_city.popular_styles": "Популярные стили в {city}",
  "nav.home": "Главная",
  "lead.estimate_header": "Оценка Татуировки",
  "discover.verified_badge": "ПРОВЕРЕНО",
  "wa.title": "Отправить мастеру через WhatsApp",
  "wa.subtitle": "Отправьте оценку напрямую",
  "wa.name": "Ваше имя (необязательно)",
  "wa.name_placeholder": "Ваше имя",
  "wa.message": "Сообщение (необязательно)",
  "wa.message_placeholder": "Опишите вашу идею, вдохновение или детали...",
  "wa.photo": "Фото-референс (необязательно)",
  "wa.upload_photo": "Загрузить фото",
  "wa.file_too_large": "Файл слишком большой",
  "wa.summary": "Итого:",
  "wa.send": "Отправить через WhatsApp",
  "wa.sent_title": "Сообщение отправлено!",
  "wa.sent_desc": "Ваш запрос отправлен {name} через WhatsApp.",
  "wa.upload_failed": "Ошибка загрузки",
  "wa.save_failed": "Ошибка сохранения",
  "wa.success": "Отправлено!",
  "wa.success_desc": "Запрос создан и WhatsApp открыт.",
  "wa.error": "Что-то пошло не так",
  "wa.error_desc": "Неизвестная ошибка. Проверьте подключение и попробуйте снова.",
  "wa.msg_greeting": "Привет {name}, я рассчитал оценку стоимости:",
  "wa.msg_name_label": "Имя",
  "wa.msg_style_label": "🎨 Стиль",
  "wa.msg_bodypart_label": "📍 Часть тела",
  "wa.msg_size_label": "📏 Размер",
  "wa.msg_complexity_label": "⚙️ Сложность",
  "wa.msg_time_label": "⏱ Ориентировочное время",
  "wa.msg_total_label": "💰 Итого",
  "wa.msg_deposit_label": "💳 Задаток",
  "wa.msg_details_label": "🔗 Подробности",
  "wa.template_high_greeting": "Привет {name}, хорошие новости! Я рассчитал подробную оценку для моей тату:",
  "wa.template_high_vault": "🔗 Я сохранил это в моём Тату-хранилище: {url}",
  "wa.template_low_greeting": "Привет {name}, интересуюсь татуировкой. Расскажите о наличии и ценах?",
  "wa.template_low_note": "На основе калькулятора, первое представление:",
  "tooltip.labor": "На основе времени для этого размера, умноженного на часовую ставку мастера.",
  "tooltip.complexity": "Более сложные дизайны требуют больше времени и точности.",
  "tooltip.placement": "Некоторые части тела более чувствительны или труднодоступны.",
  "tooltip.style": "Разные стили требуют специфических техник, влияющих на цену.",
  "tooltip.design": "Затраты на разработку и подготовку вашей уникальной татуировки.",
  "tooltip.deposit": "Задаток бронирует встречу и вычитается из общей суммы.",
  "pricing_edu.button": "Как работает этот расчёт?",
  "pricing_edu.title": "Как рассчитывается цена",
  "pricing_edu.intro": "Цена татуировки определяется несколькими факторами, отражающими мастерство и время.",
  "pricing_edu.disclaimer": "Это всегда оценка. Окончательная цена определяется после личной консультации.",
  "ai.title": "ИИ-сканер",
  "ai.desc": "Загрузите фото и позвольте ИИ определить параметры",
  "ai.analyzing": "ИИ анализирует...",
  "ai.scan_complete": "✨ ИИ-сканирование завершено",
  "ai.scan_failed": "Сканирование не удалось",
  "ai.try_again": "Попробуйте снова",
  "referral.title": "Рекомендовать коллегу",
  "referral.generate": "Создать реферальную ссылку",
  "pwa.install_title": "Установить как приложение",
  "pwa.install_button": "Установить сейчас",
  // Batch 3+4: Dashboard management, onboarding, settings, landing FAQs, vault, analytics, studio, help
  "tab.price_engine": "Ценовой движок",
  "tab.price_engine_desc": "Управляйте всеми ценовыми множителями: размеры, сложность, части тела и стили.",
  "basic.desc": "Установите стандартные тарифы и расходы",
  "basic.min_price_desc": "Минимальная цена за сеанс",
  "basic.hourly_rate_desc": "Ваша стандартная почасовая ставка",
  "basic.daily_rate_placeholder": "Оставьте пустым, если не применимо",
  "basic.percentage": "%",
  "basic.fixed_amount": "Фикс.",
  "basic.consultation_email": "Email для консультаций",
  "basic.consultation_email_desc": "Адрес для запросов на консультацию",
  "basic.booking_url": "URL бронирования",
  "basic.booking_url_desc": "Ссылка на внешнюю систему бронирования",
  "sizes.title": "Настройки размеров",
  "sizes.desc": "Установите расчётное время по категории размера",
  "sizes.min_hours": "Мин. часов",
  "sizes.max_hours": "Макс. часов",
  "sizes.hours": "часов",
  "complexity.title": "Множители сложности",
  "complexity.desc": "Настройте ценовой коэффициент по сложности",
  "bodyparts.title": "Множители частей тела",
  "bodyparts.desc": "Некоторые части тела сложнее для татуировки",
  "styles.title": "Управление стилями",
  "styles.desc": "Включайте/отключайте стили и настраивайте ценовой коэффициент.",
  "theme.title": "Цветовая тема",
  "theme.desc": "Настройте цвета вашего калькулятора",
  "theme.preview": "Предпросмотр",
  "theme.preview_desc": "Так выглядит ваш калькулятор",
  "theme.hue": "Оттенок",
  "theme.saturation": "Насыщенность",
  "theme.lightness": "Яркость",
  "theme.presets": "Быстрый выбор",
  "branding.title": "Брендинг",
  "branding.desc": "Загрузите логотип, установите язык и валюту",
  "branding.logo": "Логотип",
  "branding.logo_desc": "Логотип студии (макс. 2 МБ, PNG или JPG)",
  "branding.upload": "Загрузить логотип",
  "branding.remove": "Удалить",
  "branding.language": "Язык калькулятора",
  "branding.currency": "Валюта",
  "branding.qr_code": "QR-код",
  "branding.qr_desc": "Клиенты сканируют этот код для доступа к калькулятору.",
  "branding.download_qr": "Скачать QR",
  "onboard.step_minimum": "Минимальный тариф",
  "onboard.step_hourly": "Почасовой тариф",
  "onboard.step_sizes": "Подтвердить размеры",
  "onboard.step_styles": "Установить стили",
  "onboard.step_logo": "Загрузить логотип",
  "onboard.step_finish": "Калькулятор готов!",
  "dash.onboard_min_desc": "Установите минимальный тариф.",
  "dash.onboard_hourly_desc": "Установите почасовую ставку. Она является основой всех расчётов.",
  "dash.onboard_sizes_desc": "Стандартные категории размеров настроены. Измените позже.",
  "dash.onboard_styles_desc": "Все популярные стили включены. Включите/отключите в панели.",
  "dash.onboard_logo_desc": "Загрузите логотип сейчас или позже через вкладку Брендинг.",
  "dash.onboard_logo_later": "Загрузить позже через Панель → Брендинг",
  "dash.onboard_finish_title": "Ваш калькулятор готов!",
  "dash.onboard_finish_desc": "Поделитесь уникальной ссылкой. Настройки можно изменить в любое время.",
  "dash.your_link": "Ссылка на калькулятор:",
  "dash.online_card": "Ваша онлайн-визитка.",
  "dash.view_my_calculator": "Посмотреть калькулятор",
  "dash.upload_thumbnail": "Своё изображение",
  "dash.reset_to_default": "Сбросить",
  "dash.reset_all_thumbnails": "Сбросить все",
  "dash.thumbnail_uploaded": "Изображение загружено!",
  "dash.thumbnail_reset": "Изображение сброшено",
  "dash.thumbnails_reset_all": "Все изображения сброшены",
  "dash.invite_created": "Приглашение создано!",
  "dash.invite_error": "Ошибка приглашения",
  "dash.member_removed": "Участник удалён",
  "dash.link_copied_generic": "Ссылка скопирована!",
  "dash.profile_saved": "Профиль сохранён!",
  "dash.profile_save_error": "Ошибка сохранения",
  "dash.calculator_ready": "🎉 Калькулятор готов!",
  "dash.share_with_clients": "Поделитесь ссылкой.",
  "dash.step_of": "Шаг {current} из {total}",
  "dash.start_inkprice": "Начать с InkPrice",
  "laf.title": "Внешний вид",
  "laf.desc": "Настройте внешний вид публичного калькулятора.",
  "laf.color_mode": "Цветовой режим",
  "laf.dark_mode": "Тёмный режим",
  "laf.light_mode": "Светлый режим",
  "laf.background": "Фон",
  "laf.solid_color": "Сплошной цвет",
  "laf.image_url": "URL изображения",
  "laf.preview": "Предпросмотр",
  "laf.saved": "Внешний вид сохранён",
  "laf.presets": "Премиум-темы",
  "laf.font_selector": "Шрифт калькулятора",
  "laf.overlay_opacity": "Прозрачность наложения",
  "laf.bg_fixed": "Фиксированный (параллакс)",
  "laf.bg_scroll": "Прокрутка",
  "consult_settings.title": "Настройки консультации",
  "consult_settings.desc": "Настройте способы связи.",
  "consult_settings.booking_url_label": "URL бронирования",
  "consult_settings.email_label": "Email для консультаций",
  "consult_settings.channels_title": "Каналы лидов",
  "consult_settings.channels_desc": "Видимые способы связи.",
  "help.min_price": "Абсолютный минимальный прайс за сеанс.",
  "help.hourly_rate": "Ваша почасовая ставка — основа всех расчётов.",
  "help.multipliers": "Множители корректируют цену по стилю, сложности и расположению.",
  "help.sizes": "Установите временной диапазон для каждого размера.",
  "help.complexity": "Настройте множители для сложности.",
  "help.body_parts": "Некоторые зоны требуют более высоких множителей.",
  "help.styles": "Включите/отключите стили и установите множители.",
  "help.logo": "Ваш логотип отображается на калькуляторе и в каталоге. Макс. 2 МБ.",
  "help.currency": "Валюта, отображаемая на калькуляторе.",
  "help.calculator_language": "Язык публичного калькулятора.",
  "help.dashboard_language": "Язык панели управления — виден только вам.",
  "profile.mfa_title": "Безопасность аккаунта",
  "profile.mfa_desc": "Защитите аккаунт с помощью 2FA.",
  "profile.mfa_enable": "Включить 2FA",
  "profile.mfa_verify": "Подтвердить",
  "profile.mfa_active": "Активна",
  "profile.mfa_enabled_success": "✅ 2FA включена!",
  "profile.google_reviews": "Отзывы Google",
  "profile.google_reviews_desc": "Привяжите Google Business профиль.",
  "profile.sync_now": "Синхронизировать",
  "profile.sync_success": "Отзывы Google синхронизированы!",
  "landing.faq_title": "FAQ",
  "landing.faq_subtitle": "Всё об Inkvaluator",
  "landing.faq_q1": "Как работает калькулятор?",
  "landing.faq_a1": "Настройте тарифы и множители. Клиенты получат мгновенную оценку.",
  "landing.faq_q2": "Inkvaluator бесплатен?",
  "landing.faq_a2": "Да! План Free включает полный калькулятор. Обновитесь до Pro для большего.",
  "landing.faq_q3": "Как работает AI-сканер?",
  "landing.faq_a3": "Загрузите фото, и AI определит размер, сложность и стиль за секунды.",
  "landing.faq_q4": "Могут ли клиенты связаться со мной?",
  "landing.faq_a4": "Да, через WhatsApp с предзаполненным сообщением.",
  "landing.faq_q5": "Мультиязычная поддержка?",
  "landing.faq_a5": "17 языков и 13 валют — идеально для международных мастеров.",
  "landing.faq_q6": "Как привлечь больше клиентов?",
  "landing.faq_a6": "Pro/Studio мастера на карте мира и странице открытий.",
  "city.title": "Тату-мастера в {city}",
  "city.description": "Найдите мастеров в {city}.",
  "city.no_artists": "Мастера в {city} не найдены.",
  "city.discover_all": "Все мастера",
  "seo.landing_title": "Inkvaluator – Калькулятор Цен на Татуировки",
  "seo.landing_desc": "Мгновенно рассчитайте стоимость татуировки.",
  "vault.seo_title": "Моё Тату-хранилище | Inkvaluator",
  "vault.title": "Моё",
  "vault.title_accent": "Тату-хранилище",
  "vault.desc": "Ваши сохранённые оценки.",
  "vault.empty": "Нет оценок. Используйте калькулятор!",
  "vault.save_btn": "Сохранить",
  "vault.saved_title": "Сохранено!",
  "vault.saved_desc": "Оценка сохранена.",
  "success.active": "Активно — всё разблокировано",
  "success.setup_whatsapp": "Настроить WhatsApp",
  "success.syncing": "Активация плана…",
  "success.view_details": "Подробности",
  "analytics.title": "Аналитика",
  "analytics.desc": "Использование калькулятора",
  "analytics.total_calculations": "Всего расчётов",
  "analytics.conversion_rate": "Конверсия",
  "analytics.this_month": "В этом месяце",
  "analytics.no_data": "Данных пока нет.",
  "studio.title": "Управление студией",
  "studio.team": "Команда",
  "studio.invite": "Пригласить мастера",
  "studio.invite_desc": "Создайте ссылку-приглашение.",
  "studio.generate_link": "Создать ссылку",
  "studio.no_members": "Нет участников. Пригласите мастеров.",
  "color.gold": "Золото",
  "color.red": "Красный",
  "color.blue": "Синий",
  "color.green": "Зелёный",
  "color.purple": "Фиолетовый",
  "color.pink": "Розовый",
  "color.turquoise": "Бирюзовый",
  "color.orange": "Оранжевый",
  "tip.title": "Порекомендовать мастера",
  "tip.desc": "Знаете мастера, который должен быть на Inkvaluator?",
  "share.button": "Поделиться",
  "sw.update_available": "Доступна новая версия.",
  "embed.iframe_desc": "Вставьте это в ваш HTML.",
  "embed.floating_desc": "Вставьте перед </body>.",
  "embed.verified_badge": "Верификационный значок",
  "embed.badge_desc": "Разместите значок на сайте.",
  "compare.title": "Сравнение цен",
  "compare.disclaimer": "Сравнение на основе стандартной татуировки.",
  "landing.calculate_price": "Рассчитать цену",
  "landing.total_value_prefix": "Уже",
  "landing.total_value_suffix": "тату-искусства рассчитано",
  "step.next": "Далее",
  "step.view_result": "Посмотреть цену",
  "step.details": "Детали",
  "step.details_desc": "Насколько детализирован ваш дизайн?",
  "layout.stepper": "Пошагово",
  "layout.form": "Всё сразу",
  "calc.view_price_cta": "Посмотреть обзор цен",
  "calc.rough_estimate": "Приблизительная оценка",
  "calc.calculated_price": "Рассчитанная цена",
  "calc.save_for_later": "Сохранить на потом",
  "calc.share": "Поделиться",
  "calc.more_options": "Ещё",
  "calc.compare_configs": "Сравнить конфигурации",
  "calc.compare_desc": "Сравнить две ценовые конфигурации рядом",
  "calc.no_photo": "Референсное фото не добавлено",
  "calc.link_copied": "Ссылка скопирована!",
  "whatsapp.add_photo": "Добавьте фото",
  "whatsapp.photos_label": "Референсные фото — макс. 3",
  "laf.reset_success": "Тема сброшена к стандартным значениям",
  "admin.stripe_managed":
    "Подписки управляются автоматически через Stripe — изменения видны после следующей синхронизации webhook",
  "calc.view_tattoo_plan": "Посмотреть ваш Tattoo Plan ✓",
  "wa.plan_message": "Вот мой Tattoo Plan:",
  "lead.tattoo_plan": "Tattoo Plan",
  "lead.made_with": "Создано с Inkvaluator",
  "lead.contact_artist": "Связаться с {artist}",
  "lead.share": "Поделиться",
  "lead.share_title": "Мой Tattoo Plan",
  "lead.link_copied": "Ссылка скопирована!",
  "lead.deposit_label": "Задаток",
  "lead.pay_deposit": "Оплатить задаток {amount}",
  "lead.deposit_request_title": "Запросить соглашение о задатке",
  "lead.deposit_request_desc": "Спросите мастера, возможен ли задаток ~{amount}.",
  "lead.deposit_request_wa": "Отправить запрос через WhatsApp",
  "lead.for_you": "Вас",
  "lead.for_label": "Для",
  "lead.back_to_calculator": "Назад к калькулятору",
  "lead.deposit_wa_msg":
    "Здравствуйте {artist}, я просмотрел свой Tattoo Plan на Inkvaluator. Возможен ли задаток ~{amount}?\nПлан: {url}",
  "blog.no_translation": "Перевод недоступен",
  "flash.tab": "Флэш",
  "flash.upload": "Загрузить флэш-дизайн",
  "flash.title": "Название",
  "flash.price": "Цена",
  "flash.size": "Размер (см)",
  "flash.available": "Доступно",
  "flash.featured": "Рекомендуемый",
  "flash.limit_used": "из {max} флэш-дизайнов использовано",
  "flash.delete_confirm": "Удалить флэш-дизайн?",
  "flash.reserve_whatsapp": "Забронировать через WhatsApp",
  "flash.whatsapp_msg": "Меня интересует ваш флэш-тату:",
  "flash.matching_designs": "Подходящие флэш-дизайны",
  "flash.only_with_flash": "Только артисты с флэш",
  "groei.score": "Оценка профиля",
  "groei.views": "просмотры профиля",
  "groei.sessions": "сессии калькулятора",
  "groei.leads": "лиды",
  "groei.this_week": "на этой неделе",
  "groei.improve": "Улучшите свой профиль",
  "groei.add_photos": "Добавьте больше фото в портфолио",
  "groei.add_whatsapp": "Настройте номер WhatsApp",
  "groei.add_bio": "Напишите более длинное описание",
  "groei.add_avatar": "Загрузите фото профиля",
  "groei.add_rate": "Установите почасовую ставку",
  "groei.add_city": "Добавьте ваш город",
  "groei.get_leads": "Поделитесь калькулятором для получения лидов",
  "groei.collapse": "Свернуть",
  "groei.expand": "Развернуть",
  "groei.tip_portfolio": "Художники с 6+ фото получают в 3 раза больше сессий",
  "groei.tip_whatsapp": "Клиенты с кнопкой WhatsApp конвертируют в 5 раз лучше",
  "groei.tip_leads": "Поделитесь ссылкой на калькулятор чтобы получить первый лид",
  "groei.tip_avatar": "Фото профиля повышает вашу надёжность",
  "groei.tip_action_photos": "Добавить фото",
  "groei.tip_action_whatsapp": "Настроить WhatsApp",
  "groei.tip_action_share": "Поделиться калькулятором",
  "groei.tip_action_avatar": "Загрузить фото",
  "milestone.eerste_lead": "Первый лид получен!",
  "milestone.tien_leads": "10 лидов достигнуто!",
  "milestone.profiel_compleet": "Профиль 100% заполнен!",
  "milestone.eerste_flash": "Первый флеш-дизайн загружен!",
  "wa.name_required": "Имя обязательно",
  "wa.plan_message_full": "Смотрите мой полный Tattoo Plan: {url}",
};

// ─── TURKISH (complete) ──────────────────────────────────────────
const tr: Record<string, string> = {
  ...en,
  "app.tagline": "Dövme Fiyatını Hesapla",
  back: "Geri",
  save: "Kaydet",
  copy: "Kopyala",
  copied: "Kopyalandı!",
  loading: "Yükleniyor...",
  error: "Hata",
  optional: "isteğe bağlı",
  "landing.hero_title": "Hesapla",
  "landing.hero_title_accent": "Dövme Fiyatını",
  "landing.hero_desc": "Bir sonraki dövmenizin maliyetini saniyeler içinde öğrenin.",
  "landing.try_it": "Deneyin!",
  "landing.estimated_price": "Tahmini fiyat",
  "landing.your_rates": "Fiyatlarınız",
  "landing.rates_desc": "Kaydırıcıları hareket ettirin ve fiyat değişimini görün",
  "landing.hourly_rate": "Saatlik ücret",
  "landing.start_cost": "Sabit başlangıç maliyeti",
  "landing.size_label": "Boyut",
  "landing.complexity_label": "Karmaşıklık",
  "landing.body_placement": "Vücut yerleşimi",
  "landing.find_artist": "Mükemmel",
  "landing.find_artist_accent": "sanatçınızı bulun",
  "landing.nearby_soon": "Yakında burada yakınlardaki sanatçılar görünecek.",
  "landing.discover_all": "Tüm Sanatçılar",
  "landing.artist_question": "Sanatçı veya stüdyo musunuz?",
  "landing.view_options": "Seçenekleri ve fiyatları görün",
  "landing.start_free": "Ücretsiz başlayın",
  "landing.discover": "Keşfet",
  "landing.login": "Giriş Yap",
  "landing.founder_desc": "İlk 50 kayıt için %50 indirim",
  "landing.claim_now": "Şimdi talep et →",
  "landing.how_it_works_cta": "Stüdyonuz için nasıl çalıştığını görün",
  "calc.estimate": "Tahmini fiyat",
  "calc.size": "Boyut",
  "calc.size_desc": "Dövmeniz ne kadar büyük olacak?",
  "calc.complexity": "Karmaşıklık",
  "calc.complexity_desc": "Tasarım ne kadar detaylı?",
  "calc.body_part": "Vücut bölgesi",
  "calc.body_part_desc": "Dövmenizin yapılacağı bölgeye tıklayın",
  "calc.style": "Stil",
  "calc.style_desc": "Hangi dövme stili istiyorsunuz?",
  "calc.budget": "Bütçe",
  "calc.budget_desc": "Maksimum bütçeniz nedir?",
  "calc.budget_placeholder": "Ör. 300",
  "calc.get_estimate": "Gerçekçi bir fiyat tahmini alın",
  "calc.no_artist": "Bu kod veya bağlantıyla sanatçı bulunamadı.",
  "calc.back_home": "Ana sayfaya dön",
  "calc.artist_login": "Sanatçı mısınız? Giriş yapın",
  "calc.add_color": "Renk eklensin mi?",
  "calc.color_surcharge": "+%20 renk çalışma ücreti",
  "breakdown.title": "Fiyat dökümü",
  "breakdown.labor": "İşçilik (zaman)",
  "breakdown.est_hours": "Tahmini saat",
  "breakdown.subtotal": "Ara toplam",
  "breakdown.complexity": "Karmaşıklık",
  "breakdown.placement": "Yerleşim",
  "breakdown.style": "Stil",
  "breakdown.design_cost": "Tasarım maliyeti",
  "breakdown.deposit": "Depozito",
  "breakdown.total_range": "Toplam aralık",
  "breakdown.disclaimer":
    "Bu bir tahmindir, kesin fiyat değildir. Nihai fiyat sanatçıyla yapılacak danışma sonrası belirlenir.",
  "breakdown.total_indication": "Toplam gösterge",
  "breakdown.required_deposit": "Gerekli depozito",
  "breakdown.hours_unit": "saat",
  "faq.title": "Sıkça Sorulan Sorular",
  "dash.welcome": "Inkvaluator'a hoş geldiniz",
  "dash.logout": "Çıkış yap",
  "dash.saved": "Kaydedildi!",
  "dash.save_changes": "Değişiklikleri kaydet",
  "dash.cancel": "İptal",
  "dash.add": "Ekle",
  "dash.delete": "Sil",
  "dash.previous": "Önceki",
  "dash.next": "Sonraki",
  "tab.basic": "Temel",
  "tab.sizes": "Boyutlar",
  "tab.complexity": "Karmaşıklık",
  "tab.body_parts": "Vücut bölgeleri",
  "tab.styles": "Stiller",
  "tab.theme": "Tema",
  "tab.branding": "Marka",
  "tab.analytics": "Analitik",
  "basic.title": "Temel ücret",
  "basic.min_price": "Minimum fiyat",
  "basic.hourly_rate": "Saatlik ücret",
  "basic.daily_rate": "Günlük ücret",
  "basic.design_cost": "Tasarım maliyeti",
  "basic.fixed": "Sabit",
  "basic.hourly": "Saatlik",
  "basic.deposit": "Depozito",
  "size.mini": "Mini (< 5 cm)",
  "size.small": "Küçük (5-10 cm)",
  "size.medium": "Orta (10-20 cm)",
  "size.large": "Büyük (20-35 cm)",
  "size.xl": "Çok Büyük (35+ cm)",
  "complexity.simple": "Basit",
  "complexity.medium": "Orta",
  "complexity.detailed": "Detaylı",
  "body.arm": "Kol (genel)",
  "body.forearm": "Ön kol",
  "body.upper_arm": "Üst kol",
  "body.leg": "Bacak (genel)",
  "body.thigh": "Uyluk",
  "body.calf": "Baldır",
  "body.back": "Sırt",
  "body.chest": "Göğüs",
  "body.ribs": "Kaburgalar",
  "body.stomach": "Karın",
  "body.hand": "El",
  "body.foot": "Ayak",
  "body.neck": "Boyun",
  "body.face": "Yüz",
  "body.shoulder": "Omuz",
  "body.hip": "Kalça",
  "body.finger": "Parmak",
  "body.other": "Diğer",
  "body.selected": "Seçildi",
  "body.front": "Ön",
  "body.back_view": "Arka",
  "body.front_view": "Ön",
  "body.back_view_btn": "Arka",
  "style.black_grey": "Siyah & Gri",
  "style.full_color": "Tam Renkli",
  "style.realism": "Realizm",
  "style.fine_line": "İnce Çizgi / Minimal",
  "style.traditional": "Geleneksel",
  "style.neo_traditional": "Neo Geleneksel",
  "style.watercolor": "Sulu Boya",
  "style.tribal": "Tribal",
  "style.japanese": "Japon",
  "style.geometric": "Geometrik",
  "style.lettering": "Yazı",
  "style.script": "El Yazısı",
  "style.dotwork": "Dotwork",
  "style.mandala": "Mandala",
  "style.blackwork": "Blackwork",
  "style.illustrative": "İllüstratif",
  "style.surrealism": "Sürrealizm",
  "style.ornamental": "Süsleme",
  "style.micro_realism": "Mikro Realizm",
  "style.trash_polka": "Trash Polka",
  "style.other": "Diğer",
  "consult.title": "Başlamaya hazır mısınız?",
  "consult.book": "Danışma randevusu alın",
  "pdf.title": "Fiyat tahmini",
  "pdf.download": "PDF indir",
  "upgrade.choose": "Planınızı",
  "upgrade.plan_word": "seçin",
  "upgrade.per_month": "/ay",
  "upgrade.perfect_start": "Başlamak için mükemmel",
  "upgrade.everything_pro": "Profesyoneller için her şey",
  "upgrade.for_studios": "Dövme stüdyoları ve ekipler için",
  "upgrade.upgrade_pro": "Pro'ya yükselt",
  "upgrade.upgrade_studio": "Studio'ya yükselt",
  "upgrade.manage_sub": "Aboneliği yönet",
  "upgrade.cancel_anytime_long": "Her zaman iptal edebilirsiniz. Gizli ücret yok.",
  "upgrade.discount": "indirim",
  "upgrade.test_account_active": "Test hesabı — özellikler aktif",
  "sub.free": "Ücretsiz",
  "sub.pro": "Pro",
  "sub.studio": "Studio",
  "auth.login": "Giriş Yap",
  "auth.register": "Kayıt Ol",
  "auth.artist_name": "Sanatçı adı",
  "auth.email": "E-posta",
  "auth.password": "Şifre",
  "auth.submit_login": "Giriş Yap",
  "auth.submit_register": "Hesap oluştur",
  "auth.switch_register": "Hesabınız yok mu? Kayıt olun",
  "auth.switch_login": "Zaten hesabınız var mı? Giriş yapın",
  "discover.title": "Keşfet",
  "discover.title_accent": "Sanatçılar",
  "discover.search_placeholder": "Şehir, sanatçı veya stüdyo ara...",
  "discover.no_artists": "Sanatçı bulunamadı",
  "discover.view_calculator": "Hesaplayıcıyı görüntüle",
  "footer.tagline": "Sanatçılar ve müşteriler için en akıllı dövme fiyat hesaplayıcısı.",
  "footer.links": "Bağlantılar",
  "footer.discover_artists": "Sanatçıları Keşfet",
  "footer.artist_login": "Sanatçı Girişi",
  "footer.privacy": "Gizlilik Politikası",
  "footer.language_label": "Dil",
  "footer.all_rights": "Tüm hakları saklıdır.",
  "footer.install_title": "Yükle",
  "footer.install_ok": "Anladım!",
  "footer.terms": "Kullanım Koşulları",
  "footer.support": "Destek",
  "footer.how_it_works": "Nasıl Çalışır",
  "success.welcome": "Kulübe hoş geldiniz,",
  "success.go_dashboard": "Panele git",
  "hiw.seo_title": "Nasıl Çalışır – Inkvaluator",
  "hiw.seo_desc": "Inkvaluator'ın dövme sanatçılarına nasıl yardım ettiğini keşfedin.",
  "hiw.hero_title": "Mürekkebe odaklanın, faturaya değil",
  "hiw.hero_p1":
    "Fiyatlar hakkında DM'lere cevap vermekle mi meşgulsünüz? Dürüst olalım: siz dövme sanatçısınız, müşteri hizmetleri değil.",
  "hiw.hero_p2": 'Manuel olarak cevapladığınız her "Bu ne kadar?" sorusu, sanatınıza ayıramadığınız zamandır.',
  "hiw.hero_p3": "Inkvaluator işletmenizi filtreleyen dijital asistanınızdır.",
  "hiw.hero_p4": 'Bütçesiz "fiyat turisti" artık yok. Sadece ciddi müşteriler ve profesyonel teklifler.',
  "hiw.hero_cta": "Çalışma zamanınızı geri almaya hazır mısınız?",
  "hiw.faq_title": "Sıkça Sorulan Sorular",
  "hiw.faq_cat1": "Temel bilgiler",
  "hiw.faq_q1": "Bu başka bir karmaşık uygulama mı?",
  "hiw.faq_a1": "Hayır. Instagram bionu veya sitenize ekleyebileceğiniz süper hızlı bir hesaplayıcıdır.",
  "hiw.faq_q2": "Nasıl zaman kazandırıyor?",
  "hiw.faq_a2": "On kez sormak yerine, her şeyin doldurulmuş olduğu bir WhatsApp mesajı alırsınız.",
  "hiw.faq_cat2": "Fiyatlar ve ayarlar",
  "hiw.faq_q3": "Kendi fiyatlarımı belirleyebilir miyim?",
  "hiw.faq_a3": "Kesinlikle. Saatlik ücretinizi ve boyut başına ücretleri siz belirlersiniz.",
  "hiw.faq_q4": "Ya dövme ortalamadan çok daha karmaşıksa?",
  "hiw.faq_a4": "Yerleşik AI tarayıcı fotoğraftaki detayları tanır ve fiyat tahminini ayarlar.",
  "hiw.faq_cat3": "Görünüm ve marka",
  "hiw.faq_q5": "Jenerik bir araç gibi görünmüyor mu?",
  "hiw.faq_a5": "Kesinlikle hayır. Studio planıyla tamamen white-label: logonuz, renkleriniz.",
  "hiw.faq_q6": "Müşteriye ne kadar profesyonel görünüyor?",
  "hiw.faq_a6": "Çok profesyonel. Müşteri teklifi şık PDF olarak indirebilir.",
  "hiw.faq_cat4": "Büyüme ve erişim",
  "hiw.faq_q7": "Bölgemden daha fazla müşteri istiyorum, yardımcı olur mu?",
  "hiw.faq_a7": "Evet! Inkvaluator'da ülke, il ve şehir bazında arama yapılabilen interaktif dünya haritası var.",
  "hiw.backend_title": "Perde arkası: backend gücü",
  "hiw.backend_intro": "Teknoloji severler için: Inkvaluator gelişmiş özelliklerle dolu.",
  "hiw.cta_title": "Neyi bekliyorsunuz?",
  "hiw.cta_accent": "Hesaplamayı bırakın, yaratmaya başlayın.",
  "hiw.cta_button": "Inkvaluator'ı ücretsiz deneyin",
  "privacy.seo_title": "Gizlilik Politikası | Inkvaluator",
  "privacy.title": "Gizlilik Politikası",
  "privacy.updated": "Son güncelleme:",
  "privacy.s1_title": "1. Hangi verileri topluyoruz?",
  "privacy.s1_intro": "Inkvaluator platformun çalışması için gerekli verileri toplar:",
  "privacy.s1_artists_label": "Sanatçılar için",
  "privacy.s1_artists": "Ad, e-posta, stüdyo adı, adres ve portföy bilgileri.",
  "privacy.s1_visitors_label": "Ziyaretçiler için",
  "privacy.s1_visitors": "Tahmin oluşturmak için hesaplayıcıya girilen veriler.",
  "privacy.s1_leads_label": "Müşteri adayları",
  "privacy.s1_leads": "Bir sanatçıyla iletişime geçtiğinizde mesajınız güvenli şekilde işlenir.",
  "privacy.s2_title": "2. Veri Kullanımı",
  "privacy.s2_intro": "Gizliliğiniz önceliğimizdir. Kişisel verileri asla üçüncü taraflara satmayız.",
  "privacy.s2_matching": "Müşterileri doğru sanatçılarla eşleştirme.",
  "privacy.s2_algorithms": "Fiyatlandırma algoritmalarını iyileştirme.",
  "privacy.s2_pwa": "PWA teknik işleyişi.",
  "privacy.s3_title": "3. Uluslararası Mevzuat (GDPR)",
  "privacy.s3_intro": "Avrupa mevzuatı uyarınca aşağıdaki haklara sahipsiniz:",
  "privacy.s3_access_label": "Erişim hakkı",
  "privacy.s3_access": "Hangi verilerinizi tuttuğumuzu sorgulayabilirsiniz.",
  "privacy.s3_erasure_label": "Unutulma hakkı",
  "privacy.s3_erasure": "Hesabınızın ve verilerinizin tamamen silinmesini talep edebilirsiniz.",
  "privacy.s3_portability_label": "Veri taşınabilirliği",
  "privacy.s3_portability": "Verilerinizi okunabilir formatta talep edebilirsiniz.",
  "privacy.s4_title": "4. Çerezler ve PWA",
  "privacy.s4_text":
    "Inkvaluator çevrimdışı kullanım için fonksiyonel önbellekleme (PWA) kullanır. İzleme çerezleri kullanmıyoruz.",
  "privacy.contact_title": "Gizliliğiniz hakkında sorularınız mı var?",
  "privacy.contact_text": "Veri koruma sorumlumuzla iletişime geçin:",
  "support.seo_title": "Destek | Inkvaluator",
  "support.title": "Destek",
  "support.faq_title": "Sıkça Sorulan Sorular",
  "support.faq_q1": "Tarifelerimi nasıl ayarlarım?",
  "support.faq_a1": 'Panelinize gidin ve "Fiyatlar"a tıklayın.',
  "support.faq_q2": "Stripe entegrasyonu nasıl çalışır?",
  "support.faq_a2": "Pro veya Studio'ya yükselttiğinizde güvenli Stripe ödemesine yönlendirilirsiniz.",
  "support.faq_q3": "WhatsApp modülü nedir?",
  "support.faq_a3":
    "WhatsApp modülüyle (Pro'dan itibaren) ziyaretçiler fiyat hesapladıktan sonra WhatsApp ile iletişime geçebilir.",
  "support.faq_q4": "Hesaplayıcımı nasıl paylaşırım?",
  "support.faq_a4": 'Panelde "Paylaş" bölümünde link, QR kodu ve gömme kodu bulabilirsiniz.',
  "support.faq_q5": "Aboneliğimi iptal edebilir miyim?",
  "support.faq_a5": "Evet, her zaman iptal edebilir ve fatura dönemi sonuna kadar erişiminizi koruyabilirsiniz.",
  "support.faq_q6": "AI tarayıcı nasıl çalışır?",
  "support.faq_a6": "AI tarayıcı fotoğrafı analiz eder ve boyut, karmaşıklık, stili otomatik tahmin eder.",
  "support.faq_q7": "Bir hata buldum, ne yapmalıyım?",
  "support.faq_a7": "support@inkvaluator.com adresine açıklama ve ekran görüntüleriyle e-posta gönderin.",
  "support.contact_title": "İletişim",
  "support.contact_text": "Sorunuz listede yok mu? Ekibimizle doğrudan iletişime geçin:",
  "support.form_name": "Ad",
  "support.form_name_placeholder": "Adınız",
  "support.form_email": "E-posta",
  "support.form_message": "Mesaj",
  "support.form_message_placeholder": "Sorunuzu veya probleminizi açıklayın...",
  "support.form_submit": "Mesaj gönder",
  "support.form_subject": "{name} destek talebi",
  "support.toast_title": "E-posta istemcisi açıldı",
  "support.toast_desc": "E-posta uygulamanız mesajınızla açılıyor.",
  "vault.status_concept": "Taslak",
  "vault.status_sent": "İstek gönderildi",
  "reviews.rating": "puan",
  "reviews.verified_reviews": "doğrulanmış yorum",
  "reviews.read_all": "Google'da tüm yorumları oku",
  "discover.swipe": "kaydır",
  "profile.manual_place_id": "Stüdyo bulunamadı? Place ID'nizi manuel girin.",
  "vault.inspiration_title": "İlham",
  "vault.try_style": "Dene →",
  "vault.compare": "Karşılaştır",
  "vault.compare_title": "Karşılaştırma",
  "trends.title": "Dövme",
  "trends.title_accent": "Trendler",
  "trends.top_styles": "Popüler stiller",
  "trends.top_body_parts": "Popüler vücut bölgeleri",
  "match.title": "Yakınındaki eşleşen sanatçılar",
  "match.score": "Eşleşme",
  "match.view": "Profili gör",
  "urgency.recent_requests": "bu hafta talep",
  "urgency.wait_time": "Tahmini bekleme",
  "urgency.weeks": "hafta",
  "urgency.high_demand": "Yüksek talep",
  "urgency.popular": "Popüler",
  "benchmark.above_market": "piyasa ortalamasının üstünde",
  "benchmark.below_market": "piyasa ortalamasının altında",
  "benchmark.at_market": "Piyasa ortalamasında",
  "benchmark.market_avg": "Piyasa ortalaması",
  "portfolio.style_examples": "Stil örnekleri",
  "story.title": "Instagram Hikaye Üreteci",
  "story.desc": "QR kodunuzla premium Black & Gold hikaye görseli oluşturun.",
  "story.generate": "Hikaye Oluştur",
  "story.regenerate": "Yeniden Oluştur",
  "story.download": "İndir",
  "story.share": "Paylaş",
  "story.downloaded": "Hikaye indirildi!",
  "story.cta_line1": "Fiyatını Hesapla",
  "story.cta_line2": "Aşağıdaki QR kodu tarayın",
  "story.scan_prompt": "↑ Fiyat tahmini için tarayın",
  "trends.filter_country": "Ülkeye göre filtrele",
  "trends.all_countries": "Tüm ülkeler",
  "trends.analyzed": "hesaplama analiz edildi",
  "trends.realtime": "Gerçek zamanlı güncelleniyor",
  "match.style_experts": "{city} yakınında {style} uzmanları",
  "vault.review_booster_title": "Dövme bitti mi? Sanatçına yardım et!",
  "vault.review_booster_desc": "Google yorumu bırakarak diğer müşterilerin bu sanatçıyı bulmasına yardım edin.",
  "vault.review_booster_btn": "Google Yorumu Yaz",
  "checklist.title": "Doğrulama Durumu",
  "checklist.profile_complete": "Profil tamamlandı",
  "checklist.google_linked": "Google Place ID bağlı",
  "checklist.badge_detected": "Web sitesinde doğrulama rozeti",
  "checklist.push_enabled": "Push bildirimleri aktif",
  "checklist.not_yet": "Henüz değil",
  "checklist.active": "Aktif",
  "lead.status_new": "Yeni",
  "lead.status_contacted": "İletişimde",
  "lead.status_booked": "Rezerve Edildi",
  "lead.change_status": "Durum",
  "lead.add_price": "Son fiyatı ekle",
  "push.test_body": "Push bildirimleri AKTİF! Yeni müşteri adayları için uyarılar alacaksınız.",
  "push.send_test": "Test bildirimi gönder",
  "push.active_desc": "Kaliteli müşteri adayları geldiğinde bildirim alırsınız.",
  "push.blocked": "Bildirimler tarayıcı ayarlarınızda engellenmiş.",
  "push.enable_desc": "Kaliteli müşteri adayları geldiğinde bildirim alın.",
  "push.label": "Push bildirimleri",
  "push.welcome_body": "Hoş geldiniz! Kaliteli müşteri adayları geldiğinde uyarılar alacaksınız.",
  "push.denied_info": "Yeni müşteri adaylarından haberdar olun — Tarayıcı ayarlarınızda bildirimleri etkinleştirin.",
  "vault.live_status": "Canlı Durum",
  "vault.status_new_desc": "Talebiniz alındı",
  "vault.status_contacted_desc": "Sanatçı mesajınızı gördü",
  "vault.status_booked_desc": "Randevunuz onaylandı!",
  "vault.link_copied": "🔗 Bağlantı kopyalandı!",
  "analytics.actual_revenue": "Gerçek Gelir",
  "style_city.popular_styles": "{city} şehrinde popüler stiller",
  "nav.home": "Ana Sayfa",
  "lead.estimate_header": "Dövme Tahmini",
  "discover.verified_badge": "DOĞRULANMIŞ",
  "wa.title": "WhatsApp ile sanatçıya gönder",
  "wa.subtitle": "Tahmininizi doğrudan gönderin",
  "wa.name": "Adınız (isteğe bağlı)",
  "wa.name_placeholder": "Adınız",
  "wa.message": "Mesaj (isteğe bağlı)",
  "wa.message_placeholder": "Fikrinizi, ilhamınızı veya detaylarınızı açıklayın...",
  "wa.photo": "Referans fotoğrafı (isteğe bağlı)",
  "wa.upload_photo": "Fotoğraf yükle",
  "wa.file_too_large": "Dosya çok büyük",
  "wa.summary": "Özet:",
  "wa.send": "WhatsApp ile gönder",
  "wa.sent_title": "Mesaj gönderildi!",
  "wa.sent_desc": "Talebiniz {name} adresine WhatsApp ile gönderildi.",
  "wa.upload_failed": "Yükleme başarısız",
  "wa.save_failed": "Kaydetme başarısız",
  "wa.success": "Gönderildi!",
  "wa.success_desc": "Talebiniz oluşturuldu ve WhatsApp açıldı.",
  "wa.error": "Bir şeyler yanlış gitti",
  "wa.error_desc": "Bilinmeyen hata. Bağlantınızı kontrol edip tekrar deneyin.",
  "wa.msg_greeting": "Merhaba {name}, bir fiyat tahmini hesapladım:",
  "wa.msg_name_label": "Ad",
  "wa.msg_style_label": "🎨 Stil",
  "wa.msg_bodypart_label": "📍 Vücut bölgesi",
  "wa.msg_size_label": "📏 Boyut",
  "wa.msg_complexity_label": "⚙️ Karmaşıklık",
  "wa.msg_time_label": "⏱ Tahmini süre",
  "wa.msg_total_label": "💰 Toplam",
  "wa.msg_deposit_label": "💳 Depozito",
  "wa.msg_details_label": "🔗 Detaylar",
  "wa.template_high_greeting": "Merhaba {name}, iyi haberler! Dövmem için detaylı bir tahmin hesapladım:",
  "wa.template_high_vault": "🔗 Dövme Kasama kaydettim: {url}",
  "wa.template_low_greeting":
    "Merhaba {name}, bir dövme ile ilgileniyorum. Müsaitlik ve fiyatlar hakkında bilgi verir misiniz?",
  "wa.template_low_note": "Hesaplayıcıya göre ilk fikir:",
  "tooltip.labor": "Bu boyut için gereken tahmini süreye sanatçının saatlik ücretinin çarpılmasıyla hesaplanır.",
  "tooltip.complexity": "Daha karmaşık tasarımlar daha fazla zaman ve hassasiyet gerektirir.",
  "tooltip.placement": "Bazı vücut bölgeleri daha hassas veya erişimi zordur.",
  "tooltip.style": "Farklı stiller fiyatı etkileyen özel teknikler gerektirir.",
  "tooltip.design": "Benzersiz dövmenizin tasarım ve hazırlık maliyetleri.",
  "tooltip.deposit": "Depozito randevunuzu garanti eder ve toplam fiyattan düşülür.",
  "pricing_edu.button": "Bu hesaplama nasıl çalışır?",
  "pricing_edu.title": "Fiyat nasıl hesaplanır",
  "pricing_edu.intro": "Bir dövmenin fiyatı, ustalığı ve zamanı yansıtan çeşitli faktörlerle belirlenir.",
  "pricing_edu.disclaimer": "Bu her zaman bir tahmindir. Nihai fiyat sanatçıyla yapılan danışma sonrası belirlenir.",
  "ai.title": "AI Tarayıcı",
  "ai.desc": "Bir fotoğraf yükleyin ve AI ayarları belirlesin",
  "ai.analyzing": "AI analiz ediyor...",
  "ai.scan_complete": "✨ AI tarama tamamlandı",
  "ai.scan_failed": "Tarama başarısız",
  "ai.try_again": "Tekrar deneyin",
  "referral.title": "Meslektaşını öner",
  "referral.generate": "Referans linki oluştur",
  "pwa.install_title": "Uygulama olarak yükle",
  "pwa.install_button": "Şimdi yükle",
  // Batch 3+4
  "tab.price_engine": "Fiyat Motoru",
  "tab.price_engine_desc": "Tüm fiyat çarpanlarını yönetin: boyutlar, karmaşıklık, vücut bölgeleri ve stiller.",
  "basic.desc": "Standart ücretlerinizi ve maliyetlerinizi belirleyin",
  "basic.min_price_desc": "Faturalandırdığınız en düşük fiyat",
  "basic.hourly_rate_desc": "Standart saatlik ücretiniz",
  "basic.daily_rate_placeholder": "Geçerli değilse boş bırakın",
  "basic.percentage": "%",
  "basic.fixed_amount": "Sabit",
  "basic.consultation_email": "Danışma e-postası",
  "basic.consultation_email_desc": "Danışma istekleri için e-posta",
  "basic.booking_url": "Rezervasyon URL",
  "basic.booking_url_desc": "Harici rezervasyon sistemi bağlantısı",
  "sizes.title": "Boyut Ayarları",
  "sizes.desc": "Boyut kategorisine göre tahmini süreyi ayarlayın",
  "sizes.min_hours": "Min. saat",
  "sizes.max_hours": "Maks. saat",
  "sizes.hours": "saat",
  "complexity.title": "Karmaşıklık Çarpanları",
  "complexity.desc": "Karmaşıklığa göre fiyat faktörünü ayarlayın",
  "bodyparts.title": "Vücut Bölgesi Çarpanları",
  "bodyparts.desc": "Bazı bölgeler dövme için daha zordur",
  "styles.title": "Stil Yönetimi",
  "styles.desc": "Stilleri etkinleştirin/devre dışı bırakın ve fiyat faktörünü ayarlayın.",
  "theme.title": "Renk Teması",
  "theme.desc": "Hesaplayıcınızın renklerini özelleştirin",
  "theme.preview": "Önizleme",
  "theme.preview_desc": "Hesaplayıcınız böyle görünüyor",
  "theme.hue": "Ton",
  "theme.saturation": "Doygunluk",
  "theme.lightness": "Parlaklık",
  "theme.presets": "Hızlı seçim",
  "branding.title": "Marka",
  "branding.desc": "Logonuzu yükleyin, dil ve para birimi ayarlayın",
  "branding.logo": "Logo",
  "branding.logo_desc": "Stüdyo logosu (maks. 2 MB, PNG veya JPG)",
  "branding.upload": "Logo yükle",
  "branding.remove": "Kaldır",
  "branding.language": "Hesaplayıcı dili",
  "branding.currency": "Para birimi",
  "branding.qr_code": "QR Kodu",
  "branding.qr_desc": "Müşteriler hesaplayıcınıza erişmek için tarar.",
  "branding.download_qr": "QR İndir",
  "onboard.step_minimum": "Minimum ücret",
  "onboard.step_hourly": "Saatlik ücret",
  "onboard.step_sizes": "Boyutları onayla",
  "onboard.step_styles": "Stilleri ayarla",
  "onboard.step_logo": "Logo yükle",
  "onboard.step_finish": "Hesaplayıcı hazır!",
  "dash.onboard_min_desc": "Minimum ücretinizi belirleyin.",
  "dash.onboard_hourly_desc": "Saatlik ücretinizi belirleyin. Bu tüm hesaplamaların temelidir.",
  "dash.onboard_sizes_desc": "Varsayılan boyut kategorileri ayarlandı. Daha sonra değiştirin.",
  "dash.onboard_styles_desc": "Tüm yaygın stiller etkin. Panelde etkinleştirin/devre dışı bırakın.",
  "dash.onboard_logo_desc": "Şimdi veya daha sonra Marka sekmesinden yükleyin.",
  "dash.onboard_logo_later": "Daha sonra Panel → Marka üzerinden yükle",
  "dash.onboard_finish_title": "Hesaplayıcınız hazır!",
  "dash.onboard_finish_desc": "Benzersiz bağlantınızı paylaşın. Ayarları her zaman değiştirebilirsiniz.",
  "dash.your_link": "Hesaplayıcı linkiniz:",
  "dash.online_card": "Online kartvizitiniz.",
  "dash.view_my_calculator": "Hesaplayıcımı gör",
  "dash.upload_thumbnail": "Özel görsel",
  "dash.reset_to_default": "Sıfırla",
  "dash.reset_all_thumbnails": "Tümünü sıfırla",
  "dash.thumbnail_uploaded": "Görsel yüklendi!",
  "dash.thumbnail_reset": "Görsel sıfırlandı",
  "dash.thumbnails_reset_all": "Tüm görseller sıfırlandı",
  "dash.invite_created": "Davet oluşturuldu!",
  "dash.invite_error": "Davet hatası",
  "dash.member_removed": "Üye kaldırıldı",
  "dash.link_copied_generic": "Link kopyalandı!",
  "dash.profile_saved": "Profil kaydedildi!",
  "dash.profile_save_error": "Kaydetme hatası",
  "dash.calculator_ready": "🎉 Hesaplayıcı hazır!",
  "dash.share_with_clients": "Linkinizi paylaşın.",
  "dash.step_of": "Adım {current} / {total}",
  "dash.start_inkprice": "InkPrice ile başla",
  "laf.title": "Görünüm",
  "laf.desc": "Genel hesaplayıcınızın görünümünü özelleştirin.",
  "laf.color_mode": "Renk modu",
  "laf.dark_mode": "Koyu mod",
  "laf.light_mode": "Açık mod",
  "laf.background": "Arka plan",
  "laf.solid_color": "Düz renk",
  "laf.image_url": "Görsel URL",
  "laf.preview": "Önizleme",
  "laf.saved": "Görünüm kaydedildi",
  "laf.presets": "Premium temalar",
  "laf.font_selector": "Hesaplayıcı yazı tipi",
  "laf.overlay_opacity": "Kaplama opaklığı",
  "laf.bg_fixed": "Sabit (Parallax)",
  "laf.bg_scroll": "Kaydırma",
  "consult_settings.title": "Danışma Ayarları",
  "consult_settings.desc": "İletişim yöntemlerini yapılandırın.",
  "consult_settings.booking_url_label": "Rezervasyon URL",
  "consult_settings.email_label": "Danışma e-postası",
  "consult_settings.channels_title": "Lead kanalları",
  "consult_settings.channels_desc": "Görünür iletişim yöntemleri.",
  "help.min_price": "Her seans için mutlak minimum fiyat.",
  "help.hourly_rate": "Saatlik ücretiniz tüm hesaplamaların temelidir.",
  "help.multipliers": "Çarpanlar stile, karmaşıklığa ve yerleşime göre fiyatı ayarlar.",
  "help.sizes": "Her boyut için zaman aralığını belirleyin.",
  "help.complexity": "Karmaşıklık çarpanlarını ayarlayın.",
  "help.body_parts": "Bazı bölgeler daha yüksek çarpanlar gerektirir.",
  "help.styles": "Stilleri etkinleştirin/devre dışı bırakın ve çarpanları ayarlayın.",
  "help.logo": "Logonuz hesaplayıcıda ve rehberde görünür. Maks. 2 MB.",
  "help.currency": "Hesaplayıcıda gösterilen para birimi.",
  "help.calculator_language": "Genel hesaplayıcınızın dili.",
  "help.dashboard_language": "Bu panelin dili — sadece size görünür.",
  "profile.mfa_title": "Hesap Güvenliği",
  "profile.mfa_desc": "Hesabınızı 2FA ile koruyun.",
  "profile.mfa_enable": "2FA Etkinleştir",
  "profile.mfa_verify": "Doğrula",
  "profile.mfa_active": "Aktif",
  "profile.mfa_enabled_success": "✅ 2FA etkinleştirildi!",
  "profile.google_reviews": "Google Yorumları",
  "profile.google_reviews_desc": "Google Business profilinizi bağlayın.",
  "profile.sync_now": "Senkronize et",
  "profile.sync_success": "Google yorumları senkronize edildi!",
  "landing.faq_title": "SSS",
  "landing.faq_subtitle": "Inkvaluator hakkında her şey",
  "landing.faq_q1": "Hesaplayıcı nasıl çalışır?",
  "landing.faq_a1": "Ücretlerinizi ve çarpanlarınızı ayarlayın. Müşteriler anında tahmin alır.",
  "landing.faq_q2": "Inkvaluator ücretsiz mi?",
  "landing.faq_a2": "Evet! Free plan tam hesaplayıcı içerir. Daha fazlası için Pro'ya geçin.",
  "landing.faq_q3": "AI tarayıcı nasıl çalışır?",
  "landing.faq_a3": "Fotoğraf yükleyin, AI saniyeler içinde boyut, karmaşıklık ve stili analiz eder.",
  "landing.faq_q4": "Müşteriler bana ulaşabilir mi?",
  "landing.faq_a4": "Evet, ön-doldurulmuş mesajla WhatsApp üzerinden.",
  "landing.faq_q5": "Çoklu dil desteği?",
  "landing.faq_a5": "17 dil ve 13 para birimi — uluslararası sanatçılar için mükemmel.",
  "landing.faq_q6": "Daha fazla müşteri nasıl alırım?",
  "landing.faq_a6": "Pro/Studio sanatçılar dünya haritasında ve keşif sayfasında görünür.",
  "city.title": "{city} dövme sanatçıları",
  "city.description": "{city} bölgesinde dövme sanatçıları bulun.",
  "city.no_artists": "{city} bölgesinde sanatçı bulunamadı.",
  "city.discover_all": "Tüm sanatçılar",
  "seo.landing_title": "Inkvaluator – Dövme Fiyat Hesaplayıcısı",
  "seo.landing_desc": "Dövme fiyatınızı anında hesaplayın.",
  "vault.seo_title": "Dövme Kasam | Inkvaluator",
  "vault.title": "Dövme",
  "vault.title_accent": "Kasam",
  "vault.desc": "Kaydedilmiş tahminleriniz.",
  "vault.empty": "Tahmin yok. Bir hesaplayıcı kullanın!",
  "vault.save_btn": "Kaydet",
  "vault.saved_title": "Kaydedildi!",
  "vault.saved_desc": "Tahmin kaydedildi.",
  "success.active": "Aktif — her şey açık",
  "success.setup_whatsapp": "WhatsApp'ı ayarla",
  "success.syncing": "Planınız etkinleştiriliyor…",
  "success.view_details": "Detayları gör",
  "analytics.title": "Analitik",
  "analytics.desc": "Hesaplayıcı kullanımınız",
  "analytics.total_calculations": "Toplam hesaplama",
  "analytics.conversion_rate": "Dönüşüm oranı",
  "analytics.this_month": "Bu ay",
  "analytics.no_data": "Henüz veri yok.",
  "studio.title": "Stüdyo Yönetimi",
  "studio.team": "Ekip",
  "studio.invite": "Sanatçı davet et",
  "studio.invite_desc": "Davet linki oluşturun.",
  "studio.generate_link": "Link oluştur",
  "studio.no_members": "Üye yok. Sanatçıları davet edin.",
  "color.gold": "Altın",
  "color.red": "Kırmızı",
  "color.blue": "Mavi",
  "color.green": "Yeşil",
  "color.purple": "Mor",
  "color.pink": "Pembe",
  "color.turquoise": "Turkuaz",
  "color.orange": "Turuncu",
  "tip.title": "Sanatçı öner",
  "tip.desc": "Inkvaluator'da olması gereken bir dövme sanatçısı tanıyor musunuz?",
  "share.button": "Paylaş",
  "sw.update_available": "Yeni sürüm mevcut.",
  "embed.iframe_desc": "Bunu HTML'nize yapıştırın.",
  "embed.floating_desc": "</body> etiketinden önce yapıştırın.",
  "embed.verified_badge": "Doğrulanmış rozet",
  "embed.badge_desc": "Bu rozeti sitenize yerleştirin.",
  "compare.title": "Fiyat karşılaştırması",
  "compare.disclaimer": "Standart dövme bazında karşılaştırma.",
  "landing.calculate_price": "Fiyatı hesapla",
  "landing.total_value_prefix": "Şimdiye kadar",
  "landing.total_value_suffix": "dövme sanatı hesaplandı",
  "step.next": "Devam",
  "step.view_result": "Fiyatı gör",
  "step.details": "Detaylar",
  "step.details_desc": "Tasarımınız ne kadar detaylı?",
  "layout.stepper": "Adım adım",
  "layout.form": "Hepsi bir arada",
  "calc.view_price_cta": "Fiyat özetini gör",
  "calc.rough_estimate": "Kabataslak tahmin",
  "calc.calculated_price": "Hesaplanan fiyat",
  "calc.save_for_later": "Sonra için kaydet",
  "calc.share": "Paylaş",
  "calc.more_options": "Daha fazla seçenek",
  "calc.compare_configs": "Yapılandırmaları karşılaştır",
  "calc.compare_desc": "İki fiyat yapılandırmasını yan yana karşılaştır",
  "calc.no_photo": "Referans fotoğraf eklenmedi",
  "calc.link_copied": "Bağlantı kopyalandı!",
  "whatsapp.add_photo": "Fotoğrafını ekle",
  "whatsapp.photos_label": "Referans fotoğraflar — maks. 3",
  "laf.reset_success": "Tema varsayılana sıfırlandı",
  "admin.stripe_managed":
    "Abonelikler Stripe üzerinden otomatik olarak yönetilir — değişiklikler bir sonraki webhook senkronizasyonundan sonra görünür",
  "calc.view_tattoo_plan": "Dövme Planınızı görüntüleyin ✓",
  "wa.plan_message": "İşte Dövme Planım:",
  "lead.tattoo_plan": "Dövme Planı",
  "lead.made_with": "Inkvaluator ile oluşturuldu",
  "lead.contact_artist": "{artist} ile iletişime geçin",
  "lead.share": "Paylaş",
  "lead.share_title": "Dövme Planım",
  "lead.link_copied": "Bağlantı kopyalandı!",
  "lead.deposit_label": "Depozito",
  "lead.pay_deposit": "Depozito {amount} öde",
  "lead.deposit_request_title": "Depozito düzenlemesi talep et",
  "lead.deposit_request_desc": "Sanatçıya ~{amount} depozito mümkün mü sorun.",
  "lead.deposit_request_wa": "WhatsApp ile talep gönder",
  "lead.for_you": "Sen",
  "lead.for_label": "İçin",
  "lead.back_to_calculator": "Hesaplayıcıya dön",
  "lead.deposit_wa_msg":
    "Merhaba {artist}, Inkvaluator'da Dövme Planımı inceledim. ~{amount} depozito mümkün olur mu?\nPlan: {url}",
  "blog.no_translation": "Çeviri mevcut değil",
  "flash.tab": "Flash",
  "flash.upload": "Flash tasarım yükle",
  "flash.title": "Başlık",
  "flash.price": "Fiyat",
  "flash.size": "Boyut (cm)",
  "flash.available": "Mevcut",
  "flash.featured": "Öne çıkan",
  "flash.limit_used": "{max} flash tasarımdan kullanılmış",
  "flash.delete_confirm": "Flash tasarım silinsin mi?",
  "flash.reserve_whatsapp": "WhatsApp'tan rezerve et",
  "flash.whatsapp_msg": "Flash dövmenizle ilgileniyorum:",
  "flash.matching_designs": "Eşleşen flash tasarımlar",
  "flash.only_with_flash": "Sadece flash olan sanatçılar",
  "groei.score": "Profil puanı",
  "groei.views": "profil görüntülemeleri",
  "groei.sessions": "hesaplayıcı oturumları",
  "groei.leads": "müşteri adayları",
  "groei.this_week": "bu hafta",
  "groei.improve": "Profilinizi geliştirin",
  "groei.add_photos": "Daha fazla portfolyo fotoğrafı ekleyin",
  "groei.add_whatsapp": "WhatsApp numaranızı ayarlayın",
  "groei.add_bio": "Daha uzun bir biyografi yazın",
  "groei.add_avatar": "Profil fotoğrafı yükleyin",
  "groei.add_rate": "Saatlik ücretinizi belirleyin",
  "groei.add_city": "Şehrinizi ekleyin",
  "groei.get_leads": "Müşteri adayları almak için hesaplayıcınızı paylaşın",
  "groei.collapse": "Daralt",
  "groei.expand": "Genişlet",
  "groei.tip_portfolio": "6+ fotoğrafı olan sanatçılar 3 kat daha fazla oturum alır",
  "groei.tip_whatsapp": "WhatsApp butonu olan müşteriler 5 kat daha iyi dönüşür",
  "groei.tip_leads": "İlk leadinizi almak için hesaplayıcı linkinizi paylaşın",
  "groei.tip_avatar": "Profil fotoğrafı güvenilirliğinizi artırır",
  "groei.tip_action_photos": "Fotoğraf ekle",
  "groei.tip_action_whatsapp": "WhatsApp kur",
  "groei.tip_action_share": "Hesaplayıcıyı paylaş",
  "groei.tip_action_avatar": "Fotoğraf yükle",
  "milestone.eerste_lead": "İlk lead alındı!",
  "milestone.tien_leads": "10 lead ulaşıldı!",
  "milestone.profiel_compleet": "Profil %100 tamamlandı!",
  "milestone.eerste_flash": "İlk flash tasarım yüklendi!",
  "wa.name_required": "İsim gereklidir",
  "wa.plan_message_full": "Tam Tattoo Planımı görüntüle: {url}",
};

// ─── DANISH (complete) ──────────────────────────────────────────
const da: Record<string, string> = {
  ...en,
  "app.tagline": "Beregn din Tattoo-pris",
  back: "Tilbage",
  save: "Gem",
  copy: "Kopiér",
  copied: "Kopieret!",
  loading: "Indlæser...",
  error: "Fejl",
  optional: "valgfrit",
  "landing.hero_title": "Beregn din",
  "landing.hero_title_accent": "Tattoo-pris",
  "landing.hero_desc": "Find ud af på sekunder, hvad din næste tatovering koster.",
  "landing.try_it": "Prøv det!",
  "landing.estimated_price": "Estimeret pris",
  "landing.your_rates": "Dine priser",
  "landing.rates_desc": "Flyt skyderne og se prisændringen",
  "landing.hourly_rate": "Timepris",
  "landing.start_cost": "Faste startomkostninger",
  "landing.size_label": "Størrelse",
  "landing.complexity_label": "Kompleksitet",
  "landing.body_placement": "Placering på kroppen",
  "landing.find_artist": "Find din perfekte",
  "landing.find_artist_accent": "kunstner",
  "landing.discover_all": "Alle kunstnere",
  "landing.discover": "Udforsk",
  "landing.login": "Log ind",
  "landing.founder_desc": "50% rabat for de første 50 tilmeldinger",
  "landing.claim_now": "Gør krav nu →",
  "calc.estimate": "Estimeret pris",
  "calc.size": "Størrelse",
  "calc.complexity": "Kompleksitet",
  "calc.body_part": "Kropsdel",
  "calc.style": "Stil",
  "calc.budget": "Budget",
  "calc.back_home": "Tilbage til forsiden",
  "size.mini": "Mini (< 5 cm)",
  "size.small": "Lille (5-10 cm)",
  "size.medium": "Medium (10-20 cm)",
  "size.large": "Stor (20-35 cm)",
  "size.xl": "Ekstra Stor (35+ cm)",
  "complexity.simple": "Simpel",
  "complexity.medium": "Medium",
  "complexity.detailed": "Detaljeret",
  "body.arm": "Arm",
  "body.back": "Ryg",
  "body.chest": "Bryst",
  "body.neck": "Hals",
  "body.face": "Ansigt",
  "body.shoulder": "Skulder",
  "body.hand": "Hånd",
  "body.foot": "Fod",
  "body.other": "Andet",
  "body.front": "Forside",
  "body.back_view": "Bagside",
  "body.front_view": "Forside",
  "body.back_view_btn": "Bagside",
  "style.black_grey": "Sort & Grå",
  "style.full_color": "Fuld farve",
  "style.realism": "Realisme",
  "style.fine_line": "Fine Line / Minimal",
  "style.traditional": "Traditionel",
  "style.neo_traditional": "Neo Traditionel",
  "style.watercolor": "Akvarel",
  "style.tribal": "Tribal",
  "style.japanese": "Japansk",
  "style.geometric": "Geometrisk",
  "style.lettering": "Lettering",
  "style.script": "Skrift",
  "style.dotwork": "Dotwork",
  "style.mandala": "Mandala",
  "style.blackwork": "Blackwork",
  "style.illustrative": "Illustrativ",
  "style.surrealism": "Surrealisme",
  "style.ornamental": "Ornamental",
  "style.micro_realism": "Mikro-realisme",
  "style.trash_polka": "Trash Polka",
  "style.other": "Andet",
  "upgrade.choose": "Vælg din",
  "upgrade.plan_word": "Plan",
  "upgrade.per_month": "/måned",
  "upgrade.test_account_active": "Testkonto — funktioner aktiveret",
  "auth.login": "Log ind",
  "auth.register": "Registrer",
  "dash.logout": "Log ud",
  "dash.saved": "Gemt!",
  "dash.save_changes": "Gem ændringer",
  "dash.cancel": "Annuller",
  "dash.add": "Tilføj",
  "dash.delete": "Slet",
  "footer.tagline": "Den smarteste tattoo-prisberegner.",
  "footer.language_label": "Sprog",
  "footer.all_rights": "Alle rettigheder forbeholdes.",
  "footer.install_title": "Installér",
  "footer.install_ok": "Forstået!",
  "footer.terms": "Vilkår",
  "footer.support": "Support",
  "footer.how_it_works": "Sådan fungerer det",
  "success.welcome": "Velkommen til klubben,",
  "success.go_dashboard": "Gå til dashboard",
  "hiw.seo_title": "Sådan fungerer det – Inkvaluator",
  "hiw.hero_title": "Fokusér på blækket, ikke på regningen",
  "hiw.faq_title": "Ofte stillede spørgsmål",
  "hiw.cta_title": "Hvad venter du på?",
  "hiw.cta_accent": "Stop med at regne, begynd at skabe.",
  "hiw.cta_button": "Prøv Inkvaluator gratis",
  "vault.status_concept": "Udkast",
  "vault.status_sent": "Forespørgsel sendt",
  "reviews.rating": "vurdering",
  "reviews.verified_reviews": "verificerede anmeldelser",
  "reviews.read_all": "Læs alle anmeldelser på Google",
  "discover.swipe": "swipe",
  "vault.compare": "Sammenlign",
  "trends.title": "Tatovering",
  "trends.title_accent": "Trends",
  "trends.top_styles": "Populære stilarter",
  "trends.top_body_parts": "Populære kropsdele",
  "match.title": "Matchende kunstnere i nærheden",
  "match.score": "Match",
  "match.view": "Se profil",
  "urgency.recent_requests": "anmodninger denne uge",
  "urgency.wait_time": "Estimeret ventetid",
  "urgency.weeks": "uger",
  "urgency.high_demand": "Stor efterspørgsel",
  "urgency.popular": "Populær",
  "benchmark.above_market": "over gennemsnittet",
  "benchmark.below_market": "under gennemsnittet",
  "benchmark.at_market": "I gennemsnittet",
  "benchmark.market_avg": "Markedsgennemsnit",
  "portfolio.style_examples": "Stileksempler",
  "story.title": "Instagram Story Generator",
  "story.desc": "Opret et premium Black & Gold story-billede med din QR-kode.",
  "story.generate": "Generer Story",
  "story.regenerate": "Generer igen",
  "story.download": "Download",
  "story.share": "Del",
  "story.downloaded": "Story downloadet!",
  "story.cta_line1": "Beregn Din Pris",
  "story.cta_line2": "Scan QR-koden nedenfor",
  "story.scan_prompt": "↑ Scan for dit prisestimat",
  "trends.filter_country": "Filtrer efter land",
  "trends.all_countries": "Alle lande",
  "trends.analyzed": "beregninger analyseret",
  "trends.realtime": "Opdateret i realtid",
  "match.style_experts": "Eksperter i {style} nær {city}",
  "vault.review_booster_title": "Tatovering færdig? Hjælp din kunstner!",
  "vault.review_booster_desc": "Skriv en Google-anmeldelse og hjælp andre med at finde denne kunstner.",
  "vault.review_booster_btn": "Skriv en Google-anmeldelse",
  "checklist.title": "Verificeringsstatus",
  "checklist.profile_complete": "Profil komplet",
  "checklist.google_linked": "Google Place ID tilknyttet",
  "checklist.badge_detected": "Verificeret badge på hjemmesiden",
  "checklist.push_enabled": "Push-notifikationer aktiveret",
  "checklist.not_yet": "Endnu ikke",
  "checklist.active": "Aktiv",
  "lead.status_new": "Ny",
  "lead.status_contacted": "I kontakt",
  "lead.status_booked": "Booket",
  "lead.change_status": "Status",
  "lead.add_price": "Tilføj slutpris",
  "push.test_body": "Push-notifikationer er LIVE! Du modtager nu advarsler ved nye leads.",
  "push.send_test": "Send testnotifikation",
  "push.active_desc": "Du modtager notifikationer ved leads af høj kvalitet.",
  "push.blocked": "Notifikationer er blokeret i dine browserindstillinger.",
  "push.enable_desc": "Modtag notifikationer, når leads af høj kvalitet ankommer.",
  "push.label": "Push-notifikationer",
  "push.welcome_body": "Velkommen! Du modtager nu advarsler ved leads af høj kvalitet.",
  "push.denied_info": "Hold dig opdateret om nye leads — Aktivér notifikationer i dine browserindstillinger.",
  "vault.live_status": "Live Status",
  "vault.status_new_desc": "Din forespørgsel er modtaget",
  "vault.status_contacted_desc": "Kunstneren har set din besked",
  "vault.status_booked_desc": "Din aftale er bekræftet!",
  "vault.link_copied": "🔗 Link kopieret!",
  "analytics.actual_revenue": "Faktisk Omsætning",
  "style_city.popular_styles": "Populære stilarter i {city}",
  "nav.home": "Hjem",
  "lead.estimate_header": "Tatoveringsvurdering",
  "discover.verified_badge": "VERIFICERET",
  "wa.title": "Send til kunstneren via WhatsApp",
  "wa.subtitle": "Send dit estimat direkte til",
  "wa.name": "Dit navn (valgfrit)",
  "wa.name_placeholder": "Dit navn",
  "wa.message": "Besked (valgfrit)",
  "wa.message_placeholder": "Beskriv din idé, inspiration eller detaljer...",
  "wa.send": "Send via WhatsApp",
  "wa.sent_title": "Besked sendt!",
  "wa.sent_desc": "Din forespørgsel er sendt til {name} via WhatsApp.",
  "wa.success": "Sendt!",
  "wa.error": "Noget gik galt",
  "wa.msg_greeting": "Hej {name}, jeg har beregnet et prisestimat:",
  "wa.msg_name_label": "Navn",
  "wa.msg_style_label": "🎨 Stil",
  "wa.msg_bodypart_label": "📍 Kropsdel",
  "wa.msg_size_label": "📏 Størrelse",
  "wa.msg_complexity_label": "⚙️ Kompleksitet",
  "wa.msg_time_label": "⏱ Estimeret tid",
  "wa.msg_total_label": "💰 Total",
  "wa.msg_deposit_label": "💳 Depositum",
  "wa.msg_details_label": "🔗 Detaljer",
  "wa.template_high_greeting": "Hej {name}, gode nyheder! Jeg har beregnet et detaljeret estimat:",
  "wa.template_low_greeting":
    "Hej {name}, jeg er interesseret i en tatovering. Kan du fortælle om tilgængelighed og priser?",
  "tooltip.labor": "Baseret på den estimerede tid gange kunstnerens timepris.",
  "tooltip.complexity": "Mere komplekse designs kræver mere tid og præcision.",
  "tooltip.placement": "Nogle kropsdele er mere følsomme eller sværere at nå.",
  "tooltip.style": "Forskellige stile kræver specifikke teknikker der påvirker prisen.",
  "pricing_edu.button": "Hvordan fungerer beregningen?",
  "pricing_edu.title": "Hvordan prisen beregnes",
  "pricing_edu.disclaimer": "Dette er altid et estimat. Den endelige pris bestemmes efter personlig konsultation.",
  "ai.title": "AI Scanner",
  "ai.analyzing": "AI analyserer...",
  "ai.scan_complete": "✨ AI-scanning fuldført",
  "pwa.install_title": "Installér som app",
  "pwa.install_button": "Installér nu",
  // Batch 3+4
  "tab.price_engine": "Prismotor",
  "tab.price_engine_desc": "Administrer alle prismultiplikatorer: størrelser, kompleksitet, kropsdele og stilarter.",
  "basic.desc": "Indstil dine standardpriser og omkostninger",
  "basic.min_price_desc": "Den laveste pris du opkræver",
  "basic.hourly_rate_desc": "Din standard timepris",
  "basic.daily_rate_placeholder": "Lad stå tomt hvis ikke relevant",
  "basic.percentage": "%",
  "basic.fixed_amount": "Fast",
  "sizes.title": "Størrelsesindstillinger",
  "sizes.desc": "Indstil estimeret tid pr. størrelse",
  "sizes.min_hours": "Min. timer",
  "sizes.max_hours": "Maks. timer",
  "sizes.hours": "timer",
  "complexity.title": "Kompleksitetsmultiplikatorer",
  "complexity.desc": "Juster prisfaktoren efter kompleksitet",
  "bodyparts.title": "Kropsdelsmultiplikatorer",
  "bodyparts.desc": "Nogle kropsdele er sværere at tatovere",
  "styles.title": "Stilstyring",
  "styles.desc": "Aktivér/deaktivér stile og juster prisfaktoren.",
  "theme.title": "Farvetema",
  "theme.desc": "Tilpas farverne på din beregner",
  "theme.preview": "Forhåndsvisning",
  "theme.preview_desc": "Sådan ser din beregner ud",
  "theme.hue": "Tone",
  "theme.saturation": "Mætning",
  "theme.lightness": "Lysstyrke",
  "theme.presets": "Hurtigvalg",
  "branding.title": "Branding",
  "branding.desc": "Upload logo, vælg sprog og valuta",
  "branding.logo": "Logo",
  "branding.logo_desc": "Studielogo (maks. 2 MB, PNG eller JPG)",
  "branding.upload": "Upload logo",
  "branding.remove": "Fjern",
  "branding.language": "Beregnersprog",
  "branding.currency": "Valuta",
  "branding.qr_code": "QR-kode",
  "branding.qr_desc": "Kunder scanner denne for at åbne din beregner.",
  "branding.download_qr": "Download QR",
  "onboard.step_minimum": "Minimumspris",
  "onboard.step_hourly": "Timepris",
  "onboard.step_sizes": "Bekræft størrelser",
  "onboard.step_styles": "Indstil stile",
  "onboard.step_logo": "Upload logo",
  "onboard.step_finish": "Beregner klar!",
  "dash.onboard_min_desc": "Indstil din minimumspris.",
  "dash.onboard_hourly_desc": "Indstil din timepris. Det er grundlaget for alle beregninger.",
  "dash.onboard_sizes_desc": "Standard størrelseskategorier er konfigureret. Juster dem senere.",
  "dash.onboard_styles_desc": "Alle almindelige stile er aktiveret. Aktivér/deaktivér i dashboardet.",
  "dash.onboard_logo_desc": "Upload et logo nu eller senere via Branding-fanen.",
  "dash.onboard_logo_later": "Upload senere via Dashboard → Branding",
  "dash.onboard_finish_title": "Din beregner er klar!",
  "dash.onboard_finish_desc": "Del dit unikke link. Ændr indstillinger når som helst.",
  "dash.your_link": "Dit beregnerlink:",
  "dash.online_card": "Dit online visitkort.",
  "dash.view_my_calculator": "Se min beregner",
  "dash.upload_thumbnail": "Brugerdefineret billede",
  "dash.reset_to_default": "Nulstil",
  "dash.reset_all_thumbnails": "Nulstil alle",
  "dash.thumbnail_uploaded": "Billede uploadet!",
  "dash.thumbnail_reset": "Billede nulstillet",
  "dash.thumbnails_reset_all": "Alle billeder nulstillet",
  "dash.invite_created": "Invitation oprettet!",
  "dash.invite_error": "Invitationsfejl",
  "dash.member_removed": "Medlem fjernet",
  "dash.link_copied_generic": "Link kopieret!",
  "dash.profile_saved": "Profil gemt!",
  "dash.profile_save_error": "Gemningsfejl",
  "dash.calculator_ready": "🎉 Beregner klar!",
  "dash.share_with_clients": "Del dit link.",
  "dash.step_of": "Trin {current} af {total}",
  "dash.start_inkprice": "Start med InkPrice",
  "laf.title": "Udseende",
  "laf.desc": "Tilpas udseendet af din offentlige beregner.",
  "laf.color_mode": "Farvetilstand",
  "laf.dark_mode": "Mørk tilstand",
  "laf.light_mode": "Lys tilstand",
  "laf.background": "Baggrund",
  "laf.solid_color": "Ensfarvet",
  "laf.image_url": "Billed-URL",
  "laf.preview": "Forhåndsvisning",
  "laf.saved": "Udseende gemt",
  "laf.presets": "Premium-temaer",
  "laf.font_selector": "Beregnerskrifttype",
  "laf.overlay_opacity": "Overlay-gennemsigtighed",
  "laf.bg_fixed": "Fast (Parallax)",
  "laf.bg_scroll": "Scroll",
  "consult_settings.title": "Konsultationsindstillinger",
  "consult_settings.desc": "Konfigurer kontaktmetoder.",
  "consult_settings.booking_url_label": "Reservations-URL",
  "consult_settings.email_label": "Konsultations-e-mail",
  "consult_settings.channels_title": "Lead-kanaler",
  "consult_settings.channels_desc": "Synlige kontaktmetoder.",
  "help.min_price": "Absolut minimumspris per session.",
  "help.hourly_rate": "Din timepris er grundlaget for alle beregninger.",
  "help.multipliers": "Multiplikatorer justerer prisen efter stil, kompleksitet og placering.",
  "help.sizes": "Indstil tidsintervallet for hver størrelse.",
  "help.complexity": "Juster multiplikatorerne for kompleksitet.",
  "help.body_parts": "Nogle zoner kræver højere multiplikatorer.",
  "help.styles": "Aktivér/deaktivér stile og indstil multiplikatorer.",
  "help.logo": "Dit logo vises på beregneren og i kataloget. Maks. 2 MB.",
  "help.currency": "Valutaen vist på din beregner.",
  "help.calculator_language": "Sproget for din offentlige beregner.",
  "help.dashboard_language": "Sproget for dette dashboard — kun synligt for dig.",
  "profile.mfa_title": "Kontosikkerhed",
  "profile.mfa_desc": "Beskyt din konto med 2FA.",
  "profile.mfa_enable": "Aktivér 2FA",
  "profile.mfa_verify": "Bekræft",
  "profile.mfa_active": "Aktiv",
  "profile.mfa_enabled_success": "✅ 2FA aktiveret!",
  "profile.google_reviews": "Google Anmeldelser",
  "profile.google_reviews_desc": "Tilknyt din Google Business-profil.",
  "profile.sync_now": "Synkroniser",
  "profile.sync_success": "Google-anmeldelser synkroniseret!",
  "landing.faq_title": "FAQ",
  "landing.faq_subtitle": "Alt om Inkvaluator",
  "landing.faq_q1": "Hvordan fungerer beregneren?",
  "landing.faq_a1": "Indstil priser og multiplikatorer. Kunder får øjeblikkeligt estimat.",
  "landing.faq_q2": "Er Inkvaluator gratis?",
  "landing.faq_a2": "Ja! Free-planen inkluderer en komplet beregner. Opgradér til Pro for mere.",
  "landing.faq_q3": "Hvordan fungerer AI-scanneren?",
  "landing.faq_a3": "Upload et foto, AI analyserer størrelse, kompleksitet og stil på sekunder.",
  "landing.faq_q4": "Kan kunder kontakte mig?",
  "landing.faq_a4": "Ja, via WhatsApp med forudfyldt besked.",
  "landing.faq_q5": "Flersproget support?",
  "landing.faq_a5": "17 sprog og 13 valutaer — perfekt for internationale kunstnere.",
  "landing.faq_q6": "Hvordan får jeg flere kunder?",
  "landing.faq_a6": "Pro/Studio-kunstnere vises på verdenskortet og opdagelsessiden.",
  "city.title": "Tatovører i {city}",
  "city.description": "Find tatovører i {city}.",
  "city.no_artists": "Ingen kunstnere fundet i {city}.",
  "city.discover_all": "Alle kunstnere",
  "seo.landing_title": "Inkvaluator – Tattoo Prisberegner",
  "seo.landing_desc": "Beregn din tatoveringspris øjeblikkeligt.",
  "vault.seo_title": "Min Tattoo-boks | Inkvaluator",
  "vault.title": "Min",
  "vault.title_accent": "Tattoo-boks",
  "vault.desc": "Dine gemte estimater.",
  "vault.empty": "Ingen estimater. Brug en beregner!",
  "vault.save_btn": "Gem",
  "vault.saved_title": "Gemt!",
  "vault.saved_desc": "Estimat gemt.",
  "success.active": "Aktiv — alt låst op",
  "success.setup_whatsapp": "Konfigurer WhatsApp",
  "success.syncing": "Aktiverer dit abonnement…",
  "success.view_details": "Se detaljer",
  "analytics.title": "Analyser",
  "analytics.desc": "Dit beregnerforbrug",
  "analytics.total_calculations": "Samlede beregninger",
  "analytics.conversion_rate": "Konverteringsrate",
  "analytics.this_month": "Denne måned",
  "analytics.no_data": "Ingen data endnu.",
  "studio.title": "Studiestyring",
  "studio.team": "Team",
  "studio.invite": "Inviter kunstner",
  "studio.invite_desc": "Generér et invitationslink.",
  "studio.generate_link": "Generér link",
  "studio.no_members": "Ingen medlemmer. Inviter kunstnere.",
  "color.gold": "Guld",
  "color.red": "Rød",
  "color.blue": "Blå",
  "color.green": "Grøn",
  "color.purple": "Lilla",
  "color.pink": "Pink",
  "color.turquoise": "Turkis",
  "color.orange": "Orange",
  "tip.title": "Anbefal en kunstner",
  "tip.desc": "Kender du en tatovør der burde være på Inkvaluator?",
  "share.button": "Del",
  "sw.update_available": "Ny version tilgængelig.",
  "embed.iframe_desc": "Indsæt dette i din HTML.",
  "embed.floating_desc": "Indsæt før </body>.",
  "embed.verified_badge": "Verificeret badge",
  "embed.badge_desc": "Placér dette badge på din hjemmeside.",
  "compare.title": "Prissammenligning",
  "compare.disclaimer": "Sammenligning baseret på standard tatovering.",
  "landing.calculate_price": "Beregn pris",
  "landing.total_value_prefix": "Allerede",
  "landing.total_value_suffix": "i tatoveringskunst beregnet",
  "ai.desc": "Upload et foto og lad AI bestemme indstillingerne",
  "ai.scan_failed": "Scanning mislykkedes",
  "ai.try_again": "Prøv igen",
  "referral.title": "Anbefal en kollega",
  "referral.generate": "Generér henvisningslink",
  "tooltip.design": "Omkostninger til design og forberedelse af din unikke tatovering.",
  "tooltip.deposit": "Depositummet sikrer din aftale og trækkes fra totalprisen.",
  "pricing_edu.intro": "Prisen på en tatovering bestemmes af flere faktorer der afspejler håndværk og tid.",
  "step.next": "Fortsæt",
  "step.view_result": "Se pris",
  "step.details": "Detaljer",
  "step.details_desc": "Hvor detaljeret er dit design?",
  "layout.stepper": "Trin for trin",
  "layout.form": "Alt på én gang",
  "calc.view_price_cta": "Se prisoversigt",
  "calc.rough_estimate": "Groft estimat",
  "calc.calculated_price": "Beregnet pris",
  "calc.save_for_later": "Gem til senere",
  "calc.share": "Del",
  "calc.more_options": "Flere muligheder",
  "calc.compare_configs": "Sammenlign konfigurationer",
  "calc.compare_desc": "Sammenlign to priskonfigurationer side om side",
  "calc.no_photo": "Intet referencefoto tilføjet",
  "calc.link_copied": "Link kopieret!",
  "whatsapp.add_photo": "Tilføj dit foto",
  "whatsapp.photos_label": "Referencefotos — maks. 3",
  "laf.reset_success": "Tema nulstillet til standard",
  "admin.stripe_managed":
    "Abonnementer administreres automatisk via Stripe — ændringer er synlige efter næste webhook-synkronisering",
  "calc.view_tattoo_plan": "Se din Tattoo Plan ✓",
  "wa.plan_message": "Her er min Tattoo Plan:",
  "lead.tattoo_plan": "Tattoo Plan",
  "lead.made_with": "Lavet med Inkvaluator",
  "lead.contact_artist": "Kontakt {artist}",
  "lead.share": "Del",
  "lead.share_title": "Min Tattoo Plan",
  "lead.link_copied": "Link kopieret!",
  "lead.deposit_label": "Depositum",
  "lead.pay_deposit": "Betal depositum {amount}",
  "lead.deposit_request_title": "Anmod om depositumaftale",
  "lead.deposit_request_desc": "Spørg kunstneren om et depositum på ~{amount} er muligt.",
  "lead.deposit_request_wa": "Send anmodning via WhatsApp",
  "lead.for_you": "Dig",
  "lead.for_label": "Til",
  "lead.back_to_calculator": "Tilbage til beregner",
  "lead.deposit_wa_msg":
    "Hej {artist}, jeg har set min Tattoo Plan på Inkvaluator. Ville et depositum på ~{amount} være muligt?\nPlan: {url}",
  "blog.no_translation": "Ingen oversættelse tilgængelig",
  "flash.tab": "Flash",
  "flash.upload": "Upload flash-design",
  "flash.title": "Titel",
  "flash.price": "Pris",
  "flash.size": "Størrelse (cm)",
  "flash.available": "Tilgængelig",
  "flash.featured": "Fremhævet",
  "flash.limit_used": "af {max} flash-designs brugt",
  "flash.delete_confirm": "Slet flash-design?",
  "flash.reserve_whatsapp": "Reserver via WhatsApp",
  "flash.whatsapp_msg": "Jeg er interesseret i din flash-tatovering:",
  "flash.matching_designs": "Matchende flash-designs",
  "flash.only_with_flash": "Kun kunstnere med flash",
  "groei.score": "Profilscore",
  "groei.views": "profilvisninger",
  "groei.sessions": "beregner-sessioner",
  "groei.leads": "leads",
  "groei.this_week": "denne uge",
  "groei.improve": "Forbedr din profil",
  "groei.add_photos": "Tilføj flere portfoliobilleder",
  "groei.add_whatsapp": "Opsæt dit WhatsApp-nummer",
  "groei.add_bio": "Skriv en længere bio",
  "groei.add_avatar": "Upload et profilbillede",
  "groei.add_rate": "Sæt din timepris",
  "groei.add_city": "Tilføj din by",
  "groei.get_leads": "Del din beregner for at få leads",
  "groei.collapse": "Fold sammen",
  "groei.expand": "Fold ud",
  "groei.tip_portfolio": "Kunstnere med 6+ fotos får 3x flere sessioner",
  "groei.tip_whatsapp": "Kunder med WhatsApp-knap konverterer 5x bedre",
  "groei.tip_leads": "Del dit beregner-link for at modtage dit første lead",
  "groei.tip_avatar": "Et profilbillede øger din troværdighed",
  "groei.tip_action_photos": "Tilføj fotos",
  "groei.tip_action_whatsapp": "Opsæt WhatsApp",
  "groei.tip_action_share": "Del beregner",
  "groei.tip_action_avatar": "Upload foto",
  "milestone.eerste_lead": "Første lead modtaget!",
  "milestone.tien_leads": "10 leads nået!",
  "milestone.profiel_compleet": "Profil 100% komplet!",
  "milestone.eerste_flash": "Første flash design uploadet!",
  "wa.name_required": "Navn er påkrævet",
  "wa.plan_message_full": "Se min fulde Tattoo Plan: {url}",
};

// ─── NORWEGIAN (complete) ──────────────────────────────────────
const no: Record<string, string> = {
  ...en,
  "app.tagline": "Beregn Tattoo-prisen din",
  back: "Tilbake",
  save: "Lagre",
  copy: "Kopier",
  copied: "Kopiert!",
  loading: "Laster...",
  error: "Feil",
  optional: "valgfritt",
  "landing.hero_title": "Beregn din",
  "landing.hero_title_accent": "Tattoo-pris",
  "landing.hero_desc": "Finn ut på sekunder hva din neste tatovering koster.",
  "landing.try_it": "Prøv det!",
  "landing.estimated_price": "Estimert pris",
  "landing.your_rates": "Dine priser",
  "landing.rates_desc": "Flytt glidebryterne og se prisendringen",
  "landing.hourly_rate": "Timepris",
  "landing.start_cost": "Faste startkostnader",
  "landing.size_label": "Størrelse",
  "landing.complexity_label": "Kompleksitet",
  "landing.body_placement": "Plassering på kroppen",
  "landing.find_artist": "Finn din perfekte",
  "landing.find_artist_accent": "kunstner",
  "landing.discover_all": "Alle kunstnere",
  "landing.discover": "Utforsk",
  "landing.login": "Logg inn",
  "landing.founder_desc": "50% rabatt for de første 50 registreringene",
  "landing.claim_now": "Hent nå →",
  "calc.estimate": "Estimert pris",
  "calc.size": "Størrelse",
  "calc.complexity": "Kompleksitet",
  "calc.body_part": "Kroppsdel",
  "calc.style": "Stil",
  "calc.budget": "Budsjett",
  "calc.back_home": "Tilbake til forsiden",
  "size.mini": "Mini (< 5 cm)",
  "size.small": "Liten (5-10 cm)",
  "size.medium": "Medium (10-20 cm)",
  "size.large": "Stor (20-35 cm)",
  "size.xl": "Ekstra Stor (35+ cm)",
  "complexity.simple": "Enkel",
  "complexity.medium": "Medium",
  "complexity.detailed": "Detaljert",
  "body.arm": "Arm",
  "body.back": "Rygg",
  "body.chest": "Bryst",
  "body.neck": "Nakke",
  "body.face": "Ansikt",
  "body.shoulder": "Skulder",
  "body.hand": "Hånd",
  "body.foot": "Fot",
  "body.other": "Annet",
  "body.front": "Forside",
  "body.back_view": "Bakside",
  "body.front_view": "Forside",
  "body.back_view_btn": "Bakside",
  "style.black_grey": "Svart & Grå",
  "style.full_color": "Fullfarge",
  "style.realism": "Realisme",
  "style.fine_line": "Fine Line / Minimal",
  "style.traditional": "Tradisjonell",
  "style.neo_traditional": "Neo Tradisjonell",
  "style.watercolor": "Akvarell",
  "style.tribal": "Tribal",
  "style.japanese": "Japansk",
  "style.geometric": "Geometrisk",
  "style.lettering": "Lettering",
  "style.script": "Skrift",
  "style.dotwork": "Dotwork",
  "style.mandala": "Mandala",
  "style.blackwork": "Blackwork",
  "style.illustrative": "Illustrativ",
  "style.surrealism": "Surrealisme",
  "style.ornamental": "Ornamental",
  "style.micro_realism": "Mikro-realisme",
  "style.trash_polka": "Trash Polka",
  "style.other": "Annet",
  "upgrade.choose": "Velg din",
  "upgrade.plan_word": "Plan",
  "upgrade.per_month": "/mnd",
  "upgrade.test_account_active": "Testkonto — funksjoner aktivert",
  "auth.login": "Logg inn",
  "auth.register": "Registrer",
  "dash.logout": "Logg ut",
  "dash.saved": "Lagret!",
  "dash.save_changes": "Lagre endringer",
  "dash.cancel": "Avbryt",
  "dash.add": "Legg til",
  "dash.delete": "Slett",
  "footer.tagline": "Den smarteste tattoo-priskalkulatoren.",
  "footer.language_label": "Språk",
  "footer.all_rights": "Alle rettigheter forbeholdt.",
  "footer.install_title": "Installer",
  "footer.install_ok": "Forstått!",
  "footer.terms": "Vilkår",
  "footer.support": "Støtte",
  "footer.how_it_works": "Hvordan fungerer det",
  "success.welcome": "Velkommen til klubben,",
  "success.go_dashboard": "Gå til dashbordet",
  "hiw.seo_title": "Hvordan fungerer det – Inkvaluator",
  "hiw.hero_title": "Fokuser på blekket, ikke på regningen",
  "hiw.faq_title": "Ofte stilte spørsmål",
  "hiw.cta_title": "Hva venter du på?",
  "hiw.cta_accent": "Slutt å regne, begynn å skape.",
  "hiw.cta_button": "Prøv Inkvaluator gratis",
  "vault.status_concept": "Utkast",
  "vault.status_sent": "Forespørsel sendt",
  "reviews.rating": "vurdering",
  "reviews.verified_reviews": "verifiserte anmeldelser",
  "reviews.read_all": "Les alle anmeldelser på Google",
  "discover.swipe": "sveip",
  "vault.compare": "Sammenlign",
  "trends.title": "Tatovering",
  "trends.title_accent": "Trender",
  "trends.top_styles": "Populære stiler",
  "trends.top_body_parts": "Populære kroppsdeler",
  "match.title": "Matchende kunstnere i nærheten",
  "match.score": "Match",
  "match.view": "Se profil",
  "urgency.recent_requests": "forespørsler denne uken",
  "urgency.wait_time": "Estimert ventetid",
  "urgency.weeks": "uker",
  "urgency.high_demand": "Stor etterspørsel",
  "urgency.popular": "Populær",
  "benchmark.above_market": "over gjennomsnittet",
  "benchmark.below_market": "under gjennomsnittet",
  "benchmark.at_market": "I gjennomsnittet",
  "benchmark.market_avg": "Markedsgjennomsnitt",
  "portfolio.style_examples": "Stileksempler",
  "story.title": "Instagram Story Generator",
  "story.desc": "Lag et premium Black & Gold story-bilde med din QR-kode.",
  "story.generate": "Generer Story",
  "story.regenerate": "Generer på nytt",
  "story.download": "Last ned",
  "story.share": "Del",
  "story.downloaded": "Story lastet ned!",
  "story.cta_line1": "Beregn Din Pris",
  "story.cta_line2": "Skann QR-koden nedenfor",
  "story.scan_prompt": "↑ Skann for prisestimatet ditt",
  "trends.filter_country": "Filtrer etter land",
  "trends.all_countries": "Alle land",
  "trends.analyzed": "beregninger analysert",
  "trends.realtime": "Oppdatert i sanntid",
  "match.style_experts": "Eksperter på {style} nær {city}",
  "vault.review_booster_title": "Tatovering ferdig? Hjelp kunstneren din!",
  "vault.review_booster_desc": "Legg igjen en Google-anmeldelse og hjelp andre med å finne denne kunstneren.",
  "vault.review_booster_btn": "Skriv en Google-anmeldelse",
  "checklist.title": "Verifiseringsstatus",
  "checklist.profile_complete": "Profil komplett",
  "checklist.google_linked": "Google Place ID tilknyttet",
  "checklist.badge_detected": "Verifisert badge på nettsiden",
  "checklist.push_enabled": "Push-varsler aktivert",
  "checklist.not_yet": "Ikke ennå",
  "checklist.active": "Aktiv",
  "lead.status_new": "Ny",
  "lead.status_contacted": "I kontakt",
  "lead.status_booked": "Booket",
  "lead.change_status": "Status",
  "lead.add_price": "Legg til sluttprisen",
  "push.test_body": "Push-varsler er LIVE! Du mottar nå varsler ved nye leads.",
  "push.send_test": "Send testvarsel",
  "push.active_desc": "Du mottar varsler ved leads av høy kvalitet.",
  "push.blocked": "Varsler er blokkert i nettleserinnstillingene dine.",
  "push.enable_desc": "Motta varsler når leads av høy kvalitet kommer inn.",
  "push.label": "Push-varsler",
  "push.welcome_body": "Velkommen! Du mottar nå varsler ved leads av høy kvalitet.",
  "push.denied_info": "Hold deg oppdatert om nye leads — Aktiver varsler i nettleserinnstillingene dine.",
  "vault.live_status": "Direktestatus",
  "vault.status_new_desc": "Forespørselen din er mottatt",
  "vault.status_contacted_desc": "Kunstneren har sett meldingen din",
  "vault.status_booked_desc": "Avtalen din er bekreftet!",
  "vault.link_copied": "🔗 Lenke kopiert!",
  "analytics.actual_revenue": "Faktisk Inntekt",
  "style_city.popular_styles": "Populære stiler i {city}",
  "nav.home": "Hjem",
  "lead.estimate_header": "Tatoveringsestimat",
  "discover.verified_badge": "VERIFISERT",
  "wa.title": "Send til kunstneren via WhatsApp",
  "wa.subtitle": "Send estimatet ditt direkte til",
  "wa.name": "Ditt navn (valgfritt)",
  "wa.name_placeholder": "Ditt navn",
  "wa.message": "Melding (valgfritt)",
  "wa.message_placeholder": "Beskriv ideen din, inspirasjon eller detaljer...",
  "wa.send": "Send via WhatsApp",
  "wa.sent_title": "Melding sendt!",
  "wa.sent_desc": "Forespørselen din er sendt til {name} via WhatsApp.",
  "wa.success": "Sendt!",
  "wa.error": "Noe gikk galt",
  "wa.msg_greeting": "Hei {name}, jeg har beregnet et prisestimat:",
  "wa.msg_name_label": "Navn",
  "wa.msg_style_label": "🎨 Stil",
  "wa.msg_bodypart_label": "📍 Kroppsdel",
  "wa.msg_size_label": "📏 Størrelse",
  "wa.msg_complexity_label": "⚙️ Kompleksitet",
  "wa.msg_time_label": "⏱ Estimert tid",
  "wa.msg_total_label": "💰 Totalt",
  "wa.msg_deposit_label": "💳 Depositum",
  "wa.msg_details_label": "🔗 Detaljer",
  "wa.template_high_greeting": "Hei {name}, gode nyheter! Jeg har beregnet et detaljert estimat:",
  "wa.template_low_greeting":
    "Hei {name}, jeg er interessert i en tatovering. Kan du fortelle om tilgjengelighet og priser?",
  "tooltip.labor": "Basert på estimert tid ganget med kunstnerens timepris.",
  "tooltip.complexity": "Mer komplekse design krever mer tid og presisjon.",
  "tooltip.placement": "Noen kroppsdeler er mer sensitive eller vanskeligere å nå.",
  "tooltip.style": "Ulike stiler krever spesifikke teknikker som påvirker prisen.",
  "pricing_edu.button": "Hvordan fungerer beregningen?",
  "pricing_edu.title": "Hvordan prisen beregnes",
  "pricing_edu.disclaimer": "Dette er alltid et estimat. Den endelige prisen bestemmes etter personlig konsultasjon.",
  "ai.title": "AI-skanner",
  "ai.analyzing": "AI analyserer...",
  "ai.scan_complete": "✨ AI-skanning fullført",
  "pwa.install_title": "Installer som app",
  "pwa.install_button": "Installer nå",
  // Batch 3+4
  "tab.price_engine": "Prismotor",
  "tab.price_engine_desc": "Administrer alle prismultiplikatorer: størrelser, kompleksitet, kroppsdeler og stiler.",
  "basic.desc": "Sett dine standardpriser og kostnader",
  "basic.min_price_desc": "Den laveste prisen du tar",
  "basic.hourly_rate_desc": "Din standard timepris",
  "basic.daily_rate_placeholder": "La stå tomt hvis ikke relevant",
  "basic.percentage": "%",
  "basic.fixed_amount": "Fast",
  "sizes.title": "Størrelsesinnstillinger",
  "sizes.desc": "Sett estimert tid per størrelse",
  "sizes.min_hours": "Min. timer",
  "sizes.max_hours": "Maks. timer",
  "sizes.hours": "timer",
  "complexity.title": "Kompleksitetsmultiplikatorer",
  "complexity.desc": "Juster prisfaktoren etter kompleksitet",
  "bodyparts.title": "Kroppsdelmultiplikatorer",
  "bodyparts.desc": "Noen kroppsdeler er vanskeligere å tatovere",
  "styles.title": "Stiladministrasjon",
  "styles.desc": "Aktiver/deaktiver stiler og juster prisfaktoren.",
  "theme.title": "Fargetema",
  "theme.desc": "Tilpass fargene på kalkulatoren din",
  "theme.preview": "Forhåndsvisning",
  "theme.preview_desc": "Slik ser kalkulatoren din ut",
  "theme.hue": "Tone",
  "theme.saturation": "Metning",
  "theme.lightness": "Lysstyrke",
  "theme.presets": "Hurtigvalg",
  "branding.title": "Merkevare",
  "branding.desc": "Last opp logo, velg språk og valuta",
  "branding.logo": "Logo",
  "branding.logo_desc": "Studiologo (maks. 2 MB, PNG eller JPG)",
  "branding.upload": "Last opp logo",
  "branding.remove": "Fjern",
  "branding.language": "Kalkulator-språk",
  "branding.currency": "Valuta",
  "branding.qr_code": "QR-kode",
  "branding.qr_desc": "Kunder skanner denne for å åpne kalkulatoren din.",
  "branding.download_qr": "Last ned QR",
  "onboard.step_minimum": "Minimumspris",
  "onboard.step_hourly": "Timepris",
  "onboard.step_sizes": "Bekreft størrelser",
  "onboard.step_styles": "Sett stiler",
  "onboard.step_logo": "Last opp logo",
  "onboard.step_finish": "Kalkulator klar!",
  "dash.onboard_min_desc": "Sett minimumsprisen din.",
  "dash.onboard_hourly_desc": "Sett timeprisen din. Det er grunnlaget for alle beregninger.",
  "dash.onboard_sizes_desc": "Standard størrelseskategorier er konfigurert. Juster dem senere.",
  "dash.onboard_styles_desc": "Alle vanlige stiler er aktivert. Aktiver/deaktiver i dashbordet.",
  "dash.onboard_logo_desc": "Last opp en logo nå eller senere via Merkevare-fanen.",
  "dash.onboard_logo_later": "Last opp senere via Dashbord → Merkevare",
  "dash.onboard_finish_title": "Kalkulatoren din er klar!",
  "dash.onboard_finish_desc": "Del den unike lenken din. Endre innstillinger når som helst.",
  "dash.your_link": "Kalkulator-lenken din:",
  "dash.online_card": "Ditt online visittkort.",
  "dash.view_my_calculator": "Se kalkulatoren min",
  "dash.upload_thumbnail": "Tilpasset bilde",
  "dash.reset_to_default": "Tilbakestill",
  "dash.reset_all_thumbnails": "Tilbakestill alle",
  "dash.thumbnail_uploaded": "Bilde lastet opp!",
  "dash.thumbnail_reset": "Bilde tilbakestilt",
  "dash.thumbnails_reset_all": "Alle bilder tilbakestilt",
  "dash.invite_created": "Invitasjon opprettet!",
  "dash.invite_error": "Invitasjonsfeil",
  "dash.member_removed": "Medlem fjernet",
  "dash.link_copied_generic": "Lenke kopiert!",
  "dash.profile_saved": "Profil lagret!",
  "dash.profile_save_error": "Lagringsfeil",
  "dash.calculator_ready": "🎉 Kalkulator klar!",
  "dash.share_with_clients": "Del lenken din.",
  "dash.step_of": "Steg {current} av {total}",
  "dash.start_inkprice": "Start med InkPrice",
  "laf.title": "Utseende",
  "laf.desc": "Tilpass utseendet til den offentlige kalkulatoren din.",
  "laf.color_mode": "Fargemodus",
  "laf.dark_mode": "Mørk modus",
  "laf.light_mode": "Lys modus",
  "laf.background": "Bakgrunn",
  "laf.solid_color": "Ensfarget",
  "laf.image_url": "Bilde-URL",
  "laf.preview": "Forhåndsvisning",
  "laf.saved": "Utseende lagret",
  "laf.presets": "Premium-temaer",
  "laf.font_selector": "Kalkulator-skrifttype",
  "laf.overlay_opacity": "Overleggsopasitet",
  "laf.bg_fixed": "Fast (Parallax)",
  "laf.bg_scroll": "Rull",
  "consult_settings.title": "Konsultasjonsinnstillinger",
  "consult_settings.desc": "Konfigurer kontaktmetoder.",
  "consult_settings.booking_url_label": "Reservasjons-URL",
  "consult_settings.email_label": "Konsultasjons-e-post",
  "consult_settings.channels_title": "Lead-kanaler",
  "consult_settings.channels_desc": "Synlige kontaktmetoder.",
  "help.min_price": "Absolutt minimumspris per sesjon.",
  "help.hourly_rate": "Timeprisen din er grunnlaget for alle beregninger.",
  "help.multipliers": "Multiplikatorer justerer prisen etter stil, kompleksitet og plassering.",
  "help.sizes": "Sett tidsintervallet for hver størrelse.",
  "help.complexity": "Juster multiplikatorene for kompleksitet.",
  "help.body_parts": "Noen soner krever høyere multiplikatorer.",
  "help.styles": "Aktiver/deaktiver stiler og sett multiplikatorer.",
  "help.logo": "Logoen din vises på kalkulatoren og i katalogen. Maks. 2 MB.",
  "help.currency": "Valutaen vist på kalkulatoren din.",
  "help.calculator_language": "Språket for den offentlige kalkulatoren din.",
  "help.dashboard_language": "Språket for dette dashbordet — bare synlig for deg.",
  "profile.mfa_title": "Kontosikkerhet",
  "profile.mfa_desc": "Beskytt kontoen din med 2FA.",
  "profile.mfa_enable": "Aktiver 2FA",
  "profile.mfa_verify": "Bekreft",
  "profile.mfa_active": "Aktiv",
  "profile.mfa_enabled_success": "✅ 2FA aktivert!",
  "profile.google_reviews": "Google-anmeldelser",
  "profile.google_reviews_desc": "Koble Google Business-profilen din.",
  "profile.sync_now": "Synkroniser",
  "profile.sync_success": "Google-anmeldelser synkronisert!",
  "landing.faq_title": "FAQ",
  "landing.faq_subtitle": "Alt om Inkvaluator",
  "landing.faq_q1": "Hvordan fungerer kalkulatoren?",
  "landing.faq_a1": "Sett priser og multiplikatorer. Kunder får øyeblikkelig estimat.",
  "landing.faq_q2": "Er Inkvaluator gratis?",
  "landing.faq_a2": "Ja! Free-planen inkluderer en komplett kalkulator. Oppgrader til Pro for mer.",
  "landing.faq_q3": "Hvordan fungerer AI-skanneren?",
  "landing.faq_a3": "Last opp et bilde, AI analyserer størrelse, kompleksitet og stil på sekunder.",
  "landing.faq_q4": "Kan kunder kontakte meg?",
  "landing.faq_a4": "Ja, via WhatsApp med forhåndsutfylt melding.",
  "landing.faq_q5": "Flerspråklig støtte?",
  "landing.faq_a5": "17 språk og 13 valutaer — perfekt for internasjonale kunstnere.",
  "landing.faq_q6": "Hvordan får jeg flere kunder?",
  "landing.faq_a6": "Pro/Studio-kunstnere vises på verdenskartet og oppdagelsessiden.",
  "city.title": "Tatovører i {city}",
  "city.description": "Finn tatovører i {city}.",
  "city.no_artists": "Ingen kunstnere funnet i {city}.",
  "city.discover_all": "Alle kunstnere",
  "seo.landing_title": "Inkvaluator – Tattoo Priskalkulator",
  "seo.landing_desc": "Beregn tatoveringsprisen din øyeblikkelig.",
  "vault.seo_title": "Mitt Tattoo-hvelv | Inkvaluator",
  "vault.title": "Mitt",
  "vault.title_accent": "Tattoo-hvelv",
  "vault.desc": "Dine lagrede estimater.",
  "vault.empty": "Ingen estimater. Bruk en kalkulator!",
  "vault.save_btn": "Lagre",
  "vault.saved_title": "Lagret!",
  "vault.saved_desc": "Estimat lagret.",
  "success.active": "Aktiv — alt opplåst",
  "success.setup_whatsapp": "Konfigurer WhatsApp",
  "success.syncing": "Aktiverer abonnementet ditt…",
  "success.view_details": "Se detaljer",
  "analytics.title": "Analyser",
  "analytics.desc": "Kalkulatorbruken din",
  "analytics.total_calculations": "Totale beregninger",
  "analytics.conversion_rate": "Konverteringsrate",
  "analytics.this_month": "Denne måneden",
  "analytics.no_data": "Ingen data ennå.",
  "studio.title": "Studioledelse",
  "studio.team": "Team",
  "studio.invite": "Inviter kunstner",
  "studio.invite_desc": "Generer en invitasjonslenke.",
  "studio.generate_link": "Generer lenke",
  "studio.no_members": "Ingen medlemmer. Inviter kunstnere.",
  "color.gold": "Gull",
  "color.red": "Rød",
  "color.blue": "Blå",
  "color.green": "Grønn",
  "color.purple": "Lilla",
  "color.pink": "Rosa",
  "color.turquoise": "Turkis",
  "color.orange": "Oransje",
  "tip.title": "Anbefal en kunstner",
  "tip.desc": "Kjenner du en tatovør som burde vært på Inkvaluator?",
  "share.button": "Del",
  "sw.update_available": "Ny versjon tilgjengelig.",
  "embed.iframe_desc": "Lim dette inn i din HTML.",
  "embed.floating_desc": "Lim inn før </body>.",
  "embed.verified_badge": "Verifisert badge",
  "embed.badge_desc": "Plasser dette badget på nettsiden din.",
  "compare.title": "Prissammenligning",
  "compare.disclaimer": "Sammenligning basert på standard tatovering.",
  "landing.calculate_price": "Beregn pris",
  "landing.total_value_prefix": "Allerede",
  "landing.total_value_suffix": "i tatoveringskunst beregnet",
  "ai.desc": "Last opp et bilde og la AI bestemme innstillingene",
  "ai.scan_failed": "Skanning mislyktes",
  "ai.try_again": "Prøv igjen",
  "referral.title": "Anbefal en kollega",
  "referral.generate": "Generer henvisningslenke",
  "tooltip.design": "Kostnader for design og forberedelse av din unike tatovering.",
  "tooltip.deposit": "Depositumet sikrer avtalen din og trekkes fra totalprisen.",
  "pricing_edu.intro": "Prisen på en tatovering bestemmes av flere faktorer som reflekterer håndverk og tid.",
  "step.next": "Neste",
  "step.view_result": "Se pris",
  "step.details": "Detaljer",
  "step.details_desc": "Hvor detaljert er designet ditt?",
  "layout.stepper": "Steg for steg",
  "layout.form": "Alt på en gang",
  "calc.view_price_cta": "Se prisoversikt",
  "calc.rough_estimate": "Grovt estimat",
  "calc.calculated_price": "Beregnet pris",
  "calc.save_for_later": "Lagre til senere",
  "calc.share": "Del",
  "calc.more_options": "Flere alternativer",
  "calc.compare_configs": "Sammenlign konfigurasjoner",
  "calc.compare_desc": "Sammenlign to priskonfigurasjoner side ved side",
  "calc.no_photo": "Ingen referansefoto lagt til",
  "calc.link_copied": "Lenke kopiert!",
  "whatsapp.add_photo": "Legg til bildet ditt",
  "whatsapp.photos_label": "Referansebilder — maks. 3",
  "laf.reset_success": "Tema tilbakestilt til standard",
  "admin.stripe_managed":
    "Abonnementer administreres automatisk via Stripe — endringer er synlige etter neste webhook-synkronisering",
  "calc.view_tattoo_plan": "Se din Tattoo Plan ✓",
  "wa.plan_message": "Her er min Tattoo Plan:",
  "lead.tattoo_plan": "Tattoo Plan",
  "lead.made_with": "Laget med Inkvaluator",
  "lead.contact_artist": "Kontakt {artist}",
  "lead.share": "Del",
  "lead.share_title": "Min Tattoo Plan",
  "lead.link_copied": "Lenke kopiert!",
  "lead.deposit_label": "Depositum",
  "lead.pay_deposit": "Betal depositum {amount}",
  "lead.deposit_request_title": "Be om depositumsavtale",
  "lead.deposit_request_desc": "Spør kunstneren om et depositum på ~{amount} er mulig.",
  "lead.deposit_request_wa": "Send forespørsel via WhatsApp",
  "lead.for_you": "Deg",
  "lead.for_label": "For",
  "lead.back_to_calculator": "Tilbake til kalkulator",
  "lead.deposit_wa_msg":
    "Hei {artist}, jeg har sett på min Tattoo Plan på Inkvaluator. Ville et depositum på ~{amount} vært mulig?\nPlan: {url}",
  "blog.no_translation": "Ingen oversettelse tilgjengelig",
  "flash.tab": "Flash",
  "flash.upload": "Last opp flash-design",
  "flash.title": "Tittel",
  "flash.price": "Pris",
  "flash.size": "Størrelse (cm)",
  "flash.available": "Tilgjengelig",
  "flash.featured": "Fremhevet",
  "flash.limit_used": "av {max} flash-design brukt",
  "flash.delete_confirm": "Slette flash-design?",
  "flash.reserve_whatsapp": "Reserver via WhatsApp",
  "flash.whatsapp_msg": "Jeg er interessert i din flash-tatovering:",
  "flash.matching_designs": "Matchende flash-design",
  "flash.only_with_flash": "Kun artister med flash",
  "groei.score": "Profilpoeng",
  "groei.views": "profilvisninger",
  "groei.sessions": "kalkulatorøkter",
  "groei.leads": "leads",
  "groei.this_week": "denne uken",
  "groei.improve": "Forbedre profilen din",
  "groei.add_photos": "Legg til flere portfoliobilder",
  "groei.add_whatsapp": "Sett opp WhatsApp-nummeret ditt",
  "groei.add_bio": "Skriv en lengre bio",
  "groei.add_avatar": "Last opp et profilbilde",
  "groei.add_rate": "Sett timesatsen din",
  "groei.add_city": "Legg til byen din",
  "groei.get_leads": "Del kalkulatoren din for å få leads",
  "groei.collapse": "Skjul",
  "groei.expand": "Vis",
  "groei.tip_portfolio": "Artister med 6+ bilder får 3x flere økter",
  "groei.tip_whatsapp": "Kunder med WhatsApp-knapp konverterer 5x bedre",
  "groei.tip_leads": "Del kalkulatorlinken din for å motta din første lead",
  "groei.tip_avatar": "Et profilbilde øker din troverdighet",
  "groei.tip_action_photos": "Legg til bilder",
  "groei.tip_action_whatsapp": "Sett opp WhatsApp",
  "groei.tip_action_share": "Del kalkulator",
  "groei.tip_action_avatar": "Last opp bilde",
  "milestone.eerste_lead": "Første lead mottatt!",
  "milestone.tien_leads": "10 leads nådd!",
  "milestone.profiel_compleet": "Profil 100% komplett!",
  "milestone.eerste_flash": "Første flash-design lastet opp!",
  "wa.name_required": "Navn er påkrevd",
  "wa.plan_message_full": "Se min fullstendige Tattoo Plan: {url}",
};

// ─── FINNISH (complete) ──────────────────────────────────────────
const fi: Record<string, string> = {
  ...en,
  "app.tagline": "Laske tatuointisi hinta",
  back: "Takaisin",
  save: "Tallenna",
  copy: "Kopioi",
  copied: "Kopioitu!",
  loading: "Ladataan...",
  error: "Virhe",
  optional: "valinnainen",
  "landing.hero_title": "Laske",
  "landing.hero_title_accent": "Tatuointihintasi",
  "landing.hero_desc": "Selvitä sekunneissa, mitä seuraava tatuointisi maksaa.",
  "landing.try_it": "Kokeile!",
  "landing.estimated_price": "Arvioitu hinta",
  "landing.your_rates": "Hintasi",
  "landing.rates_desc": "Siirrä liukusäätimiä ja katso hinnan muuttuvan",
  "landing.hourly_rate": "Tuntihinta",
  "landing.start_cost": "Kiinteät aloituskustannukset",
  "landing.size_label": "Koko",
  "landing.complexity_label": "Monimutkaisuus",
  "landing.body_placement": "Sijoitus kehossa",
  "landing.find_artist": "Löydä täydellinen",
  "landing.find_artist_accent": "tatuoijasi",
  "landing.discover_all": "Kaikki tatuoijat",
  "landing.discover": "Tutustu",
  "landing.login": "Kirjaudu",
  "landing.founder_desc": "50% alennus ensimmäisille 50 rekisteröitymiselle",
  "landing.claim_now": "Lunasta nyt →",
  "calc.estimate": "Arvioitu hinta",
  "calc.size": "Koko",
  "calc.complexity": "Monimutkaisuus",
  "calc.body_part": "Kehon osa",
  "calc.style": "Tyyli",
  "calc.budget": "Budjetti",
  "calc.back_home": "Takaisin etusivulle",
  "size.mini": "Mini (< 5 cm)",
  "size.small": "Pieni (5-10 cm)",
  "size.medium": "Keskikokoinen (10-20 cm)",
  "size.large": "Suuri (20-35 cm)",
  "size.xl": "Erittäin suuri (35+ cm)",
  "complexity.simple": "Yksinkertainen",
  "complexity.medium": "Keskivaikea",
  "complexity.detailed": "Yksityiskohtainen",
  "body.arm": "Käsivarsi",
  "body.back": "Selkä",
  "body.chest": "Rinta",
  "body.neck": "Niska",
  "body.face": "Kasvot",
  "body.shoulder": "Olkapää",
  "body.hand": "Käsi",
  "body.foot": "Jalka",
  "body.other": "Muu",
  "body.front": "Edestä",
  "body.back_view": "Takaa",
  "body.front_view": "Edestä",
  "body.back_view_btn": "Takaa",
  "style.black_grey": "Musta & Harmaa",
  "style.full_color": "Täysväri",
  "style.realism": "Realismi",
  "style.fine_line": "Ohut viiva / Minimaalinen",
  "style.traditional": "Perinteinen",
  "style.neo_traditional": "Uusperinteinen",
  "style.watercolor": "Vesiväri",
  "style.tribal": "Tribal",
  "style.japanese": "Japanilainen",
  "style.geometric": "Geometrinen",
  "style.lettering": "Lettering",
  "style.script": "Käsikirjoitus",
  "style.dotwork": "Dotwork",
  "style.mandala": "Mandala",
  "style.blackwork": "Blackwork",
  "style.illustrative": "Kuvituksellinen",
  "style.surrealism": "Surrealismi",
  "style.ornamental": "Koristeellinen",
  "style.micro_realism": "Mikrorealismi",
  "style.trash_polka": "Trash Polka",
  "style.other": "Muu",
  "upgrade.choose": "Valitse",
  "upgrade.plan_word": "Paketti",
  "upgrade.per_month": "/kk",
  "upgrade.test_account_active": "Testitili — ominaisuudet aktivoitu",
  "auth.login": "Kirjaudu",
  "auth.register": "Rekisteröidy",
  "dash.logout": "Kirjaudu ulos",
  "dash.saved": "Tallennettu!",
  "dash.save_changes": "Tallenna muutokset",
  "dash.cancel": "Peruuta",
  "dash.add": "Lisää",
  "dash.delete": "Poista",
  "footer.tagline": "Älykkäin tatuointihintalaskuri.",
  "footer.language_label": "Kieli",
  "footer.all_rights": "Kaikki oikeudet pidätetään.",
  "footer.install_title": "Asenna",
  "footer.install_ok": "Selvä!",
  "footer.terms": "Käyttöehdot",
  "footer.support": "Tuki",
  "footer.how_it_works": "Miten se toimii",
  "success.welcome": "Tervetuloa klubiin,",
  "success.go_dashboard": "Siirry hallintapaneeliin",
  "hiw.seo_title": "Miten se toimii – Inkvaluator",
  "hiw.hero_title": "Keskity musteeseen, älä laskuun",
  "hiw.faq_title": "Usein kysytyt kysymykset",
  "hiw.cta_title": "Mitä odotat?",
  "hiw.cta_accent": "Lopeta laskeminen, aloita luominen.",
  "hiw.cta_button": "Kokeile Inkvaluatoria ilmaiseksi",
  "vault.status_concept": "Luonnos",
  "vault.status_sent": "Pyyntö lähetetty",
  "reviews.rating": "arvosana",
  "reviews.verified_reviews": "vahvistetut arvostelut",
  "reviews.read_all": "Lue kaikki arvostelut Googlessa",
  "discover.swipe": "pyyhkäise",
  "vault.compare": "Vertaa",
  "trends.title": "Tatuointi",
  "trends.title_accent": "Trendit",
  "trends.top_styles": "Suositut tyylit",
  "trends.top_body_parts": "Suositut kehonosat",
  "match.title": "Sopivat tatuoijat lähellä",
  "match.score": "Osuma",
  "match.view": "Näytä profiili",
  "urgency.recent_requests": "pyyntöjä tällä viikolla",
  "urgency.wait_time": "Arvioitu odotusaika",
  "urgency.weeks": "viikkoa",
  "urgency.high_demand": "Suuri kysyntä",
  "urgency.popular": "Suosittu",
  "benchmark.above_market": "keskiarvon yläpuolella",
  "benchmark.below_market": "keskiarvon alapuolella",
  "benchmark.at_market": "Keskiarvossa",
  "benchmark.market_avg": "Markkinakeskiarvo",
  "portfolio.style_examples": "Tyyliesimerkkejä",
  "story.title": "Instagram Story -generaattori",
  "story.desc": "Luo premium Black & Gold -story-kuva QR-koodillasi.",
  "wa.title": "Lähetä artistille WhatsAppilla",
  "wa.subtitle": "Lähetä arvio suoraan",
  "wa.name": "Nimesi (valinnainen)",
  "wa.name_placeholder": "Nimesi",
  "wa.message": "Viesti (valinnainen)",
  "wa.message_placeholder": "Kuvaile ideaasi, inspiraatiota tai yksityiskohtia...",
  "wa.send": "Lähetä WhatsAppilla",
  "wa.sent_title": "Viesti lähetetty!",
  "wa.sent_desc": "Pyyntösi on lähetetty osoitteeseen {name} WhatsAppilla.",
  "wa.success": "Lähetetty!",
  "wa.error": "Jotain meni pieleen",
  "wa.msg_greeting": "Hei {name}, laskin hinta-arvion:",
  "wa.msg_name_label": "Nimi",
  "wa.msg_style_label": "🎨 Tyyli",
  "wa.msg_bodypart_label": "📍 Kehonosa",
  "wa.msg_size_label": "📏 Koko",
  "wa.msg_complexity_label": "⚙️ Monimutkaisuus",
  "wa.msg_time_label": "⏱ Arvioitu aika",
  "wa.msg_total_label": "💰 Yhteensä",
  "wa.msg_deposit_label": "💳 Vakuus",
  "wa.msg_details_label": "🔗 Tiedot",
  "wa.template_high_greeting": "Hei {name}, hyviä uutisia! Laskin yksityiskohtaisen arvion:",
  "wa.template_low_greeting": "Hei {name}, olen kiinnostunut tatuoinnista. Kertoisitko saatavuudesta ja hinnoista?",
  "tooltip.labor": "Perustuu tälle koolle arvioituun aikaan kerrottuna artistin tuntihinnalla.",
  "tooltip.complexity": "Monimutkaisemmat mallit vaativat enemmän aikaa ja tarkkuutta.",
  "tooltip.placement": "Jotkut kehonosat ovat herkempiä tai vaikeammin saavutettavia.",
  "tooltip.style": "Eri tyylit vaativat erityistekniikoita, jotka vaikuttavat hintaan.",
  "pricing_edu.button": "Miten tämä laskelma toimii?",
  "pricing_edu.title": "Miten hinta lasketaan",
  "pricing_edu.disclaimer": "Tämä on aina arvio. Lopullinen hinta määräytyy henkilökohtaisen konsultoinnin jälkeen.",
  "ai.title": "AI-skanneri",
  "ai.analyzing": "AI analysoi...",
  "ai.scan_complete": "✨ AI-skannaus valmis",
  "pwa.install_title": "Asenna sovelluksena",
  "pwa.install_button": "Asenna nyt",
  "story.generate": "Luo Story",
  "story.regenerate": "Luo uudelleen",
  "story.download": "Lataa",
  "story.share": "Jaa",
  "story.downloaded": "Story ladattu!",
  "story.cta_line1": "Laske Hintasi",
  "story.cta_line2": "Skannaa QR-koodi alla",
  "story.scan_prompt": "↑ Skannaa hinta-arviosi",
  "trends.filter_country": "Suodata maittain",
  "trends.all_countries": "Kaikki maat",
  "trends.analyzed": "laskelmaa analysoitu",
  "trends.realtime": "Reaaliaikainen päivitys",
  "match.style_experts": "{style}-asiantuntijat lähellä paikkaa {city}",
  "vault.review_booster_title": "Tatuointi tehty? Auta tatuoijaasi!",
  "vault.review_booster_desc": "Jätä Google-arvostelu ja auta muita löytämään tämä tatuoija.",
  "vault.review_booster_btn": "Kirjoita Google-arvostelu",
  "checklist.title": "Vahvistustila",
  "checklist.profile_complete": "Profiili valmis",
  "checklist.google_linked": "Google Place ID yhdistetty",
  "checklist.badge_detected": "Vahvistettu badge verkkosivulla",
  "checklist.push_enabled": "Push-ilmoitukset käytössä",
  "checklist.not_yet": "Ei vielä",
  "checklist.active": "Aktiivinen",
  "lead.status_new": "Uusi",
  "lead.status_contacted": "Yhteydessä",
  "lead.status_booked": "Varattu",
  "lead.change_status": "Tila",
  "lead.add_price": "Lisää lopullinen hinta",
  "push.test_body": "Push-ilmoitukset ovat KÄYTÖSSÄ! Saat nyt hälytykset uusista liideistä.",
  "push.send_test": "Lähetä testi-ilmoitus",
  "push.active_desc": "Saat ilmoitukset, kun laadukkaita liidejä saapuu.",
  "push.blocked": "Ilmoitukset on estetty selaimen asetuksissa.",
  "push.enable_desc": "Saa ilmoituksia, kun laadukkaita liidejä saapuu.",
  "push.label": "Push-ilmoitukset",
  "push.welcome_body": "Tervetuloa! Saat nyt hälytykset laadukkaista liideistä.",
  "push.denied_info": "Pysy ajan tasalla uusista liideistä — Ota ilmoitukset käyttöön selaimen asetuksissa.",
  "vault.live_status": "Live-tila",
  "vault.status_new_desc": "Pyyntösi on vastaanotettu",
  "vault.status_contacted_desc": "Tatuoija on nähnyt viestisi",
  "vault.status_booked_desc": "Varauksesi on vahvistettu!",
  "vault.link_copied": "🔗 Linkki kopioitu!",
  "analytics.actual_revenue": "Todellinen Liikevaihto",
  "style_city.popular_styles": "Suositut tyylit paikkakunnalla {city}",
  "nav.home": "Etusivu",
  "lead.estimate_header": "Tatuointiarvio",
  "discover.verified_badge": "VAHVISTETTU",
  // Batch 3+4
  "tab.price_engine": "Hintamoottori",
  "tab.price_engine_desc": "Hallitse kaikkia hintakertoimia: koot, monimutkaisuus, kehonosat ja tyylit.",
  "basic.desc": "Aseta vakiohinnat ja kustannukset",
  "basic.min_price_desc": "Alin veloittamasi hinta",
  "basic.hourly_rate_desc": "Vakiotuntihintasi",
  "basic.daily_rate_placeholder": "Jätä tyhjäksi jos ei sovelleta",
  "basic.percentage": "%",
  "basic.fixed_amount": "Kiinteä",
  "sizes.title": "Kokoasetukset",
  "sizes.desc": "Aseta arvioitu aika kokokategorioittain",
  "sizes.min_hours": "Min. tuntia",
  "sizes.max_hours": "Maks. tuntia",
  "sizes.hours": "tuntia",
  "complexity.title": "Monimutkaisuuskertoimet",
  "complexity.desc": "Säädä hintakerrointa monimutkaisuuden mukaan",
  "bodyparts.title": "Kehonosakertoimet",
  "bodyparts.desc": "Jotkut kehonosat ovat vaikeampia tatuoida",
  "styles.title": "Tyylien hallinta",
  "styles.desc": "Ota tyylejä käyttöön/poista käytöstä ja säädä hintakerrointa.",
  "theme.title": "Väriteema",
  "theme.desc": "Mukauta laskurin värit",
  "theme.preview": "Esikatselu",
  "theme.preview_desc": "Näin laskurisi näyttää",
  "theme.hue": "Sävy",
  "theme.saturation": "Kylläisyys",
  "theme.lightness": "Kirkkaus",
  "theme.presets": "Pikavalinta",
  "branding.title": "Brändi",
  "branding.desc": "Lataa logo, valitse kieli ja valuutta",
  "branding.logo": "Logo",
  "branding.logo_desc": "Studion logo (maks. 2 Mt, PNG tai JPG)",
  "branding.upload": "Lataa logo",
  "branding.remove": "Poista",
  "branding.language": "Laskurin kieli",
  "branding.currency": "Valuutta",
  "branding.qr_code": "QR-koodi",
  "branding.qr_desc": "Asiakkaat skannaavat tämän avatakseen laskurisi.",
  "branding.download_qr": "Lataa QR",
  "onboard.step_minimum": "Vähimmäishinta",
  "onboard.step_hourly": "Tuntihinta",
  "onboard.step_sizes": "Vahvista koot",
  "onboard.step_styles": "Aseta tyylit",
  "onboard.step_logo": "Lataa logo",
  "onboard.step_finish": "Laskuri valmis!",
  "dash.onboard_min_desc": "Aseta vähimmäishintasi.",
  "dash.onboard_hourly_desc": "Aseta tuntihintasi. Se on kaikkien laskelmien perusta.",
  "dash.onboard_sizes_desc": "Oletuskokokategoriat on määritetty. Muokkaa myöhemmin.",
  "dash.onboard_styles_desc": "Kaikki yleisimmät tyylit ovat käytössä. Ota käyttöön/poista hallintapaneelissa.",
  "dash.onboard_logo_desc": "Lataa logo nyt tai myöhemmin Brändi-välilehdeltä.",
  "dash.onboard_logo_later": "Lataa myöhemmin: Hallintapaneeli → Brändi",
  "dash.onboard_finish_title": "Laskurisi on valmis!",
  "dash.onboard_finish_desc": "Jaa ainutlaatuinen linkkisi. Muuta asetuksia milloin tahansa.",
  "dash.your_link": "Laskurilinkkisi:",
  "dash.online_card": "Online-käyntikorttisi.",
  "dash.view_my_calculator": "Näytä laskurini",
  "dash.upload_thumbnail": "Oma kuva",
  "dash.reset_to_default": "Palauta",
  "dash.reset_all_thumbnails": "Palauta kaikki",
  "dash.thumbnail_uploaded": "Kuva ladattu!",
  "dash.thumbnail_reset": "Kuva palautettu",
  "dash.thumbnails_reset_all": "Kaikki kuvat palautettu",
  "dash.invite_created": "Kutsu luotu!",
  "dash.invite_error": "Kutsuvirhe",
  "dash.member_removed": "Jäsen poistettu",
  "dash.link_copied_generic": "Linkki kopioitu!",
  "dash.profile_saved": "Profiili tallennettu!",
  "dash.profile_save_error": "Tallennusvirhe",
  "dash.calculator_ready": "🎉 Laskuri valmis!",
  "dash.share_with_clients": "Jaa linkkisi.",
  "dash.step_of": "Vaihe {current} / {total}",
  "dash.start_inkprice": "Aloita InkPricellä",
  "laf.title": "Ulkoasu",
  "laf.desc": "Muokkaa julkisen laskurin ulkoasua.",
  "laf.color_mode": "Väritila",
  "laf.dark_mode": "Tumma tila",
  "laf.light_mode": "Vaalea tila",
  "laf.background": "Tausta",
  "laf.solid_color": "Yksivärinen",
  "laf.image_url": "Kuvan URL",
  "laf.preview": "Esikatselu",
  "laf.saved": "Ulkoasu tallennettu",
  "laf.presets": "Premium-teemat",
  "laf.font_selector": "Laskurin fontti",
  "laf.overlay_opacity": "Peittokuvan läpinäkyvyys",
  "laf.bg_fixed": "Kiinteä (Parallax)",
  "laf.bg_scroll": "Vieritys",
  "consult_settings.title": "Konsultointiasetukset",
  "consult_settings.desc": "Määritä yhteydenottotavat.",
  "consult_settings.booking_url_label": "Varaus-URL",
  "consult_settings.email_label": "Konsultointisähköposti",
  "consult_settings.channels_title": "Liidikanavat",
  "consult_settings.channels_desc": "Näkyvät yhteydenottotavat.",
  "help.min_price": "Absoluuttinen vähimmäishinta per istunto.",
  "help.hourly_rate": "Tuntihintasi on kaikkien laskelmien perusta.",
  "help.multipliers": "Kertoimet säätävät hintaa tyylin, monimutkaisuuden ja sijainnin mukaan.",
  "help.sizes": "Aseta aikaväli kullekin koolle.",
  "help.complexity": "Säädä monimutkaisuuden kertoimia.",
  "help.body_parts": "Jotkut alueet vaativat korkeampia kertoimia.",
  "help.styles": "Ota tyylit käyttöön/poista ja aseta kertoimet.",
  "help.logo": "Logosi näkyy laskurissa ja hakemistossa. Maks. 2 Mt.",
  "help.currency": "Laskurissa näytettävä valuutta.",
  "help.calculator_language": "Julkisen laskurin kieli.",
  "help.dashboard_language": "Tämän hallintapaneelin kieli — näkyy vain sinulle.",
  "profile.mfa_title": "Tilin turvallisuus",
  "profile.mfa_desc": "Suojaa tilisi 2FA:lla.",
  "profile.mfa_enable": "Ota 2FA käyttöön",
  "profile.mfa_verify": "Vahvista",
  "profile.mfa_active": "Aktiivinen",
  "profile.mfa_enabled_success": "✅ 2FA otettu käyttöön!",
  "profile.google_reviews": "Google-arvostelut",
  "profile.google_reviews_desc": "Yhdistä Google Business -profiilisi.",
  "profile.sync_now": "Synkronoi",
  "profile.sync_success": "Google-arvostelut synkronoitu!",
  "landing.faq_title": "UKK",
  "landing.faq_subtitle": "Kaikki Inkvaluatorista",
  "landing.faq_q1": "Miten laskuri toimii?",
  "landing.faq_a1": "Aseta hinnat ja kertoimet. Asiakkaat saavat välittömän arvion.",
  "landing.faq_q2": "Onko Inkvaluator ilmainen?",
  "landing.faq_a2": "Kyllä! Free-paketti sisältää täydellisen laskurin. Päivitä Prohon saadaksesi lisää.",
  "landing.faq_q3": "Miten AI-skanneri toimii?",
  "landing.faq_a3": "Lataa kuva, AI analysoi koon, monimutkaisuuden ja tyylin sekunneissa.",
  "landing.faq_q4": "Voivatko asiakkaat ottaa yhteyttä?",
  "landing.faq_a4": "Kyllä, WhatsAppilla esitäytetyllä viestillä.",
  "landing.faq_q5": "Monikielinen tuki?",
  "landing.faq_a5": "17 kieltä ja 13 valuuttaa — täydellinen kansainvälisille tatuoijille.",
  "landing.faq_q6": "Miten saan lisää asiakkaita?",
  "landing.faq_a6": "Pro/Studio-tatuoijat näkyvät maailmankartalla ja löytösivulla.",
  "city.title": "Tatuoijat kaupungissa {city}",
  "city.description": "Löydä tatuoijia kaupungissa {city}.",
  "city.no_artists": "Ei tatuoijia kaupungissa {city}.",
  "city.discover_all": "Kaikki tatuoijat",
  "seo.landing_title": "Inkvaluator – Tatuointihintalaskuri",
  "seo.landing_desc": "Laske tatuointisi hinta välittömästi.",
  "vault.seo_title": "Tatuointiholvini | Inkvaluator",
  "vault.title": "Tatuointi-",
  "vault.title_accent": "holvini",
  "vault.desc": "Tallennetut arviosi.",
  "vault.empty": "Ei arvioita. Käytä laskuria!",
  "vault.save_btn": "Tallenna",
  "vault.saved_title": "Tallennettu!",
  "vault.saved_desc": "Arvio tallennettu.",
  "success.active": "Aktiivinen — kaikki avattu",
  "success.setup_whatsapp": "Määritä WhatsApp",
  "success.syncing": "Tilaustasi aktivoidaan…",
  "success.view_details": "Näytä tiedot",
  "analytics.title": "Analytiikka",
  "analytics.desc": "Laskurin käyttösi",
  "analytics.total_calculations": "Laskelmat yhteensä",
  "analytics.conversion_rate": "Konversioaste",
  "analytics.this_month": "Tässä kuussa",
  "analytics.no_data": "Ei dataa vielä.",
  "studio.title": "Studion hallinta",
  "studio.team": "Tiimi",
  "studio.invite": "Kutsu tatuoija",
  "studio.invite_desc": "Luo kutsulinkki.",
  "studio.generate_link": "Luo linkki",
  "studio.no_members": "Ei jäseniä. Kutsu tatuoijia.",
  "color.gold": "Kulta",
  "color.red": "Punainen",
  "color.blue": "Sininen",
  "color.green": "Vihreä",
  "color.purple": "Violetti",
  "color.pink": "Pinkki",
  "color.turquoise": "Turkoosi",
  "color.orange": "Oranssi",
  "tip.title": "Suosittele tatuoijaa",
  "tip.desc": "Tunnetko tatuoijan joka pitäisi olla Inkvaluatorissa?",
  "share.button": "Jaa",
  "sw.update_available": "Uusi versio saatavilla.",
  "embed.iframe_desc": "Liitä tämä HTML-koodiisi.",
  "embed.floating_desc": "Liitä ennen </body>-tagia.",
  "embed.verified_badge": "Vahvistettu badge",
  "embed.badge_desc": "Sijoita tämä badge verkkosivullesi.",
  "compare.title": "Hintavertailu",
  "compare.disclaimer": "Vertailu perustuu vakiotatuointiin.",
  "landing.calculate_price": "Laske hinta",
  "landing.total_value_prefix": "Jo",
  "landing.total_value_suffix": "tatuointitaidetta laskettu",
  "ai.desc": "Lataa kuva ja anna AI:n määrittää asetukset",
  "ai.scan_failed": "Skannaus epäonnistui",
  "ai.try_again": "Yritä uudelleen",
  "referral.title": "Suosittele kollegaa",
  "referral.generate": "Luo suosittelulinkki",
  "tooltip.design": "Kustannukset ainutlaatuisen tatuoinnin suunnitteluun ja valmisteluun.",
  "tooltip.deposit": "Vakuusmaksu varaa tapaamisen ja vähennetään kokonaishinnasta.",
  "pricing_edu.intro":
    "Tatuoinnin hinta määräytyy useiden tekijöiden perusteella, jotka heijastavat käsityötaitoa ja aikaa.",
  "step.next": "Jatka",
  "step.view_result": "Näytä hinta",
  "step.details": "Yksityiskohdat",
  "step.details_desc": "Kuinka yksityiskohtainen suunnitelmasi on?",
  "layout.stepper": "Askel askeleelta",
  "layout.form": "Kaikki kerralla",
  "calc.view_price_cta": "Näytä hintayhteenveto",
  "calc.rough_estimate": "Karkea arvio",
  "calc.calculated_price": "Laskettu hinta",
  "calc.save_for_later": "Tallenna myöhemmäksi",
  "calc.share": "Jaa",
  "calc.more_options": "Lisää vaihtoehtoja",
  "calc.compare_configs": "Vertaa kokoonpanoja",
  "calc.compare_desc": "Vertaa kahta hintakokoonpanoa vierekkäin",
  "calc.no_photo": "Viitekuvaa ei lisätty",
  "calc.link_copied": "Linkki kopioitu!",
  "whatsapp.add_photo": "Lisää valokuvasi",
  "whatsapp.photos_label": "Viitekuvat — enint. 3",
  "laf.reset_success": "Teema palautettu oletusasetuksiin",
  "admin.stripe_managed":
    "Tilauksia hallitaan automaattisesti Stripen kautta — muutokset näkyvät seuraavan webhook-synkronoinnin jälkeen",
  "calc.view_tattoo_plan": "Katso Tattoo Plan ✓",
  "wa.plan_message": "Tässä on Tattoo Planini:",
  "lead.tattoo_plan": "Tattoo Plan",
  "lead.made_with": "Tehty Inkvaluatorilla",
  "lead.contact_artist": "Ota yhteyttä {artist}",
  "lead.share": "Jaa",
  "lead.share_title": "Minun Tattoo Plan",
  "lead.link_copied": "Linkki kopioitu!",
  "lead.deposit_label": "Ennakkomaksu",
  "lead.pay_deposit": "Maksa ennakkomaksu {amount}",
  "lead.deposit_request_title": "Pyydä ennakkomaksujärjestelyä",
  "lead.deposit_request_desc": "Kysy taiteilijalta, onko ~{amount} ennakkomaksu mahdollinen.",
  "lead.deposit_request_wa": "Lähetä pyyntö WhatsAppilla",
  "lead.for_you": "Sinulle",
  "lead.for_label": "",
  "lead.back_to_calculator": "Takaisin laskuriin",
  "lead.deposit_wa_msg":
    "Hei {artist}, katsoin Tattoo Plan -suunnitelmaani Inkvaluatorissa. Olisiko ~{amount} ennakkomaksu mahdollinen?\nSuunnitelma: {url}",
  "blog.no_translation": "Käännöstä ei saatavilla",
  "flash.tab": "Flash",
  "flash.upload": "Lataa flash-design",
  "flash.title": "Otsikko",
  "flash.price": "Hinta",
  "flash.size": "Koko (cm)",
  "flash.available": "Saatavilla",
  "flash.featured": "Suositeltu",
  "flash.limit_used": "{max} flash-designista käytetty",
  "flash.delete_confirm": "Poista flash-design?",
  "flash.reserve_whatsapp": "Varaa WhatsAppin kautta",
  "flash.whatsapp_msg": "Olen kiinnostunut flash-tatuoinnistasi:",
  "flash.matching_designs": "Sopivat flash-designit",
  "flash.only_with_flash": "Vain artistit joilla on flash",
  "groei.score": "Profiilipistemäärä",
  "groei.views": "profiilinäkymät",
  "groei.sessions": "laskuriistunnot",
  "groei.leads": "liidit",
  "groei.this_week": "tällä viikolla",
  "groei.improve": "Paranna profiiliasi",
  "groei.add_photos": "Lisää portfoliokuvia",
  "groei.add_whatsapp": "Aseta WhatsApp-numerosi",
  "groei.add_bio": "Kirjoita pidempi kuvaus",
  "groei.add_avatar": "Lataa profiilikuva",
  "groei.add_rate": "Aseta tuntihintasi",
  "groei.add_city": "Lisää kaupunkisi",
  "groei.get_leads": "Jaa laskurisi saadaksesi liidejä",
  "groei.collapse": "Pienennä",
  "groei.expand": "Laajenna",
  "groei.tip_portfolio": "Artistit, joilla on 6+ kuvaa, saavat 3x enemmän istuntoja",
  "groei.tip_whatsapp": "WhatsApp-painikkeella varustetut asiakkaat konvertoivat 5x paremmin",
  "groei.tip_leads": "Jaa laskurilinkki saadaksesi ensimmäisen liidin",
  "groei.tip_avatar": "Profiilikuva lisää uskottavuuttasi",
  "groei.tip_action_photos": "Lisää kuvia",
  "groei.tip_action_whatsapp": "Aseta WhatsApp",
  "groei.tip_action_share": "Jaa laskuri",
  "groei.tip_action_avatar": "Lataa kuva",
  "milestone.eerste_lead": "Ensimmäinen liidi vastaanotettu!",
  "milestone.tien_leads": "10 liidiä saavutettu!",
  "milestone.profiel_compleet": "Profiili 100% valmis!",
  "milestone.eerste_flash": "Ensimmäinen flash-suunnittelu ladattu!",
  "wa.name_required": "Nimi on pakollinen",
  "wa.plan_message_full": "Katso koko Tattoo Plan: {url}",
};

// ─── CZECH (complete) ──────────────────────────────────────────
const cs: Record<string, string> = {
  ...en,
  "app.tagline": "Vypočítejte cenu tetování",
  back: "Zpět",
  save: "Uložit",
  copy: "Kopírovat",
  copied: "Zkopírováno!",
  loading: "Načítání...",
  error: "Chyba",
  optional: "volitelné",
  "landing.hero_title": "Vypočítejte svou",
  "landing.hero_title_accent": "Cenu Tetování",
  "landing.hero_desc": "Zjistěte během sekund, kolik stojí vaše další tetování.",
  "landing.try_it": "Vyzkoušejte!",
  "landing.estimated_price": "Odhadovaná cena",
  "landing.your_rates": "Vaše sazby",
  "landing.rates_desc": "Posouvejte posuvníky a sledujte změnu ceny",
  "landing.hourly_rate": "Hodinová sazba",
  "landing.start_cost": "Fixní startovní náklady",
  "landing.size_label": "Velikost",
  "landing.complexity_label": "Složitost",
  "landing.body_placement": "Umístění na těle",
  "landing.find_artist": "Najděte svého",
  "landing.find_artist_accent": "umělce",
  "landing.discover_all": "Všichni umělci",
  "landing.discover": "Objevte",
  "landing.login": "Přihlásit se",
  "landing.founder_desc": "50% sleva pro prvních 50 registrací",
  "landing.claim_now": "Získat nyní →",
  "calc.estimate": "Odhadovaná cena",
  "calc.size": "Velikost",
  "calc.complexity": "Složitost",
  "calc.body_part": "Část těla",
  "calc.style": "Styl",
  "calc.budget": "Rozpočet",
  "calc.back_home": "Zpět na hlavní stránku",
  "size.mini": "Mini (< 5 cm)",
  "size.small": "Malé (5-10 cm)",
  "size.medium": "Střední (10-20 cm)",
  "size.large": "Velké (20-35 cm)",
  "size.xl": "Extra Velké (35+ cm)",
  "complexity.simple": "Jednoduché",
  "complexity.medium": "Střední",
  "complexity.detailed": "Detailní",
  "body.arm": "Paže",
  "body.back": "Záda",
  "body.chest": "Hrudník",
  "body.neck": "Krk",
  "body.face": "Obličej",
  "body.shoulder": "Rameno",
  "body.hand": "Ruka",
  "body.foot": "Noha",
  "body.other": "Ostatní",
  "body.front": "Přední",
  "body.back_view": "Zadní",
  "body.front_view": "Přední",
  "body.back_view_btn": "Zadní",
  "style.black_grey": "Černá & Šedá",
  "style.full_color": "Plnobarevný",
  "style.realism": "Realismus",
  "style.fine_line": "Jemná linka / Minimální",
  "style.traditional": "Tradiční",
  "style.neo_traditional": "Neo Tradiční",
  "style.watercolor": "Akvarel",
  "style.tribal": "Tribal",
  "style.japanese": "Japonský",
  "style.geometric": "Geometrický",
  "style.lettering": "Lettering",
  "style.script": "Písmo",
  "style.dotwork": "Dotwork",
  "style.mandala": "Mandala",
  "style.blackwork": "Blackwork",
  "style.illustrative": "Ilustrativní",
  "style.surrealism": "Surrealismus",
  "style.ornamental": "Ornamentální",
  "style.micro_realism": "Mikro realismus",
  "style.trash_polka": "Trash Polka",
  "style.other": "Ostatní",
  "upgrade.choose": "Vyberte svůj",
  "upgrade.plan_word": "Plán",
  "upgrade.per_month": "/měs",
  "upgrade.test_account_active": "Testovací účet — funkce aktivovány",
  "auth.login": "Přihlásit se",
  "auth.register": "Registrovat",
  "dash.logout": "Odhlásit se",
  "dash.saved": "Uloženo!",
  "dash.save_changes": "Uložit změny",
  "dash.cancel": "Zrušit",
  "dash.add": "Přidat",
  "dash.delete": "Smazat",
  "footer.tagline": "Nejchytřejší kalkulačka cen tetování.",
  "footer.language_label": "Jazyk",
  "footer.all_rights": "Všechna práva vyhrazena.",
  "footer.install_title": "Nainstalovat",
  "footer.install_ok": "Rozumím!",
  "footer.terms": "Podmínky služby",
  "footer.support": "Podpora",
  "footer.how_it_works": "Jak to funguje",
  "success.welcome": "Vítejte v klubu,",
  "success.go_dashboard": "Přejít na panel",
  "hiw.seo_title": "Jak to funguje – Inkvaluator",
  "hiw.hero_title": "Soustřeďte se na inkoust, ne na fakturu",
  "hiw.faq_title": "Často kladené otázky",
  "hiw.cta_title": "Na co čekáte?",
  "hiw.cta_accent": "Přestaňte počítat, začněte tvořit.",
  "hiw.cta_button": "Vyzkoušejte Inkvaluator zdarma",
  "vault.status_concept": "Koncept",
  "vault.status_sent": "Žádost odeslána",
  "reviews.rating": "hodnocení",
  "reviews.verified_reviews": "ověřených recenzí",
  "reviews.read_all": "Přečíst všechny recenze na Google",
  "discover.swipe": "přejeďte",
  "vault.compare": "Porovnat",
  "trends.title": "Tetování",
  "trends.title_accent": "Trendy",
  "trends.top_styles": "Populární styly",
  "trends.top_body_parts": "Populární části těla",
  "match.title": "Odpovídající umělci poblíž",
  "match.score": "Shoda",
  "match.view": "Zobrazit profil",
  "urgency.recent_requests": "žádostí tento týden",
  "urgency.wait_time": "Odhadovaná čekací doba",
  "urgency.weeks": "týdnů",
  "urgency.high_demand": "Vysoká poptávka",
  "urgency.popular": "Oblíbené",
  "benchmark.above_market": "nad průměrem trhu",
  "benchmark.below_market": "pod průměrem trhu",
  "benchmark.at_market": "Na průměru trhu",
  "benchmark.market_avg": "Průměr trhu",
  "portfolio.style_examples": "Příklady stylu",
  "story.title": "Generátor Instagram Stories",
  "story.desc": "Vytvořte prémiový Black & Gold story s QR kódem.",
  "story.generate": "Generovat Story",
  "story.regenerate": "Přegenerovat",
  "story.download": "Stáhnout",
  "story.share": "Sdílet",
  "story.downloaded": "Story stažena!",
  "story.cta_line1": "Vypočítejte Cenu",
  "story.cta_line2": "Naskenujte QR kód níže",
  "story.scan_prompt": "↑ Naskenujte pro odhad ceny",
  "trends.filter_country": "Filtrovat podle země",
  "trends.all_countries": "Všechny země",
  "trends.analyzed": "výpočtů analyzováno",
  "trends.realtime": "Aktualizováno v reálném čase",
  "match.style_experts": "Odborníci na {style} v blízkosti {city}",
  "vault.review_booster_title": "Tetování hotové? Pomozte svému umělci!",
  "vault.review_booster_desc": "Zanechte recenzi na Google a pomozte ostatním najít tohoto umělce.",
  "vault.review_booster_btn": "Napsat recenzi na Google",
  "checklist.title": "Stav ověření",
  "checklist.profile_complete": "Profil dokončen",
  "checklist.google_linked": "Google Place ID propojeno",
  "checklist.badge_detected": "Ověřený badge na webu",
  "checklist.push_enabled": "Push notifikace zapnuty",
  "checklist.not_yet": "Zatím ne",
  "checklist.active": "Aktivní",
  "lead.status_new": "Nový",
  "lead.status_contacted": "V kontaktu",
  "lead.status_booked": "Rezervováno",
  "lead.change_status": "Stav",
  "lead.add_price": "Přidat konečnou cenu",
  "push.test_body": "Push notifikace jsou AKTIVNÍ! Budete dostávat upozornění na nové zájemce.",
  "push.send_test": "Odeslat testovací notifikaci",
  "push.active_desc": "Dostáváte upozornění na kvalitní zájemce.",
  "push.blocked": "Notifikace jsou zablokovány v nastavení prohlížeče.",
  "push.enable_desc": "Dostávejte upozornění, když přijdou kvalitní zájemci.",
  "push.label": "Push notifikace",
  "push.welcome_body": "Vítejte! Budete dostávat upozornění na kvalitní zájemce.",
  "push.denied_info": "Buďte informováni o nových zájemcích — Povolte notifikace v nastavení prohlížeče.",
  "vault.live_status": "Živý stav",
  "vault.status_new_desc": "Váš požadavek byl přijat",
  "vault.status_contacted_desc": "Umělec viděl vaši zprávu",
  "vault.status_booked_desc": "Vaše schůzka je potvrzena!",
  "vault.link_copied": "🔗 Odkaz zkopírován!",
  "analytics.actual_revenue": "Skutečný Příjem",
  "style_city.popular_styles": "Populární styly v {city}",
  "nav.home": "Domů",
  "lead.estimate_header": "Odhad Tetování",
  "discover.verified_badge": "OVĚŘENO",
  "wa.title": "Odeslat umělci přes WhatsApp",
  "wa.subtitle": "Pošlete svůj odhad přímo",
  "wa.name": "Vaše jméno (volitelné)",
  "wa.name_placeholder": "Vaše jméno",
  "wa.message": "Zpráva (volitelné)",
  "wa.message_placeholder": "Popište svůj nápad, inspiraci nebo podrobnosti...",
  "wa.send": "Odeslat přes WhatsApp",
  "wa.sent_title": "Zpráva odeslána!",
  "wa.sent_desc": "Váš požadavek byl odeslán {name} přes WhatsApp.",
  "wa.success": "Odesláno!",
  "wa.error": "Něco se pokazilo",
  "wa.msg_greeting": "Ahoj {name}, vypočítal/a jsem cenový odhad:",
  "wa.msg_name_label": "Jméno",
  "wa.msg_style_label": "🎨 Styl",
  "wa.msg_bodypart_label": "📍 Část těla",
  "wa.msg_size_label": "📏 Velikost",
  "wa.msg_complexity_label": "⚙️ Složitost",
  "wa.msg_time_label": "⏱ Odhadovaný čas",
  "wa.msg_total_label": "💰 Celkem",
  "wa.msg_deposit_label": "💳 Záloha",
  "wa.msg_details_label": "🔗 Podrobnosti",
  "wa.template_high_greeting": "Ahoj {name}, skvělé zprávy! Vypočítal/a jsem podrobný odhad:",
  "wa.template_low_greeting": "Ahoj {name}, zajímám se o tetování. Můžete mi říci o dostupnosti a cenách?",
  "tooltip.labor": "Na základě odhadovaného času násobeného hodinovou sazbou umělce.",
  "tooltip.complexity": "Složitější návrhy vyžadují více času a přesnosti.",
  "tooltip.placement": "Některé části těla jsou citlivější nebo hůře přístupné.",
  "tooltip.style": "Různé styly vyžadují specifické techniky ovlivňující cenu.",
  "pricing_edu.button": "Jak tento výpočet funguje?",
  "pricing_edu.title": "Jak se počítá cena",
  "pricing_edu.disclaimer": "Toto je vždy odhad. Konečná cena se určí po osobní konzultaci.",
  "ai.title": "AI skener",
  "ai.analyzing": "AI analyzuje...",
  "ai.scan_complete": "✨ AI sken dokončen",
  "pwa.install_title": "Nainstalovat jako aplikaci",
  "pwa.install_button": "Nainstalovat nyní",
  // Batch 3+4
  "tab.price_engine": "Cenový motor",
  "tab.price_engine_desc": "Spravujte všechny cenové násobky: velikosti, složitost, části těla a styly.",
  "basic.desc": "Nastavte své standardní sazby a náklady",
  "basic.min_price_desc": "Nejnižší cena, kterou účtujete",
  "basic.hourly_rate_desc": "Vaše standardní hodinová sazba",
  "basic.daily_rate_placeholder": "Ponechte prázdné, pokud nepoužíváte",
  "basic.percentage": "%",
  "basic.fixed_amount": "Pevná",
  "sizes.title": "Nastavení velikostí",
  "sizes.desc": "Nastavte odhadovaný čas podle kategorie velikosti",
  "sizes.min_hours": "Min. hodin",
  "sizes.max_hours": "Max. hodin",
  "sizes.hours": "hodin",
  "complexity.title": "Násobky složitosti",
  "complexity.desc": "Upravte cenový faktor podle složitosti",
  "bodyparts.title": "Násobky částí těla",
  "bodyparts.desc": "Některé části těla jsou obtížnější na tetování",
  "styles.title": "Správa stylů",
  "styles.desc": "Zapněte/vypněte styly a upravte cenový faktor.",
  "theme.title": "Barevný motiv",
  "theme.desc": "Přizpůsobte barvy kalkulačky",
  "theme.preview": "Náhled",
  "theme.preview_desc": "Takto vypadá vaše kalkulačka",
  "theme.hue": "Odstín",
  "theme.saturation": "Sytost",
  "theme.lightness": "Světlost",
  "theme.presets": "Rychlý výběr",
  "branding.title": "Značka",
  "branding.desc": "Nahrajte logo, zvolte jazyk a měnu",
  "branding.logo": "Logo",
  "branding.logo_desc": "Logo studia (max. 2 MB, PNG nebo JPG)",
  "branding.upload": "Nahrát logo",
  "branding.remove": "Odstranit",
  "branding.language": "Jazyk kalkulačky",
  "branding.currency": "Měna",
  "branding.qr_code": "QR kód",
  "branding.qr_desc": "Klienti naskenují tento kód pro přístup ke kalkulačce.",
  "branding.download_qr": "Stáhnout QR",
  "onboard.step_minimum": "Minimální cena",
  "onboard.step_hourly": "Hodinová sazba",
  "onboard.step_sizes": "Potvrdit velikosti",
  "onboard.step_styles": "Nastavit styly",
  "onboard.step_logo": "Nahrát logo",
  "onboard.step_finish": "Kalkulačka připravena!",
  "dash.onboard_min_desc": "Nastavte minimální cenu.",
  "dash.onboard_hourly_desc": "Nastavte hodinovou sazbu. Je základem všech výpočtů.",
  "dash.onboard_sizes_desc": "Výchozí kategorie velikostí jsou nastaveny. Upravte později.",
  "dash.onboard_styles_desc": "Všechny běžné styly jsou aktivní. Zapněte/vypněte v panelu.",
  "dash.onboard_logo_desc": "Nahrajte logo nyní nebo později přes kartu Značka.",
  "dash.onboard_logo_later": "Nahrát později přes Panel → Značka",
  "dash.onboard_finish_title": "Vaše kalkulačka je připravena!",
  "dash.onboard_finish_desc": "Sdílejte svůj jedinečný odkaz. Nastavení můžete kdykoli změnit.",
  "dash.your_link": "Odkaz na kalkulačku:",
  "dash.online_card": "Vaše online vizitka.",
  "dash.view_my_calculator": "Zobrazit kalkulačku",
  "dash.upload_thumbnail": "Vlastní obrázek",
  "dash.reset_to_default": "Resetovat",
  "dash.reset_all_thumbnails": "Resetovat vše",
  "dash.thumbnail_uploaded": "Obrázek nahrán!",
  "dash.thumbnail_reset": "Obrázek resetován",
  "dash.thumbnails_reset_all": "Všechny obrázky resetovány",
  "dash.invite_created": "Pozvánka vytvořena!",
  "dash.invite_error": "Chyba pozvánky",
  "dash.member_removed": "Člen odstraněn",
  "dash.link_copied_generic": "Odkaz zkopírován!",
  "dash.profile_saved": "Profil uložen!",
  "dash.profile_save_error": "Chyba uložení",
  "dash.calculator_ready": "🎉 Kalkulačka připravena!",
  "dash.share_with_clients": "Sdílejte svůj odkaz.",
  "dash.step_of": "Krok {current} z {total}",
  "dash.start_inkprice": "Začít s InkPrice",
  "laf.title": "Vzhled",
  "laf.desc": "Přizpůsobte vzhled veřejné kalkulačky.",
  "laf.color_mode": "Barevný režim",
  "laf.dark_mode": "Tmavý režim",
  "laf.light_mode": "Světlý režim",
  "laf.background": "Pozadí",
  "laf.solid_color": "Jednobarevné",
  "laf.image_url": "URL obrázku",
  "laf.preview": "Náhled",
  "laf.saved": "Vzhled uložen",
  "laf.presets": "Prémiové motivy",
  "laf.font_selector": "Písmo kalkulačky",
  "laf.overlay_opacity": "Průhlednost překryvu",
  "laf.bg_fixed": "Fixní (Parallax)",
  "laf.bg_scroll": "Posun",
  "consult_settings.title": "Nastavení konzultací",
  "consult_settings.desc": "Nastavte způsoby kontaktu.",
  "consult_settings.booking_url_label": "URL rezervace",
  "consult_settings.email_label": "Konzultační e-mail",
  "consult_settings.channels_title": "Kanály zájemců",
  "consult_settings.channels_desc": "Viditelné způsoby kontaktu.",
  "help.min_price": "Absolutní minimální cena za sezení.",
  "help.hourly_rate": "Vaše hodinová sazba je základem všech výpočtů.",
  "help.multipliers": "Násobky upravují cenu podle stylu, složitosti a umístění.",
  "help.sizes": "Nastavte časový rozsah pro každou velikost.",
  "help.complexity": "Upravte násobky pro složitost.",
  "help.body_parts": "Některé zóny vyžadují vyšší násobky.",
  "help.styles": "Zapněte/vypněte styly a nastavte násobky.",
  "help.logo": "Vaše logo se zobrazuje na kalkulačce a v katalogu. Max. 2 MB.",
  "help.currency": "Měna zobrazená na kalkulačce.",
  "help.calculator_language": "Jazyk vaší veřejné kalkulačky.",
  "help.dashboard_language": "Jazyk tohoto panelu — viditelný pouze pro vás.",
  "profile.mfa_title": "Zabezpečení účtu",
  "profile.mfa_desc": "Chraňte svůj účet pomocí 2FA.",
  "profile.mfa_enable": "Zapnout 2FA",
  "profile.mfa_verify": "Ověřit",
  "profile.mfa_active": "Aktivní",
  "profile.mfa_enabled_success": "✅ 2FA zapnuta!",
  "profile.google_reviews": "Google recenze",
  "profile.google_reviews_desc": "Propojte svůj Google Business profil.",
  "profile.sync_now": "Synchronizovat",
  "profile.sync_success": "Google recenze synchronizovány!",
  "landing.faq_title": "Často kladené otázky",
  "landing.faq_subtitle": "Vše o Inkvaluatoru",
  "landing.faq_q1": "Jak funguje kalkulačka?",
  "landing.faq_a1": "Nastavte ceny a násobky. Klienti dostanou okamžitý odhad.",
  "landing.faq_q2": "Je Inkvaluator zdarma?",
  "landing.faq_a2": "Ano! Free plán zahrnuje kompletní kalkulačku. Pro více přejděte na Pro.",
  "landing.faq_q3": "Jak funguje AI skener?",
  "landing.faq_a3": "Nahrajte foto, AI analyzuje velikost, složitost a styl za sekundy.",
  "landing.faq_q4": "Mohou mě klienti kontaktovat?",
  "landing.faq_a4": "Ano, přes WhatsApp s předvyplněnou zprávou.",
  "landing.faq_q5": "Vícejazyčná podpora?",
  "landing.faq_a5": "17 jazyků a 13 měn — perfektní pro mezinárodní umělce.",
  "landing.faq_q6": "Jak získám více klientů?",
  "landing.faq_a6": "Pro/Studio umělci jsou na světové mapě a stránce objevování.",
  "city.title": "Tatéři v {city}",
  "city.description": "Najděte tatéry v {city}.",
  "city.no_artists": "Žádní umělci v {city} nenalezeni.",
  "city.discover_all": "Všichni umělci",
  "seo.landing_title": "Inkvaluator – Kalkulačka Cen Tetování",
  "seo.landing_desc": "Vypočítejte cenu tetování okamžitě.",
  "vault.seo_title": "Můj Trezor Tetování | Inkvaluator",
  "vault.title": "Můj",
  "vault.title_accent": "Trezor Tetování",
  "vault.desc": "Vaše uložené odhady.",
  "vault.empty": "Žádné odhady. Použijte kalkulačku!",
  "vault.save_btn": "Uložit",
  "vault.saved_title": "Uloženo!",
  "vault.saved_desc": "Odhad uložen.",
  "success.active": "Aktivní — vše odemčeno",
  "success.setup_whatsapp": "Nastavit WhatsApp",
  "success.syncing": "Aktivace vašeho plánu…",
  "success.view_details": "Zobrazit detaily",
  "analytics.title": "Analytika",
  "analytics.desc": "Využití vaší kalkulačky",
  "analytics.total_calculations": "Celkem výpočtů",
  "analytics.conversion_rate": "Konverzní poměr",
  "analytics.this_month": "Tento měsíc",
  "analytics.no_data": "Zatím žádná data.",
  "studio.title": "Správa studia",
  "studio.team": "Tým",
  "studio.invite": "Pozvat umělce",
  "studio.invite_desc": "Vygenerujte pozvánkový odkaz.",
  "studio.generate_link": "Vygenerovat odkaz",
  "studio.no_members": "Žádní členové. Pozvěte umělce.",
  "color.gold": "Zlatá",
  "color.red": "Červená",
  "color.blue": "Modrá",
  "color.green": "Zelená",
  "color.purple": "Fialová",
  "color.pink": "Růžová",
  "color.turquoise": "Tyrkysová",
  "color.orange": "Oranžová",
  "tip.title": "Doporučit umělce",
  "tip.desc": "Znáte tatéra, který by měl být na Inkvaluatoru?",
  "share.button": "Sdílet",
  "sw.update_available": "Nová verze dostupná.",
  "embed.iframe_desc": "Vložte toto do svého HTML.",
  "embed.floating_desc": "Vložte před </body>.",
  "embed.verified_badge": "Ověřený badge",
  "embed.badge_desc": "Umístěte tento badge na svůj web.",
  "compare.title": "Srovnání cen",
  "compare.disclaimer": "Srovnání na základě standardního tetování.",
  "landing.calculate_price": "Vypočítat cenu",
  "landing.total_value_prefix": "Již",
  "landing.total_value_suffix": "tetovacího umění vypočteno",
  "ai.desc": "Nahrajte foto a nechte AI určit parametry",
  "ai.scan_failed": "Skenování selhalo",
  "ai.try_again": "Zkuste znovu",
  "referral.title": "Doporučit kolegu",
  "referral.generate": "Vygenerovat doporučující odkaz",
  "tooltip.design": "Náklady na návrh a přípravu vašeho unikátního tetování.",
  "tooltip.deposit": "Záloha zajistí vaši schůzku a odečte se od celkové ceny.",
  "pricing_edu.intro": "Cena tetování je určena několika faktory odrážejícími řemeslo a čas.",
  "step.next": "Pokračovat",
  "step.view_result": "Zobrazit cenu",
  "step.details": "Detaily",
  "step.details_desc": "Jak detailní je váš návrh?",
  "layout.stepper": "Krok za krokem",
  "layout.form": "Vše najednou",
  "calc.view_price_cta": "Zobrazit přehled cen",
  "calc.rough_estimate": "Hrubý odhad",
  "calc.calculated_price": "Vypočítaná cena",
  "calc.save_for_later": "Uložit na později",
  "calc.share": "Sdílet",
  "calc.more_options": "Více možností",
  "calc.compare_configs": "Porovnat konfigurace",
  "calc.compare_desc": "Porovnat dvě cenové konfigurace vedle sebe",
  "calc.no_photo": "Nebyla přidána referenční fotografie",
  "calc.link_copied": "Odkaz zkopírován!",
  "whatsapp.add_photo": "Přidejte svou fotku",
  "whatsapp.photos_label": "Referenční fotky — max. 3",
  "laf.reset_success": "Motiv obnoven na výchozí hodnoty",
  "calc.view_tattoo_plan": "Zobrazit váš Tattoo Plan ✓",
  "wa.plan_message": "Zde je můj Tattoo Plan:",
  "lead.tattoo_plan": "Tattoo Plan",
  "lead.made_with": "Vytvořeno s Inkvaluator",
  "lead.contact_artist": "Kontaktovat {artist}",
  "lead.share": "Sdílet",
  "lead.share_title": "Můj Tattoo Plan",
  "lead.link_copied": "Odkaz zkopírován!",
  "lead.deposit_label": "Záloha",
  "lead.pay_deposit": "Zaplatit zálohu {amount}",
  "lead.deposit_request_title": "Požádat o zálohu",
  "lead.deposit_request_desc": "Zeptejte se umělce, zda je záloha ~{amount} možná.",
  "lead.deposit_request_wa": "Odeslat žádost přes WhatsApp",
  "lead.for_you": "Vás",
  "lead.for_label": "Pro",
  "lead.back_to_calculator": "Zpět na kalkulačku",
  "lead.deposit_wa_msg":
    "Dobrý den {artist}, prohlédl jsem si svůj Tattoo Plan na Inkvaluator. Byla by záloha ~{amount} možná?\nPlán: {url}",
  "blog.no_translation": "Překlad není k dispozici",
  "flash.tab": "Flash",
  "flash.upload": "Nahrát flash design",
  "flash.title": "Název",
  "flash.price": "Cena",
  "flash.size": "Velikost (cm)",
  "flash.available": "Dostupné",
  "flash.featured": "Doporučené",
  "flash.limit_used": "z {max} flash designů použito",
  "flash.delete_confirm": "Smazat flash design?",
  "flash.reserve_whatsapp": "Rezervovat přes WhatsApp",
  "flash.whatsapp_msg": "Mám zájem o vaše flash tetování:",
  "flash.matching_designs": "Odpovídající flash designy",
  "flash.only_with_flash": "Pouze umělci s flash",
  "groei.score": "Skóre profilu",
  "groei.views": "zobrazení profilu",
  "groei.sessions": "relace kalkulačky",
  "groei.leads": "leady",
  "groei.this_week": "tento týden",
  "groei.improve": "Vylepšete svůj profil",
  "groei.add_photos": "Přidejte více portfoliových fotek",
  "groei.add_whatsapp": "Nastavte své číslo WhatsApp",
  "groei.add_bio": "Napište delší popis",
  "groei.add_avatar": "Nahrajte profilový obrázek",
  "groei.add_rate": "Nastavte svou hodinovou sazbu",
  "groei.add_city": "Přidejte své město",
  "groei.get_leads": "Sdílejte kalkulačku pro získání leadů",
  "groei.collapse": "Sbalit",
  "groei.expand": "Rozbalit",
  "groei.tip_portfolio": "Umělci s 6+ fotkami získávají 3x více relací",
  "groei.tip_whatsapp": "Klienti s WhatsApp tlačítkem konvertují 5x lépe",
  "groei.tip_leads": "Sdílejte odkaz na kalkulačku pro získání prvního leadu",
  "groei.tip_avatar": "Profilová fotka zvyšuje vaši důvěryhodnost",
  "groei.tip_action_photos": "Přidat fotky",
  "groei.tip_action_whatsapp": "Nastavit WhatsApp",
  "groei.tip_action_share": "Sdílet kalkulačku",
  "groei.tip_action_avatar": "Nahrát fotku",
  "milestone.eerste_lead": "První lead přijat!",
  "milestone.tien_leads": "10 leadů dosaženo!",
  "milestone.profiel_compleet": "Profil 100% kompletní!",
  "milestone.eerste_flash": "První flash design nahrán!",
  "wa.name_required": "Jméno je povinné",
  "wa.plan_message_full": "Zobrazit můj kompletní Tattoo Plan: {url}",
};

export const translations: Record<Language, Record<string, string>> = {
  en,
  nl,
  de,
  fr,
  es,
  it,
  pt,
  sv,
  pl,
  ja,
  ko,
  ru,
  tr,
  da,
  no,
  fi,
  cs,
};

// Translation keys type
export type TranslationKey = string;

// Helper to get localized labels for types
export function getSizeLabels(lang: Language): Record<string, string> {
  const t = translations[lang];
  return {
    mini: t["size.mini"],
    small: t["size.small"],
    medium: t["size.medium"],
    large: t["size.large"],
    xl: t["size.xl"],
  };
}

export function getComplexityLabels(lang: Language): Record<string, string> {
  const t = translations[lang];
  return { simple: t["complexity.simple"], medium: t["complexity.medium"], detailed: t["complexity.detailed"] };
}

export function getBodyPartLabels(lang: Language): Record<string, string> {
  const t = translations[lang];
  return {
    arm: t["body.arm"],
    forearm: t["body.forearm"],
    upper_arm: t["body.upper_arm"],
    leg: t["body.leg"],
    thigh: t["body.thigh"],
    calf: t["body.calf"],
    back: t["body.back"],
    chest: t["body.chest"],
    ribs: t["body.ribs"],
    stomach: t["body.stomach"],
    hand: t["body.hand"],
    foot: t["body.foot"],
    neck: t["body.neck"],
    face: t["body.face"],
    shoulder: t["body.shoulder"],
    hip: t["body.hip"],
    finger: t["body.finger"],
    other: t["body.other"],
  };
}

export function getStyleLabels(lang: Language): Record<string, string> {
  const t = translations[lang];
  return {
    black_grey: t["style.black_grey"],
    full_color: t["style.full_color"],
    realism: t["style.realism"],
    fine_line: t["style.fine_line"],
    traditional: t["style.traditional"],
    neo_traditional: t["style.neo_traditional"],
    watercolor: t["style.watercolor"],
    tribal: t["style.tribal"],
    japanese: t["style.japanese"],
    geometric: t["style.geometric"],
    lettering: t["style.lettering"],
    script: t["style.script"],
    dotwork: t["style.dotwork"],
    mandala: t["style.mandala"],
    blackwork: t["style.blackwork"],
    illustrative: t["style.illustrative"],
    surrealism: t["style.surrealism"],
    ornamental: t["style.ornamental"],
    micro_realism: t["style.micro_realism"],
    trash_polka: t["style.trash_polka"],
    other: t["style.other"],
    // New expanded styles
    chicano: t["style.chicano"] || "Chicano",
    cyber_sigilism: t["style.cyber_sigilism"] || "Cyber Sigilism",
    patchwork: t["style.patchwork"] || "Patchwork",
    sketch: t["style.sketch"] || "Sketch",
    abstract: t["style.abstract"] || "Abstract",
    minimalist: t["style.minimalist"] || "Minimalist",
    american_traditional: t["style.american_traditional"] || "American Traditional",
    new_school: t["style.new_school"] || "New School",
    biomechanical: t["style.biomechanical"] || "Biomechanical",
    dark_art: t["style.dark_art"] || "Dark Art",
    anime: t["style.anime"] || "Anime",
    kawaii: t["style.kawaii"] || "Kawaii",
    celtic: t["style.celtic"] || "Celtic",
    polynesian: t["style.polynesian"] || "Polynesian",
    maori: t["style.maori"] || "Māori",
    haida: t["style.haida"] || "Haida",
    etching: t["style.etching"] || "Etching",
    woodcut: t["style.woodcut"] || "Woodcut",
    portrait: t["style.portrait"] || "Portrait",
    pet_portrait: t["style.pet_portrait"] || "Pet Portrait",
    floral: t["style.floral"] || "Floral",
    botanical: t["style.botanical"] || "Botanical",
    sacred_geometry: t["style.sacred_geometry"] || "Sacred Geometry",
    horror: t["style.horror"] || "Horror",
    fantasy: t["style.fantasy"] || "Fantasy",
    mythology: t["style.mythology"] || "Mythology",
    sleeve_art: t["style.sleeve_art"] || "Sleeve Art",
    hand_poke: t["style.hand_poke"] || "Hand Poke",
    stick_and_poke: t["style.stick_and_poke"] || "Stick & Poke",
    negative_space: t["style.negative_space"] || "Negative Space",
    white_ink: t["style.white_ink"] || "White Ink",
    uv_ink: t["style.uv_ink"] || "UV Ink",
  };
}
